/*
 * Fresh Faces  ·  assets/hoh-fresh-faces.js
 *
 * A rail at the top of the gallery that shows characters submitted in
 * the last 3 days. Auto-rotates as new submissions arrive. Mobile gets
 * a horizontal scroll, desktop gets a grid row.
 *
 * Lives outside sections/hall-of-heroes.liquid so the section file
 * stays under Shopify's 256 KB limit.
 *
 * Coupling to the host page:
 *   window.hohGetAllCharacters()    pool source
 *   window.hohOpenDetail(id)        click target
 *   window.hohGetRarity(hearts)     rarity tier accent
 *
 * Public API:
 *   hohRenderFreshFaces()           full re-render, called from host
 *   hohFreshFacesPickRandom()       returns one fresh face id (for the
 *                                   cap-rejection redirect)
 */
(function(){
  'use strict';

  /* The newcomer window. A character is "fresh" (carries the NEW
     badge) for this many days after submission. After the window
     closes the character drops into the regular gallery rotation
     only — Fresh Faces will NOT fall back to older characters; an
     empty Fresh Faces simply hides the section. */
  var FRESH_DAYS = 7;
  var FRESH_MS = FRESH_DAYS * 24 * 60 * 60 * 1000;
  /* Default visible count: two rows of FIVE on desktop (10 cards).
     The rail auto-expands to up to MAX_VISIBLE (20) when the matched
     pool has more candidates — no View More button needed. The cap
     is a hard ceiling regardless of how big the pool is. Themed and
     special-event refreshes use MAX_VISIBLE for the slice so
     "All Common (47 chars)" actually shows a town instead of just
     ten faces. PAGE_SIZE stays 10 for the snapshot baseline. */
  var PAGE_SIZE = 10;
  var MAX_VISIBLE = 20;
  var visibleCount = PAGE_SIZE;
  /* Cached wandering-phase pool. Shuffled when refresh is hit. View
     More reuses the same pool (just expanding visibleCount) so cards
     don't re-shuffle every time the user wants to see more. */
  var cachedWanderingPool = null;
  var cachedWanderingPhase = null;
  /* Persistent memory of which characters and themes have already
     been shown in the wandering rail. Persists across page reloads
     via localStorage so the user's "wandering" experience continues
     where it left off — every refresh delivers something new until
     the entire pool/theme set has been exhausted, then resets.
     Last-state memory: when seen sets reset, we also remember that
     happened so we can show the "you've seen everything" copy
     instead of looping silently. */
  var SEEN_CHARS_KEY = 'hoh-fresh-seen-chars';
  var SEEN_THEMES_KEY = 'hoh-fresh-seen-themes';
  function loadSeenSet(key){
    try {
      var raw = localStorage.getItem(key);
      if(!raw) return Object.create(null);
      var parsed = JSON.parse(raw);
      if(!parsed || typeof parsed !== 'object') return Object.create(null);
      return parsed;
    } catch(e){ return Object.create(null); }
  }
  function saveSeenSet(key, set){
    try { localStorage.setItem(key, JSON.stringify(set)); } catch(e){}
  }
  var seenChars = loadSeenSet(SEEN_CHARS_KEY);
  var seenThemes = loadSeenSet(SEEN_THEMES_KEY);
  /* R136-themed-rotation: ring buffer of the last N themed-phase
     eyebrows shown. seenThemes provides the long-cycle "every theme
     gets a turn before any repeats" behavior, but it RESETS once
     fully cycled — at that moment a recently-seen theme could be
     re-picked back-to-back. This buffer is the short-term "don't
     repeat the last few" guard that survives the cycle reset.
     Picker excludes any theme whose eyebrow is in this list, in
     addition to seenThemes. In-memory only; resets on page reload. */
  var recentThemeEyebrows = [];
  var RECENT_THEME_BUFFER = 5;
  function rememberRecentTheme(eyebrow){
    if(!eyebrow) return;
    recentThemeEyebrows.push(eyebrow);
    if(recentThemeEyebrows.length > RECENT_THEME_BUFFER){
      recentThemeEyebrows = recentThemeEyebrows.slice(-RECENT_THEME_BUFFER);
    }
  }
  function isRecentTheme(eyebrow){
    return eyebrow && recentThemeEyebrows.indexOf(eyebrow) !== -1;
  }
  /* Minimum chars a themed phase needs to be worth showing. Smaller
     than this and the rail looks half-empty + pointless to refresh
     into. The picker skips below-threshold themes. */
  var MIN_THEME_CHARS = 3;

  /* ================================================================ */
  /* R140-Z3n: ROLL TYPE REGISTRY                                     */
  /* ================================================================ */
  /* Single source of truth for which bucket each event/roll lives in.
     Three types:
       'heart'   — Heart Rolls. User spends hearts to like heroes.
                   Themed phases, bustling streets, familiar faces,
                   spectrum, epic encounters, legendary encounters,
                   ff portrait gallery, plain wandering / fresh faces.
       'event'   — Event Rolls. Active interactions with stakes,
                   guesses, picks. Brawls, duels, dice, quizzes,
                   bard/healer, toll road, common thread, etc.
       'special' — Special Event Rolls. Very rare big moments.
                   Mage Bazaar, Legendary Shop, Hero Circle.

     Future pacing math reads this registry to enforce the 65/30/5
     distribution. New event keys MUST be added here when wired into
     a picker so the categorization stays complete. */
  var ROLL_TYPE_HEART   = 'heart';
  var ROLL_TYPE_EVENT   = 'event';
  var ROLL_TYPE_SPECIAL = 'special';
  var ROLL_TYPE_REGISTRY = {
    /* ---- HEART ROLLS ---- */
    /* Themed phases — the THEMED_PHASES array entries all map here.
       Keyed by eyebrow-as-id but the picker lookups use the array
       index. These keys exist so future code can route by name. */
    freshFaces:                 ROLL_TYPE_HEART,
    wanderingHeroes:            ROLL_TYPE_HEART,
    crossroads:                 ROLL_TYPE_HEART,
    theLongRoad:                ROLL_TYPE_HEART,
    /* Rarity tribes */
    rarityTribe:                ROLL_TYPE_HEART,
    /* Familiar Faces re-encounter rail */
    familiar:                   ROLL_TYPE_HEART,
    /* FF Portrait Gallery showcase */
    ffPortrait:                 ROLL_TYPE_HEART,
    /* Spectrum of Heroes (one per tier) */
    spectrum:                   ROLL_TYPE_HEART,
    /* Bustling Streets variants — heart-spend scrolling rails */
    bustlingStreets:            ROLL_TYPE_HEART,
    bustlingStreetsUncommon:    ROLL_TYPE_HEART,
    bustlingStreetsRare:        ROLL_TYPE_HEART,
    bustlingStreetsPortrait:    ROLL_TYPE_HEART,
    bustlingStreetsCasters:     ROLL_TYPE_HEART,
    bustlingStreetsBeastfolk:   ROLL_TYPE_HEART,
    bustlingStreetsClergy:      ROLL_TYPE_HEART,
    bustlingStreetsDragonborn:  ROLL_TYPE_HEART,
    /* R140-Z3n: 5 new variants. */
    bustlingStreetsDragonHoard: ROLL_TYPE_HEART,
    bustlingStreetsWildHunt:    ROLL_TYPE_HEART,
    bustlingStreetsCharlatans:  ROLL_TYPE_HEART,
    bustlingStreetsFurredFolk:  ROLL_TYPE_HEART,
    bustlingStreetsFeyCourt:    ROLL_TYPE_HEART,
    /* R140-Z3n Phase 3: 5 more processions. */
    bustlingStreetsKnightProcession: ROLL_TYPE_HEART,
    bustlingStreetsBardstorm:        ROLL_TYPE_HEART,
    bustlingStreetsDruidMigration:   ROLL_TYPE_HEART,
    bustlingStreetsPactWalk:         ROLL_TYPE_HEART,
    bustlingStreetsTemplarMarch:     ROLL_TYPE_HEART,
    /* R150: The Conveyer Belt — one-row Type 1 heart rolls. */
    conveyerCommon:                  ROLL_TYPE_HEART,
    conveyerUncommon:                ROLL_TYPE_HEART,
    conveyerRare:                    ROLL_TYPE_HEART,
    conveyerEpic:                    ROLL_TYPE_HEART,
    conveyerLegendary:               ROLL_TYPE_HEART,
    conveyerMythic:                  ROLL_TYPE_HEART,
    conveyerArcane:                  ROLL_TYPE_HEART,
    conveyerMartial:                 ROLL_TYPE_HEART,
    conveyerWanderers:               ROLL_TYPE_HEART,
    /* Epic / Legendary Encounters — heart-spend showcase rails */
    epic:                       ROLL_TYPE_HEART,
    legendary:                  ROLL_TYPE_HEART,
    legendaryTrio:              ROLL_TYPE_HEART,
    twoLegends:                 ROLL_TYPE_HEART,
    championMeeting:            ROLL_TYPE_HEART,
    /* R554: ffSpotlight retired. See the note at its producer in pickSpecial. */
    /* Second Chances / Banished rails (heart-spend re-engage) */
    secondChances:              ROLL_TYPE_HEART,
    banished:                   ROLL_TYPE_HEART,
    familiarCrowd:              ROLL_TYPE_HEART,

    /* ---- EVENT ROLLS ---- */
    diceTrader:                 ROLL_TYPE_EVENT,
    championDuel:               ROLL_TYPE_EVENT,
    rareDuel:                   ROLL_TYPE_EVENT,
    epicDuel:                   ROLL_TYPE_EVENT,
    legendaryDuel:              ROLL_TYPE_EVENT,
    mythicDuel:                 ROLL_TYPE_EVENT,
    crownMatch:                 ROLL_TYPE_EVENT,
    tavernBrawl:                ROLL_TYPE_EVENT,
    tavernBrawl4:               ROLL_TYPE_EVENT,
    barFight:                   ROLL_TYPE_EVENT,
    spinWheel:                  ROLL_TYPE_EVENT,
    pickACard:                  ROLL_TYPE_EVENT,
    tollRoad:                   ROLL_TYPE_EVENT,
    carnival:                   ROLL_TYPE_EVENT,
    commonThread:               ROLL_TYPE_EVENT,
    travelHealer:               ROLL_TYPE_EVENT,
    travelBard:                 ROLL_TYPE_EVENT,
    popularity:                 ROLL_TYPE_EVENT,
    guessFB:                    ROLL_TYPE_EVENT,
    guessWW:                    ROLL_TYPE_EVENT,
    guessRR:                    ROLL_TYPE_EVENT,
    guessEHE:                   ROLL_TYPE_EVENT,
    findCulprit:                ROLL_TYPE_EVENT,
    findCulpritRace:            ROLL_TYPE_EVENT,
    findImposter:               ROLL_TYPE_EVENT,
    coinBet:                    ROLL_TYPE_EVENT,
    /* R150-Z45: guessHearts ("How Loved Are They?") removed — retired
       per user as a boring event. Kept out of every pool + picker
       map below so it can never roll. */
    classQuiz:                  ROLL_TYPE_EVENT,
    /* R150: The Class War head-to-head recruitment event. */
    classWar:                   ROLL_TYPE_EVENT,
    /* Coin Toss is a Bazaar-buff-fired event but mechanically Event */
    coinToss:                   ROLL_TYPE_EVENT,
    /* R148-Z23: The Echo — nostalgia "pick your most memorable" event */
    echo:                       ROLL_TYPE_EVENT,
    /* R148-Z29: Pay the Guide — bard offers paid bazaar/legshop trip */
    payGuide:                   ROLL_TYPE_EVENT,
    /* R148-Z37: Type 3 Mythic Duel — special-tier 2-mythic duel
       with optional stealth +d6 buff. Distinct from the gambling-
       pool mythicDuel (which is ROLL_TYPE_EVENT). */
    mythicDuelSpecial:          ROLL_TYPE_SPECIAL,

    /* R148-Z23: mageBazaar reclassified to ROLL_TYPE_EVENT (Type 2).
       The bazaar still has its own buffer cadence (every ~25 rolls)
       so it doesn't dominate the Event pool — but it's now part of
       the unlimited rotation rather than gated behind the rare-
       special pacing. Mirrors UNLIMITED_EVENT_TYPES which already
       lists mageBazaar. */
    mageBazaar:                 ROLL_TYPE_EVENT,
    /* R148-Z24: Thieves' + Sage's bazaars — siblings of mageBazaar.
       Same cadence (shared cooldown), routed via _pickSpecialRoll. */
    thievesBazaar:              ROLL_TYPE_EVENT,
    sageBazaar:                 ROLL_TYPE_EVENT,

    /* ---- SPECIAL EVENT ROLLS ---- */
    legendaryShop:              ROLL_TYPE_SPECIAL,
    heroCircle:                 ROLL_TYPE_SPECIAL,
    /* Hold-stream events — rare engagement moments */
    richGetRicher:              ROLL_TYPE_SPECIAL,
    underdog:                   ROLL_TYPE_SPECIAL,
    feedYourOwn:                ROLL_TYPE_SPECIAL,
    promotion:                  ROLL_TYPE_SPECIAL
  };
  /* Lookup helper. Returns 'heart' | 'event' | 'special' | null. */
  function rollTypeOf(eventKey){
    if(!eventKey) return null;
    return ROLL_TYPE_REGISTRY[eventKey] || null;
  }
  /* Public so external scripts can introspect the categorization. */
  window.hohRollTypeOf = rollTypeOf;
  window.HOH_ROLL_TYPES = {
    HEART: ROLL_TYPE_HEART,
    EVENT: ROLL_TYPE_EVENT,
    SPECIAL: ROLL_TYPE_SPECIAL
  };
  /* Per-device dismissed list. Shared between the Fresh Faces rail
     and the main gallery: a hidden hero is hidden everywhere on this
     device. The cap prevents the list from growing forever. */
  var DISMISSED_KEY = 'hoh-dismissed';
  var DISMISSED_CAP = 500;
  /* Tracks ids rendered in the previous pass so we can fade-in only
     newly-added cards, not the ones that were already on screen. */
  var lastRenderedIds = [];
  /* R148-Z28: Tracks the last phase key the renderer painted, so the
     swap-fade only triggers on actual phase transitions (Type 1 → Type
     1 with different themes, or going into a special event). Within-
     phase re-renders (e.g. heart click chip update) skip the fade. */
  var _lastTrackPhaseKey = null;
  /* Desktop-only frozen snapshot of the initial visible set. After
     the first render of a phase, subsequent renders intersect the
     pool with this snapshot — heart or X on a card removes it but
     the freed slot stays empty (no backfill). When the snapshot
     intersection is empty, we auto-refresh to a new themed phase.
     Mobile keeps the backfill behavior because the 2x2 grid would
     feel broken with empty slots on a small screen. */
  var renderedSnapshotIds = null;
  var snapshotPhaseKey = null;
  /* Set to true when an auto-refresh is in flight so we don't kick
     off a second one mid-render. */
  var autoRefreshing = false;

  function esc(s){
    if(s==null)return '';
    return String(s).replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;').replace(/"/g,'&quot;').replace(/'/g,'&#39;');
  }
  /* CSS selector escape so we can find a card by its id attribute
     without breaking on uuids that happen to start with a digit. */
  function cssEscape(s){
    if(typeof CSS !== 'undefined' && CSS.escape) return CSS.escape(s);
    return String(s).replace(/[^a-zA-Z0-9_-]/g, '\\$&');
  }
  /* R590 (Jan): THE SHUFFLE THAT WAS NOT A SHUFFLE.
     This whole file used to sort pools with
       .sort(function(){ return Math.random() - 0.5; })
     in 38 separate places. That comparator is not a shuffle. It is
     inconsistent, so V8's TimSort leaves elements near where they
     started, and the bias is severe at the ends of the array.

     MEASURED over 3,000 trials, N=1000, taking the visible 20:
       the FIRST element of the source array surfaced  9.40%
       rank 20                                          3.40%
       rank 500                                         2.25%
       the LAST element                                 0.50%
       a fair shuffle would give every one of them      2.00%

     window.hohGetAllCharacters returns rows ordered created_at DESC
     (hoh-section-core.js:3183), so that bias ran NEWEST-first and
     buried the oldest heroes at roughly a quarter of their fair rate.
     Jan: "just literally make every single one random ... that way
     different characters can actually show up instead of just the
     top ones."

     Fisher-Yates is uniform. Every hero draws at exactly 1/n.
     Copies first, so a caller's array is never reordered underneath
     it (several call sites shuffle a pool they still hold a reference
     to). harness/freshfaces.cjs fails if the old comparator returns. */
  function hfShuffle(list){
    var a = (list && list.slice) ? list.slice() : [];
    for(var i = a.length - 1; i > 0; i--){
      var j = Math.floor(Math.random() * (i + 1));
      var t = a[i]; a[i] = a[j]; a[j] = t;
    }
    return a;
  }

  /* R590 (Jan): SHARED THEME PREDICATES.
     ⛔ unseenList() and themePoolSize() are two hand-mirrored copies of
     the same filter logic in different scopes, one rejecting with
     `return false` and the other with `continue`. They share no
     mechanism, and the R136 comments inside both record this drift
     biting twice already. A pred added to only one either never appears
     (missing from unseenList) or appears and renders an empty rail
     (missing from themePoolSize).

     Every NEW pred lives here and is called from both, so the two
     copies cannot disagree about it. The legacy preds are left where
     they are on purpose: rewriting them is a separate change with its
     own risk, and this file is 22,000 lines. */

  /* Jan: "a role specifically made for heroes with the Merchant's Mark
     ... that way they actually feel more impacted."
     quest_meta rides on every Explore card: it is in HOH_CHAR_COLS
     (hoh-section-core.js:536) and hohGetAllCharacters is a .filter,
     never a .map, so nothing strips it. Verified. */
  function hfHasMerchantsMark(c){
    if(!c || !c.quest_meta) return false;
    var qm = c.quest_meta;
    /* Postgres jsonb can arrive as an object or, on some client paths,
       as a JSON string. Both shapes are accepted so the roll never
       silently empties. */
    if(typeof qm === 'string'){
      try { qm = JSON.parse(qm); } catch(_){ return false; }
    }
    if(!qm || !qm.merchantsMark) return false;
    /* ⛔ A HIDDEN BADGE PAYS NOTHING, and this predicate has to know it.
       sql/R208:105 reads
         (v_qm->>'merchantsMark') = 'true' AND NOT (v_hidden ? 'merchants-mark')
       with v_hidden := COALESCE(v_qm->'hidden', '[]'). A player who
       hides the badge on their hero silently switches the buff off.
       Without this check the themed roll would gather them anyway and
       the card would promise "Hearts land double" over a hero the
       server pays single for. This function answers "is the buff LIVE",
       which is the only question any caller here actually has. */
    var hidden = qm.hidden;
    if(hidden && typeof hidden.indexOf === 'function' && hidden.indexOf('merchants-mark') !== -1) return false;
    return true;
  }

  /* Jan: "maybe those characters never get shown, so we might need to
     make another themed role, which shows all of the weird ones, so
     that all of the characters have a chance to be shown."

     MEASURED CORRECTION to the brief's premise: all 20 canonical races
     and all 14 canonical classes ARE already covered by at least one
     themed filter. There are no gaps there. The real hole is FREE-TEXT
     custom values, which are invisible to every themed filter unless
     that exact string was hand-typed into one (44 such strings already
     were, one at a time, by hand).

     An orphan is a hero whose race OR class sits outside the canonical
     lists. Blanks are NOT orphans: a hero with no race recorded is
     missing data, not a custom identity, and putting them under an
     eyebrow promising the unusual would be a lie. The literal string
     'Other' IS an orphan — it is what the form writes when someone
     picks Other and leaves the custom box empty. */
  var _hfCanonRace = null, _hfCanonClass = null;
  function hfIsOrphanChar(c){
    if(!c) return false;
    /* Built lazily: COMMON_RACES / COMMON_CLASSES are assigned further
       down this same IIFE, so a load-time build would read undefined. */
    if(!_hfCanonRace){
      _hfCanonRace = {};
      _hfCanonClass = {};
      try {
        (COMMON_RACES || []).forEach(function(r){ _hfCanonRace[String(r).toLowerCase()] = 1; });
        (COMMON_CLASSES || []).forEach(function(k){ _hfCanonClass[String(k).toLowerCase()] = 1; });
      } catch(_){ _hfCanonRace = null; return false; }
    }
    var race = (c.race == null) ? '' : String(c.race).trim();
    var cls  = (c.character_class == null) ? '' : String(c.character_class).trim();
    /* Missing data is not an orphan. */
    var raceOdd = race !== '' && !_hfCanonRace[race.toLowerCase()];
    var clsOdd  = cls  !== '' && !_hfCanonClass[cls.toLowerCase()];
    return raceOdd || clsOdd;
  }

  /* The one gate both pool builders call for every pred defined above.
     Returns TRUE when the character passes (or when the pred is not one
     of ours, so the caller's own legacy chain still runs). */
  function hfSharedPredOk(pred, c){
    if(pred === 'merchantsMark') return hfHasMerchantsMark(c);
    if(pred === 'orphans')       return hfIsOrphanChar(c);
    return true;
  }
  function getDismissed(){
    try { var v = JSON.parse(localStorage.getItem(DISMISSED_KEY) || '[]'); return Array.isArray(v) ? v : []; } catch(e){ return []; }
  }
  /* Returns the number of hearts the user has given this character on
     this device (reads the same hoh-hearts map the gallery writes).
     Used to mark Fresh Face cards with the hearted state and to show
     a small "x N" chip when the count is > 1. */
  /* R148-Z28 perf: Cache the hoh-hearts parse for the duration of one
     microtask. getMineCount fires inside per-character loops in
     unseenList / themePoolSize / heartedList — without the cache,
     each character pays a localStorage.getItem + JSON.parse. With the
     cache, the whole rail's worth of lookups becomes one parse plus a
     dictionary read per character. The cache invalidates on the next
     microtask (Promise.resolve) so subsequent renders see fresh state,
     and any hoh:hearts-bumped event explicitly invalidates immediately. */
  var _mineCacheMap = null;
  var _mineCacheScheduled = false;
  function _invalidateMineCache(){ _mineCacheMap = null; _mineCacheScheduled = false; }
  try {
    document.addEventListener('hoh:hearts-bumped', _invalidateMineCache);
  } catch(_){}
  function getMineCount(id){
    if(_mineCacheMap === null){
      try {
        var raw = localStorage.getItem('hoh-hearts');
        _mineCacheMap = raw ? (JSON.parse(raw) || {}) : {};
      } catch(_){ _mineCacheMap = {}; }
      /* Schedule one microtask invalidation so calls in the same tick
         share the parse but the next render frame re-reads. */
      if(!_mineCacheScheduled){
        _mineCacheScheduled = true;
        try { Promise.resolve().then(_invalidateMineCache); } catch(_){ setTimeout(_invalidateMineCache, 0); }
      }
    }
    var v = _mineCacheMap[id];
    return v ? (parseInt(v, 10) || 0) : 0;
  }
  function isCharHearted(id){ return getMineCount(id) > 0; }
  /* ================================================================ */
  /* R148-Z26 Round 4: Inventory state                                 */
  /* ================================================================ */
  /* Cross-day persistent inventory map.
     R150-Z45: STACKABLE model. Each key holds an ARRAY of instances —
       o[key] = [ {acquiredAt}, {acquiredAt}, ... ]
     so a player who buys the same shop item twice ends up holding two
     of it (count = array length). The previous flat model stored
       o[key] = true | {acquiredAt}
     which meant a second purchase silently overwrote the first — the
     player paid hearts and got nothing. _invRead migrates the legacy
     shape to a 1-element array on first read so existing pouches are
     preserved.
     Slots:
       lonefireCharm   — one-shot heart-loss interceptor (stackable)
       scrollOfLineage — free hero unlock coupon (stackable)
       patronsOath     — 5-day claim; each instance has its own timer
     Stored in 'hoh-inventory-v1'. Exposed via window.hohInventory*
     helpers so any module (your-hall, tickets, header) can read,
     count, and consume. These are RELICS — a tiny RPG pouch beneath
     the wallet pill. */
  var INVENTORY_KEY = 'hoh-inventory-v1';
  function _invRead(){
    try {
      var raw = localStorage.getItem(INVENTORY_KEY);
      var o = raw ? JSON.parse(raw) : null;
      if(!o || typeof o !== 'object') o = {};
      /* R150-Z45: Migrate the legacy flat model to the stackable
         array-of-instances model. Idempotent — keys already holding
         an array are left untouched. true → [{}], {obj} → [obj],
         anything falsy → dropped. Persists the migration so it runs
         only once per device. */
      var migrated = false;
      for(var k in o){
        if(!o.hasOwnProperty(k)) continue;
        var v = o[k];
        if(Array.isArray(v)) continue;
        if(v === true){ o[k] = [{}]; migrated = true; }
        else if(v && typeof v === 'object'){ o[k] = [v]; migrated = true; }
        else { delete o[k]; migrated = true; }
      }
      if(migrated){
        try { localStorage.setItem(INVENTORY_KEY, JSON.stringify(o)); } catch(_){}
      }
      return o;
    } catch(_){ return {}; }
  }
  function _invWrite(o){
    try { localStorage.setItem(INVENTORY_KEY, JSON.stringify(o || {})); } catch(_){}
  }
  window.hohInventoryGet = function(){ return _invRead(); };
  window.hohInventoryHas = function(key){
    var o = _invRead();
    var arr = o[key];
    return Array.isArray(arr) && arr.length > 0;
  };
  /* R150-Z45: How many of an item the player holds. 0 when none. */
  window.hohInventoryCount = function(key){
    var o = _invRead();
    var arr = o[key];
    return Array.isArray(arr) ? arr.length : 0;
  };
  window.hohInventoryAdd = function(key, payload){
    var o = _invRead();
    if(!Array.isArray(o[key])) o[key] = [];
    /* Every instance carries an acquiredAt so timed items (the Oath)
       each track their own countdown independently. */
    o[key].push(payload || { acquiredAt: Date.now() });
    _invWrite(o);
    try { document.dispatchEvent(new CustomEvent('hoh:inventory-changed', { detail: { added: key, count: o[key].length } })); } catch(_){}
  };
  window.hohInventoryConsume = function(key){
    var o = _invRead();
    var arr = o[key];
    if(!Array.isArray(arr) || arr.length === 0) return false;
    /* FIFO — spend the OLDEST instance first. For the Oath this is
       the instance closest to (or already) claimable, which is what
       the player expects when they claim. */
    arr.shift();
    if(arr.length === 0) delete o[key];
    else o[key] = arr;
    _invWrite(o);
    try { document.dispatchEvent(new CustomEvent('hoh:inventory-changed', { detail: { consumed: key, count: arr.length } })); } catch(_){}
    return true;
  };
  /* Patron's Oath is the only timed inventory item. Returns null if
     none owned, otherwise { acquiredAt, daysHeld, claimable, count }.
     R150-Z45: With stacking, the player can hold several Oaths, each
     with its own timer. The array is FIFO (oldest first), and the
     oldest Oath has the most days held — so it's always the one
     closest to claimable. We report that one's status plus the total
     count so the header chip can render "Oath ×3 · 2d left". */
  window.hohInventoryOathStatus = function(){
    var o = _invRead();
    var arr = o && o.patronsOath;
    if(!Array.isArray(arr) || arr.length === 0) return null;
    var oath = arr[0];
    var acquired = (oath && parseInt(oath.acquiredAt, 10)) || 0;
    if(!acquired) return null;
    var now = Date.now();
    var elapsed = now - acquired;
    var dayMs = 24 * 60 * 60 * 1000;
    var daysHeld = Math.floor(elapsed / dayMs);
    return {
      acquiredAt: acquired,
      daysHeld: daysHeld,
      claimable: daysHeld >= 5,
      count: arr.length
    };
  };
  /* Consume the Oath and credit +500 hearts. Returns true on success.
     Callers should refresh the header counter + repaint inventory. */
  window.hohInventoryClaimOath = function(){
    var status = window.hohInventoryOathStatus();
    if(!status || !status.claimable) return false;
    /* R268: the heart wallet is capped at HEART_BANK_MAX (9999 in
       hoh-section-core.js) and hohAddHeartsToBank SILENTLY CLAMPS any overflow.
       Without this guard, a nearly-full wallet would swallow the +500 and the
       player loses a reward they paid 100 hearts for and held five days. So if
       there is not room for the full payout, DON'T consume the Oath — keep it
       claimable and tell them to make room, so nothing is ever lost. */
    var _bankNow = 0;
    try { _bankNow = (typeof window.heartsRemainingToday === 'function') ? (window.heartsRemainingToday() | 0) : 0; } catch(_){}
    if(_bankNow + 500 > 9999){
      if(typeof window.hohShowHeartToast === 'function'){
        try { window.hohShowHeartToast('Wallet too full', 'Spend some hearts first, then claim your Oath.'); } catch(_){}
      }
      return false;
    }
    window.hohInventoryConsume('patronsOath');
    if(typeof window.hohAddHeartsToBank === 'function'){
      try { window.hohAddHeartsToBank(500); } catch(_){}
    }
    if(typeof window.hohShowHeartToast === 'function'){
      try { window.hohShowHeartToast('Patron’s Oath honored', 'Five days, well kept.'); } catch(_){}
    }
    if(typeof window.hohRenderHeaderCounter === 'function'){
      try { window.hohRenderHeaderCounter(); } catch(_){}
    }
    return true;
  };
  /* Consume the Scroll of Lineage. Exposed for the unlock UI to call;
     returns true if the scroll existed and was burned, false otherwise.
     Callers wrap their unlock RPC in: if(scrollAvailable) consume.
     If false, fall back to charging the normal 100h unlock fee. */
  window.hohConsumeScrollForUnlock = function(){
    if(!window.hohInventoryHas('scrollOfLineage')) return false;
    window.hohInventoryConsume('scrollOfLineage');
    if(typeof window.hohShowHeartToast === 'function'){
      try { window.hohShowHeartToast('Scroll of Lineage burns away', 'The hero joins your hall for free.'); } catch(_){}
    }
    return true;
  };
  /* R148-Z32: Lonefire Charm trigger — fires when the user's wallet
     reaches 0. Listens to the global hoh:hearts-bumped event (which
     every heart-removal path dispatches). Guards: the charm only
     fires for SPEND events (event.detail.spent present), never for
     grants, and only when the post-spend bank is exactly 0. The
     consume happens before the +50 grant so re-entry from the grant
     event won't see a still-owned charm. The 100ms debounce prevents
     two rapid spends both crossing into 0 from triggering twice. */
  var _lonefireFiredAt = 0;
  try {
    document.addEventListener('hoh:hearts-bumped', function(ev){
      try {
        var detail = (ev && ev.detail) || {};
        if(typeof detail.spent !== 'number' || detail.spent <= 0) return;
        if(typeof window.heartsRemainingToday !== 'function') return;
        var bank = window.heartsRemainingToday();
        if(bank !== 0) return;
        if(!window.hohInventoryHas || !window.hohInventoryHas('lonefireCharm')) return;
        var now = Date.now();
        if(now - _lonefireFiredAt < 200) return;
        _lonefireFiredAt = now;
        /* Consume FIRST so the +50 grant event can't re-trigger us. */
        try { window.hohInventoryConsume('lonefireCharm'); } catch(_){}
        if(typeof window.hohAddHeartsToBank === 'function'){
          try { window.hohAddHeartsToBank(50); } catch(_){}
        }
        if(typeof window.hohShowHeartToast === 'function'){
          try { window.hohShowHeartToast('Lonefire Charm ignites', 'Your last embers, and the charm is spent.'); } catch(_){}
        }
        if(typeof window.hohRenderHeaderCounter === 'function'){
          try { window.hohRenderHeaderCounter(); } catch(_){}
        }
        /* Fly the +50 to the wallet pill for visual confirmation. */
        try {
          var pill = document.getElementById('hoh-tk-header');
          if(pill && typeof flyDiceTraderHeartsToPill === 'function'){
            flyDiceTraderHeartsToPill(pill, 12);
          }
        } catch(_){}
      } catch(_){}
    });
  } catch(_){}
  /* In-flight "intent to heart" set. When the user clicks a Fresh
     Face heart we add the id here IMMEDIATELY so subsequent renders
     hide the card even if hohHeart's RPC is still pending and
     getMineCount hasn't bumped yet. Also covers cap-rejected /
     server-slowed cases where hohHeart returns without updating
     localStorage — the card still vanishes from Fresh Faces (the
     user already expressed they don't want to see it again right
     now); a refresh resurfaces unhearted ones. Session-only. */
  var heartIntent = Object.create(null);

  /* ================================================================ */
  /* Daily quest progress — "Explore the Hall"                        */
  /* ================================================================ */
  /* The daily quest used to be Hero's Circle (open the tinder modal,
     heart 20 inside it). It moved to Fresh Faces so engaging with the
     wander rail directly is what counts. Hearts on the main gallery
     do NOT progress this quest; hearts here DO. We track total hearts
     given via Fresh Faces today (duplicates allowed — re-encountering
     a hero in a special event still progresses the quest because the
     user is doing the wandering action).

     The per-10 server-side ticket cap (2/day) operates independently
     against any heart source — that's a server policy. This counter
     is purely the QUEST UI tracker. */
  /* R140-Z: Cache the Fresh Faces container element. Was hit by
     document.getElementById('hoh-fresh-faces') 59+ times across this
     file — every render pass, every paint, every refresh sequence. The
     element exists for the page's lifetime so a single lookup with an
     isConnected check covers the rare DOM-replacement case (e.g. soft
     navigation, theme editor preview).
     R140-Z3f: CRITICAL FIX. The bulk find/replace that introduced this
     cache accidentally rewrote the call inside the helper itself, so
     `_ffContainer()` recursed forever and threw a stack overflow on
     every render attempt. The body now calls document.getElementById
     directly (the only place in the file that should). */
  var _ffContainerCache = null;
  function _ffContainer(){
    if(_ffContainerCache && _ffContainerCache.isConnected) return _ffContainerCache;
    _ffContainerCache = document.getElementById('hoh-fresh-faces');
    return _ffContainerCache;
  }
  /* R140-Z: Memoize ymdToday + getFfHeartsToday so hot render paths don't
     hit Date.toISOString or localStorage.getItem on every call. ymd is
     cached for 30s (auto-refreshes if a session crosses midnight); the
     hearts count is cached against the day key and invalidated on every
     bump call. The cache is invisible to callers — same shape, same
     return values. */
  var _ymdTodayCache = '';
  var _ymdTodayCacheTs = 0;
  function ymdToday(){
    var now = Date.now();
    if(_ymdTodayCache && (now - _ymdTodayCacheTs) < 30000) return _ymdTodayCache;
    try {
      _ymdTodayCache = new Date().toISOString().slice(0,10);
      _ymdTodayCacheTs = now;
      return _ymdTodayCache;
    } catch(_){ return ''; }
  }
  /* ============================================================
     v39 (Noam, by email): "the clock ticked over and the hall didn't open,
     I still had the wait-until-dawn message with the clock reset".
     NOTHING IN THIS THEME EVER NOTICED THE DAY ROLLING OVER.
     The closed panel's timer rewrote its countdown string every 30 seconds and
     never once compared the day it was rendered for against the day it is now.
     paintQuestProgress did the same. hohRenderFreshFaces, which is the thing
     that actually swaps the shut panel back to the open one, runs only from
     host mount points and explicit taps, never on a timer, and no focus,
     pageshow or visibilitychange handler anywhere re-checked it either.
     So at UTC midnight with the tab open the panel stayed shut and
     _wanderResetCountdownText started counting to the NEXT midnight, which
     reads exactly as Noam described: still closed, clock back to 24 hours.
     A reload always fixed it. He had no reason to think it would.
     NOT a timezone bug, which was the first guess and it was wrong: ymdToday
     is toISOString, the countdown targets Date.UTC, and the server keys on
     (now() at time zone 'utc')::date. All three already agreed.
     This is the one watcher. It costs one comparison every 30 seconds, does
     nothing at all while the tab is hidden, and catches up the instant the tab
     comes back, which is the case that actually matters on a phone left
     overnight. ============================================================ */
  var _dayWatchLast = '';
  var _dayWatchIv = null;
  function _dayWatchTick(){
    if(document.hidden) return;
    /* read the clock DIRECTLY, not through ymdToday. That helper holds its
       answer for 30 seconds, so a cache filled at 23:59:45 would still be
       reporting yesterday at 00:00:15 and the watcher would not see the change
       until its second tick. The whole job of this function is to be the one
       thing that notices, so it does not get to look at a stale copy. */
    var d = '';
    try { d = new Date().toISOString().slice(0,10); } catch(_){ return; }
    if(!d) return;
    if(!_dayWatchLast){ _dayWatchLast = d; return; }
    if(d === _dayWatchLast) return;
    _dayWatchLast = d;
    /* every cache below is keyed on the day that just ended, so they are
       cleared BEFORE anything repaints or the new day reads yesterday's totals */
    try { _ymdTodayCache = ''; _ymdTodayCacheTs = 0; } catch(_){}
    try { _ffHeartsCache = -1; _ffHeartsCacheKey = ''; } catch(_){}
    try { if(typeof window.hohRenderFreshFaces === 'function') window.hohRenderFreshFaces(); } catch(_){}
    try { if(typeof window.hohPaintBgDoor === 'function') window.hohPaintBgDoor(); } catch(_){}
    try { document.dispatchEvent(new CustomEvent('hoh:day-rolled', { detail:{ ymd: d } })); } catch(_){}
  }
  window.hohDayRolloverCheck = _dayWatchTick;
  try {
    if(!_dayWatchIv) _dayWatchIv = setInterval(_dayWatchTick, 30000);
    /* the phone case: backgrounded at 11pm, opened at 8am. A 30 second poll
       would leave a stale, wrong screen on the first thing they look at. */
    document.addEventListener('visibilitychange', function(){
      if(!document.hidden) { try { _dayWatchTick(); } catch(_){} }
    });
    window.addEventListener('pageshow', function(){ try { _dayWatchTick(); } catch(_){} });
  } catch(_){}

  function ffHeartsKey(){ return 'hoh-ff-hearts-' + ymdToday(); }
  var _ffHeartsCache = -1;
  var _ffHeartsCacheKey = '';
  function getFfHeartsToday(){
    try {
      var key = ffHeartsKey();
      if(_ffHeartsCacheKey === key && _ffHeartsCache >= 0) return _ffHeartsCache;
      var raw = localStorage.getItem(key);
      var n = parseInt(raw || '0', 10);
      var v = isNaN(n) ? 0 : n;
      _ffHeartsCache = v;
      _ffHeartsCacheKey = key;
      return v;
    } catch(_){ return 0; }
  }
  function bumpFfHeartsToday(){
    try {
      var bKey = ffHeartsKey();
      var before = getFfHeartsToday();
      /* The target is terminal. Once the fiftieth Explore heart lands, later
         taps must not keep minting page rewards while milestone claims are
         settling asynchronously. Clamp the ledger and drive the close path. */
      if(before >= WANDER_QUEST_TARGET){
        _beginWanderCompletion();
        return false;
      }
      var n = Math.min(WANDER_QUEST_TARGET, before + 1);
      localStorage.setItem(bKey, String(n));
      /* Keep the cache hot — next call reads memory, not localStorage. */
      _ffHeartsCache = n;
      _ffHeartsCacheKey = bKey;
      /* Notify other surfaces (earn-tickets pop, header pill) that the
         quest progress changed so they can re-paint without polling. */
      try {
        document.dispatchEvent(new CustomEvent('hoh:ff-quest-progress', {
          detail: { count: n, target: WANDER_QUEST_TARGET }
        }));
      } catch(_){}
      /* Update the in-panel bar and check for milestone crossings. */
      paintQuestProgress();
      tryFireMilestone(n);
      if(n >= WANDER_QUEST_TARGET) _beginWanderCompletion();
      return true;
    } catch(_){ return false; }
  }
  /* R111-E: expose the bump as a global so any heart-handling surface
     (gallery / Familiar Faces / detail modal) can advance the Explore
     the Hall bar — not just hearts cast inside #hoh-fresh-faces. The
     bar then reflects every heart given today, regardless of source.
     Fresh-faces own handlers still call bumpFfHeartsToday() directly
     for their pre-RPC optimistic bump; section-core's hohHeart calls
     this global ONLY when the click was NOT from a Fresh Faces card
     (so the same click never double-counts). */
  window.hohFreshFacesProgressFromAnyHeart = function(){
    try { return bumpFfHeartsToday(); } catch(_){ return false; }
  };
  /* R56: each ticket pip grants 1 raffle ticket and the last one also pays
     today's Battlegrounds entry plus a confetti celebration. Checkpoints may
     be claimed by hand while exploring; reaching the daily target collects
     every unclaimed reward automatically, banks the final pack, and closes
     Explore for the day. R406 moved the ladder; see the block below. */
  /* ============================================================
     R406 (Jan): THE LADDER SLID LEFT AND THE FIRST PIP IS FREE.

     The day used to open with a debt. You had to heart ten heroes
     before the bar paid you anything, so a first-time visitor met a
     row of locked pips and a task. Now the ladder starts at ZERO with
     a blue heart pip sitting on the very start of the rail, already
     pulsing, already claimable. One tap and 20 hearts fly to the
     wallet before the user has done a single thing. The reward comes
     before the effort, which is the whole point.

     And the finish moved from 60 to 50. The 50-to-60 stretch was the
     flattest part of the day and it sat in front of the Battlegrounds
     entry, so the prize most worth having was behind the least
     interesting ten hearts. The ticket now lands on 50.

       0  → +20 hearts        (blue, free, claimable on arrival)
       10 → ticket
       20 → +30 hearts        (blue)
       30 → ticket
       40 → +50 hearts        (purple capstone bonus)
       50 → ticket + today's Battlegrounds entry + confetti

     Every reward kept its seat and its value. The ladder just slid
     ten hearts left. Still 3 wander tickets, still 100 bonus hearts,
     still a daily ceiling of 4 tickets (1 submit + 3 wander). */
  /* R793 (Jan, 2026-09-22): Explore is 20 refreshes a day now, so the
     ladder is squeezed to fit 20 with the same six pips in the same order
     and the same payouts:
       0 → +20 hearts, 4 → ticket, 8 → +30 hearts, 12 → ticket,
       16 → +50 hearts (capstone), 20 → ticket + Battlegrounds entry
     Nothing about the SLOT names moves (WANDER_SLOT below binds each pip by
     its position), so a player mid-day when this ships keeps every pip.
     This ladder is what LEGACY mode (the R793B doors not installed) runs;
     the live Hall's Explore is the one-hero-a-refresh event at the end of
     this file, whose 20th hero pays the entry from the server. */
  var WANDER_QUEST_TARGET = 20;
  var WANDER_MILESTONES = [0, 4, 8, 12, 16, 20];
  /* Heart-bonus milestones with their reward amounts. Anything in
     WANDER_MILESTONES NOT in this map is a ticket milestone.
     NOTE the key 0 is a real entry, so every read of this map must
     use `WANDER_HEART_BONUSES[n]` and never a truthiness test on n. */
  var WANDER_HEART_BONUSES = { 0: 20, 8: 30, 16: 50 };
  var WANDER_FINAL_MILESTONE = 20;
  /* The purple pip. Was 50 under the old ladder, is 40 under this one.
     Drives the heart-fly colour and the capstone glow. */
  var WANDER_CAPSTONE_MILESTONE = 16;

  /* ============================================================
     R406: THE SLOT MAP, AND WHY THE THRESHOLDS DO NOT DRIVE IT.

     Every claim writes to the server under a slot string built from
     the milestone. Two unique indexes key off those strings:
       daily_user_claims  UNIQUE (email, day, milestone)  via claim_daily_milestone
       ticket_grants_log  UNIQUE (email, ymd, reason)     via grant_daily_ticket
     If the slot names followed the new thresholds, deploy day would
     eat people's rewards. Someone who already claimed the old 10 pip
     holds `wander_10` on the server; the new 10 pip is a TICKET, so
     their ticket would come back "already claimed today" and vanish.
     Someone reaching the new 50 would write `wander_50`, which the old
     ladder spent on a heart bonus, and the ticket grant would collide
     with a row that already exists.

     So the slots do not move. Each pip binds to a stable id by its
     POSITION in the ladder, which happens to be the old threshold.
     This is not a fudge — pip 0 pays exactly what old pip 10 paid, so
     the slot genuinely names the same reward. The upshot: no SQL, no
     worker change, and a user who is mid-day when the theme flips
     keeps every pip they earned and loses nothing.

     The local claim flag is keyed on the slot too (hoh-ff-claim-{ymd}-
     {slot}), which is what lets hoh-tickets.js keep speaking in slot
     names for the cross-device sync and the wallet ledger without a
     single functional change over there. */
  /* R793: same six slots, same order; only the thresholds that point at
     them moved (0/4/8/12/16/20). */
  var WANDER_SLOT = { 0: 10, 4: 20, 8: 30, 12: 40, 16: 50, 20: 60 };
  function wanderSlot(n){
    var s = WANDER_SLOT[n];
    return (s === undefined) ? n : s;
  }
  /* The three ticket pips, in order, for anything that needs to talk
     about "the first ticket" without hardcoding a number. */
  var WANDER_TICKET_MILESTONES = WANDER_MILESTONES.filter(function(m){
    return WANDER_HEART_BONUSES[m] === undefined;
  });

  /* ================================================================ */
  /* Per-page heart allowance                                         */
  /* ================================================================ */
  /* Each "page" of Fresh Faces (a normal phase or a content special
     event) gets a budget of hearts the user can spend on THIS page
     before it auto-refreshes. The budget is `ceil(visibleCount / 4)`
     so a 20-card tribe gives 5, a 10-card phase gives 3, and a 3-card
     epic gives 1. The user's heart wallet caps the budget — if they
     have 2 in the bank but the page allows 5, they can spend 2; the
     UI surfaces both numbers so the user knows what's available
     versus what they could have spent. When the spent count hits the
     budget, we auto-refresh into a new page so the user keeps moving. */
  var PER_PAGE_HEART_RATIO = 4;
  var pageHearts = { spent: 0, max: 0, budget: 0, key: '' };
  /* R148-Z10: Tracks whether the per-page completion reward has
     already fired for the current page. Reset whenever pageHearts.key
     changes (new page = new chance to earn the reward). Without this
     flag, multiple late-arriving spend events on the same page could
     re-trigger the reward and double-grant hearts. */
  var pageCompletionRewardFired = false;

  function recomputePageHearts(visibleCount, pageKey){
    var bank = (typeof window.heartsRemainingToday === 'function') ? window.heartsRemainingToday() : 999;
    /* If the page key changed, this is a NEW page. Reset spent +
       lock max from the visible count once. Subsequent re-renders
       within the same page key keep `max` stable so the counter
       doesn't jump as cards animate. Wallet refills bump only the
       budget (bank cap), not the max. */
    if(pageHearts.key !== pageKey){
      /* R148-Z22g: ONE HEART PER ROW. Per user direction —
         "spent hearts needs to always be 1 per row" for normal
         themed/wandering rolls. Bustling Streets and other 5-heart
         special events have their OWN budget set via
         currentSpecial.budget directly into pageHearts.max (see
         line 8544 in renderSpecial for streets — it bypasses
         recomputePageHearts entirely), so this formula does NOT
         apply to them.
         Implementation: divide visibleCount by current cards-per-row
         and ceil. Yields:
            Desktop (5/row), 10 cards → 2 rows → 2 hearts
            Desktop (5/row),  5 cards → 1 row  → 1 heart
            Tablet  (4/row),  8 cards → 2 rows → 2 hearts
            Mobile  (2/row),  ANY    → HARD CAP at 1 heart (see below)
         R148-Z22o: HARD MOBILE CAP = 1. Per user — "on mobile each
         type 1 roll other than bustling streets should only be 1
         heart". The 2-per-row mobile grid means 4 cards = 2 rows =
         2 hearts under the per-row rule, but the user wants mobile
         to feel tighter (one-tap-and-done discovery) regardless of
         visible count. Special events that pass their charCount in
         (legendary / twoLegends / championMeeting / epic / spectrum
         / familiar / tribe — all the "feature one" event types the
         user called out) all flow through THIS function, so capping
         here covers them too. Bustling Streets' direct
         pageHearts.max assignment in renderSpecial is unaffected
         because it never calls recomputePageHearts.
         cardsPerRowApprox is defined later in the same IIFE (line
         ~15629); function declarations are hoisted so calling it
         here works. Wrapped in try/catch as belt-and-suspenders. */
      var max;
      if(visibleCount <= 0){
        max = 0;
      } else {
        var rowSize = 5;
        try {
          if(typeof cardsPerRowApprox === 'function'){
            var r = cardsPerRowApprox() | 0;
            if(r > 0) rowSize = r;
          }
        } catch(_){}
        max = Math.max(1, Math.ceil(visibleCount / rowSize));
        /* Cap by visibleCount so a single-card phase doesn't claim
           an allowance the user could never spend. */
        max = Math.min(max, visibleCount);
        /* R335: MOBILE IS ONE HEART. NOT TWO.
           The per-row rule is arithmetically right and behaviourally wrong on
           a phone. Desktop shows 5 across, so a 4-card roll is one row and
           costs one heart. Mobile shows 2 across, so the SAME roll becomes
           two rows and quietly costs double — the player pays double for the
           identical set of four heroes purely because their screen is narrow.
           R233's guard was written as Math.min(max, 2), which for a 4-card
           grid clamps 2 down to... 2. It never did anything, and it stranded
           the R148-Z22o comment above that already stated the intent: one
           heart per roll on mobile. This is that cap, actually applied. */
        if(rowSize <= 2) max = 1;
      }
      pageHearts.spent = 0;
      pageHearts.key = pageKey;
      pageHearts.max = max;
      /* R148-Z10: New page → reward becomes claimable again. */
      pageCompletionRewardFired = false;
      /* R148-Z11: Defensive strip — paintPageHearts updates innerHTML
         on the same element, so the reward-complete class can persist
         across an auto-refresh if the timeout hasn't fired yet. Force-
         remove it here on every new page so purple-flash NEVER bleeds
         into the next roll. */
      try {
        var prevPill = document.getElementById('hoh-fresh-page-hearts');
        if(prevPill){
          prevPill.classList.remove('hoh-fresh-page-hearts-complete');
          prevPill.removeAttribute('data-reward');
        }
      } catch(_){}
      /* Drop stale per-page consumed bucket — only keep the current
         page's set so memory doesn't grow forever in a long session. */
      consumedThisPage = Object.create(null);
      /* Drop the per-batch heart-counted set too. Each new page
         starts with an empty Set, so a hero hearted on a previous
         batch but re-rendered now (e.g. Familiar Faces) will count
         as a fresh first-click for batch allowance purposes. */
      pageHeartsCountedSet = Object.create(null);
      /* New page accepted — clear the auto-refresh flag so the next
         exhaust on this page can schedule its own refresh. */
      pageAutoRefreshScheduled = false;
      /* Scale the auto-refresh base by row count so larger pages
         get more time. Recomputed only on phase change so the bar
         doesn't speed up/slow down mid-page from spurious renders. */
      AUTO_REFRESH_MS = computeAutoRefreshMs(visibleCount);
    }
    /* Budget = min(max, bank + spent). See pageHeartsRecordSpend
       comment for why we add `spent` — it makes the budget reflect
       the wallet AT PAGE START so on-page spends don't shrink the
       gate, while external spends still do. On the initial paint
       within a new page, `spent` is 0 so this is just min(max, bank);
       on re-renders mid-page, the formula keeps the gate stable. */
    pageHearts.budget = Math.min(pageHearts.max, Math.max(0, bank + (pageHearts.spent || 0)));
    paintPageHearts();
  }

  function paintPageHearts(){
    var el = document.querySelector('#hoh-fresh-page-hearts');
    if(!el) return;
    var max = pageHearts.max;
    var budget = pageHearts.budget;
    var remaining = Math.max(0, budget - pageHearts.spent);
    /* Hide if budget is irrelevant (e.g., zero visible chars). */
    if(!max){ el.style.display = 'none'; return; }
    el.style.display = '';
    var heartIcon = '<span class="hoh-fresh-page-hearts-icon" aria-hidden="true">&#9829;&#xFE0E;</span>';
    /* Three states for the empty-budget case:
         1. Wallet is empty (budget came up 0 because user has 0
            hearts in bank): "Out of hearts" — clickable, opens
            the Daily Tickets modal so the user can earn more.
         2. Page budget exhausted but wallet still has hearts:
            "Spent here" — the user spent the page's allowance
         3. Has remaining: "X hearts to give"
       The wallet-empty branch is what the user explicitly asked for
       so the wording matches reality (you didn't spend here, you
       have nothing to spend). */
    var bank = (typeof window.heartsRemainingToday === 'function') ? window.heartsRemainingToday() : 1;
    var label;
    var walletEmpty = bank <= 0;
    if(remaining > 0){
      /* R52: "X heart(s) to give here" — the original "heart here" was
         too terse and users didn\'t parse it as a per-page spend
         allowance. "to give" anchors the verb so the meaning ("you can
         give this many on this page before moving on") reads cleanly. */
      /* R421 (Jan): SAY THE TRADE. "2 hearts to give here" read as a pure
         cost; nobody knew completion pays back MORE than it asks. When the
         wallet can finish the page, the pill states the whole deal; when it
         cannot (budget clipped below max), the old cost-only wording stays,
         because promising a payout the completion gate will refuse is worse
         than saying nothing. */
      var _backN = (pageHearts.max | 0) + 1;
      if(pageHearts.budget >= pageHearts.max){
        label = (remaining === pageHearts.max)
          ? 'Give ' + remaining + ' \u00b7 get ' + _backN + ' back'
          : remaining + ' more \u00b7 ' + _backN + ' back';
      } else {
        var verb = remaining === 1 ? 'heart to give here' : 'hearts to give here';
        label = remaining + ' ' + verb;
      }
    } else if(walletEmpty){
      label = 'Out of hearts';
    } else {
      label = 'Spent here';
    }
    el.innerHTML = heartIcon +
      '<span class="hoh-fresh-page-hearts-num">' + label + '</span>';
    /* R314: when this batch's allowance is gone, mark the whole section spent so
       the hero cards stop taking clicks entirely (CSS dims them and kills
       pointer events). Previously they stayed live and answered a click with a
       red shake, and the gap before the auto-refresh let a third hero be tapped.
       Making them inert closes that window instead of scolding the user. */
    try {
      var _spentC = _ffContainer();
      if(_spentC) _spentC.classList.toggle('hoh-fresh-page-spent', remaining <= 0 && !walletEmpty);
    } catch(_){}
    /* R217: soft bump ONLY when the label actually changes, so the count updates
       smoothly instead of the pill vanishing or popping on a refresh. */
    try {
      var _prevPh = el.getAttribute('data-hoh-ph-label');
      if(_prevPh !== null && _prevPh !== label){
        el.classList.remove('hoh-fresh-page-hearts-bump');
        void el.offsetWidth;
        el.classList.add('hoh-fresh-page-hearts-bump');
      }
      el.setAttribute('data-hoh-ph-label', label);
    } catch(_){}
    /* The per-batch tooltip is set below in the !walletEmpty branch
       (after the existing remove-then-rebuild pattern) so it survives
       the wallet-empty handling that strips other attributes. */
    if(remaining <= 0) el.classList.add('hoh-fresh-page-hearts-empty');
    else el.classList.remove('hoh-fresh-page-hearts-empty');
    /* Click target — only when the wallet is fully empty. We point
       the user at the Daily Tickets modal so they have an immediate
       path to earning more hearts (submit a hero, solve the bounty,
       Explore the Hall). The "Spent here" state is informational — no
       click action because the user just needs to wait for the next
       page (auto-refresh handles that). We rebind every paint to
       avoid stale closures. */
    if(walletEmpty){
      el.classList.add('hoh-fresh-page-hearts-clickable');
      el.setAttribute('role', 'button');
      el.setAttribute('tabindex', '0');
      el.setAttribute('title', 'Open Daily Tickets to earn more hearts');
      el.onclick = function(){
        if(typeof window.hohOpenEarnTickets === 'function'){
          try { window.hohOpenEarnTickets(); } catch(_){}
        }
      };
      el.onkeydown = function(e){
        if(e.key === 'Enter' || e.key === ' '){
          e.preventDefault();
          if(typeof window.hohOpenEarnTickets === 'function'){
            try { window.hohOpenEarnTickets(); } catch(_){}
          }
        }
      };
    } else {
      el.classList.remove('hoh-fresh-page-hearts-clickable');
      el.removeAttribute('role');
      el.removeAttribute('tabindex');
      el.onclick = null;
      el.onkeydown = null;
      /* Keep the per-batch tooltip set above — only swap it when
         the wallet flips empty (handled in the if-branch). */
      if(remaining > 0){
        el.setAttribute('title', 'Hearts you can spend on this batch. Refresh to find a new one.');
      } else {
        el.setAttribute('title', 'You spent this batch’s allowance. Refresh to find a new one.');
      }
    }
  }

  function pageHeartsAllowSpend(){
    /* Returns false if the page budget is exhausted OR the user's
       wallet is empty. Caller still needs to verify wallet > 0
       (existing flow does). */
    return pageHearts.spent < pageHearts.budget;
  }
  /* Public read API. Returns the current per-batch state so other
     modules (the detail-modal heart path in hoh-section-core) can
     gate against the same allowance the small-card heart click
     respects. Without this, a user with "Spent here" on the chip
     could still bypass the page allowance by hearting from the
     detail modal. The return shape is intentionally a plain object
     so callers don't share a reference to mutable state. */
  window.hohFreshFacesGetPageAllowanceState = function(){
    return {
      spent: pageHearts.spent || 0,
      max: pageHearts.max || 0,
      budget: pageHearts.budget || 0,
      key: pageHearts.key || '',
      remaining: Math.max(0, (pageHearts.budget || 0) - (pageHearts.spent || 0))
    };
  };
  /* Public read API for the per-batch idempotence set. Returns true
     if this character has already counted toward the page allowance
     this batch (i.e. a re-click on this card is "free" and shouldn't
     be blocked by the allowance gate). Used by the detail modal
     heart path so fractional Epic clicks aren't rejected. */
  window.hohFreshFacesIsCountedThisBatch = function(id){
    if(!id) return false;
    return !!(pageHeartsCountedSet && pageHeartsCountedSet[id]);
  };
  /* Single flag so the budget-exhaust path AND the all-consumed
     path can't BOTH schedule a refresh and double-trigger. The
     flag clears in recomputePageHearts when the new page key is
     accepted. */
  var pageAutoRefreshScheduled = false;
  function schedulePageAutoRefresh(delayMs){
    if(pageAutoRefreshScheduled) return;
    pageAutoRefreshScheduled = true;
    var d = delayMs || 800;
    /* R132-C: Fade-out transition on the way to refresh. The cards
       smoothly fade + slightly translate up before the refresh fires,
       which prevents the user from clicking dead cards in the gap
       between "allowance exhausted" and "new batch rendered". The
       fade lasts 380ms; we time the refresh to hit just after it
       completes so the new batch enters fresh and animates in via
       its existing reveal CSS.
       The container is the inner cards wrap inside #hoh-fresh-faces.
       We tag both the wrapper AND #hoh-fresh-faces itself so the
       CSS rule can target whatever the current render style is
       (themed-phase wrappers vs the standard rail). */
    var FADE_MS = 380;
    try {
      var ff = _ffContainer();
      if(ff){
        ff.classList.add('hoh-fresh-fading-out');
      }
    } catch(_){}
    /* R148-Z22e: SAFETY TIMER. Belt + suspenders. The double-rAF
       removal below works most of the time but can silently fail
       when:
         - The browser throttles rAF (tab backgrounded mid-transition)
         - _ffContainer's cache invalidates mid-flow (element
           re-mounted by an unrelated render)
         - hohFreshFacesRefresh throws inside its inner pipeline
       Any of those leaves .hoh-fresh-fading-out STUCK on the
       container, which makes EVERY subsequent render paint at
       opacity:0 (cards exist, clickable through the gap in CSS
       coverage, but invisible). The "veterans roll all invisible"
       bug is exactly this state.
       The safety timer fires 1800ms after the class is added —
       longer than any legitimate fade window (FADE_MS=380 + total
       delay ~900 = ~1280) — and unconditionally strips the class
       from a freshly-queried container. If the normal path already
       cleaned up, this is a no-op. */
    setTimeout(function(){
      try {
        var ffSafety = document.getElementById('hoh-fresh-faces');
        if(ffSafety && ffSafety.classList.contains('hoh-fresh-fading-out')){
          ffSafety.classList.remove('hoh-fresh-fading-out');
        }
      } catch(_){}
    }, 1800);
    /* Adjust delay so the refresh lands AFTER the fade completes,
       not before. If the caller passes a delay shorter than the fade,
       use the fade duration instead so the visual doesn't get cut. */
    var totalDelay = Math.max(d, FADE_MS + 40);
    setTimeout(function(){
      /* R148-Z19: ATOMIC fade-then-swap. The previous flow removed the
         fading-out class BEFORE calling hohFreshFacesRefresh, which
         created a flicker: the old (faded) content would start
         transitioning back to opacity:1, then innerHTML would replace
         with new content that snapped in instantly. The user described
         this as "1 type 1 roll loading then getting replaced by
         another" and "a random card disappearing".
         New flow:
           1. Keep the fading-out class active across the DOM swap.
           2. Run hohFreshFacesRefresh — new content renders INTO the
              already-faded container, so it's invisible at first.
           3. Double-rAF the class removal so the new content paints
              once at opacity:0, then transitions to opacity:1 over
              360ms via the existing CSS transition.
         Result: a clean cross-fade. Old content fades out, new
         content fades in. No flicker between them. */
      /* R140-Z3p + R148-Z12: BUG FIX for stuck "Spent here" on themed
         grids. Root cause: when the picker lands on the SAME themed
         phase after auto-refresh (uniform pool picks it by chance, or
         the recent-buffer cycled out the just-used entry), the
         renderer short-circuits at multiple points and the user sees
         identical content with a re-running auto-bar — looks like the
         timer reset but nothing refreshed.
         R148-Z12 expands the invalidation to cover EVERY cache that
         can return stale content on a same-theme reroll:
           - pageHearts.key      → recomputePageHearts treats render
                                    as a new page (resets spent etc.)
           - renderedSnapshotIds → forces a fresh snapshot of visible
                                    cards instead of reusing the same
                                    IDs that were just exhausted.
           - snapshotPhaseKey    → same snapshot path; trips the
                                    "first render of this phase" branch.
           - cachedWanderingPool → kills the line-2093 short-circuit
                                    in pickListForPhase that returns
                                    the SAME character list when phase
                                    matches the cache.
           - cachedWanderingPhase → companion of the above; must clear
                                    in lockstep.
           - pageCompletionRewardFired → defensive reset so the next
                                    completion can re-trigger the
                                    reward bonus.
           - currentSpecial      → ensure the dispatcher's special-
                                    event branch doesn't see stale
                                    state and bail. */
      try {
        pageHearts.key = null;
        /* R148-Z22s: HARD-RESET pageHearts.spent + the per-character
           counted set + consumedThisPage here, not relying on
           recomputePageHearts to trip its key-changed branch. The
           previous "set key to null and let recompute reset spent"
           dance failed in one specific case: when the refresh
           re-rendered into the SAME theme/phase as before (same
           phaseKey computed by renderInto). Old key=null vs new
           phaseKey=tavern was treated as different → reset fires.
           But syncWalletState (line 1138) and other "soft re-renders"
           pass pageHearts.key BACK in as the pageKey arg, and if
           those run BETWEEN the key=null set and the refresh
           landing, they'd seed pageHearts.key = null → null pageKey
           comparison → no reset. By zeroing spent here directly we
           defend against every path. The next render computes max
           against this zeroed spent and the user sees a clean
           "X hearts to give here" pill. */
        pageHearts.spent = 0;
        pageHeartsCountedSet = Object.create(null);
        try { consumedThisPage = Object.create(null); } catch(_){}
        renderedSnapshotIds = null;
        snapshotPhaseKey = null;
        cachedWanderingPool = null;
        cachedWanderingPhase = null;
        pageCompletionRewardFired = false;
        currentSpecial = null;
      } catch(_){}
      if(typeof window.hohFreshFacesRefresh === 'function'){
        /* R140-Z3n: fromExhaustion flag distinguishes "user finished
           spending the batch / consumed every card" from the idle
           auto-timer firing. The tour gate treats this as a manual-
           equivalent refresh and ADVANCES the tour step.
           R148-Z22s: Wrapped in try/catch so a synchronous throw
           inside the refresh pipeline (deep render bug, picker
           edge case, etc.) can't skip the pageAutoRefreshScheduled
           reset below — that would freeze ALL future auto-refreshes
           until page reload. */
        try {
          window.hohFreshFacesRefresh({ auto: true, fromExhaustion: true });
        } catch(refreshErr){
          try { console.warn('[schedulePageAutoRefresh] refresh threw', refreshErr); } catch(_){}
        }
      }
      /* R140-Z3p: Always clear the scheduled flag AFTER firing so
         subsequent budget-exhaust events on the next page can
         schedule their own refresh. Previously this relied on
         recomputePageHearts seeing a NEW page key — which could
         silently fail (same theme rerolled, or render didn't
         happen). The unconditional clear here makes the flag a
         pure "one refresh in flight" guard, not a state-machine
         dependency. */
      pageAutoRefreshScheduled = false;
      /* R148-Z19: Remove the fading-out class on the NEXT animation
         frame after new content paints. Double-rAF guarantees the
         new DOM has been committed at opacity:0 before we transition
         it back to opacity:1, producing a smooth cross-fade instead
         of the prior flicker. */
      try {
        requestAnimationFrame(function(){
          requestAnimationFrame(function(){
            try {
              var ff3 = _ffContainer();
              if(ff3) ff3.classList.remove('hoh-fresh-fading-out');
            } catch(_){}
          });
        });
      } catch(_){
        /* Fallback for environments without rAF — strip immediately. */
        try {
          var ff3b = _ffContainer();
          if(ff3b) ff3b.classList.remove('hoh-fresh-fading-out');
        } catch(_){}
      }
    }, totalDelay);
  }
  /* Per-batch idempotence: each character can only count ONCE toward
     the page-hearts allowance, no matter how many fractional hearts
     the user gives them. Epic heroes need 3 clicks to fill one heart
     (1/3 each), Legendary need 5 (1/5 each) — without this dedupe,
     each fractional click would tick "2 hearts here" -> "1 here" ->
     "Spent here" instantly, which is wrong. The user expects: first
     click on a card = 1 batch slot, every subsequent click on the
     same card = no further chip change.
     The set is reset whenever recomputePageHearts seeds a new page
     key (same lifecycle as consumedThisPage). */
  var pageHeartsCountedSet = Object.create(null);
  function pageHeartsRecordSpend(charId){
    /* Idempotent: if this character already counted toward the batch
       allowance on this page, skip the increment but still re-derive
       the budget (wallet may have shrunk via the underlying spend). */
    var alreadyCounted = !!(charId && pageHeartsCountedSet[charId]);
    if(charId && !alreadyCounted){
      pageHeartsCountedSet[charId] = true;
      pageHearts.spent++;
    } else if(!charId){
      /* Caller didn't pass an id (legacy callers / unknown source) —
         fall back to the old behavior so we don't lose state. */
      pageHearts.spent++;
    }
    /* Re-derive budget against the live wallet so external spends (a
       detail-modal heart, a daily-cap rejection that refunded the
       wallet, etc.) keep the chip honest. The formula is
       `min(max, bank + spent)` rather than `min(max, bank)` — the
       second form caused the R136 hearts-stuck bug. Walking through it
       with wallet=2, max=3:
         click 1 → gate passes (0<2). spend → bank=1, spent=1.
                   stale formula: budget = min(3, 1) = 1 → next gate
                   check 1<1 = FALSE, red-bounce + stuck.
       The fix uses `bank + spent` because that quantity equals the
       wallet AT PAGE START (modulo external spends): internal spends
       shrink bank by 1 and grow spent by 1, sum stays constant.
       External spends shrink bank without growing spent → budget
       shrinks correctly, exactly the case the original re-derive was
       guarding against. */
    try {
      var bank = (typeof window.heartsRemainingToday === 'function') ? window.heartsRemainingToday() : 999;
      pageHearts.budget = Math.min(pageHearts.max, Math.max(0, bank + pageHearts.spent));
    } catch(_){}
    paintPageHearts();
    /* When the user has spent their full page budget, trigger an
       auto-refresh after a short beat so the heart animation lands
       and the user sees their last card lock in. */
    if(pageHearts.spent >= pageHearts.budget && pageHearts.budget > 0){
      schedulePageAutoRefresh(950);
    }
    /* R148-Z10 + R148-Z14: Per-page completion reward.
         - Type 1 / themed rolls: reward = max - 1, gated on max >= 2
           so mobile single-card pages don't loop.
         - Specific special events (Bustling Streets, Spectrum, Epic
           Encounter) have a per-event reward in the registry inside
           grantPageCompletionReward — they BYPASS the max>=2 gate
           since some are 1-heart pages by design.
       Common gate: spent must reach the FULL max (not just budget),
       so a user who could only afford 1 heart on a 2-heart page
       doesn't get the reward — the message is "complete the page to
       earn". Flag-guarded against double-fire. */
    var key = String(pageHearts.key || '');
    var isRegisteredEvent =
      key === 'special:bustlingStreets' ||
      key === 'special:spectrum' ||
      key === 'special:epic';
    /* R421 (Jan): the max>=2 gate is gone. Mobile pages often hold ONE
       heart, and under the old gate they completed for nothing — with the
       new pay-more-than-it-asks rule the pill would have promised "get 2
       back" on a page that never paid. Every completable page pays now:
       give 1, get 2. */
    var qualifiesForReward = (pageHearts.max >= 1);
    if(!pageCompletionRewardFired
        && qualifiesForReward
        && pageHearts.spent >= pageHearts.max){
      pageCompletionRewardFired = true;
      try { grantPageCompletionReward(); } catch(_){}
    }
    /* R143-C: Belt-and-suspenders refresh trigger. The block above
       only fires when budget > 0 — but themed phases with N<5 cards
       can land at budget=0 (the floor(N/5) formula), and special
       events sometimes set pageHearts.key without seeding a budget.
       In either case, the user can drain their wallet to 0 without
       the page allowance ever reaching exhaustion, and the only
       refresh hook becomes syncWalletState's wallet→0 transition,
       which can miss for a variety of reasons (detail modal deferral
       cleared late, the event listener fired on a stale lastKnownBank,
       a same-themed reroll silently keeping the key, etc.).
       Fix: any time pageHeartsRecordSpend runs AND the wallet is now
       0, schedule a refresh. The schedule itself is idempotent
       (pageAutoRefreshScheduled flag), so it's a no-op if the budget
       path already scheduled one. The 950ms delay matches the budget
       path so animations land before the swap. */
    try {
      var liveBank = (typeof window.heartsRemainingToday === 'function') ? window.heartsRemainingToday() : 999;
      if(liveBank <= 0 && pageHearts.key){
        schedulePageAutoRefresh(950);
      }
    } catch(_){}
  }

  /* R148-Z10: Per-page completion reward. Fires once when a user has
     spent the full page max, which means they actually engaged with
     the roll instead of refreshing past it.
       Type 1 rolls (themed phases including Wandering Heroes):
         pageHearts.key starts with 'themed:', 'wandering:', or the
         empty fresh-faces key — anything NOT 'special:*'. Reward = 1.
       Bustling Streets:
         pageHearts.key === 'special:bustlingStreets'. Reward = 5.
       Other 'special:*' events (events that have their own reward
       mechanics — Brawl, Bazaar, Side Bet, etc.) get NO reward here
       because their gameplay already handles payouts. */
  function grantPageCompletionReward(){
    var key = String(pageHearts.key || '');
    /* R148-Z14: Per-event reward registry. Special events with a fixed
       reward here BYPASS the max>=2 gate at the caller (so 1-heart
       events like Spectrum / Epic Encounter still pay out). Type 1 /
       themed rolls fall through to the scaled formula below. */
    var SPECIAL_REWARDS = {
      'special:bustlingStreets': 5,
      'special:spectrum':        5,
      'special:epic':            3
    };
    var reward = 0;
    if(SPECIAL_REWARDS.hasOwnProperty(key)){
      reward = SPECIAL_REWARDS[key];
    } else if(key.indexOf('special:') !== 0){
      /* R148-Z13: Reward scales with the page's max for Type 1 / themed
         rolls. Formula: max - 1. Always keeps a 1-heart net cost so the
         user has skin in the game, while bigger pages pay back more.
           max=2 → 1 heart back   (spend 2, net cost 1)
           max=3 → 2 hearts back  (spend 3, net cost 1)
           max=4 → 3 hearts back  (spend 4, net cost 1) */
      /* R421 (Jan): the page pays MORE than it asks. max-1 was a quiet net
         loss ("spend 3 get 2") and new players read the whole section as a
         heart sink — the exact opposite of its job. Spend N, get N+1: the
         wander itself is the earn, and the pill above says so out loud. */
      reward = (pageHearts.max | 0) + 1;
    }
    if(reward <= 0) return;
    var pillEl = _ffContainer && _ffContainer()
      ? _ffContainer().querySelector('#hoh-fresh-page-hearts')
      : document.getElementById('hoh-fresh-page-hearts');
    /* 1. Green flash + label swap on the "Hearts to give here" pill
       so the user sees the completion register on the chip they were
       just spending against. The class auto-strips after the keyframe
       finishes via setTimeout. */
    if(pillEl){
      pillEl.classList.add('hoh-fresh-page-hearts-complete');
      try { pillEl.setAttribute('data-reward', '+' + reward); } catch(_){}
      /* R148-Z11: Class strip BEFORE the auto-refresh fires at 950ms.
         750ms gives the 700ms CSS animation just enough room to finish
         then immediately cleans up — no purple-flash bleed into the
         next roll. The defensive strip in recomputePageHearts on key
         change is a belt-and-suspenders backup. */
      setTimeout(function(){
        try { pillEl.classList.remove('hoh-fresh-page-hearts-complete'); } catch(_){}
        try { pillEl.removeAttribute('data-reward'); } catch(_){}
      }, 750);
    }
    /* 2. The reward's hearts pop on the page-pill they were earned on.
       Jan, 2026-09-27: nothing flies to the wallet pill any more (see
       hohHeartBurst in hoh-wallet.js). The burst is fixed to <body>, so
       the page refresh at 950ms cannot cut it off. */
    if(pillEl && typeof window.hohHeartBurst === 'function'){
      window.hohHeartBurst(pillEl, { n: reward });
    }
    /* 3. Bank bump — pulled forward to 380ms so the wallet counter
       ticks up while the hearts are mid-flight. Reads as "the deposit
       is happening" — the visual hearts trail in to confirm it. */
    setTimeout(function(){
      if(typeof window.hohAddHeartsToBank === 'function'){
        try{ window.hohHeartHint && window.hohHeartHint('earn_explore_page'); }catch(e){}
        try { window.hohAddHeartsToBank(reward); } catch(_){}
      }
    }, 380);
    /* 4. Toast — subtle confirmation. Uses the existing heart-toast
       so the styling matches the rest of the app's earn moments.
       R148-Z13: Sub-copy now reads the LIKES count from pageHearts.max
       so it matches what the user actually did (3 hearts → "liking 3
       heroes" not the old hardcoded "2"). Bustling Streets gets its
       own copy since 5 is a notable count worth calling out. */
    /* R384 (Jan): the heart-back toast taught its lesson ONCE and then kept
       spamming every completed page. The hearts still fly and the wallet
       still ticks — that is the feedback. The toast fires once per device,
       ever, then goes quiet for good. */
    var _hbSeen = false;
    try { _hbSeen = localStorage.getItem('hoh-heartback-toast-v1') === '1'; } catch(_){}
    if(!_hbSeen && typeof window.hohShowHeartToast === 'function'){
      try { localStorage.setItem('hoh-heartback-toast-v1', '1'); } catch(_){}
      var likedN = pageHearts.max || (reward + 1);
      var subText;
      if(key === 'special:bustlingStreets'){
        subText = 'Reward for liking 5 heroes in Bustling Streets.';
      } else if(key === 'special:spectrum'){
        subText = 'Reward for stepping into the Spectrum.';
      } else if(key === 'special:epic'){
        subText = 'Reward for honoring the three known names.';
      } else {
        subText = 'Reward for liking ' + likedN + ' hero' + (likedN === 1 ? '' : 'es') + '. Keep exploring.';
      }
      try { window.hohShowHeartToast('Thanks for exploring', subText); } catch(_){}
    }
  }

  /* Re-derive after wallet changes (heart grant, daily refresh, etc).
     The header pill listens for storage events and re-renders; we
     piggyback on that so the page-hearts label stays truthful.

     Two extra behaviors gated on wallet transitions:
       • Wallet → 0: reset to Fresh Faces (phase 0) and hide the
         refresh icon so the user has no way to "wander" further
         when they can't spend anything. Auto-bar pauses too.
       • Wallet 0 → positive: restore refresh icon and resume the
         auto-bar.
     The transition is detected by tracking lastBank across calls. */
  var lastKnownBank = (function(){
    try { return (typeof window.heartsRemainingToday === 'function') ? window.heartsRemainingToday() : 999; }
    catch(_){ return 999; }
  })();
  /* R271: self-healing watchdog. When the section is parked on "Out of hearts"
     it must recover the instant the wallet climbs again — even if the refill
     event was missed (fired while the pack modal covered the section, lost to a
     listener-order race with the bank cache, etc.). While out of hearts we poll
     the real wallet (straight from localStorage, immune to any cache) and, the
     moment it is positive, run the normal refill path. Self-terminating: it only
     runs while out of hearts and stops as soon as the wallet is back. */
  var __ffOutWatch = null;
  function ffReadRawBank(){
    try { var v = parseInt(localStorage.getItem('hoh-heart-bank-v1')||'0',10); return isNaN(v) ? 0 : v; }
    catch(_){ return 0; }
  }
  function ffStartOutWatch(){
    if(__ffOutWatch) return;
    __ffOutWatch = setInterval(function(){
      if(document.hidden) return; /* PERF-6: a hidden tab earns no hearts to notice */
      if(ffReadRawBank() > 0){ try { syncWalletState(); } catch(_){} }
    }, 2500);
  }
  function ffStopOutWatch(){ if(__ffOutWatch){ clearInterval(__ffOutWatch); __ffOutWatch = null; } }
  function applyOutOfHeartsState(bank){
    var c = _ffContainer();
    if(!c) return;
    /* Lock EVERY refresh icon in the section. eventHeaderHtml renders
       its own icon per special event, and the regular phase header
       has another. querySelectorAll catches them all so nothing is
       clickable while the wallet is empty. */
    var icons = c.querySelectorAll('#hoh-fresh-refresh, .hoh-fresh-refresh');
    if(bank <= 0){
      c.classList.add('hoh-fresh-out-of-hearts');
      icons.forEach(function(icon){
        /* display:none removes the icon from layout entirely so the
           user doesn't see a greyed-out empty circle they can't
           interact with. Reverts on wallet refill. */
        icon.style.display = 'none';
        icon.style.pointerEvents = 'none';
        icon.setAttribute('aria-disabled', 'true');
      });
      ffStartOutWatch();
    } else {
      c.classList.remove('hoh-fresh-out-of-hearts');
      icons.forEach(function(icon){
        icon.style.display = '';
        icon.style.pointerEvents = '';
        icon.removeAttribute('aria-disabled');
      });
      ffStopOutWatch();
    }
  }
  /* Set when wallet→0 happens while the detail modal is open. We
     defer the phase reset until the modal closes so the user finishes
     their action without the section visibly thrashing behind them. */
  var pendingWalletReset = false;
  function detailModalIsOpen(){
    try {
      var m = document.getElementById('hoh-modal');
      return !!(m && m.style.display === 'flex');
    } catch(_){ return false; }
  }
  function performWalletReset(){
    currentSpecial = null;
    setPhase(0);
    currentThemedPhase = null;
    cachedWanderingPool = null;
    cachedWanderingPhase = null;
    renderedSnapshotIds = null;
    snapshotPhaseKey = null;
    try { window.hohRenderFreshFaces(); } catch(_){}
  }
  function syncWalletState(){
    /* Read the wallet STRAIGHT from storage so a stale in-tab cache (the section
       caches getHeartBank and only clears on a cross-tab storage event) can never
       make the section believe it is still broke after a same-tab refund. */
    var bank;
    try { bank = parseInt(localStorage.getItem('hoh-heart-bank-v1')||'0',10); if(isNaN(bank)) bank = null; } catch(_){ bank = null; }
    if(bank === null){ try { bank = (typeof window.heartsRemainingToday === 'function') ? window.heartsRemainingToday() : 999; } catch(_){ bank = 999; } }
    /* Transition: just went broke. Reset to Fresh Faces phase 0 —
       UNLESS the detail modal is open, in which case we defer the
       reset to avoid a jarring section flash behind the modal. The
       deferred reset fires when the modal closes (see the modal
       observer below). */
    if(lastKnownBank > 0 && bank <= 0){
      if(detailModalIsOpen()){
        pendingWalletReset = true;
      } else {
        try { performWalletReset(); } catch(_){}
      }
    }
    /* Wallet refilled while a deferred reset was queued (e.g. passive
       heart grant landed during a detail-modal heart). Clear the
       queued reset so the section doesn't snap to phase 0 after the
       modal closes when the user clearly has hearts again. */
    if(pendingWalletReset && bank > 0){
      pendingWalletReset = false;
    }
    /* R114-A: Wallet went from EMPTY to POSITIVE on the same page.
       The page allowance bookkeeping (pageHearts.spent) is sticky to
       the page key, so a user who exhausted the budget at wallet=0
       was stuck in "Spent here" forever — even after the wallet
       refilled. Their heart clicks pulsed red (denied) and never
       turned purple. Fix: zero out spent + the per-batch counted
       set so the user gets a fresh page budget against the new
       wallet. The page key stays the same (we're not refreshing
       the cards), only the spend tally resets. Page allowance
       still caps total spend on this batch via the freshly-zero
       pageHearts.spent vs pageHearts.max gate downstream.
       R148-Z12: SKIP this entire branch when a refresh is already
       scheduled. The completion-reward path (R148-Z10) bumps the
       wallet back up by +1 right after exhaustion; on a low-budget
       wallet that bump triggers a false "refill" here that would
       otherwise reset spent + cancel the pending refresh, leaving
       the user stuck on the same page with the spend counter back
       to 0 (the bug the user reported). The refresh is already in
       flight via setTimeout — let it land, then the new page's
       render path will set spent=0 cleanly. */
    if(lastKnownBank <= 0 && bank > 0 && !pageAutoRefreshScheduled){
      pageHearts.spent = 0;
      try { pageHeartsCountedSet = Object.create(null); } catch(_){}
      try { consumedThisPage = Object.create(null); } catch(_){}
      try { pageAutoRefreshScheduled = false; } catch(_){}
    }
    /* Transition: wallet refilled. Resume timer (already wired in
       startAutoTimer's eligibility check). Wrap each downstream call
       in try/catch so a single throw can't break the section. */
    lastKnownBank = bank;
    try { applyOutOfHeartsState(bank); } catch(_){}
    try { recomputePageHearts(currentPageVisibleCount(), pageHearts.key); } catch(_){}
    try { if(typeof startAutoTimer === 'function') startAutoTimer(); } catch(_){}
  }
  /* Watch the detail modal — when it closes after a deferred reset
     was queued, run the reset now. We poll in addition to the
     observer so this is robust even if the observer isn't bound
     yet. */
  function checkDeferredWalletReset(){
    if(!pendingWalletReset) return;
    if(detailModalIsOpen()) return;
    pendingWalletReset = false;
    try { performWalletReset(); } catch(_){}
  }
  try {
    window.addEventListener('storage', function(e){
      if(e && (e.key === 'hoh-heart-bank-v1' || e.key === 'hoh-hearts-daily')){
        syncWalletState();
      }
    });
  } catch(_){}
  document.addEventListener('hoh:hearts-bumped', syncWalletState);
  /* Drain any deferred reset whenever the detail modal closes. */
  document.addEventListener('hoh:detail-closed', checkDeferredWalletReset);
  /* Apply once on bind so the initial state is correct (e.g., user
     loads the page already at zero). */
  setTimeout(function(){ applyOutOfHeartsState(lastKnownBank); }, 100);
  function currentPageVisibleCount(){
    var c = _ffContainer();
    if(!c) return 0;
    return c.querySelectorAll('.hoh-fresh-card').length;
  }
  /* Public read API for the earn-tickets module. */
  window.hohGetWanderQuestProgress = function(){
    var n = getFfHeartsToday();
    return {
      count: n,
      target: WANDER_QUEST_TARGET,
      done: n >= WANDER_QUEST_TARGET
    };
  };
  /* R61: Single source of truth for "how many wander tickets has the
     user actually CLAIMED today". The header counter previously
     derived this from the raw heart count (hearts>=10 ? 1 : 0) +
     (hearts>=20 ? 1 : 0), which:
       - Used stale thresholds (R56 moved them to 20/40)
       - Auto-credited on heart bump instead of waiting for the user
         to click the milestone pip (the post-R56 model)
       - Could fluctuate as hearts moved up over the day
     This reads the actual claim flags (date-keyed in localStorage so
     they auto-reset at midnight). Three of the six pips are tickets,
     so wander caps at 3 a day. */
  window.hohGetWanderQuestClaimed = function(){
    /* R406: the three ticket pips now sit at 10/30/50 and the three
       heart pips at 0/20/40, but the RETURNED KEYS stay m20/m40/m60
       for the tickets and m10/m30/m50 for the hearts. They are slot
       names, not thresholds, and hoh-tickets.js reads them to seed the
       wallet ledger against server rows called wander_20 and friends.
       Renaming them here would silently break that join. Daily wander
       ticket max stays 3. */
    /* R793: read the pips by position, not by a threshold number, so the
       slot names hold whatever the ladder's thresholds are. */
    var _tk = WANDER_TICKET_MILESTONES;
    var _hb = WANDER_MILESTONES.filter(function(m){ return WANDER_HEART_BONUSES[m] !== undefined; });
    var c20 = isMilestoneClaimed(_tk[0]) ? 1 : 0;   /* slot wander_20 */
    var c40 = isMilestoneClaimed(_tk[1]) ? 1 : 0;   /* slot wander_40 */
    var c60 = isMilestoneClaimed(_tk[2]) ? 1 : 0;   /* slot wander_60 */
    return {
      count: c20 + c40 + c60,
      max: 3,
      m20: !!c20,
      m40: !!c40,
      m60: !!c60,
      /* Heart bonuses: slots wander_10 / wander_30 / wander_50. */
      m10: isMilestoneClaimed(_hb[0]),
      m30: isMilestoneClaimed(_hb[1]),
      m50: isMilestoneClaimed(_hb[2])
    };
  };

  /* Paints the in-panel progress bar (X/20 with two milestone
     pips). Called after every render and after every quest bump.
     Also detects when a milestone has just been crossed and fires
     the ticket-fly celebration. We use a localStorage flag per
     milestone+day so a refresh doesn't re-celebrate the same
     milestone.

     At 20/20 for a logged-out user, surface a "Claim your tickets"
     CTA below the bar that opens the home-for-your-heroes lightbox
     so they can submit / sign in to bank what they earned. Signed-
     in users see the bar hide instead — the tickets are already
     theirs. */
  /* R56: per-milestone CLAIMED state. Stored in localStorage keyed by
     ymd so it resets daily. "Hit" = threshold reached. "Claimed" =
     user clicked to bank it. A milestone is "ready" (glowing) when
     hit but not yet claimed; "done" (greyed) when claimed. */
  /* R406: keyed on the SLOT, not the threshold. See the WANDER_SLOT
     note above — this is what keeps hoh-tickets.js's cross-device
     pre-mark (which writes hoh-ff-claim-{ymd}-{slot} straight from the
     server's slot list) pointing at the right pip after the renumber. */
  function milestoneClaimedKey(n){ return 'hoh-ff-claim-' + ymdToday() + '-' + wanderSlot(n); }
  function isMilestoneClaimed(n){
    try { return localStorage.getItem(milestoneClaimedKey(n)) === '1'; } catch(_){ return false; }
  }
  function markMilestoneClaimed(n){
    try { localStorage.setItem(milestoneClaimedKey(n), '1'); } catch(_){}
  }

  /* R112-A: rAF coalescer. Hold-to-stream and rapid heart taps can call
     this 60+ times per second. Each call does ~10 DOM reads + writes
     across the bar, label, fill, milestones, and claim button. The
     browser would batch some layout work automatically, but the JS
     itself still runs 60x. We coalesce all calls within a single
     animation frame into ONE actual paint. The function always returns
     synchronously; the rAF handler reads CURRENT state on the next
     frame so the result is identical to a sync paint. */
  var __paintScheduled = false;
  /* R150: interval handle for the wander-quest reset countdown. Runs
     only while every milestone is claimed for the day; cleared otherwise. */
  var _wanderResetTimer = null;
  /* R150: time until the wander quest resets. Progress is keyed by UTC
     date (ymdToday uses toISOString), so it rolls over at UTC midnight. */
  function _wanderResetCountdownText(){
    var now = new Date();
    var next = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() + 1, 0, 0, 0, 0);
    var ms = next - now.getTime();
    if(ms < 0) ms = 0;
    var totalMin = Math.floor(ms / 60000);
    var h = Math.floor(totalMin / 60);
    var m = totalMin % 60;
    if(h > 0) return 'Resets in ' + h + 'h ' + m + 'm';
    if(m > 0) return 'Resets in ' + m + 'm';
    return 'Resets shortly';
  }

  /* ---- The Contract morph -------------------------------------------------
     When every wander milestone is claimed, the completed Explore the Hall
     panel becomes the doorway to the once-a-day party battle. Eligibility:
     the event is switched on (section setting), the visitor is signed in, and
     the day's wander is fully claimed. Returns 'open' (not played yet),
     'spent' (already took today's contract), or null (not eligible). */
  function _contractEligible(){
    /* god mode (jangela) treats the contract as live and signed in, so the
       morph and the whole flow are testable on any device with the section
       flag still off. Normal players go through the real gates below. */
    var god = false;
    try { god = typeof window.hohIsGodMode === 'function' && window.hohIsGodMode(); } catch(_){}
    var enabled = false;
    try { enabled = !!window.HOH_CONTRACT_ENABLED; } catch(_){ enabled = false; }
    if(!enabled && !god) return null;
    var signedIn = false;
    try {
      signedIn = (typeof window.hohYourHallIsSignedIn === 'function') ? !!window.hohYourHallIsSignedIn() : false;
      if(!signedIn && typeof window.hohYourHallEmail === 'function') signedIn = !!window.hohYourHallEmail();
    } catch(_){}
    if(!signedIn && !god) return null;
    /* R376 (2026-08-06, Jan): the milestone gate and the free daily run are
       retired. Entry always costs one Battlegrounds Ticket. R381: the door is
       the scene of its own Battlegrounds sub tab in Events (hohPaintBgDoor),
       and this surface never becomes the doorway anymore. Only a LIVE battle
       still takes the panel over, which hohRenderFreshFaces detects by the
       #hoh-contract host, not by this return value. */
    return null;
  }
  /* ---- The Battlegrounds entry ------------------------------------------
     When the daily is fully claimed, the Explore the Hall bar ITSELF becomes
     the doorway: _wanderBarHtml (every render path, main panel AND stream
     events) swaps the bar for this entry card. The game mounts into a
     #hoh-contract host that lives OUTSIDE the rotating panel, so the
     auto-refresh that cycles Fresh Faces pages can never wipe a battle
     mid-fight. */
  function _battlegroundsEntryHtml(state){
    if(state === 'spent'){
      return '<div class="hoh-contract-entry hoh-bg-entry" data-state="spent">' +
        '<div class="hoh-contract-entry-card hoh-contract-entry-spent">' +
          '<span class="hoh-contract-entry-eyebrow">The battlegrounds are closed</span>' +
          '<span class="hoh-contract-entry-line">You fought today. The gates open again at dawn.</span>' +
        '</div></div>';
    }
    if(state === 'live'){
      return '<div class="hoh-contract-entry hoh-bg-entry" data-state="live">' +
        '<div class="hoh-contract-entry-card hoh-contract-entry-spent">' +
          '<span class="hoh-contract-entry-eyebrow">The battlegrounds are open</span>' +
          '<span class="hoh-contract-entry-line">Your battle is below.</span>' +
        '</div></div>';
    }
    /* R298: the gates are the reward for finishing the day — a forge-lit
       banner with drifting embers and a sweeping blade of light, not a box.
       Styles are injected here (deploy-safe: the entry's old rules live in
       the drifted hall stylesheet). */
    _ensureBgGateCss();
    /* R301: the rematch. Same banner, honest price, and the whole card still
       launches the game — the picker charges the contract itself. */
    if(state === 'replay'){
      /* R334: THE ENTRY FEE IS GONE.
         This button used to read "Enter · 300 ♥", and that 300 was a real
         charge: it became the rematch's forced stake, so a player paid to walk
         in AND wagered again inside. Two prices for one fight. The ticket is
         the price of an extra run now, and the wager is chosen at the table
         like it is on the daily. Show the ticket so the cost is still honest,
         and only when they hold one. */
      var tix = 0;
      try { tix = (typeof window.hohGetUnspentTickets === 'function') ? (window.hohGetUnspentTickets() | 0) : 0; } catch(_){}
      /* R336 UX: SAY IT HERE, NOT FOUR SCREENS LATER.
         With no ticket the gate still read "Enter" and still opened. The
         player then picked a party, chose a contract, pressed Battle, and
         only THEN met "You are out of tickets" — a dead end discovered after
         the work, which is the most expensive place to discover anything.
         The door now tells the truth before it is opened, and points at the
         one thing that fixes it. It stays a calm state, not an error: they
         have not done anything wrong, they have simply spent the day. */
      if(tix < 1){
        return '<div class="hoh-contract-entry hoh-bg-entry hoh-bg-gate" data-state="replay-spent">' +
          '<div class="hoh-bg-gate-card hoh-bg-gate-quiet">' +
            '<span class="hoh-bg-gate-embers" aria-hidden="true">' +
              '<i style="left:22%;animation-delay:-1.2s"></i><i style="left:61%;animation-delay:-3.1s"></i>' +
            '</span>' +
            '<span class="hoh-bg-gate-eyebrow">Today&rsquo;s contract is fought</span>' +
            '<span class="hoh-bg-gate-title">The Gates Rest</span>' +
            '<span class="hoh-bg-gate-line">A ticket opens them again. Explore the Hall pays one, ' +
              'and the daily run is free again at dawn.</span>' +
          '</div></div>';
      }
      return '<div class="hoh-contract-entry hoh-bg-entry hoh-bg-gate" data-state="replay">' +
        '<div class="hoh-bg-gate-card" role="button" tabindex="0" onclick="hohEnterBattlegrounds()" ' +
          'onkeydown="if(event.key===\'Enter\'||event.key===\' \'){event.preventDefault();hohEnterBattlegrounds();}">' +
          '<span class="hoh-bg-gate-embers" aria-hidden="true">' +
            '<i style="left:14%;animation-delay:-1.2s"></i><i style="left:33%;animation-delay:-3.4s"></i>' +
            '<i style="left:58%;animation-delay:-.6s"></i><i style="left:79%;animation-delay:-2.6s"></i>' +
          '</span>' +
          '<span class="hoh-bg-gate-swords" aria-hidden="true">' +
            '<svg viewBox="0 0 24 24" width="30" height="30" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round">' +
            '<path d="M14.5 3.5 20 3.5 20 9"/><path d="M20 3.5 9.5 14"/><path d="M4 20l3.5-3.5"/><path d="M6.5 17.5 4.5 19.5"/>' +
            '<path d="M9.5 3.5 4 3.5 4 9"/><path d="M4 3.5 14.5 14"/><path d="M20 20l-3.5-3.5"/></svg>' +
          '</span>' +
          '<span class="hoh-bg-gate-eyebrow">Today&rsquo;s contract is fought</span>' +
          '<span class="hoh-bg-gate-title">Fight Again</span>' +
          '<span class="hoh-bg-gate-line">The gates never close. Another fight is waiting' +
            (tix > 0 ? ', and you hold ' + tix + ' ' + (tix === 1 ? 'ticket' : 'tickets') : '') + '.</span>' +
          '<span class="hoh-bg-gate-cta hoh-bg-enter">Enter' +
            (tix > 0 ? ' &middot; 1 &#127915;' : '') + ' <span aria-hidden="true">&rarr;</span></span>' +
        '</div></div>';
    }
    return '<div class="hoh-contract-entry hoh-bg-entry hoh-bg-gate" data-state="open">' +
      '<div class="hoh-bg-gate-card" role="button" tabindex="0" onclick="hohEnterBattlegrounds()" ' +
        'onkeydown="if(event.key===\'Enter\'||event.key===\' \'){event.preventDefault();hohEnterBattlegrounds();}">' +
        /* R300: the sheen sweep is gone — the ember light carries it alone. */
        '<span class="hoh-bg-gate-embers" aria-hidden="true">' +
          '<i style="left:12%;animation-delay:-1.2s"></i><i style="left:28%;animation-delay:-3.4s"></i>' +
          '<i style="left:47%;animation-delay:-.4s"></i><i style="left:63%;animation-delay:-2.6s"></i>' +
          '<i style="left:81%;animation-delay:-4.1s"></i><i style="left:92%;animation-delay:-1.9s"></i>' +
        '</span>' +
        '<span class="hoh-bg-gate-swords" aria-hidden="true">' +
          '<svg viewBox="0 0 24 24" width="30" height="30" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round">' +
          '<path d="M14.5 3.5 20 3.5 20 9"/><path d="M20 3.5 9.5 14"/><path d="M4 20l3.5-3.5"/><path d="M6.5 17.5 4.5 19.5"/>' +
          '<path d="M9.5 3.5 4 3.5 4 9"/><path d="M4 3.5 14.5 14"/><path d="M20 20l-3.5-3.5"/></svg>' +
        '</span>' +
        '<span class="hoh-bg-gate-eyebrow">The day is done · the gates open</span>' +
        '<span class="hoh-bg-gate-title">The Battlegrounds</span>' +
        '<span class="hoh-bg-gate-line">Take your heroes in. Winner takes all.</span>' +
        '<span class="hoh-bg-gate-cta hoh-bg-enter">Enter <span aria-hidden="true">→</span></span>' +
      '</div></div>';
  }
  /* R298: the gate's own styling, injected once. */
  function _ensureBgGateCss(){
    if(document.getElementById('hoh-bg-gate-css')) return;
    var st = document.createElement('style'); st.id = 'hoh-bg-gate-css';
    st.textContent =
      /* R299: NO BOXES. The Explore panel's own chrome is stripped while the
         gate holds the surface, and the gate itself is a full-bleed banner
         whose glow, embers and sweep all fade out into the sides. */
      '#hoh-fresh-faces[data-bg="open"],#hoh-fresh-faces[data-bg="replay"]{background:none !important;border:0 !important;'+
        'box-shadow:none !important;padding:0 !important;overflow:visible !important}'+
      /* R300: while the Battlegrounds hold the surface, the "Explore the Hall"
         collapsible header is a lie about where you are — hide it (and its
         chrome) for both the gate and a live fight. */
      /* R302 FIX: hide ONLY the collapsible header button. The previous rule
         also hid #hoh-yh-explore-slot — and #hoh-fresh-faces is mounted INSIDE
         that slot (hoh-your-hall.js ~2060), so it took Explore AND the
         Battlegrounds gate down with it. Never hide the slot or the panel. */
      'body.hoh-bg-holding .hoh-yh-explore-toggle{display:none !important}'+
      'body.hoh-bg-holding .hoh-yh-explore-panel{background:none !important;border:0 !important;box-shadow:none !important;padding:0 !important}'+
      '.hoh-bg-gate{max-width:none !important;width:100%;margin:6px auto 0 !important}'+
      '.hoh-bg-gate .hoh-bg-gate-card{position:relative;overflow:hidden;display:flex;flex-direction:column;align-items:center;'+
        'text-align:center;padding:40px 18px 34px;border:0;border-radius:0;box-shadow:none;background:none;cursor:pointer;'+
        'transition:filter .25s ease}'+
      '.hoh-bg-gate .hoh-bg-gate-card:hover{filter:brightness(1.1)}'+
      /* R336: the ticketless rest state. It is not a button, so it must not
         behave like one — no pointer, no hover lift — and the forge light
         banks down so the surface reads as closed rather than broken. */
      '.hoh-bg-gate .hoh-bg-gate-quiet{cursor:default}'+
      '.hoh-bg-gate .hoh-bg-gate-quiet:hover{filter:none}'+
      '.hoh-bg-gate .hoh-bg-gate-quiet::before{opacity:.4}'+
      '.hoh-bg-gate .hoh-bg-gate-quiet .hoh-bg-gate-title{color:#b8ac93}'+
      /* the ember light itself — masked so it dissolves at both edges */
      '.hoh-bg-gate .hoh-bg-gate-card::before{content:"";position:absolute;inset:0;pointer-events:none;'+
        'background:radial-gradient(62% 128% at 50% 116%,rgba(232,120,40,.34),rgba(232,120,40,.10) 46%,transparent 70%);'+
        '-webkit-mask-image:linear-gradient(90deg,transparent 0,#000 22%,#000 78%,transparent 100%);'+
        'mask-image:linear-gradient(90deg,transparent 0,#000 22%,#000 78%,transparent 100%)}'+
      /* a single hairline that fades to nothing, instead of a border */
      '.hoh-bg-gate .hoh-bg-gate-card::after{content:"";position:absolute;left:0;right:0;bottom:0;height:1px;pointer-events:none;'+
        'background:linear-gradient(90deg,transparent,rgba(240,176,112,.55),transparent)}'+
      '.hoh-bg-gate-sweep{position:absolute;top:0;bottom:0;left:-45%;width:38%;pointer-events:none;'+
        'background:linear-gradient(100deg,transparent,rgba(255,225,170,.16),transparent);'+
        'transform:skewX(-18deg);animation:hohBgSweep 4.6s ease-in-out infinite;'+
        '-webkit-mask-image:linear-gradient(90deg,transparent,#000 25%,#000 75%,transparent);'+
        'mask-image:linear-gradient(90deg,transparent,#000 25%,#000 75%,transparent)}'+
      '@keyframes hohBgSweep{0%{left:-45%}55%{left:115%}100%{left:115%}}'+
      '.hoh-bg-gate-embers{position:absolute;inset:0;pointer-events:none;overflow:hidden;'+
        '-webkit-mask-image:linear-gradient(90deg,transparent,#000 24%,#000 76%,transparent);'+
        'mask-image:linear-gradient(90deg,transparent,#000 24%,#000 76%,transparent)}'+
      '.hoh-bg-gate-embers i{position:absolute;bottom:-8px;width:3px;height:3px;border-radius:50%;background:#e8934a;'+
        'box-shadow:0 0 8px #e8934a;opacity:0;animation:hohBgEmber 5.4s linear infinite}'+
      '@keyframes hohBgEmber{0%{opacity:0;transform:translateY(0)}15%{opacity:.9}100%{opacity:0;transform:translateY(-150px)}}'+
      '.hoh-bg-gate-swords{color:#f0b070;filter:drop-shadow(0 0 10px rgba(232,150,74,.7));margin-bottom:8px;'+
        'animation:hohBgPulse 3.2s ease-in-out infinite}'+
      '@keyframes hohBgPulse{0%,100%{transform:scale(1);opacity:.92}50%{transform:scale(1.07);opacity:1}}'+
      '.hoh-bg-gate-eyebrow{font-family:\'Cinzel\',serif;font-size:9.5px;letter-spacing:.28em;text-transform:uppercase;color:#c98f5a}'+
      '.hoh-bg-gate-title{font-family:\'Cinzel\',serif;font-size:26px;font-weight:700;letter-spacing:.12em;text-transform:uppercase;'+
        'margin:6px 0 4px;background:linear-gradient(100deg,#ffe0a8,#f0a860,#ffe0a8);-webkit-background-clip:text;background-clip:text;color:transparent}'+
      '.hoh-bg-gate-line{font-family:\'Cormorant Garamond\',Georgia,serif;font-style:italic;font-size:15px;color:#b8a898}'+
      '.hoh-bg-gate-cta{margin-top:16px;font-family:\'Cinzel\',serif;font-size:11.5px;font-weight:700;letter-spacing:.16em;'+
        'text-transform:uppercase;color:#1a1108;padding:11px 30px;border-radius:11px;'+
        'background:linear-gradient(120deg,#ffd79a,#e8934a);box-shadow:0 8px 24px -10px rgba(232,150,74,.9)}'+
      '.hoh-bg-gate-card:hover .hoh-bg-gate-cta span{transform:translateX(3px);display:inline-block;transition:transform .2s ease}'+
      /* phones: the fade pulls in so the title never dips into the falloff */
      '@media(max-width:560px){.hoh-bg-gate .hoh-bg-gate-card{padding:30px 14px 26px}.hoh-bg-gate-title{font-size:21px}'+
        '.hoh-bg-gate .hoh-bg-gate-card::before,.hoh-bg-gate-embers,.hoh-bg-gate-sweep{'+
        '-webkit-mask-image:linear-gradient(90deg,transparent,#000 10%,#000 90%,transparent);'+
        'mask-image:linear-gradient(90deg,transparent,#000 10%,#000 90%,transparent)}}'+
      '@media(prefers-reduced-motion:reduce){.hoh-bg-gate-sweep,.hoh-bg-gate-embers i,.hoh-bg-gate-swords{animation:none}}';
    (document.head||document.documentElement).appendChild(st);
  }
  /* The one entrance. Creates (or relocates) the #hoh-contract host as a
     SIBLING after the Fresh Faces container so panel rotation can't destroy
     a running battle, then mounts the game and scrolls to it. Global because
     the entry card renders via innerHTML from any surface. */
  /* ============================================================
     R376 (2026-08-06): THE DOOR. One quiet card under Explore on the
     Events tab. It never takes the Explore panel over. Three states:
     you hold a ticket, you need a ticket, a battle is live (blank,
     the fight owns the screen). Short words, one action each.
     ============================================================ */
  /* R382 (Jan): the champions parade. The most hearted heroes across the
     whole hall drift under the Battlegrounds scene in a seamless loop — the
     names you could topple ARE the stakes. Chips reuse the (now cached)
     crop reader; the strip is transform-only and stops whenever the pane is
     hidden. Returns '' until the hall pool has landed; the painter retries
     once when characters arrive. */
  function _bgChampsHtml(){
    var all = [];
    try { all = (typeof window.hohGetAllCharacters === 'function') ? (window.hohGetAllCharacters() || []) : []; } catch(_){}
    var rows = [];
    for(var i = 0; i < all.length; i++){
      var c = all[i];
      if(c && c.approved !== false && (c.image_thumb_url || c.image_url)) rows.push(c);
    }
    rows.sort(function(a, b){ return (b.hearts || 0) - (a.hearts || 0); });
    rows = rows.slice(0, 14);
    if(rows.length < 4) return '';
    /* R385 (Jan): SQUARE, IMAGE-FOCUSED tiles — the portrait IS the chip.
       Name under it, no heart counts. */
    var chips = '';
    for(var j = 0; j < rows.length; j++){
      var h = rows[j];
      var img = h.image_thumb_url || h.image_url;
      var pos = '50% 50%';
      try { if(typeof window.hohImgObjPos === 'function') pos = window.hohImgObjPos(h.id, 'primary') || pos; } catch(_){}
      var cropStyle = (typeof window.hohImgStyle === 'function') ? window.hohImgStyle(h.id, 'primary') : ('object-position:' + pos + ';');
      chips += '<button type="button" class="hoh-bgscene-champ" data-champ="' + esc(String(h.id)) + '">' +
        '<img src="' + esc(img) + '" alt="" loading="lazy" draggable="false" style="' + esc(cropStyle) + '">' +
        '<span class="hoh-bgscene-champ-name">' + esc(h.name || 'Hero') + '</span>' +
      '</button>';
    }
    return '<div class="hoh-bgscene-champs" aria-label="Strongest heroes in the Hall">' +
        '<div class="hoh-bgscene-champs-head">Strongest in the Hall</div>' +
        '<div class="hoh-bgscene-champs-rail"><div class="hoh-bgscene-champs-track">' +
          '<span class="hoh-bgscene-champs-run">' + chips + '</span>' +
          '<span class="hoh-bgscene-champs-run" aria-hidden="true">' + chips + '</span>' +
        '</div></div>' +
      '</div>';
  }
  /* The scene's crest — the same crossed swords the old gate carried. */
  var _BG_SWORDS_SVG =
    '<svg viewBox="0 0 24 24" width="34" height="34" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round">' +
    '<path d="M14.5 3.5 20 3.5 20 9"/><path d="M20 3.5 9.5 14"/><path d="M4 20l3.5-3.5"/><path d="M6.5 17.5 4.5 19.5"/>' +
    '<path d="M9.5 3.5 4 3.5 4 9"/><path d="M4 3.5 14.5 14"/><path d="M20 20l-3.5-3.5"/></svg>';
  /* The synchronous climb mirror is useful only when it belongs to the
     credential currently painting the Hall. Older builds left it unscoped;
     trust that shape only in the explicit local sandbox, never for a signed
     account. Contract data clears it at every account boundary as a second
     line of defence. */
  function bgCurrentClimb(){
    var climb=window.__hohBgClimb||null;
    if(!climb) return null;
    var local=false,account='';
    try{local=typeof window.hohContractIsLocal==='function'&&window.hohContractIsLocal();}catch(_){}
    try{account=String((typeof window.hohYourHallEmail==='function'&&window.hohYourHallEmail())||'').trim().toLowerCase();}catch(_){}
    if(climb.account==null) return local?climb:null;
    return String(climb.account||'').trim().toLowerCase()===account?climb:null;
  }
  window.hohCurrentBgClimb=bgCurrentClimb;
  /* ============ R623 · THE UNLIMITED DOOR (Part 5d S1) ============
     Chunk 1 (R622) publishes window.__hohBgUnlimited from the state door and
     fires hoh:bg-unlimited-changed. Same scoping rule as the climb mirror
     above, and for the same reason: a mirror that belongs to another
     credential is not this player's run.

     ⛔ mode_open FALSE MEANS THIS CARD DOES NOT EXIST AND THE LEGACY DOOR
     STANDS, UNCHANGED. That is the whole reason a theme push can ship before
     the stamp moves: every player sees exactly today's card until the mode
     opens, and nothing below can paint. */
  function bgCurrentUnlimited(){
    var u=window.__hohBgUnlimited||null;
    if(!u) return null;
    var local=false,account='';
    try{local=typeof window.hohContractIsLocal==='function'&&window.hohContractIsLocal();}catch(_){}
    try{account=String((typeof window.hohYourHallEmail==='function'&&window.hohYourHallEmail())||'').trim().toLowerCase();}catch(_){}
    if(u.account==null) return local?u:null;
    return String(u.account||'').trim().toLowerCase()===account?u:null;
  }
  window.hohCurrentBgUnlimited=bgCurrentUnlimited;
  /* R778: "away 3 h 12 m". Whole minutes only (at least one), hours only
     from the first full hour, and a trip stops counting at its 12 hour cap. */
  function bgAwayDur(seconds, trip){
    var s=Math.max(0,Math.floor(Number(seconds)||0));
    if(trip && s>=43200) return '12 h+';
    var m=Math.max(1,Math.floor(s/60)), h=Math.floor(m/60), mm=m%60;
    if(h<1) return m+' m';
    return mm ? h+' h '+mm+' m' : h+' h';
  }
  /* R778 review: "Your party kept fighting" only when it can be true. The
     catch-up plays whole segments by the pace table in hoh-contract-away.js,
     so a round counts as fought only once it is due, and a run paused
     mid-segment needs the rest of that segment due (the fight you left is
     played whole or not at all). A Stop before at or below the next round
     is treated as Never, as the catch-up treats it. With the engine or the
     pace table missing this says nothing it cannot back. */
  function bgAwayFought(unlim, trip, seconds){
    var A=window.HOHAway, E=window.HOHContract;
    if(!A || typeof A.roundsDue!=='function' || !E || typeof E.wavePlan!=='function') return false;
    var from=Math.max(1, trip.from_round|0), cur=Math.max(from, unlim.round|0);
    var ver=(unlim.engine_ver|0) || (unlim.start_engine_ver|0) || (E.ENGINE_VERSION|0);
    var due=0;
    try{ due=(A.roundsDue(E, from, seconds, ver)|0) - (cur - from); }catch(_){ return false; }
    var need=1, ws=unlim.wave_start|0, wc=unlim.wave_count|0;
    if(unlim.run_status==='open' && !unlim.at_stop && ws>0 && wc>0 && ws+wc>cur) need=ws+wc-cur;
    var o=trip.orders||{}, sb=o.stop_before==null ? null : (o.stop_before|0);
    if(sb!=null && sb>cur && cur+need>sb) return false;
    return due>=need;
  }
  /* ⛔ ONE PREDICATE FOR THE FIVE-HERO GATE, AND IT LIVES IN THE GAME FILE.
     hoh-contract-ui.js owns unlimOwnedPlayableCount() because it owns
     ownedRoster() and the Start gate that actually refuses. Asking it here
     rather than counting again is what stops the door card and the setup
     screen from ever disagreeing about how many heroes you have.
     null means "not known yet" (an unloaded collection is not an empty one),
     and this card then says nothing about the count: the setup screen, which
     always runs after the collection load, is the one that refuses. */
  function bgUnlimitedMinimumOwned(){
    var ui = window.HOHContractUI;
    return ui && ui.unlimMinimumOwned ? ui.unlimMinimumOwned() : 5;
  }
  function bgUnlimitedOwnedCount(){
    try{
      if(window.HOHContractUI&&typeof window.HOHContractUI.unlimOwnedPlayableCount==='function'){
        var n=window.HOHContractUI.unlimOwnedPlayableCount();
        return (typeof n==='number')?n:null;
      }
    }catch(_){}
    return null;
  }
  /* ⛔ R647 · AND SOMEBODY HAS TO ACTUALLY LOAD IT. This card asks how many
     heroes the player owns, and until now NOTHING on this page had ever
     fetched the collection: hoh-contract-data.js loads it inside the game's
     own boot, which happens after this card has painted AND after the player
     has tapped it. So the count above had nothing to count.

     Ask once, then repaint — the same shape as the climb and Unlimited state
     reads in hohPaintBgDoor, and for the same reason: this door repaints on a
     one-minute timer and on three events, so an unlatched request would fire
     forever. Unforced on purpose: a warm Shop snapshot resolves it with no
     network at all, and the game's own boot does the authoritative forced
     refresh a moment later.

     The latch is the ACCOUNT, not a bare flag, so signing in as somebody else
     asks again for them. ⛔ AND THE PIN IS PART OF THAT ACCOUNT: the data
     layer's own boundary keys on email AND pin (R495 added the pin because
     comparing only the email admitted an old response after A -> B -> A), so a
     latch that knew only the email would go quiet after a PIN rotation while
     the cache underneath had reset. */
  function bgUnlimitedEnsureCollection(){
    try{
      var D=window.HOHContractData;
      if(!D||typeof D.loadCollection!=='function') return;
      var em='guest',pn='';
      try{ em=String((typeof window.hohYourHallEmail==='function'&&window.hohYourHallEmail())||'guest').trim().toLowerCase()||'guest'; }catch(_){}
      try{ var tok=JSON.parse(localStorage.getItem('hoh-your-hall-token')||'null'); pn=String(tok&&tok.pin||''); }catch(_){}
      var scope=em+'\u0000'+pn;
      if(window.__hohBgUnlimCollAsked===scope) return;
      window.__hohBgUnlimCollAsked=scope;
      var again=function(){ try{ window.hohPaintBgDoor(); }catch(_){} };
      var p=D.loadCollection();
      if(p&&typeof p.then==='function') p.then(again,again);
      else again();
    }catch(_){}
  }
  /* R558: Shop and Events now share one entrance decision. The former dead
     owned-entry button used to know only the ticket mirror, while this door
     also knew about saved climbs and free claims. Keeping those decisions in
     two click handlers made the labels disagree and made a fast Shop tap able
     to skip the safest path. One in-flight promise also absorbs double taps
     across both surfaces. */
  window.hohActivateBattlegroundsEntry = function(){
    function scopeKey(){
      var em='guest',pn='';
      try{ em=String((typeof window.hohYourHallEmail==='function'&&window.hohYourHallEmail())||'guest').trim().toLowerCase()||'guest'; }catch(_){}
      try{ var tok=JSON.parse(localStorage.getItem('hoh-your-hall-token')||'null'); pn=String(tok&&tok.pin||''); }catch(_){}
      var h=2166136261,s=em+'\u0000'+pn; for(var i=0;i<s.length;i++){ h^=s.charCodeAt(i); h=Math.imul(h,16777619); }
      return em+':'+(h>>>0).toString(16);
    }
    var activationScope=scopeKey();
    var prior=window.__hohBgActivatePromise;
    if(prior&&prior.scope===activationScope&&prior.promise) return prior.promise;
    function stillCurrent(){ return scopeKey()===activationScope; }
    function localSandbox(){ try{ return typeof window.hohContractIsLocal==='function' && window.hohContractIsLocal(); }catch(_){ return false; } }
    function tickets(){ try{ return typeof window.hohGetBgTickets === 'function' ? Math.max(0, window.hohGetBgTickets() | 0) : 0; }catch(_){ return 0; } }
    function openBuilder(){
      if(!stillCurrent()) return false;
      if(typeof window.hohShopOpenBuilder === 'function'){
        var opened=window.hohShopOpenBuilder();
        return opened!==false && !!document.getElementById('hoh-yh-bg-stage');
      }
      if(typeof window.hohEnterBattlegrounds === 'function'){ window.hohEnterBattlegrounds(); return true; }
      return false;
    }
    /* R623: when the mode is open the SETUP SCREEN is the party builder, so
       the entry goes straight to the game instead of routing through the
       Shop's builder first. Two builders for one party is the "one thing on
       screen at a time" rule broken twice over (R-6 rule 5). */
    function openUnlimited(){
      if(!stillCurrent()) return false;
      if(typeof window.hohEnterBattlegrounds !== 'function') return false;
      window.hohEnterBattlegrounds();
      return true;
    }
    var checkedUnlimited = null, checkedClimb = null;
    function decide(){
      if(!stillCurrent()) return { ok:false, status:'account_changed' };
      if(!checkedUnlimited || checkedUnlimited.ok !== true || typeof checkedUnlimited.mode_open !== 'boolean') return { ok:false, status:'state_unavailable' };
      var unlimOpen = !!checkedUnlimited.mode_open;
      if(!unlimOpen && (!checkedClimb || checkedClimb.ok !== true)) return { ok:false, status:'state_unavailable' };
      var active = unlimOpen ? checkedUnlimited : checkedClimb;
      if(active && active.ok === true && active.live && active.run_id != null){
        if(unlimOpen){
          if(!active.at_stop && active.run_status !== 'open' && active.run_status !== 'settled_win') return { ok:false, status:'state_unavailable' };
          return openUnlimited() ? { ok:true, action:'resume_unlimited' } : { ok:false, status:'not_ready' };
        }
        if(typeof window.hohEnterBattlegrounds === 'function'){ window.hohEnterBattlegrounds(); return { ok:true, action:'resume' }; }
        return { ok:false, status:'not_ready' };
      }
      if(unlimOpen && checkedUnlimited.live) return { ok:false, status:'state_unavailable' };
      if(unlimOpen){
        var owned = bgUnlimitedOwnedCount();
        if(owned != null && owned < bgUnlimitedMinimumOwned()) return { ok:false, status:'need_heroes', owned:owned, needed:bgUnlimitedMinimumOwned() };
      }
      if(tickets() > 0){
        var opened = unlimOpen ? openUnlimited() : openBuilder();
        return opened ? { ok:true, action:'held' } : { ok:false, status:'not_ready' };
      }
      return { ok:false, status:'no_entry' };
    }
    var stateRead = Promise.resolve(null);
    try{
      if(window.HOHContractData && typeof window.HOHContractData.ladderState === 'function'){
        var ladderRead=window.HOHContractData.ladderState(true);
        /* A stalled network must not strand both Shop and Events in an
           eternal Opening state. The decision can safely continue from the
           account-scoped local mirrors after this bounded read. */
        stateRead = new Promise(function(resolve){
          var settled=false, timer=setTimeout(function(){ if(!settled){ settled=true; resolve(null); } },6000);
          Promise.resolve(ladderRead).then(function(value){ if(settled)return; settled=true;clearTimeout(timer);resolve(value); },function(){ if(settled)return;settled=true;clearTimeout(timer);resolve(null); });
        });
      }
    }catch(_){ }
    /* R623: the Unlimited state door beside the ladder read, bounded the same
       way. It WRITES NOTHING and spends nothing, so it is safe on this path,
       and reading it here is what stops a cold page's first tap from routing
       an open mode through the Shop's climb builder. A failed or slow read
       leaves the mirror as it was, which degrades to the legacy door. */
    var unlimRead = Promise.resolve(null);
    try{
      if(window.HOHContractData && typeof window.HOHContractData.unlimitedState === 'function'){
        var uRaw=window.HOHContractData.unlimitedState(true);
        unlimRead = new Promise(function(resolve){
          var settled=false, timer=setTimeout(function(){ if(!settled){ settled=true; resolve(null); } },6000);
          Promise.resolve(uRaw).then(function(v){ if(settled)return; settled=true;clearTimeout(timer);resolve(v); },function(){ if(settled)return;settled=true;clearTimeout(timer);resolve(null); });
        });
      }
    }catch(_){ }
    /* A ticket mirror is only paint, never authority. Refresh it for this exact
       credential before deciding that the player has no entry. This closes
       A→B account switches and cold-page races without delaying the explicit
       local god sandbox. The saved-climb path remains resumable even if the
       wallet read is temporarily unavailable because resume consumes nothing. */
    var ticketRead = Promise.resolve({ok:true,local:true});
    if(!localSandbox()){
      if(typeof window.hohSyncBgTickets !== 'function') ticketRead=Promise.resolve({ok:false,status:'still_loading'});
      else {
        try{
          var rawTicketRead=window.hohSyncBgTickets();
          ticketRead=new Promise(function(resolve){
            var settled=false,timer=setTimeout(function(){if(!settled){settled=true;resolve({ok:false,status:'still_loading'});}},6000);
            Promise.resolve(rawTicketRead).then(function(value){if(settled)return;settled=true;clearTimeout(timer);resolve(value||{ok:false,status:'still_loading'});},function(){if(settled)return;settled=true;clearTimeout(timer);resolve({ok:false,status:'still_loading'});});
          });
        }catch(_){ ticketRead=Promise.resolve({ok:false,status:'still_loading'}); }
      }
    }
    var work = Promise.all([stateRead,ticketRead,unlimRead]).then(function(reads){
      if(!stillCurrent()) return {ok:false,status:'account_changed'};
      checkedClimb = reads[0]; checkedUnlimited = reads[2];
      var active = checkedUnlimited && checkedUnlimited.mode_open ? checkedUnlimited : checkedClimb;
      if(!(active && active.ok === true && active.live && active.run_id != null) && reads[1] && reads[1].ok === false){
        return {ok:false,status:reads[1].status || 'still_loading'};
      }
      return decide();
    },function(){ return {ok:false,status:'state_unavailable'}; });
    var record={scope:activationScope,promise:null};
    record.promise = work.then(function(res){
      if(window.__hohBgActivatePromise===record) window.__hohBgActivatePromise = null;
      return res;
    }, function(err){
      if(window.__hohBgActivatePromise===record) window.__hohBgActivatePromise = null;
      return { ok:false, status:'failed', error:err };
    });
    window.__hohBgActivatePromise=record;
    return record.promise;
  };
  window.hohPaintBgDoor = function(host){
    host = host || document.getElementById('hoh-yh-bg-door');
    if(!host) return;
    var god = false;
    try { god = typeof window.hohIsGodMode === 'function' && window.hohIsGodMode(); } catch(_){}
    var enabled = !!window.HOH_CONTRACT_ENABLED || god;
    var live = !!document.getElementById('hoh-contract');
    /* R381: while the party builder owns the stage the scene steps aside too —
       one thing on screen at a time inside the Battlegrounds tab. */
    var building = false;
    try { building = document.body.classList.contains('hoh-bg-building'); } catch(_){}
    if(!enabled || live || building){ host.__hohDoorHtml = ''; host.innerHTML = ''; return; }
    var held = (typeof window.hohGetBgTickets === 'function') ? window.hohGetBgTickets() : 0;
    /* ============ R466 · A SAVED CLIMB IS NOT A PURCHASE ============
       Jan: "if they click back to hall then make sure that the hall button
       says 'resume' instead of 1 ticket, etc across the baord in the hall."

       He is right, and it was worse than a label. All three branches below
       were keyed on the entry WALLET alone, so a player who paused at stage 24
       with an empty wallet met a greyed "Needs 1 entry" and had NO WAY BACK
       into a run he had already paid for. Resuming charges nothing: the
       Battlegrounds passes _prepaid on a live row and re-adopts the same rung.
       The door just never knew a climb existed. */
    var climb = bgCurrentClimb();
    var onClimb = !!(climb && climb.live && climb.run_id != null);
    var bgAuthState='ready';
    try{
      var bgLocal=typeof window.hohContractIsLocal==='function'&&window.hohContractIsLocal();
      if(!bgLocal){
        var bgEmail=String((typeof window.hohYourHallEmail==='function'&&window.hohYourHallEmail())||'').trim();
        var bgToken=JSON.parse(localStorage.getItem('hoh-your-hall-token')||'null');
        var bgPin=String(bgToken&&bgToken.pin||'');
        if(!bgEmail||!bgPin)bgAuthState='sign_in_required';
        else if(!window.hohSupabaseClient)bgAuthState='still_loading';
      }
    }catch(_){bgAuthState='sign_in_required';}
    /* R623: one Unlimited state read per paint cycle, guarded exactly like the
       climb read below so a door that repaints on a timer cannot spawn a
       request every tick. The door repaints on hoh:bg-unlimited-changed. */
    var unlim = bgCurrentUnlimited();
    if(unlim && unlim.mode_open) onClimb = false;
    if(!unlim && window.HOHContractData && typeof window.HOHContractData.unlimitedState === 'function'){
      if(!window.__hohBgUnlimitedAsked){
        window.__hohBgUnlimitedAsked = 1;
        try {
          window.HOHContractData.unlimitedState(true).then(function(){
            try { window.hohPaintBgDoor(); } catch(_){}
          }).catch(function(){});
        } catch(_){}
      }
    }
    if(!climb && window.HOHContractData && typeof window.HOHContractData.ladderState === 'function'){
      /* one read, then repaint. Guarded so a door that repaints on a timer
         cannot spawn a request every tick. */
      if(!window.__hohBgClimbAsked){
        window.__hohBgClimbAsked = 1;
        try {
          window.HOHContractData.ladderState(true).then(function(){
            try { window.hohPaintBgDoor(); } catch(_){}
          }).catch(function(){});
        } catch(_){}
      }
    }
    var doneToday = (typeof window.hohBgExploreClaimedToday === 'function')
      ? window.hohBgExploreClaimedToday() : !!window.__hohBgExploreClaimed;
    /* R381 (Jan): the door is now THE scene of its own tab, not a box under
       Explore. Three ticket states, every path tappable:
       - hold tickets: one Enter button and your count
       - none, Explore unfinished: FREE path (jumps to the Explore tab) and
         the Shop path (jumps to the Shop tab), side by side
       - none, Explore done: the free path shows claimed, the Shop remains */
    /* R484: chrome glyphs, drawn. 22px viewBox, currentColor, so each one
       takes the colour of the card it sits in and needs no second asset. */
    var _BG_ICON = {
      door: '<svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round">' +
            '<path d="M4 21V8a8 8 0 0 1 16 0v13"/><path d="M4 21h16"/><circle cx="15" cy="13" r="1.1" fill="currentColor" stroke="none"/></svg>',
      shop: '<svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round">' +
            '<path d="M3 9h18l-1.2-4.2A1.5 1.5 0 0 0 18.35 3.7H5.65A1.5 1.5 0 0 0 4.2 4.8Z"/>' +
            '<path d="M4.5 9v10.5A1.5 1.5 0 0 0 6 21h12a1.5 1.5 0 0 0 1.5-1.5V9"/><path d="M9.5 21v-5.5h5V21"/></svg>',
      check: '<svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round">' +
            '<path d="M4 12.5l5.2 5.2L20 7"/></svg>',
      /* R647: a plus inside a card outline, for "put your own hero in the
         Hall". Drawn, never generated (house rule 4). */
      hero: '<svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round">' +
            '<rect x="4" y="3" width="16" height="18" rx="2.4"/><path d="M12 8.4v7.2"/><path d="M8.4 12h7.2"/></svg>'
    };
    /* ⛔ R647 · THE TWO WAYS IN, HOISTED, BECAUSE THREE STATES NEED THEM.
       R484 built this pair for the one branch that had run out of entries, and
       when R623 added the Unlimited card its two LOCKED states each got a flat
       grey sentence instead: "Add your hero in the Hall, or open a pack in the
       Shop." and "You have N. Finish Explore the Hall for a free entry."
       Both name the cure and neither can be tapped, so the newest mode's dead
       ends were worse than the oldest one's. Same problem shape, same answer,
       written once. */
    function _bgWaysHtml(){
      return '<div class="hoh-bgscene-ways">' +
        (doneToday
          ? '<div class="hoh-bgscene-way is-done">' +
              '<span class="hoh-bgscene-wayico" aria-hidden="true">' + _BG_ICON.check + '</span>' +
              '<b>Done</b><span>Explore&rsquo;s free entry claimed</span><small>Another tomorrow.</small></div>'
          : '<button type="button" class="hoh-bgscene-way is-free" id="hohBgDoorExplore">' +
              '<span class="hoh-bgscene-wayico" aria-hidden="true">' + _BG_ICON.door + '</span>' +
              '<b class="hoh-bgscene-freetag">Free</b><span>Complete Explore the Hall</span>' +
              /* R793: the live Hall's Explore is 20 new heroes in the main Hall.
                 R793.1 (Jan): the line about paying an entry is gone. Players
                 do not know what an entry is; the Free tag above says enough. */
              (((typeof window.hohR793Live === 'function') && window.hohR793Live())
                ? '<small>Meet 20 new heroes in the main Hall.</small></button>'
                : '<small>Finish today&rsquo;s run. It pays one entry.</small></button>')) +
        '<button type="button" class="hoh-bgscene-way is-shop" id="hohBgDoorShop">' +
          '<span class="hoh-bgscene-wayico" aria-hidden="true">' + _BG_ICON.shop + '</span>' +
          '<b>Entries</b><span>Open the Entry Shop</span>' +
          '<small>Claim a free entry or buy one for 300 hearts.</small></button>' +
      '</div>';
    }
    /* R647: and the OTHER shortage. Missing heroes and missing entries are not
       the same problem, so they do not get the same two doors: no entry price
       belongs on a screen where the player already holds a free run and simply
       has nobody to send. Both of these go somewhere. */
    function _bgHeroWaysHtml(){
      return '<div class="hoh-bgscene-ways">' +
        '<button type="button" class="hoh-bgscene-way is-free" id="hohBgDoorHall">' +
          '<span class="hoh-bgscene-wayico" aria-hidden="true">' + _BG_ICON.hero + '</span>' +
          '<b class="hoh-bgscene-freetag">Free</b><span>Add your own hero</span>' +
          '<small>Put your character in the Hall and play them.</small></button>' +
        '<button type="button" class="hoh-bgscene-way is-shop" id="hohBgDoorShop">' +
          '<span class="hoh-bgscene-wayico" aria-hidden="true">' + _BG_ICON.shop + '</span>' +
          /* the Shop's own promise, word for word off its pack tile
             (assets/hoh-shop.js: "Five cards a pack. Every card is yours to
             keep."), so the two surfaces cannot drift into two offers */
          '<b>Packs</b><span>Open a pack in the Shop</span>' +
          '<small>Five cards a pack, all yours to keep.</small></button>' +
      '</div>';
    }
    /* ============ R623 · THE UNLIMITED CARD, Part 5d S1 ============
       One container, a vertical stack, no inner boxes, NO POT COPY and no
       climb line. Four states, ONE LINE EACH, never two:
         live run          -> the line says the round, the button says Continue
         fewer than five   -> the line says how many you have, button disabled
         no entry held     -> the button carries the way to get one
         mode not open     -> this whole branch never runs and today's card
                              stands, which is what keeps a push invisible
       ⛔ "Best · Round N" IS OMITTED BELOW 1. A best of zero is a state that
       is always true for a new player and reads as broken, which is the same
       no-zero rule R459 and R486 each paid for once. */
    var rows = '';
    var unlimOpen = !!(unlim && unlim.mode_open) && !onClimb;
    /* the scene's own three lines, so the Unlimited branch swaps copy rather
       than forking the whole card and its wiring into a second copy */
    var _uEyebrow = 'The Battlegrounds';
    var _uDoorLine = 'One entry, one run. Winner takes the pot.';
    var _uDoorExtra = '';
    if(unlimOpen){
      var uRound = Math.max(1, unlim.round | 0);
      var uBest = unlim.best_round | 0;
      var uOwned = bgUnlimitedOwnedCount();
      /* R647: null means the collection has never landed on this page, and
         this card is the only thing that ever needed it here. Ask once. */
      if(uOwned == null) bgUnlimitedEnsureCollection();
      var uLine = 'Fight round after round until your party falls.';
      var uHearts = 0;
      try { uHearts = (window.HOHWallet && typeof window.HOHWallet.hearts === 'function') ? (window.HOHWallet.hearts() | 0) : 0; } catch(_){ uHearts = 0; }
      if(unlim.live && unlim.run_id != null){
        /* R778 (Jan): the card says Resume and how long you have been away.
           A trip is the party fighting on without you; away_seconds is the
           run waiting since this account's driver last checked in. Both come
           off the mirror's away block (hoh-contract-data.js), plus the time
           since that block was fetched, so a card left open keeps counting. */
        var uAway = unlim.away || null;
        var uSince = uAway && typeof uAway.fetched_at === 'number' ? Math.max(0, Math.floor((Date.now() - uAway.fetched_at) / 1000)) : 0;
        if(uAway && uAway.trip && uAway.trip.open){
          var uTrip = (uAway.trip.seconds | 0) + uSince;
          uLine = (bgAwayFought(unlim, uAway.trip, uTrip) ? 'Your party kept fighting' : 'Away mode is on') +
            ' &middot; away ' + bgAwayDur(uTrip, true);
        } else if(uAway && (uAway.away_seconds | 0) >= 120){
          uLine = 'Your run is waiting at Round ' + uRound + ' &middot; away ' + bgAwayDur((uAway.away_seconds | 0) + uSince, false);
        } else {
          uLine = 'Your run is going &middot; Round ' + uRound;
        }
        rows = '<button type="button" class="hoh-bgscene-enter" id="hohBgDoorEnter" data-resume="1">Resume' +
          '<small>No entry needed</small></button>';
      } else if(uOwned != null && uOwned < bgUnlimitedMinimumOwned()){
        /* R647: the count is worth stating as PROGRESS, because it is. One
           sentence, and the number that moves is the number on the screen. */
        uLine = 'Bring ' + bgUnlimitedMinimumOwned() + (bgUnlimitedMinimumOwned() === 1 ? ' hero' : ' heroes') + ' of your own to fight.';
        try {
          var submitted = typeof window.hohYourHallSubmittedRows === 'function' ? window.hohYourHallSubmittedRows() : [];
          if(submitted.some(function(c){ return c.is_hidden === true || !String(c.image_url || c.image_thumb_url || '').trim(); })) {
            uLine = 'Your hero is saved, but not battle-ready yet. It needs a portrait and must be visible in the Hall.';
          }
        } catch(_) {}
        rows = '<button type="button" class="hoh-bgscene-enter is-locked" id="hohBgDoorEnter" disabled aria-disabled="true">' +
            'Start a run<small>' + uOwned + ' of ' + bgUnlimitedMinimumOwned() + (bgUnlimitedMinimumOwned() === 1 ? ' hero' : ' heroes') + '</small></button>' +
          _bgHeroWaysHtml();
      } else if(held > 0){
        rows = '<button type="button" class="hoh-bgscene-enter" id="hohBgDoorEnter">Start a run<small>1 entry</small></button>' +
          '<div class="hoh-bgscene-note">You currently have: ' + held + ' Battlegrounds ' + (held === 1 ? 'entry' : 'entries') + '.</div>';
      } else if(uHearts >= 300){
        /* ⛔ THE PRICE IS ON A BUTTON THAT ACTUALLY DOES SOMETHING. S1 asks
           for "Start a run · 300 ♥", and a door that names a price it cannot
           charge is a dead end: this card holds no purchase, the Shop does.
           So the button says where the entry comes from and goes there. */
        rows = '<button type="button" class="hoh-bgscene-enter" id="hohBgDoorShop">Get an Entry' +
            '<small>Buy or claim in the Shop</small></button>';
      } else {
        /* R647: the same dead end R484 already fixed on the legacy door, so it
           gets the same two tappable ways rather than a sentence naming them. */
        rows = '<button type="button" class="hoh-bgscene-enter is-locked" id="hohBgDoorEnter" disabled aria-disabled="true">' +
            'Start a run<small>Needs 1 entry</small></button>' +
          _bgWaysHtml();
      }
      /* ⛔ R647 · JAN: "dont call it a unlimited run, just call it
         battlegrounds". He is right, and it was not only a label: the Shop
         sells this as BATTLEGROUNDS ENTRY, the game panel's own eyebrow says
         The Battlegrounds, the tab says Battlegrounds, and every other state
         of THIS CARD said The Battlegrounds too. Only the open-mode branch
         renamed the game under the player, so one product wore two names on
         one screen. The climb is being retired, which makes Unlimited simply
         what Battlegrounds is, and a mode does not need a second name for
         itself.
         The 20px inline override went with it: the eyebrow's own rule is
         15px/.26em, this is the longer of the two words, and an override that
         only ever applied to one branch of one card is how a type ramp stops
         being a ramp. */
      _uDoorLine = uLine;
      _uDoorExtra = uBest >= 1
        ? '<div class="hoh-bgscene-note">Best &middot; <b style="color:#f2d98e;font-size:15px">Round ' + uBest + '</b></div>'
        : '';
    } else if(onClimb){
      /* the climb outranks every wallet state, because it is already bought.
         No price, no lock, no ways to afford it: there is nothing to afford. */
      rows =
        '<button type="button" class="hoh-bgscene-enter" id="hohBgDoorEnter" data-resume="1">Resume your climb' +
          '<small>Stage ' + Math.max(1, climb.stage | 0) + ' &middot; no entry needed</small></button>' +
        '<div class="hoh-bgscene-note">Your run is saved where you left it.' +
          (held > 0 ? ' You also hold ' + held + ' ' + (held === 1 ? 'entry' : 'entries') + ' for later.' : '') +
        '</div>';
    } else if(bgAuthState==='sign_in_required'){
      rows =
        '<button type="button" class="hoh-bgscene-enter" id="hohBgDoorEnter" data-sign-in="1">Sign in for Battlegrounds<small>Your email and PIN keep entries with your account</small></button>'+
        '<div class="hoh-bgscene-note">Finish signing in before claiming or spending an entry.</div>';
    } else if(bgAuthState==='still_loading'){
      rows =
        '<button type="button" class="hoh-bgscene-enter is-locked" id="hohBgDoorEnter" disabled aria-disabled="true">Checking your entries<small>The Battlegrounds desk is loading</small></button>';
    } else if(held > 0){
      rows =
        '<button type="button" class="hoh-bgscene-enter" id="hohBgDoorEnter">Enter the Battlegrounds<small>1 entry</small></button>' +
        '<div class="hoh-bgscene-note">You currently have: ' + held + ' Battlegrounds ' + (held === 1 ? 'entry' : 'entries') + '.</div>';
    } else {
      /* v39 (Jan): THE DOOR KEEPS ITS DOOR. This branch used to delete the
         Enter button outright, so the one thing the whole card is built around
         vanished and the screen read as somewhere else entirely. The button is
         the anchor: it stays exactly where it always sits, greyed, naming its
         own price, with the two ways to afford it underneath. Disabled, so it
         cannot be tapped and cannot be focused into a dead end, and the ways
         below carry the actual action. */
      rows =
        '<button type="button" class="hoh-bgscene-enter is-locked" id="hohBgDoorEnter" disabled aria-disabled="true">Enter the Battlegrounds<small>Needs 1 entry</small></button>' +
        '<div class="hoh-bgscene-note">You need a Battlegrounds entry.</div>' +
        /* ============ R484 · THE TWO WAYS IN LOOK LIKE SOMETHING ==========
           Jan: "the text here when you dont have an entry also looks pretty
           basic and not stylized, it should feel exciting. Like explore the
           hall that says free should be in the mythic colors of explore the
           hall, and the shop should also have at least a shop icon or
           something and same with explore the hall."

           This is the screen a player hits the moment they run out, so it is
           the screen that decides whether they come back today. It was two
           grey boxes of text with no picture in either of them.

           THE FREE ONE WEARS THE MYTHIC GRADIENT, #ef94ff to #b3f5ee, which is
           the brand pair the Hall already uses for a mythic ring and for the
           summon word. It is the right colour here for a reason beyond looking
           good: the free entry comes FROM Explore the Hall, so the badge is
           now the same colour as the thing it is sending you to do.

           BOTH CARRY AN ICON. A doorway for the Hall, a shop awning for the
           shop, drawn as inline SVG rather than an image: house rule 4 keeps
           real paintings as the only pictures on this site, and chrome is
           drawn, never generated.

           R647: hoisted to _bgWaysHtml above, character for character, because
           the Unlimited card's own two locked states need exactly this pair and
           were each shipping a flat grey sentence instead. */
        _bgWaysHtml();
    }
    /* R382: crest + embers inside the card. R385 (Jan): the champions parade
       lives INSIDE the scene card now — one module, not a strip floating
       under the border. */
    var _doorHtml =
      '<div class="hoh-bgscene" data-has="' + (held > 0 ? '1' : '0') + '"' +
        (unlimOpen ? ' data-unlimited="1"' : '') + '>' +
        '<span class="hoh-bgscene-glow" aria-hidden="true"></span>' +
        '<span class="hoh-bgscene-embers" aria-hidden="true">' +
          '<i style="left:16%;animation-delay:-1.4s"></i><i style="left:37%;animation-delay:-3.6s"></i>' +
          '<i style="left:62%;animation-delay:-.7s"></i><i style="left:84%;animation-delay:-2.5s"></i>' +
        '</span>' +
        '<span class="hoh-bgscene-swords" aria-hidden="true">' + _BG_SWORDS_SVG + '</span>' +
        '<div class="hoh-bgscene-eyebrow">' + _uEyebrow + '</div>' +
        '<div class="hoh-bgscene-line">' + _uDoorLine + '</div>' +
        _uDoorExtra +
        /* R389 (Jan): the parade rides ABOVE the Enter button — see the
           champions, then step through them.

           ⛔ R648 (Jan): "we had a rolling carousel of charactes, and you just
           stripped it all and made it plain wtf?" — about the intro screen.
           R623 gated this out on an open mode, and its reason was WRONG ABOUT
           ITS OWN CSS: it called the parade "a bordered strip inside it" and
           dropped it under R-6 rule 2 (one container, no inner boxes). Read the
           stylesheet and there is no border and no box. `.hoh-bgscene-champs`
           is a hairline `::before` divider, a small-caps heading, and a rail
           whose `margin:24px -26px 4px` BLEEDS OUT to the card's own edge (the
           -26px is the card's desktop padding, -18px its phone padding). That
           is the no-boxes-inside-boxes shape, not a violation of it.

           It stayed invisible for as long as the mode was shut, so nobody saw
           the loss until the stamp moved and Jan opened his own front door to
           a plain card. One name for the mode, one door for the mode: the
           parade rides on every state again. */
        _bgChampsHtml() +
        rows +
      '</div>';
    if(host.__hohDoorHtml === _doorHtml) return;   /* identical state, keep the DOM */
    host.__hohDoorHtml = _doorHtml;
    host.innerHTML = _doorHtml;
    /* champions parade wiring: tap a chip, meet the hero. One listener per
       paint (the strip is replaced with the innerHTML above). */
    var champs = host.querySelector('.hoh-bgscene-champs');
    if(champs) champs.addEventListener('click', function(e){
      var b = e.target && e.target.closest ? e.target.closest('.hoh-bgscene-champ') : null;
      if(!b) return;
      var id = b.getAttribute('data-champ');
      try { if(id && typeof window.hohOpenDetail === 'function') window.hohOpenDetail(id); } catch(_){}
    });
    /* pool not loaded on the very first paint: repaint once when it lands */
    if(!champs && !window.__hohBgChampsRetry){
      window.__hohBgChampsRetry = 1;
      setTimeout(function(){
        try {
          var h = document.getElementById('hoh-yh-bg-door');
          if(h && h.querySelector('.hoh-bgscene') && !h.querySelector('.hoh-bgscene-champs')){
            h.__hohDoorHtml = null;
            window.hohPaintBgDoor(h);
          }
        } catch(_){}
      }, 2500);
    }
    var enter = host.querySelector('#hohBgDoorEnter');
    if(enter) enter.addEventListener('click', function(){
      if(enter.disabled) return;
      if(enter.getAttribute('data-sign-in')==='1'){
        try{if(typeof window.hohSignInToYourHall==='function')window.hohSignInToYourHall();}catch(_){}
        return;
      }
      if(typeof window.hohActivateBattlegroundsEntry !== 'function') return;
      var old = enter.innerHTML;
      enter.disabled = true;
      enter.setAttribute('aria-busy','true');
      enter.innerHTML = 'Opening Battlegrounds<small>Checking your run</small>';
      window.hohActivateBattlegroundsEntry().then(function(res){
        enter.removeAttribute('aria-busy');
        if(res && res.ok) return;
        enter.disabled = false;
        enter.innerHTML = old;
        try{ window.hohPaintBgDoor(); }catch(_){ }
        try{
          if(typeof window.hohShowHeartToast === 'function'){
            if(res && res.status === 'free_changed') window.hohShowHeartToast('Your free run was already used','The entry button has been updated.');
            else if(res && res.status === 'sign_in_required'){
              window.hohShowHeartToast('Sign in for Battlegrounds','Use your email and PIN so entries stay with your account.');
              try{if(typeof window.hohSignInToYourHall==='function')window.hohSignInToYourHall();}catch(_){}
            }
            else if(res && res.status === 'no_entry') window.hohShowHeartToast('You need an entry','Finish Explore the Hall or buy one in the Shop.');
            /* R623 (R-2): named before anything is claimed or spent, so the
               refusal never costs the player an entry to discover. */
            /* R647: the same sentence the card itself now shows, so a player
               who taps a stale button is not told a different story. */
            else if(res && res.status === 'need_heroes') window.hohShowHeartToast('Bring a hero to play','You need ' + res.needed + '. Add your own hero in the Hall, or open a pack in the Shop.');
            else window.hohShowHeartToast('Battlegrounds did not open','Please try again in a moment.');
          }
        }catch(_){ }
      });
    });
    /* R400: the cooldown chip counts down by the minute while it shows */
    if(_doorHtml.indexOf('hoh-bgscene-daily') >= 0 && !window.__hohBgDailyTick){
      /* PERF v39: a backgrounded tab repainted this door every minute for as
         long as it stayed open. The paint is guarded on identical html so it
         was usually a no-op, but it still woke the main thread and cost battery
         on a phone. The rollover watcher above repaints on return anyway. */
      window.__hohBgDailyTick = setInterval(function(){ if(document.hidden) return; try { window.hohPaintBgDoor(); } catch(_){} }, 60000);
    }
    var explBtn = host.querySelector('#hohBgDoorExplore');
    if(explBtn) explBtn.addEventListener('click', function(){
      /* R793: in the live Hall Explore is the main Hall's event. */
      if((typeof window.hohR793Live === 'function') && window.hohR793Live() && typeof window.hohExploreEventOpen === 'function'){
        window.hohExploreEventOpen();
        return;
      }
      try { if(typeof window.hohYourHallSetEventsTab === 'function') window.hohYourHallSetEventsTab('explore'); } catch(_){}
      setTimeout(function(){
        try {
          var ff = document.getElementById('hoh-fresh-faces');
          if(ff) ff.scrollIntoView({ behavior:'smooth', block:'start' });
        } catch(_){}
      }, 60);
    });
    var shopBtn = host.querySelector('#hohBgDoorShop');
    if(shopBtn) shopBtn.addEventListener('click', function(){
      try { if(typeof window.hohYourHallSwitchView === 'function') window.hohYourHallSwitchView('shop'); } catch(_){}
    });
    /* R647: the five-hero card's free way. hohOpenForm is the Hall's own
       "+ Add Your Hero" entry (assets/hoh-section-core.js), the same one the
       gallery's button calls, so this goes exactly where the sentence it
       replaced was pointing. */
    var hallBtn = host.querySelector('#hohBgDoorHall');
    if(hallBtn) hallBtn.addEventListener('click', function(){
      try { if(typeof window.hohOpenForm === 'function') window.hohOpenForm(); } catch(_){}
    });
    if(!window.__hohBgDoorWired){
      window.__hohBgDoorWired = 1;
      document.addEventListener('hoh:bg-tickets-changed', function(){ try { window.hohPaintBgDoor(); } catch(_){} });
      /* R466: and when a climb opens, ends or is given up */
      document.addEventListener('hoh:bg-climb-changed', function(){ try { window.hohPaintBgDoor(); } catch(_){} });
      /* R623: and when an Unlimited run opens, advances a round, or is freed */
      document.addEventListener('hoh:bg-unlimited-changed', function(){ try { window.hohPaintBgDoor(); } catch(_){} });
    }
    try {
      var _syncScope=(function(){
        var em='guest',pn='';
        try{em=String((typeof window.hohYourHallEmail==='function'&&window.hohYourHallEmail())||'guest').trim().toLowerCase()||'guest';}catch(_){}
        try{var tok=JSON.parse(localStorage.getItem('hoh-your-hall-token')||'null');pn=String(tok&&tok.pin||'');}catch(_){}
        var h=2166136261,s=em+'\u0000'+pn;for(var i=0;i<s.length;i++){h^=s.charCodeAt(i);h=Math.imul(h,16777619);}
        return em+':'+(h>>>0).toString(16);
      })();
      var _isLocal=false;try{_isLocal=typeof window.hohContractIsLocal==='function'&&window.hohContractIsLocal();}catch(_){}
      if(_isLocal) window.__hohBgSyncedAccount=_syncScope;
      else if(typeof window.hohSyncBgTickets === 'function'
        && window.__hohBgSyncedAccount!==_syncScope
        && window.__hohBgSyncPendingAccount!==_syncScope
        && (!window.__hohBgSyncRetryAt||Date.now()>=window.__hohBgSyncRetryAt)){
        window.__hohBgSyncPendingAccount=_syncScope;
        window.hohSyncBgTickets().then(function(res){
          if(window.__hohBgSyncPendingAccount===_syncScope)window.__hohBgSyncPendingAccount='';
          if(res&&res.ok){window.__hohBgSyncedAccount=_syncScope;window.__hohBgSyncRetryAt=0;try{window.hohPaintBgDoor();}catch(_){}}
          else window.__hohBgSyncRetryAt=Date.now()+1200;
        },function(){if(window.__hohBgSyncPendingAccount===_syncScope)window.__hohBgSyncPendingAccount='';window.__hohBgSyncRetryAt=Date.now()+1200;});
      }
    } catch(_){}
  };
  window.hohEnterBattlegrounds = function(){
    try {
      /* R380: entering from the tray closes the builder stage cleanly. */
      try {
        if(window.__hohCollMode === 'builder'){
          window.__hohCollMode = '';
          window.__hohBuilderRepaint = null;
          document.body.classList.remove('hoh-bg-building');
          var _st = document.getElementById('hoh-yh-bg-stage');
          if(_st) _st.innerHTML = '';
        }
      } catch(_){}
      var c = _ffContainer();
      var hostEl = document.getElementById('hoh-contract');
      if(!hostEl || (c && c.contains(hostEl))){
        var fresh = document.createElement('div');
        fresh.id = 'hoh-contract';
        fresh.className = 'hoh-contract-host';
        if(hostEl && hostEl.parentNode) hostEl.parentNode.removeChild(hostEl);
        /* R381: the fight lives in the Battlegrounds pane now, first child so
           the (blanked) door div sits under it. Legacy fallback keeps the old
           beside-Explore mount for any context without the pane. */
        var _bgPane = document.getElementById('hoh-yh-evpane-bg');
        if(_bgPane) _bgPane.insertBefore(fresh, _bgPane.firstChild);
        else if(c && c.parentNode) c.parentNode.insertBefore(fresh, c.nextSibling);
        else document.body.appendChild(fresh);
        hostEl = fresh;
      } else {
        /* Host already mounted elsewhere (rematch, reroute) — pull it into
           the pane if the pane exists and doesn't hold it yet. Same node,
           only re-homed. */
        try {
          var _bgPane2 = document.getElementById('hoh-yh-evpane-bg');
          if(_bgPane2 && !_bgPane2.contains(hostEl)) _bgPane2.insertBefore(hostEl, _bgPane2.firstChild);
        } catch(_){}
      }
      /* R381: the Battlegrounds sub tab comes up so the fight is on screen. */
      try { if(typeof window.hohYourHallSetEventsTab === 'function') window.hohYourHallSetEventsTab('bg'); } catch(_){}
      if(typeof window.hohContractUnlock === 'function') window.hohContractUnlock();
      setTimeout(function(){ try { hostEl.scrollIntoView({ behavior: 'smooth', block: 'center' }); } catch(_){} }, 80);
      /* re-render the panel so it flips from the "enter" CTA to the quiet
         "live" line now that the fight is mounted below. */
      try { if(window.hohRenderFreshFaces) window.hohRenderFreshFaces(); } catch(_){}
      /* R376: the door goes quiet while the fight owns the screen. */
      try { if(window.hohPaintBgDoor) window.hohPaintBgDoor(); } catch(_){}
    } catch(_){}
  };
  /* Compat shim: older callers ask this to paint the morph. The entry now
     renders inside _wanderBarHtml, so this only reports whether the entry
     owns the completed state (used to suppress the plain reset countdown). */
  function _paintContractMorph(c){
    if(!c) return false;
    var legacy = c.querySelector('#hoh-contract-entry:not(.hoh-bg-entry)');
    if(legacy){ legacy.hidden = true; }
    return !!c.querySelector('.hoh-bg-entry');
  }
  function paintQuestProgress(){
    if(__paintScheduled) return;
    __paintScheduled = true;
    var run = function(){
      __paintScheduled = false;
      try { paintQuestProgressNow(); } catch(_){}
    };
    /* R141-C: expose for cross-module callers (server daily-claims
       sync needs this to trigger a repaint after pre-marking
       milestones from the server). */
    if(typeof window !== 'undefined'){
      window.hohPaintQuestProgress = paintQuestProgress;
    }
    /* requestAnimationFrame returns 0 on some pages where rAF is
       unavailable (very rare); fall back to a 16ms timeout. */
    if(typeof requestAnimationFrame === 'function'){
      requestAnimationFrame(run);
    } else {
      setTimeout(run, 16);
    }
  }
  function paintQuestProgressNow(){
    var c = _ffContainer();
    if(!c) return;
    var label = c.querySelector('#hoh-fresh-quest-label');
    var fill = c.querySelector('#hoh-fresh-quest-fill');
    if(!label || !fill){
      /* Bar not in the DOM — stop the reset countdown interval. */
      if(_wanderResetTimer){ clearInterval(_wanderResetTimer); _wanderResetTimer = null; }
      return;
    }
    var n = getFfHeartsToday();
    var target = WANDER_QUEST_TARGET;
    var capped = Math.min(n, target);
    var pct = (capped / target) * 100;
    label.textContent = capped + '/' + target;
    fill.style.width = pct.toFixed(1) + '%';
    /* R56: walk every milestone and apply hit/ready/claimed classes. */
    WANDER_MILESTONES.forEach(function(m){
      var el = c.querySelector('#hoh-fresh-quest-mile-' + m);
      if(!el) return;
      var hit = (n >= m);
      var claimed = isMilestoneClaimed(m);
      el.classList.toggle('hoh-fresh-quest-mile-hit', hit);
      el.classList.toggle('hoh-fresh-quest-mile-ready', hit && !claimed);
      el.classList.toggle('hoh-fresh-quest-mile-claimed', hit && claimed);
    });
    var bar = c.querySelector('#hoh-fresh-quest');
    var signedIn = false;
    try {
      signedIn = (typeof window.hohYourHallIsSignedIn === 'function') ? !!window.hohYourHallIsSignedIn() : false;
      if(!signedIn && typeof window.hohYourHallEmail === 'function'){
        signedIn = !!window.hohYourHallEmail();
      }
    } catch(_){}
    /* R56: bar always visible. The "complete" greyed style fires only
       when ALL milestones (including the final ticket) are claimed.
       Anonymous users still get a claim CTA prompting sign-in once
       the FIRST milestone is reached — once they sign in, the click
       handler banks the actual server-side ticket. */
    if(bar){
      bar.style.display = '';
      var allClaimed = WANDER_MILESTONES.every(isMilestoneClaimed);
      bar.classList.toggle('hoh-fresh-quest-complete', allClaimed);
      /* R150: once every milestone is claimed, show a subtle countdown to the
         daily reset. When the daily is fully complete the WHOLE panel is
         replaced by the Battlegrounds in hohRenderFreshFaces, so this bar (and
         its countdown) is only a brief transient before that takeover. */
      var resetEl = c.querySelector('#hoh-fresh-quest-reset');
      if(resetEl){
        if(allClaimed){
          resetEl.textContent = _wanderResetCountdownText();
          resetEl.style.display = '';
          if(!_wanderResetTimer){
            _wanderResetTimer = setInterval(function(){
              if(document.hidden) return;   /* PERF v39 */
              try { paintQuestProgress(); } catch(_){}
            }, 30000);
          }
        } else {
          resetEl.style.display = 'none';
          if(_wanderResetTimer){ clearInterval(_wanderResetTimer); _wanderResetTimer = null; }
        }
      }
    }
    /* R140-Z2: Only count TICKET milestones for the sign-in CTA —
       heart-bonus pips don't need sign-in to claim, so showing
       "Sign in to use your tickets" after only a heart-bonus milestone
       reads as a non-sequitur. Filter out heart-bonus pips first.
       R406: reads the shared list, which tests `=== undefined` rather
       than truthiness. Same answer today, but the truthy form would
       misread any pip whose bonus value was ever set to 0 — and with a
       milestone literally numbered 0 in the ladder now, this file has
       no business leaning on truthiness anywhere near these maps. */
    var TICKET_MILESTONES = WANDER_TICKET_MILESTONES;
    if(!signedIn && TICKET_MILESTONES.length && n >= TICKET_MILESTONES[0]){
      var claimedCount = TICKET_MILESTONES.filter(function(m){ return n >= m; }).length;
      ensureClaimButton(c, claimedCount);
    } else {
      removeClaimButton(c);
    }
    /* Claim All remains available during the journey. At the target there is
       no extra choice: freeze Explore and collect the entire ready set,
       including the final reward, through the canonical claim path. */
    if(!_ffClaimAllBusy){
      var readyAll = signedIn
        ? WANDER_MILESTONES.filter(function(m){ return n >= m && !isMilestoneClaimed(m); })
        : [];
      if(n >= target && signedIn){
        removeClaimAllButton(c);
        _beginWanderCompletion();
      }
      else if(readyAll.length > 2) ensureClaimAllButton(c, readyAll.length);
      else removeClaimAllButton(c);
    }
  }

  function ensureClaimButton(c, ticketCount){
    var bar = c.querySelector('#hoh-fresh-quest');
    if(!bar) return;
    var existing = c.querySelector('#hoh-fresh-quest-claim');
    /* R136-claim: Reframed copy. The previous "Claim your tickets" was
       ambiguous about HOW the user claims them. The new label tells the
       user the action (sign in) and the outcome (use their tickets) so
       the CTA is self-explanatory. Singular form drops the count word
       entirely since the icon already communicates "ticket". */
    /* R140-Z3i: Copy clarified — "claim" reads as the action the user
       is about to take, "use" sounded like the ticket would be spent. */
    var label = (ticketCount === 1)
      ? 'Sign in to claim your ticket'
      : 'Sign in to claim your tickets';
    if(existing){
      /* Already mounted — only update the label so the singular →
         plural transition at 20 hearts doesn't recreate the button
         (and lose the user's hover/focus). */
      var lblEl = existing.querySelector('.hoh-fresh-quest-claim-label');
      if(lblEl) lblEl.textContent = label;
      return;
    }
    /* R136-claim2: Wrap the button + a small "Tickets?" info link in
       a single column so they stack neatly under the quest bar. The
       wrapper centers itself, the button sits on top with the ticket
       icon and CTA copy, and the info link sits below it as a quiet
       sub-action that opens the existing rules modal for users who
       want to know what tickets are before signing in. */
    var wrap = document.createElement('div');
    wrap.id = 'hoh-fresh-quest-claim';
    wrap.className = 'hoh-fresh-quest-claim-wrap';
    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'hoh-fresh-quest-claim';
    btn.innerHTML = '<span class="hoh-tk-icon" aria-hidden="true"></span>' +
      '<span class="hoh-fresh-quest-claim-label">' + label + '</span>';
    btn.addEventListener('click', function(e){
      e.preventDefault();
      if(typeof window.hohOpenAnonHallIntro === 'function'){
        try { window.hohOpenAnonHallIntro(); } catch(_){}
      } else if(typeof window.hohOpenForm === 'function'){
        /* Fallback if the anon intro modal isn't loaded yet. */
        try { window.hohOpenForm(); } catch(_){}
      }
    });
    wrap.appendChild(btn);
    /* Info link: opens the existing How It Works / rules modal. The
       hohOpenRules helper is the same handler the spotlight panel
       uses, so the user lands on the canonical explanation rather
       than a one-off panel. */
    var info = document.createElement('button');
    info.type = 'button';
    info.className = 'hoh-fresh-quest-claim-info';
    info.textContent = 'Tickets?';
    info.setAttribute('aria-label', 'Learn how raffle tickets work');
    info.addEventListener('click', function(e){
      e.preventDefault();
      e.stopPropagation();
      if(typeof window.hohOpenRules === 'function'){
        try { window.hohOpenRules(); } catch(_){}
      }
    });
    wrap.appendChild(info);
    bar.appendChild(wrap);
  }
  function removeClaimButton(c){
    var wrap = c.querySelector('#hoh-fresh-quest-claim');
    if(wrap && wrap.parentNode) wrap.parentNode.removeChild(wrap);
  }

  /* R254: CLAIM ALL — mounts under the bar when MORE THAN TWO pips sit
     ready (hit, unclaimed). One tap walks every ready milestone through
     the exact same claim path the pips use (server slot gates, heart
     flights, ticket flights, capstone confetti), staggered so each
     reward lands with its own beat. Members only — ticket pips need a
     signed-in email, and visitors already get the sign-in CTA. */
  var _ffClaimAllBusy = false;
  function ensureClaimAllButton(c, readyCount){
    var bar = c.querySelector('#hoh-fresh-quest');
    if(!bar) return;
    var existing = c.querySelector('#hoh-fresh-quest-claimall');
    if(existing){
      var l = existing.querySelector('.hoh-ffca-label');
      if(l) l.textContent = 'Claim all (' + readyCount + ')';
      return;
    }
    var wrap = document.createElement('div');
    wrap.id = 'hoh-fresh-quest-claimall';
    wrap.style.cssText = 'display:flex;justify-content:center;margin:12px 0 2px;';
    var btn = document.createElement('button');
    btn.type = 'button';
    btn.setAttribute('aria-label', 'Claim all ready milestones');
    btn.style.cssText = 'cursor:pointer;font-family:\'Cinzel\',serif;font-weight:700;font-size:11px;'+
      'letter-spacing:.12em;text-transform:uppercase;color:#1a1108;'+
      'background:linear-gradient(120deg,#f2d98e,#c8a45e);border:none;border-radius:10px;'+
      'padding:10px 22px;box-shadow:0 4px 16px -6px rgba(242,217,142,.55);transition:transform .15s ease,opacity .2s ease;';
    btn.innerHTML = '<span class="hoh-ffca-label">Claim all (' + readyCount + ')</span>';
    btn.onmouseenter = function(){ btn.style.transform = 'translateY(-1px)'; };
    btn.onmouseleave = function(){ btn.style.transform = ''; };
    btn.addEventListener('click', function(){ runClaimAll(btn); });
    wrap.appendChild(btn);
    bar.appendChild(wrap);
  }
  function removeClaimAllButton(c){
    var w = c.querySelector('#hoh-fresh-quest-claimall');
    if(w && w.parentNode) w.parentNode.removeChild(w);
  }
  function runClaimAll(btn){
    if(_ffClaimAllBusy) return;
    var n = getFfHeartsToday();
    /* R489: "Claim all" clears the ordinary checkpoints, then leaves the
       branded present on the rail for the player to open themselves. */
    var ready = WANDER_MILESTONES.filter(function(m){
      return m !== WANDER_FINAL_MILESTONE && n >= m && !isMilestoneClaimed(m);
    });
    if(!ready.length) return;
    _ffClaimAllBusy = true;
    if(btn){ btn.disabled = true; btn.style.opacity = '.65'; }
    var i = 0;
    (function step(){
      if(i >= ready.length){
        _ffClaimAllBusy = false;
        try { paintQuestProgress(); } catch(_){}
        return;
      }
      var m = ready[i++];
      if(btn){ var l = btn.querySelector('.hoh-ffca-label'); if(l) l.textContent = 'Claiming ' + i + '/' + ready.length + '…'; }
      try { window.hohFreshFacesClaimQuestMilestone(m); } catch(_){}
      setTimeout(step, 720);
    })();
  }

  /* Claim in order, then reuse the final gift ceremony over Explore's cooldown.
     The pack is saved by its existing server receipt; no Shop renderer opens. */
  var _ffAutoClaiming = false;
  var _ffQuestClosing = false;
  var _ffQuestCloseScheduled = false;
  var _ffCompletionRewards = null;
  function _hohAutoClaimAll(){
    if(_ffAutoClaiming || _ffClaimAllBusy) return;
    var n0 = getFfHeartsToday();
    var ready = WANDER_MILESTONES.filter(function(m){
      return n0 >= m && !isMilestoneClaimed(m);
    });
    if(!ready.length){
      if(WANDER_MILESTONES.every(isMilestoneClaimed)) _scheduleExploreClose(0);
      return;
    }
    _ffAutoClaiming = true; _ffClaimAllBusy = true;
    var i = 0;
    (function step(){
      if(i >= ready.length){
        _ffClaimAllBusy = false; _ffAutoClaiming = false;
        try { paintQuestProgress(); } catch(_){}
        return;
      }
      var m = ready[i++];
      var claim = null;
      try { claim = window.hohFreshFacesClaimQuestMilestone(m); } catch(_){}
      /* The final pack must settle only after every earlier server-protected
         receipt has settled. Chain the automatic sweep instead of racing six
         requests whose replies could arrive out of order. */
      if(claim && typeof claim.then === 'function'){
        claim.then(function(){ setTimeout(step, 720); }, function(){ setTimeout(step, 720); });
      } else {
        setTimeout(step, 720);
      }
    })();
  }

  function _scheduleExploreClose(delay){
    if(_ffQuestCloseScheduled) return;
    _ffQuestCloseScheduled = true;
    setTimeout(function(){
      _ffQuestCloseScheduled = false;
      try { if(window.hohRenderFreshFaces) window.hohRenderFreshFaces(); } catch(_){}
      if(_ffCompletionRewards && !_ffCompletionRewards.shown){
        try { showQuestClaimLightbox(WANDER_FINAL_MILESTONE); } catch(_){}
      }
    }, Math.max(0, delay || 0));
  }

  function _beginWanderCompletion(){
    if(getFfHeartsToday() < WANDER_QUEST_TARGET) return false;
    _ffQuestClosing = true;
    try { if(typeof stopAutoTimer === 'function') stopAutoTimer(); } catch(_){}
    try { if(typeof stopStreamWindow === 'function') stopStreamWindow(); } catch(_){}
    try { if(typeof stopAllStreams === 'function') stopAllStreams(); } catch(_){}
    try {
      var c = _ffContainer();
      if(c){
        c.classList.add('hoh-fresh-quest-closing');
        c.setAttribute('aria-busy', 'true');
      }
    } catch(_){}
    _hohAutoClaimAll();
    if(WANDER_MILESTONES.every(isMilestoneClaimed)) _scheduleExploreClose(0);
    return true;
  }

  function _questAcceptsHeart(){
    return !_ffQuestClosing && getFfHeartsToday() < WANDER_QUEST_TARGET;
  }

  /* R391 (Jan): THE ENTRY CEREMONY. Earning the day's Battlegrounds entry is
     the completion prize, so it gets a real moment — a gold ring, the crossed
     swords, embers — instead of a corner toast. Tap anywhere to dismiss early. */
  function showBgEntryCeremony(){
    try {
      var old = document.getElementById('hoh-bgentry-cere');
      if(old && old.parentNode) old.parentNode.removeChild(old);
      if(!document.getElementById('hoh-bgentry-cere-css')){
        var st = document.createElement('style');
        st.id = 'hoh-bgentry-cere-css';
        st.textContent =
          '#hoh-bgentry-cere{position:fixed;inset:0;z-index:100092;display:flex;align-items:center;justify-content:center;background:rgba(6,4,10,.82);opacity:0;transition:opacity .35s ease;cursor:pointer;}' +
          '#hoh-bgentry-cere.on{opacity:1;}' +
          '#hoh-bgentry-cere .bgec-card{position:relative;text-align:center;padding:34px 30px 30px;transform:scale(.86);transition:transform .45s cubic-bezier(.2,1.2,.3,1);}' +
          '#hoh-bgentry-cere.on .bgec-card{transform:scale(1);}' +
          '#hoh-bgentry-cere .bgec-ring{width:108px;height:108px;margin:0 auto 18px;border-radius:50%;border:2px solid rgba(232,198,116,.85);display:flex;align-items:center;justify-content:center;color:#e8c674;box-shadow:0 0 0 0 rgba(232,198,116,.45);animation:bgecPulse 1.6s ease-out infinite;background:radial-gradient(circle at 50% 42%,rgba(60,44,20,.55),rgba(14,10,20,.9));}' +
          '@keyframes bgecPulse{0%{box-shadow:0 0 0 0 rgba(232,198,116,.4)}70%{box-shadow:0 0 0 26px rgba(232,198,116,0)}100%{box-shadow:0 0 0 0 rgba(232,198,116,0)}}' +
          '#hoh-bgentry-cere .bgec-head{font-family:"Cinzel",serif;font-size:19px;font-weight:700;letter-spacing:.12em;text-transform:uppercase;color:#f2d98e;margin-bottom:8px;}' +
          '#hoh-bgentry-cere .bgec-sub{font-family:"Cormorant Garamond",Georgia,serif;font-size:15px;font-style:italic;color:#cdc4dd;max-width:300px;margin:0 auto;line-height:1.5;}' +
          '#hoh-bgentry-cere .bgec-embers{position:absolute;inset:-30px;pointer-events:none;overflow:hidden;}' +
          '#hoh-bgentry-cere .bgec-embers i{position:absolute;bottom:0;width:4px;height:4px;border-radius:50%;background:#e8c674;opacity:0;animation:bgecEmber 2.6s ease-in infinite;}' +
          '@keyframes bgecEmber{0%{opacity:0;transform:translateY(0)}12%{opacity:.85}100%{opacity:0;transform:translateY(-190px)}}' +
          '@media (prefers-reduced-motion: reduce){#hoh-bgentry-cere .bgec-ring{animation:none}#hoh-bgentry-cere .bgec-embers i{animation:none;display:none}}';
        document.head.appendChild(st);
      }
      var el = document.createElement('div');
      el.id = 'hoh-bgentry-cere';
      el.innerHTML =
        '<div class="bgec-card">' +
          '<span class="bgec-embers" aria-hidden="true">' +
            '<i style="left:12%;animation-delay:-.4s"></i><i style="left:30%;animation-delay:-1.7s"></i>' +
            '<i style="left:52%;animation-delay:-.9s"></i><i style="left:71%;animation-delay:-2.2s"></i>' +
            '<i style="left:88%;animation-delay:-1.2s"></i>' +
          '</span>' +
          '<div class="bgec-ring" aria-hidden="true">' +
            '<svg viewBox="0 0 24 24" width="46" height="46" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round">' +
            '<path d="M14.5 3.5 20 3.5 20 9"/><path d="M20 3.5 9.5 14"/><path d="M4 20l3.5-3.5"/><path d="M6.5 17.5 4.5 19.5"/>' +
            '<path d="M9.5 3.5 4 3.5 4 9"/><path d="M4 3.5 14.5 14"/><path d="M20 20l-3.5-3.5"/></svg>' +
          '</div>' +
          '<div class="bgec-head">+1 Battlegrounds Entry</div>' +
          '<div class="bgec-sub">The gates will open for you. Find your entry in the Battlegrounds tab.</div>' +
        '</div>';
      var gone = false;
      function dismiss(){
        if(gone) return; gone = true;
        el.classList.remove('on');
        setTimeout(function(){ try { if(el.parentNode) el.parentNode.removeChild(el); } catch(_){} }, 380);
        try { if(typeof window.hohRenderHeaderCounter === 'function') window.hohRenderHeaderCounter(); } catch(_){}
        try { if(typeof window.hohPaintBgDoor === 'function') window.hohPaintBgDoor(); } catch(_){}
      }
      el.addEventListener('click', dismiss);
      document.body.appendChild(el);
      requestAnimationFrame(function(){ el.classList.add('on'); });
      setTimeout(dismiss, 3400);
    } catch(_){}
  }

  /* R56: tryFireMilestone now JUST repaints. The actual reward grant
     happens when the user clicks the milestone icon to claim it.
     This makes each ticket feel earned — the milestone glows,
     hovers, and demands a tap. */
  function tryFireMilestone(n){
    if(WANDER_MILESTONES.indexOf(n) === -1) return;
    /* Repaint to apply the "ready" glow on the newly-hit milestone. */
    try { paintQuestProgress(); } catch(_){}
  }

  /* R56: claim handler. Wired via inline onclick on each milestone
     icon. Shows the lightbox, flies the ticket, bumps the ticket
     state, and on the final milestone also grants +30 hearts +
     double confetti. */
  window.hohFreshFacesClaimQuestMilestone = function(milestone){
    var n = parseInt(milestone, 10);
    if(WANDER_MILESTONES.indexOf(n) === -1) return;
    if(isMilestoneClaimed(n)){
      /* A second tap is also a quiet recovery attempt. The stable daily pack
         request can only return the already-earned cards; it never opens the
         Shop over the Events tab. */
      if(n === WANDER_FINAL_MILESTONE && typeof window.hohShopBankExploreGiftPack === 'function'){
        try { window.hohShopBankExploreGiftPack(); } catch(_){}
        return;
      }
      /* R148-Z32: User clicked an already-claimed pip. Give a quick
         toast so the click feels acknowledged instead of silently
         dropped — matches the "no feedback" complaint from the user
         when they double-clicked the heart pip. */
      if(typeof window.hohShowHeartToast === 'function'){
        try { window.hohShowHeartToast('Already claimed', 'This milestone is done for today.'); } catch(_){}
      }
      return;
    }
    var hearts = getFfHeartsToday();
    if(hearts < n){
      /* R148-Z32: Pip pulsed but not yet hit. Tell the user how many
         more hearts they need so the click feels acknowledged.
         Previously this silently returned, which the user read as
         "the click did nothing". */
      if(typeof window.hohShowHeartToast === 'function'){
        try {
          var need = n - hearts;
          window.hohShowHeartToast('Not yet', 'Heart ' + need + ' more hero' + (need === 1 ? '' : 'es') + ' to claim this.');
        } catch(_){}
      }
      return;
    }
    /* R140-Z3: TICKET milestones (10/20/40) require a signed-in email
       BEFORE granting. Closes the incognito-then-sign-in abuse vector:
       previously an anonymous user could click a ticket pip, get a
       local +1 ticket credit, sign in, and the server's
       daily_user_claims dedup never ran because there was no email at
       the time of claim. After sign-in, the local ledger reconciled
       and topped up the wallet on top of any tickets the account had
       already claimed elsewhere — same account, double tickets per
       day. By gating ticket claims behind a real email, every ticket
       grant now goes through hohServerClaimDailyMilestone which
       enforces UNIQUE (email, ymd, slot) at the database level. The
       table is the only authority; local state is advisory.
       Heart-bonus milestones (0/20/40) stay anonymous-friendly because
       they only modify the local heart bank — no raffle stakes. The
       user has explicitly OK'd that minor surface as acceptable.
       R406: that now includes the free pip at 0, so a signed-out
       visitor can take 20 hearts on arrival. The heart bank has always
       been client-side, so this opens no door that was not already
       open; it just makes the first one friendly. */
    var isTicketMilestone = (WANDER_HEART_BONUSES[n] === undefined);
    var em = (typeof window.hohYourHallEmail === 'function') ? window.hohYourHallEmail() : '';
    if(isTicketMilestone && !em){
      if(typeof window.hohShowHeartToast === 'function'){
        try { window.hohShowHeartToast('Sign in to bank tickets', 'Tickets stay tied to your account so they can\'t be doubled up.'); } catch(_){}
      }
      /* Pull the user's eye to the existing sign-in CTA at the bottom
         of the wander bar. The flash class is a brief outline pulse
         defined in CSS — same component, just emphasized. */
      try {
        var cFlash = _ffContainer();
        /* The CTA renders inside the wander-bar wrapper. Look for it
           via the wrap class first since that's the actual claim
           CTA the user can interact with (the inner button is at
           .hoh-fresh-quest-claim). */
        var btnFlash = cFlash && (cFlash.querySelector('.hoh-fresh-quest-claim-wrap .hoh-fresh-quest-claim')
                                  || cFlash.querySelector('#hoh-fresh-quest-claim'));
        if(btnFlash){
          if(btnFlash.scrollIntoView){
            btnFlash.scrollIntoView({ behavior: 'smooth', block: 'center' });
          }
          /* R140-Z3i: animation extended to 2.4s with three rapid
             pulses + an expanding gold ring; remove class after the
             animation cycle so a re-trigger restarts cleanly. */
          btnFlash.classList.remove('hoh-fresh-quest-claim-flash');
          void btnFlash.offsetWidth; /* force reflow so re-add restarts the keyframes */
          btnFlash.classList.add('hoh-fresh-quest-claim-flash');
          setTimeout(function(){ try { btnFlash.classList.remove('hoh-fresh-quest-claim-flash'); } catch(_){} }, 2500);
        }
      } catch(_){}
      return;
    }
    /* R136-fix-4: server-side double-claim gate. Same account on
       desktop + mobile previously could claim each wander milestone
       twice (once per device) because the local claim flag is per-
       device. The server gate atomically reserves the milestone
       slot in daily_user_claims for (email, ymd, 'wander_N'). If
       another device already claimed it, the gate returns ok+
       newClaim:false and we abort BEFORE bumping local state.
       Falls through to the normal claim path when:
         - the SQL migration hasn't been applied yet (RPC 404s)
         - any RPC error (graceful degradation)
       so the legitimate single-device single-session case is never
       blocked by this gate. Anonymous claims hit the early return
       above (R140-Z3) for ticket pips and never reach this code. */
    /* R406: the slot is the pip's stable id, NOT its heart threshold. */
    var slot = 'wander_' + wanderSlot(n);
    var serverCheck = (typeof window.hohServerClaimDailyMilestone === 'function')
      ? window.hohServerClaimDailyMilestone(slot)
      : Promise.resolve({ ok: false, reason: 'helper_missing' });
    return serverCheck.then(function(result){
      if(result && result.ok === true && result.newClaim === false){
        /* Already claimed today on another device. Mark local so the
           UI shows the correct "claimed" state on this device too,
           toast the user, and bail before crediting anything. */
        try { markMilestoneClaimed(n); } catch(_){}
        try { paintQuestProgress(); } catch(_){}
        try {
          if(typeof window.hohShowHeartToast === 'function'){
            window.hohShowHeartToast('Already claimed today',
              'You earned this milestone\'s ticket on another device.');
          }
        } catch(_){}
        return false;
      }
      /* Either the server granted us the claim or the gate isn't
         active (degraded path). Continue with the standard local
         credit flow below. */
      _hohFinishMilestoneClaim(n);
      return true;
    });
  };
  /* R136-fix-4: extracted body of the post-gate claim flow into a
     helper so the async server check can fire it after resolving.
     R140-R: Lightbox display deferred by 700ms so the heart-fly +
     confetti animations play with the user's full attention before
     the lightbox dominates the screen. The user reported "no effect
     or heart flying" — root cause was the lightbox firing first and
     hiding the milestone pip + wallet pill behind its dark backdrop,
     so the fly trajectory was invisible. Now: animations fire
     immediately on click, the lightbox lands ~700ms later as a
     celebration overlay. */
  function _hohFinishMilestoneClaim(n){
    if(isMilestoneClaimed(n)) return;
    markMilestoneClaimed(n);
    /* If this claim completes the daily, replace the live rail with its
       cooldown promptly after the ticket/confetti motion has had one beat. */
    try {
      if(WANDER_MILESTONES.every(isMilestoneClaimed)){
        _scheduleExploreClose(720);
      }
    } catch(_){}
    var isFinal = (n === WANDER_FINAL_MILESTONE);
    /* R406: heart bonuses sit at 0/20/40. The 50 pip is a TICKET, so
       the heart-bonus branch never fires for it; the final-milestone
       celebration (confetti + the Battlegrounds entry) triggers for
       n===50 AFTER the ticket flow runs — see below. */
    var heartBonus = WANDER_HEART_BONUSES[n] || 0;
    var isHeartBonus = heartBonus > 0;
    if(isHeartBonus){
      /* R148-Z32: Lightbox removed per user — the heart-fly to wallet
         is now the entire ceremony. The previous flow showed a popup
         AFTER hearts arrived, which the user reported as "sometimes
         nothing pops up" because the lightbox failed to render under
         certain conditions (DOM swapped mid-flight, focus stolen by
         another modal, etc.). With the lightbox stripped, the heart-
         fly + pill pulse + toast become the canonical feedback —
         simpler, more reliable, and they always fire. */
      try {
        if(typeof window.hohAddHeartsToBank === 'function'){
          window.hohAddHeartsToBank(heartBonus);
        }
      } catch(_){}
      try {
        if(typeof window.hohRenderHeaderCounter === 'function'){
          window.hohRenderHeaderCounter();
        }
      } catch(_){}
      if(typeof window.hohShowHeartToast === 'function'){
        try { window.hohShowHeartToast('Explore bonus claimed', 'Thanks for exploring.'); } catch(_){}
      }
      /* Heart-fly from the milestone pip to the wallet. With no
         lightbox following, this is the moment — boost the particle
         count slightly (cap 14 instead of 12) so a 30/50 reward
         feels worth the click. */
      try {
        var anchor = document.getElementById('hoh-fresh-quest-mile-' + n);
        if(!anchor){
          anchor = document.createElement('div');
          anchor.style.cssText = 'position:fixed;left:50%;top:50%;width:2px;height:2px;pointer-events:none;';
          document.body.appendChild(anchor);
          setTimeout(function(){ if(anchor.parentNode) anchor.parentNode.removeChild(anchor); }, 1500);
        }
        var heartFlyColor = (n === WANDER_CAPSTONE_MILESTONE) ? 'purple' : 'blue';
        if(anchor && typeof flyDiceTraderHeartsToPill === 'function'){
          flyDiceTraderHeartsToPill(anchor, Math.min(heartBonus, 14), heartFlyColor);
        }
      } catch(_){}
      /* Pulse the wallet pill so the bonus reads. */
      try {
        var hbPill = document.getElementById('hoh-tk-header') || document.getElementById('hoh-heart-remaining');
        if(hbPill){
          hbPill.classList.remove('hoh-tk-heart-pulse');
          void hbPill.offsetWidth;
          hbPill.classList.add('hoh-tk-heart-pulse');
          setTimeout(function(){ hbPill.classList.remove('hoh-tk-heart-pulse'); }, 900);
        }
      } catch(_){}
      /* Repaint so the just-claimed heart-bonus pip greys out. */
      try { paintQuestProgress(); } catch(_){}
      return;
    }
    /* R406: ticket pips are 10/30/50. The 50 is BOTH the final
       milestone (triggers the quest-complete celebration below) AND a
       ticket grant — both flows run in sequence so the user sees the
       ticket fly + the confetti capstone on the same click. */
    {
      /* R406: fly from the pip that was actually clicked. The old call
         took no argument and flyTicketToHeader picked the FIRST hit pip
         in the bar. That was already slightly wrong, and it becomes
         plainly wrong now the pip at 0 is hit from the moment the bar
         renders — every ticket would have launched from the far left
         of the rail no matter which pip you tapped. */
      flyTicketToHeader(n);
      try {
        if(!window.hohTicketState) window.hohTicketState = {};
        var st = window.hohTicketState;
        var max = (typeof st.tickets_today_max === 'number' && st.tickets_today_max > 0) ? st.tickets_today_max : 4;
        st.tickets_today_max = max;
        /* R64: wander milestones (20, 40) deposit a DISTRIBUTABLE
           ticket into the user\'s unspent wallet. The user has to
           actively spend these on characters they own — they no
           longer auto-credit anywhere.
           R126-A: REMOVED the redundant `st.tickets_today =
           min(max, st.tickets_today + 1)` pre-bump that used to live
           here. hohAddUnspentTicket() ALREADY bumps tickets_today
           optimistically AND uses it to gate the cap check. With the
           pre-bump in place, every wander claim double-counted: the
           cap appeared full after one wander claim if any other
           ticket source had landed earlier in the day, and the
           second milestone's wallet credit got refused at the cap
           gate. Deleting the pre-bump leaves hohAddUnspentTicket as
           the single writer of tickets_today and the math reads
           correctly across all claim paths. */
        if(typeof window.hohAddUnspentTicket === 'function'){
          try {
            /* v38 (Cortez, by email): every caller in this theme passed NO reason,
               so grant_daily_ticket filed every earn under 'unknown'. The table is
               UNIQUE on (hearter_email, ymd, reason), so the SECOND earn of any day
               collided, came back duplicate_reason, and the client rolled the ticket
               back and blamed a cap nobody had hit. One ticket a day, forever, no
               matter what you did. Each milestone carries its own key now, which is
               what that unique index was built for: idempotent per milestone, and
               four a day finally reachable. */
            var creditOk = window.hohAddUnspentTicket(1, 'wander_' + wanderSlot(n));
            /* R127-A: Mark the wallet-credit ledger when the credit
               lands. The ledger lets the reconcile pass tell genuine
               post-fix gaps from already-credited slots so users don't
               get retroactively topped up for tickets that already
               banked. */
            if(creditOk !== false && typeof window.hohMarkWalletCredited === 'function'){
              window.hohMarkWalletCredited('wander_' + wanderSlot(n));
            }
          } catch(_){}
        }
        if(typeof window.hohRenderHeaderCounter === 'function'){
          window.hohRenderHeaderCounter();
        }
        document.dispatchEvent(new CustomEvent('hoh:ticket-state-changed', { detail: { source: 'wanderMilestone', n: n } }));
      } catch(_){}
      /* Pulse the ticket pill so the +1 lands visually. */
      try {
        var pill = document.getElementById('hoh-tk-header') || document.getElementById('hoh-heart-remaining');
        if(pill){
          pill.classList.remove('hoh-tk-heart-pulse');
          void pill.offsetWidth;
          pill.classList.add('hoh-tk-heart-pulse');
          setTimeout(function(){ pill.classList.remove('hoh-tk-heart-pulse'); }, 900);
        }
      } catch(_){}
    }
    /* R140-Z3h: Final milestone (60) is now a TICKET, not a heart
       bonus. The ticket flow above already ran. Here we layer ON the
       quest-complete celebration: confetti, tinder_today flag, and
       the wander-quest-complete event so other surfaces (header pill,
       daily-tickets modal) repaint as "done for today". The ticket
       grant + the celebration both apply to the SAME click. */
    if(isFinal){
      var rewards = _ffCompletionRewards = {
        day:ymdToday(), email:typeof window.hohYourHallEmail === 'function' ? window.hohYourHallEmail() : '',
        shown:false, entry:Promise.resolve({ok:false}), pack:Promise.resolve({ok:false})
      };
      try { spawnConfetti(); } catch(_){}
      setTimeout(function(){ try { spawnConfetti(); } catch(_){} }, 220);
      if(typeof window.hohShowHeartToast === 'function'){
        try { window.hohShowHeartToast('Quest complete', 'You\'ve walked the whole Hall today.'); } catch(_){}
      }
      /* Finishing pays one entry under the existing server once-a-day rule.
         Keep its receipt for the gift panel rather than assuming it landed. */
      try {
        if(typeof window.hohBgGrantExplore === 'function'){
          rewards.entry = window.hohBgGrantExplore().then(function(res){
            try { if(typeof window.hohPaintBgDoor === 'function') window.hohPaintBgDoor(); } catch(_){}
            return res;
          }, function(){ return {ok:false}; });
        }
      } catch(_){}
      try {
        if(!window.hohTicketState) window.hohTicketState = {};
        window.hohTicketState.tinder_today = true;
        try { localStorage.setItem('hoh-daily-tinder-' + ymdToday(), '1'); } catch(_){}
        document.dispatchEvent(new CustomEvent('hoh:wander-quest-complete'));
      } catch(_){}
      try {
        if(typeof window.hohShopBankExploreGiftPack === 'function'){
          rewards.pack = window.hohShopBankExploreGiftPack();
        }
      } catch(_){}
    }
    /* Repaint so the just-claimed milestone gets the greyed style. */
    try { paintQuestProgress(); } catch(_){}
    /* Non-final checkpoints may still queue a Bazaar for the next roll. The
       final checkpoint clears that queue because completion closes Explore;
       carrying a final Legendary Shop into tomorrow would be a stale reward,
       not part of today's completed journey. */
    try {
      if(n === WANDER_FINAL_MILESTONE){
        window.__hohQueuedSpecial = null;
      } else {
        window.__hohQueuedSpecial = 'mageBazaar';
      }
    } catch(_){}
    /* R140-R: Lightbox lands AFTER animations have started so it reads
       as a celebration overlay instead of obscuring the heart-fly /
       confetti trajectory. 700ms gives the burst time to paint and the
       hearts to begin their flight; the lightbox then settles on top
       and presents the headline. The user reported missing the heart
       fly entirely — this ordering ensures they see it before the
       lightbox dominates. */
    if(!isFinal){
      setTimeout(function(){ try { showQuestClaimLightbox(n); } catch(_){} }, 700);
    }
  };

  /* R56: Lightbox shown on milestone claim. Pure DOM (no modal lib).
     Auto-dismisses after 1.8s; tapping anywhere dismisses earlier. */
  function showQuestClaimLightbox(n){
    if(n === WANDER_FINAL_MILESTONE){
      showExploreRewardGift();
      return;
    }
    /* The auto-claim sweep stays quiet — six claims must not stack popups. */
    if(_ffAutoClaiming && n !== WANDER_FINAL_MILESTONE) return;
    var existing = document.getElementById('hoh-quest-claim-lightbox');
    if(existing && existing.parentNode) existing.parentNode.removeChild(existing);
    var lb = document.createElement('div');
    lb.id = 'hoh-quest-claim-lightbox';
    lb.className = 'hoh-quest-claim-lightbox';
    /* Ordinary checkpoints still use the compact claim acknowledgement. The
       final checkpoint returned above because its terminal UI is cooldown. */
    var heartBonusAmt = WANDER_HEART_BONUSES[n] || 0;
    var isHeartBonus = heartBonusAmt > 0;
    var iconHtml = isHeartBonus
      ? '<span class="hoh-quest-claim-heart-icon" aria-hidden="true">&#9829;&#xFE0E;</span>'
      : '<span class="hoh-tk-icon" aria-hidden="true"></span>';
    var titleText = isHeartBonus
      ? 'Bonus Claimed'
      : 'Raffle Ticket Claimed';
    var subText = isHeartBonus
      ? 'A bonus for exploring.'
      : 'Banked from the Explore quest.';
    lb.innerHTML =
      '<div class="hoh-quest-claim-card">' +
        '<div class="hoh-quest-claim-icon">' + iconHtml + '</div>' +
        '<div class="hoh-quest-claim-title">' + titleText + '</div>' +
        '<div class="hoh-quest-claim-sub">' + subText + '</div>' +
      '</div>';
    function closeClaim(){
      try { lb.classList.add('hoh-quest-claim-leaving'); } catch(_){}
      setTimeout(function(){
        if(lb.parentNode) lb.parentNode.removeChild(lb);
      }, 220);
    }
    lb.addEventListener('click', closeClaim);
    document.body.appendChild(lb);
    setTimeout(function(){
      if(!lb.parentNode) return;
      closeClaim();
    }, 1800);
  }

  /* The existing gift panel belongs to Explore. It reports the saved pack,
     never opens Shop or consumes that pack, and only runs for a fresh finish. */
  function showExploreRewardGift(){
    var rewards = _ffCompletionRewards;
    if(!rewards || rewards.shown || rewards.day !== ymdToday()) return;
    if(typeof window.hohYourHallEmail === 'function' && rewards.email !== window.hohYourHallEmail()) return;
    rewards.shown = true;
    var prior = document.getElementById('hoh-quest-claim-lightbox');
    if(prior) prior.remove();
    var lb = document.createElement('div');
    lb.id = 'hoh-quest-claim-lightbox';
    lb.className = 'hoh-quest-claim-lightbox hoh-quest-claim-lightbox-final';
    lb.setAttribute('role', 'dialog'); lb.setAttribute('aria-modal', 'true');
    lb.setAttribute('aria-labelledby', 'hoh-explore-reward-title');
    var heartTotal = WANDER_MILESTONES.reduce(function(total, m){ return total + (WANDER_HEART_BONUSES[m] || 0); }, 0);
    var ticketTotal = WANDER_TICKET_MILESTONES.length;
    lb.innerHTML = '<div class="hoh-quest-claim-card hoh-quest-final-card">' +
      '<div class="hoh-quest-final-box" aria-hidden="true"><svg viewBox="0 0 24 24" fill="none" stroke-linejoin="round">' +
      '<path class="hoh-gift-box-ink" d="M4 10h16v11H4zM3 6h18v4H3z"/>' +
      '<path class="hoh-gift-ribbon-ink" d="M12 6v15M12 6C3 6 4 0 7 2c3 1 5 4 5 4Zm0 0c9 0 8-6 5-4-3 1-5 4-5 4Z"/></svg></div>' +
      '<div class="hoh-quest-claim-title" id="hoh-explore-reward-title">Explore complete</div>' +
      '<div class="hoh-quest-final-loot">' +
      '<span><svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M12 21S3 15 3 8a5 5 0 0 1 9-3 5 5 0 0 1 9 3c0 7-9 13-9 13Z"/></svg><b>Hearts found</b></span>' +
      '<span><span class="hoh-tk-icon" aria-hidden="true"></span><b>'+ticketTotal+' tickets</b></span>' +
      '<span><svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M14.5 3.5H20V9M20 3.5 9.5 14M4 20l3.5-3.5M9.5 3.5H4V9M4 3.5 14.5 14M20 20l-3.5-3.5"/></svg><b data-entry-status>Saving entry…</b></span></div>' +
      '<p class="hoh-quest-final-packline" data-pack-status role="status">Saving your bonus pack…</p>' +
      '<button type="button" class="hoh-btn-primary" style="margin-top:18px;min-height:44px">Back to Explore</button></div>';
    var previousFocus = document.activeElement, closed = false;
    function closeGift(){
      if(closed) return; closed = true;
      document.removeEventListener('keydown', onKey);
      lb.classList.add('hoh-quest-claim-leaving');
      setTimeout(function(){ lb.remove(); }, 220);
      if(previousFocus && document.body.contains(previousFocus) && previousFocus.focus) previousFocus.focus();
      else { var back = document.getElementById('hoh-exdone-bgbtn'); if(back) back.focus(); }
    }
    function onKey(e){
      if(e.key === 'Escape'){ e.preventDefault(); closeGift(); }
      else if(e.key === 'Tab'){ e.preventDefault(); lb.querySelector('button').focus(); }
    }
    lb.querySelector('button').addEventListener('click', closeGift);
    document.addEventListener('keydown', onKey);
    document.body.appendChild(lb); lb.querySelector('button').focus();
    if(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches){
      lb.style.animation = 'none'; lb.firstElementChild.style.animation = 'none';
    }
    function update(selector, promise, success, failure){
      Promise.resolve(promise).then(function(result){
        if(!closed) lb.querySelector(selector).textContent = result && result.ok === true ? success : failure;
      }, function(){ if(!closed) lb.querySelector(selector).textContent = failure; });
    }
    update('[data-entry-status]', rewards.entry, '1 entry', 'Entry pending');
    update('[data-pack-status]', rewards.pack, '1 bonus pack saved in your Wallet. Open it whenever you like.',
      'Your pack could not be confirmed yet. Reopen Explore to retry safely.');
  }

  /* Fly a small ticket icon from the appropriate quest milestone pip
     to the ticket counter pill. Uses position:fixed so we can animate
     in screen coordinates regardless of layout. */
  function flyTicketToHeader(fromMilestone){
    var c = _ffContainer();
    var pill = document.getElementById('hoh-tk-header') || document.getElementById('hoh-heart-remaining');
    if(!c || !pill) return;
    /* Source: the pip the user actually claimed, when the caller knows
       it. R406 added that argument — before it, this fell straight to
       the first .hoh-fresh-quest-mile-hit in document order, which is
       the LEFTMOST reached pip rather than the clicked one. With a pip
       at 0 that is hit on arrival, the untargeted version would launch
       every ticket from the start of the rail.
       If the quest bar isn't rendered (special event) fall back to the
       section centre so the fly still happens — hearts in special
       events DO progress the quest. */
    var src = null;
    if(fromMilestone !== undefined && fromMilestone !== null){
      src = c.querySelector('#hoh-fresh-quest-mile-' + fromMilestone);
    }
    src = src ||
              c.querySelector('.hoh-fresh-quest-mile-ready') ||
              c.querySelector('.hoh-fresh-quest-mile-hit') ||
              c.querySelector('#hoh-fresh-quest') ||
              c.querySelector('.hoh-fresh-header') ||
              c;
    if(!src) return;
    var srcRect = src.getBoundingClientRect();
    var dstRect = pill.getBoundingClientRect();
    if(!srcRect || !dstRect || !srcRect.width || !dstRect.width) return;
    var fly = document.createElement('div');
    fly.className = 'hoh-fresh-ticket-fly';
    fly.innerHTML = '<span class="hoh-fresh-ticket-fly-icon"></span><span class="hoh-fresh-ticket-fly-label">+1</span>';
    fly.style.left = (srcRect.left + srcRect.width / 2) + 'px';
    fly.style.top = (srcRect.top + srcRect.height / 2) + 'px';
    /* Initial transform centers the pill on its left/top coords. We
       compose the destination delta into the same translate so the
       element stays centered through the animation. */
    fly.style.transform = 'translate(-50%, -50%)';
    document.body.appendChild(fly);
    var dx = (dstRect.left + dstRect.width / 2) - (srcRect.left + srcRect.width / 2);
    var dy = (dstRect.top + dstRect.height / 2) - (srcRect.top + srcRect.height / 2);
    requestAnimationFrame(function(){
      fly.style.transform = 'translate(calc(-50% + ' + dx + 'px), calc(-50% + ' + dy + 'px)) scale(0.55)';
      fly.style.opacity = '0';
    });
    setTimeout(function(){
      if(fly && fly.parentNode) fly.parentNode.removeChild(fly);
    }, 1100);
  }

  /* Drag-to-rearrange was removed by request — the long-press rail
     reorder was conflicting with the click-and-hold heart combo on
     mobile and adding more complexity than it earned in usage. The
     refresh button + per-card heart/X is enough. */
  function setDismissed(list){
    try {
      if(list.length > DISMISSED_CAP) list = list.slice(-DISMISSED_CAP);
      localStorage.setItem(DISMISSED_KEY, JSON.stringify(list));
    } catch(e){}
  }
  /* Public dismissed-list helpers used by the main gallery filter. */
  window.hohGetDismissedList = getDismissed;
  window.hohIsDismissed = function(id){
    var list = getDismissed();
    return list.indexOf(id) !== -1;
  };
  window.hohDismissCharacter = function(id){
    if(!id) return;
    var list = getDismissed();
    if(list.indexOf(id) === -1){ list.push(id); setDismissed(list); }
  };
  window.hohRestoreCharacter = function(id){
    if(!id) return;
    var list = getDismissed();
    var i = list.indexOf(id);
    if(i !== -1){ list.splice(i, 1); setDismissed(list); }
  };

  /* PHASE STATE
     ----------------------------------------------------------------
     Phase 0  → "Fresh Faces" (newest in 3-day window) — default
     Phase 1-3 → "Wandering Heroes" / "Crossroads" / "The Long Road"
                 random unseen+unhearted pool, refresh cycles text
     Phase -1 → "Heroes You've Lifted" — only the user's hearted ones,
                 entered when the unseen pool runs dry. Refresh button
                 hides at this phase since there's nothing more to find.
     Phase persists per-day via localStorage. Reset at midnight UTC. */
  var FRESH_PHASE_KEY = 'hoh-fresh-phase';
  var FRESH_PHASE_DATE_KEY = 'hoh-fresh-phase-date';
  function todayYmd(){ return new Date().toISOString().slice(0,10); }
  function getPhase(){
    try {
      var d = localStorage.getItem(FRESH_PHASE_DATE_KEY);
      if(d !== todayYmd()){
        localStorage.setItem(FRESH_PHASE_DATE_KEY, todayYmd());
        localStorage.setItem(FRESH_PHASE_KEY, '0');
        return 0;
      }
      return parseInt(localStorage.getItem(FRESH_PHASE_KEY) || '0', 10) || 0;
    } catch(e){ return 0; }
  }
  function setPhase(p){
    try {
      localStorage.setItem(FRESH_PHASE_KEY, String(p));
      localStorage.setItem(FRESH_PHASE_DATE_KEY, todayYmd());
    } catch(e){}
  }
  /* Phase 0 is the default Fresh Faces (newest in 3-day window). All
     other entries below are RANDOMLY picked when the user refreshes,
     creating a sense of wandering deeper into the Hall and stumbling
     into peculiar gatherings. Filters narrow the pool by race/class
     so the eyebrow + sub actually match what's on screen. */
  var DEFAULT_PHASE = { eyebrow:'Fresh Faces', sub:'New arrivals at the Hall. Give them a welcome with a heart.' };
  /* Each themed phase carries an optional `color` field (CSS color
     string) for the eyebrow headline. The renderer applies it via
     inline style — so a Wizard council reads in arcane purple, a
     Druid grove reads in moss green, etc. Falls back to the default
     gold from the stylesheet when no color is set. */
  var THEMED_PHASES = [
    { eyebrow:'From Across the Hall', sub:'Random heroes from every corner. Refresh for more.' },
    { eyebrow:'A Council of Wizards', sub:'You stumble into a quiet meeting of arcane minds. Robes whisper as they turn.', filter:{cls:'Wizard'}, color:'#a78bfa' },
    { eyebrow:'The Goblin Market', sub:'A peculiar gathering of small, clever folk. Don\'t taste the bread.', filter:{race:['Gnome','Goblin','Halfling','Harengon']}, color:'#9be07a' },
    { eyebrow:'A Tavern of Bards', sub:'Music spills out before you reach the door. Three songs argue at once.', filter:{cls:'Bard'}, color:'#ff9bd2' },
    { eyebrow:'The Druid Grove', sub:'Moss and stillness. The trees lean in. They are watching.', filter:{cls:'Druid'}, color:'#7ed49a' },
    { eyebrow:'The Paladins\' Order', sub:'Polished armor and unspoken oaths. Nobody sits with their back to the door.', filter:{cls:'Paladin'}, color:'#f5e6a3' },
    { eyebrow:'The Warlock Pact Hall', sub:'Whispers in old languages. Don\'t ask whose names they\'re saying.', filter:{cls:'Warlock'}, color:'#c4a8f8' },
    { eyebrow:'A Rogue\'s Den', sub:'Three of them are pickpocketing each other. The fourth is keeping score.', filter:{cls:'Rogue'}, color:'#a09aae' },
    { eyebrow:'Children of the Dragon', sub:'Scales catch the firelight. They speak softly, but the room listens.', filter:{race:'Dragonborn'}, color:'#ff8a5b' },
    { eyebrow:'The Tiefling Court', sub:'Horns, tails, and very long memories. They remember what you said five minutes ago.', filter:{race:'Tiefling'}, color:'#e07aa0' },
    { eyebrow:'Among the Elves', sub:'Centuries of judgment in a single glance. Try not to mention the war.', filter:{race:['Elf','Half-Elf','Drow']}, color:'#c0e8d0' },
    { eyebrow:'The Orc Encampment', sub:'You can hear the laughter from a mile out. The food is better than you expect.', filter:{race:['Orc','Half-Orc']}, color:'#bce074' },
    { eyebrow:'The Cleric\'s Vigil', sub:'Candles. A long table. Quiet faith. Someone says grace before the bread.', filter:{cls:'Cleric'}, color:'#fff5d8' },
    { eyebrow:'The Barbarian Camp', sub:'They\'ve been waiting for someone to fight all evening. You\'ll do.', filter:{cls:'Barbarian'}, color:'#e88a5c' },
    { eyebrow:'The Sorcerers\' Salon', sub:'Magic without the homework. Their parlor tricks could end a kingdom.', filter:{cls:'Sorcerer'}, color:'#ff6f8a' },
    { eyebrow:'The Monastery Gate', sub:'You\'ll need to take off your boots. The stones know your weight.', filter:{cls:'Monk'}, color:'#bdb8c4' },
    { eyebrow:'The Hunters\' Lodge', sub:'Boots wet with morning dew. Something pinned outside isn\'t a deer.', filter:{cls:'Ranger'}, color:'#9cc69b' },
    { eyebrow:'The Smithy of Wonders', sub:'Sparks. Steam. The hum of artifice. They sell things that probably shouldn\'t exist.', filter:{cls:'Artificer'}, color:'#f2c889' },
    { eyebrow:'The Knights\' Hall', sub:'Steel and ceremony. Don\'t mention the dragon they all owe money to.', filter:{cls:'Fighter'}, color:'#dbe2ec' },
    { eyebrow:'The Aasimar Choir', sub:'Their voices carry farther than they should. The candles lean toward them.', filter:{race:'Aasimar'}, color:'#fff0bd' },
    { eyebrow:'The Halfling Brunch', sub:'Six courses already and it\'s only noon. They insist you stay for dessert.', filter:{race:'Halfling'}, color:'#f4c97a' },
    { eyebrow:'The Genasi Caucus', sub:'Wind, water, fire, stone. All arguing about who pays the tab.', filter:{race:'Genasi'}, color:'#7ed4ff' },
    { eyebrow:'The Warforged Concord', sub:'They remember every promise. Every one. Be careful what you say.', filter:{race:'Warforged'}, color:'#b8c5cf' },
    { eyebrow:'The Underdark Embassy', sub:'You weren\'t supposed to be invited. Smile politely.', filter:{race:'Drow'}, color:'#b794f4' },
    { eyebrow:'The Beastfolk Gathering', sub:'A table of cats, foxes, and rabbits. Formal as kings, twice as quick.', filter:{race:['Tabaxi','Harengon']}, color:'#e8b878' },
    { eyebrow:'Crossroads', sub:'Paths cross with strangers you haven\'t met yet.' },
    { eyebrow:'The Long Road', sub:'Heroes you might never have found otherwise.' },
    { eyebrow:'The Blood Hunters\' Pact', sub:'Ritual scars under their sleeves. They speak of the curse like an old friend.', filter:{cls:'Blood Hunter'}, color:'#d1605c' },
    { eyebrow:'The Shapeshifter\'s Salon', sub:'Three of them have your face. None of them admit it.', filter:{race:'Changeling'}, color:'#c1b6e0' },
    { eyebrow:'The Fairy Ring', sub:'Don\'t make a deal. Don\'t accept the wine. Don\'t fall for the music.', filter:{race:'Fairy'}, color:'#ffb3e0' },
    { eyebrow:'The Goliath\'s Hearth', sub:'The doorways are tall. The handshakes are firm. The stew is excellent.', filter:{race:'Goliath'}, color:'#cfd8e3' },
    /* Rising Stars: heroes with above-average heart velocity. Surfaces
       cards that are picking up steam right now, not just the all-time
       favorites. Predicate runs against the full pool so the threshold
       reflects today's data. Capped at 2 rows (10) — momentum reads
       crisper as a short list than a wall. */
    { eyebrow:'Rising Stars', sub:'Heroes who picked up speed lately. Their hearts came in faster than most.', filter:{pred:'rising'}, color:'#ffe27a', cards:10 },
    /* All FF Portraits: only commissioned portraits. Their gilded badges
       gleam side by side; the gallery feels like a private viewing.
       Marked rare:true so the random phase picker shows it less often
       than other ambient themes — see pickThemedPhase. 10 cards (2
       rows) so the gallery has presence without becoming a wall.
       sortBy:'hearts' biases the unseenList sort toward most-loved
       portraits when this filter is active. */
    /* R96: Portrait Gallery — bumped cards 10 → 12 so the showcase
       reliably fills 2 full rows of 6 (or matches whatever the FF
       portrait pool has). sortBy:'hearts' keeps the most-loved
       portraits leading. */
    /* R590 (Jan): sortBy:'hearts' REMOVED from both the filter and the
       phase. It was the only hearts sort left in the file, and paired
       with showcase:true it made this the one phase with ZERO
       randomness: showcase bypasses the unseen/seen split, then the
       hearts sort fixed the order, so the same portraits appeared in
       the same order on every single fire. Jan: "instead of showing the
       top goblins with the most hearts, just show random ... that way
       different characters can actually show up." showcase:true STAYS
       (R116: it is what lets an already-hearted portrait remain in the
       wing, and removing it collapsed the gallery to a handful of
       cards and read as broken). */
    { eyebrow:'The Portrait Gallery', sub:'A wing of the Hall reserved for FramedFantasy portraits. Every face here was painted by Jan.', filter:{pred:'ff', showcase:true}, color:'#e8d4b0', rare:true, ffShowcase:true, cards:10 },
    /* R118-G: The Crowded Tavern — low-rarity gathering. 4 rows
       desktop (~20 cards) of heroes still climbing the ladder.
       Every face here is below epic (hearts < 200). Theme is
       camaraderie among the rank-and-file. */
    { eyebrow:'The Crowded Tavern', sub:'Common faces, shared stories. Every hero here is still climbing the ladder.', filter:{pred:'lowRarity'}, color:'#9be07a', cards:20 },
    /* R118-H: The Hall of Champions — high-rarity gathering. 3 rows
       desktop (~15 cards) of epic+ heroes. Theme is reverence —
       every face here has earned their renown. */
    { eyebrow:'The Hall of Champions', sub:'Epic, legendary, mythic — every face here has earned their renown.', filter:{pred:'highRarity'}, color:'#c4a8f8', cards:15 },
    /* R136-themed-rotation: Four NEW themed phases. Each is 2 rows
       (cards:10) and carries a borderColor that paints a subtle
       glowing border around the rail container so the theme reads
       at a glance. The colors are tuned to be distinct from existing
       themes so the user can FEEL the difference between phases
       without having to read the eyebrow. Filters intentionally use
       multi-race / multi-class combinations the existing themes
       don't cover, so older accounts that have hearted entire
       single-class pools still find fresh matches here. */
    { eyebrow:'The Moonless Court', sub:'Three peoples that move best when the lanterns flicker. Don\'t make sudden gestures.', filter:{race:['Drow','Tiefling','Changeling']}, color:'#c89bff', borderColor:'#7e3fa3', cards:10 },
    { eyebrow:'The Pact-Bound', sub:'Heroes who paid a price for the power they carry. Don\'t ask what.', filter:{cls:['Warlock','Blood Hunter']}, color:'#ff7d8a', borderColor:'#a83545', cards:10 },
    { eyebrow:'The Painted Wing', sub:'Fur, ears, and gossamer. The most-talked-about table at the festival.', filter:{race:['Tabaxi','Harengon','Fairy']}, color:'#ffb3df', borderColor:'#ff5fa8', cards:10 },
    { eyebrow:'The Stone Council', sub:'Mountains in mortal form. They speak slowly, then they speak last.', filter:{race:['Dwarf','Goliath']}, color:'#cdd5e0', borderColor:'#7c8a9c', cards:10 },
    /* R140-Z3l: Twilight Walkers added to THEMED_PHASES so the anon
       tour can resolve it. Travelers' Camp removed — the tour now
       fires the proper rarityTribe-uncommon EVENT (3-row uncommon
       grid) instead of a themed-phase substitute. Marked rare:true so
       the random theme rotation doesn't over-pick this outside the tour. */
    { eyebrow:'The Twilight Walkers', sub:'Heroes the moonlight remembers. Drow, Tiefling, and Half-Elf walk together tonight.', filter:{race:['Drow','Tiefling','Half-Elf']}, color:'#c89bff', borderColor:'#7e3fa3', rare:true, cards:10 },
    /* R140-Z3n: 20 NEW themed Heart Rolls (the other 5 of the 25-pack
       are implemented as Bustling Streets variants below). Each entry
       carries:
         color        — accent (eyebrow, tint, glow). Use brand-aware
                        hues that read against the dark hall background.
         borderColor  — darker complement painted into the bottom-of-
                        section glow + border by the themed-tint CSS.
         filter       — race / cls intersection. Population data from
                        the SQL counts the user pasted made these
                        viable (all hit at least MIN_THEME_CHARS=3).
         cards        — 10 for tighter themes, 15 for big pools that
                        warrant 3 rows. */
    { eyebrow:"The Hexblade's Choir", sub:'Tiefling Warlocks. Each one shook a hand they shouldn\'t have.', filter:{race:['Tiefling'], cls:['Warlock']}, color:'#ef94ff', borderColor:'#7e3fa3', cards:10 },
    { eyebrow:'The Bloodless Court', sub:'Vampires, Dhampirs, and the quiet undead. They\'ve all stopped breathing for one reason or another.', filter:{race:['Vampire','Dhampir','Dhampir/Drow','Lich','Undead','Shade','Shadow-Born','Elven Vampire Spawn','Black Dragonborn']}, color:'#c0a8e8', borderColor:'#3b1a4a', cards:10 },
    { eyebrow:"The Sunlit Order", sub:'Aasimar Paladins. Light follows them. They like that less than you\'d think.', filter:{race:['Aasimar'], cls:['Paladin']}, color:'#fff0bd', borderColor:'#c89a2a', cards:10 },
    { eyebrow:'The Heaven-Touched', sub:'Aasimars, Celestials, the Fallen. The Hall\'s high blood, sweet and otherwise.', filter:{race:['Aasimar','Celestial','Celestial (Fallen)','Heavenborn','Fallen']}, color:'#fff5d8', borderColor:'#d4af37', cards:10 },
    { eyebrow:'The Elemental Court', sub:'Genasi and Tritons. Four arguments at once, none of them out loud.', filter:{race:['Genasi','Triton']}, color:'#7ed4ff', borderColor:'#1a5a8a', cards:10 },
    { eyebrow:'The Iron Templars', sub:'Human Paladins. Bare-faced, gold-trimmed, oath-bound.', filter:{race:['Human'], cls:['Paladin']}, color:'#f5e6a3', borderColor:'#b8862a', cards:10 },
    { eyebrow:'The Pact-Twisted', sub:'Half-Elf Warlocks. They split themselves in half twice.', filter:{race:['Half-Elf'], cls:['Warlock']}, color:'#ff7d8a', borderColor:'#5a1a28', cards:10 },
    { eyebrow:'The Cursed Bloodlines', sub:'Half-Elves, Half-Orcs, Dhampirs, Cursed Humans. Bloodlines mix; trouble follows.', filter:{race:['Half-Elf','Half-Orc','Half-Dragon','Half Dragon Dragonborn','Dhampir','Dhampir/Drow','Cursed Human','Half drow, half steam genasi','Half Orc Half Drow']}, color:'#c89bff', borderColor:'#3a1a4a', cards:15 },
    { eyebrow:'The Greenblood Pact', sub:'Orcs, Goblins, Hobgoblins, Kobolds. Rough hands, fair deals — usually.', filter:{race:['Orc','Half-Orc','Goblin','Hobgoblin','Kobold','Bugbear']}, color:'#bce074', borderColor:'#4a6a18', cards:10 },
    { eyebrow:'The Stone Bastion', sub:'Dwarves, Goliaths, Minotaurs. Tall doorways, firm handshakes, excellent stew.', filter:{race:['Dwarf','Snow Dwarf','Goliath','Minotaur','Giantborne']}, color:'#cfd8e3', borderColor:'#5a6878', cards:10 },
    { eyebrow:'The Spellsword Conclave', sub:'Wizards who fight, Fighters who incant, the in-between ones nobody quite categorizes.', filter:{cls:['Wizard','Sorcerer','Warlock','Fighter']}, color:'#a78bfa', borderColor:'#3a2a7a', cards:15 },
    { eyebrow:'The Beastmasters', sub:'Rangers and Druids. Sleeves smell like rain and fur.', filter:{cls:['Ranger','Druid']}, color:'#7ed49a', borderColor:'#2a5a3a', cards:10 },
    { eyebrow:'The Halfling & Gnome Coalition', sub:'Small folk, short hours, long memory. Six courses already, somehow.', filter:{race:['Halfling','Gnome']}, color:'#f4c97a', borderColor:'#8a5a18', cards:10 },
    { eyebrow:'The Newcomers', sub:'Heroes who walked in this week. Show them the door, then the door behind it.', filter:{pred:'newcomers'}, color:'#b3f5ee', borderColor:'#3a8a82', cards:10 },
    { eyebrow:'The Forgotten Few', sub:'Quiet heroes the Hall hasn\'t lifted in a month. Lend a heart.', filter:{pred:'forgotten'}, color:'#a09aae', borderColor:'#3a3548', cards:10 },
    { eyebrow:'The Drow Conspiracy', sub:'Drow, Shadar-kai, Stygian Elves. Whispers in the dark, contracts on the table.', filter:{race:['Drow','Shader-kai','Shdar-kai','Stygian Elf (essentially a tiefling)','Night Elf']}, color:'#b794f4', borderColor:'#3a1a4a', cards:10 },
    { eyebrow:"The Witch's Coven", sub:'Warlocks and Witches. Sage smoke, sharp eyes, sharper bargains.', filter:{cls:['Warlock','Witch']}, color:'#9d4edd', borderColor:'#3a1958', cards:15 },
    { eyebrow:'The Lonely Wanderers', sub:'Rangers, Monks, and Druids. They travel light. The road is the company.', filter:{cls:['Ranger','Monk','Druid']}, color:'#a09aae', borderColor:'#3a3548', cards:15 },
    { eyebrow:'The Battlefield Trio', sub:'Fighters, Barbarians, Monks. Different weapons, one shared certainty.', filter:{cls:['Fighter','Barbarian','Monk']}, color:'#e88a5c', borderColor:'#7a3318', cards:15 },
    { eyebrow:'The Tiger Den', sub:'Tabaxi, Leonid hybrids, Tiger beastkin. Soft paws. Sharp eyes.', filter:{race:['Tabaxi','Tiger beastkin','Leonid / Khjask hybrid','Khenra']}, color:'#e8b878', borderColor:'#7a4a18', cards:10 },
    /* R140-Z3n (Phase 3): 15 MORE themed Heart Rolls, leaning into
       TTRPG-flavored archetypes — stat-grouped halls, level-band
       gatherings, role-based ensembles, lore-deep cohorts. Each uses
       intersections that resonate with experienced players. */
    /* Stat-grouped halls — CHA, WIS, INT, STR. The four core combat-
       stat archetypes, each gathering the classes that key off them. */
    { eyebrow:'The Charisma Court', sub:'Bards, Sorcerers, Warlocks, Paladins. The hall is louder when they walk in.', filter:{cls:['Bard','Sorcerer','Warlock','Paladin']}, color:'#ff9bd2', borderColor:'#7a2a52', cards:15 },
    { eyebrow:'The Wisdom Council', sub:'Clerics, Druids, Rangers, Monks. They hear what the room is doing.', filter:{cls:['Cleric','Druid','Ranger','Monk']}, color:'#7ed49a', borderColor:'#2a5a3a', cards:15 },
    { eyebrow:'The Intelligence Annex', sub:'Wizards and Artificers. Notebooks, gears, and unfinished questions.', filter:{cls:['Wizard','Artificer']}, color:'#a78bfa', borderColor:'#3a2a7a', cards:10 },
    { eyebrow:'The Strength Brigade', sub:'Fighters and Barbarians. Doors open faster when they\'re visiting.', filter:{cls:['Fighter','Barbarian']}, color:'#e88a5c', borderColor:'#7a3318', cards:15 },
    /* Career / role themes */
    { eyebrow:'The Mercenary Company', sub:'Fighters, Rogues, Barbarians. They take coin and they keep their word — usually.', filter:{cls:['Fighter','Rogue','Barbarian']}, color:'#d9a677', borderColor:'#5a3818', cards:15 },
    { eyebrow:'The Holy Triad', sub:'Paladins, Clerics, Monks. Three disciplines, one quiet certainty.', filter:{cls:['Paladin','Cleric','Monk']}, color:'#fff0bd', borderColor:'#c89a2a', cards:15 },
    { eyebrow:'The Wandering Mystics', sub:'Bards, Druids, Monks. They never quite arrive anywhere.', filter:{cls:['Bard','Druid','Monk']}, color:'#c4a8f8', borderColor:'#3a2a7a', cards:15 },
    { eyebrow:'The Back Row', sub:'Wizards, Sorcerers, Warlocks, Clerics, Bards. Every party\'s lifeline.', filter:{cls:['Wizard','Sorcerer','Warlock','Cleric','Bard']}, color:'#b3f5ee', borderColor:'#1a5a7a', cards:15 },
    /* Lore-band themes — the world's outcasts, veterans, newcomers, mythics. */
    { eyebrow:'The Underclass', sub:'The races the hall side-eyes. They sit at their own table, and the table is louder.', filter:{race:['Half-Orc','Half Orc Half Drow','Orc','Goblin','Hobgoblin','Bugbear','Kobold','Tiefling','Cursed Human']}, color:'#bce074', borderColor:'#4a6a18', cards:15 },
    { eyebrow:'The Veterans', sub:'Heroes the hall remembers. Each one has crossed 100 hearts.', filter:{pred:'veterans'}, color:'#f2d98e', borderColor:'#7a5318', cards:10 },
    { eyebrow:'The Apprentices', sub:'New, small, untested. Their stories are still being written.', filter:{pred:'apprentices'}, color:'#b3f5ee', borderColor:'#3a8a82', cards:10 },
    { eyebrow:'The Pantheon', sub:'Mythics walk the hall tonight. They never come together twice.', filter:{pred:'mythics'}, color:'#ef94ff', borderColor:'#7e3fa3', cards:10 },
    /* Subculture themes */
    { eyebrow:'The Death-Touched', sub:'Vampires, Liches, Shades. Their breath is colder than the room.', filter:{race:['Vampire','Dhampir','Dhampir/Drow','Lich','Undead','Shade','Shadow-Born','Elven Vampire Spawn','Black Dragonborn','Duskwalker','Avatar of Death','Fallen','Celestial (Fallen)']}, color:'#a8a4c4', borderColor:'#3a3548', cards:10 },
    { eyebrow:'The Goblinkin', sub:'Goblins, Hobgoblins, Bugbears, Kobolds, Orcs. Tribes that taught the hall to share a table.', filter:{race:['Goblin','Hobgoblin','Bugbear','Orc','Half-Orc','Half Orc Half Drow','Kobold']}, color:'#9be07a', borderColor:'#3a5a18', cards:10 },
    { eyebrow:'The Storm-Blooded', sub:'Genasi, Tritons, Storm Sorcerers. The room hums when they argue.', filter:{race:['Genasi','Triton','Djinn Blood Human','Primordial Concept']}, color:'#7ed4ff', borderColor:'#1a5a8a', cards:10 },
    /* R148-Z22x: 15 new themed Type 1 rolls — adds atmospheric
       variety to the heart-roll bucket. The Spectrum Ascendant
       (last entry) is a rare 4-row capstone that uses a custom
       predicate to assemble a rarity ladder (Uncommon → Rare →
       Epic → Legendary), one row per tier. The picker treats it
       as a normal themed phase but the predicate ensures the pool
       arrives pre-segmented; the renderer reads pool length 16
       and lays out 4-per-row by default. */
    { eyebrow:'The Iron Veterans', sub:'Old hands, scarred and unhurried. They\'ve seen what you\'re about to learn.', filter:{pred:'ironVeterans'}, color:'#cd7f32', borderColor:'#5a3818', cards:10 },
    { eyebrow:'The Silent Order', sub:'Monks of the Open Hand and Shadow. Stillness is a weapon.', filter:{cls:['Monk']}, color:'#9be07a', borderColor:'#3a5a18', cards:10 },
    { eyebrow:'The Tide-Sworn', sub:'Tritons, water genasi, sea-blooded sorcerers. Salt in the marble.', filter:{race:['Triton','Water Genasi','Genasi','Sea Elf'],cls:['Sorcerer','Druid','Fighter']}, color:'#5ec4d4', borderColor:'#1a5a6a', cards:10 },
    { eyebrow:'The Lich\'s Court', sub:'Necromancers, Death Clerics, the long-undead. The candles dim.', filter:{race:['Vampire','Lich','Dhampir','Undead','Shade']}, color:'#6b4d8f', borderColor:'#2a1a4a', cards:10 },
    { eyebrow:'The Wild Hunt Rides', sub:'Druids of the moon, Fey-cursed rangers, beasts. Horns at dusk.', filter:{cls:['Druid','Ranger'],race:['Fairy','Centaur','Satyr','Firbolg']}, color:'#5b8a3c', borderColor:'#1a3a18', cards:15 },
    { eyebrow:'The Last Lantern', sub:'Heroes whose light hasn\'t carried yet. Carry it now.', filter:{pred:'lastLantern'}, color:'#f2d98e', borderColor:'#7a5318', cards:10 },
    { eyebrow:'The Pact Choir', sub:'Warlocks of every patron, voices overlapping in old languages.', filter:{cls:['Warlock']}, color:'#b794f4', borderColor:'#4a2a8a', cards:10 },
    { eyebrow:'The Steel Procession', sub:'Fighters, Paladins, anyone who carries armor like a second skin.', filter:{cls:['Fighter','Paladin']}, color:'#c0c0c0', borderColor:'#4a4a4a', cards:15 },
    { eyebrow:'The Trickster\'s Stage', sub:'Bards, Rogues, anyone who has stolen a heart.', filter:{cls:['Bard','Rogue']}, color:'#ff9bd2', borderColor:'#7a2a5a', cards:10 },
    { eyebrow:'The Stone Choir', sub:'Goliaths, Dwarves, Earth Genasi. The floor remembers them.', filter:{race:['Goliath','Dwarf','Earth Genasi']}, color:'#8b7355', borderColor:'#3a2a18', cards:10 },
    { eyebrow:'The Soft-Spoken', sub:'Halflings, Gnomes, Kobolds. Don\'t underestimate quiet.', filter:{race:['Halfling','Gnome','Kobold']}, color:'#ffb380', borderColor:'#6a3a18', cards:10 },
    { eyebrow:'The Crowned Wits', sub:'Wizards, Artificers, Bards. The room re-arranges around them.', filter:{cls:['Wizard','Artificer']}, color:'#4a90e2', borderColor:'#1a4a8a', cards:10 },
    { eyebrow:'The Long Memory', sub:'Heroes who have walked the hall for a season. They watched you arrive.', filter:{pred:'longMemory'}, color:'#c9a96e', borderColor:'#5a3a18', cards:10 },
    { eyebrow:'The Final Verse', sub:'Bardic colleges, story-bringers, elven minstrels. The hall hums when they begin.', filter:{cls:['Bard'],race:['Elf','High Elf','Wood Elf','Drow','Half-Elf']}, color:'#ff7eb5', borderColor:'#7a2a5a', cards:10 },
    /* The Spectrum Ascendant — rare 4-row capstone. Predicate
       'spectrumAscendant' returns a pre-segmented array: 4 uncommon
       → 4 rare → 4 epic → 4 legendary. Backfill with epics if the
       legendary pool is thin (user has 9 legendaries currently, so
       backfill is just safety). cards:16 tells the renderer the
       expected grid size. */
    { eyebrow:'The Spectrum Ascendant', sub:'Common to Legendary, rising. The hall pauses to watch them climb.', filter:{pred:'spectrumAscendant'}, color:'rainbow', borderColor:'#5a3a7a', cards:16 },
    /* R590 (Jan): two new rolls, both aimed at heroes the rotation
       could not reach before.

       THE MERCHANT'S MARKED. Jan: "a role specifically made for heroes
       with the Merchant's Mark ... that way they actually feel more
       impacted." The subtitle states the buff plainly because the buff
       is real but invisible everywhere else: mark_heart_explore
       (sql/R208:105) adds +1 to a marked hero on every Explore heart,
       and until now nothing on screen ever said so.
       No rare:true. The pool is small by nature, and isRecentTheme's
       5-deep buffer already stops back-to-back repeats. */
    { eyebrow:'The Merchant\'s Marked', sub:'Heroes wearing the Merchant\'s Mark. Every heart you give one of them lands twice.', filter:{pred:'merchantsMark'}, color:'#f2c889', borderColor:'#8a5a18', cards:10 },
    /* NO TWO ALIKE. Jan: "maybe those characters never get shown, so we
       might need to make another themed role, which shows all of the
       weird ones, so that all of the characters have a chance to be
       shown." Selects heroes whose race or class sits outside the
       canonical lists, which is the only group no other themed filter
       can reach. See hfIsOrphanChar for why blanks are excluded. */
    { eyebrow:'No Two Alike', sub:'Heroes who match no list in the Hall. They wrote their own race and their own calling.', filter:{pred:'orphans'}, color:'#8ce0c0', borderColor:'#2a6a55', cards:15 }
  ];
  var PHASE_HEARTED = { eyebrow:'Heroes You\'ve Lifted', sub:'The ones you\'ve already loved.' };
  /* Picked phase for this session (random). Persists per page-load,
     not per refresh — refresh re-rolls. */
  var currentThemedPhase = null;

  /* Random unseen + unhearted characters. Optional filter narrows the
     pool by race or class so themed phases (Wizards / Tieflings /
     Bards) actually show what their copy promises.

     filter.pred = 'rising' picks heroes with above-average heart
     velocity (hearts / days since submission) within the last 30 days.
     Surfaces momentum, not all-time totals.

     filter.pred = 'ff' picks only is_ff_portrait characters. */
  function unseenList(filter){
    var all = (typeof window.hohGetAllCharacters === 'function') ? window.hohGetAllCharacters() : [];
    if(!all || !all.length) return [];
    var dismissed = getDismissed();
    var dismissedSet = {};
    dismissed.forEach(function(id){ dismissedSet[id] = true; });
    var raceFilter = filter && filter.race ? (Array.isArray(filter.race) ? filter.race : [filter.race]) : null;
    /* R136-themed-rotation: cls normalized to array — same fix as
       themePoolSize. Without this, themes like The Pact-Bound
       (cls:['Warlock','Blood Hunter']) would render an empty rail
       even though themePoolSize correctly reported eligible chars. */
    var classFilter = filter && filter.cls ? (Array.isArray(filter.cls) ? filter.cls : [filter.cls]) : null;
    var pred = filter && filter.pred ? filter.pred : null;
    /* Compute rising-star threshold once: median velocity across all
       chars submitted in the last 30d that have >=3 hearts. Anyone
       above the median (and >=3 hearts) qualifies. */
    var velThresh = 0;
    if(pred === 'rising'){
      var now = Date.now();
      var WINDOW = 30 * 24 * 60 * 60 * 1000;
      var vels = all.map(function(c){
        if(!c || !c.created_at || (c.hearts||0) < 3) return null;
        var t = new Date(c.created_at).getTime();
        if(isNaN(t) || (now - t) > WINDOW) return null;
        var days = Math.max(1, (now - t) / (24*60*60*1000));
        return (c.hearts || 0) / days;
      }).filter(function(v){ return v !== null; }).sort(function(a,b){ return a - b; });
      velThresh = vels.length ? vels[Math.floor(vels.length / 2)] : 0;
    }
    /* R116: Showcase mode bypasses hearted/dismissed/intent filters so
       a curated wing (Portrait Gallery) shows EVERY matching character,
       not just the ones the user hasn't engaged with. Without this,
       hearting 5 of 12 FF portraits collapsed the gallery to 7 cards
       and the user experienced it as "the gallery is broken." */
    var isShowcase = !!(filter && filter.showcase);
    /* R148-Z28 sticky-visible guard: build a set of currently rendered
       card ids. A card with heartIntent set but still in the grid is
       mid-fade — the CSS animation will remove it visually, but the
       picker should NOT prune it from the pool yet, because the
       reconciliation step downstream uses the pool to validate slot
       contents. Pruning a card that's still on screen forces the
       reconciler to drop a UNRELATED slot to fill the count, which is
       the root cause of "I hearted 2 cards and a third one
       disappeared." After the fade completes the card is no longer in
       the rendered set (next render's lastRenderedIds excludes it)
       and normal pruning resumes. */
    var renderedSet = {};
    try {
      if(Array.isArray(lastRenderedIds)){
        lastRenderedIds.forEach(function(id){ if(id) renderedSet[id] = true; });
      }
    } catch(_){}
    function buildPool(opts){
      opts = opts || {};
      return all.filter(function(c){
        if(!c || !c.id) return false;
        if(!isShowcase && !opts.includeDismissed && dismissedSet[c.id]) return false;
        /* R148-Z29: hearted exclusion ALSO honors the sticky-visible
           guard. Without this, the moment getMineCount(c.id) > 0
           (server confirms the heart), the card gets pruned from the
           pool. Snapshot reconciliation drops the slot, and the user
           watches their just-hearted card fade away instead of
           settling into its "spent" state in place. The visual
           contract is: a card you spend hearts on STAYS in its slot
           with the spent-overlay class, until the phase changes or
           the user navigates away. */
        if(!isShowcase && !opts.includeHearted && getMineCount(c.id) > 0 && !renderedSet[c.id]) return false;
        /* heartIntent exclusion: ONLY exclude if the card is NOT
           currently rendered. In-flight hearts on visible cards stay
           in the pool so the reconciler keeps the slot stable until
           the fade-out completes. */
        if(!isShowcase && heartIntent[c.id] && !renderedSet[c.id]) return false;
        if(raceFilter && raceFilter.indexOf(c.race) === -1) return false;
        if(classFilter && classFilter.indexOf(c.character_class) === -1) return false;
        if(pred === 'ff' && !c.is_ff_portrait) return false;
        /* R590: shared preds. Same call in themePoolSize — see
           hfSharedPredOk. Never inline one of these in only one copy. */
        if(!hfSharedPredOk(pred, c)) return false;
        /* R118-G/H: rarity-band predicates for the two new gathering
           themes. Epic threshold = 200 hearts (matches hohGetRarity).
           Mythic chars are excluded from low-rarity (they're at 1000+
           which is way above epic). For highRarity, mythic IS allowed
           since the gathering celebrates the upper tiers. */
        if(pred === 'lowRarity' && (c.hearts || 0) >= 200) return false;
        if(pred === 'highRarity' && (c.hearts || 0) < 200) return false;
        if(pred === 'rising'){
          if((c.hearts||0) < 3) return false;
          if(!c.created_at) return false;
          var t = new Date(c.created_at).getTime();
          if(isNaN(t)) return false;
          var nowR = Date.now();
          if((nowR - t) > 30*24*60*60*1000) return false;
          var days = Math.max(1, (nowR - t) / (24*60*60*1000));
          if(((c.hearts || 0) / days) < velThresh) return false;
        }
        /* R140-Z3n: 'newcomers' — heroes created in the last 7 days. */
        if(pred === 'newcomers'){
          if(!c.created_at) return false;
          var tN = new Date(c.created_at).getTime();
          if(isNaN(tN)) return false;
          if((Date.now() - tN) > 7*24*60*60*1000) return false;
        }
        /* R140-Z3n: 'forgotten' — hearts <10 AND created >30 days ago.
           Quiet heroes the Hall hasn't lifted. */
        if(pred === 'forgotten'){
          if((c.hearts||0) >= 10) return false;
          if(!c.created_at) return false;
          var tF = new Date(c.created_at).getTime();
          if(isNaN(tF)) return false;
          if((Date.now() - tF) < 30*24*60*60*1000) return false;
        }
        /* R140-Z3n (Phase 3): TTRPG-flavored band predicates. */
        if(pred === 'veterans'){
          if((c.hearts||0) < 100) return false;
        }
        if(pred === 'apprentices'){
          if((c.hearts||0) >= 10) return false;
          if(!c.created_at) return false;
          var tA = new Date(c.created_at).getTime();
          if(isNaN(tA)) return false;
          if((Date.now() - tA) > 14*24*60*60*1000) return false;
        }
        if(pred === 'mythics'){
          if((c.hearts||0) < 1000) return false;
        }
        /* R148-Z23: New predicates for the second-wave themed phases. */
        if(pred === 'ironVeterans'){
          /* Old hands. Hearts >=50 AND created >=60 days ago. The age
             gate is what makes them VETERANS not just popular. */
          if((c.hearts||0) < 50) return false;
          if(!c.created_at) return false;
          var tIV = new Date(c.created_at).getTime();
          if(isNaN(tIV)) return false;
          if((Date.now() - tIV) < 60*24*60*60*1000) return false;
        }
        if(pred === 'lastLantern'){
          /* Heroes whose light hasn't carried yet. Hearts <5 AND created
             >=7 days ago (so they're not brand-new and need a boost). */
          if((c.hearts||0) >= 5) return false;
          if(!c.created_at) return false;
          var tLL = new Date(c.created_at).getTime();
          if(isNaN(tLL)) return false;
          if((Date.now() - tLL) < 7*24*60*60*1000) return false;
        }
        if(pred === 'longMemory'){
          /* Walked the hall for a season. Created >=90 days ago. */
          if(!c.created_at) return false;
          var tLM = new Date(c.created_at).getTime();
          if(isNaN(tLM)) return false;
          if((Date.now() - tLM) < 90*24*60*60*1000) return false;
        }
        /* spectrumAscendant: accept ALL chars in basePool — the slice
           composition below buckets them into the 4-row ladder. */
        return true;
      });
    }
    var basePool = buildPool();
    /* Full reset: if every matching character has been hearted or
       hidden, relax the filters and let users go through their
       hearted/dismissed heroes again. Better than collapsing the
       section to nothing — the user explicitly asked for a "do it
       again with all the liked heroes" fallback. We also clear the
       seen set so the relaxed pool isn't immediately filtered to
       empty by the unseen pre-pass below. */
    if(!basePool.length){
      basePool = buildPool({ includeHearted: true, includeDismissed: true });
      if(basePool.length){
        seenChars = Object.create(null);
        try { saveSeenSet(SEEN_CHARS_KEY, seenChars); } catch(_){}
      }
    }
    /* R148-Z14: Unseen-first rotation with seen-as-filler.
       Previous behavior aggressively cleared seenChars whenever the
       unseen subset dropped below MIN_THEME_CHARS — which meant a
       new player hitting a thin themed roll early on would have their
       hall-wide seen-memory wiped, defeating the "every hero shows
       up once" goal.
       New behavior:
         - Split basePool into unseen + seen tiers.
         - Shuffle each tier independently, then concat (unseen-first).
         - The slice naturally picks unseen chars first; if there aren't
           enough unseen for the slice, seen chars fill the remaining
           slots as supporting cast. The user still sees the unseen
           heroes they haven't met, padded with familiar faces.
         - Reset seenChars ONLY when unseenPool is empty for this filter
           AND the full hall has been viewed. That's the genuine full-
           cycle moment: every eligible hero has had at least one
           appearance. */
    var pool;
    if(isShowcase){
      pool = basePool;
    } else {
      var unseenPool = basePool.filter(function(c){ return !seenChars[c.id]; });
      var seenPool = basePool.filter(function(c){ return !!seenChars[c.id]; });
      /* Full-cycle reset: only clear when unseen is empty for this
         filter. Pad-with-seen handles the thin-pool case without
         destroying cross-theme seen-memory. */
      if(unseenPool.length === 0 && seenPool.length > 0){
        seenChars = Object.create(null);
        try { saveSeenSet(SEEN_CHARS_KEY, seenChars); } catch(_){}
        unseenPool = basePool;
        seenPool = [];
      }
      pool = { unseen: unseenPool, seen: seenPool };
    }
    /* Themed phases can opt for a specific sort order via filter.sortBy.
       Default is random shuffle ("you stumble into a gathering"). The
       Portrait Gallery uses 'hearts' so the most-loved portraits lead
       the wing — turning a random feed into a curated showcase. */
    var sortBy = filter && filter.sortBy;
    var shuf;
    if(isShowcase){
      /* Showcase mode bypasses unseen/seen tiering. */
      if(sortBy === 'hearts'){
        shuf = pool.slice().sort(function(a, b){ return (b.hearts || 0) - (a.hearts || 0); });
      } else {
        shuf = hfShuffle(pool);
      }
    } else {
      /* Sort each tier separately, then concat with unseen first. The
         slice below naturally takes unseen chars before falling back
         to seen filler. */
      var unseenTier, seenTier;
      if(sortBy === 'hearts'){
        unseenTier = pool.unseen.slice().sort(function(a, b){ return (b.hearts || 0) - (a.hearts || 0); });
        seenTier   = pool.seen.slice().sort(function(a, b){ return (b.hearts || 0) - (a.hearts || 0); });
      } else {
        unseenTier = hfShuffle(pool.unseen);
        seenTier   = hfShuffle(pool.seen);
      }
      shuf = unseenTier.concat(seenTier);
    }
    var sliced = shuf.slice(0, MAX_VISIBLE);
    /* R148-Z23: Spectrum Ascendant — 4-row rainbow ladder. The phase
       wants exactly 4 chars per tier (uncommon, rare, epic, legendary)
       in that order, not a random shuffle. Bucket the shuffled pool
       by rarity and reassemble with 4 per row, backfilling missing
       tiers from the next-lower tier so a sparse hall still renders. */
    if(pred === 'spectrumAscendant'){
      var rar = function(c){
        try { return (window.hohGetRarity ? window.hohGetRarity(c.hearts || 0).tier : 'common'); }
        catch(_){ return 'common'; }
      };
      var bUncommon = shuf.filter(function(c){ return rar(c) === 'uncommon'; });
      var bRare     = shuf.filter(function(c){ return rar(c) === 'rare'; });
      var bEpic     = shuf.filter(function(c){ return rar(c) === 'epic'; });
      var bLegend   = shuf.filter(function(c){ return rar(c) === 'legendary' || rar(c) === 'mythic'; });
      var SLOT = 4;
      var take = function(arr, n){ return arr.slice(0, n); };
      var u = take(bUncommon, SLOT);
      var r = take(bRare,     SLOT);
      var e = take(bEpic,     SLOT);
      var l = take(bLegend,   SLOT);
      /* Backfill: if legendary row is thin, top up from epic; if epic
         is thin, top up from rare; if rare thin, top up from uncommon.
         Keeps the visual ladder full even when the hall lacks the
         exact rarity mix. */
      function backfill(target, src){
        var taken = {};
        target.forEach(function(c){ if(c && c.id) taken[c.id] = 1; });
        for(var i = 0; i < src.length && target.length < SLOT; i++){
          if(!taken[src[i].id]){ target.push(src[i]); taken[src[i].id] = 1; }
        }
      }
      backfill(l, bEpic);
      backfill(l, bRare);
      backfill(e, bRare);
      backfill(e, bUncommon);
      backfill(r, bUncommon);
      sliced = u.concat(r).concat(e).concat(l);
    }
    /* Mark everything we're returning as seen. Persist to localStorage
       so subsequent visits continue where the user left off. */
    sliced.forEach(function(c){ if(c && c.id) seenChars[c.id] = true; });
    saveSeenSet(SEEN_CHARS_KEY, seenChars);
    return sliced;
  }

  /* Cheap pool-size check used by the refresh picker to decide
     whether a themed phase has enough chars to be worth showing.
     Mirrors the unseenList filter logic but skips the shuffle and
     just counts. */
  function themePoolSize(filter){
    var all = (typeof window.hohGetAllCharacters === 'function') ? window.hohGetAllCharacters() : [];
    if(!all || !all.length) return 0;
    var dismissed = getDismissed();
    var dSet = {};
    dismissed.forEach(function(id){ dSet[id] = true; });
    var raceFilter = filter && filter.race ? (Array.isArray(filter.race) ? filter.race : [filter.race]) : null;
    /* R136-themed-rotation: cls filter now also normalizes to array
       so themes like The Pact-Bound (cls:['Warlock','Blood Hunter'])
       and The Healers' Round equivalents work. The original code
       compared with strict !== against the raw value, which silently
       returned 0 for array filters. */
    var classFilter = filter && filter.cls ? (Array.isArray(filter.cls) ? filter.cls : [filter.cls]) : null;
    var pred = filter && filter.pred ? filter.pred : null;
    /* R140-W: Count BOTH strict (unhearted only) and relaxed (all
       matching chars) so the picker can prefer themes with unhearted
       material but still treat themes as eligible when the user has
       hearted everything. The previous strict-only count made all
       themes ineligible for active users — they ended up stuck on
       Phase 0 / Wandering Heroes with 1 hero (the just-submitted
       new arrival). The renderer's unseenList has its own relaxation
       that includes hearted chars when needed, so an "eligible via
       relaxed count" theme still renders a populated grid. */
    var strict = 0;
    var relaxed = 0;
    for(var i = 0; i < all.length; i++){
      var c = all[i];
      if(!c || !c.id) continue;
      if(dSet[c.id]) continue;
      if(heartIntent[c.id]) continue;
      if(raceFilter && raceFilter.indexOf(c.race) === -1) continue;
      if(classFilter && classFilter.indexOf(c.character_class) === -1) continue;
      if(pred === 'ff' && !c.is_ff_portrait) continue;
      /* R590: shared preds. Same call in unseenList — see hfSharedPredOk. */
      if(!hfSharedPredOk(pred, c)) continue;
      if(pred === 'rising'){
        if((c.hearts||0) < 3) continue;
        if(!c.created_at) continue;
        var t = new Date(c.created_at).getTime();
        if(isNaN(t)) continue;
        if((Date.now() - t) > 30*24*60*60*1000) continue;
      }
      /* R140-Z3n: newcomers + forgotten predicate gates mirror unseenList. */
      if(pred === 'newcomers'){
        if(!c.created_at) continue;
        var tN = new Date(c.created_at).getTime();
        if(isNaN(tN)) continue;
        if((Date.now() - tN) > 7*24*60*60*1000) continue;
      }
      if(pred === 'forgotten'){
        if((c.hearts||0) >= 10) continue;
        if(!c.created_at) continue;
        var tF = new Date(c.created_at).getTime();
        if(isNaN(tF)) continue;
        if((Date.now() - tF) < 30*24*60*60*1000) continue;
      }
      /* R140-Z3n (Phase 3): TTRPG-band predicates mirror unseenList. */
      if(pred === 'veterans'){
        if((c.hearts||0) < 100) continue;
      }
      if(pred === 'apprentices'){
        if((c.hearts||0) >= 10) continue;
        if(!c.created_at) continue;
        var tA = new Date(c.created_at).getTime();
        if(isNaN(tA)) continue;
        if((Date.now() - tA) > 14*24*60*60*1000) continue;
      }
      if(pred === 'mythics'){
        if((c.hearts||0) < 1000) continue;
      }
      /* R148-Z23: pool-size gates for the new predicates. */
      if(pred === 'ironVeterans'){
        if((c.hearts||0) < 50) continue;
        if(!c.created_at) continue;
        var tIVp = new Date(c.created_at).getTime();
        if(isNaN(tIVp)) continue;
        if((Date.now() - tIVp) < 60*24*60*60*1000) continue;
      }
      if(pred === 'lastLantern'){
        if((c.hearts||0) >= 5) continue;
        if(!c.created_at) continue;
        var tLLp = new Date(c.created_at).getTime();
        if(isNaN(tLLp)) continue;
        if((Date.now() - tLLp) < 7*24*60*60*1000) continue;
      }
      if(pred === 'longMemory'){
        if(!c.created_at) continue;
        var tLMp = new Date(c.created_at).getTime();
        if(isNaN(tLMp)) continue;
        if((Date.now() - tLMp) < 90*24*60*60*1000) continue;
      }
      /* spectrumAscendant: no filter — anyone qualifies for the ladder
         composition below. The slice step does the rarity bucketing. */
      relaxed++;
      if(getMineCount(c.id) === 0) strict++;
      /* Early exit when STRICT count meets a comfortable target —
         strict pool is preferred and we don't need to walk the rest
         of the array if we've found plenty of unhearted matches.
         R148-Z2: Was capped at MIN_THEME_CHARS (3); raised to 12 so
         we get an accurate count for the dispatcher's filtering /
         logging needs even when strict is plentiful. */
      if(strict >= 12) return strict;
    }
    /* R148-Z2: Return the relaxed count regardless of whether it
       hits a threshold. The dispatcher's job is to decide eligibility
       (it now only requires >= 1 match), and the renderer's job is to
       pad short pools with random heroes (so a 2-match theme still
       fills the grid with thematic + filler heroes instead of bailing
       to Wandering Heroes). Returning a small relaxed value lets both
       layers behave intelligently. The previous floor-or-strict
       fallback hid thin themes from the dispatcher entirely, which
       is what caused Wandering Heroes to dominate the rotation: it's
       the ONLY theme with no filter and therefore the only one whose
       count was reliably above the floor. */
    return relaxed > 0 ? relaxed : strict;
  }

  /* User's hearted heroes, most-loved first. Used in final phase. */
  function heartedList(){
    var all = (typeof window.hohGetAllCharacters === 'function') ? window.hohGetAllCharacters() : [];
    if(!all || !all.length) return [];
    return all.filter(function(c){
      return c && c.id && getMineCount(c.id) > 0;
    }).sort(function(a, b){ return getMineCount(b.id) - getMineCount(a.id); });
  }

  function pickListForPhase(phase){
    if(phase === -1) return heartedList();
    if(phase === 0) return freshList();
    /* Themed wandering. Reuse the cached pool unless refresh cleared
       it (View More re-renders against the same cards). */
    if(cachedWanderingPool && cachedWanderingPhase === phase) return cachedWanderingPool;
    var theme = currentThemedPhase || THEMED_PHASES[0];
    var unseen = unseenList(theme.filter);
    /* R148-Z2: Per user, themed rolls NEVER silently switch to
       Wandering Heroes when their thematic pool is thin. Instead we
       KEEP the picked theme (so the eyebrow still reads
       "Druid Migration" / "Bardstorm" / whatever the user was
       promised) and PAD the grid with random heroes from the wider
       pool so the user sees ≥2 rows. The padding logic mirrors the
       untyped-wandering safety net immediately below — same source
       (allChars minus dismissed/intent), same shuffle, same target
       count (WANDER_MIN).

       History of this block:
         R62: Reset to phase 0 / Fresh Faces if pool < MIN_THEME_CHARS.
         R140-J: Switched to Wandering Heroes instead of phase 0 to
                 avoid the "refresh loops back to Fresh Faces" bug.
         R148-Z2: Stop switching themes entirely — pad with randoms
                  and let the theme stand. Wandering Heroes was being
                  picked too often as a side effect of this fallback. */
    if(theme.filter){
      var WANDER_MIN_THEMED = 12;
      if(unseen.length < WANDER_MIN_THEMED){
        var allCharsT = (typeof window.hohGetAllCharacters === 'function') ? window.hohGetAllCharacters() : [];
        var dismissedT = getDismissed();
        var dSetT = {};
        dismissedT.forEach(function(id){ dSetT[id] = true; });
        var haveT = {};
        unseen.forEach(function(c){ if(c && c.id) haveT[c.id] = true; });
        var padT = allCharsT.filter(function(c){
          return c && c.id && !haveT[c.id] && !dSetT[c.id] && !heartIntent[c.id];
        });
        /* Shuffle so each refresh on a thin theme shows a different
           filler set — keeps the experience feeling alive even when
           the same theme repeats. */
        padT = hfShuffle(padT);
        unseen = unseen.concat(padT.slice(0, WANDER_MIN_THEMED - unseen.length));
      }
    }
    /* R140-Z3m: Untyped wandering safety net — guarantee at least 2
       rows of characters on Wandering Heroes / Crossroads / The Long
       Road. Long-time accounts that have hearted most of the hall
       were seeing the wandering pool shrink to 1-2 chars (just the
       newest unhearted arrivals). When the active theme has NO
       filter (= untyped wandering) and the unseen+unhearted pool is
       under WANDER_MIN, we backfill with a random sample of hearted
       chars (excluding dismissed) so the user always sees a
       populated rail. Filtered themes (Wizards, Druids, Tieflings,
       etc.) keep their stricter unseen-only rotation because the
       point of those is discovery, not familiar faces. */
    var WANDER_MIN = 12;
    if(!theme.filter && unseen.length < WANDER_MIN){
      var allChars = (typeof window.hohGetAllCharacters === 'function') ? window.hohGetAllCharacters() : [];
      var dismissed = getDismissed();
      var dSet = {};
      dismissed.forEach(function(id){ dSet[id] = true; });
      var have = {};
      unseen.forEach(function(c){ if(c && c.id) have[c.id] = true; });
      var pad = allChars.filter(function(c){
        return c && c.id && !have[c.id] && !dSet[c.id] && !heartIntent[c.id];
      });
      /* Shuffle so the backfill set varies on each refresh — the user
         doesn't see the same hearted-pad cards every time. */
      pad = hfShuffle(pad);
      unseen = unseen.concat(pad.slice(0, WANDER_MIN - unseen.length));
    }
    if(!unseen.length){
      setPhase(-1);
      cachedWanderingPool = null;
      cachedWanderingPhase = null;
      return heartedList();
    }
    cachedWanderingPool = unseen;
    cachedWanderingPhase = phase;
    return unseen;
  }

  function getEyebrowForPhase(phase){
    if(phase === -1) return PHASE_HEARTED;
    if(phase === 0) return DEFAULT_PHASE;
    return currentThemedPhase || THEMED_PHASES[0];
  }

  /* Reset phase back to 0 (Fresh Faces). Used by the empty-state UI
     when phase -1 has nothing to show — keeps the user from getting
     stuck with no heroes and no controls. */
  window.hohFreshFacesResetPhase = function(){
    setPhase(0);
    cachedWanderingPool = null;
    cachedWanderingPhase = null;
    renderedSnapshotIds = null;
    snapshotPhaseKey = null;
    window.hohRenderFreshFaces();
  };

  /* ================================================================ */
  /* Special events                                                   */
  /* ================================================================ */
  /* The Fresh Faces rail isn't just a refresh button — sometimes
     refreshing yields a one-off encounter that breaks out of the
     normal grid. These events make the act of refreshing feel like
     traveling through the world rather than reloading a feed.

       legendary     1 centered legendary character, custom title.
       epic          3 epic characters in a stronger-glow trio.
       choice        Two-path picker. User picks a destination, the
                     rail populates with characters from that path.
       thievesCoin   Coin flip mini-game. Heads = rogue theme +
                     bonus heart with full heart animation. Tails =
                     "you missed the secret hideout" auto-refresh.

     Events roll on each user-driven refresh. Auto-refresh (the
     20-second timer) skips events on purpose — surprise should
     reward intent, not catch the user mid-thought. */

  var currentSpecial = null;

  /* Event history. We persist a Set of event types the user has seen
     so the roller prefers unseen ones — refreshing should reveal new
     experiences before showing repeats. When every eligible event
     has been seen, the set resets and the cycle starts over.

     Event types tracked: legendary, legendaryTrio, epic, thievesCoin,
     classQuiz, spectrum, rarityTribe-common, rarityTribe-uncommon,
     rarityTribe-rare, rarityTribe-epic, familiar, ffPortrait, marrowEnd,
     secondChances, heroCircle, choice, banished. Stored client-side
     only; resets per device on full localStorage clear. */
  var SEEN_SPECIALS_KEY = 'hoh-fresh-seen-specials';
  var seenSpecials = loadSeenSet(SEEN_SPECIALS_KEY);
  function eventKey(type, sub){ return sub ? type + '-' + sub : type; }
  function markEventSeen(key){
    seenSpecials[key] = true;
    saveSeenSet(SEEN_SPECIALS_KEY, seenSpecials);
  }
  function isEventSeen(key){ return !!seenSpecials[key]; }
  /* Reset when ALL of these have been seen so cycling restarts. The
     key list is the universe of event types the roller can produce. */
  var ALL_EVENT_KEYS = [
    /* R554: 'ffSpotlight' removed with its producer. THIS LINE MATTERS AS
       MUCH AS THE PRODUCER: the seen-set resets only once EVERY key here has
       been seen, so leaving a key that can never be produced would mean the
       reset condition is never satisfied and the rotation slowly starves. */
    'legendary','legendaryTrio','twoLegends','championMeeting',
    'epic','thievesCoin','classQuiz','spectrum',
    'rarityTribe-common','rarityTribe-uncommon','rarityTribe-rare','rarityTribe-epic',
    /* R136-B: New themed character phases. Each is a color-themed
       gathering of heroes the user can heart on the rail. Listed here
       so the seen-set cycle includes them in rotation, otherwise the
       reset condition would never include them and they'd dominate
       the rail past the natural reset point. */
    'themedPhase-twilight','themedPhase-healers','themedPhase-vanguard',
    'themedPhase-mages','themedPhase-beastfolk','themedPhase-quietest',
    'themedPhase-brightest',
    'familiar','ffPortrait','marrowEnd','secondChances','heroCircle','choice',
    'banished',
    /* R136-audit: mageBazaar (R116-C) needs to be on this list so the
       cycle resetSeenSpecialsIfFullyCycled() check actually waits for
       it before the wipe.
       R136-revert: lostAdventurer + starFalls removed entirely — the
       minigame events were dropped per user request. Their picker
       entries, render functions, and key-list memberships are gone.
       Stale localStorage seen-flags from prior sessions are harmless
       (they just sit there; no event references them anymore). */
    'mageBazaar'
  ];
  function resetSeenSpecialsIfFullyCycled(){
    var unseen = ALL_EVENT_KEYS.filter(function(k){ return !seenSpecials[k]; });
    if(unseen.length === 0){
      seenSpecials = Object.create(null);
      saveSeenSet(SEEN_SPECIALS_KEY, seenSpecials);
    }
  }

  /* Special-event pool helpers. Hearted heroes are ALLOWED to surface
     in special events (re-encountering a familiar face is part of the
     drama), so the default opts.includeHearted is true. Dismissed
     heroes stay out — the user explicitly hid those. heartIntent is
     also honored to avoid showing a card the user just hearted in the
     same render cycle. */
  function _basePool(opts){
    opts = opts || {};
    var includeHearted = opts.includeHearted !== false; /* default ON */
    var all = (typeof window.hohGetAllCharacters === 'function') ? window.hohGetAllCharacters() : [];
    var dismissed = getDismissed();
    var dSet = {}; dismissed.forEach(function(id){ dSet[id] = true; });
    return all.filter(function(c){
      if(!c || !c.id) return false;
      /* R140-Z3h: Hidden chars (debug-mode hide) NEVER reach event
         pickers — they shouldn't host legendary encounters, appear in
         themed phases, or feature in any wandering content. The main
         gallery has its own search-by-exact-name escape hatch. */
      if(c.is_hidden === true) return false;
      if(dSet[c.id]) return false;
      if(!includeHearted && getMineCount(c.id) > 0) return false;
      if(heartIntent[c.id]) return false;
      return true;
    });
  }

  function poolByTier(tier, opts){
    return _basePool(opts).filter(function(c){
      var t = (typeof window.hohGetRarity === 'function') ? window.hohGetRarity(c.hearts || 0).tier : 'common';
      return t === tier;
    });
  }

  function poolByClass(cls, opts){
    return _basePool(opts).filter(function(c){
      return c.character_class === cls;
    });
  }

  /* Heroes the user has hearted at least once on this device. Used by
     the Familiar Faces event. Dismissed heroes still excluded. */
  function poolHearted(){
    var all = (typeof window.hohGetAllCharacters === 'function') ? window.hohGetAllCharacters() : [];
    var dismissed = getDismissed();
    var dSet = {}; dismissed.forEach(function(id){ dSet[id] = true; });
    return all.filter(function(c){
      if(!c || !c.id) return false;
      if(dSet[c.id]) return false;
      return getMineCount(c.id) > 0;
    }).sort(function(a, b){ return getMineCount(b.id) - getMineCount(a.id); });
  }

  /* Pool of FF portraits only — used by the FF Portrait event. */
  function poolFf(opts){
    return _basePool(opts).filter(function(c){ return !!c.is_ff_portrait; });
  }

  /* The choice event picks two themed phases at random whose pools
     are big enough. We avoid offering the same path twice and prefer
     pairs that contrast (race vs class so the user feels like they're
     making a real choice, not a-vs-a-but-different). Returns null if
     we can't find two viable options. */
  function pickChoiceOptions(){
    var pool = THEMED_PHASES.filter(function(p){
      return p.filter && themePoolSize(p.filter) >= MIN_THEME_CHARS;
    });
    if(pool.length < 2) return null;
    var first = pool[Math.floor(Math.random() * pool.length)];
    var rest = pool.filter(function(p){ return p.eyebrow !== first.eyebrow; });
    if(!rest.length) return null;
    var second = rest[Math.floor(Math.random() * rest.length)];
    /* Shorten flavored "destination" labels for the choice cards.
       Fall back to the eyebrow if we don't have a custom mapping. */
    function destLabel(p){
      var m = p.eyebrow.replace(/^The\s+/i, '').replace(/\s+'s?$/, '').trim();
      return m;
    }
    /* R96: Pick a sample HOST char per option — first matching char
       in that theme's pool. The renderer shows it above each "Take
       this road" card so the user can see what kind of hero lives
       down each path.
       R136-rising-host: _matchesThemeFilter only knows race/cls/ff
       predicates — it doesn't understand pred:'rising' (Rising Stars
       uses heart-velocity math that lives in unseenList, not in the
       filter helper). That meant Rising Stars produced a null host
       and rendered an empty option card. Fallback: when the theme's
       filter returns zero matches, pick a legendary character as the
       host. Thematically appropriate for Rising Stars (climbing fast
       toward established status) and a safe catch-all for any future
       theme with an unrecognized predicate. */
    function sampleHost(themePhase){
      try {
        var allChars = (typeof window.hohGetAllCharacters === 'function') ? window.hohGetAllCharacters() : [];
        var matches = allChars.filter(function(c){ return c && c.id && _matchesThemeFilter(c, themePhase.filter); });
        if(matches.length){
          return matches[Math.floor(Math.random() * matches.length)];
        }
        /* Fallback: random legendary. poolByTier respects dismissed +
           heartIntent so the host is a "live" hero from the user's
           current pool, not a hidden or pending one. */
        var legendaries = (typeof poolByTier === 'function') ? (poolByTier('legendary') || []) : [];
        if(legendaries.length){
          return legendaries[Math.floor(Math.random() * legendaries.length)];
        }
        /* Last resort: any character with an id. Keeps the option card
           from rendering hostless even on a fresh hall with no
           legendaries yet. */
        var anyHost = allChars.filter(function(c){ return c && c.id; });
        if(anyHost.length){
          return anyHost[Math.floor(Math.random() * anyHost.length)];
        }
        return null;
      } catch(_){ return null; }
    }
    return [
      { theme: first,  label: destLabel(first),  flavor: shortenFlavor(first.sub),  host: sampleHost(first) },
      { theme: second, label: destLabel(second), flavor: shortenFlavor(second.sub), host: sampleHost(second) }
    ];
  }
  /* R96: Filter helper — checks if a char matches a themed phase's
     filter spec (race / class / pred). Mirrors the logic used by
     themePoolSize so the host pick reflects the theme accurately.
     R136-orc: filter.race / filter.cls can be either a string OR an
     array of strings. The original equality check missed the array
     case, which meant the Orc Encampment (race: ['Orc','Half-Orc'])
     and Among the Elves (race: ['Elf','Half-Elf','Drow']) themes
     produced a null sampleHost on the Crossroads Choice event — the
     user got an option card with no host portrait. Now we handle
     both shapes. */
  function _matchesThemeFilter(c, filter){
    if(!c || !filter) return false;
    if(filter.race){
      if(Array.isArray(filter.race)){
        if(filter.race.indexOf(c.race) !== -1) return true;
      } else if(c.race === filter.race){
        return true;
      }
    }
    if(filter.cls){
      if(Array.isArray(filter.cls)){
        if(filter.cls.indexOf(c.character_class) !== -1) return true;
      } else if(c.character_class === filter.cls){
        return true;
      }
    }
    if(filter.pred === 'ff' && c.is_ff_portrait) return true;
    return false;
  }
  function shortenFlavor(s){
    if(!s) return '';
    var idx = s.indexOf('.');
    if(idx > 0 && idx < 80) return s.slice(0, idx + 1);
    return s.length > 90 ? s.slice(0, 87) + '...' : s;
  }

  /* Roll for an event on each user refresh. Returns an event config
     object or null. Auto-refresh callers pass {auto:true} to skip
     special events. Eligibility checks happen inside so a thieves
     coin flip doesn't trigger when zero rogues are submittable.

     Probability budget tuned so the user encounters something special
     roughly half the time — refreshing should feel surprising. The
     order matters: rarer events checked first so they aren't crowded
     out by common ones. Each branch has its own eligibility gate, and
     when a branch fails its gate the budget effectively rolls forward
     to the next. */
  /* Two-tier spacing strategy.
     The Hall now has two distinct event families:
       1. Rare specials — legendary/crossroads/quiz/portrait — feel
          like a moment, should land roughly every 12-18 refreshes.
       2. Gambling events — dice trader, coin streak, battle —
          feel like everyday encounters, should land every 4-6
          refreshes so the world feels alive.
     Plus a hard MIN_ABSOLUTE_GAP of 2 normal refreshes between ANY
     two events of either family so the rail never feels like a
     casino. Both counters tick on every refresh (auto or user);
     only user refreshes are eligible to actually fire an event. */
  /* R136-A: Tightened from R54's 3-5/4-6 to 2-3/2-4. User reported
     that base "Wandering Heroes" / themed-phase rolls were dominating
     the rail. With the auto-refresh slower (R136-A AUTO_REFRESH_BASE_MS
     raised from 20s to ~29s, see the constant near line 18514) AND the
     special/gamble gaps tighter, every
     refresh cycle has a much higher chance of landing on a real event
     (Wheel, Bazaar, Five-Card, Bar Fight, Duel, etc.) instead of
     another themed phase. */
  /* R421 (Jan): "amp up the amount of events". The gamble family
     tightens (min 1, max 3) and the absolute floor drops to 1, so the rail
     lands on a real event roughly every other refresh instead of every
     third. Specials keep their rarity — they are the seasoning. */
  var SPECIAL_MIN_GAP = 2;
  var SPECIAL_MAX_GAP = 3;
  var GAMBLE_MIN_GAP  = 1;
  var GAMBLE_MAX_GAP  = 3;
  /* R136-revert: Reverted to MIN_ABSOLUTE_GAP=2 because the previous
     attempt (set to 0 with a 10-roll baseline forcing) made special
     events fire constantly. The user wants themed BASELINE rolls
     (Sorcerers' Salon, Goblin Market, Tavern of Bards, etc. — the
     rotating eyebrows on the Fresh Faces rail) to be the bulk variety
     and special events (Legendary Shop, Toll Road, Wheel, Bazaar,
     Crossroads, gambling minigames) to be RARE. The 2-roll floor keeps
     events from stacking back-to-back. */
  var MIN_ABSOLUTE_GAP = 1;
  /* Start counters high so the very first refresh is eligible for an
     event (the gambling family rolls first since it's the more
     frequent of the two). */
  var refreshesSinceLastEvent  = 99; /* any-event tracker */
  var refreshesSinceLastSpecial = 99;
  var refreshesSinceLastGamble  = 99;
  /* R136-event-frequency: per-tier MAX-gap counters. Replaces the old
     RARE_EVENT_MIN_GAP rejection (which blocked events) with a force-
     fire system that GUARANTEES events at the user-specified cadence.
     - Unlimited: ≥1 every 5 rolls (gambling minigames, stream events)
     - Special:   ≥1 every 20 rolls (Bazaar, Crossroads, Hero's Circle,
                   Marrow's End, secondChances, banished, Legendary Shop)
     Initialized high so the first qualifying refresh of a session can
     fire an event without waiting for the counter to ramp up. */
  var refreshesSinceLastUnlimited = 99;
  var refreshesSinceLastSpecialEvent = 99;
  /* R136-fix-2: dedicated counter for the GAMBLING-POOL action events
     specifically (brawls, duels, trivia, dice/coin/wheel/cards, travel
     events). After the R136-fix-3 recategorization, many UNLIMITED
     events live OUTSIDE the gambling pool (Crossroads, Hero's Circle,
     Marrow's End, secondChances, banished, mageBazaar) — those would
     reset refreshesSinceLastUnlimited every time they fired and starve
     out the action games the user actually wants. This counter only
     resets when a gambling-pool event fires, so brawls/fights/trivia
     are guaranteed at least once every GAMBLING_POOL_FORCE_GAP rolls.
     Initialized high so the first eligible refresh can fire one. */
  var refreshesSinceLastGamblingPool = 99;
  /* R150: dedicated Type 2 (Event bucket) drought counter. Increments
     every refresh, resets to 0 inside _runUniformDispatcher whenever an
     Event-bucket roll resolves — regardless of which specific gambling
     event resolved, so it cannot run away the way
     refreshesSinceLastGamblingPool can when a non-GAMBLING_POOL_TYPES
     variant (e.g. a themed Bustling Streets) resolves. Read by the
     EVENT_FORCE_GAP check in the dispatcher. */
  var refreshesSinceLastType2 = 99;
  /* R41: dedicated tracker for INTERACTIVE events — coin flips, dice
     rolls, guessing/picking. The picker forces one of these by roll
     #10 since the last so users always have an action beat in their
     near future. Non-interactive specials (rarityTribe, familiar,
     ffPortrait, etc.) don\'t reset this counter. */
  var refreshesSinceLastInteractive = 99;
  /* R421: an action beat is guaranteed every SIX rolls, not ten */
  var INTERACTIVE_FORCE_GAP = 6;
  var INTERACTIVE_TYPES = {
    thievesCoin: 1,
    classQuiz: 1,
    diceTrader: 1,
    championDuel: 1,
    tavernBrawl: 1,
    tavernBrawl4: 1,
    miniSpinner: 1,
    spinWheel: 1,
    pickACard: 1,
    /* R55+R57: guess-style minigames count as interactive — they keep
       the every-10-rolls action beat satisfied so the user isn\'t
       waiting on a duel/brawl when there\'s a culprit/guess sitting
       in the pool. */
    guessFB: 1,
    guessWW: 1,
    guessRR: 1,
    findCulprit: 1,
    findCulpritRace: 1,
    coinBet: 1,
    popularity: 1,
    /* R86: New duel variants. Wired identically to championDuel —
       interactive (count toward 10-roll guarantee), respect anti-repeat,
       fire from the gambling pool.
       R93: Added rare + mythic duels for a complete tier ladder. */
    rareDuel: 1,
    epicDuel: 1,
    legendaryDuel: 1,
    mythicDuel: 1,
    /* R86: New unlimited events — Toll Road, Carnival Strength Test,
       Common Thread. */
    tollRoad: 1,
    carnival: 1,
    commonThread: 1,
    /* R87: Elf or Half-Elf race binary. */
    guessEHE: 1,
    /* R97: Traveling Healer + Bard — free pick-a-card all-good. */
    travelHealer: 1,
    travelBard: 1,
    /* R136-fix-2d: missing entries — these were silently NOT counting
       toward the every-10-roll interactive guarantee, which let dry
       spells stretch longer than intended. */
    findImposter: 1,
    familiarCrowd: 1,
    bustlingStreets: 1,
    barFight: 1,
    /* R150: picking a side in the Class War counts as an action beat. */
    classWar: 1
  };
  function isInteractiveEvent(ev){
    return !!(ev && ev.type && INTERACTIVE_TYPES[ev.type]);
  }
  /* R136-event-frequency: Two-tier event categorization.
     - UNLIMITED events (gambling minigames + stream events) should
       fire AT LEAST once every 5 rolls. They keep the rail feeling
       alive without overwhelming.
     - SPECIAL events (Mage's Bazaar, Crossroads, Hero's Circle,
       Marrow's End, Legendary Shop, etc.) should fire AT LEAST once
       every 20 rolls. They're the "moments" that make the user
       remember a session.
     Themed Fresh Faces (Sorcerers' Salon, Goblin Market, Moonless
     Court, etc.) are NOT in either set — they fill the gaps between
     events as the rail's bulk variety. Showcase types (legendary,
     epic, spectrum, championMeeting, twoLegends, rarityTribe,
     themedPhase, familiar, ffPortrait) also fall through naturally
     and aren't gated by the force counters. */
  /* R136-recategorize: SPECIAL is now just the three cinematic events
     the user explicitly named — Legendary Shop, Spin the Wheel, Five
     Card Fortune. Everything else (Bazaar, Crossroads, Hero's Circle,
     Marrow's End, secondChances, banished, all gambling minigames,
     stream events) is UNLIMITED. The three SPECIAL events still have
     their own per-event cooldowns (legshop weekly, etc.), but the
     pool of unlimited events is now wide enough that ≥1 every 5 rolls
     produces real variety: brawls, duels, trivia, Bazaar, Crossroads,
     etc. all rotate. */
  var UNLIMITED_EVENT_TYPES = {
    /* Gambling pool minigames. */
    thievesCoin: 1, classQuiz: 1, diceTrader: 1,
    pickACard: 1, miniSpinner: 1,
    championDuel: 1, rareDuel: 1, epicDuel: 1, legendaryDuel: 1, mythicDuel: 1,
    tavernBrawl: 1, tavernBrawl4: 1,
    guessFB: 1, guessWW: 1, guessRR: 1,
    findCulprit: 1, findCulpritRace: 1, findImposter: 1,
    coinBet: 1, popularity: 1,
    /* R148-Z23: The Echo — nostalgia event in Type 2 rotation. */
    echo: 1,
    /* R148-Z29: Pay the Guide — Type 2, independent cooldown. */
    payGuide: 1,
    tollRoad: 1, carnival: 1, commonThread: 1,
    guessEHE: 1, travelHealer: 1, travelBard: 1,
    barFight: 1, bustlingStreets: 1, familiarCrowd: 1,
    /* Stream events (press-and-hold mechanics). */
    underdog: 1, feedYourOwn: 1, richGetRicher: 1, promotion: 1,
    /* Previously-special events now firing on the unlimited cadence. */
    mageBazaar: 1, choice: 1, heroCircle: 1, marrowEnd: 1,
    secondChances: 1, banished: 1,
    /* R148-Z24: Thieves' + Sage's bazaars share the bazaar cadence. */
    thievesBazaar: 1, sageBazaar: 1
  };
  var SPECIAL_EVENT_TYPES = {
    /* R268: key was 'fiveCardFortune' but the event's real type is 'fiveCards',
       so isSpecialEvent({type:'fiveCards'}) was silently returning false and the
       five-card event was mis-paced. Renamed to match the actual type. */
    legendaryShop: 1, spinWheel: 1, fiveCards: 1
  };
  function isUnlimitedEvent(ev){
    return !!(ev && ev.type && UNLIMITED_EVENT_TYPES[ev.type]);
  }
  function isSpecialEvent(ev){
    return !!(ev && ev.type && SPECIAL_EVENT_TYPES[ev.type]);
  }
  /* Force-fire gaps. If the matching counter hits the gap, the picker
     is told mustForce=true so even idle/auto refreshes will surface
     the missing event family. */
  var UNLIMITED_FORCE_GAP = 5;
  var SPECIAL_EVENT_FORCE_GAP = 20;
  /* R136-fix-2: action-pool guarantee — at least one brawl/duel/trivia/
     dice/coin/wheel/card-pick event every 5 refreshes. Same cadence as
     the unlimited gap but scoped to the gambling pool only. */
  var GAMBLING_POOL_FORCE_GAP = 5;
  /* R136-fix-2: events that are dispatched by pickGamblingEventInner.
     Mirrors the entries[] array in that function — kept as a separate
     set so isGamblingPoolEvent() is O(1) at the picker hot path. Used
     to track the dedicated action-pool force counter so the rare-
     special tier (Crossroads, Hero's Circle, etc.) can no longer
     "satisfy" the brawl/fight/trivia guarantee by being unlimited. */
  var GAMBLING_POOL_TYPES = {
    diceTrader:1, championDuel:1, rareDuel:1, epicDuel:1, legendaryDuel:1, mythicDuel:1, crownMatch:1,
    tavernBrawl:1, tavernBrawl4:1, barFight:1, spinWheel:1, pickACard:1,
    tollRoad:1, carnival:1, commonThread:1, travelHealer:1, travelBard:1,
    popularity:1, guessFB:1, guessWW:1, guessRR:1, guessEHE:1,
    findCulprit:1, findCulpritRace:1, findImposter:1, familiarCrowd:1,
    bustlingStreets:1, coinBet:1, classQuiz:1,
    miniSpinner:1,
    /* R150: The Class War rolls from the gambling pool. */
    classWar:1
  };
  function isGamblingPoolEvent(ev){
    return !!(ev && ev.type && GAMBLING_POOL_TYPES[ev.type]);
  }
  /* R136-fix-2b: Subtype classification for gambling-pool events. Each
     pool entry maps to one of:
       trivia  — guessing games (free, low-stakes mind games)
       brawl   — pick-a-fighter dice events (3-5 fighters, d20)
       duel    — 1v1 dice events (rare/epic/legendary/mythic tiers)
       dice    — gambling games (dice trader, coin bet, mini spinner)
       wheel   — spin/pick mechanics (spinWheel, pickACard)
       travel  — scene events (toll, carnival, common thread, healers)
       scene   — character-scene events (familiarCrowd, bustlingStreets)
     The user wants VARIETY across subtypes, not just the same trivia
     event repeating. We track recent subtypes and bias the picker away
     from the last 2 subtypes fired. Without this, a 32-entry pure
     shuffle will frequently land on the same subtype back-to-back
     (e.g., guessFB then guessWW are both 'trivia' — feels like the
     same beat). */
  /* R140-D: Trivia + Tavern Brawl events were perceived as "rarer than
     they should be" because all 13 trivia entries shared a single
     'trivia' subtype and all 3 brawl entries shared 'brawl' — so after
     one trivia fired, the recent-subtype buffer locked out all other
     trivia for ~3 events, even though trivia/brawl variants are
     mechanically different (binary-class guess vs find-culprit vs
     popularity-rank). Splitting trivia into 3 sub-buckets and brawl
     into 3 distinct keys lets these events fire back-to-back across
     the buckets, naturally bumping their session frequency without
     touching the picker logic. The user explicitly asked for these
     to be more common. */
  var GAMBLING_POOL_SUBTYPE = {
    /* trivia — split into 3 buckets so a class-guess doesn't block a
       find-culprit or a meta-question. */
    classQuiz:'trivia-quiz', popularity:'trivia-meta',
    guessFB:'trivia-quiz', guessWW:'trivia-quiz', guessRR:'trivia-quiz', guessEHE:'trivia-quiz',
    findCulprit:'trivia-find', findCulpritRace:'trivia-find', findImposter:'trivia-find',
    familiarCrowd:'trivia-meta',
    /* R148-Z23: The Echo — its own subtype so it doesn't fight other
       meta-trivia for the bucket slot. */
    echo:'trivia-echo',
    /* brawl — each variant is its own subtype so the user can see
       tavernBrawl4 immediately after tavernBrawl3 without the buffer
       blocking. The 3 entries cycle naturally. */
    tavernBrawl:'brawl-3', tavernBrawl4:'brawl-4', barFight:'brawl-bar',
    /* duel */
    championDuel:'duel', rareDuel:'duel', epicDuel:'duel',
    legendaryDuel:'duel', mythicDuel:'duel', crownMatch:'duel',
    /* dice */
    diceTrader:'dice', coinBet:'dice', miniSpinner:'dice',
    /* wheel / cards */
    spinWheel:'wheel', pickACard:'wheel',
    /* travel / scene */
    tollRoad:'travel', carnival:'travel', commonThread:'travel',
    travelHealer:'travel', travelBard:'travel',
    bustlingStreets:'scene',
    /* R140-D: Themed bustling streets variants (uncommon/rare/portrait)
       share the 'scene' subtype with the base event so the picker
       doesn't fire 4 scrolling-rail events in a row. They're added to
       the gambling pool below. */
    bustlingStreetsUncommon:'scene',
    bustlingStreetsRare:'scene',
    bustlingStreetsPortrait:'scene',
    /* R140-Z: 4 new themed variants with class/race filters. Same
       'scene' subtype so they share the 6-event family throttle. */
    bustlingStreetsCasters:'scene',
    bustlingStreetsBeastfolk:'scene',
    bustlingStreetsClergy:'scene',
    bustlingStreetsDragonborn:'scene',
    /* R148-Z24: All 3 bazaars share the 'bazaar' subtype so back-to-
       back bazaars (even of different flavors) are blocked by the
       subtype anti-repeat. */
    mageBazaar:'bazaar', thievesBazaar:'bazaar', sageBazaar:'bazaar',
    /* R148-Z29: Pay the Guide gets its own subtype since it's a
       distinct narrative beat from bazaars themselves. */
    payGuide:'guide',
    /* R150: The Class War — its own subtype so it never collides with
       another gambling subtype in the recent buffer. */
    classWar:'war'
  };
  /* Recent subtype ring buffer. Max 3 deep — long enough that a
     guessing event won't follow another guessing event within 2 fires,
     short enough that subtypes can naturally cycle. */
  var recentGamblingSubtypes = [];
  var GAMBLING_SUBTYPE_RECENT_DEPTH = 3;
  function rememberGamblingSubtype(key){
    var st = GAMBLING_POOL_SUBTYPE[key];
    if(!st) return;
    recentGamblingSubtypes.push(st);
    if(recentGamblingSubtypes.length > GAMBLING_SUBTYPE_RECENT_DEPTH){
      recentGamblingSubtypes = recentGamblingSubtypes.slice(-GAMBLING_SUBTYPE_RECENT_DEPTH);
    }
  }
  function isRecentSubtype(key){
    var st = GAMBLING_POOL_SUBTYPE[key];
    if(!st) return false;
    return recentGamblingSubtypes.indexOf(st) !== -1;
  }
  /* Back-compat: keep isRareEvent as a union of both sets so any
     remaining callers don't break, but this is no longer the gating
     mechanism — see the force-fire counters in pickFreshFacesEvent. */
  function isRareEvent(ev){
    return isUnlimitedEvent(ev) || isSpecialEvent(ev);
  }
  var nextSpecialGap = SPECIAL_MIN_GAP;
  var nextGambleGap  = GAMBLE_MIN_GAP;
  function rollNextSpecialGap(){
    nextSpecialGap = SPECIAL_MIN_GAP + Math.floor(Math.random() * (SPECIAL_MAX_GAP - SPECIAL_MIN_GAP + 1));
  }
  function rollNextGambleGap(){
    nextGambleGap = GAMBLE_MIN_GAP + Math.floor(Math.random() * (GAMBLE_MAX_GAP - GAMBLE_MIN_GAP + 1));
  }
  /* Back-compat shim — the legacy var name is read by event-history
     debug code. Keep it as an alias for the new specials counter. */
  var nextEventGap = nextSpecialGap;

  /* The recent-keys buffer still exists as a secondary filter so a
     specific event type doesn't repeat too soon even after the gap
     resets — e.g., we just had Crossroads 6 refreshes ago, the
     gap clears, but we'd still rather pick a different event next.
     The buffer is much shorter (last 2 events). */
  /* R55: bumped 3 → 4 per user spec — same event family can\'t fire
     within 4 consecutive events, so a brawl is followed by 4 non-
     brawl events before brawl is eligible again.
     Anti-repeat fires when an event is PICKED (returned to the user),
     not when it\'s completed. So skipping/refreshing/idling out an
     event still counts toward the buffer — it can\'t reappear for 4
     more events.
     EXCEPTION: daily-capped events (DAILY_CAPPED_KEYS below) are NOT
     added to the buffer. They keep showing on every refresh until the
     user actually completes them and the daily flag locks them out. */
  /* R136-event-frequency: bumped 4 → 6 so unlimited events fire at the
     5-roll cadence WITHOUT immediately repeating themselves. With the
     buffer at 4, an event firing every ~5 rolls could re-pick itself
     after just 4 refreshes. At 6, the buffer outlasts the typical
     unlimited fire window and forces the picker into other types. */
  var RECENT_BUFFER_SIZE = 6;
  /* Events that should ignore the recent-buffer rule because they\'re
     gated by a once-per-day completion flag. They keep showing up
     (subject to natural cadence) until the user resolves them. */
  var DAILY_CAPPED_KEYS = {
    /* R119-I: All daily caps DROPPED. The user prefers rare events to
       reappear occasionally rather than vanish for 24h after a single
       fire. Each formerly-capped event now relies on:
         (a) the same low rare-special threshold (small per-roll
             probability) it always had
         (b) the 4-event anti-repeat buffer to prevent back-to-back
       Empty object kept so callers that probe `DAILY_CAPPED_KEYS[key]`
       still get a falsy answer — including the recent-buffer
       exemption logic in rememberEvent(), which now adds these keys
       to the buffer like every other event. */
    diceTrader: 0 /* unlimited (R53) — listed for clarity, value 0 = NOT capped */
  };
  var recentEventKeys = [];
  function isRecentEvent(key){
    return key && recentEventKeys.indexOf(key) !== -1;
  }
  /* R86: Family-level anti-repeat. Returns true if ANY event in the
     same family appears in the recent buffer. Currently used for the
     3 duel events (championDuel/epicDuel/legendaryDuel) so we never
     fire two back-to-back even though they have different keys. */
  var EVENT_FAMILIES = {
    /* R93: Family expanded — rare + mythic now part of the duel
       family so we never fire two duel-type events back-to-back.
       R148-Z37: mythicDuelSpecial joins the duel family too. */
    duel: ['championDuel', 'rareDuel', 'epicDuel', 'legendaryDuel', 'mythicDuel', 'crownMatch', 'mythicDuelSpecial'],
    /* R119-H: Bar Fight grouped with Tavern Brawls so we never fire
       two pick-a-fighter dice events back-to-back. */
    brawl: ['tavernBrawl', 'tavernBrawl4', 'barFight'],
    /* R120-D / R124-I: All "spot the odd one out" events grouped so
       we never fire two guess-the-imposter events in a row. */
    culprit: ['findCulprit', 'findCulpritRace', 'findImposter', 'familiarCrowd'],
    /* R140-I: All Bustling Streets variants (base + uncommon + rare +
       FF-portrait) grouped under a single family. Without this rule
       the picker would fire two scrolling-rail events back-to-back
       because each variant has a unique _eventKey but they LOOK the
       same to the user (same rail, same mechanic, same 5-heart spend).
       The 'scene' subtype rotation gates pass-0 only; pass-1 ignores
       subtype and would happily land on a sibling variant. With this
       family, isRecentFamily blocks any variant for the full 6-event
       recent-buffer window after one fires. Fixes the "Fresh Faces
       keeps refreshing into itself" report — the user was hitting 5
       hearts on streets, the refresh would land on another streets
       variant immediately, the wallet drained, and they got stuck on
       streets-only events while the auto-timer paused at bank=0. */
    /* R148-Z22w: COMPLETED family list. Previous list was missing 10
       newer variants added in R140-Z3n Phase 3 (DragonHoard, WildHunt,
       Charlatans, FurredFolk, FeyCourt) and the 5 procession variants
       (KnightProcession, Bardstorm, DruidMigration, PactWalk,
       TemplarMarch). Those gaps let the picker fire e.g.
       bustlingStreetsCharlatans right after bustlingStreets — the
       user saw two streets-style rolls back to back because
       familyOf('bustlingStreetsCharlatans') returned null and the
       family-anti-repeat rule was bypassed. All 18 variants now
       sit in the family so isRecentFamily catches any consecutive
       streets-pattern fire. */
    streets: [
      'bustlingStreets', 'bustlingStreetsUncommon', 'bustlingStreetsRare', 'bustlingStreetsPortrait',
      /* R140-Z: 4 themed variants. */
      'bustlingStreetsCasters', 'bustlingStreetsBeastfolk', 'bustlingStreetsClergy', 'bustlingStreetsDragonborn',
      /* R148-Z15: dragon + hunt + 3 social procession variants. */
      'bustlingStreetsDragonHoard', 'bustlingStreetsWildHunt',
      'bustlingStreetsCharlatans', 'bustlingStreetsFurredFolk', 'bustlingStreetsFeyCourt',
      /* R140-Z3n Phase 3: 5 ceremonial processions. */
      'bustlingStreetsKnightProcession', 'bustlingStreetsBardstorm',
      'bustlingStreetsDruidMigration', 'bustlingStreetsPactWalk',
      'bustlingStreetsTemplarMarch'
    ],
    /* R140-I: Healer + Bard share a family too. Both are free pick-a-
       card kindness events with the same shape (3 face-down cards,
       all positive). The 'travel' subtype groups them with toll/
       carnival/commonThread which is too coarse — getting a Bard
       right after a Healer reads as the same event repeated. The
       45% probabilistic skip on each picker AND this family rule
       together produce real variety. */
    kindness: ['travelHealer', 'travelBard'],
    /* R148-Z24: All 3 bazaars share a family so that even if the shared
       'mb' cooldown reset somehow allowed two in close succession, the
       6-event recent-buffer family rule still blocks them. The shared
       cooldown is the primary gate (25 rolls); the family rule is the
       belt-and-suspenders backup. */
    bazaar: ['mageBazaar', 'thievesBazaar', 'sageBazaar']
  };
  function familyOf(key){
    for(var fam in EVENT_FAMILIES){
      if(EVENT_FAMILIES[fam].indexOf(key) !== -1) return fam;
    }
    return null;
  }
  function isRecentFamily(key){
    var fam = familyOf(key);
    if(!fam) return false;
    var members = EVENT_FAMILIES[fam];
    for(var i = 0; i < members.length; i++){
      if(isRecentEvent(members[i])) return true;
    }
    return false;
  }
  function rememberEvent(key){
    if(!key) return;
    /* R55: daily-capped events skip the buffer entirely. They\'re
       allowed to keep reappearing on every refresh until the user
       completes them (which sets their daily-cap flag, after which
       the picker self-gates). Without this exemption the buffer
       would hide a once-per-day event for 4 refreshes after the
       first time it shows, even if the user just skipped it. */
    if(DAILY_CAPPED_KEYS[key]) return;
    recentEventKeys.push(key);
    if(recentEventKeys.length > RECENT_BUFFER_SIZE){
      recentEventKeys = recentEventKeys.slice(-RECENT_BUFFER_SIZE);
    }
  }

  /* ================================================================ */
  /* Day-state persistence (R38)                                       */
  /* ================================================================ */
  /* Tracks per-day refresh count + whether the first-of-day event has
     already fired. Persisted to localStorage keyed by ymd so the
     guarantees survive page reloads. Two guarantees this state powers:
       1. By refresh #10 of the day, an event must have fired.
       2. At least one event every 15 refreshes (any time of day). */
  /* R140-Z: Memoized so the picker chain + buff queue don't recompute the
     YMD string every roll. Refreshes every 30s so a session that crosses
     midnight self-heals. */
  var _ymdNowCache = '';
  var _ymdNowCacheTs = 0;
  function _ymdNow(){
    var now = Date.now();
    if(_ymdNowCache && (now - _ymdNowCacheTs) < 30000) return _ymdNowCache;
    var d = new Date();
    _ymdNowCache = d.getFullYear() + '-' + String(d.getMonth()+1).padStart(2,'0') + '-' + String(d.getDate()).padStart(2,'0');
    _ymdNowCacheTs = now;
    return _ymdNowCache;
  }
  function _dayStateKey(){ return 'hoh-day-state-' + _ymdNow(); }
  function getDayState(){
    try {
      var raw = localStorage.getItem(_dayStateKey()) || '{}';
      var s = JSON.parse(raw);
      return {
        rollCount: s.r || 0,
        eventFired: !!s.e,
        /* R102-F: gambleSeen tracks which gambling-pool keys have
           fired this session for the two-pass dedupe scan. Was
           accidentally dropped during R116-A's setDayState refactor;
           re-added so the gambling pool's broad-coverage guarantee
           works again. */
        gambleSeen: (s.gs && typeof s.gs === 'object') ? s.gs : {}
      };
    } catch(_){ return { rollCount: 0, eventFired: false, gambleSeen: {} }; }
  }
  function setDayState(state){
    try {
      localStorage.setItem(_dayStateKey(), JSON.stringify({
        r: state.rollCount || 0,
        e: !!state.eventFired,
        gs: (state.gambleSeen && typeof state.gambleSeen === 'object') ? state.gambleSeen : {}
      }));
    } catch(_){}
  }
  /* R116-A: Cooldown roll-fields moved OUT of dayState into their own
     localStorage keys. The original design (R113-C/R115-B) stored them
     inside dayState, but pickFreshFacesEvent loads dayState BEFORE the
     picker chain runs, then calls setDayState(dayState) at the end —
     overwriting any cooldown marks the picker chain wrote during its
     run. The result was silent: marks were saved fresh by markFfShowcase
     /markStreamEvent then immediately clobbered. The cooldowns never
     engaged and stream/showcase events spammed every few rolls.
     Separate keys decouple the two completely. dayState.rollCount is
     still the comparison reference (it's the source of truth for "what
     roll # are we on"), but the cooldown marks live independently. */
  function _cooldownKey(suffix){ return 'hoh-fresh-cd-' + suffix + '-' + _ymdNow(); }
  function _readCooldownRoll(suffix){
    try {
      var raw = localStorage.getItem(_cooldownKey(suffix));
      var n = parseInt(raw || '', 10);
      return isNaN(n) ? -999 : n;
    } catch(_){ return -999; }
  }
  function _writeCooldownRoll(suffix, value){
    try { localStorage.setItem(_cooldownKey(suffix), String(value)); } catch(_){}
  }
  /* R113-C: 10-roll buffer between Captured by Jan + Portrait Gallery. */
  var FF_SHOWCASE_BUFFER = 10;
  function ffShowcaseGated(){
    var rc = getDayState().rollCount;
    return (rc - _readCooldownRoll('ff')) < FF_SHOWCASE_BUFFER;
  }
  function markFfShowcaseFired(){
    _writeCooldownRoll('ff', getDayState().rollCount);
  }
  /* R115-B: Stream-event family cooldown (Underdog/RichGetRicher/FeedYourOwn).
     R124-E: Bumped 10 → 20 rolls. Stream events were firing too often
     and crowding out other event types. Doubling the cooldown halves
     the rate without removing them entirely.
     R136-fix-2c: Bumped 20 → 30. Even with 20-roll family cooldown the
     same key (almost always Underdog, since Feed Your Own needs a
     signed-in user with own heroes) would re-fire on its first eligible
     roll and the user perceived spam. 30 rolls = roughly 2 per long
     session, which matches the "rare beat" intent. */
  var STREAM_EVENT_BUFFER = 30;
  function streamEventOnCooldown(){
    var rc = getDayState().rollCount;
    return (rc - _readCooldownRoll('se')) < STREAM_EVENT_BUFFER;
  }
  function markStreamEventFired(){
    _writeCooldownRoll('se', getDayState().rollCount);
  }
  /* R136-fix-2c: Per-stream-key cooldown. The family cooldown above
     gates the WHOLE stream branch, but a different stream key can fire
     immediately after the family cooldown clears. Add per-key tracking
     so e.g. Underdog can't fire two times in 25 rolls even if the
     family cooldown was shorter. The two cooldowns layer:
       - family `se`: at MOST one stream event per 30 rolls
       - per-key `se-{key}`: at MOST one of THIS specific stream per 25 rolls
     For Underdog (the most common stream the user complained about),
     this means a real ~30-roll gap between Underdog fires. */
  var STREAM_PER_KEY_BUFFER = 25;
  function streamKeyOnCooldown(key){
    if(!key) return false;
    var rc = getDayState().rollCount;
    return (rc - _readCooldownRoll('se-' + key)) < STREAM_PER_KEY_BUFFER;
  }
  function markStreamKeyFired(key){
    if(!key) return;
    _writeCooldownRoll('se-' + key, getDayState().rollCount);
  }
  /* R140-D: Hard daily fire counters for Underdog + Feed Your Own.
     Distinct from the roll-based cooldown above (which only governs
     spacing). The user wants a real upper bound: each event can fire
     at most 2 times per day total. After 2 fires, the picker skips
     that key entirely until the next day's `_ymdNow()` rolls over.
     Counter lives in localStorage under hoh-stream-fires-{key}-{ymd}. */
  var STREAM_DAILY_FIRE_CAP = 2;
  /* R793 (Jan, 2026-09-22): "The own-heroes heart event (up to 20 today)
     becomes 10." Feed Your Own fires at most ONCE a day and pours at most
     10 hearts, so it is 10 a day in total. Underdog keeps its two fires. */
  var STREAM_DAILY_FIRE_CAP_BY_KEY = { feedYourOwn: 1 };
  /* R140-E: Per-event heart caps for the press-and-hold streams. The
     user spammed the portrait and saw lingering "Celebrated enough
     today" toasts long after the event was supposed to end — the
     1280ms fade-out window let them re-grab the portrait, restart the
     interval, and trigger the per-character 125-cap toast over and
     over. Lower caps (50 / 30) plus an explicit `eventEnded` lock on
     `currentSpecial` close the door the instant the cap hits. */
  /* R140-Z3j: Caps lowered. Feed Your Own 50→30 keeps the event a
     focused emotional beat instead of a long grind. Underdog 30→10
     trims it to a quick "lift this hero off the floor" moment that
     feels generous without dominating the user's daily heart budget. */
  var STREAM_EVENT_HEART_CAP = {
    feedYourOwn: 10,
    underdog: 10
  };

  /* ================================================================ */
  /* R140-Z3j: Orchestrated 22-roll tour for anonymous first-day      */
  /* visitors. Detailed comments above the call site in               */
  /* pickFreshFacesEvent. This block defines the sequence + the       */
  /* state helpers + the consumer.                                    */
  /* ================================================================ */
  /* Map step kinds to picker functions or themed-phase eyebrows. */
  /* R140-Z3l: Sequence locked to the user's exact spec. Each entry maps
     to a real event picker (verified in code) OR a real THEMED_PHASES
     eyebrow. The 'rarityTribe' kind exists specifically for Travelers'
     Camp (which is a 3-row uncommon-tier event in the inner picker
     chain, not a themed phase). */
  var ANON_TOUR_SEQUENCE = [
    /*  1 */ { kind:'rarityTribe', tier:'uncommon', eyebrow:"The Travelers' Camp", sub:'A gathering of names with stories worth a second telling.' },
    /*  2 */ { kind:'themed', eyebrow:'The Moonless Court' },
    /*  3 */ { kind:'event',  picker:'tavernBrawl4' },
    /*  4 */ { kind:'themed', eyebrow:"The Hunters' Lodge" },
    /*  5 */ { kind:'event',  picker:'tollRoad' },  /* R144-D: was travelBard */
    /*  6 */ { kind:'themed', eyebrow:'The Tiefling Court' },
    /*  7 */ { kind:'themed', eyebrow:'The Underdark Embassy' },
    /*  8 */ { kind:'event',  picker:'pickACard' },
    /*  9 */ { kind:'themed', eyebrow:'The Pact-Bound' },
    /* 10 */ { kind:'event',  picker:'bustlingStreetsPortrait' },
    /* 11 */ { kind:'event',  picker:'findImposter' },
    /* 12 */ { kind:'themed', eyebrow:'The Twilight Walkers' },
    /* 13 */ { kind:'event',  picker:'spectrum' },
    /* 14 */ { kind:'event',  picker:'championDuel' },
    /* 15 */ { kind:'themed', eyebrow:'The Underdark Embassy' },
    /* 16 */ { kind:'themed', eyebrow:"The Paladins' Order" },
    /* 17 */ { kind:'event',  picker:'championDuel' },
    /* 18 */ { kind:'themed', eyebrow:"The Cleric's Vigil" },
    /* 19 */ { kind:'event',  picker:'diceTrader' },
    /* 20 */ { kind:'themed', eyebrow:'Among the Elves' },
    /* 21 */ { kind:'themed', eyebrow:'The Portrait Gallery' }
  ];
  function _tourStateKey(){ return 'hoh-anon-tour-' + _ymdNow(); }
  function _tourReadState(){
    try {
      var raw = localStorage.getItem(_tourStateKey());
      if(!raw) return null;
      return JSON.parse(raw);
    } catch(_){ return null; }
  }
  function _tourWriteState(s){
    try {
      if(!s) localStorage.removeItem(_tourStateKey());
      else localStorage.setItem(_tourStateKey(), JSON.stringify(s));
    } catch(_){}
  }
  function _tourIsSignedIn(){
    try {
      if(typeof window.hohYourHallEmail === 'function' && window.hohYourHallEmail()) return true;
      if(typeof window.hohYourHallIsSignedIn === 'function' && window.hohYourHallIsSignedIn()) return true;
    } catch(_){}
    return false;
  }
  function _tourActiveForUser(){
    /* Signed-in users never get the tour. */
    if(_tourIsSignedIn()) return false;
    var state = _tourReadState();
    /* State present + active flag true → continue tour. */
    if(state && state.active === true) return true;
    /* State present + active flag false → tour already finished today.
       Don't re-activate even if user signs out. */
    if(state && state.active === false) return false;
    /* No state yet: this is the user's FIRST refresh of the day as
       an anonymous user. Start the tour. */
    _tourWriteState({ active:true, idx:0, started: _ymdNow() });
    return true;
  }
  /* Picker function map keyed by `picker` field in the sequence. */
  function _tourPickerFor(name){
    var m = {
      tavernBrawl4: typeof pickTavernBrawl4Event === 'function' ? pickTavernBrawl4Event : null,
      travelBard:   typeof pickTravelingBardEvent === 'function' ? pickTravelingBardEvent : null,
      /* R144-D: tollRoad added so the new-player tour can route to it
         (step 5). Bard's Blessing was too generous for an early stop
         and skewed first impressions; Toll Road's dice duel is a more
         representative sample of the Hall's interaction rhythm. */
      tollRoad:     typeof pickTollRoadEvent === 'function' ? pickTollRoadEvent : null,
      pickACard:    typeof pickPickACardEvent === 'function' ? pickPickACardEvent : null,
      bustlingStreetsPortrait: typeof pickBustlingStreetsPortraitEvent === 'function' ? pickBustlingStreetsPortraitEvent : null,
      findImposter: typeof pickFindImposterEvent === 'function' ? pickFindImposterEvent : null,
      /* R140-Z3k: Spectrum picker is named `pickSpectrum` (not
         pickSpectrumEvent). Wire correctly. */
      spectrum:     typeof pickSpectrum === 'function' ? pickSpectrum : null,
      championDuel: typeof pickChampionDuelEvent === 'function' ? pickChampionDuelEvent : null,
      diceTrader:   typeof pickDiceTraderEvent === 'function' ? pickDiceTraderEvent : null
    };
    return m[name] || null;
  }
  function _tourFindThemeByEyebrow(eyebrow){
    if(!eyebrow || !Array.isArray(THEMED_PHASES)) return -1;
    for(var i=0;i<THEMED_PHASES.length;i++){
      if(THEMED_PHASES[i] && THEMED_PHASES[i].eyebrow === eyebrow) return i;
    }
    return -1;
  }
  /* R140-Z3m: Consumer called from pickFreshFacesEvent. Returns a status
     object so the caller can HARD-BAIL out of the normal picker chain
     whenever the tour is active, even when the current step produced
     no event (failed picker, missing theme, etc). Without this, a step
     miss would let the random/buff/forced-event flow inject an
     off-script roll into the user's curated first-22 sequence.

       { tourActive: boolean, event: object|null }

     - tourActive=true  → caller MUST NOT fall through. If event is
                          non-null, fire it. If null, render the current
                          themed/grid state and stop.
     - tourActive=false → caller proceeds with normal flow (tour done
                          or user is signed in).

     Auto-refreshes during the tour DO NOT consume a step. The 21-step
     sequence is anchored to MANUAL refreshes only, so timer-driven
     auto-refreshes pause the tour cleanly (return tourActive=true,
     event=null) instead of stealing rolls. */
  /* R140-Z3n: Build one tour step's outcome. Returns either an event
     object, a themed-skip sentinel, or null when the step couldn't be
     fulfilled (picker miss, missing theme, empty pool). Called inside
     a loop by consumeAnonTourStep so failed steps are skipped past in
     a single refresh — the user never lands back on the prior page
     with an exhausted budget. */
  function _buildTourStepOutcome(step){
    if(!step) return null;
    if(step.kind === 'event'){
      var picker = _tourPickerFor(step.picker);
      if(typeof picker !== 'function') return null;
      /* R140-Z3n: Several pickers have probability gates (e.g.
         pickTravelingBardEvent's 25% TRAVEL_KINDNESS_SKIP_RATE,
         spectrum's pool checks, championDuel's character requirements).
         A single call can miss for benign reasons even when the event
         is fundamentally available. We retry up to 8 times so the
         tour step lands reliably without us having to thread a
         bypass flag through every picker function. If all retries
         miss, the consumeAnonTourStep loop moves on to the next
         curated step. */
      var ev = null;
      for(var attempt = 0; attempt < 8; attempt++){
        try { ev = picker(); } catch(_){ ev = null; }
        if(ev) break;
      }
      if(!ev) return null;
      ev._eventKey = step.picker;
      ev._fromTour = true;
      /* Rig the user's FIRST 4-way brawl in the tour to always win.
         Step 3 of the curated sequence is the only tavernBrawl4 the
         tour fires. Gives new visitors a guaranteed payout on their
         first wager. */
      if(step.picker === 'tavernBrawl4'){
        ev._alwaysWin = true;
      }
      return ev;
    }
    if(step.kind === 'rarityTribe'){
      if(typeof poolByTier !== 'function') return null;
      var rtPool = poolByTier(step.tier || 'uncommon');
      if(!rtPool || rtPool.length < 6) return null;
      var rtShuf = hfShuffle(rtPool).slice(0, 15);
      return {
        type: 'rarityTribe',
        tier: step.tier || 'uncommon',
        chars: rtShuf,
        eyebrow: step.eyebrow,
        sub: step.sub,
        _eventKey: 'rarityTribe-' + (step.tier || 'uncommon'),
        _fromTour: true
      };
    }
    if(step.kind === 'themed'){
      var idx = _tourFindThemeByEyebrow(step.eyebrow);
      if(idx < 0 || typeof setPhase !== 'function') return null;
      currentThemedPhase = THEMED_PHASES[idx];
      try { rememberRecentTheme(THEMED_PHASES[idx].eyebrow); } catch(_){}
      /* Pick a non-zero phase (1) so renderInto pulls from
         pickListForPhase(currentThemedPhase) instead of freshList(). */
      setPhase(1);
      cachedWanderingPool = null;
      cachedWanderingPhase = null;
      renderedSnapshotIds = null;
      snapshotPhaseKey = null;
      /* Sentinel: caller bails out of event-picking but renders the
         themed grid we just set. */
      return { _tourThemedSkip:true };
    }
    return null;
  }

  function consumeAnonTourStep(dayState, isAuto){
    if(!_tourActiveForUser()) return { tourActive:false, event:null };

    /* R140-Z3n: isAuto retained for callers but no longer pauses
       the tour. Every refresh — manual, idle-timer, budget-exhausted,
       interactive-resolved — advances the tour. The browser-tab
       visibility hook already pauses the timer when the tab is
       hidden, so backgrounded tabs don't burn through the sequence. */

    /* R140-Z3n: LOOP through steps so a failed picker (e.g. Traveling
       Bard returns null due to an empty class pool, themed phase not
       found in THEMED_PHASES) doesn't leave the user stranded on the
       previous page. We keep advancing idx until we get a valid
       outcome or run out of steps. Cap by sequence length so we can't
       infinite loop. */
    var attempts = 0;
    var MAX = ANON_TOUR_SEQUENCE.length + 1;
    while(attempts < MAX){
      attempts++;
      var state = _tourReadState() || { active:true, idx:0 };
      if(state.idx >= ANON_TOUR_SEQUENCE.length){
        state.active = false;
        _tourWriteState(state);
        return { tourActive:false, event:null };
      }
      var step = ANON_TOUR_SEQUENCE[state.idx];
      /* Advance the index BEFORE building the outcome so a miss can't
         replay the same step. The new index persists immediately so
         repeated picker misses can't double-consume. */
      state.idx = state.idx + 1;
      if(state.idx >= ANON_TOUR_SEQUENCE.length){ state.active = false; }
      _tourWriteState(state);

      var outcome = _buildTourStepOutcome(step);
      if(outcome){
        return { tourActive:true, event:outcome };
      }
      /* Outcome null — step failed. Loop and try the next one so the
         caller still gets a fresh page on this refresh. */
    }
    /* Defensive: ran out of attempts without a hit. Exit the tour so
       normal flow takes over. */
    try {
      var s = _tourReadState() || { active:false };
      s.active = false;
      _tourWriteState(s);
    } catch(_){}
    return { tourActive:false, event:null };
  }
  /* End anon tour helpers. */
  function _streamFireCountKey(key){ return 'hoh-stream-fires-' + key + '-' + _ymdNow(); }
  function streamKeyFiredToday(key){
    if(!key) return 0;
    try {
      var raw = localStorage.getItem(_streamFireCountKey(key));
      var n = parseInt(raw || '0', 10);
      return isNaN(n) ? 0 : n;
    } catch(_){ return 0; }
  }
  function streamKeyAtDailyCap(key){
    var cap = (STREAM_DAILY_FIRE_CAP_BY_KEY[key] != null) ? STREAM_DAILY_FIRE_CAP_BY_KEY[key] : STREAM_DAILY_FIRE_CAP;
    return streamKeyFiredToday(key) >= cap;
  }
  function bumpStreamFireCount(key){
    if(!key) return;
    try {
      var n = streamKeyFiredToday(key) + 1;
      localStorage.setItem(_streamFireCountKey(key), String(n));
    } catch(_){}
  }
  /* R116-C: Mage's Bazaar (shop) cooldown.
     R124-F: Bumped 20 → 35 rolls. The bazaar was firing too often and
     felt less like a special encounter. 35 rolls = roughly once per
     long session, which matches the user's "rare encounter" intent. */
  /* R148-Z22u: Bazaar cadence tightened 35 → 25 rolls per user.
     "Bazaar should show up every ~25 rolls." The picker becomes
     eligible to fire Mage's Bazaar after 25 rolls have passed since
     the last bazaar — combined with the existing unlimited-event
     force every 5 rolls, the bazaar tends to surface within a few
     refreshes of the buffer expiring. */
  var MAGE_BAZAAR_BUFFER = 25;
  function mageBazaarOnCooldown(){
    var rc = getDayState().rollCount;
    return (rc - _readCooldownRoll('mb')) < MAGE_BAZAAR_BUFFER;
  }
  function markMageBazaarFired(){
    _writeCooldownRoll('mb', getDayState().rollCount);
  }
  /* R148-Z24: Bazaar registry — Mage / Thieves / Sage. All three share
     the 'mb' cooldown key so the bazaar slot fires at most every
     MAGE_BAZAAR_BUFFER rolls regardless of which variant rolls. Each
     bazaar has 4 items with their own price + resolve handler. The
     Mage bazaar items keep their original buff wiring (dice/coliseum/
     scholar/special). The new Thieves + Sage items hit placeholder
     resolves in Round 2 — Round 3 swaps those for interactive
     mini-game screens (coin-flip, mimic chests, dice rolls). */
  var BAZAAR_REGISTRY = {
    mage: {
      type: 'mageBazaar',
      title: "The Mage's Bazaar",
      intro: "A robed figure steps from the shadows. Four trinkets on a velvet cloth.",
      hostClasses: ['Wizard','Sorcerer','Warlock'],
      hostFallback: 'A wandering mage',
      cssMod: 'mage',
      items: [
        { key:'dice',     price:5,  title:'Magical Dice',           sub:'Roll a d20. The mage gives you the result in hearts.' },
        { key:'coliseum', price:10, title:'Coliseum Ticket',        sub:'Four brawls in a row. Mixed tier.' },
        { key:'scholar',  price:10, title:"Scholar's Lens",          sub:'The next five rolls are guessing games. No repeats.' },
        /* R148-Z32: 25 → 30. Guaranteed special is the strongest
           buff the bazaar sells; price reflects it. */
        { key:'special',  price:30, title:'Audience with the Hall',  sub:'Your next roll is guaranteed a special event.' }
      ]
    },
    thieves: {
      type: 'thievesBazaar',
      title: "The Thieves' Bazaar",
      intro: "A hooded figure slips out from an alley. Four trinkets on a chipped table.",
      hostClasses: ['Rogue','Charlatan','Bard'],
      hostFallback: 'A cloaked stranger',
      cssMod: 'thieves',
      items: [
        { key:'coin',   price:10,  title:"Cutpurse's Coin",      sub:"Flip until tails. Every heads pays +15 hearts. The risk grows." },
        /* R148-Z32: Three Mimics 10 → 15. Loaded Dice 10 → 30 (EV ≈ 47). */
        { key:'chests', price:15,  title:"Three Mimics",         sub:"Four chests. Three pay. One bites. Pick well." },
        { key:'dice',   price:30,  title:"Loaded Dice",          sub:"Roll six d20s. Highest three count. The dice tilt your way." },
        /* R148-Z32: Liar's Penny (slot reel, ~+10-75 EV at 10h)
           replaced with "The Flip" — a single coin flip at 100h.
           Win = +500 hearts, lose = the 100h gone. 50/50 odds.
           EV ≈ +150. High-stakes single-tap moment the thieves'
           table is built around. */
        { key:'penny',  price:100, title:"The Flip",             sub:"One coin. One toss. Heads pays 500 hearts. Tails takes everything." }
      ]
    },
    sage: {
      type: 'sageBazaar',
      title: "The Sage's Bazaar",
      intro: "An old reader looks up from a stack of scrolls. Four offerings between you.",
      hostClasses: ['Cleric','Druid','Wizard','Bard'],
      hostFallback: 'A traveling sage',
      cssMod: 'sage',
      items: [
        { key:'bones',    price:10,  title:"Sage's Bones",       sub:"Roll four d6. The Hall reads your fortune in hearts." },
        /* Keep the existing charms; Scholar's Lens replaces the retired
           free-unlock scroll at the same price/effect as the Mage's offer. */
        { key:'lonefire', price:25,  title:'Lonefire Charm',     sub:'Carry it. The next time your hearts run out, the charm steps in.', inventoryItem:true },
        { key:'scholar',  price:10,  title:"Scholar's Lens",     sub:'The next five rolls are guessing games. No repeats.' },
        { key:'oath',     price:100, title:"Patron's Oath",      sub:'Hold it five days. Honor the oath and claim it.', inventoryItem:true }
      ]
    }
  };
  /* Shared affordability + host helpers — same logic across all 3
     bazaars. _bazaarPickHost runs the same Wizard-first fallback chain
     for Mage; Thieves prefer Rogue/Charlatan/Bard; Sage prefer
     Cleric/Druid. */
  function _bazaarPickHost(kind){
    var entry = BAZAAR_REGISTRY[kind];
    if(!entry) return null;
    var host = null;
    try {
      var classes = entry.hostClasses || [];
      for(var i = 0; i < classes.length && !host; i++){
        var pool = (typeof poolByClass === 'function') ? (poolByClass(classes[i]) || []) : [];
        if(pool.length){ host = pool[Math.floor(Math.random() * pool.length)]; }
      }
      if(!host){
        var allChars = (typeof window.hohGetAllCharacters === 'function') ? (window.hohGetAllCharacters() || []) : [];
        var any = allChars.filter(function(c){ return c && c.id; });
        if(any.length) host = any[Math.floor(Math.random() * any.length)];
      }
    } catch(_){}
    return host;
  }
  /* Build a bazaar event object for the named kind. Used by the
     special-tier dispatcher and the queued-special early-fire path. */
  function _buildBazaarEvent(kind){
    var entry = BAZAAR_REGISTRY[kind];
    if(!entry) return null;
    return {
      type: entry.type,
      bazaarKind: kind,
      phase: 'idle',
      host: _bazaarPickHost(kind),
      _eventKey: entry.type
    };
  }
  /* Picker entry points used by _pickSpecialRoll. Each one consults the
     shared cooldown so when ANY bazaar fires, no bazaar of any flavor
     can fire again for MAGE_BAZAAR_BUFFER rolls. */
  function pickMageBazaarEvent(){
    if(mageBazaarOnCooldown()) return null;
    var ev = _buildBazaarEvent('mage');
    if(ev){ try { markMageBazaarFired(); } catch(_){} }
    return ev;
  }
  function pickThievesBazaarEvent(){
    if(mageBazaarOnCooldown()) return null;
    var ev = _buildBazaarEvent('thieves');
    if(ev){ try { markMageBazaarFired(); } catch(_){} }
    return ev;
  }
  function pickSageBazaarEvent(){
    if(mageBazaarOnCooldown()) return null;
    var ev = _buildBazaarEvent('sage');
    if(ev){ try { markMageBazaarFired(); } catch(_){} }
    return ev;
  }
  /* ================================================================ */
  /* R148-Z29: Pay the Guide — Type 2 event                            */
  /* ================================================================ */
  /* A bard host offers to take the user to a destination of their
     choice. Pay 10 hearts → guaranteed bazaar (random of 3 flavors)
     on the next roll. Pay 50 hearts → guaranteed legendary shop on
     the next roll. The 50h option is GREYED if the user can't afford,
     not hidden, so they know the option exists and can save up. The
     event has its own cooldown (independent of bazaars) so it can
     surface even when bazaars are still on their 25-roll lockout. */
  var GUIDE_BUFFER = 18;
  function guideOnCooldown(){
    var rc = getDayState().rollCount;
    return (rc - _readCooldownRoll('gd')) < GUIDE_BUFFER;
  }
  function markGuideFired(){
    _writeCooldownRoll('gd', getDayState().rollCount);
  }
  function pickGuideEvent(){
    if(guideOnCooldown()) return null;
    /* Bard host preferred; fall back to any character so a thin Hall
       still surfaces the event. */
    var host = null;
    try {
      var bards = (typeof poolByClass === 'function') ? (poolByClass('Bard') || []) : [];
      if(bards.length){
        host = bards[Math.floor(Math.random() * bards.length)];
      } else {
        var allChars = (typeof window.hohGetAllCharacters === 'function') ? (window.hohGetAllCharacters() || []) : [];
        var any = allChars.filter(function(c){ return c && c.id; });
        if(any.length) host = any[Math.floor(Math.random() * any.length)];
      }
    } catch(_){}
    if(!host) return null;
    try { markGuideFired(); } catch(_){}
    return {
      type: 'payGuide',
      host: host,
      phase: 'idle',
      _eventKey: 'payGuide'
    };
  }
  /* R116-D: Bazaar buff state. Three independent buffs the user can
     buy from the Mage's Bazaar; all per-day, all stored under their
     own localStorage keys to survive across page loads.
       coliseumQueue  — array of event TYPES to force in order
                        (e.g. ['championDuel','rareDuel','epicDuel',
                         'tavernBrawl','tavernBrawl4']).
       scholarBuff    — { remaining: N, seen: [keys...] } — next N
                        rolls forced to a guessing event not in seen.
       specialFlag    — bool — next roll forced to a special event.
       bazaarSold     — { dice:bool, coliseum:bool, scholar:bool,
                          special:bool } — items already purchased
                        today; persists so a re-roll of the bazaar
                        renders with sold-state disabled. */
  function _bazaarKey(suffix){ return 'hoh-mb-' + suffix + '-' + _ymdNow(); }
  function getColiseumQueue(){
    try {
      var raw = localStorage.getItem(_bazaarKey('cq'));
      var a = raw ? JSON.parse(raw) : [];
      return Array.isArray(a) ? a : [];
    } catch(_){ return []; }
  }
  function setColiseumQueue(arr){
    try { localStorage.setItem(_bazaarKey('cq'), JSON.stringify(arr || [])); } catch(_){}
  }
  function getScholarBuff(){
    try {
      var raw = localStorage.getItem(_bazaarKey('sb'));
      var s = raw ? JSON.parse(raw) : null;
      if(!s || typeof s.remaining !== 'number') return { remaining: 0, seen: [] };
      return { remaining: s.remaining, seen: Array.isArray(s.seen) ? s.seen : [] };
    } catch(_){ return { remaining: 0, seen: [] }; }
  }
  function setScholarBuff(state){
    try { localStorage.setItem(_bazaarKey('sb'), JSON.stringify({
      remaining: state.remaining || 0,
      seen: Array.isArray(state.seen) ? state.seen : []
    })); } catch(_){}
  }
  function getSpecialFlag(){
    try { return localStorage.getItem(_bazaarKey('sf')) === '1'; } catch(_){ return false; }
  }
  function setSpecialFlag(on){
    try {
      if(on) localStorage.setItem(_bazaarKey('sf'), '1');
      else localStorage.removeItem(_bazaarKey('sf'));
    } catch(_){}
  }
  function getBazaarSold(){
    try {
      var raw = localStorage.getItem(_bazaarKey('sold'));
      return raw ? (JSON.parse(raw) || {}) : {};
    } catch(_){ return {}; }
  }
  function setBazaarSold(state){
    try { localStorage.setItem(_bazaarKey('sold'), JSON.stringify(state || {})); } catch(_){}
  }
  function markBazaarItemSold(key){
    var s = getBazaarSold();
    s[key] = true;
    setBazaarSold(s);
  }
  /* R140-Z: Legendary Shop "Experience All Events" buff queue. Stored
     under the same per-day bazaar key namespace so it expires with the
     rest at midnight. The queue has three phases:
       phase 1: 2 forced specials
       phase 2: every trivia event, in shuffled order
       phase 3: every duel event, in shuffled order
     Each phase pulls one event per refresh until the list empties.
     consumeActiveBuff drains it before the normal picker chain runs. */
  /* R387 (Jan): familiarCrowd removed from the tour too — a paid "every
     event" run must not include a retired event. A stale saved queue that
     still holds the key drains gracefully (its picker is gone from the map
     below, so the slot is consumed and skipped). */
  var ALLEVENTS_TRIVIA_TYPES = [
    'classQuiz','popularity','guessFB','guessWW','guessRR',
    'guessEHE','findCulprit','findCulpritRace','findImposter'
  ];
  /* R148-Z37: 'mythicDuelSpecial' is intentionally NOT in this base
     shuffle pool — it's pinned to the END of the queue in
     startAllEventsBuff so it lands as the climactic final event
     regardless of shuffle outcome. The user paid for an experience
     of every event; the Mythic Reckoning is the headliner finish. */
  var ALLEVENTS_DUEL_TYPES = [
    'championDuel','rareDuel','epicDuel','legendaryDuel','mythicDuel','crownMatch'
  ];
  function getAllEventsBuff(){
    try {
      var raw = localStorage.getItem(_bazaarKey('aeb'));
      return raw ? JSON.parse(raw) : null;
    } catch(_){ return null; }
  }
  function setAllEventsBuff(state){
    try {
      if(!state) localStorage.removeItem(_bazaarKey('aeb'));
      else localStorage.setItem(_bazaarKey('aeb'), JSON.stringify(state));
    } catch(_){}
  }
  function startAllEventsBuff(){
    var sh = function(arr){
      var a = arr.slice();
      for(var i = a.length - 1; i > 0; i--){
        var j = Math.floor(Math.random() * (i + 1));
        var t = a[i]; a[i] = a[j]; a[j] = t;
      }
      return a;
    };
    /* R148-Z37: Shuffle the regular duels, then APPEND mythicDuelSpecial
       at the very end so it always lands as the final event the user
       walks through. Only included when at least 2 mythics exist
       (the picker would return null otherwise — silently skipped). */
    var duels = sh(ALLEVENTS_DUEL_TYPES);
    duels.push('mythicDuelSpecial');
    setAllEventsBuff({
      specials: 2,
      trivia: sh(ALLEVENTS_TRIVIA_TYPES),
      duels: duels
    });
  }
  /* R116-D: Buff consumer. Called at the TOP of pickFreshFacesEvent
     so active buffs override the normal picker chain. Returns an event
     object to fire (matching the picker chain's return shape) or null
     to fall through. The order matters:
       1. Coliseum queue first — it's a chain the user paid for and
          interrupting it would feel like the brawls disappeared.
       2. Special flag second — the user paid for a guaranteed special
          on the next roll specifically.
       3. Scholar buff third — guessing games are lower-stakes than
          brawls and special events. */
  function consumeActiveBuff(){
    /* R140-Z: 0. All-Events buff queue (Legendary Shop, 50h). Drains
       through 2 specials → all trivia → all duels in that order. Runs
       BEFORE the Coliseum queue because the user just paid for a long
       chain and we don't want a Coliseum tail to interleave with it.
       When the queue is empty, this block is a no-op and we fall
       through to the existing buff stack. */
    try {
      var aeb = getAllEventsBuff();
      if(aeb){
        /* Phase 1 — specials. Try forceSpecialEventOnly; if no special
           is eligible right now, skip to phase 2 so the buff isn't
           wasted. */
        if((aeb.specials || 0) > 0){
          var aebSp = null;
          try { aebSp = (typeof forceSpecialEventOnly === 'function') ? forceSpecialEventOnly() : null; } catch(_){ aebSp = null; }
          if(aebSp){
            aeb.specials = Math.max(0, aeb.specials - 1);
            setAllEventsBuff(aeb);
            aebSp._fromBuff = 'allevents';
            return aebSp;
          }
          /* No special available this roll — drop the phase-1 counter
             so we don't loop forever and move to phase 2. */
          aeb.specials = 0;
          setAllEventsBuff(aeb);
        }
        /* Phase 2 — every trivia event. Walk the shuffled list and
           return the first one that's pickable RIGHT NOW. Drop entries
           that decline (e.g. familiarCrowd needs ≥10 hearted chars and
           may not be eligible; we still consume the slot so the list
           drains). */
        var triviaPickers = {
          classQuiz: pickClassQuizEvent,
          popularity: pickPopularityEvent,
          guessFB: pickGuessFighterBarbarianEvent,
          guessWW: pickGuessWizardWarlockEvent,
          guessRR: pickGuessRangerRogueEvent,
          guessEHE: pickGuessElfHalfElfEvent,
          findCulprit: pickFindCulpritEvent,
          findCulpritRace: pickFindCulpritRaceEvent,
          findImposter: pickFindImposterEvent
          /* R387: familiarCrowd retired — stale queued keys skip via the
             existing drop-entries-that-decline path. */
        };
        while(aeb.trivia && aeb.trivia.length){
          var tKey = aeb.trivia[0];
          var tFn = triviaPickers[tKey];
          var tEv = null;
          try { tEv = (typeof tFn === 'function') ? tFn() : null; } catch(_){ tEv = null; }
          aeb.trivia.shift();
          setAllEventsBuff(aeb);
          if(tEv){
            tEv._eventKey = tKey;
            tEv._fromBuff = 'allevents';
            return tEv;
          }
          /* Loop continues — try the next trivia type on the SAME
             refresh so the user doesn't get a non-event roll. */
        }
        /* Phase 3 — every duel. Same drain pattern as trivia. */
        var duelPickers = {
          championDuel: pickChampionDuelEvent,
          rareDuel: pickRareDuelEvent,
          epicDuel: pickEpicDuelEvent,
          legendaryDuel: pickLegendaryDuelEvent,
          mythicDuel: pickMythicDuelEvent,
          crownMatch: pickCrownMatchEvent,
          /* R148-Z37: Type 3 Mythic Duel. Pinned to end of the duels
             queue via startAllEventsBuff. Pass force:true so the
             50h wallet floor inside the picker is bypassed for the
             buff-queued path — the user already paid 50h for the
             All-Events buff itself; they shouldn't be locked out of
             the headline event because they're temporarily under
             the wallet floor. */
          mythicDuelSpecial: function(){ return pickMythicDuelSpecialEvent({ force: true }); }
        };
        while(aeb.duels && aeb.duels.length){
          var dKey = aeb.duels[0];
          var dFn = duelPickers[dKey];
          var dEv = null;
          try { dEv = (typeof dFn === 'function') ? dFn() : null; } catch(_){ dEv = null; }
          aeb.duels.shift();
          setAllEventsBuff(aeb);
          if(dEv){
            dEv._eventKey = dKey;
            dEv._fromBuff = 'allevents';
            return dEv;
          }
        }
        /* All three phases drained — clear state and fall through to
           the rest of consumeActiveBuff (Coliseum / Scholar / etc.). */
        setAllEventsBuff(null);
      }
    } catch(_){}
    /* 1. Coliseum queue. */
    try {
      var queue = getColiseumQueue();
      if(queue.length){
        var nextType = queue.shift();
        setColiseumQueue(queue);
        /* R136-coliseum-fix: legendaryDuel + mythicDuel were MISSING
           from this map. The Coliseum buy queues a `legendaryDuel`
           tail event when the user owns ≥2 legendaries (see the
           bazaar buy handler), but with no entry here the picker
           silently skipped that step — the user paid for a 4-event
           chain and only got 3, with the climax (a Legendary Duel)
           vanishing. Adding both duels here closes the gap. */
        var picker = ({
          championDuel:    pickChampionDuelEvent,
          rareDuel:        pickRareDuelEvent,
          epicDuel:        pickEpicDuelEvent,
          legendaryDuel:   pickLegendaryDuelEvent,
          mythicDuel:      pickMythicDuelEvent,
          tavernBrawl:     pickTavernBrawlEvent,
          tavernBrawl4:    pickTavernBrawl4Event
        })[nextType];
        if(typeof picker === 'function'){
          var ev = picker();
          if(ev){
            ev._eventKey = nextType;
            ev._fromBuff = 'coliseum';
            return ev;
          }
          /* R136-coliseum-fix: when the picker returns null (e.g. the
             user doesn't have enough chars at that tier), recurse so
             the NEXT queued type gets a chance THIS refresh — not on a
             future one. Otherwise the user pays a heart, sees nothing
             new (Fresh Faces fallback), and loses confidence in the
             buff. The queue's been shifted already so recursion is
             bounded by queue length. */
          return consumeActiveBuff();
        } else {
          /* Unknown type slipped into the queue — recurse the same way
             so the chain doesn't dead-end on one bad entry. */
          return consumeActiveBuff();
        }
      }
    } catch(_){}
    /* 2. Special-event guarantee.
       R118-A: KEEP the flag set if no special is eligible this roll.
       The user paid hearts for a guaranteed special — silently
       clearing the flag when the rare-special tier returns null
       evaporated their purchase. Now the flag persists across rolls
       until a special actually fires. The bazaar itself is on a
       20-roll cooldown so it can't satisfy the flag right after
       being purchased; other rare specials might also be on
       per-day caps. The flag is patient. */
    try {
      if(getSpecialFlag()){
        /* R140-D: Audience-with-the-Hall buff was firing the 16-slot
           Spin Wheel almost every time. Two compounding bugs:
             1. Pool was only 3 entries (spinWheel, legendaryShop,
                fiveCards) and Legendary Shop's 70-roll natural-cadence
                threshold made it return null in 99% of cases — leaving
                spinWheel + fiveCards as the realistic options, and the
                shuffle still landed on spinWheel half the time.
             2. No "last picked" memory, so back-to-back Audience buys
                could return the same event.
           Fix:
             - Expand pool to all 4 special events the user expects to
               see: spinWheel, legendaryShop, fiveCards, pickACard.
             - Pass force:true to Legendary Shop so it bypasses the
               natural-cadence gates when fired from this buff.
             - Track last fired event in localStorage and prefer NOT to
               re-fire it. If only the last-fired event is eligible we
               still fire it so the buff is never wasted.
           heroCircle / marrowEnd intentionally NOT included — those
           have legitimate daily/weekly caps and live outside the
           "rare special" tier the user described. */
        var bigEventTypes = ['spinWheel', 'legendaryShop', 'fiveCards', 'pickACard'];
        /* Fisher-Yates shuffle so each fire of the buff lands on a
           random event when all are eligible. */
        for(var biShuf = bigEventTypes.length - 1; biShuf > 0; biShuf--){
          var biJ = Math.floor(Math.random() * (biShuf + 1));
          var biTmp = bigEventTypes[biShuf]; bigEventTypes[biShuf] = bigEventTypes[biJ]; bigEventTypes[biJ] = biTmp;
        }
        /* Read last-fired so we can deprioritize it. */
        var lastBuff = '';
        try { lastBuff = localStorage.getItem('hoh-mb-special-last-' + _ymdNow()) || ''; } catch(_){ lastBuff = ''; }
        if(lastBuff){
          /* Move lastBuff to the END of the shuffled order so it's
             tried last and only fires if no other eligible. */
          var withoutLast = [];
          for(var biL = 0; biL < bigEventTypes.length; biL++){
            if(bigEventTypes[biL] !== lastBuff) withoutLast.push(bigEventTypes[biL]);
          }
          if(withoutLast.length !== bigEventTypes.length){
            withoutLast.push(lastBuff);
            bigEventTypes = withoutLast;
          }
        }
        var bigSp = null;
        for(var biE = 0; biE < bigEventTypes.length && !bigSp; biE++){
          var bigKey = bigEventTypes[biE];
          var picker;
          var pickerArgs;
          if(bigKey === 'spinWheel'){ picker = pickSpinWheelEvent; pickerArgs = undefined; }
          else if(bigKey === 'legendaryShop'){ picker = pickLegendaryShopEvent; pickerArgs = { force: true }; }
          else if(bigKey === 'fiveCards'){ picker = pickFiveCardsEvent; pickerArgs = undefined; }
          else if(bigKey === 'pickACard'){ picker = pickPickACardEvent; pickerArgs = undefined; }
          if(typeof picker !== 'function') continue;
          try { bigSp = pickerArgs ? picker(pickerArgs) : picker(); } catch(_){ bigSp = null; }
          if(bigSp){
            try { localStorage.setItem('hoh-mb-special-last-' + _ymdNow(), bigKey); } catch(_){}
          }
        }
        if(bigSp){
          setSpecialFlag(false);
          bigSp._fromBuff = 'special';
          return bigSp;
        }
        /* Keep flag, fall through to normal pick. */
      }
    } catch(_){}
    /* 3. Scholar buff — force a guessing event not in seen list. */
    try {
      var sch = getScholarBuff();
      if(sch.remaining > 0){
        var GUESSING_PICKERS = [
          ['classQuiz',       pickClassQuizEvent],
          ['popularity',      pickPopularityEvent],
          ['guessFB',         pickGuessFighterBarbarianEvent],
          ['guessWW',         pickGuessWizardWarlockEvent],
          ['guessRR',         pickGuessRangerRogueEvent],
          ['guessEHE',        pickGuessElfHalfElfEvent],
          ['findCulprit',     pickFindCulpritEvent],
          ['findCulpritRace', pickFindCulpritRaceEvent],
          ['coinBet',         pickCoinBetEvent]
        ];
        /* Filter to keys the user hasn't seen on this buff yet. */
        var available = GUESSING_PICKERS.filter(function(p){
          return sch.seen.indexOf(p[0]) === -1;
        });
        /* If all 12 keys exhausted (user bought scholar twice on same
           day), reset the seen list. */
        if(!available.length){
          sch.seen = [];
          available = GUESSING_PICKERS;
        }
        /* Shuffle for variety. */
        for(var i = available.length - 1; i > 0; i--){
          var j = Math.floor(Math.random() * (i + 1));
          var tmp = available[i]; available[i] = available[j]; available[j] = tmp;
        }
        for(var k = 0; k < available.length; k++){
          var key = available[k][0];
          var fn = available[k][1];
          if(typeof fn !== 'function') continue;
          var gev;
          try { gev = fn(); } catch(_){ gev = null; }
          if(gev){
            sch.remaining = Math.max(0, sch.remaining - 1);
            sch.seen.push(key);
            setScholarBuff(sch);
            gev._eventKey = key;
            gev._fromBuff = 'scholar';
            return gev;
          }
        }
        /* No guessing event eligible right now (pool too thin) — let
           normal picker run, but DON'T decrement remaining so the
           user gets their next attempt. */
      }
    } catch(_){}
    return null;
  }

  /* Force-fire fallback used when first-of-day or 15-cap thresholds
     trigger. Tries every event family with normal gap gating BYPASSED,
     so it always has a candidate as long as something is eligible
     (affordability, daily caps, pool size). Falls back through:
       1. Gambling pool — cheapest, most variety, hosted by characters.
       2. Rare specials — always pickable from a healthy pool.
     Returns null only if literally nothing is pickable (extremely
     thin pool or every daily gamble already capped AND no rare
     specials available). MIN_ABSOLUTE_GAP is still respected by the
     caller before this is invoked. */
  /* R136-event-frequency: force-pick a SPECIAL event (Bazaar,
     Crossroads, Hero's Circle, Marrow's End, secondChances, banished,
     Legendary Shop). Loops the inner picker accepting only special-
     tier results. The threshold ladder allocates ~24% to specials
     under the eligible/seen/recent gates, so 8 attempts gives ~85%
     chance of returning a special when one is available. Returns null
     if the entire special pool is on cooldown / unavailable, in which
     case the caller falls through to other force tiers. */
  function forceSpecialEventOnly(){
    /* Try Legendary Shop first directly — it has its own counter and
       is the rarest event, so when its gate opens we want to fire it. */
    var legshop = pickLegendaryShopEvent();
    if(legshop && isSpecialEvent(legshop)){
      delete legshop._eventKey;
      return legshop;
    }
    /* Loop the threshold ladder for a special-tier hit. Each call
       generates a fresh random roll. */
    for(var i = 0; i < 8; i++){
      var ev = pickFreshFacesEventInner(true, false);
      if(ev && isSpecialEvent(ev)) return ev;
    }
    return null;
  }
  function forceAnyEvent(){
    var gb = pickGamblingEventInner();
    if(gb) return gb;
    resetSeenSpecialsIfFullyCycled();
    var sp = pickFreshFacesEventInner(false, true);
    if(!sp) sp = pickFreshFacesEventInner(true, true);
    if(!sp) sp = pickFreshFacesEventInner(true, false);
    return sp || null;
  }

  /* Force-pick an INTERACTIVE event specifically. Used when the user
     hasn\'t had a coin/dice/quiz/duel/brawl/wheel/pickCard for 10+
     refreshes — the user explicitly asked for action beats every
     7-10 rolls. We try the gambling pool first (all five types are
     interactive), then directly attempt thievesCoin and classQuiz
     from the rare-special pool. Returns null only if nothing is
     eligible (all daily caps hit, can\'t afford coin, no quiz pool). */
  function forceInteractiveEvent(){
    /* Gambling first — every gamble is interactive by definition. */
    var gb = pickGamblingEventInner();
    if(gb) return gb;
    /* Coin and quiz from the rare-special tier, picked directly so
       we don\'t accidentally land on a rarityTribe or familiar.
       R119-I: Daily cap dropped — Sneaky Encounter no longer self-
       gates after one win. The 10-refresh interactive-overdue
       trigger above keeps this from looping; we only land here when
       an interactive is genuinely owed. */
    var coinPool = (typeof poolByClass === 'function') ? poolByClass('Rogue') : [];
    if(coinPool && coinPool.length >= MIN_THEME_CHARS){
      /* R71: same host pattern as the regular picker — mythic roll
         first, else random Rogue. */
      var forcedMythicHost = (typeof window.hohRollMythicHost === 'function') ? window.hohRollMythicHost() : null;
      var forcedHost = forcedMythicHost || coinPool[Math.floor(Math.random() * coinPool.length)];
      return {
        type: 'thievesCoin',
        rogues: coinPool,
        host: forcedHost,
        mythicHost: !!forcedMythicHost,
        _eventKey: 'thievesCoin'
      };
    }
    if(typeof pickClassQuiz === 'function'){
      var quiz = pickClassQuiz();
      if(quiz){ quiz._eventKey = 'classQuiz'; return quiz; }
    }
    return null;
  }

  /* ================================================================ */
  /* R140-Z3n: UNIFORM DISPATCHER — Type 1 / Type 2 / Type 3            */
  /* ================================================================ */
  /* Roll a bucket per user spec (65/30/5) and pick uniformly within
     the chosen bucket. This replaces the old weighted/tier-forced
     picker chain as the primary path. The existing logic remains as
     a fallback for safety nets (dry-spell guarantees, day-roll
     guarantees) when the dispatcher chooses to defer. */
  var BUCKET_HEART   = 0.62;
  var BUCKET_EVENT   = 0.32;
  /* BUCKET_SPECIAL implied = 0.06 (remainder). R150: heart 0.65->0.62,
     event 0.30->0.32, special 0.05->0.06 so Type 2 and Type 3 both land
     a little more often. The hard Type 2 force gap (EVENT_FORCE_GAP)
     is what actually guarantees the 3-to-5 cadence; these weights just
     shift the average. */
  /* R150: hard ceiling on the Type 2 drought. When refreshesSinceLastType2
     reaches this value the next manual refresh is forced into the Event
     bucket. With the 0.32 base bucket roll the felt cadence averages
     about one Type 2 every 2.5 to 3 refreshes and is hard-capped so it
     can never be more than 5 refreshes away. */
  var EVENT_FORCE_GAP = 5;

  function _runUniformDispatcher(isAuto, dayState){
    /* While the Battlegrounds owns the surface, stop rolling Explore events so
       nothing fires over it: that's the "open" entry (daily done, not yet
       fought) and any live fight (the #hoh-contract host is mounted). Once the
       battle is over for the day, this gate opens and events resume, which is
       exactly the "heart heroes and do events for the rest of the day" state. */
    try { if(_contractEligible() === 'open' || document.getElementById('hoh-contract')){ setDayState(dayState); return null; } } catch(_){}
    /* Wallet-empty Fresh Faces fallback: when bank is 0, skip event
       picking entirely so renderInto's wallet-empty branch lands on
       Fresh Faces. This honors the user's "back to Fresh Faces on
       empty wallet" requirement. */
    try {
      var bankNow = (typeof window.heartsRemainingToday === 'function') ? window.heartsRemainingToday() : 1;
      if(bankNow <= 0){
        setDayState(dayState);
        return null; /* caller's wallet-empty fallback handles render */
      }
    } catch(_){}

    /* Auto-refresh: don't fire random events on idle ticks. Auto only
       runs the dispatcher when an event must fire (dry spell). Manual
       refreshes always run it. */
    var dryspell = refreshesSinceLastEvent >= 12;
    var dayGuarantee = !dayState.eventFired && dayState.rollCount >= 10;
    /* R150: Type 2 force gap. When EVENT_FORCE_GAP refreshes have passed
       with no Event-bucket roll, force one. This caps the worst-case
       Type 2 drought so an event lands every 3 to 5 manual refreshes.
       Checked after the isAuto early return below, so it only steers
       manual refreshes — idle auto refreshes stay ambient. */
    var type2Gap = refreshesSinceLastType2 >= EVENT_FORCE_GAP;
    if(isAuto && !dryspell && !dayGuarantee){
      return null; /* fall through — auto-render keeps current state */
    }

    /* Bucket roll. Force biases:
         - Day guarantee → Event (the user owes themselves an event by roll 10)
         - Dry-spell → Event (no Heart Roll satisfies the dry-spell guard)
         - Type 2 gap → Event (R150 hard ceiling on the Type 2 drought) */
    var bucket;
    if(dayGuarantee || dryspell || type2Gap){
      bucket = ROLL_TYPE_EVENT;
    } else {
      var r = Math.random();
      if(r < BUCKET_HEART) bucket = ROLL_TYPE_HEART;
      else if(r < BUCKET_HEART + BUCKET_EVENT) bucket = ROLL_TYPE_EVENT;
      else bucket = ROLL_TYPE_SPECIAL;
    }

    /* Resolve the bucket. If the chosen bucket has no eligible entry,
       fall through to the next-most-common bucket so we never return
       empty on a manual click. */
    var resolved = null;
    if(bucket === ROLL_TYPE_SPECIAL){
      resolved = _pickSpecialRoll();
      if(!resolved) bucket = ROLL_TYPE_EVENT;
    }
    if(!resolved && bucket === ROLL_TYPE_EVENT){
      resolved = _pickEventRollUniform();
      if(!resolved) bucket = ROLL_TYPE_HEART;
    }
    if(!resolved && bucket === ROLL_TYPE_HEART){
      resolved = _pickHeartRollUniform();
    }
    if(!resolved) return null;

    /* Resolved an event/themed-render — bookkeep the counters before
       returning. Mirror what the old picker chain does. */
    dayState.eventFired = true;
    setDayState(dayState);
    refreshesSinceLastEvent = 0;
    /* R150: reset the Type 2 drought counter whenever an Event-bucket
       roll resolved. `bucket` holds the bucket that actually resolved
       after any Special->Event or Event->Heart fallthrough, so this
       fires for every gambling/event roll regardless of the specific
       event type and never runs away. */
    if(bucket === ROLL_TYPE_EVENT) refreshesSinceLastType2 = 0;
    try {
      if(resolved && resolved.type){
        if(typeof isInteractiveEvent === 'function' && isInteractiveEvent(resolved)) refreshesSinceLastInteractive = 0;
        if(typeof isUnlimitedEvent === 'function' && isUnlimitedEvent(resolved)) refreshesSinceLastUnlimited = 0;
        if(typeof isSpecialEvent === 'function' && isSpecialEvent(resolved)) refreshesSinceLastSpecialEvent = 0;
        if(typeof isGamblingPoolEvent === 'function' && isGamblingPoolEvent(resolved)){
          refreshesSinceLastGamblingPool = 0;
          try { rememberGamblingSubtype(resolved.type); } catch(_){}
        }
        if(typeof rememberEvent === 'function' && resolved._eventKey){
          try { rememberEvent(resolved._eventKey); } catch(_){}
        }
      }
      if(resolved && resolved._heartThemedRender){
        /* Themed-phase render — counter as a heart roll, no special
           cooldowns to reset. */
        refreshesSinceLastEvent = 0;
      }
    } catch(_){}
    /* Strip internal flags before returning to caller. */
    if(resolved && resolved._eventKey) delete resolved._eventKey;
    return resolved;
  }

  /* Type 3 — Special Event Rolls. Each gated by its own cooldown /
     daily flag. Tried in random order so the user doesn't always see
     the same one first when multiple are eligible. */
  function _pickSpecialRoll(){
    var candidates = [];
    /* Legendary Shop — daily cooldown + 70-refresh threshold inside picker. */
    if(typeof pickLegendaryShopEvent === 'function'){
      var ls = pickLegendaryShopEvent();
      if(ls) candidates.push(ls);
    }
    /* Bazaar trio — all three share the 'mb' cooldown bucket so at most
       one bazaar lands per cadence window. The picker rolls a random
       flavor when eligible. */
    if(typeof pickMageBazaarEvent === 'function'){
      var bazaarKindsSp = ['mage','thieves','sage'];
      var spKind = bazaarKindsSp[Math.floor(Math.random() * bazaarKindsSp.length)];
      var bz = null;
      if(spKind === 'mage' && typeof pickMageBazaarEvent === 'function') bz = pickMageBazaarEvent();
      else if(spKind === 'thieves' && typeof pickThievesBazaarEvent === 'function') bz = pickThievesBazaarEvent();
      else if(spKind === 'sage' && typeof pickSageBazaarEvent === 'function') bz = pickSageBazaarEvent();
      if(bz) candidates.push(bz);
    }
    /* R148-Z37: Type 3 Mythic Duel — needs 2+ mythic chars in pool,
       50h wallet floor. Returns null when ineligible. */
    if(typeof pickMythicDuelSpecialEvent === 'function'){
      var mds = pickMythicDuelSpecialEvent();
      if(mds) candidates.push(mds);
    }
    /* Hero Circle / Crossroads / Captured by Jan etc — any "special"
       picker that exists. The list below is conservative; if a picker
       isn't defined the typeof check skips it cleanly. */
    if(typeof pickHeroCircleEvent === 'function'){
      var hc = pickHeroCircleEvent();
      if(hc) candidates.push(hc);
    }
    if(!candidates.length) return null;
    return candidates[Math.floor(Math.random() * candidates.length)];
  }

  /* Type 2 — Event Rolls. Uniform pick from the gambling-pool entries,
     filtered by recent buffer and eligibility. */
  function _pickEventRollUniform(){
    /* Reuse the existing pickGamblingEventInner which already runs
       through entries with anti-repeat + subtype rotation. Its output
       is already uniform-ish at the family level. For a stricter
       uniform pick we'd need to bypass its weight logic — for now
       this is good enough and preserves the existing gating. */
    if(typeof pickGamblingEventInner === 'function'){
      try { return pickGamblingEventInner(); } catch(_){ return null; }
    }
    return null;
  }

  /* Type 1 — Heart Rolls. Uniform pick from:
       - Every THEMED_PHASES entry (including Fresh Faces at index 0
         and Wandering Heroes when present)
       - Every Bustling Streets variant
       - Spectrum, Familiar, FF Portrait Gallery, Rarity Tribe events
     The user wants all of these EQUALLY likely. We build the pool on
     each call (cheap — ~50 entries), filter for eligibility, drop the
     recent-buffer subset, then pick uniformly. */
  function _pickHeartRollUniform(){
    var entries = [];
    /* Themed phases (including index 0 = Fresh Faces / Wandering Heroes). */
    if(Array.isArray(THEMED_PHASES)){
      for(var i = 0; i < THEMED_PHASES.length; i++){
        var theme = THEMED_PHASES[i];
        if(!theme) continue;
        /* R148-Z2: Threshold loosened from MIN_THEME_CHARS (3) to 1.
           Per user: "if we can't get enough characters for a specific
           roll to fill 2 rows, just add some random ones to the pool
           so that the roll still shows up instead of getting canceled."
           Themes with even a single thematic match now stay eligible
           in the dispatcher entries; the renderer (pickListForPhase
           below) pads the rest of the grid with random heroes when
           needed so the user sees 2+ rows under the themed eyebrow.
           ROOT CAUSE FIX for Wandering-Heroes-dominance: previously
           themes with thin pools were silently excluded from entries,
           leaving Wandering Heroes (the only filter-less theme that
           always passed the gate) over-represented in the rotation. */
        if(typeof themePoolSize === 'function' && themePoolSize(theme.filter) < 1) continue;
        entries.push({ kind: 'themedPhase', themedPhaseIdx: i });
      }
    }
    /* Bustling Streets variants — each its own equal slot. */
    var bustling = [
      ['bustlingStreets',           typeof pickBustlingStreetsEvent === 'function' ? pickBustlingStreetsEvent : null],
      ['bustlingStreetsUncommon',   typeof pickBustlingStreetsUncommonEvent === 'function' ? pickBustlingStreetsUncommonEvent : null],
      ['bustlingStreetsRare',       typeof pickBustlingStreetsRareEvent === 'function' ? pickBustlingStreetsRareEvent : null],
      ['bustlingStreetsPortrait',   typeof pickBustlingStreetsPortraitEvent === 'function' ? pickBustlingStreetsPortraitEvent : null],
      ['bustlingStreetsCasters',    typeof pickBustlingStreetsCastersEvent === 'function' ? pickBustlingStreetsCastersEvent : null],
      ['bustlingStreetsBeastfolk',  typeof pickBustlingStreetsBeastfolkEvent === 'function' ? pickBustlingStreetsBeastfolkEvent : null],
      ['bustlingStreetsClergy',     typeof pickBustlingStreetsClergyEvent === 'function' ? pickBustlingStreetsClergyEvent : null],
      ['bustlingStreetsDragonborn', typeof pickBustlingStreetsDragonbornEvent === 'function' ? pickBustlingStreetsDragonbornEvent : null],
      ['bustlingStreetsDragonHoard', typeof pickBustlingStreetsDragonHoardEvent === 'function' ? pickBustlingStreetsDragonHoardEvent : null],
      ['bustlingStreetsWildHunt',    typeof pickBustlingStreetsWildHuntEvent === 'function' ? pickBustlingStreetsWildHuntEvent : null],
      ['bustlingStreetsCharlatans',  typeof pickBustlingStreetsCharlatansEvent === 'function' ? pickBustlingStreetsCharlatansEvent : null],
      ['bustlingStreetsFurredFolk',  typeof pickBustlingStreetsFurredFolkEvent === 'function' ? pickBustlingStreetsFurredFolkEvent : null],
      ['bustlingStreetsFeyCourt',    typeof pickBustlingStreetsFeyCourtEvent === 'function' ? pickBustlingStreetsFeyCourtEvent : null],
      /* R140-Z3n Phase 3: 5 more processions in the uniform pool. */
      ['bustlingStreetsKnightProcession', typeof pickBustlingStreetsKnightProcessionEvent === 'function' ? pickBustlingStreetsKnightProcessionEvent : null],
      ['bustlingStreetsBardstorm',        typeof pickBustlingStreetsBardstormEvent === 'function' ? pickBustlingStreetsBardstormEvent : null],
      ['bustlingStreetsDruidMigration',   typeof pickBustlingStreetsDruidMigrationEvent === 'function' ? pickBustlingStreetsDruidMigrationEvent : null],
      ['bustlingStreetsPactWalk',         typeof pickBustlingStreetsPactWalkEvent === 'function' ? pickBustlingStreetsPactWalkEvent : null],
      ['bustlingStreetsTemplarMarch',     typeof pickBustlingStreetsTemplarMarchEvent === 'function' ? pickBustlingStreetsTemplarMarchEvent : null]
    ];
    bustling.forEach(function(pair){
      if(pair[1]) entries.push({ kind: 'eventPicker', key: pair[0], pickerFn: pair[1] });
    });
    /* R150: Conveyer Belt variants — one-row sibling of Bustling
       Streets. Each its own equal slot in the Type 1 pool. */
    var conveyer = [
      ['conveyerCommon',    typeof pickConveyerCommonEvent === 'function' ? pickConveyerCommonEvent : null],
      ['conveyerUncommon',  typeof pickConveyerUncommonEvent === 'function' ? pickConveyerUncommonEvent : null],
      ['conveyerRare',      typeof pickConveyerRareEvent === 'function' ? pickConveyerRareEvent : null],
      ['conveyerEpic',      typeof pickConveyerEpicEvent === 'function' ? pickConveyerEpicEvent : null],
      ['conveyerLegendary', typeof pickConveyerLegendaryEvent === 'function' ? pickConveyerLegendaryEvent : null],
      ['conveyerMythic',    typeof pickConveyerMythicEvent === 'function' ? pickConveyerMythicEvent : null],
      ['conveyerArcane',    typeof pickConveyerArcaneEvent === 'function' ? pickConveyerArcaneEvent : null],
      ['conveyerMartial',   typeof pickConveyerMartialEvent === 'function' ? pickConveyerMartialEvent : null],
      ['conveyerWanderers', typeof pickConveyerWanderersEvent === 'function' ? pickConveyerWanderersEvent : null]
    ];
    conveyer.forEach(function(pair){
      if(pair[1]) entries.push({ kind: 'eventPicker', key: pair[0], pickerFn: pair[1] });
    });
    /* Other Heart-Roll events (Spectrum, Familiar, Rarity Tribes,
       Encounters). Each goes through its own picker. */
    var others = [
      ['spectrum',         typeof pickSpectrum === 'function' ? pickSpectrum : null],
      ['familiar',         typeof pickFamiliarEvent === 'function' ? pickFamiliarEvent : null],
      ['epic',             typeof pickEpicEncounterEvent === 'function' ? pickEpicEncounterEvent : null],
      ['legendary',        typeof pickLegendaryEncounterEvent === 'function' ? pickLegendaryEncounterEvent : null],
      ['legendaryTrio',    typeof pickLegendaryTrioEvent === 'function' ? pickLegendaryTrioEvent : null],
      ['twoLegends',       typeof pickTwoLegendsEvent === 'function' ? pickTwoLegendsEvent : null]
    ];
    others.forEach(function(pair){
      if(pair[1]) entries.push({ kind: 'eventPicker', key: pair[0], pickerFn: pair[1] });
    });
    if(!entries.length) return null;
    /* Recent-buffer exclusion so we don't see the same theme twice in
       a short window. We filter, then if nothing remains we fall back
       to the unfiltered list.
       R148-Z22w: Added isRecentFamily check for event pickers. Without
       this, a recent 'bustlingStreets' fire would only block the
       exact key — leaving all OTHER bustlingStreets variants
       (Clergy, Charlatans, KnightProcession, etc.) eligible. Result:
       two streets-style rolls back to back, which the user reported.
       isRecentFamily walks the EVENT_FAMILIES.streets group (now
       complete with all 18 variants) so any recent BS variant blocks
       every BS variant from re-firing inside the buffer window. */
    var filtered = entries.filter(function(e){
      if(e.kind === 'themedPhase'){
        var eb = THEMED_PHASES[e.themedPhaseIdx] && THEMED_PHASES[e.themedPhaseIdx].eyebrow;
        if(eb && typeof isRecentTheme === 'function' && isRecentTheme(eb)) return false;
        return true;
      }
      if(e.kind === 'eventPicker'){
        if(e.key && typeof isRecentEvent === 'function' && isRecentEvent(e.key)) return false;
        /* R148-Z22w: family-level anti-repeat. Blocks the whole BS
           variant set after any one of them fires, and also blocks
           the duel / brawl / culprit / kindness families. */
        if(e.key && typeof isRecentFamily === 'function' && isRecentFamily(e.key)) return false;
        return true;
      }
      return true;
    });
    var pool = filtered.length ? filtered : entries;
    /* Try entries in shuffled order until one succeeds. */
    pool = hfShuffle(pool);
    for(var k = 0; k < pool.length; k++){
      var entry = pool[k];
      if(entry.kind === 'themedPhase'){
        var idx = entry.themedPhaseIdx;
        var phaseTheme = THEMED_PHASES[idx];
        if(!phaseTheme) continue;
        currentThemedPhase = phaseTheme;
        try { rememberRecentTheme(phaseTheme.eyebrow); } catch(_){}
        try { setPhase(idx === 0 ? 0 : 1); } catch(_){ try { setPhase(1); } catch(_){} }
        cachedWanderingPool = null;
        cachedWanderingPhase = null;
        renderedSnapshotIds = null;
        snapshotPhaseKey = null;
        return { _heartThemedRender: true };
      }
      if(entry.kind === 'eventPicker'){
        var ev;
        try { ev = entry.pickerFn(); } catch(_){ ev = null; }
        if(ev){
          ev._eventKey = entry.key;
          return ev;
        }
      }
    }
    return null;
  }

  function pickFreshFacesEvent(opts){
    var isAuto = !!(opts && opts.auto);
    /* R140-Z3n (revised): The tour ALWAYS advances on a refresh —
       manual, idle-timer auto, budget-exhausted auto, interactive-
       resolved auto, all of them. Previously idle-timer ticks paused
       the tour and re-rendered the same step, which the user reported
       as "refreshes into itself" / "spamming the same one". With
       advance-on-every-refresh, the auto-timer naturally walks the
       user through the 21 curated steps even if they sit idle. The
       browser-tab-hidden pause already stops the timer entirely (see
       startAutoTimer's visibility hook), so a backgrounded tab still
       can't burn through the tour without the user actively present. */

    /* Always count refreshes (auto + manual) toward gap counters and
       day-roll count. The original "auto refreshes drain but never
       fire" behavior left mobile/passive users without events because
       they rarely tap the manual refresh icon — R38 lets auto refreshes
       fire events through the FORCED path only (preserving the "events
       feel earned" beat for active users while ensuring passive users
       still see them). */
    refreshesSinceLastEvent++;
    refreshesSinceLastSpecial++;
    refreshesSinceLastGamble++;
    refreshesSinceLastInteractive++;
    /* R136-event-frequency: bump per-tier max-gap counters. They reset
       to 0 when a matching event fires; while they exceed the gap the
       picker forces a fire of that tier. */
    refreshesSinceLastUnlimited++;
    refreshesSinceLastSpecialEvent++;
    /* R136-fix-2: bump the action-pool counter every refresh too. */
    refreshesSinceLastGamblingPool++;
    /* R150: bump the Type 2 drought counter every refresh. */
    refreshesSinceLastType2++;

    var dayState = getDayState();
    dayState.rollCount = (dayState.rollCount || 0) + 1;

    /* R140-Z3j / R140-Z3m / R140-Z3n: Orchestrated 22-roll tour for
       anonymous first-day visitors. New (signed-out) users on their
       first daily session get a curated sequence of 21 events that
       showcases the full breadth of mechanics before random rolls
       take over at roll 22. Signed-in users skip the tour entirely.
       Tour state persists per-day in localStorage; users who sign
       in mid-tour exit it on the next refresh.

       R140-Z3n: ROOT-CAUSE FIX for "random rolls leaking in between
       tour steps". consumeAnonTourStep can return three kinds of
       outcomes; the picker now signals each with a DIFFERENT return
       value so the caller (_pickAndRenderInner) knows what to do:

         1. Event step succeeded → return the event object. Caller
            renders the event with renderSpecial.
         2. Themed-phase step → return `{ _tourThemedRender: true }`.
            currentThemedPhase + phase have ALREADY been set inside
            consumeAnonTourStep; the caller MUST skip its own
            theme-picking block and call renderInto with the
            already-set phase.
         3. Tour active but no work to do (auto-refresh pause,
            picker miss) → return `{ _tourSkipRender: true }`.
            Caller bails out without re-rendering anything so the
            current screen stays put.

       Previously themed-skip returned plain null which made the
       caller fall through to its random theme-picking block — that
       overwrote currentThemedPhase right after we set it, and the
       user saw a random theme instead of the curated step. The new
       sentinels stop that leak completely. */
    /* R140-Z3n (revised): Pass false for isAuto so every refresh
       (manual or auto, idle or interaction-driven) advances the tour.
       The user explicitly asked for auto-timer ticks to walk the
       sequence forward rather than re-render the same step. */
    var tourResult = (typeof consumeAnonTourStep === 'function')
      ? consumeAnonTourStep(dayState, false)
      : { tourActive:false, event:null };
    if(tourResult.tourActive){
      var tourEvent = tourResult.event;
      /* Themed-phase step: currentThemedPhase + phase are set.
         Return a sentinel so the caller renders the themed phase
         instead of running normal theme-picking logic. */
      if(tourEvent && tourEvent._tourThemedSkip){
        dayState.eventFired = true;
        setDayState(dayState);
        return { _tourThemedRender: true };
      }
      if(tourEvent){
        refreshesSinceLastEvent = 0;
        if(isInteractiveEvent(tourEvent)) refreshesSinceLastInteractive = 0;
        if(isUnlimitedEvent(tourEvent)) refreshesSinceLastUnlimited = 0;
        if(isSpecialEvent(tourEvent)) refreshesSinceLastSpecialEvent = 0;
        if(isGamblingPoolEvent(tourEvent)) refreshesSinceLastGamblingPool = 0;
        dayState.eventFired = true;
        setDayState(dayState);
        delete tourEvent._eventKey;
        return tourEvent;
      }
      /* Tour active but no event this refresh (auto-pause, picker
         miss, missing theme). Return a SKIP-RENDER sentinel so the
         caller bails completely — no clearing of currentSpecial,
         no random theme pick. The current screen stays put. */
      setDayState(dayState);
      return { _tourSkipRender: true };
    }

    /* R116-D: Honor active Bazaar buffs BEFORE the normal picker chain.
       The user paid hearts to bias the next N rolls; consumeActiveBuff
       returns a forced event matching the buff and we short-circuit
       the rest of the picker. The event still gets the standard
       counter/anti-repeat treatment via pickedEvent below — we DON'T
       call rememberEvent for buff-forced events because the user
       explicitly chose the sequence; gating them with the recent
       buffer would block their own purchases. */
    var buffEvent = (typeof consumeActiveBuff === 'function') ? hfPickRequestedEvent(consumeActiveBuff) : null;
    if(buffEvent){
      refreshesSinceLastEvent = 0;
      if(isInteractiveEvent(buffEvent)){
        refreshesSinceLastInteractive = 0;
      }
      /* R136-event-frequency: reset per-tier counters when a buff
         fires the matching tier. Bazaar buffs typically fire unlimited
         events (Wheel, Five-Card, etc.) but a future buff could fire
         a special — both paths covered. */
      if(isUnlimitedEvent(buffEvent)){
        refreshesSinceLastUnlimited = 0;
      }
      if(isSpecialEvent(buffEvent)){
        refreshesSinceLastSpecialEvent = 0;
      }
      /* R136-fix-2: Coliseum-buff events (brawls/duels) ARE gambling-
         pool events — reset the counter so we don't immediately force
         another action event one refresh after a paid Coliseum chain. */
      if(isGamblingPoolEvent(buffEvent)){
        refreshesSinceLastGamblingPool = 0;
        /* R136-fix-2b: also track the subtype so the next gambling-pool
           fire after a Coliseum brawl doesn't immediately pick another
           brawl. The user paid for a brawl chain — variety AFTER the
           chain matters more than within. */
        try { rememberGamblingSubtype(buffEvent.type); } catch(_){}
      }
      dayState.eventFired = true;
      setDayState(dayState);
      delete buffEvent._eventKey;
      delete buffEvent._fromBuff;
      return buffEvent;
    }

    /* R148-Z22v: Queued-special check FIRST, before the back-to-back
       gap check below. Queued events from user-driven actions
       (Bustling Streets ends → unlimited; milestone claim → mage
       bazaar / legendary shop) explicitly override the cadence
       rules. The user EARNED this event; we don't smother it under
       "casino gap" logic. Each branch consumes its flag exactly
       once so the queue can't double-fire on subsequent rolls. */
    try {
      var queuedSpecialEarly = window.__hohQueuedSpecial || null;
      if(queuedSpecialEarly){
        window.__hohQueuedSpecial = null;
        if(queuedSpecialEarly === 'legendaryShop' && typeof pickLegendaryShopEvent === 'function'){
          var queuedLsEarly = pickLegendaryShopEvent({ force: true });
          if(queuedLsEarly){
            rememberEvent(queuedLsEarly._eventKey || queuedLsEarly.type);
            markEventSeen(queuedLsEarly._eventKey || queuedLsEarly.type);
            delete queuedLsEarly._eventKey;
            dayState.eventFired = true;
            setDayState(dayState);
            return queuedLsEarly;
          }
        } else if(queuedSpecialEarly === 'mageBazaar'){
          /* R148-Z24: Milestone-claim "queue a bazaar" now randomizes
             across all 3 bazaar kinds via _buildBazaarEvent. The user
             still gets the rewarded bazaar trip but sees variety
             across claims. The shared 'mb' cooldown is bumped so this
             counts as the day's bazaar regardless of flavor. */
          var bazaarKindsQ = ['mage','thieves','sage'];
          var qKind = bazaarKindsQ[Math.floor(Math.random() * bazaarKindsQ.length)];
          var queuedBzEarly = (typeof _buildBazaarEvent === 'function') ? _buildBazaarEvent(qKind) : null;
          if(!queuedBzEarly){
            /* Defensive fallback to a minimal mage payload. */
            queuedBzEarly = { type: 'mageBazaar', bazaarKind: 'mage', phase: 'idle', host: null, _eventKey: 'mageBazaar' };
          }
          rememberEvent(queuedBzEarly.type || 'mageBazaar');
          try { if(typeof markMageBazaarFired === 'function') markMageBazaarFired(); } catch(_){}
          dayState.eventFired = true;
          setDayState(dayState);
          return queuedBzEarly;
        }
        /* Fall through if queued event couldn't fire — flag is
           already cleared so we don't loop. Subsequent gap + normal
           picker logic continues below. */
      }
    } catch(_){}
    /* Hard floor — never fire two events back-to-back, regardless of
       any guarantee. The MIN_ABSOLUTE_GAP keeps the rail from feeling
       like a casino even when force-firing kicks in. We persist the
       updated rollCount before bailing so the day-roll guarantee still
       counts this refresh.
       R148-Z22v: The queued-special check above bypasses this entirely.
       The unlimited-next queue (Bustling Streets follow-up) is also
       elevated below so users always get their earned event. */
    /* ================================================================ */
    /* R590 (Jan): FEED YOUR OWN LANDS INSIDE THE FIRST FIVE ROLLS      */
    /* ================================================================ */
    /* Jan: "always make sure that this event of the Like Your Own
       Heroes event shows up within the first five events, which means
       that they're able to kind of breeze through the Explore the Hall
       event."

       Placed ABOVE the MIN_ABSOLUTE_GAP bail on purpose. That bail
       returns null on roughly every other refresh, so a guarantee
       sitting below it is not a guarantee. The Bustling-Streets queue
       is elevated the same way for the same reason.

       ⛔ NO NEW DAILY LATCH, DELIBERATELY. The obvious implementation
       is a localStorage "fired today" flag, and it is the wrong call
       twice over. It is REDUNDANT, because streamKeyAtDailyCap already
       caps this key at STREAM_DAILY_FIRE_CAP fires per day and
       bumpStreamFireCount stamps it at pick time. And it is HARMFUL,
       because a player who reloads before tapping would burn the day
       on an event they never saw. dayState.rollCount already persists
       per day in localStorage, so the five-roll window closes on its
       own across reloads and cannot be farmed by refreshing.

       Every existing gate is still honoured: the anti-repeat buffer
       stops it firing twice in a row, the per-key cooldown stands, and
       the daily fire cap stands. A guarantee that overrode those would
       just be a different kind of spam. */
    try {
      if((dayState.rollCount || 0) <= 5
         && !isRecentEvent('feedYourOwn')
         && !streamKeyOnCooldown('feedYourOwn')
         && !streamKeyAtDailyCap('feedYourOwn')){
        /* Signed-out players and players who own nothing get null here,
           so this whole branch is invisible to them. */
        var fyEv = pickFeedYourOwnEvent();
        if(fyEv){
          /* Same bookkeeping the normal stream producer does, or the
             cooldown and the 2-per-day cap never see this fire. */
          try {
            markStreamEventFired();
            markStreamKeyFired('feedYourOwn');
            bumpStreamFireCount('feedYourOwn');
          } catch(_){}
          rememberEvent('feedYourOwn');
          dayState.eventFired = true;
          setDayState(dayState);
          delete fyEv._eventKey;
          return fyEv;
        }
      }
    } catch(_){}

    if(refreshesSinceLastEvent <= MIN_ABSOLUTE_GAP && !window.__hohQueueUnlimitedNext){
      setDayState(dayState);
      return null;
    }

    /* Manual refresh: bump the legendary-shop counter (it unlocks at
       100 manual refreshes). Auto refreshes do not bump the legshop
       counter — the shop is a reward for active engagement. */
    if(!isAuto) legshopBumpCount();

    /* ================================================================ */
    /* R140-Z3n: UNIFORM TYPE 1/2/3 DISPATCHER                          */
    /* ================================================================ */
    /* New architecture per user spec. Every refresh rolls a bucket
       (65% Heart Roll, 30% Event Roll, 5% Special) and picks UNIFORMLY
       within that bucket. With ~50 Type 1 entries, the chance of
       seeing any specific themed roll twice in a session is low.
       Fresh Faces is just one Type 1 slot with equal weight to the
       rest. The wallet-empty + first-render Fresh Faces fallbacks
       live downstream in _pickAndRenderInner (when this returns null).
       The dispatcher returns the same shape sentinels the tour uses
       so _pickAndRenderInner can route render appropriately:
         - event object (special render path)
         - { _heartThemedRender: true } (themed phase via renderInto)
         - null (fall through to existing flow for safety nets) */
    /* R148-Z22w: Consume the post-Bustling-Streets queue BEFORE the
       uniform dispatcher runs. The dispatcher's heart-roll bucket
       contains every Bustling Streets variant with equal weight, so
       if it runs first it can roll right back into another BS — the
       exact bug the user reported. By short-circuiting here, the
       BS-followup forces an unlimited event (gambling minigame or
       interactive special) and the dispatcher never gets a chance
       to re-land BS. forceInteractiveEvent's pool is gambling-pool
       only (coin/dice/quiz/duels/brawls/etc) — Bustling Streets is
       NOT in that path, so this also guarantees no BS-on-BS chain.
       Flag is cleared the moment we consume it so subsequent rolls
       continue normally. */
    try {
      if(window.__hohQueueUnlimitedNext){
        window.__hohQueueUnlimitedNext = false;
        var bsFollow = null;
        try { bsFollow = (typeof forceInteractiveEvent === 'function') ? forceInteractiveEvent() : null; } catch(_){}
        if(bsFollow){
          var bsKey = bsFollow._eventKey || bsFollow.type;
          rememberEvent(bsKey);
          if(bsFollow.type === 'classQuiz' || bsFollow.type === 'thievesCoin'){
            markEventSeen(bsKey);
          }
          delete bsFollow._eventKey;
          dayState.eventFired = true;
          setDayState(dayState);
          return bsFollow;
        }
        /* Rare fallback: gambling pool entirely empty. Fall through
           to the normal dispatcher BUT we've already cleared the
           flag — and the dispatcher's _pickHeartRollUniform's
           recent-buffer filter will reject the just-fired BS
           variant. We can't guarantee no other BS variant lands but
           in practice if the gambling pool is empty something is
           genuinely wrong with the day-state. */
      }
    } catch(_){}
    var dispatcherResult = _runUniformDispatcher(isAuto, dayState);
    if(dispatcherResult){
      return dispatcherResult;
    }

    /* Decide whether we MUST force an event this refresh:
        - First-of-day guarantee: by roll #10 of the day, fire one.
        - Dry-spell cap: at most 15 refreshes between events.
        - Interactive cadence: at most INTERACTIVE_FORCE_GAP (10)
          refreshes between coin/dice/quiz/duel/brawl/wheel/pickCard
          events. mustForceInteractive is a stronger ask than mustForce
          — when set, the picker tries the interactive event chain
          first instead of any event. */
    var mustForce = false;
    var mustForceInteractive = false;
    var mustForceUnlimited = false;
    var mustForceSpecialEvent = false;
    var mustForceGamblingPool = false;
    /* R148-Z22u: Consume the post-Bustling-Streets queue flag. Set in
       hohFreshFacesStreetsContinue when a streets event finishes;
       forces the NEXT roll to land an unlimited-pool event (gambling
       minigame or stream event) per user. Cleared HERE the moment the
       picker honors it so the flag never re-fires on subsequent
       rolls — even if the picker ends up not landing an unlimited
       (rare fallback), the flag still clears to avoid stuck state. */
    try {
      if(window.__hohQueueUnlimitedNext){
        mustForce = true;
        mustForceUnlimited = true;
        window.__hohQueueUnlimitedNext = false;
      }
    } catch(_){}
    /* R148-Z22v: Queued-special consumption moved to BEFORE the
       MIN_ABSOLUTE_GAP bail above so milestone-earned events can't
       be smothered by the cadence rule. The block here used to run
       after var declarations but that meant the gap check could
       short-circuit out before this code ran. Kept the comment for
       breadcrumb. */
    if(!dayState.eventFired && dayState.rollCount >= 10) mustForce = true;
    if(refreshesSinceLastEvent >= 15) mustForce = true;
    if(refreshesSinceLastInteractive >= INTERACTIVE_FORCE_GAP){
      mustForce = true;
      mustForceInteractive = true;
    }
    /* R136-event-frequency: per-tier guarantees. Unlimited events
       (gambling minigames + stream events) must fire at least once
       every 5 rolls. Special events (Bazaar, Crossroads, Hero's
       Circle, Marrow's End, Legendary Shop) must fire at least once
       every 20 rolls. The flags below feed into the force-pick chain
       lower in this function. */
    if(refreshesSinceLastUnlimited >= UNLIMITED_FORCE_GAP){
      mustForce = true;
      mustForceUnlimited = true;
    }
    if(refreshesSinceLastSpecialEvent >= SPECIAL_EVENT_FORCE_GAP){
      mustForce = true;
      mustForceSpecialEvent = true;
    }
    /* R136-fix-2: gambling-pool guarantee — every 5 rolls the user MUST
       see a brawl/duel/trivia/dice/coin/wheel/card-pick event. Without
       this dedicated counter, Crossroads / Hero's Circle / Marrow's
       End were satisfying refreshesSinceLastUnlimited (since they're
       all UNLIMITED post R136-fix-3) and the action games never got
       their turn. */
    if(refreshesSinceLastGamblingPool >= GAMBLING_POOL_FORCE_GAP){
      mustForce = true;
      mustForceGamblingPool = true;
    }

    /* Normal picker chain runs on manual refreshes always, OR on auto
       refreshes when we must force. This keeps auto refreshes ambient
       (no surprise events while user is scrolled away or idle) but
       still fires the guarantees. */
    var pickedEvent = null;
    if(!isAuto || mustForce){
      /* Tier 0a — Legendary Shop. Rarest event in the Hall (every ~100
         manual refreshes AND once per week). The picker gates on its
         own counters; null = not eligible. */
      var legshop = pickLegendaryShopEvent();
      if(legshop){
        delete legshop._eventKey;
        pickedEvent = legshop;
      }
      /* R136-fix-2d: PREEMPT Tier 1 with the gambling-pool force.
         Critical bug: Tier 1 (rare-specials natural picker) almost
         always returns SOMETHING because its threshold ladder cascades
         through 100+ entries. So the old `if(!pickedEvent &&
         mustForceGamblingPool)` check after Tier 1 was dead code —
         pickedEvent was already set. Result: the user never saw the
         "guaranteed action event every 5 rolls" they were promised.
         Fix: when the gambling-pool counter is past its gap, fire
         the gambling pool BEFORE Tier 1 even runs. The user's
         "action beat every 5 rolls" guarantee is now real. */
      if(!pickedEvent && mustForceGamblingPool){
        var forcedGpEarly = pickGamblingEventInner();
        if(forcedGpEarly){
          var fgEarlyKey = forcedGpEarly._eventKey || forcedGpEarly.type;
          rememberEvent(fgEarlyKey);
          delete forcedGpEarly._eventKey;
          pickedEvent = forcedGpEarly;
        }
      }
      /* R136-fix-2d: PREEMPT Tier 1 with the special-event force too.
         Same logic as gambling-pool above — Tier 1 would otherwise eat
         the slot before the special-event guarantee runs. */
      if(!pickedEvent && mustForceSpecialEvent){
        var forcedSpEarly = forceSpecialEventOnly();
        if(forcedSpEarly){
          var fsEarlyKey = forcedSpEarly._eventKey || forcedSpEarly.type;
          rememberEvent(fsEarlyKey);
          markEventSeen(fsEarlyKey);
          delete forcedSpEarly._eventKey;
          pickedEvent = forcedSpEarly;
        }
      }
      /* Tier 1 — rare specials. Tried FIRST when their counter is past
         the gap so legendary/crossroads/quiz/portrait don\'t get
         crowded out by the more frequent gambling family. */
      if(!pickedEvent && refreshesSinceLastSpecial >= nextSpecialGap){
        resetSeenSpecialsIfFullyCycled();
        var sp = pickFreshFacesEventInner(false, true);
        if(!sp) sp = pickFreshFacesEventInner(true, true);
        if(!sp) sp = pickFreshFacesEventInner(true, false);
        if(sp){
          var spKey = sp._eventKey || sp.type;
          markEventSeen(spKey);
          rememberEvent(spKey);
          delete sp._eventKey;
          pickedEvent = sp;
        }
      }
      /* Tier 2 — gambling events. Roughly every 4-6 refreshes. */
      if(!pickedEvent && refreshesSinceLastGamble >= nextGambleGap){
        var gb = pickGamblingEventInner();
        if(gb){
          var gbKey = gb._eventKey || gb.type;
          rememberEvent(gbKey);
          delete gb._eventKey;
          pickedEvent = gb;
        }
      }
    }

    /* R136-fix-2: late-tier safety net for the gambling-pool force.
       The pre-emptive call above almost always covers this guarantee,
       but if it returned null (every action event self-gated to null
       in pass 0+1+2 — extremely rare), this second attempt accepts
       any pool entry on its second try. Idempotent if pickedEvent
       was already set above. */
    if(!pickedEvent && mustForceGamblingPool){
      var forcedGp = pickGamblingEventInner();
      if(forcedGp){
        var fgKey = forcedGp._eventKey || forcedGp.type;
        rememberEvent(fgKey);
        delete forcedGp._eventKey;
        pickedEvent = forcedGp;
      }
    }
    /* R136-event-frequency: special-event force (≥1 every 20 rolls).
       Tried after the action-pool force because special events are
       rarer (one every 20 rolls) and the user prioritizes action
       beats. forceSpecialEventOnly loops the inner picker accepting
       only special-tier results; if no eligible special exists right
       now, the next force tier (unlimited / interactive) covers the
       slot. */
    if(!pickedEvent && mustForceSpecialEvent){
      var forcedSpec = forceSpecialEventOnly();
      if(forcedSpec){
        var fsKey = forcedSpec._eventKey || forcedSpec.type;
        rememberEvent(fsKey);
        markEventSeen(fsKey);
        delete forcedSpec._eventKey;
        pickedEvent = forcedSpec;
      }
    }
    /* R136-event-frequency: unlimited-event force (≥1 every 5 rolls)
       — also covers the legacy mustForceInteractive path since both
       point to the gambling pool. forceInteractiveEvent tries the
       gambling pool first (all minigames are unlimited events) then
       falls through to interactive specials (thievesCoin, classQuiz). */
    if(!pickedEvent && (mustForceInteractive || mustForceUnlimited)){
      var forcedInter = forceInteractiveEvent();
      if(forcedInter){
        var fiKey = forcedInter._eventKey || forcedInter.type;
        rememberEvent(fiKey);
        if(forcedInter.type === 'classQuiz' || forcedInter.type === 'thievesCoin'){
          markEventSeen(fiKey);
        }
        delete forcedInter._eventKey;
        pickedEvent = forcedInter;
      }
    }

    /* If nothing fired through the normal chain AND the guarantees
       say we must force, ignore gap gating and pick anything eligible. */
    if(!pickedEvent && mustForce){
      var forced = forceAnyEvent();
      if(forced){
        var fKey = forced._eventKey || forced.type;
        rememberEvent(fKey);
        if(forced.type === 'legendary' || forced.type === 'epic' || forced.type === 'classQuiz'
           || forced.type === 'thievesCoin' || forced.type === 'spectrum' || forced.type === 'familiar'
           || forced.type === 'ffPortrait' || forced.type === 'marrowEnd' || forced.type === 'secondChances'
           || forced.type === 'heroCircle' || forced.type === 'choice' || forced.type === 'rarityTribe'
           || forced.type === 'legendaryTrio' || forced.type === 'banished'
           || forced.type === 'twoLegends' || forced.type === 'championMeeting'){
          markEventSeen(fKey);
        }
        delete forced._eventKey;
        pickedEvent = forced;
      }
    }

    /* R136-event-frequency: the previous "reject rare events when
       cooldown active" block has been REMOVED. The new system uses
       force-fire counters (refreshesSinceLastUnlimited / SpecialEvent)
       to GUARANTEE minimum frequency rather than block events. Themed
       Fresh Faces fill the gaps; events fill the rest at the user-
       specified cadence. */

    /* R136-fix-2d: Debug breadcrumb. Uncomment the window assignment
       below and open the console to verify the picker is firing the
       right tiers. The window.hohPickHistory ring buffer keeps the
       last 30 picks so you can inspect cadence in real time:
         window.hohPickHistory  // → array of {type, fromForce, roll}
       Off by default to keep production console clean. */
    /*
    try {
      window.hohPickHistory = window.hohPickHistory || [];
      window.hohPickHistory.push({
        type: pickedEvent ? pickedEvent.type : 'null',
        rc: dayState.rollCount,
        gpCounter: refreshesSinceLastGamblingPool,
        spCounter: refreshesSinceLastSpecialEvent,
        forces: { gp: mustForceGamblingPool, sp: mustForceSpecialEvent, ul: mustForceUnlimited, ia: mustForceInteractive }
      });
      if(window.hohPickHistory.length > 30) window.hohPickHistory = window.hohPickHistory.slice(-30);
    } catch(_){}
    */

    if(pickedEvent){
      refreshesSinceLastEvent = 0;
      /* Reset the relevant family counter so the natural cadence
         resumes from this point.
         R136-fix-2d: Use isGamblingPoolEvent() instead of the
         hardcoded subset. The previous list missed all the trivia
         events (classQuiz, guessFB, etc.) — those
         silently reset the SPECIAL counter instead of GAMBLE,
         which made the natural Tier 2 gambling tier fire LESS
         often after a trivia event because special-tier-eligibility
         was advancing while gamble-tier-eligibility was reset.
         Now any gambling-pool event resets the gambling counter. */
      if(isGamblingPoolEvent(pickedEvent)){
        refreshesSinceLastGamble = 0;
        rollNextGambleGap();
      } else {
        refreshesSinceLastSpecial = 0;
        rollNextSpecialGap();
      }
      /* Reset the interactive counter when ANY interactive event
         fires (gambling pool + thievesCoin + classQuiz). */
      if(isInteractiveEvent(pickedEvent)){
        refreshesSinceLastInteractive = 0;
      }
      /* R136-event-frequency: reset per-tier counters when matching
         tier fires. Themed/showcase events don't reset these. */
      if(isUnlimitedEvent(pickedEvent)){
        refreshesSinceLastUnlimited = 0;
      }
      if(isSpecialEvent(pickedEvent)){
        refreshesSinceLastSpecialEvent = 0;
      }
      /* R136-fix-2: gambling-pool action events reset their dedicated
         counter independent of the unlimited/special tier. */
      if(isGamblingPoolEvent(pickedEvent)){
        refreshesSinceLastGamblingPool = 0;
      }
      dayState.eventFired = true;
      setDayState(dayState);
      return pickedEvent;
    }

    setDayState(dayState);
    return null;
  }
  /* Gambling-event picker. Returns null if no event is eligible
     (e.g. user can't afford the entry cost). New gambling events
     plug in as additional branches here following the same shape
     as the dice trader. */
  function pickGamblingEventInner(){
    /* Affordability gate — most events here are free (the new R55
       guess events) so even a 0-heart user can play. We keep the
       cheapest-cost gambles available too. */
    var bank = (typeof window.heartsRemainingToday === 'function') ? window.heartsRemainingToday() : 0;
    /* R55: pickers paired with their event-key so we can apply the
       anti-repeat buffer here too. The previous bug: the gambling
       pool didn\'t check isRecentEvent, so championDuel could fire
       twice in a row. Now each picker is gated on the same recent
       buffer the rare-special tier uses. Each picker also self-gates
       on its own affordability/daily-cap rules so capped or
       unaffordable events return null and we move to the next. */
    var entries = [
      ['diceTrader',     pickDiceTraderEvent],
      ['championDuel',   pickChampionDuelEvent],
      /* R86 / R93: Tiered duel variants. Rare (1/5), Epic (5/10),
         Legendary (10/30), Mythic (20/50). All d20. The duel FAMILY
         anti-repeat above ensures only one duel fires per anti-repeat
         window regardless of which tier. */
      ['rareDuel',       pickRareDuelEvent],
      ['epicDuel',       pickEpicDuelEvent],
      ['legendaryDuel',  pickLegendaryDuelEvent],
      ['mythicDuel',     pickMythicDuelEvent],
      /* R140-Z: Crown Match — high-stakes legendary duel (20 → 100). */
      ['crownMatch',     pickCrownMatchEvent],
      ['tavernBrawl',    pickTavernBrawlEvent],
      ['tavernBrawl4',   pickTavernBrawl4Event],
      /* R119-H: 8-fighter d20 melee. +50 win / -1 loss. EV +5.25. */
      ['barFight',       pickBarFightEvent],
      ['spinWheel',      pickSpinWheelEvent],
      ['pickACard',      pickPickACardEvent],
      /* R86: New unlimited events. */
      ['tollRoad',       pickTollRoadEvent],
      ['carnival',       pickCarnivalEvent],
      ['commonThread',   pickCommonThreadEvent],
      /* R150: The Class War — head-to-head class/race recruitment event. */
      ['classWar',       pickClassWarEvent],
      /* R97: Travelling Healer + Bard — free pick-a-card with all
         good outcomes (5/10/30 hearts). Brand-pink border. */
      ['travelHealer',   pickTravelingHealerEvent],
      ['travelBard',     pickTravelingBardEvent],
      /* R55 unlimited free guess events — all gated by anti-repeat. */
      ['popularity',     pickPopularityEvent],
      ['guessFB',        pickGuessFighterBarbarianEvent],
      ['guessWW',        pickGuessWizardWarlockEvent],
      ['guessRR',        pickGuessRangerRogueEvent],
      /* R87: Race-binary "Elf or Half-Elf" event. */
      ['guessEHE',       pickGuessElfHalfElfEvent],
      ['findCulprit',    pickFindCulpritEvent],
      ['findCulpritRace', pickFindCulpritRaceEvent],
      /* R120-D: 3-character class imposter — faster than 6-card culprit. */
      ['findImposter',   pickFindImposterEvent],
      /* R148-Z23: The Echo — pick the most memorable of 3 hearted heroes.
         R383 (Jan): REMOVED from the rotation. The picker, renderer, and
         handlers stay dormant below so a player mid-event at deploy time
         finishes cleanly; the event simply never fires again. */
      /* R148-Z29: Pay the Guide — bard offers a paid trip to a bazaar
         (10h) or the legendary shop (50h). Independent cooldown. */
      ['payGuide',       pickGuideEvent],
      /* R124-I: Familiar Crowd. R387 (Jan): REMOVED from the rotation — the
         picker and renderer stay dormant below so a player mid-event at
         deploy time finishes cleanly; it simply never fires again. */
      /* ⛔ R590 (Jan): THE EIGHTEEN BUSTLING STREETS ENTRIES ARE GONE
         FROM THIS POOL, AND THIS IS THE FIX FOR "I WANT MORE EVENT
         ROLLS."

         Jan: "I want it so that they should see more event rolls in
         general." That read as a taste request. It was not. It was a
         measurement.

         Every bustlingStreets* variant is a HEART rail: the player
         spends hearts on a scrolling row of portraits. All eighteen are
         registered ROLL_TYPE_HEART (see ROLL_TYPE_REGISTRY around line
         133) and all eighteen were ALREADY in the heart bucket's own
         entries list in _runUniformDispatcher. They sat in BOTH pools.

         MEASURED before this change: this array held 48 entries and 18
         of them were heart rails, so 37.5% of everything the "event"
         bucket could return was not an event at all. Combined with the
         MIN_ABSOLUTE_GAP bail (which returns null on roughly every
         other refresh) and BUCKET_EVENT = 0.32, a real stakes event
         landed on about one refresh in six and a half.

         Deleting them here roughly DOUBLES the real-event rate and
         touches no probability constant, no threshold and no bucket
         weight. It is a de-duplication, not a rebalance, which is why
         it is the safest of the three available levers.

         The rails themselves are untouched and still fire at exactly
         the rate they always did, from the heart bucket where they
         belong. The player loses nothing.

         ⛔ Their keys stay in GAMBLING_POOL_TYPES, GAMBLING_POOL_SUBTYPE
         and EVENT_FAMILIES on purpose. Those tables only drive
         anti-repeat lookups, and a key that resolves to nothing there
         is harmless, while REMOVING a key that another table still
         names is the exact orphan-key bug that has already shipped
         twice in this file (mythicDuelSpecial is registered in 5 of 11
         tables today, crownMatch is missing from UNLIMITED_EVENT_TYPES). */
      ['coinBet',        pickCoinBetEvent],
      /* R58: Hero Quiz moved here from the special pool — fires on
         the gambling cadence with the rest of the unlimited
         interactives. Penalty-per-wrong wired in the submit handler. */
      ['classQuiz',      pickClassQuizEvent],
      /* R65: Mini Spinner — unlimited 4-segment wheel. Cost 5,
         segments [0, 3, 7, 10]. Mythic host eligible. */
      ['miniSpinner',    pickMiniSpinnerEvent],
      /* R136-events: Lost Adventurer + A Star Falls re-added per
         user direction. Both are kind events that always grant
         hearts — Lost Adventurer is a 3-tile direction pick (right
         = +25, wrong = +5 kindness fallback), A Star Falls is a
         tap-to-catch shooting-star with weighted 10/30/50 reward
         (caught) or +10 consolation (missed). */
      /* R136-revert: lostAdventurer + starFalls removed per user — the
         minigame events feel out of place. The picker entries above are
         the full eligible gambling pool now. */
    ];
    /* Fisher-Yates shuffle so we don\'t bias toward the first
       entries on every fire. */
    for(var i = entries.length - 1; i > 0; i--){
      var j = Math.floor(Math.random() * (i + 1));
      var tmp = entries[i]; entries[i] = entries[j]; entries[j] = tmp;
    }
    /* R102: Two-pass scan to fix the dilution bug. With ~28 entries in
       the gambling pool and Fisher-Yates shuffle picking the first
       eligible match, each individual event had only ~3.6% odds per
       gambling fire — so toll road, carnival, and others were rarely
       appearing. The two-pass scan first tries entries the user
       hasn't seen today, only falling back to repeats once everyone
       has had a turn. With ~28 events and ~20 gambling fires per
       session, this guarantees broad coverage. The session-seen set
       lives in dayState alongside the existing showcase tracker.
       R136-fix-2b: NEW Pass 0 prefers entries from a SUBTYPE the user
       hasn't seen recently (trivia / brawl / duel / dice / wheel /
       travel / scene). This is the actual fix for "I keep seeing
       brawls but no trivia" — pure random gives ~44% trivia in
       theory, but with 32 entries some subtypes get crowded. Pass 0
       guarantees subtype rotation. */
    var ds = (typeof getDayState === 'function') ? (getDayState() || {}) : {};
    var gambleSeen = ds.gambleSeen || {};
    /* Pass 0: prefer entries with a SUBTYPE not in the recent buffer.
       The user's biggest perceived gap is variety across subtypes;
       this pass ensures e.g. a trivia event lands at least once
       every 3-4 gambling fires regardless of what the random shuffle
       happened to surface. */
    for(var k0 = 0; k0 < entries.length; k0++){
      var key0 = entries[k0][0];
      var pickFn0 = entries[k0][1];
      if(typeof pickFn0 !== 'function') continue;
      if(isRecentEvent(key0)) continue;
      if(isRecentFamily(key0)) continue;
      if(isRecentSubtype(key0)) continue;
      if(gambleSeen[key0]) continue;
      try {
        var ev0 = pickFn0();
        if(ev0){
          gambleSeen[key0] = 1;
          ds.gambleSeen = gambleSeen;
          if(typeof setDayState === 'function') setDayState(ds);
          rememberGamblingSubtype(key0);
          return ev0;
        }
      } catch(_){}
    }
    /* Pass 1: prefer entries with the same anti-repeat gates AND not
       yet seen this session. */
    for(var k = 0; k < entries.length; k++){
      var key = entries[k][0];
      var pickFn = entries[k][1];
      if(typeof pickFn !== 'function') continue;
      if(isRecentEvent(key)) continue;
      if(isRecentFamily(key)) continue;
      if(gambleSeen[key]) continue;
      try {
        var ev = pickFn();
        if(ev){
          gambleSeen[key] = 1;
          ds.gambleSeen = gambleSeen;
          if(typeof setDayState === 'function') setDayState(ds);
          rememberGamblingSubtype(key);
          return ev;
        }
      } catch(_){}
    }
    /* Reset the seen set so pass 2 isn't permanently blocked once
       every event has fired once today. */
    var totalEntries = entries.length;
    var seenCount = 0;
    for(var sk in gambleSeen){ if(gambleSeen.hasOwnProperty(sk)) seenCount++; }
    if(seenCount >= totalEntries){
      gambleSeen = {};
      ds.gambleSeen = gambleSeen;
      if(typeof setDayState === 'function') setDayState(ds);
    }
    /* Pass 2: regular flow — skip recent buffer + family, accept the
       first eligible entry regardless of seen state. This guarantees
       we still return a fresh event after the seen set has cycled. */
    for(var k2 = 0; k2 < entries.length; k2++){
      var key2 = entries[k2][0];
      var pickFn2 = entries[k2][1];
      if(typeof pickFn2 !== 'function') continue;
      if(isRecentEvent(key2)) continue;
      if(isRecentFamily(key2)) continue;
      try {
        var ev2 = pickFn2();
        if(ev2){
          gambleSeen[key2] = 1;
          ds.gambleSeen = gambleSeen;
          if(typeof setDayState === 'function') setDayState(ds);
          rememberGamblingSubtype(key2);
          return ev2;
        }
      } catch(_){}
    }
    return null;
  }
  function pickFreshFacesEventInner(ignoreSeen, avoidRecent){
    /* Helper to decide whether a candidate key should be skipped
       given the seen / recent filters. Centralizes the gate so each
       branch reads cleanly. */
    function gate(key){
      if(!ignoreSeen && isEventSeen(key)) return false;
      if(avoidRecent && isRecentEvent(key)) return false;
      return true;
    }
    var roll = Math.random() * 100;
    var threshold = 0;

    /* Legendary Encounter — 2 or 3 legendaries side by side, gold halo.
       R140-Z: Was a single-card showcase; the user wanted more presence,
       so we draw 2-3 chars from the legendary pool depending on how
       many are available. Falls back to 1 if the pool only has one
       legendary so the encounter still fires for thin halls. */
    threshold += 3;
    if(roll < threshold && gate('legendary')){
      var legendaries = poolByTier('legendary');
      if(legendaries.length){
        var shuffled = hfShuffle(legendaries);
        var n = legendaries.length >= 3 ? (Math.random() < 0.45 ? 3 : 2)
              : (legendaries.length >= 2 ? 2 : 1);
        var picked = shuffled.slice(0, n);
        return { type: 'legendary', chars: picked, _eventKey: 'legendary' };
      }
    }

    /* Legendary Trio — 3 legendary heroes shoulder-to-shoulder.
       Rarer than the single legendary because it requires three
       legendary candidates in the pool. Cinematic stagger entry. */
    threshold += 2;
    if(roll < threshold && gate('legendaryTrio')){
      var legPoolTrio = poolByTier('legendary');
      if(legPoolTrio.length >= 3){
        var trioShuf = hfShuffle(legPoolTrio).slice(0, 3);
        return { type: 'legendaryTrio', chars: trioShuf, _eventKey: 'legendaryTrio' };
      }
    }

    /* R54: Two Legends — pick one of two legendary cards. Lighter
       weight than legendaryTrio because it only needs 2 legendaries
       in pool. Pure visual moment — no cost, no betting. */
    threshold += 3;
    if(roll < threshold && gate('twoLegends')){
      var twoPool = poolByTier('legendary');
      if(twoPool.length >= 2){
        var twoShuf = hfShuffle(twoPool).slice(0, 2);
        return { type: 'twoLegends', chars: twoShuf, _eventKey: 'twoLegends' };
      }
    }

    /* R54: Champion Meeting — single card showcase of the current
       featured/spotlight winner. Treated as a rare special so it
       doesn\'t get crowded out.
       R119-I: Daily cap dropped. The Champion can reappear occasionally
       across the day, gated only by the rare-special threshold (small
       per-roll probability) and the 4-event anti-repeat buffer. */
    threshold += 2;
    if(roll < threshold && gate('championMeeting')){
      var allCharsCM = (typeof window.hohGetAllCharacters === 'function') ? (window.hohGetAllCharacters() || []) : [];
      var champion = allCharsCM.find(function(c){ return c && c.featured; });
      if(champion){
        return { type: 'championMeeting', char: champion, _eventKey: 'championMeeting' };
      }
    }

    /* ⛔ ============ R554 · THE FF SPOTLIGHT IS RETIRED ============
       Jan: "one of them show off a character card in the explore the hall,
       remove that event from the hall of heroes in total so that it no longer
       presents a card in the explore the hall."
       He sent two screenshots of it and BOTH were broken, in two different
       ways, and neither was cosmetic:
       ONE, THE CARD RENDERED MIRRORED. `.hoh-ffspot-back` carries
       `backface-visibility:hidden` and the FRONT face never did, so once the
       flip passed 90 degrees the front stayed painted and you read the hero's
       name backwards.
       TWO, THE CARD DID NOT FIT. This stage put a real `.ffshop-cardbox` on
       it, and that box is a native 380px wide. Every rule that scales it down
       is scoped to `.ffshop-insp`, the shop's own inspector, which this stage
       is not inside — so `--ffcard-k` was never defined here and the card
       painted at 380px inside a 310px stage. The comment left below at
       `.hoh-ffspot-stage-card` states the opposite as fact ("the shop's own
       <=430px rule transform-scales it"): it was an assumption, never true,
       and it is why the legend was clipped mid-word on Jan's phone.
       THE PRODUCER IS WHAT GOES. Removing the branch is what makes this
       total: `pickSpecial` is the only thing that can mint the event, so with
       it gone no code path anywhere can reach the renderer.
       NOTE FOR WHOEVER READS THIS NEXT: the other single-card stages
       (championMeeting, legendary) are NOT affected by either bug. They use
       the Hall's own `cardHtml` on `.hoh-fresh-special-stage-solo`, which is
       sized by hall-of-heroes.css and never touches the shop's card.
       ⛔ AND THE `threshold += 3` WENT WITH IT, DELIBERATELY. This is a
       cumulative-probability ladder: each entry adds its own slice and tests
       `roll < threshold`. Keeping the += while deleting the branch would have
       left three units of DEAD BAND that match no event and silently fall
       through, which is a worse bug than the one being fixed because it looks
       like nothing. Removing both gives every event after this one exactly the
       width it had before, and returns the Spotlight's own share to the
       ordinary rail — which is what retiring an event should mean. */

    /* Epic Encounter — 3 epics, purple aura. */
    threshold += 6;
    if(roll < threshold && gate('epic')){
      var epics = poolByTier('epic');
      if(epics.length >= 3){
        var epicShuf = hfShuffle(epics);
        return { type: 'epic', chars: epicShuf.slice(0, Math.min(3, epicShuf.length)), _eventKey: 'epic' };
      }
    }

    /* Thieves Coin — coin flip mini-game.
       R71: Adds a host character. Mythic roll wins first (rare),
       else a random Rogue from the existing pool blocks the door.
       R119-I: Daily cap dropped. The threshold delta below is the
       sole pacing mechanism + the 4-event anti-repeat buffer keeps
       it from firing back-to-back. */
    threshold += 5;
    if(roll < threshold && gate('thievesCoin')){
      var rogues = poolByClass('Rogue');
      if(rogues.length >= MIN_THEME_CHARS){
        var sneakyMythicHost = (typeof window.hohRollMythicHost === 'function') ? window.hohRollMythicHost() : null;
        var sneakyHost = sneakyMythicHost || rogues[Math.floor(Math.random() * rogues.length)];
        return {
          type: 'thievesCoin',
          rogues: rogues,
          host: sneakyHost,
          mythicHost: !!sneakyMythicHost,
          _eventKey: 'thievesCoin'
        };
      }
    }

    /* R58: classQuiz moved to the gambling pool — it lives in
       pickGamblingEventInner now and fires on the gambling cadence
       with the rest of the unlimited interactives. The special-tier
       slot is intentionally empty so the rare-special pool stays
       focused on showcase + once-per-day events. */

    /* Spectrum — one of each rarity tier in ascending order. Reads
       like a procession from common to legendary. Only fires when
       the pool has at least one of every tier. */
    threshold += 4;
    if(roll < threshold && gate('spectrum')){
      var spec = pickSpectrum();
      if(spec){ spec._eventKey = 'spectrum'; return spec; }
    }

    /* Rarity tribes — each tier gets its own cap so the section
       reads at the right "scale": the crowded town is genuinely
       crowded (4 rows), travelers feel like a camp (3 rows),
       renowned heroes stay select (2 rows), and epics are a
       short procession (2 rows). The caps live here, not in MAX_VISIBLE,
       because the user wants intuition: more cards = busier place,
       fewer cards = rarer gathering. */

    /* All Common — "A Crowded Town" (4 rows). */
    threshold += 6;
    if(roll < threshold && gate('rarityTribe-common')){
      var commons = poolByTier('common');
      if(commons.length >= 6){
        var cShuf = hfShuffle(commons).slice(0, 20);
        return { type: 'rarityTribe', tier: 'common', chars: cShuf,
          eyebrow: 'A Crowded Town',
          sub: 'You step into a market square. Voices, footsteps, the smell of bread.',
          _eventKey: 'rarityTribe-common' };
      }
    }

    /* All Uncommon — "The Travelers' Camp" (3 rows). */
    threshold += 5;
    if(roll < threshold && gate('rarityTribe-uncommon')){
      var uncommons = poolByTier('uncommon');
      if(uncommons.length >= 6){
        var uShuf = hfShuffle(uncommons).slice(0, 15);
        return { type: 'rarityTribe', tier: 'uncommon', chars: uShuf,
          eyebrow: 'The Travelers\' Camp',
          sub: 'A gathering of names with stories worth a second telling.',
          _eventKey: 'rarityTribe-uncommon' };
      }
    }

    /* All Rare — "The Renowned Lodge" (2 rows). */
    threshold += 4;
    if(roll < threshold && gate('rarityTribe-rare')){
      var rares = poolByTier('rare');
      if(rares.length >= 6){
        var rShuf = hfShuffle(rares).slice(0, 10);
        return { type: 'rarityTribe', tier: 'rare', chars: rShuf,
          eyebrow: 'The Renowned Lodge',
          sub: 'Heroes whose names travel ahead of them. The lodge keeper knows every one.',
          _eventKey: 'rarityTribe-rare' };
      }
    }

    /* All Epic — "The Council of Renown" (2 rows). Distinct from the
       Epic Encounter (3 cards above) because it gathers a wider field
       of epic-tier heroes side by side — closer to a delegation than
       a trio meeting.
       R136-B-row-rebalance: bumped 8 → 10 so it lands on a clean 2-row
       grid (5 cards/row desktop) instead of the awkward 1.6 rows. */
    threshold += 3;
    if(roll < threshold && gate('rarityTribe-epic')){
      var epicTribe = poolByTier('epic');
      if(epicTribe.length >= 5){
        var eShuf = hfShuffle(epicTribe).slice(0, 10);
        return { type: 'rarityTribe', tier: 'epic', chars: eShuf,
          eyebrow: 'The Council of Renown',
          sub: 'Names spoken in the same breath. The hall stills when they enter.',
          _eventKey: 'rarityTribe-epic' };
      }
    }

    /* R136-B: New themed phases. Each is a color-themed gathering of
       heroes the user can heart on the rail (same shape as rarityTribe
       so renderRarityTribeHtml + tier CSS hook both work for free).
       The tier key doubles as the CSS palette suffix
       (.hoh-fresh-tribe-twilight, .hoh-fresh-tribe-healers, etc.).
       Each entry self-gates on having enough matching chars in the
       hall, so themes the hall can't populate are silently skipped
       and the next theme tries — no empty rails. The cumulative
       weight of these phases (5+5+4+4+5+3+3 = 29) is roughly the
       same as all four rarity tribes combined, so themed character
       refreshes become much more frequent in rotation.
       R136-B-row-rebalance: ALL new themed phases sit at 2 rows
       (10 chars on desktop, 5 cards/row). The 3-row and 4-row
       cases live on the rarity tribes only (Travelers' Camp = 3
       rows, Crowded Town = 4 rows) so the user reads bigger rails
       as RARE moments — most refreshes are now a tight 2-row
       gathering. */
    var themedPhases = [
      { key: 'twilight',  weight: 5, eyebrow: 'The Twilight Walkers',
        sub: 'Heroes the moonlight remembers. Drow, Tiefling, and Half-Elf walk together tonight.',
        match: function(c){ return c && c.race && (c.race === 'Drow' || c.race === 'Tiefling' || c.race === 'Half-Elf' || c.race === 'Dark Elf'); },
        min: 5, target: 10 },
      { key: 'healers',   weight: 5, eyebrow: 'The Healers\' Round',
        sub: 'A circle of mending hands. Clerics, Druids, and Monks share the lantern\'s light.',
        match: function(c){ return c && c.character_class && (c.character_class === 'Cleric' || c.character_class === 'Druid' || c.character_class === 'Monk'); },
        min: 5, target: 10 },
      { key: 'vanguard',  weight: 4, eyebrow: 'The Iron Vanguard',
        sub: 'Shields up, banners down. The hall\'s steel marches in.',
        match: function(c){ return c && c.character_class && (c.character_class === 'Fighter' || c.character_class === 'Paladin' || c.character_class === 'Barbarian'); },
        min: 5, target: 10 },
      { key: 'mages',     weight: 4, eyebrow: 'The Veiled Mages',
        sub: 'Ink, glass, and sigil. The arcane wing assembles.',
        match: function(c){ return c && c.character_class && (c.character_class === 'Wizard' || c.character_class === 'Sorcerer' || c.character_class === 'Warlock'); },
        min: 5, target: 10 },
      { key: 'beastfolk', weight: 5, eyebrow: 'The Beastfolk Glade',
        sub: 'Fur, feather, and scale. The wilder bloodlines step into the hall.',
        match: function(c){
          if(!c || !c.race) return false;
          var r = c.race;
          return r === 'Tabaxi' || r === 'Lizardfolk' || r === 'Loxodon' || r === 'Tortle' || r === 'Aarakocra' || r === 'Centaur' || r === 'Goliath' || r === 'Minotaur' || r === 'Leonin' || r === 'Owlfolk' || r === 'Goblin' || r === 'Kobold' || r === 'Bugbear' || r === 'Hobgoblin' || r === 'Orc' || r === 'Half-Orc';
        },
        min: 5, target: 10 },
      { key: 'quietest',  weight: 3, eyebrow: 'The Quietest Voices',
        sub: 'Heroes the hall hasn\'t lifted yet. Lend a heart, and they ring.',
        /* Sorted ASC by hearts later — picks the LEAST-hearted in the
           hall so this theme always rotates through underdogs. */
        match: function(){ return true; },
        sortMode: 'ascHearts',
        min: 6, target: 10 },
      { key: 'brightest', weight: 3, eyebrow: 'The Brightest Stars',
        sub: 'The hall\'s most-loved names, gathered for one more bow.',
        match: function(){ return true; },
        sortMode: 'descHearts',
        min: 6, target: 10 }
    ];
    /* Hoist the base pool snapshot — every theme filters from the same
       set, so doing this once per refresh saves N character iterations
       on hall scaling up to thousands. */
    var themedBasePool = null;
    for(var ti = 0; ti < themedPhases.length; ti++){
      var theme = themedPhases[ti];
      threshold += theme.weight;
      var themeKey = 'themedPhase-' + theme.key;
      if(roll < threshold && gate(themeKey)){
        /* Pull from the base pool so dismissed/heartIntent are honored
           but hearted heroes are allowed back (re-encounters add depth
           to the rail). Pool exhaustion safety: if the matching pool
           is below `min`, the theme is silently skipped and the next
           threshold tier tries. With 7 themes plus the existing rarity
           tribes, at least one usually populates. */
        if(!themedBasePool) themedBasePool = _basePool();
        var matchPool = themedBasePool.filter(theme.match);
        if(matchPool.length >= theme.min){
          var picked;
          if(theme.sortMode === 'ascHearts'){
            picked = matchPool.slice().sort(function(a,b){ return (a.hearts||0) - (b.hearts||0); }).slice(0, theme.target);
          } else if(theme.sortMode === 'descHearts'){
            picked = matchPool.slice().sort(function(a,b){ return (b.hearts||0) - (a.hearts||0); }).slice(0, theme.target);
          } else {
            picked = hfShuffle(matchPool).slice(0, theme.target);
          }
          return { type: 'rarityTribe', tier: theme.key, chars: picked,
            eyebrow: theme.eyebrow, sub: theme.sub, _eventKey: themeKey };
        }
      }
    }

    /* Familiar Faces — only the user's hearted heroes. Capped at 2
       rows (10) so the section reads as a quick re-encounter, not a
       full back-catalog dump. */
    threshold += 5;
    if(roll < threshold && gate('familiar')){
      var hearted = poolHearted();
      if(hearted.length >= 3){
        var hShuf = hfShuffle(hearted).slice(0, 10);
        return { type: 'familiar', chars: hShuf, _eventKey: 'familiar' };
      }
    }

    /* FF Portrait Gallery — branded, with subtle waitlist CTA. Capped
       to a single gallery row (5 cards) sorted by hearts descending so
       the most-loved portraits lead the wing. Lower weight (2 vs 4)
       so the gallery feels rare and earned — the user shouldn't see
       this branded event back-to-back.
       R113-C: Also gated by ffShowcaseGated() — if Portrait Gallery
       fired within the last 10 rolls (or vice versa), skip. The two
       FF surfaces share the same 10-roll buffer. */
    threshold += 2;
    if(roll < threshold && gate('ffPortrait') && !ffShowcaseGated()){
      var ffs = poolFf();
      if(ffs.length >= 3){
        var ffSorted = ffs.slice().sort(function(a, b){
          return (b.hearts || 0) - (a.hearts || 0);
        }).slice(0, 5);
        try { markFfShowcaseFired(); } catch(_){}
        return { type: 'ffPortrait', chars: ffSorted, _eventKey: 'ffPortrait' };
      }
    }

    /* Bounty to Marrow's End — quest CTA, opens character quest page.
       R120-B: Weekly cap on acceptance. Once the user takes the +5
       hearts, the bounty hides for the rest of the ISO week. Decline
       still goes through the anti-repeat buffer and can reappear in a
       few rolls. */
    threshold += 4;
    if(roll < threshold && gate('marrowEnd') && !marrowAcceptedThisWeek()){
      return { type: 'marrowEnd', _eventKey: 'marrowEnd' };
    }

    /* Second Chances — surfaces dismissed (X'd) heroes. */
    threshold += 5;
    if(roll < threshold && gate('secondChances')){
      var dismissedIds = getDismissed();
      if(dismissedIds && dismissedIds.length >= 4){
        var allChars = (typeof window.hohGetAllCharacters === 'function') ? window.hohGetAllCharacters() : [];
        var byId = {}; allChars.forEach(function(c){ if(c && c.id) byId[c.id] = c; });
        var resurrected = dismissedIds.map(function(id){ return byId[id]; }).filter(Boolean);
        if(resurrected.length >= 4){
          /* 3 rows max — the user is reconsidering, not auditing every
             dismissal at once. */
          var scShuf = hfShuffle(resurrected).slice(0, 15);
          return { type: 'secondChances', chars: scShuf, _eventKey: 'secondChances' };
        }
      }
    }

    /* Banished Heroes — every dismissed hero, all locked at start.
       The user clicks the green + on each card to undo the banish.
       Differs from Second Chances (which lets them heart back) by
       being bulk and visual: a graveyard the user walks through and
       resurrects at will. */
    threshold += 3;
    if(roll < threshold && gate('banished')){
      var bannedIds = getDismissed();
      if(bannedIds && bannedIds.length >= 3){
        var allChars2 = (typeof window.hohGetAllCharacters === 'function') ? window.hohGetAllCharacters() : [];
        var byId2 = {}; allChars2.forEach(function(c){ if(c && c.id) byId2[c.id] = c; });
        var banished = bannedIds.map(function(id){ return byId2[id]; }).filter(Boolean);
        if(banished.length >= 3){
          /* 3 rows max for the graveyard walk-through. */
          var bShuf = hfShuffle(banished).slice(0, 15);
          return { type: 'banished', chars: bShuf, _eventKey: 'banished' };
        }
      }
    }

    /* Hero's Circle — R120-A: Daily cap REINSTATED but only on
       completion. The user can SEE the Circle multiple times in a day
       (subject to anti-repeat) but once they've ENTERED it once, the
       picker hides it for the rest of the day. The mark fires from
       the Enter Circle handler, not when the event is shown. */
    threshold += 3;
    if(roll < threshold && gate('heroCircle') && !heroCircleUsedToday() && typeof window.hohOpenTrial === 'function'){
      return { type: 'heroCircle', _eventKey: 'heroCircle' };
    }

    /* Crossroads Choice — pick between two destinations. */
    threshold += 7;
    if(roll < threshold && gate('choice')){
      var options = pickChoiceOptions();
      if(options) return { type: 'choice', options: options, _eventKey: 'choice' };
    }

    /* R116-C / R118-B: The Bazaar — themed shop with 4 buyable items
       and an in-fiction host presenting the wares. Cooldown: every
       MAGE_BAZAAR_BUFFER rolls regardless of which bazaar flavor
       rolled (shared 'mb' bucket).
       R148-Z24: Randomized across 3 bazaar kinds (Mage / Thieves /
       Sage). Each has its own items, host pool, and copy. The shared
       cooldown means the user sees ~one bazaar every 25 rolls but
       rotates flavor across visits. */
    threshold += 2;
    if(roll < threshold && !mageBazaarOnCooldown()){
      try { markMageBazaarFired(); } catch(_){}
      var bazaarKinds = ['mage','thieves','sage'];
      var pickKind = bazaarKinds[Math.floor(Math.random() * bazaarKinds.length)];
      var bzEvent = _buildBazaarEvent(pickKind);
      if(bzEvent) return bzEvent;
      /* Fallback to mage if build returned null (shouldn't happen). */
      return _buildBazaarEvent('mage');
    }

    /* R115-B: Stream events grouped. Underdog, Rich Get Richer, and
       Feed Your Own all share the same press-and-hold mechanic. Three
       separate picker entries meant they could fire back-to-back and
       reused the exact same UX, which felt boring. Now: one combined
       category that fires at most once per STREAM_EVENT_BUFFER rolls.
       When it fires, randomly picks ONE of the three based on which
       are eligible right now (mythic pool exists, signed-in user has
       a hero, etc.). The buffer gates against ANY stream event in the
       last N rolls — not just the same key — so the user gets a real
       break between stream beats. */
    /* R136-fix-2c: Stream weight dropped 3 → 2. Streams were firing
       too often relative to every other ladder entry (3% slice + cascade
       fallback when earlier entries failed self-checks). 2% halves the
       direct-hit rate; cascade fallthrough is now also gated by
       per-key cooldown below so Underdog specifically can't spam. */
    threshold += 2;
    if(roll < threshold && !streamEventOnCooldown()){
      var streamCandidates = [];
      /* R136-event-frequency: filter each stream candidate by
         isRecentEvent so a recent fire (e.g. 'underdog') can't be
         re-picked back-to-back.
         R136-fix-2c: ALSO filter by streamKeyOnCooldown so a stream
         key can't fire within STREAM_PER_KEY_BUFFER (25 rolls) even
         if the global recent buffer rolled past it. This is the
         actual fix for Underdog spam — the global recent buffer is
         only 6 deep, so by roll #7 underdog was eligible again. */
      /* Underdog candidate. R140-D: Also check the hard 2-per-day fire
         cap before adding to candidates. */
      try {
        if(!isRecentEvent('underdog') && !streamKeyOnCooldown('underdog') && !streamKeyAtDailyCap('underdog')){
          var allCharsU = (typeof window.hohGetAllCharacters === 'function') ? window.hohGetAllCharacters() : [];
          var underdog = pickUnderdogChar(allCharsU);
          if(underdog){
            streamCandidates.push({
              type: 'underdog', char: underdog, _eventKey: 'underdog', windowSec: 14
            });
          }
        }
      } catch(_){}
      /* R119-D: Rich Get Richer dropped from the stream candidate
         pool. Underdog + Feed Your Own already cover the press-and-
         hold mechanic; a third option felt redundant. The renderer
         and event code remain in case we want to bring it back. */
      /* Feed Your Own candidate (needs signed-in user with a submitted hero).
         R140-D: 2-per-day fire cap applied alongside the cooldown so the
         event can't dominate a long session. Also carries an in-event
         heart-spend cap of 100 — see FEED_YOUR_OWN_HEART_CAP usage in
         wireHeartStreamTargets. */
      try {
        if(!isRecentEvent('feedYourOwn') && !streamKeyOnCooldown('feedYourOwn') && !streamKeyAtDailyCap('feedYourOwn')){
          /* R590 (Jan): offer up to five, not one. */
          var ownFY = pickFeedYourOwnEvent();
          if(ownFY) streamCandidates.push(ownFY);
        }
      } catch(_){}
      if(streamCandidates.length){
        var streamPick = streamCandidates[Math.floor(Math.random() * streamCandidates.length)];
        try {
          markStreamEventFired();
          markStreamKeyFired(streamPick.type);
          /* R140-D: Bump the hard daily fire counter so the picker
             enforces the 2x cap on subsequent rolls. */
          bumpStreamFireCount(streamPick.type);
        } catch(_){}
        return streamPick;
      }
      /* R136-fix-2c: If ALL stream candidates are on per-key cooldown,
         intentionally fall through to the next ladder entry (Promotion)
         instead of forcing one anyway. The user explicitly asked for
         less Underdog spam — letting the slot become a non-stream beat
         is the whole point. */
    }

    /* R114-D: Promotion — UNLIMITED. Pick a hero who is 1-5 hearts
       from a tier promotion. Press-hold to give up to 5 hearts. On
       tier-up: visual celebration + silent 2x heart refund (you
       paid 3 → you get 6 back). Only fires if a qualifying hero
       exists in the hall. */
    threshold += 3;
    if(roll < threshold && gate('promotion')){
      var promotionPick = pickPromotionCandidate();
      if(promotionPick){
        return {
          type: 'promotion',
          char: promotionPick.char,
          heartsToPromotion: promotionPick.heartsToPromotion,
          nextTierLabel: promotionPick.nextTierLabel,
          spentInEvent: 0,
          windowSec: 14,
          _eventKey: 'promotion'
        };
      }
    }

    return null;
  }
  /* R114-C: Pick a random hero submitted by the signed-in user. Returns
     null if not signed in, or no submitted hero is on the wander pool.
     We sample from the user's own characters (if hohYourHallSubmittedRows
     is exposed) and fall back to filtering allCharacters by auth_email.
     Filters out mythic (locked from card-spam) and Heroes who already
     hit cap today (no point streaming into a wall). */
  /* R590 (Jan): FEED YOUR OWN NOW OFFERS A CHOICE.
     Jan: "right now it just selects a random hero that you have for you
     to heart, but instead I want it to be you can pick one of your
     heroes ... let's just do three of your heroes or five of your
     heroes for now ... if they only have one character, don't try to
     show five. Just have it focused on the one character."

     Returns an ARRAY of up to FEED_PICK_MAX of the player's own heroes,
     uniformly sampled. The caller decides whether to show a chooser
     (more than one came back) or go straight to the stream (exactly
     one), so the "don't try to show five" rule is a property of the
     data rather than a branch anyone can forget. */
  var FEED_PICK_MAX = 5;
  /* R590b (Jan): "on mobile just show 4 not 5 for that pick menu."

     MEASURED at 375x812 before this cap: five heroes render as THREE
     rows of a two-column grid, 568.8px tall, which pushes the wander
     bar and the "nothing is spent until you hold" line below the fold.
     Four is two clean rows and the whole event fits on one screen.

     ⛔ THE BREAKPOINT IS 600px, NOT AN ARBITRARY "PHONE" NUMBER. The
     grid turns two-column below 601px (see the @media (min-width:601px)
     block in hall-of-heroes.css), and TWO COLUMNS is what makes five
     heroes a three-row grid. At three columns five heroes are 3+2, two
     rows, and fit fine. So the cut belongs exactly where the column
     count changes, and it stays correct on a narrow desktop window as
     well as on a phone.

     ⛔ Decided at PRODUCE time on purpose, not at render time. The list
     is then stable for the life of the event: rotating the phone
     mid-choice can never make a hero the player was looking at
     disappear. A render-time slice would do exactly that. */
  function feedPickMax(){
    try {
      if(window.matchMedia && window.matchMedia('(max-width: 600px)').matches) return 4;
    } catch(_){}
    return FEED_PICK_MAX;
  }
  /* R591b: ONE builder for the Feed Your Own event object, because there
     are now THREE callers (the normal stream ladder, the first-five-rolls
     guarantee, and the debug panel) and three hand-copied object literals
     is how a field ends up set in two of them. */
  function pickFeedYourOwnEvent(){
    var list = pickOwnHeroesForFeed();
    if(!list || !list.length) return null;
    return {
      type: 'feedYourOwn',
      chars: list,
      /* One hero means there is nothing to choose, so skip the chooser
         entirely and open on the stream. */
      char: list.length === 1 ? list[0] : null,
      phase: list.length === 1 ? 'streaming' : 'choosing',
      _eventKey: 'feedYourOwn', windowSec: 14, spentInEvent: 0
    };
  }
  function pickOwnHeroesForFeed(){
    try {
      var em = (typeof window.hohYourHallEmail === 'function') ? window.hohYourHallEmail() : '';
      if(!em) return [];
      var emNorm = String(em).trim().toLowerCase();
      if(!emNorm) return [];
      var all = (typeof window.hohGetAllCharacters === 'function') ? window.hohGetAllCharacters() : [];
      /* ⛔ auth_email FIRST, and this is a real bug fix, not a tidy-up.
         The old code preferred window.hohYourHallSubmittedRows(), which
         matches on c.email OR c.player_name (hoh-your-hall.js:141-155).
         player_name is a DISPLAY name and is not unique, so that list
         can contain heroes submitted by OTHER PEOPLE who happen to
         share your display name. With the old single random pick that
         was an occasional oddity. With a five-card grid of "your"
         heroes it would put strangers in the player's own hall, and
         then charge them 30 hearts to feed one.
         auth_email is the real ownership column: it is what
         hohYourHallCanEditCharacter (:324-332) gates editing on. */
      var pool = (all || []).filter(function(c){
        if(!c || !c.id || !c.auth_email) return false;
        return String(c.auth_email).trim().toLowerCase() === emNorm;
      });
      /* Only if auth_email found nothing do we fall back to the curated
         list, and even then we re-check ownership per row. An older
         hero submitted before auth_email was populated is a real case;
         a stranger sharing your display name is not. */
      if(!pool.length && typeof window.hohYourHallSubmittedRows === 'function'){
        try {
          var rows = window.hohYourHallSubmittedRows() || [];
          pool = rows.filter(function(c){
            if(!c || !c.id) return false;
            var ae = c.auth_email ? String(c.auth_email).trim().toLowerCase() : '';
            var ce = c.email ? String(c.email).trim().toLowerCase() : '';
            /* Accept only an EMAIL match. Never player_name. */
            return (ae ? ae === emNorm : ce === emNorm);
          });
        } catch(_){}
      }
      /* Mythic gate, kept from R114-C, but no longer able to hide the
         whole event. A hero at 1100+ is already past the last tier, so
         pouring hearts into one is the least rewarding choice on the
         grid. If EVERY hero the player owns is mythic, the old code
         returned null and the event became permanently invisible to
         them. Now they simply get their mythics. */
      var live = pool.filter(function(c){
        if(typeof window.hohIsMythicCharacter === 'function' && window.hohIsMythicCharacter(c)) return false;
        return true;
      });
      if(!live.length) live = pool;
      if(!live.length) return [];
      /* Uniform sample. hfShuffle, never the old biased comparator. */
      return hfShuffle(live).slice(0, feedPickMax());
    } catch(_){ return []; }
  }
  /* R114-D: Find a character who is 1-5 hearts from the next rarity
     tier promotion. Returns { char, heartsToPromotion, nextTierLabel }
     or null. The promotion thresholds match hohGetRarity's ladder:
       30 (uncommon), 100 (rare), 200 (epic), 500 (legendary), 1000 (mythic).
     Mythic chars are ineligible (no further promotion). */
  function pickPromotionCandidate(){
    var TIER_LADDER = [
      { at: 30,   label: 'Uncommon' },
      { at: 100,  label: 'Rare' },
      { at: 200,  label: 'Epic' },
      { at: 500,  label: 'Legendary' },
      { at: 1000, label: 'Mythic' }
    ];
    var all = (typeof window.hohGetAllCharacters === 'function') ? window.hohGetAllCharacters() : [];
    if(!all || !all.length) return null;
    var candidates = [];
    for(var i = 0; i < all.length; i++){
      var c = all[i];
      if(!c || !c.id) continue;
      /* Skip mythics (no further tier). */
      if(typeof window.hohIsMythicCharacter === 'function' && window.hohIsMythicCharacter(c)) continue;
      var hearts = c.hearts || 0;
      for(var t = 0; t < TIER_LADDER.length; t++){
        var tier = TIER_LADDER[t];
        var gap = tier.at - hearts;
        if(gap >= 1 && gap <= 5){
          candidates.push({ char: c, heartsToPromotion: gap, nextTierLabel: tier.label });
          break; /* Only the NEXT tier matters per character. */
        }
      }
    }
    if(!candidates.length) return null;
    return candidates[Math.floor(Math.random() * candidates.length)];
  }
  /* R114-D: Promotion tier-up detector. Called from the stream fire()
     after each heart is dispatched. Reads the LIVE hearts on the char
     (allCharacters[idx] is updated by hohHeart's RPC reconcile) and
     compares against the threshold we pinned at fire time. The
     instant the count crosses, fire the celebration + silent 2x
     refund and stop the stream. The refund is silent — the user
     didn't know it was coming, which makes it land harder. */
  function handlePromotionTick(charId){
    if(!currentSpecial || currentSpecial.type !== 'promotion') return;
    if(currentSpecial._promotionFired) return;
    var heartsNeeded = currentSpecial.heartsToPromotion || 0;
    /* OPTIMISTIC local tracking. We detect promotion via spentInEvent
       vs heartsNeeded instead of awaiting allCharacters.hearts to
       reconcile from the RPC (which is async and won't be updated
       by the time this synchronous tick runs). On rare cap-hit
       false positive, the celebration fires for a heart that didn't
       actually land server-side — acceptable trade for instant
       feedback. The 5-heart event cap also bounds the false
       positive window. */
    if((currentSpecial.spentInEvent || 0) < heartsNeeded) return;
    currentSpecial._promotionFired = true;
    /* R115-C: Don't auto-refund or auto-close. Stop streams, transition
       the event to the "promoted" phase, and let the user claim their
       reward via a Claim button. The 2x refund lands in the wallet on
       claim, not now. The user gets to bask in the celebration. */
    try { stopAllStreams(); } catch(_){}
    /* Stop the auto-window timer too — user is now in claim phase. */
    try { stopStreamWindow(); } catch(_){}
    currentSpecial.phase = 'promoted';
    /* Re-render to show the celebration screen with the Claim button. */
    var container = _ffContainer();
    if(container){
      container.innerHTML = renderPromotionHtml(currentSpecial);
      try { if(typeof paintQuestProgress === 'function') paintQuestProgress(); } catch(_){}
    }
    /* Confetti waves to celebrate. */
    try { if(typeof spawnConfetti === 'function'){ spawnConfetti(); setTimeout(spawnConfetti, 220); setTimeout(spawnConfetti, 460); } } catch(_){}
    /* R131-B: Heart-fly to wallet pill the moment the tier-up fires.
       The user gave N hearts to push the hero over — show those hearts
       traveling toward the wallet so it visually anchors the upgrade
       moment. The actual refund still happens on Claim (R115-C kept
       that intentional bask-time), but the visual confirmation of
       "you did the thing" lands here. */
    try {
      var spent = currentSpecial.spentInEvent || 0;
      if(spent > 0 && typeof flyDiceTraderHeartsToPill === 'function'){
        var anchor = document.querySelector('.hoh-fresh-special-stream-promotion .hoh-fresh-stream-target')
                  || document.querySelector('.hoh-fresh-special-stream-promotion');
        if(anchor){
          /* Slight delay so confetti starts first, then the hearts
             stream up after the celebration kicks in. */
          setTimeout(function(){
            try { flyDiceTraderHeartsToPill(anchor, Math.min(spent * 2, 12)); } catch(_){}
          }, 180);
        }
      }
    } catch(_){}
  }
  /* R115-C: Claim handler. Fired when the user clicks the Claim button
     on the promoted screen. Pays out the 2x refund, fires a heart-fly
     animation from the card to the wallet pill, then closes the event
     and refreshes Fresh Faces. */
  window.hohFreshFacesPromotionClaim = function(){
    if(!currentSpecial || currentSpecial.type !== 'promotion') return;
    if(currentSpecial.phase !== 'promoted') return;
    if(currentSpecial._promotionClaimed) return;
    currentSpecial._promotionClaimed = true;
    var refund = (currentSpecial.spentInEvent || 0) * 2;
    if(refund > 0 && typeof window.hohAddHeartsToBank === 'function'){
      try { window.hohAddHeartsToBank(refund); } catch(_){}
      try { if(typeof window.hohRenderHeaderCounter === 'function') window.hohRenderHeaderCounter(); } catch(_){}
    }
    /* Heart-fly from the promoted card toward the wallet pill so the
       refund visually lands. */
    try {
      var anchor = document.querySelector('.hoh-fresh-special-stream-promotion .hoh-fresh-stream-target')
                || document.querySelector('.hoh-fresh-special-stream-promotion');
      if(anchor && typeof flyDiceTraderHeartsToPill === 'function'){
        flyDiceTraderHeartsToPill(anchor, Math.min(refund, 12));
      }
    } catch(_){}
    /* Brief delay so the user sees the hearts fly, then close. */
    setTimeout(function(){
      currentSpecial = null;
      try { if(typeof window.hohFreshFacesRefresh === 'function') window.hohFreshFacesRefresh({ fromInteractive: true }); } catch(_){}
    }, 1100);
  };
  /* R67: Underdog daily-cap helpers + char picker. */
  function underdogTodayKey(){
    var d = new Date();
    return 'hoh-underdog-shown-' + d.getFullYear() + '-' + String(d.getMonth()+1).padStart(2,'0') + '-' + String(d.getDate()).padStart(2,'0');
  }
  function underdogShownToday(){
    try { return localStorage.getItem(underdogTodayKey()) === '1'; } catch(_){ return false; }
  }
  function markUnderdogShownToday(){
    try { localStorage.setItem(underdogTodayKey(), '1'); } catch(_){}
  }
  function pickUnderdogChar(all){
    if(!Array.isArray(all) || !all.length) return null;
    /* Lowest-hearted heroes, excluding hidden + featured + mythic.
       R116-B: Pick RANDOM from the bottom 5 instead of always the
       single lowest. Always picking the same hero made the event
       feel mechanical and starved other underdogs of their moment.
       Bottom-5 random keeps the rotation fresh while still surfacing
       genuinely under-loved heroes. */
    var pool = all.filter(function(c){
      if(!c || !c.id) return false;
      if(c.is_hidden) return false;
      if(c.featured) return false;
      if(typeof window.hohIsMythicCharacter === 'function' && window.hohIsMythicCharacter(c)) return false;
      return true;
    });
    if(!pool.length) return null;
    pool.sort(function(a, b){ return ((a.hearts || 0) - (b.hearts || 0)); });
    var bottomN = pool.slice(0, Math.min(5, pool.length));
    return bottomN[Math.floor(Math.random() * bottomN.length)] || null;
  }

  /* Spectrum event setup. Picks one random hero from each rarity
     tier (common → legendary, in order) so the user reads a
     procession from quiet beginnings to renown. Returns null if any
     tier is empty. */
  function pickSpectrum(){
    /* R140-Z3n: Mobile cuts to 4 cards (MOBILE_EVENT_CARD_CAP), so on
       mobile we SKIP Common and lead with Uncommon → Rare → Epic →
       Legendary. The user wanted to see the rarer tiers featured on
       the smaller screen rather than wasting a slot on a common
       face. Desktop adds Mythic when available so a populated hall
       gets the full 6-tier procession. If a tier is empty we drop
       it gracefully (procession adapts). */
    var isMobile = false;
    try { isMobile = window.matchMedia && window.matchMedia('(max-width: 768px)').matches; } catch(_){}
    var tiers = isMobile
      ? ['uncommon', 'rare', 'epic', 'legendary']
      : ['common', 'uncommon', 'rare', 'epic', 'legendary', 'mythic'];
    var picks = [];
    for(var i = 0; i < tiers.length; i++){
      var pool = poolByTier(tiers[i]);
      if(!pool.length){
        /* Mythic is the only optional tier — many halls won't have
           one yet. Skip the empty tier instead of bailing the whole
           event. For mobile, all 4 chosen tiers should be present;
           if Uncommon-Legendary are short, bail (rare but possible). */
        if(tiers[i] === 'mythic') continue;
        if(isMobile) return null;
        /* Desktop: skip an empty tier gracefully (e.g., no Common
           chars if every Common got hearted into Uncommon). The
           procession still reads correctly with whatever tiers exist. */
        continue;
      }
      picks.push(pool[Math.floor(Math.random() * pool.length)]);
    }
    /* Need at least 3 to render a meaningful procession. */
    if(picks.length < 3) return null;
    return { type: 'spectrum', chars: picks };
  }

  /* ================================================================ */
  /* Gambling events                                                  */
  /* ================================================================ */
  /* Gambling events feature a "host" character who offers a deal.
     The host is RENDERED in the event but is NOT interactive — heart
     and X buttons are hidden via CSS. The user can still click the
     card to open the detail modal (read about the host) but the main
     attention sits on the gamble action itself. */

  /* Dice Trader. A wandering merchant offers a roll: pay N hearts
     for a single d20, get whatever the die shows back as hearts.
     Average return ~10.5 hearts on a 3-heart bet — net positive
     expected value so the deal feels generous. The host pool prefers
     mischievous classes (Rogue, Bard, Sorcerer, Warlock) for flavor;
     falls back to any character if those pools are thin. */
  /* Date-keyed flag for the once-per-day Wandering Trader cap. The
     trader is +EV (avg ~10.5 hearts back for 3) so seeing it once
     per day keeps it special. The flag is set after a successful
     roll, not at pick-time, so a user who hits the trader but bails
     before rolling will still get another chance the same day. */
  function diceTraderTodayKey(){
    var d = new Date();
    var ymd = d.getFullYear() + '-' + String(d.getMonth()+1).padStart(2,'0') + '-' + String(d.getDate()).padStart(2,'0');
    return 'hoh-dice-trader-done-' + ymd;
  }
  function diceTraderUsedToday(){
    try { return localStorage.getItem(diceTraderTodayKey()) === '1'; } catch(_){ return false; }
  }
  function markDiceTraderUsedToday(){
    try { localStorage.setItem(diceTraderTodayKey(), '1'); } catch(_){}
  }

  /* R53 sneaky-coin daily cap helpers. Coin flip got demoted from a
     "fires whenever rare-special tier is eligible" event to a once-
     per-day event because (a) the user explicitly asked, and (b) the
     win-side reward (continue → rogues den + 2 hearts) is too good
     to repeat all day. */
  function sneakyCoinTodayKey(){
    var d = new Date();
    return 'hoh-coin-done-' + d.getFullYear() + '-' + String(d.getMonth()+1).padStart(2,'0') + '-' + String(d.getDate()).padStart(2,'0');
  }
  function sneakyCoinUsedToday(){
    try { return localStorage.getItem(sneakyCoinTodayKey()) === '1'; } catch(_){ return false; }
  }
  function markSneakyCoinUsedToday(){
    try { localStorage.setItem(sneakyCoinTodayKey(), '1'); } catch(_){}
  }
  /* R58: Heroes Circle daily-cap helpers — same shape as the sneaky
     coin pattern. The Circle is now once-per-day; this key is set
     when the user clicks Enter the Circle, and the picker self-gates
     on this flag so subsequent rolls skip it cleanly. */
  function heroCircleTodayKey(){
    var d = new Date();
    return 'hoh-hero-circle-done-' + d.getFullYear() + '-' + String(d.getMonth()+1).padStart(2,'0') + '-' + String(d.getDate()).padStart(2,'0');
  }
  function heroCircleUsedToday(){
    try { return localStorage.getItem(heroCircleTodayKey()) === '1'; } catch(_){ return false; }
  }
  function markHeroCircleUsedToday(){
    try { localStorage.setItem(heroCircleTodayKey(), '1'); } catch(_){}
  }
  /* R60: Champion Meeting once-per-day. The seen-buffer alone wasn\'t
     enough — it cycles within a session, so the Champion would fire
     again after a few rolls. Persist the "shown today" flag in
     localStorage so it survives page reloads + buffer cycles. We
     mark on FIRE (the moment the picker returns it) rather than on
     completion since there\'s no resolve action — the user just
     reads the card and wanders on. */
  function championMeetingTodayKey(){
    var d = new Date();
    return 'hoh-champion-meeting-shown-' + d.getFullYear() + '-' + String(d.getMonth()+1).padStart(2,'0') + '-' + String(d.getDate()).padStart(2,'0');
  }
  function championMeetingShownToday(){
    try { return localStorage.getItem(championMeetingTodayKey()) === '1'; } catch(_){ return false; }
  }
  function markChampionMeetingShownToday(){
    try { localStorage.setItem(championMeetingTodayKey(), '1'); } catch(_){}
  }

  function pickDiceTraderEvent(){
    /* R53: cost bumped 5 → 8. Removed the once-per-day cap — dice
       trader is now an unlimited event since the higher cost
       balances out the +EV. Anti-repeat (3-event recent buffer)
       prevents back-to-back fires.
       R102: cost lowered 8 → 7 since users hit the wallet wall too
       fast at 8. EV drops slightly but the event fires more often. */
    var COST = 7;
    /* R53: removed the once-per-day cap. Dice trader is now unlimited
       (cost bumped to 8 to balance). Anti-repeat (recent buffer)
       still prevents back-to-back fires.
       R140-Z3h: Partial-cost play. As long as the user has 1+ hearts
       the event fires; the cost adjusts down to whatever they have. */
    var bank = (typeof window.heartsRemainingToday === 'function') ? window.heartsRemainingToday() : 0;
    if(bank < 1) return null;
    var actualCost = Math.min(COST, bank);
    /* R65: Mythic host roll first. If it hits, the trader IS a
       mythic and a +30 bonus rides on a winning roll. */
    var mythicHost = (typeof window.hohRollMythicHost === 'function') ? window.hohRollMythicHost() : null;
    var host;
    if(mythicHost){
      host = mythicHost;
    } else {
      var preferredClasses = {Rogue:1,Bard:1,Sorcerer:1,Warlock:1,Charlatan:1};
      var allChars = (typeof window.hohGetAllCharacters === 'function') ? window.hohGetAllCharacters() : [];
      if(!allChars.length) return null;
      var preferred = allChars.filter(function(c){
        return c && c.character_class && preferredClasses[c.character_class];
      });
      var pool = preferred.length >= 3 ? preferred : allChars.filter(function(c){ return c && c.id; });
      if(!pool.length) return null;
      host = pool[Math.floor(Math.random() * pool.length)];
    }
    return {
      type: 'diceTrader',
      host: host,
      mythicHost: !!mythicHost,
      cost: actualCost,
      phase: 'idle',
      rollResult: null,
      _eventKey: 'diceTrader'
    };
  }

  /* ================================================================ */
  /* Champion's Duel — 1v1 battle gambling event                       */
  /* ================================================================ */
  /* Two heroes face off. User pays 4 hearts to back a champion, then
     each rolls a d20. Higher roll wins (ties = champion wins so the
     pick is never wasted). Win pays 10 hearts (net +6); loss returns
     nothing (net -4). 50% odds = +3 EV per duel. Once per day, gated
     by hoh-duel-done-{ymd}. The flag is set after a successful bet
     resolves so a user who bails before clicking still gets another
     chance the same day. */
  function duelTodayKey(){
    var d = new Date();
    var ymd = d.getFullYear() + '-' + String(d.getMonth()+1).padStart(2,'0') + '-' + String(d.getDate()).padStart(2,'0');
    return 'hoh-duel-done-' + ymd;
  }
  function duelUsedToday(){
    try { return localStorage.getItem(duelTodayKey()) === '1'; } catch(_){ return false; }
  }
  function markDuelUsedToday(){
    try { localStorage.setItem(duelTodayKey(), '1'); } catch(_){}
  }

  /* ================================================================ */
  /* R591 (Jan): THE HALL FIGHT — a real, watchable, Battlegrounds-     */
  /* STYLE team battle, standing in for every dice-comparison duel.    */
  /* ================================================================ */
  /* Jan: "instead of having them both just roll one dice ... we're going
     to actually have it so that let's say there's a 1v1, then it's going
     to take the layout of the battlegrounds ... the two characters are
     going to battle until one of them loses all their health. And the
     health is also dictated by the rarity."

     WHAT THIS IS NOT, and every one of these is deliberate:
       - It is NOT the Battlegrounds engine. Nothing here imports
         window.HOHContract. Coupling a Hall minigame to the live judged
         autobattler would mean an engine edit could void real climbs.
       - Nothing PERSISTS. No run row, no settle, no worker call, no
         ENGINE_VERSION, no canonical hash, no seeded RNG. Math.random()
         is correct here, and losing the determinism requirement is what
         lets this be a few hundred lines instead of eleven thousand.
       - There are NO CARDS. Attack rolls only.

     ⛔ THE EVENT KEYS DO NOT CHANGE. championDuel stays championDuel.
     Each battle key is named in ELEVEN string-keyed tables
     (ROLL_TYPE_REGISTRY, UNLIMITED_EVENT_TYPES, GAMBLING_POOL_TYPES,
     GAMBLING_POOL_SUBTYPE, EVENT_FAMILIES, _tourPickerFor, the Coliseum
     queue map, the All-Events queue, the gambling entries list,
     renderSpecial's routing, and the never-auto list) and two of them
     are localStorage queues a player may be HOLDING at deploy time
     (hoh-mb-cq-<date>, hoh-mb-aeb-<date>). Renaming would have meant
     eleven synchronised edits plus a migration for in-flight queues.
     mythicDuelSpecial is registered in only 5 of those 11 today, and
     crownMatch is missing from UNLIMITED_EVENT_TYPES, which is already-
     shipped proof that a partial registration ships silently.
     Keeping the keys means every entry path, cooldown, anti-repeat
     family and paid queue keeps working untouched. */

  /* ---- constants, TUNED OFFLINE, not guessed ----------------------
     Measured over 60,000 simulated bets before any of this shipped:
       realised EV                1.171x  (player-favourable, matching
                                  this file's existing positive-EV events)
       the price picked the winner  78.8% (so the odds are honest)
       rail hit                     0 of 60,000
     Beat counts by size: 1v1 median 8, 3v3 29, 6v6 61. */
  var HF_TIERS  = ['common','uncommon','rare','epic','legendary','mythic'];
  /* ⛔ A HALL-LOCAL DIE TABLE, NOT the engine's DIE = [8,8,10,12,20,20].
     That one gives common and uncommon the same d8 and legendary the
     same d20 as mythic, so two of six tiers would read identically on
     screen, and it feeds the live settle judge and must never move. */
  /* ⛔ R592: ONE POOL PER SIDE. THERE IS NO PER-UNIT HEALTH, BECAUSE THERE
     IS NONE IN BATTLEGROUNDS.
     Jan: "why is there a individual health on each character?"
     Settled by grep, not by opinion:
       grep -nE "hpMax|u\.hp|curHp|unitHp" assets/hoh-contract-engine.js
     returns NOTHING. The whole autobattler carries two scalars, ta and tb
     (engine 6134/6152), read through tot(side) at 6853. And fanHtml
     (ui 14650) emits per tile exactly a face and a name span. No bar. No
     numeral. The first cut of this screen invented per-unit bars, which is
     a different game wearing the same words.

     A body does not have health. It CONTRIBUTES health to its side's pool,
     and when the pool would cross zero it is SPENT to absorb the blow and
     returns its SOAK (engine dealHp 7384, heroDown 7235).

     ⛔ SOAK IS LARGER THAN CONTRIBUTION, and that asymmetry is the design,
     not a typo. The engine sets soak = 18 + 2 x contribution (1417-1418),
     so a body is worth roughly twice as much dying as living. That is what
     makes a death land on a board with no health bars: the pool JUMPS. */
  var HF_POOL_BASE = 26;                         /* every side starts here */
  var HF_CONTRIB   = [10, 12, 15, 19, 24, 31];   /* what a body adds to the pool, by rarity */
  var HF_SOAK      = [12, 15, 19, 24, 30, 38];   /* what it returns when it falls */
  var HF_DIE    = [6,  8,  10, 12, 16, 20];   /* attack die by rarity, six distinct faces */
  var HF_MISS_AT = 2, HF_GLANCE_AT = 6, HF_CRIT_AT = 19;
  var HF_CRIT = 1.30, HF_NAT20 = 1.60;
  var HF_RAMP_FROM = 3, HF_RAMP_STEP = 0.35;
  /* The rail exists because the player has money on this. It is never
     reached in practice (0 of 60,000): the pressure ramp ends every
     fight long before. If it ever fires, the constants are wrong. */
  var HF_MAX_BEATS = 240, HF_PRESSURE_AT = 90;
  /* Odds. STEEP was FIT to 90,000 measured fights: win chance is close
     to linear in the share of total power, with this slope. */
  /* ⛔ RE-FIT FOR THE POOL MODEL. The old 6.0 was fitted to the per-unit
     health build, and moving to one shared pool made outcomes far more
     decisive. MEASURED before the re-fit, over 40,000 fights: a side priced
     at 40% actually won 29%, and one priced at 70% won 86%. That is not a
     rounding error, it is a dishonest book: backing the favourite was
     systematically +EV and backing the underdog systematically -EV, so the
     whole "read the board" idea collapses into "always take the favourite".
     Re-fitted over 120,000 fights: STEEP 12.4, clamp 0.09, rms 0.018. */
  var HF_STEEP = 12.4, HF_PMIN = 0.09, HF_PMAX = 0.91;
  /* The realised EV lands BELOW the nominal target because the clamps trim
     both tails and payouts round down, so the target is set from measurement
     rather than from arithmetic: 1.30 produced 1.024x, and 1.45 lands near
     1.15x, which matches this file's other deliberately player-favourable
     events. The harness asserts the measured band, not this number. */
  var HF_EV_TARGET = 1.45, HF_MULT_MIN = 1.35, HF_MULT_MAX = 5.0;
  var HF_SURVIVOR_KICK = 0.25;   /* Jan: "a little bit extra based on how many members survived" */
  /* R594: the move AI's two thresholds. The NUMBERS are the engine's own
     LAWS (hoh-contract-engine.js:960), but read them as PERCENTAGES here.
     ⛔ AND THAT IS A DELIBERATE DIFFERENCE, NOT AN OVERSIGHT. The engine
     compares them against absolute health (tot() returns ta/tb, which is
     50 plus the party's summed hero value), so a big party there defends
     at a much smaller FRACTION of its bar than a small one. A Hall Fight
     pool swings from about 40 to 250 depending only on how many bodies
     were drafted, so an absolute 45 would mean "defend below 18% of your
     bar" in a 6v6 and "defend below 90%" in a 1v1: the same number
     producing opposite behaviour in two fights the player sees minutes
     apart. Normalising keeps the read consistent, which is what makes
     the fight legible. An earlier comment here claimed exact engine
     parity and that claim was simply wrong. */
  /* ⛔ WHAT A UTILITY BEAT IS WORTH, AND WHY IT HAD TO GO UP. As first
     built, a hold divided one incoming blow by 1.3 and a dodge negated it
     45% of the time, so a beat spent defending was worth about a third of
     a beat spent attacking. Kits are not evenly split between the two:
     the Paladin and the Cleric spend two moves in three on support and
     the Barbarian spends two in three on damage. MEASURED over 3v3 mirror
     matches with identical hearts, ONLY the class differing: Barbarian
     81.3% against Paladin 8.8%, a 72.6 point spread produced by nothing
     the player can see. Raising the four values shrinks it, and pricing
     the rest (hfKitDamage below) makes what remains honest. */
  var HF_GUARD = 3.0;        /* a braced side divides the next blow by this */
  var HF_GUARD_HYB = 2.2;    /* a hybrid swings AND braces, so it braces less */
  var HF_BUFF = 2.1;         /* a readied blow lands this much harder */
  var HF_DEBUFF = 0.4;       /* a hexed blow lands this much softer */
  var HF_DODGE = 0.7;        /* an evading side negates the next blow this often */
  /* ⛔ A KIT'S OWN DAMAGE, USED TO PRICE ITS CLASS. Fitted, not guessed:
     a utility move is worth 0.45 of an attack, which is the value that
     maximises the correlation between a kit's mean damage and its
     measured win rate (r = 0.909 over every class pair, mean error 7.1
     points; 0.00 gives 0.877 and 1.00 gives 0.588). Without this the odds
     were blind to the single largest determinant of who wins. */
  var HF_KIT_UTIL = 0.45;
  var HF_KIT_MID  = 0.80;    /* the average kit, so an ordinary class prices at 1.0 */
  /* memoised by class: hfUnitPower runs inside hfWinChance, which runs on
     every repaint of the wager screen. It lives up here with the other
     module state so a harness slicing the constants block gets it too. */
  var _hfKitDmg = {};
  var HF_DEF_LOW = 45, HF_EXEC_LOW = 30;
  /* ⛔ USED ONLY IF THE ENGINE HAS NOT LOADED. The live table is read from
     HOHContract.content.KITS / .DEFAULT_KIT; this is the load-order
     safety net and the harness asserts it stays EQUAL to the engine's
     DEFAULT_KIT, so it can never quietly become a second answer. */
  var HF_FALLBACK_KIT = [
    { n:'STRIKE', t:'atk' }, { n:'HEAVY BLOW', t:'big', m:1.3, cd:2 }, { n:'RALLY', t:'buff' }
  ];
  var HF_WAGER_MIN = 1;
  /* R593 (Jan): "the max bet should be 300 hearts at most so make it a
     slider bet instead of a input bet", and "dont let them wager it all
     with 1 button".
     ⛔ A HARD CEILING, NOT A SUGGESTION. A slider that ran to the bank
     would be unusable for a rich player (one pixel would be worth tens
     of hearts) and a single careless drag could empty a balance built
     over weeks. 300 keeps the whole track meaningful at any balance and
     bounds the worst single mistake on the screen. */
  var HF_WAGER_MAX = 300;
  /* The real top of the slider: never more than they hold, never more
     than the ceiling, and never below the minimum (a player holding 1
     heart gets a one-notch track rather than a broken range). */
  function hfWagerCeiling(){
    var bank = (typeof window.heartsRemainingToday === 'function') ? window.heartsRemainingToday() : 0;
    return Math.max(HF_WAGER_MIN, Math.min(HF_WAGER_MAX, bank | 0));
  }
  /* The four notches under the track. Only the ones the player can
     actually afford are drawn, so the row never offers a dead button. */
  var HF_WAGER_STEPS = [10, 50, 150, 300];
  /* How far along the track the thumb sits, as a percentage, for the
     filled-portion gradient. A one-notch track (a player holding a single
     heart) would divide by zero, so it reads as full. */
  function hfWagerPct(v, ceil){
    var span = Math.max(1, (ceil | 0) - HF_WAGER_MIN);
    var pct = ((v - HF_WAGER_MIN) / span) * 100;
    return Math.max(0, Math.min(100, Math.round(pct * 10) / 10));
  }

  function hfRoll(n){ return 1 + Math.floor(Math.random() * (n || 1)); }
  /* A side's starting health. Mirrors partyHealthMax (engine 1408): a flat
     base plus every living body's contribution. Shared by the simulator and
     the builder so the number the board opens on is the number the fight
     actually starts from. */
  /* Puts a roster back on its feet and its pools back to full. One helper,
     because the browser proof must be able to run the REAL rewind rather
     than a copy of it that could drift. */
  function hfRewindRoster(ev){
    if(!ev || !ev.teams) return;
    ['A','B'].forEach(function(sd){
      (ev.teams[sd] || []).forEach(function(u){ u.dead = false; });
    });
    ev.pool = { A: hfPoolMax(ev.teams.A), B: hfPoolMax(ev.teams.B) };
  }
  function hfPoolMax(list){
    var t = HF_POOL_BASE;
    for(var k = 0; k < (list || []).length; k++) if(!list[k].dead) t += list[k].contrib;
    return t;
  }

  /* Rarity index 0..5. Prefers the one live source of truth and keeps a
     literal fallback so a load-order surprise degrades instead of
     throwing mid-fight. ⛔ The bands are 0/50/200/500/1000/1200 since
     R769 (HOH_MYTHIC_AT is 1200, the Legend Lock 1000). Mirror
     hohGetRarity in hoh-section-core.js; do not copy older literals. */
  function hfRarIdx(c){
    var h = (c && c.hearts) || 0;
    try {
      if(window.hohGetRarity){
        var i = HF_TIERS.indexOf(window.hohGetRarity(h).tier);
        if(i >= 0) return i;
      }
    } catch(_){}
    if(h >= 1200) return 5;
    if(h >= 1000) return 4;
    if(h >= 500)  return 3;
    if(h >= 200)  return 2;
    if(h >= 50)   return 1;
    return 0;
  }

  /* A fighter. Health is ROLLED at the muster, on screen, so the board
     has a rite worth watching before anyone throws a punch. */
  function hfMakeUnit(c, side, i){
    var rar = hfRarIdx(c);
    return {
      id: (c && c.id) || (side + i), name: (c && c.name) || 'Unknown',
      art: (c && (c.image_thumb_url || c.image_url)) || '',
      cls: (c && c.character_class) || '', race: (c && c.race) || '',
      rar: rar, die: HF_DIE[rar], side: side, i: i,
      /* what this body is worth to its side, alive and dead */
      contrib: HF_CONTRIB[rar], soak: HF_SOAK[rar],
      dead: false
    };
  }

  /* The number the odds are priced from. Everything in it is visible on
     the card: health, die, rarity. The player is never shown this score,
     only the PRICE it produces, which is exactly how a sportsbook works
     and is what keeps reading the board worth doing. */
  /* The number the odds are priced from. Reads what the player can SEE:
     the body's rarity (its portrait rim), its die, and therefore what it
     is worth to the pool both alive and dead. */
  /* ⛔ THE BONUS IS IN THE PRICE. A level and an ability that make a hero
     hit harder in the tape but are invisible to hfWinChance would make
     the book wrong by exactly the amount they help, which on a screen
     that pays out hearts is the one error that compounds. Anything that
     changes the fight has to change the number the fight is sold at.
     u.bonus is the level; the passive is already folded in through soak;
     the active is worth a share of its own multiplier because it only
     comes up on its cooldown. */
  /* How hard a class hits, derived from its OWN kit rather than a hand
     table, so the engine adding or retuning a move moves this with it.
     Memoised by class because hfUnitPower runs inside hfWinChance, which
     runs on every repaint of the wager screen. */
  function hfKitDamage(u){
    var key = hfKitClass(u && u.cls) || '_';
    if(u && u.abilityMove) key += '+' + u.abilityMove.n + (u.abilityMove.m || 1);
    if(_hfKitDmg[key] != null) return _hfKitDmg[key];
    var kit = hfKitFor(u), t = 0;
    for(var i = 0; i < kit.length; i++){
      var m = kit[i], ty = m.t;
      if(ty === 'quick') t += 2 * (m.m || 0.5);
      else if(ty === 'atk' || ty === 'big' || ty === 'hybrid' || ty === 'cast') t += (m.m || 1);
      else t += HF_KIT_UTIL;
    }
    var v = kit.length ? (t / kit.length) : HF_KIT_MID;
    _hfKitDmg[key] = v;
    return v;
  }
  function hfUnitPower(u){
    /* ⛔ THE CLASS IS IN THE PRICE. R594 gave every body its class's three
       real moves, which made class the biggest single thing deciding a
       fight (a 72.6 point spread on identical hearts), while this
       function still read only rarity. A book blind to the largest term
       is a book a player can beat by reading the board. */
    var kd = hfKitDamage(u) / HF_KIT_MID;
    var p = u.contrib + u.soak * 0.55 + u.die * 2.2 * kd + u.rar * 2;
    if(u.bonus) p += u.bonus * 2.2;
    /* ⛔ 1.1, NOT 2.4, AND THE DIFFERENCE WAS COSTING THE PLAYER. The
       active is one entry in a four-move kit and it carries cd:2, so it
       only lands on a fraction of that body's beats. Pricing its full
       multiplier as permanent power over-stated the backed side by ~7
       points and cut the real return from ~1.47x to ~1.24x. Measured by
       sweeping the coefficient against real fights: 2.4 over-states by
       6.91 points, 1.2 by 0.76, 1.0 under-states by 0.89. 1.1 is the
       crossing point, not a guess. */
    if(u.abilityMove) p += (u.abilityMove.m - 1) * u.die * 1.1;
    return p;
  }
  function hfSidePower(list){
    var t = 0; for(var i = 0; i < list.length; i++) t += hfUnitPower(list[i]); return t;
  }
  function hfWinChance(A, B){
    var pa = hfSidePower(A), pb = hfSidePower(B);
    if(pa + pb <= 0) return 0.5;
    var share = pa / (pa + pb);
    var p = 0.5 + HF_STEEP * (share - 0.5);
    return Math.max(HF_PMIN, Math.min(HF_PMAX, p));
  }
  function hfMultiplier(p){
    if(!(p > 0)) return HF_MULT_MAX;
    return Math.max(HF_MULT_MIN, Math.min(HF_MULT_MAX, HF_EV_TARGET / p));
  }

  /* ================================================================ */
  /* R594 (Jan): THE THREE MOVES, AND THEY ARE THE ENGINE'S OWN        */
  /* ================================================================ */
  /* "the characters need to use a vareity of skills aka the 3 they have
     not just spam the same attack, it needs to feel very close to an
     actual battlegrounds fight essentially."

     ⛔ "THE 3 THEY HAVE" IS A REAL TABLE AND IT ALREADY EXISTS.
     Battlegrounds gives every one of its thirteen classes exactly three
     named moves (hoh-contract-engine.js:1244, exported at :11368 as
     HOHContract.content.KITS), plus a three-move DEFAULT_KIT for a
     homebrew class. That is precisely what Jan is naming, so this reads
     the LIVE table instead of retyping it. Two copies of one rule in two
     files is the single most expensive bug shape in this repo, and a kit
     is exactly the kind of table someone would later edit in one place.

     The fallback below exists only for the load-order case and is
     asserted EQUAL to the engine's DEFAULT_KIT by the harness, so it
     cannot drift into being a second answer. */
  function hfEngineContent(){
    try {
      var E = window.HOHContract;
      if(E && E.content) return E.content;
    } catch(_){}
    return null;
  }
  /* The engine's clsOf takes the first word of the class. Hall classes are
     typed by real people, so this also normalises case: 'fighter' and
     'FIGHTER' must both find the Fighter kit, or most of the hall would
     silently fall to the default. */
  function hfKitClass(cls){
    var s = String(cls || '').trim().split(' ')[0];
    if(!s) return '';
    return s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();
  }
  function hfKitFor(u){
    var C = hfEngineContent();
    var K = C && C.KITS;
    var base = null;
    if(K){
      var k = hfKitClass(u && u.cls);
      if(k && K[k]) base = K[k];
    }
    if(!base) base = (C && C.DEFAULT_KIT) || HF_FALLBACK_KIT;
    /* the player's own hero fights with its rolled ability as a fourth
       move, which is the whole of Jan's "it retained the abilities" */
    if(u && u.abilityMove) return base.concat([u.abilityMove]);
    return base;
  }
  /* ⛔ A FAITHFUL PORT OF THE ENGINE'S OWN pickMove (:8697-8717), not a
     new AI. Same order, same thresholds, same never-three-in-a-row rule,
     same cooldown bookkeeping. That is what makes this read like a
     Battlegrounds fight rather than like something that resembles one:
     the choices are made by the same rules the real game uses.
       1. drop anything still on cooldown
       2. never the same move three times running
       3. near full health, stop healing
       4. hurt: hold or heal
       5. they are nearly out: attack
       6. otherwise reach for the heavy move
       7. otherwise anything left, at random  */
  function hfPickMove(u, ownPct, foePct, st, steps){
    var kit = hfKitFor(u);
    var ui = u.side + u.i;
    var hist = st.hist[ui] || [];
    /* ⛔ KEYED ON THE SLOT, NOT THE NAME. A rolled active is appended to
       the class kit, and its display name can equal one already in there
       (the Artificer kit holds OVERCLOCK and so does the skill table).
       Keyed on the name, the two shared one cooldown and one history
       slot, so playing either silently benched the other and the class
       lost a move. The index is unique by construction. */
    var mkey = function(m, ix){ return ui + '#' + ix + '#' + m.n; };
    var ready = kit.filter(function(m, ix){ return !(st.cd[mkey(m, ix)] > steps); });
    var noTriple = ready.filter(function(m){ return !(hist[0] === m.n && hist[1] === m.n); });
    if(noTriple.length) ready = noTriple;
    if(!ready.length) ready = kit;
    var pick = null;
    if(ownPct >= 95){
      var noHeal = ready.filter(function(m){ return m.t !== 'heal'; });
      if(noHeal.length) ready = noHeal;
    }
    if(ownPct < HF_DEF_LOW){
      pick = ready.filter(function(m){ return m.t === 'hold' || m.t === 'heal' || m.t === 'cast'; })[0];
    }
    if(!pick && foePct < HF_EXEC_LOW){
      pick = ready.filter(function(m){ return m.t === 'atk' || m.t === 'big'; })[0];
    }
    if(!pick) pick = ready.filter(function(m){ return m.t === 'big'; })[0];
    if(!pick) pick = ready[Math.floor(Math.random() * ready.length)];
    if(!pick) pick = kit[0];
    if(pick.cd) st.cd[mkey(pick, kit.indexOf(pick))] = steps + pick.cd * 4;
    st.hist[ui] = [pick.n, hist[0]];
    var out = { n: pick.n, t: pick.t, m: pick.m, own: pick.own };
    /* the Sorcerer's CHAOS SURGE really is a different number every time */
    if(pick.chaos) out.m = +(0.7 + Math.random() * 1.0).toFixed(2);
    return out;
  }

  /* ---- the fight -------------------------------------------------- */
  /* Produces the whole tape up front, then playback animates it. Every
     combat event carries the FULL health arrays after it (the engine
     calls this stamping), so a repaint can snap to the truth instead of
     accumulating its own idea of the board. That one convention removes
     an entire class of desync bug. */
  /* ---- the fight ------------------------------------------------- */
  /* Produces the whole tape up front; playback animates it. Every event
     carries the absolute truth after it (the engine calls this stamping,
     stampDef at 6852). The R344 comment records why: a UI that accumulated
     deltas drifted in 99.3% of simulated battles, mean error 31 HP. Here
     that is two scalars, ha and hb, not two arrays. */
  function hfSimulate(A, B){
    var ev = [];
    var i;
    for(i = 0; i < A.length; i++){ A[i].dead = false; }
    for(i = 0; i < B.length; i++){ B[i].dead = false; }

    var pool = { A: hfPoolMax(A), B: hfPoolMax(B) };
    var maxPool = { A: pool.A, B: pool.B };
    function stamp(e){ e.ha = pool.A; e.hb = pool.B; ev.push(e); return e; }
    function living(list){ var n = 0; for(var k = 0; k < list.length; k++) if(!list[k].dead) n++; return n; }
    /* The engine's nextFaller (7200) spends the bench right to left, then
       joiners, then your own line right to left, then the crown last. A
       Hall Fight side is one flat line, so this is that order with the
       ranks it does not have removed. */
    /* ⛔ THE CROWN FALLS LAST, AND WITHOUT THIS IT FELL FIRST, EVERY TIME.
       R594 pushes the player's champion and the mirror onto the TAIL of
       their arrays, an index chosen so no other body's DOM id moves. This
       walk returns the HIGHEST living index, so the last body pushed was
       the first body spent: MEASURED, the champion was first to fall in
       9297 of 9297 fights that lost anyone. It is the one body carrying
       the level, the passive soak and the fourth move, so the whole of
       "it retained the level and abilities" was being switched off at the
       first sacrifice. Worth 16.3 points of win rate purely from array
       position. Both are protected, or protecting only the champion would
       hand the player a second edge nobody priced. */
    function nextFaller(list){
      var crown = -1;
      for(var k = list.length - 1; k >= 0; k--){
        if(list[k].dead) continue;
        if(list[k].champ || list[k].mirror){ if(crown < 0) crown = k; continue; }
        return k;
      }
      return crown;                  /* spent only when nothing else is left */
    }

    for(i = 0; i < A.length; i++) stamp({ e:'muster', s:'A', i:i, die:A[i].die, contrib:A[i].contrib });
    for(i = 0; i < B.length; i++) stamp({ e:'muster', s:'B', i:i, die:B[i].die, contrib:B[i].contrib });

    var order = [];
    var n = Math.max(A.length, B.length);
    for(var k2 = 0; k2 < n; k2++){ order.push(A[k2 % A.length]); order.push(B[k2 % B.length]); }
    var openA = Math.random() < 0.5;
    if(!openA) order.push(order.shift());
    stamp({ e:'coin', a: openA ? 1 : 0 });

    /* THE SACRIFICE. One door, mirroring dealHp (engine 7384). */
    function dealPool(side, amount){
      if(amount <= 0) return;
      var list = (side === 'A') ? A : B;
      var after = pool[side] - amount;
      var guard = 0;
      while(after <= 0 && guard < 48){
        guard++;
        var k = nextFaller(list);
        if(k < 0) break;                      /* nobody left: this side is done */
        var u = list[k];
        var last = living(list) <= 1;
        u.dead = true;
        /* the last body returns nothing, because there is nothing left to
           buy (engine heroDown 7235: soak = last ? 0 : heroSoakOf(h)) */
        if(!last) after += u.soak;
        pool[side] = Math.max(0, after);
        stamp({ e:'fell', s:side, i:k, name:u.name, rar:u.rar,
                last: last ? 1 : 0, soak: last ? 0 : u.soak });
        if(last){ after = 0; break; }
      }
      pool[side] = Math.max(0, after);
    }

    /* R594: the move AI's memory (cooldowns + the last two moves per body,
       which is what enforces never-three-in-a-row) and the four one-shot
       states a move can leave behind for the next beat to spend. All four
       are per SIDE, matching the two-scalar health model: this board has
       one pool a side, so a guard raised by one body shelters the side. */
    var mst = { cd: {}, hist: {} };
    var guard  = { A:0, B:0 };   /* next incoming blow is divided by this */
    var buff   = { A:0, B:0 };   /* next outgoing blow is half again as hard */
    var debuff = { A:0, B:0 };   /* next outgoing blow is weakened */
    var dodge  = { A:0, B:0 };   /* next incoming blow may miss outright */
    var beats = 0, round = 1, j = 0;
    while(beats < HF_MAX_BEATS){
      var actor = order[j++];
      if(j >= order.length){ j = 0; round++; }
      /* ⛔ A DEAD SEAT DOES NOT COST ITS SIDE THE TURN. Skipping would make
         losing a body cost an ACTION too, and the first kill would decide
         the fight. A living team-mate steps up. */
      if(actor.dead){
        var bench = [], src = (actor.side === 'A') ? A : B;
        for(var b2 = 0; b2 < src.length; b2++) if(!src[b2].dead) bench.push(src[b2]);
        if(!bench.length) break;
        actor = bench[Math.floor(Math.random() * bench.length)];
      }
      var foeSide = (actor.side === 'A') ? 'B' : 'A';
      var foeList = (foeSide === 'A') ? A : B;
      if(pool[foeSide] <= 0 && living(foeList) === 0) break;

      /* ⛔ TWO DICE, AND THEY ARE NOT THE SAME DIE. `roll` is the body's OWN
         power die and is what the screen shows. `hit` is the contact d20.
         ui.js 21357 records the bug that comes from crossing them: a
         white-gold NATURAL 20 flare was painted over a die reading 3, which
         is "the game lying about its own dice". The die celebrates only when
         the DIE is on its own top face. The contact 20 gets a banner. */
      var roll = hfRoll(actor.die);
      if(actor.rar >= 5){ var alt = hfRoll(actor.die); if(alt > roll) roll = alt; }
      /* R594: the level a player raised is real power in the roll. */
      var basePow = roll + Math.floor(actor.rar / 2) + (actor.bonus || 0);

      /* R594: WHAT this body does, chosen by the engine's own move AI. */
      var ownPct = Math.round(pool[actor.side] / Math.max(1, maxPool[actor.side]) * 100);
      var foePct = Math.round(pool[foeSide]    / Math.max(1, maxPool[foeSide])    * 100);
      var mv = hfPickMove(actor, ownPct, foePct, mst, beats);

      beats++;
      var kind = 'hit', power = 0, healed = 0, hits = 1, hit = 0, spent = 0;

      if(mv.t === 'heal'){
        kind = 'heal'; spent = 1;
        healed = Math.max(2, Math.round(basePow * (mv.m || 1)));
        healed = Math.min(healed, Math.max(0, maxPool[actor.side] - pool[actor.side]));
      } else if(mv.t === 'hold'){
        kind = 'hold';  guard[actor.side] = (mv.m && mv.m > 1) ? mv.m * 2.31 : HF_GUARD;
      } else if(mv.t === 'buff'){
        kind = 'buff';  buff[actor.side] = 1;
      } else if(mv.t === 'debuff'){
        kind = 'debuff'; debuff[foeSide] = 1;
      } else if(mv.t === 'dodge'){
        kind = 'dodge'; dodge[actor.side] = 1;
      } else {
        /* atk, big, quick, hybrid, cast and anything else: it lands. */
        spent = 1;
        hits = (mv.t === 'quick') ? 2 : 1;
        hit = hfRoll(20);
        power = Math.round(basePow * (mv.m || 1));
        /* ⛔ TWO HITS AT m EACH, WHICH IS THE ENGINE'S OWN SEMANTICS. The
           first cut multiplied by 2 * 0.62, which made FLURRY (m 0.5) and
           QUICK BLADE (m 0.45) land 0.62 and 0.56 of a plain attack: a
           flurry on a COOLDOWN that hits for less than the free basic
           swing beside it. The move would have been strictly bad and the
           screen would have been lying about what it is. */
        if(hits === 2) power = Math.round(power * 2);
        if(buff[actor.side]){   power = Math.round(power * HF_BUFF);  buff[actor.side] = 0; }
        if(debuff[actor.side]){ power = Math.round(power * HF_DEBUFF); debuff[actor.side] = 0; }
        if(hit <= HF_MISS_AT){ power = 0; kind = 'miss'; }
        else if(hit <= HF_GLANCE_AT){ power = Math.max(1, Math.round(power / 2)); kind = 'glance'; }
        else if(hit === 20){ power = Math.round(power * HF_NAT20); kind = 'nat20'; }
        else if(hit >= HF_CRIT_AT){ power = Math.round(power * HF_CRIT); kind = 'crit'; }
        /* THE DEFENDER ANSWERS. A guard or an evade set on an earlier beat
           is spent HERE, which is what makes those beats worth watching:
           the player sees SMOKE STEP go up and then sees the next blow
           miss because of it. */
        if(power > 0 && dodge[foeSide]){
          dodge[foeSide] = 0;
          if(Math.random() < HF_DODGE){ power = 0; kind = 'dodged'; }
        }
        if(power > 0 && guard[foeSide]){
          power = Math.max(1, Math.round(power / guard[foeSide]));
          guard[foeSide] = 0;
          if(kind === 'hit') kind = 'guarded';
        }
        /* a hybrid swings AND braces, which is what makes it a hybrid */
        if(mv.t === 'hybrid') guard[actor.side] = HF_GUARD_HYB;
        if(round > HF_RAMP_FROM) power = Math.round(power * (1 + (round - HF_RAMP_FROM) * HF_RAMP_STEP));
        if(beats >= HF_PRESSURE_AT) power = power * 3;
      }

      /* ⛔ THE STAMP GOES BEFORE EVERY POOL CHANGE, HEALS INCLUDED. The
         tape's contract is that an event carries the pools as they stood
         when it began, and playback snaps to them. The first cut applied
         the heal before stamping, so a heal jumped the numbers a beat
         early while damage landed a beat late, and the two halves of the
         same board disagreed about which moment they were showing. */
      var beatEvent = stamp({ e:'beat', s:actor.side, i:actor.i, ai:actor.id, kind:kind,
              mv:mv.n, mt:mv.t, own:mv.own ? 1 : 0, hits:hits, spent:spent,
              roll:roll, die:actor.die, hit:hit, dmg:power, heal:healed,
              ts:foeSide, round:round });
      if(healed > 0) pool[actor.side] = Math.min(maxPool[actor.side], pool[actor.side] + healed);
      if(power > 0) dealPool(foeSide, power);
      /* ha/hb preserve the engine-style BEGINNING-of-beat stamp above.
         Arrival needs the other half of that truth: the pools after this
         action and any sacrifice soak have resolved. Keeping both absolute
         snapshots avoids accumulating damage in the painter. */
      beatEvent.afterA = pool.A;
      beatEvent.afterB = pool.B;
      if(living(A) === 0 || living(B) === 0) break;
      if(pool.A <= 0 || pool.B <= 0) break;
    }

    var aAlive = living(A), bAlive = living(B);
    var winner;
    if(pool.B <= 0 && pool.A > 0) winner = 'A';
    else if(pool.A <= 0 && pool.B > 0) winner = 'B';
    else if(aAlive !== bAlive) winner = (aAlive > bAlive) ? 'A' : 'B';
    else winner = ((pool.A / Math.max(1, maxPool.A)) >= (pool.B / Math.max(1, maxPool.B))) ? 'A' : 'B';
    stamp({ e:'end', w:winner, aAlive:aAlive, bAlive:bAlive, beats:beats,
            railed: beats >= HF_MAX_BEATS });
    return { events: ev, winner: winner, aAlive: aAlive, bAlive: bAlive,
             beats: beats, maxA: maxPool.A, maxB: maxPool.B };
  }
  /* ---- the ten battles ------------------------------------------
     Jan: "don't have that many 1v1s, have a lot of them to be like
     2v2s, 3v3s, 4v4s, 5v5s, you know, like up to like 6v6."

     size    where                       why
     1v1     crownMatch                  the one true duel left. A crown
                                         is fought for in single combat.
     2v2     championDuel, rareDuel      the entry stakes
     3v3     epicDuel, tavernBrawl,      the common shape
             mythicDuel, mythicDuelSpecial
     4v4     tavernBrawl4, barFight      Jan on the old eight-way:
                                         "just split it into two teams of four"
     5v5     legendaryDuel               the big one

     6v6 is fully supported and proven by the sweep. Nothing is assigned
     to it because every 6v6 needs TWELVE bodies in one tier band, and
     no tier above epic reliably holds twelve. hfDraftTeams would shrink
     it anyway, so assigning 6v6 would just be a 4v4 wearing a bigger
     label. Say the word and any of these can move. */

  /* ⛔ _hfTierFilter IS GONE, AND ITS REMOVAL IS A BUG FIX, NOT A TIDY-UP.
     The six tiered producers used to pass BOTH `tier: N` and
     `poolFilter: _hfTierFilter(N)`. The poolFilter runs first, inside
     hfDraftTeams' `base`, and strips every other tier out before the
     band-widening loop ever sees the hall. So the loop could never
     widen DOWN, and Jan's ruling ("when a mythic 3v3 cannot be filled,
     fill with legendaries") was dead code from the day it was written.
     MEASURED: on a hall with 2 mythics and 9 legendaries, the mythic
     duel SHRANK to 1v1 with nine perfectly good legendaries sitting
     right there. The tier now has exactly one expression, the `tier`
     parameter, and poolFilter is left for what it is actually for:
     CLASS constraints, like the champion duel's martial roster. */

  function pickChampionDuelEvent(){
    return hfBuildBattle({
      type: 'championDuel', size: 2, tier: null, suggest: 5,
      title: 'A Champion\'s Duel',
      subtitle: 'Both corners are ready. Back a side, then watch them fight it out.',
      sideName: { A: 'The Blue Corner', B: 'The Red Corner' },
      /* The martial classes, same roster the old duel used. */
      poolFilter: function(c){
        var cl = {Fighter:1,Barbarian:1,Paladin:1,Ranger:1,Monk:1,Rogue:1,Knight:1};
        return c && c.character_class && cl[c.character_class];
      }
    });
  }
  function pickRareDuelEvent(){
    return hfBuildBattle({
      type: 'rareDuel', size: 2, tier: 2, suggest: 3,
      title: 'A Rare Duel',
      subtitle: 'Rare fighters, evenly matched. Pick the side you believe in.',
      sideName: { A: 'The Blue Pair', B: 'The Red Pair' }
    });
  }
  function pickEpicDuelEvent(){
    return hfBuildBattle({
      type: 'epicDuel', size: 3, tier: 3, suggest: 6,
      title: 'An Epic Clash',
      subtitle: 'Epic tier. Steel and spellwork until one side is down.',
      sideName: { A: 'The Blue Three', B: 'The Red Three' }
    });
  }
  function pickLegendaryDuelEvent(){
    return hfBuildBattle({
      type: 'legendaryDuel', size: 5, tier: 4, suggest: 10,
      title: 'A Legendary Melee',
      subtitle: 'Legends on both sides. The hall clears the floor for this one.',
      sideName: { A: 'The Blue Banner', B: 'The Red Banner' }
    });
  }
  function pickCrownMatchEvent(){
    return hfBuildBattle({
      type: 'crownMatch', size: 1, tier: 4, suggest: 15,
      title: 'The Crown Match',
      subtitle: 'For the crown, and nobody is holding back.',
      sideName: { A: 'The Challenger', B: 'The Holder' }
    });
  }
  function pickMythicDuelEvent(){
    return hfBuildBattle({
      type: 'mythicDuel', size: 3, tier: 5, suggest: 15,
      title: 'A Mythic Clash',
      /* Jan: "for the mythic duel, try to get like three mythic
         characters against three mythic characters." When the hall
         cannot field six, hfDraftTeams backfills with legendaries and
         then shrinks, per his ruling. */
      subtitle: 'Mythics on both sides. The lanterns dim when they start.',
      sideName: { A: 'The Blue Myth', B: 'The Red Myth' }
    });
  }
  /* ================================================================ */
  /* R148-Z37: Type 3 Mythic Duel — special-tier high-stakes duel     */
  /* ================================================================ */
  /* Distinct from the gambling-pool Mythic Duel (cost 21 / win 50).
     This is the "rare-encounter" version that sits in the Type 3
     special pool and headlines the Experience All Events queue.
     Flow:
       1. idle:    show 2 mythics. "Enter the Duel (50h)" CTA.
       2. picking: user paid 50h. Click one of two mythics to back.
       3. buffer:  user has picked. Two CTAs:
                     - "Begin the Duel" → straight to dice
                     - "Stealth Buff +50h" → optional. Spends 50h,
                       reveals a hidden d6 with a gold-flash animation
                       on the picked combatant. The d6 is added to
                       their d20 final.
       4. rolling: dice tumble, picked + opponent + (optional) d6.
       5. resolved: win = +200h + confetti + heart-fly burst.
                    Lose = nothing back.
     Why "stealth": the d6 buff is shown to the user privately during
     the buffer phase. Narratively, the opposing mythic doesn't see
     the +d6 coming. Mechanically it just adds to picked's d20. */
  function pickMythicDuelSpecialEvent(opts){
    var forced = !!(opts && opts.force);
    var bank = (typeof window.heartsRemainingToday === 'function') ? window.heartsRemainingToday() : 0;
    if(!forced && bank < 2) return null;
    /* Jan: "for the mythic duel, try to get like three mythic characters
       against three mythic characters." Six mythics is a tall order for
       any hall, so hfDraftTeams widens into legendary and then shrinks
       rather than refusing, which is Jan's own ruling on what to do
       when the tier cannot be filled.

       ⛔ THE PAID +d6 "STEALTH BUFF" IS GONE, and it had to be. It let
       the player spend 50 hearts to secretly add to their own side's
       roll, which is a rig sold back to the player. There is nothing to
       add it to now: the fight simulates itself and the price already
       expresses how likely each side is to win. Buying an edge over a
       simulation you are betting on is just a worse price. */
    return hfBuildBattle({
      type: 'mythicDuelSpecial', size: 3, tier: 5, suggest: 25, free: forced,
      title: 'The Mythic Court',
      subtitle: 'Mythics on both sides. This does not happen twice the same way.',
      sideName: { A: 'The Elder Court', B: 'The Risen Court' }
    });
  }
  /* ---- drafting two teams --------------------------------------- */
  /* Jan (on a 3v3 of mythics that cannot be filled): "fill with
     legendaries."

     So the draft DEGRADES rather than refusing:
       1. take everyone in the requested tier band
       2. if that cannot field 2 x size, widen one tier DOWN and retry
       3. if it still cannot, SHRINK the fight (6v6 becomes 4v4 becomes
          2v2) rather than vanishing
       4. only a pool that cannot field 1v1 returns null

     ⛔ Shrinking is a real requirement, not a nicety. No constant,
     manifest or fixture in this repo states the per-tier population,
     and the only figure anywhere is an undated comment claiming nine
     legendaries. A 6v6 legendary fight would need twelve. A fight that
     silently becomes 4v4 is fine. A stage that silently disappears is
     the event never firing for anyone. */
  function hfDraftTeams(size, tierWanted, opts){
    opts = opts || {};
    var all = (typeof window.hohGetAllCharacters === 'function') ? (window.hohGetAllCharacters() || []) : [];
    if(!all.length) return null;
    var dismissed = getDismissed(), dSet = {};
    dismissed.forEach(function(id){ dSet[id] = true; });
    var base = all.filter(function(c){
      if(!c || !c.id) return false;
      if(dSet[c.id]) return false;
      if(opts.filter && !opts.filter(c)) return false;
      return true;
    });
    if(!base.length) return null;

    /* ⛔ AN UNTIERED EVENT MUST STILL ANCHOR TO A BAND, OR NEARLY EVERY
       FIGHT IS A BLOWOUT.
       MEASURED before this: drawing championDuel, tavernBrawl and
       barFight from the whole hall produced odds of 90/10, 80/20 and
       78/22 on a healthy hall, because a common and a legendary landing
       on opposite sides is a foregone conclusion. A fight you cannot
       lose is not worth watching and the bet answers itself.
       So an untiered event picks a random ANCHOR tier first, weighted
       by what the hall actually holds, and then draws BOTH sides from
       anchor-1..anchor+1. The fights stay competitive, and which band
       shows up still varies from a scrappy common brawl to a room full
       of legends. Asymmetry inside the band is what the player reads. */
    var floor;
    if(tierWanted == null){
      var byTier = [0,0,0,0,0,0];
      base.forEach(function(c){ byTier[hfRarIdx(c)]++; });
      var viable = [];
      for(var t = 0; t < 6; t++){
        /* A tier can anchor only if its own band can field both sides. */
        var band = byTier[Math.max(0,t-1)] + byTier[t] + byTier[Math.min(5,t+1)];
        if(band >= size * 2) viable.push(t);
      }
      floor = viable.length ? viable[Math.floor(Math.random() * viable.length)] : 0;
    } else {
      floor = tierWanted;
    }
    /* ⛔ THE TWO BANDS ARE DIFFERENT, AND THE FIRST CUT GOT IT WRONG.
       An ANCHORED event (no tier asked for) wants a WIDE band from the
       start: anchor-1..anchor+1 is what keeps both sides comparable.
       A TIERED event wants a NARROW band first and widens only when it
       must, because its subtitle names the tier. The first cut used the
       anchored formula for both, so a "Mythic Clash" drew from legendary
       AND mythic on its very first attempt even when the hall held
       plenty of mythics, while the screen promised "Three mythics
       against three."
       Widening goes DOWNWARD only. That is Jan's ruling: when a mythic
       3v3 cannot be filled, "fill with legendaries", never with commons
       promoted upward into a legends' fight. */
    var anchored = (tierWanted == null);
    var pool = [];
    for(var widen = 0; widen <= 5; widen++){
      var lo, hi;
      if(anchored){
        lo = Math.max(0, floor - 1 - widen);
        hi = Math.min(5, floor + 1);
      } else {
        lo = Math.max(0, floor - widen);
        hi = floor;
      }
      pool = base.filter(function(c){
        var r = hfRarIdx(c);
        return r >= lo && r <= hi;
      });
      if(pool.length >= size * 2) break;
    }
    if(pool.length < 2) pool = base;
    if(pool.length < 2) return null;

    /* Shrink until the pool can field both sides. */
    var n = Math.min(size, Math.floor(pool.length / 2));
    if(n < 1) return null;

    var shuf = hfShuffle(pool);
    var aC = shuf.slice(0, n), bC = shuf.slice(n, n * 2);
    var A = aC.map(function(c, i){ return hfMakeUnit(c, 'A', i); });
    var B = bC.map(function(c, i){ return hfMakeUnit(c, 'B', i); });
    /* R594 (Jan): "make sure a random hero also joins the other side when
       you join 1 side so the fight is fair."
       ⛔ THE MIRROR IS DRAWN FROM THE SAME BAND AS THE FIGHT, and that is
       what makes the whole mechanic honest. Drawn from the WHOLE HALL a
       mirror would usually be a common, so joining any high-tier fight
       would be a free win; drawn from the band, the stranger who arrives
       is the same calibre as everyone else on the board and the only
       thing that can tip the fight is how good YOUR hero is. It falls
       back to the wider hall only when the band is exhausted, because a
       fight one body short is worse than a slightly mismatched one. */
    var spareC = shuf[n * 2] || null;
    if(!spareC){
      var taken = {};
      A.forEach(function(u){ taken[u.id] = true; });
      B.forEach(function(u){ taken[u.id] = true; });
      var rest = hfShuffle(base.filter(function(c){ return !taken[c.id]; }));
      spareC = rest[0] || null;
    }
    return { A: A, B: B, size: n, shrunk: n < size,
             spare: spareC ? hfMakeUnit(spareC, 'B', n) : null };
  }

  /* ---- the event object ------------------------------------------ */
  /* Replaces the old _buildDuelEvent. Same call sites, same event keys,
     entirely different mechanic. The wager is NOT taken here: Jan chose
     a typed amount, so the player names it on the wager screen and the
     debit happens when they lock a side in. */
  /* ================================================================ */
  /* R593 (Jan): YOUR OWN HERO FIGHTS FOR THE SIDE YOU BACK           */
  /* ================================================================ */
  /* "it will literally add their character into it to battle which will
     shift the odds so they have a higher chance to win for all of these
     battle events ... it just adds an additional member (Their
     character) to the side they pick for the battle ... if they are
     logged in they have at least 1 character which means whatever they
     pick their character can ALWAYS join."

     A signed-out visitor has nobody to send, so the fight runs exactly
     as it did before and no copy promises a hero that will not arrive.

     ⛔ THE ODDS ARE RE-PRICED, AND THAT IS THE WHOLE SAFETY ARGUMENT.
     An extra body raises the side's pool AND adds a soak to the
     sacrifice ladder, so the backed side really does win more often.
     Leaving the payout where it was would hand every logged-in player a
     permanent free edge on every fight in a screen whose only job is
     paying out hearts. hfRepriceBattle re-derives the price from the
     rosters that will actually fight, so the shorter odds ARE the cost
     of the help, and the player watches the number move when they pick.

     ⛔ AND THE CHAMPION IS KEPT OUT OF THE DRAFT. Without the exclusion
     a player whose own hero was drafted as a stranger would see them
     standing on one side and then walk on a second time, twice on the
     board under one name, with one of the two dying while the other
     fought on. */
  function hfOwnedCharacters(){
    try {
      var em = (typeof window.hohYourHallEmail === 'function') ? window.hohYourHallEmail() : '';
      if(!em) return [];
      var emNorm = String(em).trim().toLowerCase();
      if(!emNorm) return [];
      var all = (typeof window.hohGetAllCharacters === 'function') ? window.hohGetAllCharacters() : [];
      /* auth_email FIRST. R590 records why: the curated submitted-rows
         list matches on player_name too, which is a DISPLAY name and is
         not unique, so it can hand back a stranger's hero. */
      var pool = (all || []).filter(function(c){
        if(!c || !c.id || !c.auth_email) return false;
        return String(c.auth_email).trim().toLowerCase() === emNorm;
      });
      if(!pool.length && typeof window.hohYourHallSubmittedRows === 'function'){
        try {
          var rows = window.hohYourHallSubmittedRows() || [];
          pool = rows.filter(function(c){
            if(!c || !c.id) return false;
            var ae = c.auth_email ? String(c.auth_email).trim().toLowerCase() : '';
            var ce = c.email ? String(c.email).trim().toLowerCase() : '';
            return (ae ? ae === emNorm : ce === emNorm);   /* never player_name */
          });
        } catch(_){}
      }
      return pool;
    } catch(_){ return []; }
  }
  /* ================================================================ */
  /* R594 (Jan): YOUR HERO BRINGS ITS LEVEL AND ITS REAL ABILITIES     */
  /* ================================================================ */
  /* "if your character joins one side try to keep that characters
     abilities so if one of your OP characters joins it will destroy
     everyone cause it retained the level and abilities which is fine."

     Everything below reads a LIVE source and degrades to nothing:
       window.hohCharLevel        (hoh-your-hall.js:3613) the real level
       window.HOHContractData     (hoh-contract-data.js)  the real gear
       window.HOHContractContent  (hoh-contract-content.js) the real names
     ⛔ ALL THREE ARE READ LAZILY, NEVER AT MODULE INIT. All four scripts
     load `defer` and hall-of-heroes.liquid lists hoh-fresh-faces.js at
     1051 but the engine, content and data at 1094-1096, so at THIS
     file's IIFE time none of them exist. They are all present long
     before a player can build a battle, which is the only moment these
     are called. */
  var HF_LVL_STEP  = 3;              /* one point of power per 3 levels */
  var HF_LVL_CAP   = 6;              /* and never more than six of them */
  /* what an equipped PASSIVE is worth, by its own rarity 0..5 */
  var HF_PASS_SOAK = [2, 3, 5, 7, 9, 12];

  function hfCharLevel(c){
    try {
      if(typeof window.hohCharLevel === 'function'){
        var l = window.hohCharLevel(c) | 0;
        if(l > 0) return l;
      }
    } catch(_){}
    return 1;
  }
  /* ⛔ WARM THE ABILITY CACHE, ONCE, WITHOUT BLOCKING. loadGear() with no
     argument is FREE when the cache is already warm (it returns a
     resolved promise off gearCache, hoh-contract-data.js:796) and is one
     RPC when it is cold. A player who has never opened the Shop on this
     visit has a cold cache, so the first battle they build may resolve
     with no abilities and the hero fights on its level alone. That is
     the correct trade: a fight that waits on the network to start is
     worse than a fight whose hero is slightly understated. */
  /* ⛔ NEVER LATCH ON A CALL THAT CANNOT POPULATE THE CACHE. The first cut
     set the latch on the line ABOVE the load it guards, and it is called
     for signed-OUT visitors too (hfPlayerChampion calls it before it
     checks ownership). loadGear returns an empty resolved promise and
     leaves the cache null when nobody is signed in, so one battle viewed
     signed out latched the warm forever: a player who then signed in
     through the Hall's own door, without ever opening the Shop, fought
     every later battle with no abilities at all and no way to notice.
     The latch is keyed on the ACCOUNT now, so signing in, or switching
     accounts, warms again. */
  var _hfGearWarmed = '';            /* the account the warm belongs to */
  function hfGearAccount(){
    try {
      var em = (typeof window.hohYourHallEmail === 'function') ? window.hohYourHallEmail() : '';
      return em ? String(em).trim().toLowerCase() : '';
    } catch(_){ return ''; }
  }
  function hfWarmGear(){
    var acct = hfGearAccount();
    if(!acct) return;                /* signed out: loadGear is a no-op, latch nothing */
    if(_hfGearWarmed === acct) return;
    var D = null;
    try { D = window.HOHContractData; } catch(_){}
    /* not loaded yet is not a failure either: try again on the next build */
    if(!D || typeof D.loadGear !== 'function') return;
    _hfGearWarmed = acct;
    try { D.loadGear(); } catch(_){ _hfGearWarmed = ''; }
  }
  /* ⛔ BOTH KEY SHAPES ARE TRIED. A card in the player's own collection
     is keyed 'ff:<id>' when it is a FramedFantasy portrait and 'own:<id>'
     otherwise (sql/R435 hoh_holds_card_key), and this file cannot tell
     which from a hall row alone. Asking for the wrong one returns an
     empty pair rather than throwing, so trying both is both correct and
     cheap. */
  function hfGearFor(c){
    try {
      var D = window.HOHContractData;
      if(!D || typeof D.gearFor !== 'function' || !c || !c.id) return null;
      var g = D.gearFor('ff:' + c.id);
      if(!g || (!g.active && !g.passive)) g = D.gearFor('own:' + c.id);
      if(!g || (!g.active && !g.passive)) return null;
      return g;
    } catch(_){ return null; }
  }
  /* The ability's real player-facing name, so the log names the thing the
     player actually rolled instead of inventing a word for it. */
  function hfSkillName(id, slot){
    try {
      var C = window.HOHContractContent;
      if(!C || !id) return null;
      var n = (slot === 'passive')
        ? (C.passiveName && C.passiveName(id))
        : (C.skillName && C.skillName(id));
      return n ? String(n).toUpperCase() : null;
    } catch(_){ return null; }
  }

  /* A champion is chosen from data that has crossed JSON twice (PostgREST and
     the Your Hall cache), so Hearts may be either a number or a numeric string.
     Coerce once here instead of making the choice depend on transport shape. */
  function hfChampionHeart(c){
    var n = Number(c && c.hearts);
    return isFinite(n) && n >= 0 ? n : 0;
  }
  function hfChampionTime(c){
    var raw = (c && c.created_at != null) ? String(c.created_at) : '';
    var n = raw ? Date.parse(raw) : NaN;
    return isFinite(n) ? n : -8640000000000000;
  }
  /* Strict total order: Hearts first, then newest creation, then the greater
     string id (matching the Hall query's created_at DESC, id DESC order). The
     answer is therefore identical even if a cache or test hands us the same
     rows in another order. */
  function hfChampionBetter(a, b){
    if(!a) return false;
    if(!b) return true;
    var ah = hfChampionHeart(a), bh = hfChampionHeart(b);
    if(ah !== bh) return ah > bh;
    var at = hfChampionTime(a), bt = hfChampionTime(b);
    if(at !== bt) return at > bt;
    return String(a.id || '') > String(b.id || '');
  }

  /* Their strongest hero, as a unit ready to be dropped onto a side.
     ⛔ ONE RULE, NOT A SECOND DECISION. The player already has a choice
     to make on this screen and it is which side to back. Asking them to
     also pick a hero would double the decisions on a phone for a fight
     that lasts twenty seconds, so their best hero fights, always, and
     the screen names them so it is never a mystery who turned up. */
  function hfPlayerChampion(){
    hfWarmGear();
    var mine = hfOwnedCharacters();
    if(!mine.length) return null;
    var best = null;
    for(var i = 0; i < mine.length; i++){
      if(hfChampionBetter(mine[i], best)) best = mine[i];
    }
    if(!best) return null;
    var u = hfMakeUnit(best, 'A', 0);
    u.champ = true;                 /* the flag every champion path reads */
    u.lvl = hfCharLevel(best);
    /* THE LEVEL IS REAL POWER, and it is capped. Uncapped, a level-90
       hero would price every fight at the HF_PMAX ceiling and the bet
       would stop being a bet. Six points is worth roughly one rarity
       band, which is the honest size of "this one is mine and I have
       raised it". */
    u.bonus = Math.min(HF_LVL_CAP, Math.floor((u.lvl - 1) / HF_LVL_STEP));
    var g = hfGearFor(best);
    if(g){
      if(g.passive){
        var pr = Math.max(0, Math.min(5, g.passive.r | 0));
        u.soak += HF_PASS_SOAK[pr];       /* a passive makes them harder to put down */
      }
      if(g.active){
        var ar = Math.max(0, Math.min(5, g.active.r | 0));
        /* ⛔ THE ACTIVE BECOMES A FOURTH MOVE, UNDER ITS REAL NAME. This
           is the whole point of Jan's ask: the ability the player paid
           to roll has to be a thing that HAPPENS in the fight and gets
           named in the log, not a silent number. It is typed 'big' so
           the engine's own move AI already knows how to reach for it,
           and it carries a cooldown so it punctuates the fight instead
           of replacing it. */
        u.abilityMove = {
          n: hfSkillName(g.active.id, 'active') || 'SIGNATURE',
          t: 'big', m: 1.15 + ar * 0.09, cd: 2, own: 1
        };
      }
    }
    return u;
  }
  /* Re-derive the price from the rosters as they now stand. */
  function hfRepriceBattle(ev){
    if(!ev || !ev.teams) return;
    var pA = hfWinChance(ev.teams.A, ev.teams.B);
    ev.odds = { A: pA, B: 1 - pA };
    ev.mult = { A: hfMultiplier(pA), B: hfMultiplier(1 - pA) };
    ev.pool = { A: hfPoolMax(ev.teams.A), B: hfPoolMax(ev.teams.B) };
    ev.poolMax = { A: ev.pool.A, B: ev.pool.B };
  }
  function hfChampionDetach(ev){
    if(!ev || !ev.teams) return;
    /* ⛔ SWEEPS BOTH SIDES BY FLAG, never "pop the side I think it is
       on". The champion moves every time the player changes their mind,
       and a single wrong index here would delete a real fighter out of
       the middle of a row and leave the board and the tape disagreeing
       about who is in the fight. The mirror is swept the same way and
       for the same reason. */
    ['A','B'].forEach(function(s){
      var team = ev.teams[s];
      if(!team) return;
      for(var k = team.length - 1; k >= 0; k--){
        if(team[k] && (team[k].champ || team[k].mirror)) team.splice(k, 1);
      }
      for(var j = 0; j < team.length; j++) team[j].i = j;
    });
    ev.champSide = null;
  }
  /* R594 (Jan): "make sure a random hero also joins the other side when
     you join 1 side so the fight is fair."
     ⛔ THE TWO ARRIVE TOGETHER OR NOT AT ALL. Jan's earlier ruling was
     that your hero shifts the odds your way; this one says the BOARD
     must be even. Both are satisfied by making the extra body free and
     the QUALITY of the body the whole edge: your best hero, carrying its
     level and its rolled ability, against a stranger of the same tier as
     the rest of the fight. Bring someone who outclasses the room and you
     dominate; bring someone outclassed and you are a liability, which is
     what makes the Hall worth investing in. */
  function hfChampionAttach(ev, side){
    hfChampionDetach(ev);
    if(!ev || !ev.champion) return;
    if(side !== 'A' && side !== 'B') return;
    var team = ev.teams[side];
    if(!team) return;
    var u = ev.champion;
    u.side = side;
    u.i = team.length;              /* always last, so no other index moves */
    u.dead = false;                 /* a re-pick must never inherit a rewind */
    team.push(u);
    ev.champSide = side;
    /* the mirror walks onto the OTHER side, in the same moment */
    var foe = (side === 'A') ? 'B' : 'A';
    var m = ev.mirror, mteam = ev.teams[foe];
    if(m && mteam){
      m.side = foe;
      m.i = mteam.length;
      m.dead = false;
      m.mirror = true;
      mteam.push(m);
    }
  }

  /* One organic fight per Explore day, shared by every duel and brawl.
     Keep this separate from dayState, whose stale picker snapshots can
     overwrite nested writes. Explore resets at UTC midnight. */
  var _hfRequestedFightPicking = false;
  var _hfOrganicFightSeen = Object.create(null);
  function hfOrganicFightKey(){
    var account = (typeof window.hohYourHallEmail === 'function') ? window.hohYourHallEmail() : '';
    return 'hoh-ff-organic-fight-' + ymdToday() + '-' + String(account || 'guest').trim().toLowerCase();
  }
  function hfOrganicFightUsed(){
    var key = hfOrganicFightKey();
    if(_hfOrganicFightSeen[key]) return true;
    try { return localStorage.getItem(key) === '1'; } catch(_){ return false; }
  }
  function hfRememberOrganicFight(ev){
    if(!ev || !ev.hfBattle || ev._hfRequestedFight || ev._hfOrganicCounted) return;
    var key = hfOrganicFightKey();
    _hfOrganicFightSeen[key] = true;
    ev._hfOrganicCounted = true;
    try { localStorage.setItem(key, '1'); } catch(_){}
  }
  function hfPickRequestedEvent(picker, opts){
    var previous = _hfRequestedFightPicking;
    _hfRequestedFightPicking = true;
    try { return picker(opts); }
    finally { _hfRequestedFightPicking = previous; }
  }
  function hfBuildBattle(opts){
    if(!_hfRequestedFightPicking && hfOrganicFightUsed()) return null;
    var bank = (typeof window.heartsRemainingToday === 'function') ? window.heartsRemainingToday() : 0;
    /* Below 2 hearts there is no bet worth naming. The old builder used
       bank < 1 and then silently priced the bet DOWN to whatever the
       player had while paying the same, which the player was never
       told about. A typed wager makes that whole idea unnecessary. */
    if(bank < 2 && !opts.free) return null;
    /* R593: their hero is chosen BEFORE the draft so the draft can keep
       them out of it. A champion who was also drafted as a stranger
       would stand on the board twice under one name. */
    var champ = hfPlayerChampion();
    var champId = champ ? champ.id : null;
    var userFilter = opts.poolFilter;
    var draft = hfDraftTeams(opts.size, opts.tier, { filter: function(c){
      if(!c || !c.id) return false;
      if(champId && c.id === champId) return false;
      return userFilter ? !!userFilter(c) : true;
    }});
    if(!draft) return null;

    var pA = hfWinChance(draft.A, draft.B);
    var pB = 1 - pA;
    return {
      type: opts.type,
      _eventKey: opts.type,
      hfBattle: true,                 /* the one flag the renderer routes on */
      _hfRequestedFight: _hfRequestedFightPicking,
      title: opts.title,
      subtitle: opts.subtitle,
      teams: { A: draft.A, B: draft.B },
      size: draft.size,
      shrunk: !!draft.shrunk,
      sideName: opts.sideName || { A: 'The Blue Side', B: 'The Red Side' },
      odds: { A: pA, B: pB },
      mult: { A: hfMultiplier(pA), B: hfMultiplier(pB) },
      /* the board opens showing the real starting health of each side */
      pool: { A: hfPoolMax(draft.A), B: hfPoolMax(draft.B) },
      poolMax: { A: hfPoolMax(draft.A), B: hfPoolMax(draft.B) },
      phase: 'wager',                 /* wager | coin | fighting | resolved */
      pickedSide: null,
      /* R593/R594: both wait on the sideline until a side is picked, and
         then walk on together, one to each side. */
      champion: champ,
      mirror: (champ && draft.spare) ? draft.spare : null,
      champSide: null,
      wager: 0,
      suggested: Math.max(HF_WAGER_MIN, Math.min(hfWagerCeiling(), opts.suggest || 5)),
      tape: null, tapeIdx: 0, result: null, won: null, paid: 0
    };
  }

  /* ⛔ _buildDuelEvent WAS DELETED IN R591, not left dormant.
     It built the old {combatants:[a,b], diceMax, cost, payout} shape,
     which no renderer in this file can read any more. A dormant builder
     is worse than no builder: the next person to add a duel would find
     it, call it, and ship an event that renders blank. Every one of its
     six callers now calls hfBuildBattle. */

  /* ================================================================ */
  /* Tavern Brawl — 3-way bet gambling event                           */
  /* ================================================================ */
  /* Three brawlers, user pays 5 hearts to back one. Each rolls a
     d20, highest wins, ties go to the lowest seat number (favoring
     the user\'s pick on a tie if they picked the early seat — kept
     simple so the resolution feels fair). Win pays 18 hearts (net
     +13); loss returns nothing. ~33% odds = +1 EV per brawl. Once
     per day. */
  function brawlTodayKey(){
    var d = new Date();
    var ymd = d.getFullYear() + '-' + String(d.getMonth()+1).padStart(2,'0') + '-' + String(d.getDate()).padStart(2,'0');
    return 'hoh-brawl-done-' + ymd;
  }
  function brawlUsedToday(){
    try { return localStorage.getItem(brawlTodayKey()) === '1'; } catch(_){ return false; }
  }
  function markBrawlUsedToday(){
    try { localStorage.setItem(brawlTodayKey(), '1'); } catch(_){}
  }
  /* THE BRAWLS. Jan: "we need to take off the eight-player one, where
     there's just eight characters and then you click one of them ...
     those styles aren't going to work anymore, because obviously those
     ones require some weird 1v1v1v1v1 type of thing, where we don't
     have that mechanic in battlegrounds yet."

     ⛔ AND BOTH OF THEM WERE RIGGED. pickTavernBrawl4Event chose
     winnerIdx = Math.floor(Math.random() * 4) at PRODUCE time, then
     _brawl4SynthesizeRolls manufactured dice to fit (the winner rolled
     5 or 6, everyone else 1 to 4) and the player's +1 was cosmetic, as
     that handler's own comment admitted. barFight did the same. Worse,
     a mythic host was SLOTTED INTO the winning index, so "back the
     mythic" was a guaranteed win a daily player would have learned.
     A simulated fight is honest by construction. Nothing here picks a
     winner in advance. */
  function pickTavernBrawlEvent(){
    return hfBuildBattle({
      type: 'tavernBrawl', size: 3, tier: null, suggest: 6,
      title: 'A Tavern Brawl',
      subtitle: 'It started over a chair. Back a side and see it through.',
      sideName: { A: 'By the Fire', B: 'By the Door' },
      poolFilter: function(c){
        var rough = {Fighter:1,Barbarian:1,Monk:1,Rogue:1,Bard:1,Sorcerer:1,Knight:1,Charlatan:1};
        return c && c.character_class && rough[c.character_class];
      }
    });
  }
  function pickTavernBrawl4Event(){
    return hfBuildBattle({
      type: 'tavernBrawl4', size: 4, tier: null, suggest: 6,
      title: 'The Tavern Erupts',
      subtitle: 'Both sides are in it now, and the landlord has stopped counting the damage.',
      sideName: { A: 'By the Fire', B: 'By the Door' },
      poolFilter: function(c){
        var rough = {Fighter:1,Barbarian:1,Monk:1,Rogue:1,Bard:1,Sorcerer:1,Knight:1,Charlatan:1};
        return c && c.character_class && rough[c.character_class];
      }
    });
  }

  /* ================================================================ */
  /* R86: Toll Road — unlimited gating event                            */
  /* ================================================================ */
  /* A guard blocks the road. Pay 5 hearts to pass (or your remaining
     hearts if <5, no negatives). OR if you have 10+ hearts, gamble:
     stake 10, roll d20 vs guard. Win = +10 hearts back, free passage,
     +30 mythic bonus if mythic host. Lose = 10 gone.
     Host is hidden char if available, else a Barbarian/Fighter. */
  var TOLL_ROAD_PASS_COST = 5;
  var TOLL_ROAD_DUEL_STAKE = 10;
  function pickTollRoadEvent(){
    var allChars = (typeof window.hohGetAllCharacters === 'function') ? window.hohGetAllCharacters() : [];
    if(!allChars || !allChars.length) return null;
    /* Host pool: hidden chars first (god-mode reveal), else Barbarian/
       Fighter, else any char with an id. */
    var hidden = allChars.filter(function(c){ return c && c.is_hidden && c.id; });
    var guards = allChars.filter(function(c){
      return c && c.character_class && (c.character_class === 'Barbarian' || c.character_class === 'Fighter') && c.id;
    });
    var pool = hidden.length ? hidden : (guards.length ? guards : allChars.filter(function(c){ return c && c.id; }));
    if(!pool.length) return null;
    var host = pool[Math.floor(Math.random() * pool.length)];
    /* R65 mythic host integration — if a mythic rolls, slot in. */
    var mythicHost = (typeof window.hohRollMythicHost === 'function') ? window.hohRollMythicHost() : null;
    if(mythicHost) host = mythicHost;
    return {
      type: 'tollRoad',
      host: host,
      mythicHost: !!mythicHost,
      phase: 'idle', /* idle | duelRolling | resolved */
      passCost: TOLL_ROAD_PASS_COST,
      duelStake: TOLL_ROAD_DUEL_STAKE,
      outcome: null, /* 'paid' | 'duelWin' | 'duelLose' */
      rolls: null, /* [userRoll, guardRoll] */
      _eventKey: 'tollRoad'
    };
  }
  function renderTollRoadHtml(state){
    var phase = state.phase || 'idle';
    var host = state.host || {};
    var bank = (typeof window.heartsRemainingToday === 'function') ? window.heartsRemainingToday() : 0;
    /* Host card — same compact treatment as other unlimited events.
       R136-toll-reframe: bake the saved image_offset_y into the host
       portrait so the user's chosen reframe applies. Add data-hero-id
       on the card so the live-reframe repainter (hohRepaintImageOffset)
       finds and updates this image when the user changes the offset
       elsewhere. */
    var hImg = host.image_thumb_url || host.image_url || '';
    var hLetter = esc((host.name || '?').charAt(0));
    var hPos = (typeof window.hohImgObjPos === 'function')
      ? window.hohImgObjPos(host.id, 'primary')
      : ('50% ' + ((typeof window.hohGetImageOffsetY === 'function') ? window.hohGetImageOffsetY(host.id, 'primary') : 50) + '%');
    var hStyle = (typeof window.hohImgStyle === 'function') ? window.hohImgStyle(host.id, 'primary') : ('object-position:' + hPos + ';');
    var hMedia = hImg
      ? '<img src="' + esc(hImg) + '" alt="" loading="eager" decoding="async" crossorigin="anonymous" draggable="false" oncontextmenu="return false" data-letter="' + hLetter + '" style="' + hStyle + '" onerror="if(window.hohImgFallback)hohImgFallback(this);">'
      : '<div class="hoh-fresh-letter">' + hLetter + '</div>';
    var hostHtml = '<div class="hoh-fresh-toll-host">' +
      '<div role="button" tabindex="0" class="hoh-fresh-card hoh-fresh-card-host" data-id="' + esc(host.id || '') + '" data-hero-id="' + esc(host.id || '') + '"' +
        ' onclick="if(window.hohOpenDetail)hohOpenDetail(\'' + esc(host.id || '') + '\')"' +
        ' aria-label="Open ' + esc(host.name || '') + '">' +
        '<span class="hoh-fresh-media">' + hMedia + '</span>' +
        '<span class="hoh-fresh-body">' +
          '<span class="hoh-fresh-name">' + esc(host.name || 'A guard') + '</span>' +
          '<span class="hoh-fresh-meta">' + esc((host.race || '') + (host.character_class ? ' ' + host.character_class : '')) + '</span>' +
        '</span>' +
      '</div>' +
    '</div>';
    var stageHtml;
    if(phase === 'idle'){
      var actualPassCost = Math.min(state.passCost, Math.max(0, bank));
      var canDuel = bank >= state.duelStake;
      stageHtml = '<div class="hoh-fresh-toll-stage">' +
        '<div class="hoh-fresh-toll-prompt">"This road is mine. Pay the toll, or roll for it."</div>' +
        '<div class="hoh-fresh-toll-actions">' +
          '<button type="button" class="hoh-fresh-coin-toss" onclick="hohFreshFacesTollPay()">' +
            '<span>Pay the toll</span>' +
            '<span class="hoh-fresh-dice-cost">&#9829;&#xFE0E; &minus;' + actualPassCost + '</span>' +
          '</button>' +
          (canDuel
            ? '<button type="button" class="hoh-fresh-coin-toss" onclick="hohFreshFacesTollDuel()">' +
                '<span>Roll for it (double or nothing)</span>' +
              '</button>'
            : '<div class="hoh-fresh-toll-locked">Need ' + state.duelStake + '&#9829;&#xFE0E; to roll for it.</div>') +
        '</div>' +
      '</div>';
    } else if(phase === 'duelRolling'){
      stageHtml = '<div class="hoh-fresh-toll-stage">' +
        '<div class="hoh-fresh-toll-prompt">The dice clatter. The guard watches.</div>' +
        '<div class="hoh-fresh-duel-vs hoh-fresh-duel-vs-rolling" aria-hidden="true">' +
          '<span class="hoh-fresh-duel-die hoh-fresh-duel-die-rolling">?</span>' +
          '<span class="hoh-fresh-duel-die hoh-fresh-duel-die-rolling hoh-fresh-duel-die-r">?</span>' +
        '</div>' +
      '</div>';
    } else { /* resolved */
      var headline, sub;
      if(state.outcome === 'paid'){
        headline = 'You pay and pass.';
        sub = 'The guard nods. You pass.';
      } else if(state.outcome === 'duelWin'){
        /* R92: dropped "+10 hearts" from the headline — winning the
           duel just refunds the stake (net 0). The user passes for
           free. Copy now reflects that honestly. */
        headline = 'You roll high. Free passage.';
        sub = 'The guard steps aside, surprised. You keep your hearts.';
      } else {
        headline = 'The guard rolls higher. -' + state.duelStake + ' hearts.';
        sub = 'You lose the toll and the way through. Try a different way.';
      }
      var headlineCls = (state.outcome === 'duelLose') ? ' hoh-fresh-duel-headline-lose' : ' hoh-fresh-duel-headline-win';
      /* R92: Labeled, side-by-side dice so the user can clearly tell
         which roll is theirs vs the guard's. Previous render stacked
         two unlabeled dice and the user couldn't read which was which.
         rolls[0] = user, rolls[1] = guard. */
      var userWonDie = (state.outcome === 'duelWin');
      var rollsRow = state.rolls
        ? '<div class="hoh-fresh-toll-rolls">' +
            '<div class="hoh-fresh-toll-roll-cell">' +
              '<span class="hoh-fresh-toll-roll-label">You</span>' +
              '<span class="hoh-fresh-duel-die hoh-fresh-duel-die-resolved' + (userWonDie ? ' hoh-fresh-duel-die-win' : ' hoh-fresh-duel-die-lose') + '">' + state.rolls[0] + '</span>' +
            '</div>' +
            '<span class="hoh-fresh-toll-roll-vs" aria-hidden="true">vs</span>' +
            '<div class="hoh-fresh-toll-roll-cell">' +
              '<span class="hoh-fresh-toll-roll-label">Guard</span>' +
              '<span class="hoh-fresh-duel-die hoh-fresh-duel-die-resolved' + (userWonDie ? ' hoh-fresh-duel-die-lose' : ' hoh-fresh-duel-die-win') + '">' + state.rolls[1] + '</span>' +
            '</div>' +
          '</div>'
        : '';
      stageHtml = '<div class="hoh-fresh-toll-stage">' +
        rollsRow +
        '<div class="hoh-fresh-duel-result' + headlineCls + '">' +
          '<strong>' + headline + '</strong>' +
          '<span class="hoh-fresh-duel-result-sub">' + sub + '</span>' +
          '<button type="button" class="hoh-fresh-coin-toss hoh-fresh-dice-continue" onclick="hohFreshFacesTollContinue()">' +
            '<span>Back to the Hall</span>' +
          '</button>' +
        '</div>' +
      '</div>';
    }
    /* R93/R96: Refresh icon is hidden across ALL toll phases.
       - Idle: paying 1 heart to skip a road event makes no narrative
         sense; only the auto-bar timer should advance.
       - duelRolling: dice are mid-spin, can't bail.
       - Resolved: re-rolling would charge another heart for no gain. */
    var hideRefresh = true;
    return '<div class="hoh-fresh-special hoh-fresh-special-toll' + (state.mythicHost ? ' hoh-fresh-special-mythic-host' : '') + '">' +
      eventHeaderHtml('A Toll Road', 'A guard stands in the road. Pay the toll, or roll the bones.', { centered: true, hideRefresh: hideRefresh }) +
      hostHtml +
      stageHtml +
    '</div>';
  }
  /* Pay the toll: deduct passCost (capped to bank, no negatives). */
  window.hohFreshFacesTollPay = function(){
    if(!currentSpecial || currentSpecial.type !== 'tollRoad') return;
    if(currentSpecial.phase !== 'idle') return;
    var bank = (typeof window.heartsRemainingToday === 'function') ? window.heartsRemainingToday() : 0;
    var spend = Math.min(currentSpecial.passCost, Math.max(0, bank));
    if(spend > 0){
      try {
        var raw = parseInt(localStorage.getItem('hoh-heart-bank-v1') || '0', 10) || 0;
        try{ window.hohHeartHint && window.hohHeartHint('spend_special'); }catch(e){}
        localStorage.setItem('hoh-heart-bank-v1', String(Math.max(0, raw - spend)));
        try { document.dispatchEvent(new CustomEvent('hoh:hearts-bumped', { detail: { spent: spend } })); } catch(_){}
      } catch(_){}
      if(typeof window.hohRenderHeaderCounter === 'function') window.hohRenderHeaderCounter();
    }
    currentSpecial.outcome = 'paid';
    currentSpecial.phase = 'resolved';
    var container = _ffContainer();
    if(container) container.innerHTML = renderTollRoadHtml(currentSpecial);
    if(typeof startAutoTimer === 'function') setTimeout(startAutoTimer, 0);
  };
  /* Duel the guard: stake duelStake, roll d20s. */
  window.hohFreshFacesTollDuel = function(){
    if(!currentSpecial || currentSpecial.type !== 'tollRoad') return;
    if(currentSpecial.phase !== 'idle') return;
    var bank = (typeof window.heartsRemainingToday === 'function') ? window.heartsRemainingToday() : 0;
    if(bank < currentSpecial.duelStake){
      try { window.hohShowHeartToast('Not enough hearts', 'You need ' + currentSpecial.duelStake + ' to roll.'); } catch(_){}
      return;
    }
    /* Pay the stake upfront. */
    try {
      var raw = parseInt(localStorage.getItem('hoh-heart-bank-v1') || '0', 10) || 0;
      try{ window.hohHeartHint && window.hohHeartHint('spend_duel'); }catch(e){}
      localStorage.setItem('hoh-heart-bank-v1', String(Math.max(0, raw - currentSpecial.duelStake)));
      try { document.dispatchEvent(new CustomEvent('hoh:hearts-bumped', { detail: { spent: currentSpecial.duelStake } })); } catch(_){}
    } catch(_){}
    if(typeof window.hohRenderHeaderCounter === 'function') window.hohRenderHeaderCounter();
    var userRoll = Math.floor(Math.random() * 20) + 1;
    var guardRoll = Math.floor(Math.random() * 20) + 1;
    var won = userRoll >= guardRoll; /* tie goes to user */
    currentSpecial.phase = 'duelRolling';
    var container = _ffContainer();
    if(container) container.innerHTML = renderTollRoadHtml(currentSpecial);
    setTimeout(function(){
      if(!currentSpecial || currentSpecial.type !== 'tollRoad') return;
      currentSpecial.rolls = [userRoll, guardRoll];
      currentSpecial.outcome = won ? 'duelWin' : 'duelLose';
      currentSpecial.phase = 'resolved';
      if(container) container.innerHTML = renderTollRoadHtml(currentSpecial);
      if(won){
        /* R92: Refund the staked hearts (net 0 = free passage) but
           don't toast it as "+10 hearts" — the user staked 10 a moment
           ago and would read that as profit when it's actually break-
           even. Toast frames it as the actual outcome: free passage. */
        if(typeof window.hohAddHeartsToBank === 'function'){
          try{ window.hohHeartHint && window.hohHeartHint('earn_explore_duel'); }catch(e){}
          try { window.hohAddHeartsToBank(currentSpecial.duelStake); } catch(_){}
        }
        try { window.hohShowHeartToast('Free passage', 'You kept your hearts and walked through.'); } catch(_){}
        if(currentSpecial.mythicHost){
          setTimeout(function(){
            try { applyMythicHostBonus('#hoh-fresh-faces .hoh-fresh-toll-host .hoh-fresh-card-host'); } catch(_){}
          }, 380);
        }
      } else {
        /* R93: Visible heart-loss fly so the -10 reads as a real cost.
           The stake was already deducted upfront when TollDuel started,
           so the bank is correct — this is purely the visual feedback
           the user wanted ("when you lose the roll for -10 hearts on
           the toll, have a visible effect of losing the hearts"). */
        try { flyHeartLossFromPill(currentSpecial.duelStake); } catch(_){}
        try { window.hohShowHeartToast('-' + currentSpecial.duelStake + ' hearts', 'The guard rolled higher.'); } catch(_){}
      }
      if(typeof startAutoTimer === 'function') setTimeout(startAutoTimer, 0);
    }, 1300);
  };
  window.hohFreshFacesTollContinue = function(){
    if(!currentSpecial || currentSpecial.type !== 'tollRoad') return;
    if(currentSpecial.phase !== 'resolved') return;
    currentSpecial = null;
    if(typeof window.hohFreshFacesRefresh === 'function'){
      try { window.hohFreshFacesRefresh({ fromInteractive: true }); } catch(_){}
    }
  };

  /* ================================================================ */
  /* R86: Carnival Strength Test — unlimited dice event                 */
  /* ================================================================ */
  /* Short-statured race host (Halfling, Gnome, Dwarf, Goblin, Kobold)
     runs the booth. 3 hearts to play.
     Two d6. Beat 5 to win 20 hearts; a total of 5 still loses. */
  var CARNIVAL_COST = 3;
  var CARNIVAL_PAYOUT = 20;
  var CARNIVAL_THRESHOLD = 5; /* sum must be STRICTLY greater than 5, i.e. >= 6 */
  var CARNIVAL_RACES = {Halfling:1, Gnome:1, Dwarf:1, Goblin:1, Kobold:1};
  /* R165: Barker dialogue pool. One line is picked per event so the
     booth has a voice. Idle-phase only — once the dice resolve, the
     "barker grins" / "barker shrugs" sub-line handles the outcome. */
  var CARNIVAL_BARKER_LINES = [
    'Step right up! Three hearts for the swing, twenty for the bell.',
    'Roll a six or higher, friend, and the prize is yours.',
    'Two dice. Five to beat. The hammer is light, the bell is heavy — but luck is what we sell here.',
    'I\'ve seen kings miss this throw and goblins ring it twice. The dice don\'t care who you are.',
    'Three hearts on the counter, one swing of the dice. Hit a six or better and walk away richer.',
    'You look like you\'ve got the arm for it. Drop three hearts, give the dice a kiss, beat a five.',
    'No rigged dice at this booth, friend. Just clean odds and a heavy bell. Care to try?',
    'Beat five and win twenty. Five or less… well, the dice keep what the dice take.'
  ];
  function _pickCarnivalLine(){
    return CARNIVAL_BARKER_LINES[Math.floor(Math.random() * CARNIVAL_BARKER_LINES.length)];
  }
  function pickCarnivalEvent(){
    var bank = (typeof window.heartsRemainingToday === 'function') ? window.heartsRemainingToday() : 0;
    if(bank < CARNIVAL_COST) return null;
    var allChars = (typeof window.hohGetAllCharacters === 'function') ? window.hohGetAllCharacters() : [];
    if(!allChars || !allChars.length) return null;
    var pool = allChars.filter(function(c){ return c && c.id && c.race && CARNIVAL_RACES[c.race]; });
    if(!pool.length) return null;
    var host = pool[Math.floor(Math.random() * pool.length)];
    var mythicHost = (typeof window.hohRollMythicHost === 'function') ? window.hohRollMythicHost() : null;
    if(mythicHost) host = mythicHost;
    return {
      type: 'carnival',
      host: host,
      mythicHost: !!mythicHost,
      dialogue: _pickCarnivalLine(),
      phase: 'idle', /* idle | rolling | resolved */
      cost: CARNIVAL_COST,
      payout: CARNIVAL_PAYOUT,
      threshold: CARNIVAL_THRESHOLD,
      rolls: null, /* R93: 2d6 — array [die1, die2] */
      won: null,
      _eventKey: 'carnival'
    };
  }
  function renderCarnivalHtml(state){
    var phase = state.phase || 'idle';
    var host = state.host || {};
    var hImg = host.image_thumb_url || host.image_url || '';
    var hLetter = esc((host.name || '?').charAt(0));
    /* R140-F: Offset-aware host img — see _eventCharImg helper. */
    var hMedia = _eventCharImg(host, { eager: true, draggable: false });
    var hostHtml = '<div class="hoh-fresh-carnival-host">' +
      '<div role="button" tabindex="0" class="hoh-fresh-card hoh-fresh-card-host" data-id="' + esc(host.id || '') + '"' +
        ' onclick="if(window.hohOpenDetail)hohOpenDetail(\'' + esc(host.id || '') + '\')"' +
        ' aria-label="Open ' + esc(host.name || '') + '">' +
        '<span class="hoh-fresh-media">' + hMedia + '</span>' +
        '<span class="hoh-fresh-body">' +
          '<span class="hoh-fresh-name">' + esc(host.name || 'A barker') + '</span>' +
          '<span class="hoh-fresh-meta">' + esc((host.race || '') + (host.character_class ? ' ' + host.character_class : '')) + '</span>' +
        '</span>' +
      '</div>' +
    '</div>';
    /* R165: Barker dialogue under the host card. Idle phase only — the
       resolved phase already shows "The barker grins…" / "The barker
       shrugs…" as the outcome sub-line, so a second quote there would
       crowd the result. */
    var dialogueHtml = '';
    if(phase === 'idle' && state.dialogue){
      dialogueHtml = '<div class="hoh-fresh-carnival-dialogue">' +
        '<span class="hoh-fresh-carnival-dialogue-quote" aria-hidden="true">&ldquo;</span>' +
        '<span class="hoh-fresh-carnival-dialogue-text">' + esc(state.dialogue) + '</span>' +
      '</div>';
    }
    /* R93: Trimmed copy. The previous render had a flavor prompt + a
       full rules row + a button label all explaining the same mechanic.
       Now: just the button, the cost chip, and a short flavor line.
       Two d6 dice replace the single d20. */
    var stageHtml;
    if(phase === 'idle'){
      stageHtml = '<div class="hoh-fresh-carnival-stage">' +
        '<button type="button" class="hoh-fresh-coin-toss" onclick="hohFreshFacesCarnivalRoll()">' +
          '<span>Take a swing</span>' +
          '<span class="hoh-fresh-dice-cost">&#9829;&#xFE0E; &minus;' + state.cost + '</span>' +
        '</button>' +
      '</div>';
    } else if(phase === 'rolling'){
      stageHtml = '<div class="hoh-fresh-carnival-stage">' +
        '<div class="hoh-fresh-carnival-dice">' +
          '<span class="hoh-fresh-duel-die hoh-fresh-duel-die-rolling">?</span>' +
          '<span class="hoh-fresh-duel-die hoh-fresh-duel-die-rolling hoh-fresh-duel-die-r">?</span>' +
        '</div>' +
      '</div>';
    } else { /* resolved */
      var won = state.won;
      var headline = won
        ? 'You hit the bell. +' + state.payout + ' hearts.'
        : 'Short of the mark.';
      var headlineCls = won ? ' hoh-fresh-duel-headline-win' : ' hoh-fresh-duel-headline-lose';
      var sub = won ? 'The barker grins and pays out.' : 'The barker shrugs. "Try again, friend."';
      var dieCls = won ? ' hoh-fresh-duel-die-win' : ' hoh-fresh-duel-die-lose';
      var d1 = (state.rolls && state.rolls[0] != null) ? state.rolls[0] : '?';
      var d2 = (state.rolls && state.rolls[1] != null) ? state.rolls[1] : '?';
      stageHtml = '<div class="hoh-fresh-carnival-stage">' +
        '<div class="hoh-fresh-carnival-dice">' +
          '<span class="hoh-fresh-duel-die hoh-fresh-duel-die-resolved' + dieCls + '">' + d1 + '</span>' +
          '<span class="hoh-fresh-duel-die hoh-fresh-duel-die-resolved' + dieCls + '">' + d2 + '</span>' +
        '</div>' +
        '<div class="hoh-fresh-duel-result' + headlineCls + '">' +
          '<strong>' + headline + '</strong>' +
          '<span class="hoh-fresh-duel-result-sub">' + sub + '</span>' +
          '<button type="button" class="hoh-fresh-coin-toss hoh-fresh-dice-continue" onclick="hohFreshFacesCarnivalContinue()">' +
            '<span>Back to the Hall</span>' +
          '</button>' +
        '</div>' +
      '</div>';
    }
    /* R93: Trimmed header sub from the verbose rules to a single short
       flavor line. Refresh icon is hidden once resolved so a tap on the
       refresh costs nothing — only the auto-bar advances. */
    var hideRefresh = (phase === 'resolved');
    return '<div class="hoh-fresh-special hoh-fresh-special-carnival' + (state.mythicHost ? ' hoh-fresh-special-mythic-host' : '') + '">' +
      eventHeaderHtml('A Carnival Strength Test', 'Three hearts. Two dice. Beat ' + state.threshold + ' to win ' + state.payout + ' hearts.', { centered: true, hideRefresh: hideRefresh }) +
      hostHtml +
      dialogueHtml +
      stageHtml +
    '</div>';
  }
  window.hohFreshFacesCarnivalRoll = function(){
    if(!currentSpecial || currentSpecial.type !== 'carnival') return;
    if(currentSpecial.phase !== 'idle') return;
    var bank = (typeof window.heartsRemainingToday === 'function') ? window.heartsRemainingToday() : 0;
    if(bank < currentSpecial.cost){
      try { window.hohShowHeartToast('Not enough hearts', 'You need ' + currentSpecial.cost + ' to play.'); } catch(_){}
      return;
    }
    try {
      var raw = parseInt(localStorage.getItem('hoh-heart-bank-v1') || '0', 10) || 0;
      try{ window.hohHeartHint && window.hohHeartHint('spend_special'); }catch(e){}
      localStorage.setItem('hoh-heart-bank-v1', String(Math.max(0, raw - currentSpecial.cost)));
      try { document.dispatchEvent(new CustomEvent('hoh:hearts-bumped', { detail: { spent: currentSpecial.cost } })); } catch(_){}
    } catch(_){}
    if(typeof window.hohRenderHeaderCounter === 'function') window.hohRenderHeaderCounter();
    /* R93: 2d6 instead of d20. Sum strictly greater than threshold (5)
       wins — i.e. 6 or higher. ~72% win rate. */
    var d1 = Math.floor(Math.random() * 6) + 1;
    var d2 = Math.floor(Math.random() * 6) + 1;
    var sum = d1 + d2;
    var won = sum > currentSpecial.threshold;
    currentSpecial.phase = 'rolling';
    var container = _ffContainer();
    if(container) container.innerHTML = renderCarnivalHtml(currentSpecial);
    setTimeout(function(){
      if(!currentSpecial || currentSpecial.type !== 'carnival') return;
      currentSpecial.rolls = [d1, d2];
      currentSpecial.won = won;
      currentSpecial.phase = 'resolved';
      if(container) container.innerHTML = renderCarnivalHtml(currentSpecial);
      if(won){
        if(typeof window.hohAddHeartsToBank === 'function'){
          try{ window.hohHeartHint && window.hohHeartHint('earn_explore_special'); }catch(e){}
          try { window.hohAddHeartsToBank(currentSpecial.payout); } catch(_){}
        }
        try { window.hohShowHeartToast('+' + currentSpecial.payout + ' hearts', 'The bell sang.'); } catch(_){}
        /* R119-G: Win celebration — confetti + heart-fly to pill so a
           successful carnival roll reads as a real reward, not a quiet
           toast. Three confetti waves spaced ~220ms apart match the
           Legendary Shop confetti rhythm. Hearts fly from the dice
           element ("the dice produced these hearts") in a flock of
           ~payout/2 sprites — capped at 12 so the burst doesn't drown
           the page. */
        try {
          if(typeof spawnConfetti === 'function'){
            spawnConfetti();
            setTimeout(function(){ try { spawnConfetti(); } catch(_){} }, 220);
            setTimeout(function(){ try { spawnConfetti(); } catch(_){} }, 460);
          }
        } catch(_){}
        try {
          var diceEl = document.querySelector('#hoh-fresh-faces .hoh-fresh-carnival-dice')
                    || document.querySelector('#hoh-fresh-faces .hoh-fresh-carnival-stage')
                    || document.querySelector('#hoh-fresh-faces');
          var heartCount = Math.min(12, Math.max(6, Math.round((currentSpecial.payout || 10) / 2)));
          if(diceEl && typeof flyDiceTraderHeartsToPill === 'function'){
            flyDiceTraderHeartsToPill(diceEl, heartCount);
          }
        } catch(_){}
        if(currentSpecial.mythicHost){
          setTimeout(function(){
            try { applyMythicHostBonus('#hoh-fresh-faces .hoh-fresh-carnival-host .hoh-fresh-card-host'); } catch(_){}
          }, 380);
        }
      }
      if(typeof startAutoTimer === 'function') setTimeout(startAutoTimer, 0);
    }, 1200);
  };
  window.hohFreshFacesCarnivalContinue = function(){
    if(!currentSpecial || currentSpecial.type !== 'carnival') return;
    if(currentSpecial.phase !== 'resolved') return;
    currentSpecial = null;
    if(typeof window.hohFreshFacesRefresh === 'function'){
      try { window.hohFreshFacesRefresh({ fromInteractive: true }); } catch(_){}
    }
  };

  /* R140-Z3n: Removed pickLostAdventurerEvent + pickStarFallsEvent +
     their renderers, click handlers, and renderSpecial branches. The
     pickers were defined but never called from any picker chain — pure
     dead code, ~290 lines. Removed in this turn. */
  /* (Lost Adventurer + Star Falls code lived here. Removed R140-Z3n —
     pickers were never wired into the picker chain so the events
     could never fire. Renderers, click handlers, missTimer, reward
     tables — all dead. The Common Thread section below is the next
     real piece of code in this file.) */
  /* ================================================================ */
  /* R86: Common Thread — guess what 3 chars share                       */
  /* ================================================================ */
  /* Show 3 portraits + names. They share EITHER a race OR a class.
     User guesses what they have in common from the multi-option grid.
     Right answer = +5 hearts. Wrong = -1 heart with on-card visual. */
  var COMMON_THREAD_PAYOUT = 5;
  function pickCommonThreadEvent(){
    var allChars = (typeof window.hohGetAllCharacters === 'function') ? window.hohGetAllCharacters() : [];
    if(!allChars || allChars.length < 3) return null;
    /* Alternate between race-based and class-based threads. */
    var kind = Math.random() < 0.5 ? 'race' : 'class';
    /* Group chars by the chosen attribute, find groups with 3+ members. */
    var groups = {};
    allChars.forEach(function(c){
      if(!c || !c.id) return;
      var key = (kind === 'race') ? c.race : c.character_class;
      if(!key) return;
      if(!groups[key]) groups[key] = [];
      groups[key].push(c);
    });
    var eligible = Object.keys(groups).filter(function(k){ return groups[k].length >= 3; });
    if(!eligible.length){
      /* Fall back to the other kind if this one has no group. */
      kind = (kind === 'race') ? 'class' : 'race';
      groups = {};
      allChars.forEach(function(c){
        if(!c || !c.id) return;
        var key = (kind === 'race') ? c.race : c.character_class;
        if(!key) return;
        if(!groups[key]) groups[key] = [];
        groups[key].push(c);
      });
      eligible = Object.keys(groups).filter(function(k){ return groups[k].length >= 3; });
      if(!eligible.length) return null;
    }
    var truth = eligible[Math.floor(Math.random() * eligible.length)];
    var picked = hfShuffle(groups[truth]).slice(0, 3);
    /* Build option list — 6 options total including the correct one. */
    var POOL = (kind === 'race')
      ? (typeof RACES !== 'undefined' ? RACES : Object.keys(groups))
      : (typeof CLASSES !== 'undefined' ? CLASSES : Object.keys(groups));
    var decoys = hfShuffle(POOL.filter(function(opt){ return opt !== truth; })).slice(0, 5);
    var options = hfShuffle(decoys.concat([truth]));
    return {
      type: 'commonThread',
      kind: kind,
      truth: truth,
      chars: picked,
      options: options,
      phase: 'idle', /* idle | resolved */
      guess: null,
      won: null,
      payout: COMMON_THREAD_PAYOUT,
      _eventKey: 'commonThread'
    };
  }
  function renderCommonThreadHtml(state){
    var phase = state.phase || 'idle';
    var cardsHtml = (state.chars || []).map(function(c){
      /* R140-F: Offset-aware helper. */
      var media = _eventCharImg(c, { eager: true, draggable: false });
      return '<div class="hoh-fresh-thread-card" data-id="' + esc(c.id) + '">' +
        '<div class="hoh-fresh-thread-media">' + media + '</div>' +
        '<div class="hoh-fresh-thread-name">' + esc(c.name || 'Unknown') + '</div>' +
      '</div>';
    }).join('');
    var optionsHtml;
    if(phase === 'idle'){
      optionsHtml = '<div class="hoh-fresh-quiz-options" role="radiogroup" aria-label="What do they share">' +
        (state.options || []).map(function(opt){
          return '<button type="button" class="hoh-fresh-quiz-option" onclick="hohFreshFacesThreadGuess(\'' + esc(opt) + '\')">' + esc(opt) + '</button>';
        }).join('') +
      '</div>';
    } else { /* resolved */
      var won = state.won;
      var headlineCls = won ? ' hoh-fresh-duel-headline-win' : ' hoh-fresh-duel-headline-lose';
      var headline = won ? 'A clean read.' : 'No thread there. -1 heart.';
      var sub = 'They share: ' + esc(state.truth) + ' (' + state.kind + ').';
      optionsHtml = '<div class="hoh-fresh-duel-result' + headlineCls + '">' +
        '<strong>' + headline + '</strong>' +
        '<span class="hoh-fresh-duel-result-sub">' + sub + '</span>' +
        '<button type="button" class="hoh-fresh-coin-toss hoh-fresh-dice-continue" onclick="hohFreshFacesThreadContinue()">' +
          '<span>Back to the Hall</span>' +
        '</button>' +
      '</div>';
    }
    return '<div class="hoh-fresh-special hoh-fresh-special-thread">' +
      eventHeaderHtml('A Common Thread', 'Three travelers cross paths. Look closely — something binds them together.', { centered: true }) +
      '<div class="hoh-fresh-thread-grid">' + cardsHtml + '</div>' +
      optionsHtml +
    '</div>';
  }
  window.hohFreshFacesThreadGuess = function(opt){
    if(!currentSpecial || currentSpecial.type !== 'commonThread') return;
    if(currentSpecial.phase !== 'idle') return;
    var won = (opt === currentSpecial.truth);
    currentSpecial.guess = opt;
    currentSpecial.won = won;
    currentSpecial.phase = 'resolved';
    var container = _ffContainer();
    if(container) container.innerHTML = renderCommonThreadHtml(currentSpecial);
    if(won){
      if(typeof window.hohAddHeartsToBank === 'function'){
        try{ window.hohHeartHint && window.hohHeartHint('earn_explore_special'); }catch(e){}
        try { window.hohAddHeartsToBank(currentSpecial.payout); } catch(_){}
      }
      try { window.hohShowHeartToast('A clean read', 'You spotted the thread.'); } catch(_){}
    } else {
      try { loseHeartFromGuess('Wrong thread.'); } catch(_){}
    }
    if(typeof startAutoTimer === 'function') setTimeout(startAutoTimer, 0);
  };
  window.hohFreshFacesThreadContinue = function(){
    if(!currentSpecial || currentSpecial.type !== 'commonThread') return;
    if(currentSpecial.phase !== 'resolved') return;
    currentSpecial = null;
    if(typeof window.hohFreshFacesRefresh === 'function'){
      try { window.hohFreshFacesRefresh({ fromInteractive: true }); } catch(_){}
    }
  };

  /* ================================================================ */
  /* R150: The Class War — Type 2 best-of-three guessing game         */
  /* ================================================================ */
  /* Three rounds. Each round shows two canonical D&D classes (or
     races) with their head counts HIDDEN behind a "?". The player
     guesses which side holds more of the Hall, then the real counts
     reveal with a count-up. +5 hearts per correct call, +10 bonus for
     a clean 3/3 sweep. Free to play, never a heart cost.
     Why a guess and not a side-pick: showing both numbers and asking
     the player to "pick a side" is a poll, not a game — the only move
     is to tap the bigger number. Hiding the counts turns it into a
     real guess with uncertainty, a reveal, and a reward, which is the
     fun. Counts are always the live Hall numbers, never faked.
     Canonical roster only: character_class / race are free-text
     fields full of one-off junk values, so matchups draw strictly
     from COMMON_CLASSES / COMMON_RACES. Registered as ROLL_TYPE_EVENT
     (Type 2). State machine: phase 'guess' -> 'reveal' -> (next round
     or) 'summary'. */
  var CLASS_WAR_MIN_POP    = 3;   /* a class/race needs this many heroes to enter the pool */
  /* R150-Z45: Single round. Three rounds dragged — one sharp guess
     with real stakes reads better. Right call +7 hearts, wrong call
     costs 1. The sweep bonus is retired (meaningless with one round). */
  var CLASS_WAR_ROUNDS     = 1;
  var CLASS_WAR_PER_CORRECT = 7;   /* hearts won on the right call */
  var CLASS_WAR_WRONG_PENALTY = 1; /* hearts lost on a wrong call */
  var CLASS_WAR_RATIO      = 2.6; /* a matchup is "close" if larger <= this * smaller */
  var CLASS_WAR_COLORS = {
    Paladin:'#e8c25a', Warlock:'#9d5cd0', Barbarian:'#cf5038',
    Wizard:'#5b8fd0', Rogue:'#9398a6', Cleric:'#ecdfae',
    Druid:'#5fa86b', Bard:'#e57ab0', Ranger:'#6f9a5f',
    Sorcerer:'#dd6740', Monk:'#bcae93', Fighter:'#9aa6b4',
    Artificer:'#d99a4e', 'Blood Hunter':'#b23a44',
    Tiefling:'#d4568f', Dragonborn:'#dd7a44', Elf:'#7fc99a',
    Human:'#cdb98a', Drow:'#9d7ad0', 'Half-Elf':'#86c0a0',
    Aasimar:'#ecdc9a', 'Half-Orc':'#8fae5a', Orc:'#7d9a4a',
    Tabaxi:'#d99f5e', Goliath:'#9fb0bf', Gnome:'#dd8fb0',
    Halfling:'#dab25e', Genasi:'#5fb6c8', Warforged:'#9aa8b2',
    Goblin:'#86a84a', Fairy:'#e89fcf', Changeling:'#a99fc8',
    Harengon:'#cdb07a'
  };
  function _classWarColor(label){
    return CLASS_WAR_COLORS[label] || '#c8a45e';
  }
  function _classWarCrestSvg(){
    return '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" ' +
      'stroke-width="1.3" stroke-linejoin="round" aria-hidden="true">' +
      '<path d="M12 2.5l8 3v6c0 5-3.4 8.8-8 11-4.6-2.2-8-6-8-11v-6z"/>' +
      '<path d="M12 7.5v8M8.4 11h7.2"/></svg>';
  }
  /* R150: a panel keeps EVERY member's face (capped at CLASS_WAR_FACE_CAP)
     so the reveal shows the whole crowd. A class with forty heroes
     becomes a wall of forty faces; a thin one is a lonely cluster, and
     that gap is the visual hook. FF portraits sort to the front so the
     best art leads the grid. */
  var CLASS_WAR_FACE_CAP = 60;
  function _classWarSide(label, members){
    var sorted = members.slice().sort(function(a, b){
      var af = (a && a.is_ff_portrait) ? 1 : 0;
      var bf = (b && b.is_ff_portrait) ? 1 : 0;
      if(af !== bf) return bf - af;
      return (b.hearts || 0) - (a.hearts || 0);
    });
    /* R265: the single champion who represents this side BEFORE the reveal —
       strictly the most-hearted member (not the FF-first order used for the
       crowd wall), with a valid portrait so the guess phase always has a face.
       A lone portrait never leaks the crowd size, so the guess stays fair. */
    var byHearts = members.slice()
      .filter(function(c){ return c && (c.image_thumb_url || c.image_url); })
      .sort(function(a, b){ return (b.hearts || 0) - (a.hearts || 0); });
    return {
      label: label,
      count: members.length,
      color: _classWarColor(label),
      faces: sorted.slice(0, CLASS_WAR_FACE_CAP),
      top: byHearts[0] || null
    };
  }
  /* Pick CLASS_WAR_ROUNDS matchups from a groups map keyed by the
     canonical roster. Each pair is "close" (larger <= CLASS_WAR_RATIO
     times the smaller) so the guess has real tension, is never an
     exact tie, and avoids reusing a side until the distinct pool is
     spent. Returns an array of rounds, or null if not even one round
     can be built. */
  function _classWarBuildRounds(groups, roster){
    var eligible = roster.filter(function(k){
      return groups[k] && groups[k].length >= CLASS_WAR_MIN_POP;
    });
    if(eligible.length < 2) return null;
    function shuffle(arr){
      for(var i = arr.length - 1; i > 0; i--){
        var j = Math.floor(Math.random() * (i + 1));
        var t = arr[i]; arr[i] = arr[j]; arr[j] = t;
      }
      return arr;
    }
    var used = {};
    /* Find a non-tie pair. Prefers a close ratio and unused sides,
       then relaxes step by step so a thin Hall still yields a round. */
    function findPair(){
      var fresh = eligible.filter(function(k){ return !used[k]; });
      var attempts = [
        { pool: fresh,    close: true  },
        { pool: eligible, close: true  },
        { pool: fresh,    close: false },
        { pool: eligible, close: false }
      ];
      for(var a = 0; a < attempts.length; a++){
        var pool = shuffle(attempts[a].pool.slice());
        if(pool.length < 2) continue;
        for(var x = 0; x < pool.length; x++){
          for(var y = x + 1; y < pool.length; y++){
            var cx = groups[pool[x]].length, cy = groups[pool[y]].length;
            if(cx === cy) continue;
            if(attempts[a].close){
              var hi = Math.max(cx, cy), lo = Math.min(cx, cy);
              if(hi / lo > CLASS_WAR_RATIO) continue;
            }
            return [pool[x], pool[y]];
          }
        }
      }
      return null;
    }
    var rounds = [];
    for(var r = 0; r < CLASS_WAR_ROUNDS; r++){
      var pair = findPair();
      if(!pair) break;
      used[pair[0]] = 1; used[pair[1]] = 1;
      var sideA = _classWarSide(pair[0], groups[pair[0]]);
      var sideB = _classWarSide(pair[1], groups[pair[1]]);
      /* Coin-flip the column order so the bigger side is not always
         on the same side. */
      if(Math.random() < 0.5){ var tmp = sideA; sideA = sideB; sideB = tmp; }
      rounds.push({ a: sideA, b: sideB, answer: (sideA.count > sideB.count) ? 'a' : 'b' });
    }
    return rounds.length ? rounds : null;
  }
  function pickClassWarEvent(){
    var allChars = (typeof window.hohGetAllCharacters === 'function') ? window.hohGetAllCharacters() : [];
    if(!allChars || allChars.length < 12) return null;
    function buildGroups(field){
      var g = {};
      allChars.forEach(function(c){
        if(!c || !c.id) return;
        var key = (field === 'race') ? c.race : c.character_class;
        if(!key) return;
        if(!g[key]) g[key] = [];
        g[key].push(c);
      });
      return g;
    }
    /* Canonical roster only. character_class / race are free-text, so
       the Hall is full of one-off junk values ("Fighter/Warlock",
       "Wechselkind"); drawing matchups strictly from COMMON_CLASSES /
       COMMON_RACES keeps every pairing between names a player knows. */
    function rosterFor(k){
      if(k === 'race') return (typeof COMMON_RACES !== 'undefined') ? COMMON_RACES : [];
      return (typeof COMMON_CLASSES !== 'undefined') ? COMMON_CLASSES : [];
    }
    /* Mostly a class war, sometimes a bloodline (race) war. Fall back
       to the other axis if the first cannot build a set of rounds. */
    var kind = (Math.random() < 0.65) ? 'class' : 'race';
    var rounds = _classWarBuildRounds(buildGroups(kind), rosterFor(kind));
    if(!rounds){
      kind = (kind === 'class') ? 'race' : 'class';
      rounds = _classWarBuildRounds(buildGroups(kind), rosterFor(kind));
      if(!rounds) return null;
    }
    return {
      type: 'classWar',
      kind: kind,
      rounds: rounds,
      roundIdx: 0,
      phase: 'guess',   /* guess -> reveal -> (next guess | summary) */
      guesses: [],
      correct: [],
      score: 0,
      paid: false,
      _eventKey: 'classWar'
    };
  }
  /* Round progress pips. hit = filled, miss = hollow, active = ringed. */
  function _classWarPipsHtml(state){
    var pips = '';
    for(var i = 0; i < state.rounds.length; i++){
      var c = 'hoh-cw-pip';
      if(i < state.guesses.length){
        c += state.correct[i] ? ' hoh-cw-pip-hit' : ' hoh-cw-pip-miss';
      } else if(i === state.roundIdx && state.phase !== 'summary'){
        c += ' hoh-cw-pip-active';
      }
      pips += '<span class="' + c + '"></span>';
    }
    return '<div class="hoh-cw-pips">' + pips + '</div>';
  }
  /* One combatant panel. In the guess phase it is a tappable button
     showing a "?"; once revealed it is a static div showing the real
     count, a portrait strip, and a laurel on the bigger side. */
  function _classWarPanelHtml(side, sideKey, round, state){
    var revealed = (state.phase === 'reveal' || state.phase === 'summary');
    var isTop = revealed && (round.answer === sideKey);
    var guessed = (state.phase !== 'guess') && (state.guesses[state.roundIdx] === sideKey);
    var cls = 'hoh-cw-side' +
      (isTop ? ' hoh-cw-side-top' : '') +
      (revealed && !isTop ? ' hoh-cw-side-dim' : '') +
      (guessed ? ' hoh-cw-side-picked' : '');
    var cnt = side.count | 0;
    /* R267: no "?" placeholder in the guess phase — the champion portrait now
       represents each side, so the count area stays empty until the reveal. */
    var numHtml = revealed
      ? '<span class="hoh-cw-count" data-target="' + cnt + '">' + cnt + '</span>'
      : '';
    /* Reveal packs the whole crowd into a tight face grid — the wall
       of faces IS the payoff. Guess phase stays clean: name + ? only,
       so the crowd size never leaks before the guess. */
    var faces = '';
    if(revealed){
      faces = '<span class="hoh-cw-faces">' + (side.faces || []).map(function(c){
        return '<span class="hoh-cw-face">' + _eventCharImg(c, { draggable: false, alt: c.name || '' }) + '</span>';
      }).join('') + '</span>';
    }
    /* R265: before the reveal, show the side's champion (top-hearted portrait)
       so each bloodline/class has a face representing it. Hidden once revealed,
       where the full crowd wall takes over. */
    var rep = '';
    if(!revealed && side.top){
      rep = '<span class="hoh-cw-rep">' +
              _eventCharImg(side.top, { draggable: false, alt: side.top.name || '' }) +
              (side.top.name ? '<span class="hoh-cw-rep-name">' + esc(side.top.name) + '</span>' : '') +
            '</span>';
    }
    var inner =
      '<span class="hoh-cw-side-head">' +
        '<span class="hoh-cw-side-name">' + esc(side.label) + '</span>' +
        numHtml +
      '</span>' +
      rep +
      faces;
    if(state.phase === 'guess'){
      return '<button type="button" class="' + cls + '" style="--cw-color:' + side.color + ';" ' +
        'onclick="hohFreshFacesClassWarGuess(\'' + sideKey + '\')">' + inner + '</button>';
    }
    return '<div class="' + cls + '" style="--cw-color:' + side.color + ';">' + inner + '</div>';
  }
  function _classWarSummaryHtml(state, eyebrow){
    var rows = state.rounds.map(function(rnd, i){
      var win  = (rnd.answer === 'a') ? rnd.a : rnd.b;
      var lose = (rnd.answer === 'a') ? rnd.b : rnd.a;
      var hit = !!state.correct[i];
      return '<div class="hoh-cw-recap-row">' +
        '<span class="hoh-cw-recap-mark ' + (hit ? 'hoh-cw-recap-hit' : 'hoh-cw-recap-miss') + '">' +
          (hit ? '&#10003;' : '&#10005;') + '</span>' +
        '<span class="hoh-cw-recap-text">' +
          '<strong style="color:' + win.color + ';">' + esc(win.label) + '</strong> ' + (win.count | 0) +
          '<span class="hoh-cw-recap-over">over</span>' +
          '<span style="color:' + lose.color + ';">' + esc(lose.label) + '</span> ' + (lose.count | 0) +
        '</span>' +
      '</div>';
    }).join('');
    var n = state.rounds.length;
    /* R150-Z45: Single-round outcome — won or lost. */
    var won = state.score >= 1;
    var headline = won
      ? 'A clean read of the Hall.'
      : 'The Hall outfoxed you this time.';
    var payout = won ? CLASS_WAR_PER_CORRECT : -CLASS_WAR_WRONG_PENALTY;
    var payHtml = won
      ? '<div class="hoh-cw-payout">Well read</div>'
      : '<div class="hoh-cw-payout hoh-cw-payout-zero">' + payout + ' heart</div>';
    return '<div class="hoh-fresh-special hoh-fresh-special-classwar hoh-cw hoh-cw-summary">' +
      eventHeaderHtml(eyebrow, 'The tally is in.', { centered: true }) +
      '<div class="hoh-cw-score">' + state.score + '<span class="hoh-cw-score-of">/ ' + n + '</span></div>' +
      '<p class="hoh-cw-headline">' + esc(headline) + '</p>' +
      '<div class="hoh-cw-recap">' + rows + '</div>' +
      payHtml +
      '<button type="button" class="hoh-cw-continue" onclick="hohFreshFacesClassWarContinue()">Keep exploring</button>' +
    '</div>';
  }
  function renderClassWarHtml(state){
    var eyebrow = (state.kind === 'race') ? 'THE BLOODLINE WAR' : 'THE CLASS WAR';
    if(state.phase === 'summary'){
      return _classWarSummaryHtml(state, eyebrow);
    }
    var kindWord = (state.kind === 'race') ? 'bloodline' : 'class';
    var round = state.rounds[state.roundIdx];
    var revealed = (state.phase === 'reveal');
    var hit = revealed && !!state.correct[state.roundIdx];
    var prompt = revealed
      ? (hit ? 'You called it.' : 'The Hall says otherwise.')
      : 'Which ' + kindWord + ' holds more of the Hall?';
    var promptCls = 'hoh-cw-prompt' + (revealed ? (hit ? ' hoh-cw-prompt-hit' : ' hoh-cw-prompt-miss') : '');
    var stage = '<div class="hoh-cw-stage">' +
      _classWarPanelHtml(round.a, 'a', round, state) +
      '<div class="hoh-cw-vs" aria-hidden="true"><span>VS</span></div>' +
      _classWarPanelHtml(round.b, 'b', round, state) +
    '</div>';
    var bar = '';
    var foot;
    if(revealed){
      var ra = round.a.count | 0, rb = round.b.count | 0, tot = ra + rb;
      var aPct = tot ? Math.round(ra / tot * 100) : 50;
      aPct = Math.max(8, Math.min(92, aPct));
      bar = '<div class="hoh-cw-bar" aria-hidden="true">' +
        '<span class="hoh-cw-bar-seg" style="width:' + aPct + '%;background:' + round.a.color + ';"></span>' +
        '<span class="hoh-cw-bar-seg" style="width:' + (100 - aPct) + '%;background:' + round.b.color + ';"></span>' +
      '</div>';
      var last = (state.roundIdx >= state.rounds.length - 1);
      /* R165: On the last round the reveal already shows the result
         (bar + counts + hit/miss prompt). Skip the dedicated summary
         screen and let the button drop straight back into the rail. */
      foot = '<div class="hoh-cw-foot">' +
        '<button type="button" class="hoh-cw-next" onclick="hohFreshFacesClassWarNext()">' +
          (last ? 'Keep exploring' : 'Next round') +
        '</button>' +
      '</div>';
    } else {
      foot = '<div class="hoh-cw-foot">' +
        '<p class="hoh-cw-hint">Tap the side you think is bigger. Live Hall counts.</p>' +
      '</div>';
    }
    /* R150-Z45: Pips only make sense with multiple rounds. With the
       single-round format they're a lone dot — skip them. */
    var pipsHtml = (state.rounds.length > 1) ? _classWarPipsHtml(state) : '';
    return '<div class="hoh-fresh-special hoh-fresh-special-classwar hoh-cw hoh-cw-' + state.phase + '">' +
      eventHeaderHtml(eyebrow, 'Guess which side holds more of the Hall.', { centered: true }) +
      pipsHtml +
      '<p class="' + promptCls + '">' + esc(prompt) + '</p>' +
      stage + bar + foot +
    '</div>';
  }
  /* Count-up for the revealed numbers. requestAnimationFrame, eases
     out, self-terminating — no interval to leak. The span already
     renders the real value as text, so if this never runs (e.g. a
     full re-render) the number is still correct. */
  function _classWarAnimateCounts(){
    try {
      var container = _ffContainer();
      if(!container) return;
      var els = container.querySelectorAll('.hoh-cw-count[data-target]');
      Array.prototype.forEach.call(els, function(el){
        var target = parseInt(el.getAttribute('data-target'), 10) || 0;
        if(target <= 0){ el.textContent = '0'; return; }
        var dur = 620, start = 0;
        function step(ts){
          if(!start) start = ts;
          var p = Math.min(1, (ts - start) / dur);
          var eased = 1 - Math.pow(1 - p, 3);
          el.textContent = String(Math.round(target * eased));
          if(p < 1) requestAnimationFrame(step);
          else el.textContent = String(target);
        }
        el.textContent = '0';
        requestAnimationFrame(step);
      });
    } catch(_){}
  }
  window.hohFreshFacesClassWarGuess = function(sideKey){
    if(!currentSpecial || currentSpecial.type !== 'classWar') return;
    if(currentSpecial.phase !== 'guess') return;
    if(sideKey !== 'a' && sideKey !== 'b') return;
    var round = currentSpecial.rounds[currentSpecial.roundIdx];
    if(!round) return;
    var hit = (sideKey === round.answer);
    currentSpecial.guesses[currentSpecial.roundIdx] = sideKey;
    currentSpecial.correct[currentSpecial.roundIdx] = hit;
    if(hit) currentSpecial.score++;
    currentSpecial.phase = 'reveal';
    var container = _ffContainer();
    if(container) container.innerHTML = renderClassWarHtml(currentSpecial);
    _classWarAnimateCounts();
    if(typeof startAutoTimer === 'function') setTimeout(startAutoTimer, 0);
  };
  window.hohFreshFacesClassWarNext = function(){
    if(!currentSpecial || currentSpecial.type !== 'classWar') return;
    if(currentSpecial.phase !== 'reveal') return;
    if(currentSpecial.roundIdx >= currentSpecial.rounds.length - 1){
      /* R165: Last round — pay out and drop straight back into the
         rail. The reveal already showed the result (bar + counts +
         hit/miss prompt), so the dedicated summary screen was a
         redundant in-between step. */
      if(!currentSpecial.paid){
        currentSpecial.paid = true;
        var won = currentSpecial.score >= 1;
        if(won){
          if(typeof window.hohAddHeartsToBank === 'function'){
            try{ window.hohHeartHint && window.hohHeartHint('earn_explore_classwar'); }catch(e){}
            try { window.hohAddHeartsToBank(CLASS_WAR_PER_CORRECT); } catch(_){}
          }
          try {
            if(typeof window.hohShowHeartToast === 'function'){
              window.hohShowHeartToast('Well read', 'You read the Hall.');
            }
          } catch(_){}
        } else {
          if(typeof window.hohRemoveHeartsFromBank === 'function'){
            try { window.hohRemoveHeartsFromBank(CLASS_WAR_WRONG_PENALTY, 'spend_penalty'); } catch(_){}
          }
          try {
            if(typeof window.hohShowHeartToast === 'function'){
              window.hohShowHeartToast('-' + CLASS_WAR_WRONG_PENALTY + ' heart', 'The Hall outfoxed you.');
            }
          } catch(_){}
        }
      }
      currentSpecial = null;
      if(typeof window.hohFreshFacesRefresh === 'function'){
        try { window.hohFreshFacesRefresh({ fromInteractive: true }); } catch(_){}
      }
      return;
    }
    currentSpecial.roundIdx++;
    currentSpecial.phase = 'guess';
    var container = _ffContainer();
    if(container) container.innerHTML = renderClassWarHtml(currentSpecial);
    if(currentSpecial.phase === 'reveal') _classWarAnimateCounts();
    if(typeof startAutoTimer === 'function') setTimeout(startAutoTimer, 0);
  };
  window.hohFreshFacesClassWarContinue = function(){
    if(!currentSpecial || currentSpecial.type !== 'classWar') return;
    currentSpecial = null;
    if(typeof window.hohFreshFacesRefresh === 'function'){
      try { window.hohFreshFacesRefresh({ fromInteractive: true }); } catch(_){}
    }
  };

  /* ================================================================ */
  /* R97: Traveling Healer + Traveling Bard — free pick-a-card events  */
  /* ================================================================ */
  /* Both events share the same all-good-outcomes mechanic. Three
     vague-named cards, all positive, each pre-mapped to 5/10/30
     hearts. User picks one without knowing the values. Free to play.
     Distinguished only by host class and card label flavor. The
     shared _buildHealerOrBardEvent factory keeps them DRY. */
  /* R150-Z45: Riddles rewritten so NONE telegraphs its payout. The
     old R165 set bucketed an obviously-warmer hint onto the 20-heart
     card ("the fullest gift on the road", "full of light") — the
     "right" pick was a giveaway. Now every card carries an evenly
     evocative, deliberately inscrutable line: the host openly says
     one weighs more, but the cards themselves keep their counsel.
     It's a true guess. The low/mid/high keys still drive the 5/10/20
     payout mapping; they just no longer leak through the copy. */
  var TRAVEL_RIDDLES_HEALER = {
    low: [
      { name:'Quiet Mercy',   riddle:'The cleric folds it into your palm and says nothing.' },
      { name:'Even Breath',   riddle:'A blessing handed out the way the road hands out weather.' },
      { name:'Soft Hand',     riddle:'Offered quietly, the way clerics offer most things.' },
      { name:'Calm Tide',     riddle:'It arrives on its own clock, like the tide.' }
    ],
    mid: [
      { name:'Steady Pulse',  riddle:'The cleric has carried this one a long way.' },
      { name:'Soothing Touch',riddle:'Wrapped in cloth. The weight is hard to judge.' },
      { name:'Slow Dawn',     riddle:'It comes in its own time, and the cleric will not rush it.' },
      { name:'Warm Word',     riddle:'Spoken low, the way the old prayers are spoken.' }
    ],
    high: [
      { name:'Mending Light', riddle:'The cleric will not say what waits inside it.' },
      { name:'Bright Hour',   riddle:'Sealed shut, the way every blessing on the road is sealed.' },
      { name:'Heart\'s Ease', riddle:'Even the cleric seems unsure what the road will make of it.' }
    ]
  };
  var TRAVEL_RIDDLES_BARD = {
    low: [
      { name:'Last Verse',     riddle:'A song the bard half-hums, then leaves for you to finish.' },
      { name:'A Distant Drum', riddle:'It carries from somewhere you cannot quite see.' },
      { name:'Half a Memory',  riddle:'The bard recalls the tune but not its ending.' },
      { name:'Quiet Chorus',   riddle:'Sung under the breath, meant only for the road.' }
    ],
    mid: [
      { name:'The Old Road',   riddle:'A walking song the bard has sung a thousand miles.' },
      { name:'Three Coin Reel',riddle:'A reel the bard plays the same every night.' },
      { name:'The Long Wake',  riddle:'It runs the length of the night, steady as a lantern.' },
      { name:'First Refrain',  riddle:'The opening line, before the song decides where it goes.' }
    ],
    high: [
      { name:'Ember Song',     riddle:'The bard keeps this one close and will not say why.' },
      { name:'Lantern\'s Mark',riddle:'A song the bard saves, though they will not say for whom.' },
      { name:'The True Verse', riddle:'The bard came a long way to sing it. That is all they will say.' }
    ]
  };
  /* Host dialogue lines — one is picked per event so the same host on
     two refreshes still feels different. Kept short; the riddles do the
     heavy lifting. */
  var TRAVEL_DIALOGUE_BARD = [
    'Three songs in my pocket. One was meant for you. Listen close.',
    'Sit a moment. I\'ll play three. Take whichever one finds you.',
    'I owe the road a song. Pick which I should sing. They aren\'t all the same.',
    'Three verses, all gifts. Read them slow before you choose.'
  ];
  var TRAVEL_DIALOGUE_HEALER = [
    'Three blessings to give. One is fuller than the others. Listen.',
    'Hold out your hand. I\'ll name three, you choose which to take.',
    'I\'ve carried these for the next traveller. Read them careful. Not all weigh the same.',
    'Three mercies in the satchel. Pick one. The road will tell which was right.'
  ];
  /* R140-D: Best-card payout dropped 30 → 20 per balance pass. Three-card
     pick remains all-positive [5, 10, 20] so every pick is still a gift,
     but the headline doesn't outpace the spin-wheel ceiling. */
  var TRAVEL_PAYOUTS = [5, 10, 20];
  function _shuffleArr(a){ return hfShuffle(a); }
  function _pickOne(arr){ return arr[Math.floor(Math.random() * arr.length)]; }
  function _buildHealerOrBardEvent(opts){
    var allChars = (typeof window.hohGetAllCharacters === 'function') ? window.hohGetAllCharacters() : [];
    if(!allChars || !allChars.length) return null;
    var pool = allChars.filter(function(c){
      return c && c.id && c.character_class && opts.hostClasses.indexOf(c.character_class) !== -1;
    });
    if(!pool.length){
      /* Fallback: any char with an id, so a thin pool doesn't kill
         the event entirely. */
      pool = allChars.filter(function(c){ return c && c.id; });
    }
    if(!pool.length) return null;
    var host = pool[Math.floor(Math.random() * pool.length)];
    /* R65: mythic host roll. If a mythic is available, slot in. */
    var mythicHost = (typeof window.hohRollMythicHost === 'function') ? window.hohRollMythicHost() : null;
    if(mythicHost) host = mythicHost;
    /* R165: Pull one riddle from each tier so the 20-payout card always
       carries the warmest hint. Then shuffle [low,mid,high] so the
       best card isn't always in position 2 — the riddle is the tell,
       not the slot. */
    var riddles = opts.riddles;
    var low  = _pickOne(riddles.low);
    var mid  = _pickOne(riddles.mid);
    var high = _pickOne(riddles.high);
    var tiered = [
      { name: low.name,  riddle: low.riddle,  payout: 5 },
      { name: mid.name,  riddle: mid.riddle,  payout: 10 },
      { name: high.name, riddle: high.riddle, payout: 20 }
    ];
    var cards = _shuffleArr(tiered);
    var dialogue = _pickOne(opts.dialogue);
    return {
      type: opts.type,
      host: host,
      mythicHost: !!mythicHost,
      hostKind: opts.kind, /* 'healer' | 'bard' */
      dialogue: dialogue,
      cards: cards,
      phase: 'idle', /* idle | resolved */
      pickedIndex: null,
      _eventKey: opts.type
    };
  }
  /* R140-D: Healer + Bard rolls felt too frequent in-session. The two
     events sit in the gambling pool's `travel` subtype alongside
     tollRoad / carnival / commonThread, but together with a +30 (now
     +20) headline payout they dominated rotation perception. We add
     a soft probabilistic skip — when the picker is consulted, return
     null some of the time so the next entry in the two-pass scan
     gets the slot. The two-pass picker handles falling through
     cleanly, and the gambleSeen tracker still gives every entry a
     fair turn across a session.
     R140-J: Skip rate dropped 0.45 → 0.25. The 'kindness' family rule
     added in R140-I already blocks back-to-back healer/bard for 6
     events, so the skip rate is now a softener on TOP of the family
     rule rather than the primary throttle. With both at 0.45 the
     picker was returning null too often, contributing to the
     "refreshes into another Fresh Faces" perception. */
  var TRAVEL_KINDNESS_SKIP_RATE = 0.25;
  function pickTravelingHealerEvent(){
    if(Math.random() < TRAVEL_KINDNESS_SKIP_RATE) return null;
    return _buildHealerOrBardEvent({
      type: 'travelHealer',
      kind: 'healer',
      hostClasses: ['Cleric'],
      riddles: TRAVEL_RIDDLES_HEALER,
      dialogue: TRAVEL_DIALOGUE_HEALER
    });
  }
  function pickTravelingBardEvent(){
    if(Math.random() < TRAVEL_KINDNESS_SKIP_RATE) return null;
    return _buildHealerOrBardEvent({
      type: 'travelBard',
      kind: 'bard',
      hostClasses: ['Bard'],
      riddles: TRAVEL_RIDDLES_BARD,
      dialogue: TRAVEL_DIALOGUE_BARD
    });
  }
  function renderTravelEventHtml(state){
    var phase = state.phase || 'idle';
    var isHealer = (state.hostKind === 'healer');
    var eyebrow = isHealer ? 'A Traveling Healer' : 'A Traveling Bard';
    /* R165: Subhead nudges the user to READ before picking — the
       riddles now carry real signal about which gift is biggest. */
    var sub = isHealer
      ? 'A cleric on the road offers three blessings. Read them — one weighs more than it looks.'
      : 'A bard offers three songs. Read them slow — one is fuller than the others.';
    var host = state.host || {};
    /* R140-F: Offset-aware host img — see _eventCharImg helper. */
    var hMedia = _eventCharImg(host, { eager: true, draggable: false });
    var hostName = esc(host.name || (isHealer ? 'A healer' : 'A bard'));
    var hostMeta = esc((host.race || '') + (host.character_class ? ' ' + host.character_class : ''));
    /* R165: Host card + dialogue speech sit side-by-side on desktop, stack
       on mobile. The dialogue is the host's voice — part of the riddle
       puzzle. Only shown on idle; resolved phase clears it for focus. */
    var dialogueHtml = '';
    if(phase === 'idle' && state.dialogue){
      dialogueHtml = '<div class="hoh-fresh-travel-dialogue">' +
        '<span class="hoh-fresh-travel-dialogue-quote" aria-hidden="true">&ldquo;</span>' +
        '<span class="hoh-fresh-travel-dialogue-text">' + esc(state.dialogue) + '</span>' +
      '</div>';
    }
    var hostHtml = '<div class="hoh-fresh-travel-host-row">' +
      '<div class="hoh-fresh-travel-host">' +
        '<div role="button" tabindex="0" class="hoh-fresh-card hoh-fresh-card-host" data-id="' + esc(host.id || '') + '"' +
          ' onclick="if(window.hohOpenDetail)hohOpenDetail(\'' + esc(host.id || '') + '\')"' +
          ' aria-label="Open ' + hostName + '">' +
          '<span class="hoh-fresh-media">' + hMedia + '</span>' +
          '<span class="hoh-fresh-body">' +
            '<span class="hoh-fresh-name">' + hostName + '</span>' +
            '<span class="hoh-fresh-meta">' + hostMeta + '</span>' +
          '</span>' +
        '</div>' +
      '</div>' +
      dialogueHtml +
    '</div>';
    /* Three riddle cards. On idle, each card shows NAME + a one-line
       riddle that hints at how fully the gift weighs. On resolved, the
       payout reveals beneath the name; the picked card glows. */
    var pickFn = isHealer ? 'hohFreshFacesHealerPick' : 'hohFreshFacesBardPick';
    var continueFn = isHealer ? 'hohFreshFacesHealerContinue' : 'hohFreshFacesBardContinue';
    var picked = state.pickedIndex;
    var cardsHtml = (state.cards || []).map(function(card, i){
      var revealed = (phase === 'resolved');
      var isPicked = (picked === i);
      var cls = 'hoh-fresh-travel-card';
      if(revealed) cls += ' hoh-fresh-travel-card-revealed';
      if(isPicked) cls += ' hoh-fresh-travel-card-picked';
      var nameHtml   = '<span class="hoh-fresh-travel-card-name">' + esc(card.name) + '</span>';
      var riddleHtml = card.riddle
        ? '<span class="hoh-fresh-travel-card-riddle">' + esc(card.riddle) + '</span>'
        : '';
      var payoutHtml = revealed
        ? '<span class="hoh-fresh-travel-card-payout">&#9829;&#xFE0E;</span>'
        : '';
      var inner = nameHtml + riddleHtml + payoutHtml;
      if(phase === 'idle'){
        var aria = ' aria-label="' + esc(card.name + '. ' + (card.riddle || '')) + '"';
        return '<button type="button" class="' + cls + '"' + aria + ' onclick="' + pickFn + '(' + i + ')">' + inner + '</button>';
      }
      return '<div class="' + cls + '">' + inner + '</div>';
    }).join('');
    var ctaHtml = '';
    if(phase === 'resolved'){
      var pickedCard = state.cards[picked] || state.cards[0];
      ctaHtml =
        '<div class="hoh-fresh-duel-result hoh-fresh-duel-headline-win">' +
          '<strong>' + (isHealer ? 'The healer\'s blessing lands.' : 'The bard\'s song lifts you.') + '</strong>' +
          '<span class="hoh-fresh-duel-result-sub">' + esc(pickedCard.name) + '.</span>' +
          '<button type="button" class="hoh-fresh-coin-toss hoh-fresh-dice-continue" onclick="' + continueFn + '()">' +
            '<span>Back to the Hall</span>' +
          '</button>' +
        '</div>';
    }
    return '<div class="hoh-fresh-special hoh-fresh-special-travel hoh-fresh-special-travel-' + state.hostKind + (state.mythicHost ? ' hoh-fresh-special-mythic-host' : '') + '">' +
      eventHeaderHtml(eyebrow, sub, { centered: true, hideRefresh: (phase === 'resolved') }) +
      hostHtml +
      '<div class="hoh-fresh-travel-cards">' + cardsHtml + '</div>' +
      ctaHtml +
    '</div>';
  }
  function _runTravelPick(typeName, idx){
    if(!currentSpecial || currentSpecial.type !== typeName) return;
    if(currentSpecial.phase !== 'idle') return;
    if(idx < 0 || idx > 2) return;
    currentSpecial.pickedIndex = idx;
    currentSpecial.phase = 'resolved';
    var container = _ffContainer();
    if(container) container.innerHTML = renderTravelEventHtml(currentSpecial);
    var card = currentSpecial.cards[idx];
    /* R124-D: Travel blessings (Healer + Bard) ONLY add hearts to the
       wallet via hohAddHeartsToBank — they do NOT call bumpFfHeartsToday
       or hohHeart, so they do not advance the Explore the Hall progress
       bar. The bar tracks character hearts given (the "explore" loop),
       not blessings received. Don't add bar bumps here. */
    if(card && card.payout > 0){
      if(typeof window.hohAddHeartsToBank === 'function'){
        try{ window.hohHeartHint && window.hohHeartHint('earn_explore_cards'); }catch(e){}
        try { window.hohAddHeartsToBank(card.payout); } catch(_){}
      }
      if(typeof window.hohShowHeartToast === 'function'){
        try { window.hohShowHeartToast('A gift from the road', card.name); } catch(_){}
      }
      try {
        var anchor = container && container.querySelector('.hoh-fresh-travel-card-picked');
        if(anchor) flyDiceTraderHeartsToPill(anchor, Math.min(card.payout, 12));
      } catch(_){}
    }
    /* Mythic host bonus on any winning pick (all picks here are
       wins, so mythic always fires when host is mythic). */
    if(currentSpecial.mythicHost){
      setTimeout(function(){
        try { applyMythicHostBonus('#hoh-fresh-faces .hoh-fresh-travel-host .hoh-fresh-card-host'); } catch(_){}
      }, 380);
    }
    if(typeof startAutoTimer === 'function') setTimeout(startAutoTimer, 0);
  }
  function _runTravelContinue(typeName){
    if(!currentSpecial || currentSpecial.type !== typeName) return;
    if(currentSpecial.phase !== 'resolved') return;
    currentSpecial = null;
    if(typeof window.hohFreshFacesRefresh === 'function'){
      try { window.hohFreshFacesRefresh({ fromInteractive: true }); } catch(_){}
    }
  }
  window.hohFreshFacesHealerPick     = function(idx){ _runTravelPick('travelHealer', idx); };
  window.hohFreshFacesHealerContinue = function(){ _runTravelContinue('travelHealer'); };
  window.hohFreshFacesBardPick       = function(idx){ _runTravelPick('travelBard', idx); };
  window.hohFreshFacesBardContinue   = function(){ _runTravelContinue('travelBard'); };

  /* ================================================================ */
  /* R93: Slot-reel cycle helper used by both wheel events.            */
  /* ================================================================ */
  /* Highlights tiles in sequence, decelerating, landing on the
     winning index. Pure setTimeout + classList — works on every
     browser, no transform/transition fragility.
     opts: {
       reelSelector: CSS selector for the reel container (parent of
                     the .hoh-fresh-reel-tile children),
       tileCount:    number of tiles in the reel,
       winningIndex: which index the highlight should land on,
       totalDurationMs: ~target total animation length (1500-2500),
       onDone:       called when the highlight lands (synchronously
                     paints the resolved-phase result).
     } */
  function _runReelCycle(opts){
    var reel = document.querySelector(opts.reelSelector);
    if(!reel || !opts.tileCount){
      /* No DOM available — just call onDone immediately so the event
         still resolves. The user won't see the spin animation but
         the event won't deadlock. */
      try { if(typeof opts.onDone === 'function') opts.onDone(); } catch(_){}
      return;
    }
    var tiles = reel.querySelectorAll('.hoh-fresh-reel-tile');
    var n = tiles.length;
    if(!n){
      try { if(typeof opts.onDone === 'function') opts.onDone(); } catch(_){}
      return;
    }
    /* Build the sequence of indices the highlight will visit.
       Cycle 2-3 full rotations + the winning index, with each step
       getting slightly slower so the eye reads "deceleration". */
    var totalSteps = (n * 3) + (((opts.winningIndex % n) + n) % n);
    var sequence = [];
    for(var s = 0; s < totalSteps; s++){
      sequence.push(s % n);
    }
    /* Compute per-step delays. Start fast, end slow. Linear ramp
       across the totalDurationMs target. */
    var totalMs = Math.max(800, opts.totalDurationMs || 1800);
    var minStep = 50;   /* fastest step at the start */
    var maxStep = 220;  /* slowest step at the very end */
    var stepDelays = [];
    var sumWeights = 0;
    for(var i = 0; i < sequence.length; i++){
      var t = i / Math.max(1, sequence.length - 1);
      var d = minStep + (maxStep - minStep) * t * t; /* eased */
      stepDelays.push(d);
      sumWeights += d;
    }
    /* Normalize so sum matches totalMs. */
    var scale = totalMs / sumWeights;
    var elapsed = 0;
    var clearActive = function(){
      for(var k = 0; k < tiles.length; k++){
        tiles[k].classList.remove('hoh-fresh-reel-tile-active');
      }
    };
    sequence.forEach(function(idx, i){
      elapsed += stepDelays[i] * scale;
      setTimeout(function(){
        clearActive();
        if(tiles[idx]) tiles[idx].classList.add('hoh-fresh-reel-tile-active');
      }, elapsed);
    });
    setTimeout(function(){
      try { if(typeof opts.onDone === 'function') opts.onDone(); } catch(_){}
    }, elapsed + 60);
  }

  /* ================================================================ */
  /* R65: Mini Spinner — unlimited 4-segment wheel                      */
  /* ================================================================ */
  /* Cost 5 hearts to spin. 4 segments: 0 / 3 / 7 / 10. EV = 5
     (break-even before mythic bonus). Mythic host can be rolled — if
     mythic AND landing segment is 7 or 10, +30 mythic bonus fires.
     Lives in the unlimited rotation alongside dice trader, brawl,
     coin bet, etc. */
  var MINI_SPINNER_COST = 5;
  /* R102: Wheel segments rebalanced — 0 / 5 / 10 / 30. Cost stays at
     5 hearts. Expected value moves up from ~5.0 to ~11.25 hearts per
     spin so the wheel actually feels worth the gamble. The 30-slot
     becomes the headliner outcome (was 10).
     R140-D: Top segment dropped 30 → 20. EV now ~8.75 (down from 11.25).
     Keeps the wheel positive but pulls the jackpot in line with the
     bard/healer + pick-a-card rebalance. */
  var MINI_SPINNER_SEGMENTS = [
    { value: 0,  label: '0',  flavor: 'A clean miss. The wheel keeps every coin.' },
    { value: 5,  label: '5',  flavor: 'Even trade. The wheel returned what you spent.' },
    { value: 10, label: '10', flavor: 'A bright spin. The wheel sang back.' },
    { value: 20, label: '20', flavor: 'A jackpot turn. The wheel rang gold.' }
  ];
  function pickMiniSpinnerEvent(){
    /* R140-Z3h: Partial-cost play. Fires for any bank ≥ 1. */
    var bank = (typeof window.heartsRemainingToday === 'function') ? window.heartsRemainingToday() : 0;
    if(bank < 1) return null;
    var actualCost = Math.min(MINI_SPINNER_COST, bank);
    /* R65: mythic host roll. The wheel itself is the "host" — the
       mythic stares at the user from beside the wheel and pays out
       the bonus on a winning landing. */
    var mythicHost = (typeof window.hohRollMythicHost === 'function') ? window.hohRollMythicHost() : null;
    var host = mythicHost;
    if(!host){
      var allChars = (typeof window.hohGetAllCharacters === 'function') ? window.hohGetAllCharacters() : [];
      var preferredClasses = {Bard:1,Charlatan:1,Sorcerer:1,Rogue:1};
      var preferred = allChars.filter(function(c){
        return c && c.character_class && preferredClasses[c.character_class];
      });
      var pool = preferred.length >= 1 ? preferred : allChars.filter(function(c){ return c && c.id; });
      if(pool.length){
        host = pool[Math.floor(Math.random() * pool.length)];
      }
    }
    return {
      type: 'miniSpinner',
      cost: actualCost,
      segments: MINI_SPINNER_SEGMENTS,
      host: host,
      mythicHost: !!mythicHost,
      phase: 'idle', /* idle | spinning | resolved */
      winningIndex: null,
      _eventKey: 'miniSpinner'
    };
  }
  /* R93: Wheel redesign — slot-reel pattern. The conic-gradient + CSS-
     transform wheel was unreliable across browsers (multiple fix
     attempts in R75/R86/R87 still left users reporting "doesn't spin").
     The new design lays the outcomes out as TILES in a row; a glowing
     "highlight cursor" cycles through them on tap, decelerates, and
     stops on the winning tile. No transforms on the wheel itself, no
     conic-gradient, no animation timing fragility — the highlight is
     a single class toggle driven by setTimeout. Reliable on every
     browser. */
  function renderMiniSpinnerHtml(state){
    var phase = state.phase || 'idle';
    var hostHtml = '';
    if(state.host){
      hostHtml = '<div class="hoh-fresh-mini-spinner-host">' + _miniHeroCard(state.host) + '</div>';
    }
    /* Render each segment as a tile in a row. The tile's heart value
       is shown front-and-center; a class on the tile marks the
       currently-highlighted segment during the spin animation. The
       same DOM is used in idle/spinning/resolved — only the active-
       index marker changes. */
    var activeIdx = (phase === 'resolved' && typeof state.winningIndex === 'number') ? state.winningIndex : -1;
    var tilesHtml = state.segments.map(function(seg, i){
      var isActive = (i === activeIdx);
      var cls = 'hoh-fresh-reel-tile hoh-fresh-reel-tile-v' + seg.value + (isActive ? ' hoh-fresh-reel-tile-active' : '');
      return '<div class="' + cls + '" data-reel-idx="' + i + '">' +
        '<span class="hoh-fresh-reel-tile-heart" aria-hidden="true">&#9829;&#xFE0E;</span>' +
        '<span class="hoh-fresh-reel-tile-value">' + seg.value + '</span>' +
      '</div>';
    }).join('');
    var reelHtml = '<div class="hoh-fresh-reel hoh-fresh-reel-mini" data-reel-count="' + state.segments.length + '">' +
      tilesHtml +
    '</div>';
    var ctaHtml = '';
    if(phase === 'idle'){
      ctaHtml =
        '<button type="button" class="hoh-fresh-coin-toss" onclick="hohFreshFacesMiniSpinnerSpin()">' +
          '<span>Spin the Wheel</span>' +
          '<span class="hoh-fresh-dice-cost">&#9829;&#xFE0E; &minus;' + state.cost + '</span>' +
        '</button>';
    } else if(phase === 'spinning'){
      ctaHtml = '<div class="hoh-fresh-reel-status">Spinning&hellip;</div>';
    } else if(phase === 'resolved'){
      var seg = state.segments[state.winningIndex] || state.segments[0];
      var net = seg.value - state.cost;
      var headlineCls = (net >= 0) ? ' hoh-fresh-duel-headline-win' : ' hoh-fresh-duel-headline-lose';
      var headline = (seg.value === 0)
        ? 'Nothing back. -' + state.cost + ' hearts.'
        : ((net > 0) ? '+' + seg.value + ' hearts. Net +' + net + '.' : '+' + seg.value + ' hearts. Net ' + net + '.');
      ctaHtml =
        '<div class="hoh-fresh-duel-result' + headlineCls + '">' +
          '<strong>' + headline + '</strong>' +
          '<span class="hoh-fresh-duel-result-sub">' + esc(seg.flavor) + '</span>' +
          '<button type="button" class="hoh-fresh-coin-toss hoh-fresh-dice-continue" onclick="hohFreshFacesMiniSpinnerContinue()">' +
            '<span>Back to the Hall</span>' +
          '</button>' +
        '</div>';
    }
    var hideRefresh = (phase === 'resolved');
    return '<div class="hoh-fresh-special hoh-fresh-special-mini-spinner' + (state.mythicHost ? ' hoh-fresh-special-mythic-host' : '') + '">' +
      eventHeaderHtml('Spin the Wheel', 'Four outcomes. One spin. Five hearts.', { centered: true, hideRefresh: hideRefresh }) +
      hostHtml +
      '<div class="hoh-fresh-reel-stage">' + reelHtml + '</div>' +
      ctaHtml +
    '</div>';
  }
  window.hohFreshFacesMiniSpinnerSpin = function(){
    if(!currentSpecial || currentSpecial.type !== 'miniSpinner') return;
    if(currentSpecial.phase !== 'idle') return;
    var cost = currentSpecial.cost || MINI_SPINNER_COST;
    var bank = (typeof window.heartsRemainingToday === 'function') ? window.heartsRemainingToday() : 0;
    if(bank < cost){
      if(typeof window.hohShowHeartToast === 'function'){
        try { window.hohShowHeartToast('Not enough hearts', 'You need ' + cost + ' to spin.'); } catch(_){}
      }
      return;
    }
    /* Spend the entry cost from the wallet directly. Mirrors the
       Brawl4/CoinBet pattern. */
    try {
      var raw = parseInt(localStorage.getItem('hoh-heart-bank-v1') || '0', 10) || 0;
      try{ window.hohHeartHint && window.hohHeartHint('spend_feature'); }catch(e){}
      localStorage.setItem('hoh-heart-bank-v1', String(Math.max(0, raw - cost)));
      try { document.dispatchEvent(new CustomEvent('hoh:hearts-bumped', { detail: { spent: cost } })); } catch(_){}
    } catch(_){}
    if(typeof window.hohRenderHeaderCounter === 'function'){
      try { window.hohRenderHeaderCounter(); } catch(_){}
    }
    /* Pre-roll the winning segment. */
    var winningIndex = Math.floor(Math.random() * currentSpecial.segments.length);
    currentSpecial.winningIndex = winningIndex;
    currentSpecial.phase = 'spinning';
    var container = _ffContainer();
    if(container) container.innerHTML = renderMiniSpinnerHtml(currentSpecial);
    /* R93: Slot-reel cycling animation. Highlight cursor moves through
       the tiles fast at first, decelerates, and lands on the winning
       index. Pure setTimeout-driven class toggles — no transforms, no
       CSS transitions, no WAAPI dependency. */
    _runReelCycle({
      reelSelector: '#hoh-fresh-faces .hoh-fresh-reel-mini',
      tileCount: currentSpecial.segments.length,
      winningIndex: winningIndex,
      totalDurationMs: 1900,
      onDone: function(){
        if(!currentSpecial || currentSpecial.type !== 'miniSpinner') return;
        currentSpecial.phase = 'resolved';
        if(container) container.innerHTML = renderMiniSpinnerHtml(currentSpecial);
        var seg = currentSpecial.segments[winningIndex];
        if(seg && seg.value > 0){
          if(typeof window.hohAddHeartsToBank === 'function'){
            try{ window.hohHeartHint && window.hohHeartHint('earn_explore_wheel'); }catch(e){}
            try { window.hohAddHeartsToBank(seg.value); } catch(_){}
          }
          if(typeof window.hohShowHeartToast === 'function'){
            try { window.hohShowHeartToast('+' + seg.value + ' hearts', seg.flavor); } catch(_){}
          }
          try {
            var anchor = container && container.querySelector('.hoh-fresh-reel-mini .hoh-fresh-reel-tile-active');
            if(anchor) flyDiceTraderHeartsToPill(anchor, Math.min(seg.value, 10));
          } catch(_){}
        }
        /* R65: mythic host bonus on a winning landing (7 or 10). */
        if(currentSpecial.mythicHost && seg && seg.value >= 7){
          setTimeout(function(){
            applyMythicHostBonus('#hoh-fresh-faces .hoh-fresh-mini-spinner-host .hoh-fresh-card-host');
          }, 380);
        }
        if(typeof startAutoTimer === 'function') setTimeout(startAutoTimer, 0);
      }
    });
  };
  window.hohFreshFacesMiniSpinnerContinue = function(){
    if(!currentSpecial || currentSpecial.type !== 'miniSpinner') return;
    if(currentSpecial.phase !== 'resolved') return;
    currentSpecial = null;
    if(typeof window.hohFreshFacesRefresh === 'function'){
      try { window.hohFreshFacesRefresh({ fromInteractive: true }); } catch(_){}
    }
  };

  /* ================================================================ */
  /* Pick a Card — three-card RNG gambling event                       */
  /* ================================================================ */
  /* Three face-down cards. One hides +30 hearts, two hide MIMICS
     (D&D classic — chests with teeth, themed in R52 to replace the
     less-thematic bomb icon). The user pays 5 hearts, picks one,
     and reveals it. Win = +30 hearts + confetti + heart-fly. Loss
     = nothing back, the mimics reveal so the user sees what they
     dodged. EV = (1/3 × 30) + (2/3 × 0) - 5 = +5 hearts. Once per
     day. Hosted by a charlatan/rogue character. The winning index
     is pre-picked at event-creation time so the reveal is
     deterministic regardless of which card the user clicks. */
  function pickACardTodayKey(){
    var d = new Date();
    var ymd = d.getFullYear() + '-' + String(d.getMonth()+1).padStart(2,'0') + '-' + String(d.getDate()).padStart(2,'0');
    return 'hoh-pick-card-done-' + ymd;
  }
  function pickACardUsedToday(){
    try { return localStorage.getItem(pickACardTodayKey()) === '1'; } catch(_){ return false; }
  }
  function markPickACardUsedToday(){
    try { localStorage.setItem(pickACardTodayKey(), '1'); } catch(_){}
  }
  function pickPickACardEvent(){
    var COST = 5;
    /* R119-I: Daily cap dropped. The event now lives behind the same
       low rare-special threshold + 4-event anti-repeat as every other
       gambling event.
       R140-Z3h: Partial-cost play. */
    var bank = (typeof window.heartsRemainingToday === 'function') ? window.heartsRemainingToday() : 0;
    if(bank < 1) return null;
    var actualCost = Math.min(COST, bank);
    /* Host: charlatan/rogue/bard flavor. Falls back to any if pool
       is thin. */
    var allChars = (typeof window.hohGetAllCharacters === 'function') ? window.hohGetAllCharacters() : [];
    if(!allChars || !allChars.length) return null;
    var hostClasses = {Charlatan:1,Rogue:1,Bard:1,Sorcerer:1,Warlock:1};
    var preferred = allChars.filter(function(c){ return c && c.character_class && hostClasses[c.character_class]; });
    var pool = preferred.length >= 2 ? preferred : allChars.filter(function(c){ return c && c.id; });
    if(!pool.length) return null;
    var host = pool[Math.floor(Math.random() * pool.length)];
    return {
      type: 'pickACard',
      cost: actualCost,
      /* R140-D: Win payout dropped 30 → 25. EV adjusts from +5 to +3.33
         (1/3 × 25 - 5), keeping the event a small net positive while
         pulling the headline reward in line with the new spin-wheel
         ceiling and the bard/healer rebalance. */
      payout: 25,
      host: host,
      winningIndex: Math.floor(Math.random() * 3),
      pickedIndex: null,
      phase: 'idle', /* 'idle' | 'resolved' */
      won: null,
      _eventKey: 'pickACard'
    };
  }

  /* ================================================================ */
  /* R55 unlimited free guess + bet events                             */
  /* ================================================================ */
  /* These events are no-cost (or low-cost) free-fire guess games that
     join the gambling pool to add variety. All gated by the 3-event
     anti-repeat buffer in pickGamblingEventInner so the same one
     can\'t fire within 3 consecutive events. Each lives behind its
     own picker function returning a state object with a unique
     _eventKey. The dispatcher routes the type to a renderer. */

  /* --- shared helpers --- */
  function _shuffle(arr){
    var a = arr.slice();
    for(var i=a.length-1;i>0;i--){
      var j = Math.floor(Math.random()*(i+1));
      var t = a[i]; a[i]=a[j]; a[j]=t;
    }
    return a;
  }
  function _allChars(){
    return (typeof window.hohGetAllCharacters === 'function') ? (window.hohGetAllCharacters() || []) : [];
  }
  function _isFresh(c){
    /* "New" definition matches the fresh window used in cardHtml. */
    if(!c || !c.created_at) return false;
    var t = new Date(c.created_at).getTime();
    return !isNaN(t) && (Date.now() - t) <= FRESH_MS;
  }

  /* --- Class Guess factory (Fighter|Barbarian, Wizard|Warlock, Ranger|Rogue) ---
     R93: Now also picks one SAMPLE host per option so the renderer
     can show "this is what a Fighter looks like" + "this is what a
     Barbarian looks like" above each option button. The hidden char
     in the middle is what the user is guessing. */
  function pickClassGuessEvent(eventKey, classes){
    var pool = _allChars().filter(function(c){
      return c && c.character_class && classes.indexOf(c.character_class) !== -1;
    });
    if(pool.length < 2) return null;
    var pick = pool[Math.floor(Math.random()*pool.length)];
    /* Pick one host per option class — must be DIFFERENT from `pick`
       so we don't spoil the answer. If a class has no other char,
       fall back to any char of that class. */
    var optionHosts = {};
    classes.forEach(function(cls){
      var inClass = _allChars().filter(function(c){
        return c && c.character_class === cls && c.id && c.id !== pick.id;
      });
      var host = inClass.length ? inClass[Math.floor(Math.random()*inClass.length)] : null;
      /* Last-resort fallback: same char (harmless — answer is still
         hidden from the central card). */
      if(!host){
        var anyInClass = _allChars().filter(function(c){ return c && c.character_class === cls; });
        if(anyInClass.length) host = anyInClass[0];
      }
      if(host) optionHosts[cls] = host;
    });
    return {
      type: eventKey,
      char: pick,
      classes: classes,
      optionHosts: optionHosts,
      phase: 'idle',
      pickedClass: null,
      won: null,
      _eventKey: eventKey
    };
  }
  function pickGuessFighterBarbarianEvent(){ return pickClassGuessEvent('guessFB', ['Fighter','Barbarian']); }
  function pickGuessWizardWarlockEvent(){    return pickClassGuessEvent('guessWW', ['Wizard','Warlock']); }
  function pickGuessRangerRogueEvent(){      return pickClassGuessEvent('guessRR', ['Ranger','Rogue']); }

  /* R87/R93: Race-binary guess factory. Same dual-host pattern as the
     class factory — picks a sample char per race so the renderer can
     anchor each option button with a face. */
  function pickRaceGuessEvent(eventKey, races){
    var pool = _allChars().filter(function(c){
      return c && c.race && races.indexOf(c.race) !== -1;
    });
    if(pool.length < 2) return null;
    var pick = pool[Math.floor(Math.random()*pool.length)];
    var optionHosts = {};
    races.forEach(function(r){
      var inRace = _allChars().filter(function(c){
        return c && c.race === r && c.id && c.id !== pick.id;
      });
      var host = inRace.length ? inRace[Math.floor(Math.random()*inRace.length)] : null;
      if(!host){
        var anyInRace = _allChars().filter(function(c){ return c && c.race === r; });
        if(anyInRace.length) host = anyInRace[0];
      }
      if(host) optionHosts[r] = host;
    });
    return {
      type: eventKey,
      char: pick,
      races: races,
      optionHosts: optionHosts,
      phase: 'idle',
      pickedRace: null,
      won: null,
      _eventKey: eventKey
    };
  }
  function pickGuessElfHalfElfEvent(){ return pickRaceGuessEvent('guessEHE', ['Elf','Half-Elf']); }

  /* R165: Guess the Rarity event removed — was redundant with the
     class-guess trivia variants and made the rarity tier overly
     prominent in the moment-to-moment loop. */

  /* --- Find the Culprit ---
     6 chars, 5 of one class, 1 of a different class. User picks the
     odd one out. */
  function pickFindCulpritEvent(){
    var all = _allChars().filter(function(c){ return c && c.id && c.character_class; });
    if(all.length < 6) return null;
    /* Group by class and find a class with >=5 chars to be the
       "majority"; pick a different class for the culprit. */
    var byClass = {};
    all.forEach(function(c){
      var k = c.character_class;
      if(!byClass[k]) byClass[k] = [];
      byClass[k].push(c);
    });
    var majorityClasses = Object.keys(byClass).filter(function(k){ return byClass[k].length >= 5; });
    if(!majorityClasses.length) return null;
    var majClass = majorityClasses[Math.floor(Math.random()*majorityClasses.length)];
    var others = all.filter(function(c){ return c.character_class !== majClass; });
    if(!others.length) return null;
    var five = _shuffle(byClass[majClass]).slice(0, 5);
    var culprit = others[Math.floor(Math.random()*others.length)];
    var lineup = _shuffle(five.concat([culprit]));
    return {
      type: 'findCulprit',
      dimension: 'class',
      chars: lineup,
      culpritId: culprit.id,
      majClass: majClass,
      culpritClass: culprit.character_class,
      phase: 'idle',
      pickedId: null,
      won: null,
      _eventKey: 'findCulprit'
    };
  }
  /* R57: Race-flavored Find the Culprit. Locked to Dwarf vs Halfling
     because they look similar enough that the spot is genuinely a
     game (Tiefling vs Elf would be too easy). Same shape as the
     class version: 5 of one race + 1 imposter. Returns null if the
     pool can't supply at least 5 of the majority race + 1 of the
     other so the event simply doesn't fire when the hall isn't ready
     for it. Different _eventKey from the class version so the anti-
     repeat buffer treats them as distinct. */
  function pickFindCulpritRaceEvent(){
    /* R267: pick the majority race DYNAMICALLY from the whole Hall instead of
       the old hard-locked Dwarf-vs-Halfling pair. That lock meant this event
       replayed the same tiny group of dwarves and halflings every single time.
       Now ANY race with 5+ heroes can be the crowd and ANY hero of a different
       race is the imposter, so the faces rotate across the Hall while it stays
       a real "spot the odd race" game. Mirrors how the class culprit already
       works. */
    var all = _allChars().filter(function(c){ return c && c.id && c.race; });
    if(all.length < 6) return null;
    var byRace = {};
    all.forEach(function(c){
      var k = c.race;
      if(!byRace[k]) byRace[k] = [];
      byRace[k].push(c);
    });
    var majorityRaces = Object.keys(byRace).filter(function(r){ return byRace[r].length >= 5; });
    if(!majorityRaces.length) return null;
    var majRace = majorityRaces[Math.floor(Math.random()*majorityRaces.length)];
    var otherPool = all.filter(function(c){ return c.race !== majRace; });
    if(!otherPool.length) return null;
    var five = _shuffle(byRace[majRace]).slice(0, 5);
    var culprit = otherPool[Math.floor(Math.random()*otherPool.length)];
    var lineup = _shuffle(five.concat([culprit]));
    return {
      type: 'findCulprit',
      dimension: 'race',
      chars: lineup,
      culpritId: culprit.id,
      majRace: majRace,
      culpritRace: culprit.race,
      /* Mirror class-mode field names so renderFindCulpritHtml can
         use either. We intentionally leave majClass/culpritClass
         undefined and the renderer picks based on dimension. */
      phase: 'idle',
      pickedId: null,
      won: null,
      _eventKey: 'findCulpritRace'
    };
  }

  /* R58: Hero Quiz wrapper — pickClassQuiz lives further down with the
     special-tier setup helpers, but the gambling pool needs a stable
     entry function. This wrapper just stamps the _eventKey so the
     anti-repeat buffer treats classQuiz as its own family. Returns
     null if the pool doesn\'t support a quiz (e.g. no chars with
     valid race/class), letting the picker fall through to the next
     gamble candidate. */
  function pickClassQuizEvent(){
    if(typeof pickClassQuiz !== 'function') return null;
    var quiz = pickClassQuiz();
    if(!quiz) return null;
    quiz._eventKey = 'classQuiz';
    return quiz;
  }

  /* --- Coin Flip Bet ---
     5 hearts to bet, win 10 (net +5), lose nothing back. 50/50 odds.
     Hosted by a rogue/monk character. */
  function pickCoinBetEvent(){
    var COST = 5;
    /* R140-Z3h: Partial-cost play. */
    var bank = (typeof window.heartsRemainingToday === 'function') ? window.heartsRemainingToday() : 0;
    if(bank < 1) return null;
    var actualCost = Math.min(COST, bank);
    /* R65: Mythic host roll. Probability is low by default; jumps to
       ~20% after the user clears the daily wander quest. If
       the roll comes up, the event\'s host is a mythic and a +30
       bonus fires on win. The whole event UI gets the mythic glow. */
    var mythicHost = (typeof window.hohRollMythicHost === 'function') ? window.hohRollMythicHost() : null;
    var host;
    if(mythicHost){
      host = mythicHost;
    } else {
      var hostClasses = {Rogue:1,Monk:1,Charlatan:1};
      var allChars = _allChars();
      var preferred = allChars.filter(function(c){ return c && c.character_class && hostClasses[c.character_class]; });
      var pool = preferred.length >= 1 ? preferred : allChars.filter(function(c){ return c && c.id; });
      if(!pool.length) return null;
      host = pool[Math.floor(Math.random()*pool.length)];
    }
    return {
      type: 'coinBet',
      cost: actualCost,
      payout: 10,
      host: host,
      mythicHost: !!mythicHost,
      phase: 'idle',
      pickedSide: null,
      result: null,
      won: null,
      _eventKey: 'coinBet'
    };
  }

  /* --- Popularity Contest ---
     4 chars to reorder by hearts (high to low). Pool: at least one
     with >50 hearts and one with <30. No "new" chars. +3 hearts on
     exact-order match. */
  function pickPopularityEvent(){
    /* R169: Popularity Contest removed from the Hall by request. Returning
       null makes the event engine skip it everywhere it could be picked, the
       same way it already skips this event when the pool is too thin (see the
       null returns just below). The render code and registry entry are left in
       place, dormant, so it can be restored by deleting this one line. */
    return null;
    var all = _allChars().filter(function(c){
      return c && c.id && !_isFresh(c) && typeof c.hearts === 'number';
    });
    if(all.length < 4) return null;
    var high = all.filter(function(c){ return c.hearts > 50; });
    var low = all.filter(function(c){ return c.hearts < 30; });
    if(high.length < 1 || low.length < 1) return null;
    /* Build a 4-card lineup: at least 1 from high, at least 1 from
       low, fill the rest from the broader pool. */
    var picked = [];
    picked.push(high[Math.floor(Math.random()*high.length)]);
    picked.push(low[Math.floor(Math.random()*low.length)]);
    var rest = all.filter(function(c){
      return picked.every(function(p){ return p.id !== c.id; });
    });
    rest = _shuffle(rest).slice(0, 2);
    if(rest.length < 2) return null;
    picked = picked.concat(rest);
    var lineup = _shuffle(picked);
    return {
      type: 'popularity',
      chars: lineup,
      order: lineup.map(function(c){ return c.id; }), /* user\'s current order */
      correctOrder: lineup.slice().sort(function(a,b){ return (b.hearts||0) - (a.hearts||0); }).map(function(c){ return c.id; }),
      phase: 'idle',
      won: null,
      _eventKey: 'popularity'
    };
  }

  /* ================================================================ */
  /* Spin the Wheel — rare-special prize wheel                        */
  /* ================================================================ */
  /* A casino-style prize wheel. Cost 5 hearts to spin. 16 segments
     with weighted rewards designed for a ~5-heart expected return:
       1× 50  (3.13 EV)
       1× 10  (0.625)
       2× 5   (0.625)
       3× 3   (0.563)
       4× 1   (0.25)
       5× 0   (0)
     Total EV ~5.19 hearts on a 5-heart bet — net-zero expected with
     a fat tail (the 50 segment) so the spin feels exciting.
     Frequency: rare-special tier. Tracked via its own counter so it
     fires every 30-50 SPECIALS (not refreshes) — among all the rare
     specials that fire, the wheel is one in 30-50 of them. */
  /* Reweighted segments — EV ~10 hearts per 5-heart spin (+5 EV), with
     the jackpot still at 50. Distribution:
       1× 50 = 50 hearts  (3.125 EV)
       2× 25 = 50         (3.125 EV)
       3× 10 = 30         (1.875 EV)
       4× 5  = 20         (1.250 EV)
       4× 3  = 12         (0.750 EV)
       2× 0  = 0          (0.000 EV)
     Total = 162/16 = 10.125 EV. The wheel is the heart-richest daily
     gamble — a deliberate "spin once a day, get a small reliable
     payout, and chase the big number" loop. */
  /* R124-A: Rebalanced 16-slot wheel.
       1× 0   → 6.25%   ("you missed")
       9× 10  → 56.25%  (modal payout — the average roll)
       3× 20  → 18.75%  (slightly better than average)
       2× 50  → 12.5%   (rare)
       1× 100 → 6.25%   (jackpot, the big chase)
     EV = (0 + 90 + 60 + 100 + 100) / 16 = 21.9 hearts vs 5-heart spin
     cost. The wheel feels generous (cost-positive on every expected
     roll), and the 10-heart modal locks the "small-but-real-payout"
     beat the user wants. Hosts are interleaved so the user can't
     visually pattern-match a "good half / bad half" of the wheel. */
  var WHEEL_SEGMENTS = [
    /* index 0 = top (12 o'clock), increases clockwise. */
    { value: 100, color: '#ffd76a' }, /* JACKPOT */
    { value: 10,  color: '#7ed49a' },
    { value: 20,  color: '#ED97FE' },
    { value: 10,  color: '#7ed49a' },
    { value: 50,  color: '#ff6f8a' },
    { value: 10,  color: '#7ed49a' },
    { value: 0,   color: '#3a3450' },
    { value: 10,  color: '#7ed49a' },
    { value: 20,  color: '#ED97FE' },
    { value: 10,  color: '#7ed49a' },
    { value: 50,  color: '#ff6f8a' },
    { value: 10,  color: '#7ed49a' },
    { value: 20,  color: '#ED97FE' },
    { value: 10,  color: '#7ed49a' },
    { value: 10,  color: '#7ed49a' },
    { value: 10,  color: '#7ed49a' }
  ];
  /* Daily-cap key + helpers (parallel pattern to dice trader / duel /
     brawl). The wheel was rare-special-tier (every 30-50 specials, so
     basically never) before R37; now it\'s a daily-capped gambling
     event so users see it once per day reliably. */
  function wheelTodayKey(){
    var d = new Date();
    var ymd = d.getFullYear() + '-' + String(d.getMonth()+1).padStart(2,'0') + '-' + String(d.getDate()).padStart(2,'0');
    return 'hoh-wheel-done-' + ymd;
  }
  function wheelUsedToday(){
    try { return localStorage.getItem(wheelTodayKey()) === '1'; } catch(_){ return false; }
  }
  function markWheelUsedToday(){
    try { localStorage.setItem(wheelTodayKey(), '1'); } catch(_){}
  }
  function pickSpinWheelEvent(){
    var COST = 5;
    /* R119-I: Daily cap dropped. The wheel can fire occasionally on
       the gambling cadence subject to anti-repeat.
       R140-Z3h: Partial-cost play. */
    var bank = (typeof window.heartsRemainingToday === 'function') ? window.heartsRemainingToday() : 0;
    if(bank < 1) return null;
    var actualCost = Math.min(COST, bank);
    /* Host character — a sorcerer/bard/fortune-teller flavor sells
       the "step right up" shtick. Falls back to any char if the
       preferred class pool is too thin. */
    var allChars = (typeof window.hohGetAllCharacters === 'function') ? window.hohGetAllCharacters() : [];
    if(!allChars || !allChars.length) return null;
    var preferredClasses = {Sorcerer:1,Bard:1,Warlock:1,Charlatan:1,Wizard:1};
    var preferred = allChars.filter(function(c){ return c && c.character_class && preferredClasses[c.character_class]; });
    var pool = preferred.length >= 2 ? preferred : allChars.filter(function(c){ return c && c.id; });
    if(!pool.length) return null;
    var host = pool[Math.floor(Math.random() * pool.length)];
    /* Pre-pick the winning segment so the rotation animation can
       deterministically land on it. */
    var idx = Math.floor(Math.random() * WHEEL_SEGMENTS.length);
    return {
      type: 'spinWheel',
      phase: 'idle',
      cost: actualCost,
      host: host,
      winningIndex: idx,
      _eventKey: 'spinWheel'
    };
  }
  /* R93: Spin the Wheel — rebuilt as a 4×4 slot-reel grid. Same 16
     outcomes as before, same EV, same daily cap, but the spin is a
     reliable highlight cycle through tiles instead of a CSS-transform
     conic-gradient wheel. */
  function renderSpinWheelHtml(state){
    var phase = state.phase || 'idle';
    var cost = state.cost || 5;
    var activeIdx = (phase === 'won' && typeof state.winningIndex === 'number') ? state.winningIndex : -1;
    var tilesHtml = WHEEL_SEGMENTS.map(function(seg, i){
      var isActive = (i === activeIdx);
      var cls = 'hoh-fresh-reel-tile hoh-fresh-reel-tile-v' + seg.value + (isActive ? ' hoh-fresh-reel-tile-active' : '');
      return '<div class="' + cls + '" data-reel-idx="' + i + '">' +
        '<span class="hoh-fresh-reel-tile-heart" aria-hidden="true">&#9829;&#xFE0E;</span>' +
        '<span class="hoh-fresh-reel-tile-value">' + seg.value + '</span>' +
      '</div>';
    }).join('');
    var reelHtml = '<div class="hoh-fresh-reel hoh-fresh-reel-wheel" data-reel-count="' + WHEEL_SEGMENTS.length + '">' +
      tilesHtml +
    '</div>';
    var stageHtml;
    if(phase === 'idle'){
      stageHtml =
        '<div class="hoh-fresh-reel-stage">' +
          reelHtml +
          '<button type="button" class="hoh-fresh-coin-toss hoh-fresh-wheel-go" onclick="hohFreshFacesSpinTheWheel()">' +
            '<span>Spin the Wheel</span>' +
            '<span class="hoh-fresh-dice-cost">&#9829;&#xFE0E; &minus;' + cost + '</span>' +
          '</button>' +
        '</div>';
    } else if(phase === 'spinning'){
      stageHtml =
        '<div class="hoh-fresh-reel-stage">' +
          reelHtml +
          '<div class="hoh-fresh-reel-status">Spinning&hellip;</div>' +
        '</div>';
    } else { /* won */
      var winSeg = WHEEL_SEGMENTS[state.winningIndex] || { value: 0 };
      var n = winSeg.value;
      var resultLine = (n > 0)
        ? '<strong>+' + n + ' hearts</strong><span class="hoh-fresh-wheel-sub">' + (n >= 50 ? 'The wheel sings.' : (n >= 10 ? 'A tidy haul.' : 'A small return.')) + '</span>'
        : '<strong>No prize</strong><span class="hoh-fresh-wheel-sub">The wheel turns and the wheel keeps. Try again next time.</span>';
      stageHtml =
        '<div class="hoh-fresh-reel-stage">' +
          reelHtml +
          '<div class="hoh-fresh-wheel-result">' + resultLine + '</div>' +
          '<div class="hoh-fresh-coin-actions">' +
            '<button type="button" class="hoh-fresh-coin-toss hoh-fresh-dice-continue" onclick="hohFreshFacesWheelContinue()">' +
              '<span>Back to the Hall</span>' +
            '</button>' +
          '</div>' +
        '</div>';
    }
    /* Host card — a charlatan/sorcerer-flavored character "running"
       the wheel. Same compact card pattern as the dice trader host:
       clickable to open detail, but heart/X buttons hidden via the
       parent .hoh-fresh-special-wheel scope in CSS. */
    var hostHtml = '';
    var host = state.host;
    if(host){
      var hImg = host.image_thumb_url || host.image_url || '';
      var hLetter = esc((host.name || '?').charAt(0));
      /* R140-F: Offset-aware host img — see _eventCharImg helper. */
      var hMedia = _eventCharImg(host, { eager: true, draggable: false });
      hostHtml = '<div class="hoh-fresh-wheel-host">' +
        '<div role="button" tabindex="0" class="hoh-fresh-card hoh-fresh-card-host" data-id="' + esc(host.id) + '"' +
          ' onclick="if(window.hohOpenDetail)hohOpenDetail(\'' + esc(host.id) + '\')"' +
          ' onkeydown="if((event.key===\'Enter\'||event.key===\' \')&&window.hohOpenDetail){event.preventDefault();hohOpenDetail(\'' + esc(host.id) + '\');}"' +
          ' aria-label="Open ' + esc(host.name || '') + '">' +
          '<span class="hoh-fresh-media">' + hMedia + '</span>' +
          '<span class="hoh-fresh-body">' +
            '<span class="hoh-fresh-name">' + esc(host.name || 'Unknown') + '</span>' +
            '<span class="hoh-fresh-meta">' + esc((host.race || '') + (host.character_class ? ' ' + host.character_class : '')) + '</span>' +
          '</span>' +
        '</div>' +
      '</div>';
    }
    var hideRefresh = (phase === 'won');
    return '<div class="hoh-fresh-special hoh-fresh-special-wheel">' +
      eventHeaderHtml('The Wheel of Fortune', 'Sixteen outcomes. Five hearts gets you one spin.', { centered: true, hideRefresh: hideRefresh }) +
      hostHtml +
      stageHtml +
    '</div>';
  }
  window.hohFreshFacesSpinTheWheel = function(){
    if(!currentSpecial || currentSpecial.type !== 'spinWheel') return;
    if(currentSpecial.phase !== 'idle') return;
    var cost = currentSpecial.cost || 5;
    var bank = (typeof window.heartsRemainingToday === 'function') ? window.heartsRemainingToday() : 0;
    if(bank < cost){
      if(typeof window.hohShowHeartToast === 'function'){
        try { window.hohShowHeartToast('Not enough hearts', 'You need ' + cost + ' hearts to spin.'); } catch(_){}
      }
      return;
    }
    /* Pay the cost via the same direct-ledger pattern dice trader
       and legendary shop use. */
    try {
      var raw = parseInt(localStorage.getItem('hoh-heart-bank-v1') || '0', 10) || 0;
      try{ window.hohHeartHint && window.hohHeartHint('spend_feature'); }catch(e){}
      localStorage.setItem('hoh-heart-bank-v1', String(Math.max(0, raw - cost)));
      try { document.dispatchEvent(new CustomEvent('hoh:hearts-bumped', { detail: { spent: cost } })); } catch(_){}
    } catch(_){}
    if(typeof window.hohRenderHeaderCounter === 'function'){
      try { window.hohRenderHeaderCounter(); } catch(_){}
    }
    /* R93: Slot-reel cycle — same reliable pattern as the mini
       spinner. Highlight cursor moves through 16 tiles, decelerates,
       lands on the winning index. */
    currentSpecial.phase = 'spinning';
    var container = _ffContainer();
    if(container) container.innerHTML = renderSpinWheelHtml(currentSpecial);
    _runReelCycle({
      reelSelector: '#hoh-fresh-faces .hoh-fresh-reel-wheel',
      tileCount: WHEEL_SEGMENTS.length,
      winningIndex: currentSpecial.winningIndex,
      totalDurationMs: 2600,
      onDone: function(){
        if(!currentSpecial || currentSpecial.type !== 'spinWheel') return;
        currentSpecial.phase = 'won';
        if(container) container.innerHTML = renderSpinWheelHtml(currentSpecial);
        var winSeg = WHEEL_SEGMENTS[currentSpecial.winningIndex] || { value: 0 };
        var reward = winSeg.value;
        if(reward > 0){
          if(typeof window.hohAddHeartsToBank === 'function'){
            try{ window.hohHeartHint && window.hohHeartHint('earn_explore_wheel'); }catch(e){}
            try { window.hohAddHeartsToBank(reward); } catch(_){}
          }
          if(typeof window.hohShowHeartToast === 'function'){
            try { window.hohShowHeartToast('+' + reward + ' hearts', reward >= 50 ? 'The wheel of fortune blesses you.' : 'A spin well-played.'); } catch(_){}
          }
          try {
            var anchor = document.querySelector('#hoh-fresh-faces .hoh-fresh-reel-wheel .hoh-fresh-reel-tile-active');
            if(anchor) flyDiceTraderHeartsToPill(anchor, Math.min(reward, 12));
          } catch(_){}
          if(reward >= 50){
            try { spawnConfetti(); } catch(_){}
            setTimeout(function(){ try { spawnConfetti(); } catch(_){} }, 250);
          }
        }
        try { markWheelUsedToday(); } catch(_){}
        if(typeof startAutoTimer === 'function') setTimeout(startAutoTimer, 0);
      }
    });
  };
  /* R52: Continue handler for the wheel resolved screen. Same shape
     as DiceTraderContinue / DuelContinue / BrawlContinue / PickACard
     Continue — clears the special and triggers a free advance. */
  window.hohFreshFacesWheelContinue = function(){
    if(!currentSpecial || currentSpecial.type !== 'spinWheel') return;
    if(currentSpecial.phase !== 'won') return;
    currentSpecial = null;
    if(typeof window.hohFreshFacesRefresh === 'function'){
      try { window.hohFreshFacesRefresh({ fromInteractive: true }); } catch(_){}
    }
  };

  /* ================================================================ */
  /* Legendary Shop                                                   */
  /* ================================================================ */
  /* The rarest event in the Hall. Once per ~45 manual refreshes
     AND at most once per N days (LEGSHOP_WEEKLY_COOLDOWN_DAYS), a
     wandering merchant offers four trades the user picks ONE of
     (current as of R166 — historical pricing in inline comments
     below):
       - 1000 hearts → 20% off code for a Portrait commission ('Hero20')
       - 500 hearts  → "Shape the Hall" event-idea submission to Jan
       - 50 hearts   → "Experience All Events" buff (forced tour)
       - 1 heart     → confetti, +30 hearts back
     Persistence keys:
       hoh-legshop-last-ymd        last date the shop was offered
       hoh-legshop-refresh-count   monotonic refresh counter
       hoh-extra-sub-{ymd}         set by 50h pick to allow extra submission
     The shop counts as a special event for spacing, so it respects
     MIN_ABSOLUTE_GAP and won't stack with other events.
     R117 → R166: The discount code is BACK to a percentage — 20% off
     a Portrait commission. The 'Hero20' code is configured in Shopify
     admin as a 20% discount on the Portrait product. Earlier R117
     swapped it to a fixed $20 credit, then R166 reverted to 20% based
     on user feedback that the framing should match what's at the
     checkout. If you change this again, update the option title +
     subtitle + resolved sub + the two toasts below to match. */
  var LEGSHOP_LAST_YMD_KEY = 'hoh-legshop-last-ymd';
  var LEGSHOP_REFRESH_COUNT_KEY = 'hoh-legshop-refresh-count';
  /* R53: lowered 100 → 30 so the shop appears more often. The 7-day
     cooldown still gates back-to-back appearances on the same week.
     R96: shop now appears DAILY at 70 manual refreshes — both the
     threshold and cooldown bumped to match the user's "1 daily,
     guaranteed by roll 70" spec. The 100-heart option is also
     simplified to a clean 100h→1 ticket trade (was a portrait
     discount + bonus ticket combo). */
  /* R150: lowered 70 -> 45 so the Legendary Shop (the marquee Type 3
     legendary event) comes around noticeably more often. */
  var LEGSHOP_REFRESH_THRESHOLD = 45;
  /* R119-I: Daily cooldown dropped to 0 days. The shop is now paced
     entirely by the 70-roll refresh threshold + a fresh-roll counter
     that resets on each offer, so the user has to actually wander
     ~70 events between visits even within the same day. The legacy
     name is kept to avoid touching helper signatures elsewhere. */
  var LEGSHOP_WEEKLY_COOLDOWN_DAYS = 0;
  var LEGSHOP_DISCOUNT_CODE = 'Hero20';
  function legshopReadCount(){
    try {
      var n = parseInt(localStorage.getItem(LEGSHOP_REFRESH_COUNT_KEY) || '0', 10);
      return isNaN(n) ? 0 : n;
    } catch(_){ return 0; }
  }
  function legshopBumpCount(){
    try {
      var n = legshopReadCount() + 1;
      localStorage.setItem(LEGSHOP_REFRESH_COUNT_KEY, String(n));
    } catch(_){}
  }
  function legshopReadyByCooldown(){
    try {
      var last = localStorage.getItem(LEGSHOP_LAST_YMD_KEY) || '';
      if(!last) return true;
      var lastTs = new Date(last).getTime();
      if(isNaN(lastTs)) return true;
      var ageMs = Date.now() - lastTs;
      return ageMs >= LEGSHOP_WEEKLY_COOLDOWN_DAYS * 24 * 60 * 60 * 1000;
    } catch(_){ return true; }
  }
  function legshopMarkOffered(){
    try {
      localStorage.setItem(LEGSHOP_LAST_YMD_KEY, new Date().toISOString().slice(0,10));
      /* Reset the refresh counter so the next shop is again 100
         refreshes away. */
      localStorage.setItem(LEGSHOP_REFRESH_COUNT_KEY, '0');
    } catch(_){}
  }
  function pickLegendaryShopEvent(opts){
    /* Triple gate: refresh count over threshold, weekly cooldown
       satisfied, AND user has at least 1 heart so the cheapest
       option is affordable.
       R140-D: Optional `opts.force` bypasses the refresh threshold +
       cooldown gates so the Mage's Bazaar "Audience with the Hall"
       buff can cycle through Legendary Shop on demand. The buff is
       paid for explicitly (20 hearts) — the user expects to actually
       see it, not have it silently skipped because the natural
       70-roll threshold hadn't been hit yet. */
    var forced = !!(opts && opts.force);
    if(!forced){
      if(legshopReadCount() < LEGSHOP_REFRESH_THRESHOLD) return null;
      if(!legshopReadyByCooldown()) return null;
    }
    /* R140-Z: Hide the shop entirely once all 3 daily items have been
       traded for today. The user explicitly asked for the shop to
       disappear when there's nothing left to buy — anything else
       feels like dead UI. The forced path (Audience-with-the-Hall
       buff) still skips this gate so the buff isn't wasted. */
    if(!forced && legshopAllItemsBoughtToday()) return null;
    var bank = (typeof window.heartsRemainingToday === 'function') ? window.heartsRemainingToday() : 0;
    /* R140-Z3n: Cheapest item is the 1-heart confetti option, so
       gate at bank >= 1. Higher-priced items grey out individually
       inside the shop when the wallet can't cover them. */
    if(bank < 1) return null;
    /* R101: Legendary host. The shop is supposed to feel rare, so the
       merchant in starlit robes is now an actual legendary-rarity
       character from the Hall (not just abstract title text). Falls
       back to any character with an id if the legendary pool is empty
       so a thin Hall doesn't kill the event. */
    var legPool = poolByTier('legendary');
    if(!legPool.length){
      var allChars = (typeof window.hohGetAllCharacters === 'function') ? (window.hohGetAllCharacters() || []) : [];
      legPool = allChars.filter(function(c){ return c && c.id; });
    }
    if(!legPool.length) return null;
    var host = legPool[Math.floor(Math.random() * legPool.length)];
    return {
      type: 'legendaryShop',
      phase: 'idle',
      pickedOption: null,
      host: host,
      _eventKey: 'legendaryShop'
    };
  }

  /* Render the Legendary Shop. Three centered option cards with a
     conic-rainbow border so the rarity reads instantly. After a pick
     the shop transitions to a thanks/done state. */
  function renderLegendaryShopHtml(state){
    var phase = state.phase || 'idle';
    if(phase === 'idle'){
      var bank = (typeof window.heartsRemainingToday === 'function') ? window.heartsRemainingToday() : 0;
      /* R148-Z32: Reprice + relabel.
         - Commission ($20 Portrait code) bumped 300 → 1000h, themed
           "Commission a Portrait" so the trade reads as commissioning
           a D&D artist (the actual product), not a gift card abstraction.
         - Raffle ticket REMOVED entirely. Replaced with a 500h
           "Submit Your Idea" slot where the user types a themed roll
           or heart-event idea; submission flows to Supabase for review.
         - All Events stays at 50h.
         - Confetti reward bumped from 0 → 50h on the 1h cost (net
           +49 daily heart claim, gated by once-per-day flag). */
      var aff1000 = bank >= 1000;
      var aff500  = bank >= 500;
      var aff50   = bank >= 50;
      var commissionAlreadyBoughtToday = legshopDiscountBoughtToday(); /* reuse same flag key */
      var submissionAlreadyBoughtToday = legshopTicketBoughtToday();   /* reuse same flag key */
      var allEventsAlreadyBoughtToday = legshopAllEventsBoughtToday();
      var confettiAlreadyBoughtToday = legshopConfettiBoughtToday();
      var aff1 = bank >= 1;
      /* R140-Z3j: Compute time until midnight local so each disabled
         item shows a small "Resets in 12h 34m" countdown instead of
         the generic "Try again tomorrow." text. Updates on every
         render (which happens after wallet changes / heart claims),
         so the timer ticks down naturally as the user interacts.
         Helper returns a formatted string. */
      var _legshopResetIn = function(){
        try {
          var now = new Date();
          var tomorrow = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1, 0, 0, 0);
          var diff = Math.max(0, tomorrow.getTime() - now.getTime());
          var h = Math.floor(diff / 3600000);
          var m = Math.floor((diff % 3600000) / 60000);
          if(h > 0) return 'Resets in ' + h + 'h ' + m + 'm';
          return 'Resets in ' + m + 'm';
        } catch(_){ return 'Resets at midnight'; }
      };
      var optionCard = function(pickKey, price, title, sub, affordable, alreadyUsedToday, customLock){
        var heldLocked = !!(customLock && customLock.locked);
        var lockedToday = !!alreadyUsedToday;
        var disabledAttr = (!affordable || lockedToday || heldLocked) ? ' disabled' : '';
        var disabledCls = (!affordable || lockedToday || heldLocked) ? ' hoh-fresh-legshop-option-disabled' : '';
        /* R140-Z3j: Bought-today items now show a small cooldown timer
           pinned at the bottom of the card instead of the generic
           "Try again tomorrow" sub. Keeps the item visible in the
           grid (greyed out) so the user can see what they already
           claimed and when it comes back. */
        var sub2;
        var cooldownChip = '';
        if(heldLocked){
          sub2 = (customLock && customLock.msg) || 'Already held.';
        } else if(lockedToday){
          sub2 = 'Claimed today.';
          cooldownChip = '<span class="hoh-fresh-legshop-option-cooldown">' + _legshopResetIn() + '</span>';
        } else if(!affordable){
          sub2 = 'Need ' + price + ' hearts';
        } else {
          sub2 = sub;
        }
        return '<button type="button" class="hoh-fresh-legshop-option' + disabledCls + '"' + disabledAttr +
               ' data-pick="' + pickKey + '"' +
               ' onclick="hohFreshFacesLegShopPick(\'' + pickKey + '\')">' +
          '<span class="hoh-fresh-legshop-option-price">&#9829;&#xFE0E; ' + price + '</span>' +
          '<span class="hoh-fresh-legshop-option-title">' + title + '</span>' +
          '<span class="hoh-fresh-legshop-option-sub">' + sub2 + '</span>' +
          cooldownChip +
        '</button>';
      };
      /* R140-Z: Three meaningful trades, each gated to once per day.
         "Submit a Second Hero" was removed (didn't carry weight day-to-
         day). Confetti was removed (it was filler that never let the
         shop close out). The new All Events buff is the cheapest entry
         and rewires the next ~20 rolls into a guided tour of specials,
         trivia, and duels. */
      /* R148-Z32: Reworked option list — see header comment. */
      var options =
        optionCard('discount',  1000, '20% Off a Portrait', 'A 20% discount code toward an exclusive D&amp;D portrait commission from Jan. Apply at checkout.', aff1000, commissionAlreadyBoughtToday) +
        /* R267: no "held first" lock — holding a Mark now pops the placer
           instead of blocking, so the tile is always actionable. */
        /* R590 (Jan): "change the user facing copy to say that it only
           works in explore the hall." The buff is real (sql/R208:105
           adds +1 per heart) but it rides mark_heart_explore, which the
           client only calls on the Explore path. A heart given on the
           main gallery, in Your Hall, or on the swipe surface pays
           nothing. "Double Explore hearts" read like the name of a
           currency. It names a PLACE now. */
        optionCard('merchantsMark', 1000, 'Merchant\'s Mark', 'A mythic badge sold only here. Place it on one of your heroes and every heart they are given in Explore the Hall counts twice.', aff1000, false) +
        optionCard('allEvents',   50, 'Experience All Events', 'Cycle through 2 specials, every trivia, and every duel — back to back.', aff50, allEventsAlreadyBoughtToday) +
        /* R150: Confetti — 1h in, +30h out. The cheapest item, a daily
           heart pump for engaged players (once-per-day cap stays). The
           1h cost is symbolic; the +30 plus the heart-fly is the reward. */
        optionCard('confetti',    1, 'Confetti', 'A single heart for a celebration. The Hall pays you back with 30.', aff1, confettiAlreadyBoughtToday);
      return '<div class="hoh-fresh-special hoh-fresh-special-legshop">' +
        eventHeaderHtml('A Legendary Shop', 'A merchant in starlit robes offers three trades. Choose one — only one — and the shop fades back to wherever it came from.', { centered: true }) +
        renderLegshopHostHtml(state) +
        '<div class="hoh-fresh-legshop-grid">' + options + '</div>' +
      '</div>';
    }
    /* R148-Z32: New phase — submissionForm. Renders a textarea so
       the user types their idea AFTER paying 500 hearts. The Send
       button POSTs to Supabase via submit_event_idea RPC; on success
       we transition to phase 'resolved' with picked='ticket' so the
       resolved screen below shows the thank-you copy. */
    if(phase === 'submissionForm'){
      var charCount = (state.submissionText || '').length;
      var maxLen = 500;
      /* R150: prefill the email if the visitor is signed in. The field
         is editable and required either way so Jan can always reply. */
      var prefillEmail = '';
      try {
        if(typeof window.hohYourHallEmail === 'function'){
          prefillEmail = (window.hohYourHallEmail() || '').trim();
        }
      } catch(_){}
      return '<div class="hoh-fresh-special hoh-fresh-special-legshop hoh-fresh-special-legshop-submission">' +
        eventHeaderHtml('Draft Your Event', 'Design a themed roll, a heart event, or a new way to spend hearts. The best designs become part of the Hall.', { centered: true }) +
        renderLegshopHostHtml(state) +
        '<div class="hoh-fresh-legshop-submission">' +
          '<textarea id="hoh-legshop-submission-text" class="hoh-fresh-legshop-textarea" rows="10" maxlength="' + maxLen + '" placeholder="A themed roll called The Crimson Court — only Tieflings, only at night. An event where rolling three hearts triggers a duel. A new use for hearts: tip the bard..." oninput="hohFreshFacesLegshopSubmissionInput()"></textarea>' +
          '<div class="hoh-fresh-legshop-submission-meta">' +
            '<span class="hoh-fresh-legshop-submission-count" id="hoh-legshop-submission-count">' + charCount + ' / ' + maxLen + '</span>' +
          '</div>' +
          '<input type="email" id="hoh-legshop-submission-email" class="hoh-fresh-legshop-email" inputmode="email" autocomplete="email" maxlength="120" placeholder="Your email, so Jan can reach you" value="' + esc(prefillEmail) + '" oninput="hohFreshFacesLegshopSubmissionInput()">' +
          '<button type="button" id="hoh-legshop-submission-send" class="hoh-fresh-coin-toss hoh-fresh-legshop-send" disabled onclick="hohFreshFacesLegshopSubmitIdea()">' +
            '<span>Send to Jan</span>' +
          '</button>' +
        '</div>' +
      '</div>';
    }
    /* Resolved — show the chosen reward + a wander button. */
    var picked = state.pickedOption;
    var resultBody;
    if(picked === 'ticket'){
      /* R148-Z37: Submission resolved screen. Frame the moment as
         authorship — the player just designed something that could
         become canon in the Hall. Quote-block their submission so
         their words feel preserved, not filed away. */
      var sentText = state.submissionText || '';
      var preview = sentText.length > 140 ? (sentText.slice(0, 137) + '...') : sentText;
      resultBody =
        '<div class="hoh-fresh-legshop-result-title">Your event is on the table.</div>' +
        '<div class="hoh-fresh-legshop-result-sub">Jan reads every draft. If it lands, every player in the Hall walks through your work.</div>' +
        (preview ? '<div class="hoh-fresh-legshop-result-quote">&ldquo;' + esc(preview) + '&rdquo;</div>' : '');
    } else if(picked === 'discount'){
      /* R166: Copy now reflects what the Shopify code ACTUALLY is —
         a 20% discount on the Portrait commission product, not a free
         commission ("Commission a Portrait" was reading as "you'll
         get a portrait for 1000 hearts") and not a $20 fixed credit
         (older R117 / R131 comments said that — also wrong now). The
         'Hero20' Shopify code is configured as 20% off; the copy here
         must say 20% off so the trade is honest and users aren't
         disappointed at checkout. */
      resultBody =
        '<div class="hoh-fresh-legshop-result-title">20% off, sealed.</div>' +
        '<button type="button" class="hoh-fresh-legshop-code" onclick="hohFreshFacesLegShopCopy()">' + LEGSHOP_DISCOUNT_CODE + '</button>' +
        '<div class="hoh-fresh-legshop-result-sub">Tap to copy. Apply at checkout for 20% off your D&amp;D portrait commission.</div>';
    } else if(picked === 'allEvents'){
      /* R140-Z: New 50-heart buff result. Tells the user what's next so
         the long chain of forced events doesn't feel random. */
      resultBody =
        '<div class="hoh-fresh-legshop-result-title">The hall opens its doors.</div>' +
        '<div class="hoh-fresh-legshop-result-sub">Two specials first, then every trivia, then every duel. Explore to begin.</div>';
    } else if(picked === 'confetti'){
      /* R150-Z45: Confetti — 1h in, +30h out. The result screen now
         states the payout so the reward is confirmed in text, not
         only in the fly animation + toast. The burst + heart-fly
         fire in the resolve handler via _confettiPending. */
      resultBody =
        '<div class="hoh-fresh-legshop-result-title">A small celebration.</div>' +
        '<div class="hoh-fresh-legshop-result-sub">One heart in, and a burst of color.</div>' +
        '<div class="hoh-fresh-duel-result-sub"><strong>The Hall celebrates with you</strong></div>';
    } else if(picked === 'merchantsMark'){
      resultBody =
        '<div class="hoh-fresh-legshop-result-title">A mark of starlight.</div>' +
        '<div class="hoh-fresh-legshop-result-sub">Open one of your own heroes and place the Merchant&rsquo;s Mark. They keep a mythic badge, and every heart they are given in Explore the Hall counts twice.</div>';
    } else {
      /* Defensive fallback — shouldn't render in practice now that the
         confetti / extraSub options are removed. */
      resultBody =
        '<div class="hoh-fresh-legshop-result-title">The merchant tips their hat.</div>' +
        '<div class="hoh-fresh-legshop-result-sub">Keep exploring.</div>';
    }
    /* R131-A / R166: When the user picks the 20% Portrait discount,
       the moment to convert is RIGHT NOW — they have the code, the
       hero is hot in their head. Replace "Wander Further" with a
       direct link to the Portrait product so the code gets used at
       checkout. Other options keep "Wander Further" since they don't
       have a destination outside the hall. The link opens in a new
       tab so the user doesn't lose their place in the hall. */
    var ctaHtml;
    if(picked === 'discount'){
      ctaHtml =
        '<a class="hoh-fresh-coin-toss hoh-fresh-coin-toss-waitlist" href="https://framedfantasy.com/products/fantasy-character-commission" target="_blank" rel="noopener noreferrer">' +
          '<span>Join the Waitlist</span>' +
        '</a>';
    } else {
      ctaHtml =
        '<button type="button" class="hoh-fresh-coin-toss" onclick="hohFreshFacesRefresh({fromInteractive:true})"><span>Keep exploring</span></button>';
    }
    return '<div class="hoh-fresh-special hoh-fresh-special-legshop hoh-fresh-special-legshop-resolved">' +
      eventHeaderHtml('A Legendary Shop', 'The merchant tips their hat and steps back into the dust.', { centered: true }) +
      renderLegshopHostHtml(state) +
      '<div class="hoh-fresh-legshop-result">' + resultBody +
        ctaHtml +
      '</div>' +
    '</div>';
  }
  /* R101: Host card renderer for the Legendary Shop. The "merchant in
     starlit robes" copy now points to a real legendary-rarity hero
     from the Hall. Card opens the detail modal on click; heart + X
     are hidden in event mode (CSS handles that on the parent
     .hoh-fresh-special-legshop). */
  /* R267: open the hero picker to PLACE a Merchant's Mark the user already
     holds. Used both when the Legendary Shop appears (so a held-but-unplaced
     Mark gets applied right away) and when the shop tile is tapped while
     holding one. Returns true if the picker opened. */
  function _openMerchPlacer(){
    try {
      if(typeof window.hohBadgeApplyPicker === 'function'){
        return !!window.hohBadgeApplyPicker('merchants-mark', { applyFn:function(k,id){ return (typeof window.hohApplyMerchToHero==='function') ? window.hohApplyMerchToHero(id) : null; } });
      }
    } catch(_){}
    return false;
  }
  function renderLegshopHostHtml(state){
    var host = state && state.host;
    if(!host || !host.id) return '';
    var hImg = host.image_thumb_url || host.image_url || '';
    var hLetter = esc((host.name || '?').charAt(0));
    /* R140-F: Offset-aware host img — see _eventCharImg helper. */
    var hMedia = _eventCharImg(host, { eager: true, draggable: false });
    return '<div class="hoh-fresh-legshop-host">' +
      '<div role="button" tabindex="0" class="hoh-fresh-card hoh-fresh-card-host" data-id="' + esc(host.id) + '"' +
        ' onclick="if(window.hohOpenDetail)hohOpenDetail(\'' + esc(host.id) + '\')"' +
        ' aria-label="Open ' + esc(host.name || '') + '">' +
        '<span class="hoh-fresh-media">' + hMedia + '</span>' +
        '<span class="hoh-fresh-body">' +
          '<span class="hoh-fresh-name">' + esc(host.name || 'A wandering merchant') + '</span>' +
          '<span class="hoh-fresh-meta">' + esc((host.race || '') + (host.character_class ? ' ' + host.character_class : '')) + '</span>' +
        '</span>' +
      '</div>' +
    '</div>';
  }

  /* Pick handler. Validates affordability AGAIN at action time
     (wallet may have shrunk between event-fire and click), deducts
     the price, applies the reward, and resolves the shop. */
  /* R140-D: Once-per-day ticket purchase gate. The user wants the
     raffle ticket trade limited to a single buy per day total —
     same logic that gates other daily wallet earns elsewhere. We
     store under hoh-legshop-ticket-bought-{ymd} and check on click.
     Offered hours are unbounded (the shop can re-show), but only the
     first buy of the day actually mints a ticket.
     R140-Z: Same once-per-day gate added for the $20 credit (was
     unlimited) and the new 50-heart All Events buff. When all three
     are bought today, pickLegendaryShopEvent returns null so the
     shop hides itself entirely. */
  function legshopTicketBoughtTodayKey(){
    return 'hoh-legshop-ticket-bought-' + _ymdNow();
  }
  function legshopTicketBoughtToday(){
    try { return localStorage.getItem(legshopTicketBoughtTodayKey()) === '1'; } catch(_){ return false; }
  }
  function markLegshopTicketBoughtToday(){
    try { localStorage.setItem(legshopTicketBoughtTodayKey(), '1'); } catch(_){}
  }
  function legshopDiscountBoughtTodayKey(){
    return 'hoh-legshop-discount-bought-' + _ymdNow();
  }
  function legshopDiscountBoughtToday(){
    try { return localStorage.getItem(legshopDiscountBoughtTodayKey()) === '1'; } catch(_){ return false; }
  }
  function markLegshopDiscountBoughtToday(){
    try { localStorage.setItem(legshopDiscountBoughtTodayKey(), '1'); } catch(_){}
  }
  function legshopAllEventsBoughtTodayKey(){
    return 'hoh-legshop-allevents-bought-' + _ymdNow();
  }
  function legshopAllEventsBoughtToday(){
    try { return localStorage.getItem(legshopAllEventsBoughtTodayKey()) === '1'; } catch(_){ return false; }
  }
  function markLegshopAllEventsBoughtToday(){
    try { localStorage.setItem(legshopAllEventsBoughtTodayKey(), '1'); } catch(_){}
  }
  /* R140-Z3n: Restored 4th legshop option — confetti for 1 heart. The
     user explicitly asked for the cheap-and-fun option back. Once per
     day like every other slot, so the shop still closes out cleanly
     when all 4 are spent. */
  function legshopConfettiBoughtTodayKey(){
    return 'hoh-legshop-confetti-bought-' + _ymdNow();
  }
  function legshopConfettiBoughtToday(){
    try { return localStorage.getItem(legshopConfettiBoughtTodayKey()) === '1'; } catch(_){ return false; }
  }
  function markLegshopConfettiBoughtToday(){
    try { localStorage.setItem(legshopConfettiBoughtTodayKey(), '1'); } catch(_){}
  }
  function legshopAllItemsBoughtToday(){
    return legshopTicketBoughtToday()
        && legshopDiscountBoughtToday()
        && legshopAllEventsBoughtToday()
        && legshopConfettiBoughtToday();
  }
  window.hohFreshFacesLegShopPick = function(option){
    if(!currentSpecial || currentSpecial.type !== 'legendaryShop') return;
    if(currentSpecial.phase !== 'idle') return;
    /* R150-Z45: Spending hearts is a member action — block it for
       signed-out visitors (the events are gated, but debug mode can
       surface them). Show the "Start earning hearts" popup. */
    if(typeof window.hohIsHallSignedIn === 'function' && !window.hohIsHallSignedIn()){
      if(typeof window.hohOpenSubmitToEarnModal === 'function'){
        try { window.hohOpenSubmitToEarnModal(null); } catch(_){}
      }
      return;
    }
    /* R140-Z / R140-Z3n: Price ladder.
         discount   = 300 hearts
         ticket     = 100 hearts
         allEvents  = 50 hearts
         confetti   = 1 heart (restored cheap-and-fun celebration) */
    var price = (option === 'discount') ? 300
              : (option === 'merchantsMark') ? 1000
              : (option === 'ticket')   ? 100
              : (option === 'allEvents') ? 50
              : (option === 'confetti')  ? 1
              : 0;
    if(price <= 0) return;
    /* R140-Z / R140-Z3n: Hard once-per-day gates on all four options. */
    if(option === 'confetti' && legshopConfettiBoughtToday()){
      if(typeof window.hohShowHeartToast === 'function'){
        try { window.hohShowHeartToast('Once a day', 'Confetti trade is once per day. Come back tomorrow.'); } catch(_){}
      }
      return;
    }
    if(option === 'ticket' && legshopTicketBoughtToday()){
      if(typeof window.hohShowHeartToast === 'function'){
        try { window.hohShowHeartToast('Once a day', 'Raffle ticket trade is once per day. Come back tomorrow.'); } catch(_){}
      }
      try {
        if(typeof window.hohShowRaffleTicketLightbox === 'function'){
          window.hohShowRaffleTicketLightbox({ granted: false });
        }
      } catch(_){}
      return;
    }
    if(option === 'discount' && legshopDiscountBoughtToday()){
      if(typeof window.hohShowHeartToast === 'function'){
        try { window.hohShowHeartToast('Once a day', 'The 20% discount code is once per day. Come back tomorrow.'); } catch(_){}
      }
      return;
    }
    if(option === 'allEvents' && legshopAllEventsBoughtToday()){
      if(typeof window.hohShowHeartToast === 'function'){
        try { window.hohShowHeartToast('Once a day', 'The all-events tour is once per day. Come back tomorrow.'); } catch(_){}
      }
      return;
    }
    /* R267: tapping Merchant's Mark while you already hold one opens the hero
       picker to PLACE it (no more "place it first" block). */
    if(option === 'merchantsMark' && typeof window.hohMerchHeld === 'function' && window.hohMerchHeld() > 0){
      _openMerchPlacer();
      return;
    }
    var bank = (typeof window.heartsRemainingToday === 'function') ? window.heartsRemainingToday() : 0;
    if(bank < price){
      if(typeof window.hohShowHeartToast === 'function'){
        try { window.hohShowHeartToast('Not enough hearts', 'You need ' + price + ' hearts for that trade.'); } catch(_){}
      }
      return;
    }
    /* Pay the price via the same direct-ledger pattern the dice
       trader uses. */
    try {
      var raw = parseInt(localStorage.getItem('hoh-heart-bank-v1') || '0', 10) || 0;
      try{ window.hohHeartHint && window.hohHeartHint('spend_purchase'); }catch(e){}
      localStorage.setItem('hoh-heart-bank-v1', String(Math.max(0, raw - price)));
      try { document.dispatchEvent(new CustomEvent('hoh:hearts-bumped', { detail: { spent: price } })); } catch(_){}
    } catch(_){}
    if(typeof window.hohRenderHeaderCounter === 'function'){
      try { window.hohRenderHeaderCounter(); } catch(_){}
    }
    /* Apply the reward. */
    if(option === 'discount'){
      /* R166: 1000 hearts → 20% off Portrait commission via the
         'Hero20' code (configured 20% off in Shopify admin).
         Historical: was 300h → $20 fixed credit (R140-Z), bumped to
         1000h with the rebrand to "Commission a Portrait" (R148-Z32).
         Current copy + toasts now match what the code actually does. */
      try { markLegshopDiscountBoughtToday(); } catch(_){}
      if(typeof window.hohShowHeartToast === 'function'){
        try { window.hohShowHeartToast('20% discount unlocked', 'A coveted deal. The code is yours to copy.'); } catch(_){}
      }
      /* R140-Z removed: refund + bonus ticket grant. Kept the
         old code as a comment marker so the diff is obvious. */
      /* (legacy R54/R66 refund + bonus-ticket grant removed) */
    } else if(option === 'ticket'){
      /* R148-Z32: Raffle ticket REMOVED. The 500h "Submit Your Idea"
         option now uses this slot. We mark the once-per-day flag
         immediately so the option locks on this device, then flip
         the special to a sub-phase that renders the textarea form.
         The actual Supabase submission happens when the user clicks
         Send Idea inside that form (hohFreshFacesLegshopSubmitIdea).
         If they bail out of the form, the hearts are still spent —
         this is a deliberate buy-in mechanic, not a refundable click. */
      try { markLegshopTicketBoughtToday(); } catch(_){}
      currentSpecial.phase = 'submissionForm';
      currentSpecial.pickedOption = option;
      var ideaContainer = _ffContainer();
      if(ideaContainer) ideaContainer.innerHTML = renderLegendaryShopHtml(currentSpecial);
      if(typeof window.hohShowHeartToast === 'function'){
        try { window.hohShowHeartToast('The parchment is yours', 'Design something the Hall will play through.'); } catch(_){}
      }
      return;
    } else if(option === 'allEvents'){
      /* R140-Z: Start the All Events buff queue — 2 specials, then all
         trivia, then all duels. The picker reads this state at the top
         of pickFreshFacesEvent (consumeActiveBuff) and forces matching
         events on the next ~20 rolls. */
      try { startAllEventsBuff(); } catch(_){}
      try { markLegshopAllEventsBoughtToday(); } catch(_){}
      if(typeof window.hohShowHeartToast === 'function'){
        try { window.hohShowHeartToast('The hall opens its doors', 'Explore to begin the tour.'); } catch(_){}
      }
    } else if(option === 'confetti'){
      /* R150: Confetti — 1h cost, +30h reward. Once per day cap via
         legshopConfettiBoughtToday still applies. _confettiHeartBonus
         drives the heart-fly animation that plays alongside the burst
         (see the _confettiPending consumer below). */
      try { markLegshopConfettiBoughtToday(); } catch(_){}
      if(typeof window.hohAddHeartsToBank === 'function'){
        try { window.hohAddHeartsToBank(30); } catch(_){}
      }
      currentSpecial._confettiPending = true;
      currentSpecial._confettiHeartBonus = 30;
      if(typeof window.hohShowHeartToast === 'function'){
        try { window.hohShowHeartToast('A small celebration', 'The Hall celebrates with you.'); } catch(_){}
      }
    } else if(option === 'merchantsMark'){
      try { window.hohSetMerchHeld(window.hohMerchHeld() + 1); } catch(_){}
      /* R262: auto-open the hero picker to place the Mark right away. The pick
         writes quest_meta.merchantsMark (which grants the double-Explore-hearts
         buff) via hohApplyMerchToHero, and the picker runs its own claim
         ceremony. If it can't open (no owned heroes yet, or the helper is
         missing) fall back to the buy-and-place-later toast. Delayed a beat so
         the shop result re-render settles under the picker overlay. */
      setTimeout(function(){
        var opened = false;
        try {
          if(typeof window.hohBadgeApplyPicker === 'function'){
            opened = window.hohBadgeApplyPicker('merchants-mark', {
              applyFn: function(k, id){ return window.hohApplyMerchToHero(id); }
            });
          }
        } catch(_){}
        if(!opened && typeof window.hohShowHeartToast === 'function'){
          try { window.hohShowHeartToast('Merchant\'s Mark bought', 'Open one of your heroes and place it to claim the buff.'); } catch(_){}
        }
      }, 550);
    }
    /* Mark the shop offered (resets refresh counter + sets weekly
       cooldown) so the same shop won't reappear for at least 7 days. */
    legshopMarkOffered();
    /* Resolve. */
    currentSpecial.phase = 'resolved';
    currentSpecial.pickedOption = option;
    var container = _ffContainer();
    if(container) container.innerHTML = renderLegendaryShopHtml(currentSpecial);
    /* R113-A: Burst hardened. Previous version used double-rAF + a
       single anchor lookup that could fail if the resolved DOM hadn't
       fully laid out yet. New version:
         1. Single rAF — innerHTML commit is synchronous; one frame is
            enough for layout.
         2. Anchor cascades through THREE selectors, ending in
            #hoh-fresh-faces (always present). Heart-fly always has
            an origin.
         3. ALWAYS triggers heart-fly + confetti regardless of anchor
            specificity. Even if the resolved card hasn't painted,
            the section container is.
         4. Backup setTimeout at 80ms in case rAF is throttled (some
            browsers throttle background tabs / reduced-motion).
         5. Console.info on fire so we can see in DevTools that it ran. */
    if(currentSpecial._confettiPending){
      currentSpecial._confettiPending = false;
      var heartBonus = currentSpecial._confettiHeartBonus || 30;
      var burstFired = false;
      var burst = function(){
        if(burstFired) return;
        burstFired = true;
        try { console.info('[LegShop] firing confetti burst, +' + heartBonus + ' hearts'); } catch(_){}
        /* R150-Z45: Confetti FIRES FIRST. The +heartBonus heart-fly
           used to launch on the same frame and got visually lost
           inside the burst — the user never saw the payout. Now the
           confetti blooms, then ~480ms later the hearts fly into the
           wallet as a distinct beat: "the Hall pays you back." */
        try {
          if(typeof window.hohFireConfetti === 'function'){
            window.hohFireConfetti();
            setTimeout(function(){ try { window.hohFireConfetti(); } catch(_){} }, 240);
          } else if(typeof window.confetti === 'function'){
            var isMobileC2 = window.innerWidth < 768;
            var brandColorsC2 = ['#ee99fe', '#affcfe'];
            if(!isMobileC2){
              var dCommon2 = { particleCount: 270, spread: 160, startVelocity: 130, gravity: 0.25, ticks: 330, colors: brandColorsC2, zIndex: 999999 };
              window.confetti(Object.assign({}, dCommon2, { origin: { x: 1.2, y: 0.1 }, angle: 210 }));
              window.confetti(Object.assign({}, dCommon2, { origin: { x: -0.2, y: 0.5 }, angle: 20 }));
            } else {
              window.confetti({ particleCount: 150, spread: 70, startVelocity: 60, gravity: 0.4, ticks: 430, origin: { x: 0.5, y: 1.05 }, angle: 90, colors: brandColorsC2, zIndex: 999999 });
            }
          } else {
            try { spawnConfetti(); } catch(_){}
            setTimeout(function(){ try { spawnConfetti(); } catch(_){} }, 220);
          }
        } catch(_){}
        /* Heart-fly AFTER the confetti — hearts launch from the
           result card and land in the wallet pill. */
        setTimeout(function(){
          try {
            var anchor = document.querySelector('.hoh-fresh-special-legshop .hoh-fresh-legshop-result')
                      || document.querySelector('.hoh-fresh-special-legshop')
                      || _ffContainer();
            if(anchor){
              var rect = anchor.getBoundingClientRect();
              if(rect && rect.width > 0 && rect.height > 0){
                var ghost = document.createElement('div');
                ghost.style.cssText = 'position:fixed;pointer-events:none;left:' + (rect.left + rect.width/2 - 1) + 'px;top:' + (rect.top + rect.height/2 - 1) + 'px;width:2px;height:2px;z-index:1;';
                document.body.appendChild(ghost);
                try { flyDiceTraderHeartsToPill(ghost, Math.min(heartBonus, 14)); } catch(_){}
                setTimeout(function(){ if(ghost.parentNode) ghost.parentNode.removeChild(ghost); }, 1600);
              }
            }
          } catch(_){}
        }, 480);
      };
      /* Primary path: rAF (next paint frame). Backup: 80ms timeout
         in case rAF is throttled or the user is on a low-power tab.
         burstFired guard prevents double-fire. */
      if(typeof requestAnimationFrame === 'function'){
        requestAnimationFrame(burst);
      }
      setTimeout(burst, 80);
    }
    if(typeof startAutoTimer === 'function') setTimeout(startAutoTimer, 0);
  };
  window.hohFreshFacesLegShopCopy = function(){
    try {
      if(navigator.clipboard && navigator.clipboard.writeText){
        navigator.clipboard.writeText(LEGSHOP_DISCOUNT_CODE);
      } else {
        var ta = document.createElement('textarea');
        ta.value = LEGSHOP_DISCOUNT_CODE;
        ta.style.position = 'fixed'; ta.style.left = '-9999px';
        document.body.appendChild(ta); ta.select();
        try { document.execCommand('copy'); } catch(_){}
        document.body.removeChild(ta);
      }
      if(typeof window.hohShowHeartToast === 'function'){
        try { window.hohShowHeartToast('Code copied', LEGSHOP_DISCOUNT_CODE + ' is on your clipboard.'); } catch(_){}
      }
    } catch(_){}
  };

  /* R148-Z32: Submission form handlers. The 500h "Submit Your Idea"
     option flows to the submissionForm phase, where the user types
     their pitch. SubmissionInput enables the Send button when the
     textarea has at least 10 chars. SubmitIdea POSTs to the
     submit_event_idea RPC, then transitions to the resolved phase. */
  /* R150: a basic email shape check — not RFC-perfect, just enough to
     stop blank or obviously-wrong entries on the Draft Your Event form. */
  function _legshopEmailValid(v){
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(v || '').trim());
  }
  window.hohFreshFacesLegshopSubmissionInput = function(){
    var ta = document.getElementById('hoh-legshop-submission-text');
    var counter = document.getElementById('hoh-legshop-submission-count');
    var emailEl = document.getElementById('hoh-legshop-submission-email');
    var btn = document.getElementById('hoh-legshop-submission-send');
    if(!ta) return;
    var val = ta.value || '';
    var len = val.length;
    if(counter) counter.textContent = len + ' / 500';
    /* Send needs a meaningful pitch (10+ chars) AND a valid email so
       Jan can reply to the idea. */
    var emailOk = emailEl ? _legshopEmailValid(emailEl.value) : false;
    if(btn){
      if(len >= 10 && emailOk) btn.removeAttribute('disabled');
      else btn.setAttribute('disabled', 'disabled');
    }
  };
  window.hohFreshFacesLegshopSubmitIdea = function(){
    if(!currentSpecial || currentSpecial.type !== 'legendaryShop') return;
    if(currentSpecial.phase !== 'submissionForm') return;
    var ta = document.getElementById('hoh-legshop-submission-text');
    var emailEl = document.getElementById('hoh-legshop-submission-email');
    var btn = document.getElementById('hoh-legshop-submission-send');
    var text = ta ? (ta.value || '').trim() : '';
    if(text.length < 10) return;
    /* R150: send the email the visitor typed in the form so Jan can
       reply. Fall back to a signed-in email only if the field is
       somehow empty; bail if neither is a valid address. */
    var email = emailEl ? (emailEl.value || '').toLowerCase().trim() : '';
    if(!_legshopEmailValid(email) && typeof window.hohYourHallEmail === 'function'){
      email = (window.hohYourHallEmail() || '').toLowerCase().trim();
    }
    if(!_legshopEmailValid(email)) return;
    if(btn){
      btn.setAttribute('disabled', 'disabled');
      var span = btn.querySelector('span');
      if(span) span.textContent = 'Sending...';
    }
    /* Save the text into state so the resolved screen can show a
       preview of the pitch. */
    currentSpecial.submissionText = text;
    /* Fire the RPC. We don't block on failure — the user has paid
       and we want the resolved screen to show regardless. Server
       errors are logged so Jan can spot issues. */
    try {
      if(typeof supabase !== 'undefined' && supabase && supabase.rpc){
        supabase.rpc('submit_event_idea', {
          p_user_email: email,
          p_submission_text: text
        }).then(function(res){
          if(res && res.error){
            console.warn('[LegShop] submit_event_idea failed (run sql/R148-Z32-event-submissions.sql if the RPC is missing):', res.error);
          }
        }).catch(function(e){
          console.warn('[LegShop] submit_event_idea exception:', e);
        });
      } else {
        console.warn('[LegShop] supabase not available — submission queued locally only:', text);
      }
    } catch(e){ console.warn('[LegShop] submit_event_idea threw:', e); }
    /* Always transition to resolved so the user gets the thank-you
       screen even if the RPC errors. Their hearts were already spent. */
    currentSpecial.phase = 'resolved';
    legshopMarkOffered();
    var c = _ffContainer();
    if(c) c.innerHTML = renderLegendaryShopHtml(currentSpecial);
    if(typeof window.hohShowHeartToast === 'function'){
      try { window.hohShowHeartToast('Draft submitted', 'If Jan brings it to life, the Hall will know your name.'); } catch(_){}
    }
    if(typeof startAutoTimer === 'function') setTimeout(startAutoTimer, 0);
  };

  /* R113-E: Silent +5 hearts on FF CTA click. Once per day per
     surface (capturedByJan + portraitGallery counted separately).
     We DON'T toast or show "+5 hearts" anywhere — the user's wallet
     ticks up quietly. The flag is stored in localStorage keyed by
     YMD + surface, so it resets at local midnight. The link still
     opens the FF waitlist URL in a new tab (target="_blank"),
     so click-grant + navigation both happen. */
  window.hohFreshFacesFfCtaClick = function(surface){
    if(!surface) return;
    try {
      var ymd = (typeof ymdToday === 'function') ? ymdToday() : (new Date()).toISOString().slice(0,10);
      var key = 'hoh-ff-cta-claim-' + surface + '-' + ymd;
      if(localStorage.getItem(key)) return; /* already claimed today */
      localStorage.setItem(key, '1');
      if(typeof window.hohAddHeartsToBank === 'function'){
        try { window.hohAddHeartsToBank(5); } catch(_){}
      }
      if(typeof window.hohRenderHeaderCounter === 'function'){
        try { window.hohRenderHeaderCounter(); } catch(_){}
      }
      /* No toast, no visual. Silent reward. */
    } catch(_){}
  };

  /* Class/Race Quiz event setup. The kind is randomized 50/50:
     class quizzes ask "what's their class?", race quizzes ask
     "what's their race?". Heroes whose race or class is "Other"
     are filtered out so the answer set is never ambiguous (the
     "Other" bucket is anything outside the canonical D&D options
     and would make a fair quiz impossible). */
  var COMMON_CLASSES = ['Fighter','Wizard','Rogue','Cleric','Barbarian','Bard','Druid','Paladin','Ranger','Sorcerer','Warlock','Monk','Artificer','Blood Hunter'];
  var COMMON_RACES = ['Human','Elf','Half-Elf','Dwarf','Dragonborn','Tiefling','Halfling','Gnome','Aasimar','Drow','Goliath','Genasi','Tabaxi','Orc','Half-Orc','Warforged','Changeling','Goblin','Harengon','Fairy'];
  function pickClassQuiz(){
    /* 50/50 between class quiz and race quiz so the variety lands
       organically. Pool selection differs per kind: class quiz
       skips chars whose class is in "Other"; race quiz skips chars
       whose race is in "Other". */
    var kind = Math.random() < 0.5 ? 'class' : 'race';
    var pool;
    if(kind === 'class'){
      pool = _basePool().filter(function(c){
        return c.character_class && COMMON_CLASSES.indexOf(c.character_class) !== -1 && c.race;
      });
    } else {
      pool = _basePool().filter(function(c){
        return c.race && COMMON_RACES.indexOf(c.race) !== -1 && c.character_class;
      });
    }
    if(pool.length < 1) return null;
    /* Quiz size varies between 1 and 3 chars on desktop. Weighted
       slightly toward 3 so the +3-heart jackpot is reachable, but
       single-hero rolls feel like a quick roadside encounter.
       R60: Mobile is forced to size 1 — three quiz cards plus the
       6-option grid plus the result block was eating the whole
       screen, and the +3 / -1 economy works the same with one. */
    var desiredSize;
    if(typeof isMobileViewport === 'function' && isMobileViewport()){
      desiredSize = 1;
    } else {
      var sizeRoll = Math.random();
      if(sizeRoll < 0.35) desiredSize = 1;
      else if(sizeRoll < 0.6) desiredSize = 2;
      else desiredSize = 3;
    }
    var size = Math.min(desiredSize, pool.length);
    var picked = hfShuffle(pool).slice(0, size);
    /* Build option list. Step 1: pull every CORRECT answer from the
       picked characters into a guaranteed-included list — these
       MUST appear so the quiz is winnable. Step 2: fill remaining
       slots with random decoys from the COMMON pool. Step 3: shuffle
       the final list for display order. The previous implementation
       added everything to a single map then sliced, which (in theory)
       could drop a correct answer if Object.keys insertion order
       drifted across browsers. Explicit two-step now removes the
       risk. */
    var POOL = (kind === 'class') ? COMMON_CLASSES : COMMON_RACES;
    var correctAnswers = [];
    var seen = {};
    picked.forEach(function(c){
      var k = (kind === 'class') ? c.character_class : c.race;
      if(k && !seen[k]){ seen[k] = true; correctAnswers.push(k); }
    });
    var decoyPool = POOL.filter(function(opt){ return !seen[opt]; });
    /* Shuffle decoys, take what we need to reach 6 total options. */
    var needed = Math.max(0, 6 - correctAnswers.length);
    var decoys = hfShuffle(decoyPool).slice(0, needed);
    /* Final shuffle so correct answers aren't always at the front. */
    var options = hfShuffle(correctAnswers.concat(decoys));
    return {
      type: 'classQuiz',
      kind: kind,         /* 'class' | 'race' */
      chars: picked,
      options: options,
      answers: {},
      phase: 'idle'
    };
  }

  /* Render a special event into the container. Each branch wires its
     own click handlers via globally exposed hohFreshFacesXxx fns. The
     theme class on the section root drives the per-event accent color
     so subtle theming is consistent across all events. */
  function renderSpecial(container){
    /* A Hall Fight trail owns a delayed impact. Any render replaces the
       surface that impact belonged to, so cancel it before changing DOM --
       including when the next special is not itself a fight. */
    hfTrailClear();
    /* R439: the refresh handler calls this directly, so the gate in
       hohRenderFreshFaces never sees it. Refuse rather than paint over. */
    if(_xintroUp()) return;
    if(!currentSpecial){ container.classList.remove('hoh-fresh-themed'); return; }
    container.style.display = '';
    /* Apply theme class for color accents. The data-event attribute
       feeds CSS selectors like [data-event="epic"] for purple, etc.
       For rarityTribe we additionally set data-tier so :has()-free
       selectors can reach the rarity-specific accent. We strip stale
       themes first so back-to-back events don't stack. */
    container.setAttribute('data-event', currentSpecial.type);
    /* ⛔ R591: ONE BRANCH FOR ALL TEN BATTLES, AND IT IS FIRST.
       It routes on the hfBattle FLAG, not on a list of ten type strings.
       A list would have to be edited again by whoever adds the eleventh
       battle, and this file already carries the scars of partial
       registration (mythicDuelSpecial is named in 5 of its 11 tables,
       crownMatch is missing from UNLIMITED_EVENT_TYPES). A flag set by
       the one builder cannot fall out of step with itself.
       Placed above every type test so no older branch can shadow it. */
    if(currentSpecial.hfBattle){
      container.innerHTML = renderHallFightHtml(currentSpecial);
      hfRememberOrganicFight(currentSpecial);
      var _hfIds = [];
      ['A','B'].forEach(function(s){
        (currentSpecial.teams[s] || []).forEach(function(u){ if(u && u.id) _hfIds.push(u.id); });
      });
      lastRenderedIds = _hfIds;
      recomputePageHearts(0, 'special:' + currentSpecial.type);
      return;
    }
    if(currentSpecial.type === 'rarityTribe' && currentSpecial.tier){
      container.setAttribute('data-tier', currentSpecial.tier);
    } else {
      container.removeAttribute('data-tier');
    }
    container.classList.add('hoh-fresh-themed');
    /* R436 (Jan) · "theres a green border outside of it for no reason".
       THE GREEN WAS NOT THE DUEL'S. .hoh-fresh-themed-border is added by
       renderInto whenever a themed phase carries a borderColor — the
       Greenblood Pact's is #4a6a18 — and renderInto is the ONLY place that
       ever took it off again. renderSpecial returns long before that code,
       so an event that opened straight after a themed phase inherited the
       previous phase's border and glow and wore it for the whole event.
       Jan saw the Greenblood Pact, then a Mythic Duel, and the green
       followed it across. Stripped here, where every special starts. */
    container.classList.remove('hoh-fresh-themed-border');
    container.classList.remove('hoh-fresh-themed-tinted');
    /* R436: the refresh path calls renderSpecial directly, bypassing the
       intro gate. Drop the intro's layout class here too so it can never
       linger on a panel that is now showing an event. */
    container.classList.remove('hoh-fresh-xintro-on');
    container.style.removeProperty('--theme-border-color');
    container.style.removeProperty('--theme-color');
    /* R436 · and the second border is the section's own. A duel whose card
       carries a tier edge does not need the panel to draw one too. Read off
       duelKind, which is the same value renderChampionDuelHtml uses to
       decide whether to put a border on the card at all, so the two can
       never disagree. */
    var _dk = currentSpecial.duelKind || '';
    container.classList.toggle('hoh-fresh-edge-off',
      _dk === 'rare' || _dk === 'epic' || _dk === 'legendary' || _dk === 'mythic' ||
      currentSpecial.type === 'mythicDuelSpecial');
    /* Defensive: every special event re-renders the header skeleton,
       which means the prior auto-bar element is gone and a new one is
       in DOM. Schedule startAutoTimer on a microtask so the freshly-
       injected bar gets the visible class + width animation. Without
       this, paths that bypass hohFreshFacesRefresh (e.g. internal re-
       renders mid-event) would land with a stale opacity:0 bar. The
       eligibility check inside startAutoTimer pauses naturally for
       the coin's flipping animation. */
    setTimeout(function(){
      if(typeof startAutoTimer === 'function') startAutoTimer();
    }, 0);

    if(currentSpecial.type === 'legendary'){
      /* R140-Z: legendary now carries a chars array (2-3 cards). Old
         shape (.char) still tolerated for any in-flight state. */
      var legChars = Array.isArray(currentSpecial.chars) && currentSpecial.chars.length
        ? currentSpecial.chars
        : (currentSpecial.char ? [currentSpecial.char] : []);
      container.innerHTML = renderLegendaryHtml(legChars);
      lastRenderedIds = legChars.map(function(c){ return c && c.id; }).filter(Boolean);
      recomputePageHearts(legChars.length || 1, 'special:legendary');
      try { if(typeof paintQuestProgress === 'function') paintQuestProgress(); } catch(_){}
      return;
    }
    /* ⛔ R554: UNREACHABLE. The ffSpotlight producer in pickSpecial is gone
       (Jan retired the event), so nothing can set this type any more. Kept
       inert rather than deleted so the retirement reads as one decision in one
       place — but do not revive it without fixing the two bugs named at the
       producer: the front face has no backface-visibility, and the stage puts
       a 380px shop card in a 310px box with no --ffcard-k to scale it. */
    if(currentSpecial.type === 'ffSpotlight'){
      /* R322: display-only gallery moment. No recomputePageHearts on
         purpose — there is no heartable rail here, so the page-hearts
         pill stays quiet instead of promising a heart with nowhere to
         land. Hearts live one tap away on the hero's own page. */
      container.innerHTML = renderFfSpotlightHtml(currentSpecial);
      /* R323: bind the real card's flip, tilt and text fitters through the
         shop's own wiring (a no-op if the gilt fallback rendered). */
      try {
        var _ffspTw = container.querySelector('.hoh-ffspot .ffshop-tiltwrap');
        if(_ffspTw && typeof window.hohShopWireCard === 'function') window.hohShopWireCard(_ffspTw);
      } catch(_){}
      lastRenderedIds = currentSpecial.char ? [currentSpecial.char.id] : [];
      try { if(typeof paintQuestProgress === 'function') paintQuestProgress(); } catch(_){}
      return;
    }
    if(currentSpecial.type === 'legendaryTrio'){
      container.innerHTML = renderLegendaryTrioHtml(currentSpecial);
      lastRenderedIds = currentSpecial.chars.map(function(c){ return c.id; });
      recomputePageHearts(currentSpecial.chars.length, 'special:legendaryTrio');
      return;
    }
    if(currentSpecial.type === 'twoLegends'){
      container.innerHTML = renderTwoLegendsHtml(currentSpecial);
      lastRenderedIds = currentSpecial.chars.map(function(c){ return c.id; });
      recomputePageHearts(currentSpecial.chars.length, 'special:twoLegends');
      return;
    }
    if(currentSpecial.type === 'championMeeting'){
      container.innerHTML = renderChampionMeetingHtml(currentSpecial);
      lastRenderedIds = [currentSpecial.char.id];
      recomputePageHearts(1, 'special:championMeeting');
      return;
    }
    if(currentSpecial.type === 'epic'){
      container.innerHTML = renderEpicHtml(currentSpecial.chars);
      lastRenderedIds = currentSpecial.chars.map(function(c){ return c.id; });
      recomputePageHearts(currentSpecial.chars.length, 'special:epic');
      return;
    }
    if(currentSpecial.type === 'rarityTribe'){
      container.innerHTML = renderRarityTribeHtml(currentSpecial);
      lastRenderedIds = currentSpecial.chars.map(function(c){ return c.id; });
      recomputePageHearts(currentSpecial.chars.length, 'special:tribe:' + (currentSpecial.tier || ''));
      return;
    }
    if(currentSpecial.type === 'spectrum'){
      container.innerHTML = renderSpectrumHtml(currentSpecial);
      lastRenderedIds = currentSpecial.chars.map(function(c){ return c.id; });
      recomputePageHearts(currentSpecial.chars.length, 'special:spectrum');
      return;
    }
    if(currentSpecial.type === 'familiar'){
      container.innerHTML = renderFamiliarHtml(currentSpecial);
      lastRenderedIds = currentSpecial.chars.map(function(c){ return c.id; });
      recomputePageHearts(currentSpecial.chars.length, 'special:familiar');
      return;
    }
    if(currentSpecial.type === 'ffPortrait'){
      container.innerHTML = renderFfPortraitHtml(currentSpecial);
      lastRenderedIds = currentSpecial.chars.map(function(c){ return c.id; });
      recomputePageHearts(currentSpecial.chars.length, 'special:ff');
      return;
    }
    if(currentSpecial.type === 'classQuiz'){
      container.innerHTML = renderClassQuizHtml(currentSpecial);
      lastRenderedIds = currentSpecial.chars.map(function(c){ return c.id; });
      /* Quiz uses dropdowns for the answer mechanic, not the heart
         button. Suppress the page-hearts chip with a 0 budget. */
      recomputePageHearts(0, 'special:quiz');
      return;
    }
    if(currentSpecial.type === 'marrowEnd'){
      container.innerHTML = renderMarrowEndHtml();
      lastRenderedIds = [];
      recomputePageHearts(0, 'special:marrow');
      return;
    }
    if(currentSpecial.type === 'heroCircle'){
      container.innerHTML = renderHeroCircleHtml();
      lastRenderedIds = [];
      recomputePageHearts(0, 'special:circle');
      return;
    }
    if(currentSpecial.type === 'secondChances'){
      container.innerHTML = renderSecondChancesHtml(currentSpecial);
      lastRenderedIds = currentSpecial.chars.map(function(c){ return c.id; });
      recomputePageHearts(currentSpecial.chars.length, 'special:secondChances');
      return;
    }
    if(currentSpecial.type === 'banished'){
      container.innerHTML = renderBanishedHtml(currentSpecial);
      lastRenderedIds = currentSpecial.chars.map(function(c){ return c.id; });
      /* All cards start in spent-dismiss state. Apply the class to
         each card after render so the green + restore button is the
         only interactive affordance. */
      var cards = container.querySelectorAll('.hoh-fresh-card');
      cards.forEach(function(card){
        card.classList.add('hoh-fresh-card-spent-dismiss');
        /* Mark consumed so the auto-refresh exhaust check doesn't
           keep them as "available". The user explicitly restores
           via green + and the card un-locks. */
        var id = card.getAttribute('data-id');
        if(id) markConsumed(id);
      });
      recomputePageHearts(0, 'special:banished');
      return;
    }
    if(currentSpecial.type === 'choice'){
      container.innerHTML = renderChoiceHtml(currentSpecial.options);
      lastRenderedIds = [];
      recomputePageHearts(0, 'special:choice');
      return;
    }
    /* R67: Rich Get Richer + Underdog — both use the streaming heart
       mechanic via the shared renderHeartStreamHtml. */
    if(currentSpecial.type === 'richGetRicher'){
      container.innerHTML = renderRichGetRicherHtml(currentSpecial);
      lastRenderedIds = (currentSpecial.chars || []).map(function(c){ return c && c.id; }).filter(Boolean);
      recomputePageHearts(0, 'special:richGetRicher');
      wireHeartStreamTargets(container);
      startStreamWindow(currentSpecial);
      /* R93: paint the embedded wander bar with current state. */
      try { if(typeof paintQuestProgress === 'function') paintQuestProgress(); } catch(_){}
      return;
    }
    if(currentSpecial.type === 'underdog'){
      container.innerHTML = renderUnderdogHtml(currentSpecial);
      lastRenderedIds = currentSpecial.char ? [currentSpecial.char.id] : [];
      recomputePageHearts(0, 'special:underdog');
      wireHeartStreamTargets(container);
      startStreamWindow(currentSpecial);
      try { if(typeof paintQuestProgress === 'function') paintQuestProgress(); } catch(_){}
      return;
    }
    /* R114-C: Feed Your Own — same shape as Underdog with own hero
       host. Stream window + press-and-hold via shared infrastructure. */
    if(currentSpecial.type === 'feedYourOwn'){
      container.innerHTML = renderFeedYourOwnHtml(currentSpecial);
      /* ⛔ R590: THE WINDOW MUST NOT RUN WHILE THEY ARE STILL CHOOSING.
         startStreamWindow arms a windowSec (14s) timer that nulls
         currentSpecial and refreshes the whole section when it expires.
         Arming it on the chooser would give the player fourteen seconds
         to read five heroes AND pour thirty hearts, and a slow reader
         would watch the event vanish under their thumb having spent
         nothing. The clock starts when the hold can start, and not
         before.
         wireHeartStreamTargets is gated for the same reason: there is
         no .hoh-fresh-stream-target in the chooser markup, so wiring it
         there is a no-op today, but leaving it ungated would silently
         arm a hold on whatever a future edit puts in that grid. */
      var _fyChoosing = (currentSpecial.phase === 'choosing');
      lastRenderedIds = _fyChoosing
        ? (currentSpecial.chars || []).map(function(x){ return x && x.id; }).filter(Boolean)
        : (currentSpecial.char ? [currentSpecial.char.id] : []);
      recomputePageHearts(0, 'special:feedYourOwn');
      if(!_fyChoosing){
        wireHeartStreamTargets(container);
        startStreamWindow(currentSpecial);
      }
      try { if(typeof paintQuestProgress === 'function') paintQuestProgress(); } catch(_){}
      return;
    }
    /* R114-D: Promotion event. Pin char hearts at fire time so the
       tier-up detector knows the starting baseline; the listener on
       this event watches the heart count and fires the celebration +
       silent 2x refund when hearts cross the next-tier threshold. */
    if(currentSpecial.type === 'promotion'){
      currentSpecial._startingHearts = currentSpecial.char ? (currentSpecial.char.hearts || 0) : 0;
      container.innerHTML = renderPromotionHtml(currentSpecial);
      lastRenderedIds = currentSpecial.char ? [currentSpecial.char.id] : [];
      recomputePageHearts(0, 'special:promotion');
      wireHeartStreamTargets(container);
      startStreamWindow(currentSpecial);
      try { if(typeof paintQuestProgress === 'function') paintQuestProgress(); } catch(_){}
      return;
    }
    if(currentSpecial.type === 'thievesCoin'){
      container.innerHTML = renderThievesCoinHtml(currentSpecial);
      lastRenderedIds = [];
      recomputePageHearts(0, 'special:coin');
      return;
    }
    /* R116-C / R119-A: Mage's Bazaar — items refresh on each visit
       (no sold-state persistence). The bazaar is now treated as an
       unlimited-event shop — every encounter is a fresh chance to buy
       any of the four buffs. The phase progresses idle → resolved
       (after a buy that has a result screen, e.g. Magical Dice) →
       idle again on close. */
    if(currentSpecial.type === 'mageBazaar'){
      container.innerHTML = renderMageBazaarHtml(currentSpecial);
      lastRenderedIds = [];
      recomputePageHearts(0, 'special:mageBazaar');
      return;
    }
    if(currentSpecial.type === 'diceTrader'){
      container.innerHTML = renderDiceTraderHtml(currentSpecial);
      /* Host card is renderable in the detail modal — track its id
         so a heart given via detail modal still gets the bridge
         (though heart/X buttons on the in-event card itself are
         hidden). */
      lastRenderedIds = currentSpecial.host ? [currentSpecial.host.id] : [];
      recomputePageHearts(0, 'special:diceTrader');
      return;
    }
    if(currentSpecial.type === 'legendaryShop'){
      container.innerHTML = renderLegendaryShopHtml(currentSpecial);
      lastRenderedIds = [];
      recomputePageHearts(0, 'special:legendaryShop');
      /* R267: a Merchant's Mark bought earlier but never placed shouldn't sit
         idle. When the shop appears holding one, pop the hero picker right away
         so it gets applied. Once per shop appearance so it isn't naggy. */
      try {
        if(!currentSpecial._merchPlacerShown && typeof window.hohMerchHeld === 'function' && window.hohMerchHeld() > 0){
          currentSpecial._merchPlacerShown = true;
          setTimeout(function(){ try{ _openMerchPlacer(); }catch(_){} }, 400);
        }
      } catch(_){}
      return;
    }
    if(currentSpecial.type === 'spinWheel'){
      container.innerHTML = renderSpinWheelHtml(currentSpecial);
      lastRenderedIds = [];
      recomputePageHearts(0, 'special:spinWheel');
      return;
    }
    /* R86: New unlimited events — Toll Road, Carnival Strength Test,
       Common Thread. Each renders into the container and registers
       its phase via recomputePageHearts(0, ...) since they're event
       overlays (no per-page heart budget). */
    if(currentSpecial.type === 'tollRoad'){
      container.innerHTML = renderTollRoadHtml(currentSpecial);
      lastRenderedIds = currentSpecial.host && currentSpecial.host.id ? [currentSpecial.host.id] : [];
      recomputePageHearts(0, 'special:tollRoad');
      return;
    }
    if(currentSpecial.type === 'carnival'){
      container.innerHTML = renderCarnivalHtml(currentSpecial);
      lastRenderedIds = currentSpecial.host && currentSpecial.host.id ? [currentSpecial.host.id] : [];
      recomputePageHearts(0, 'special:carnival');
      return;
    }
    /* R140-Z3n: lostAdventurer + starFalls render branches deleted
       (pickers + renderers were dead code, removed in this turn). */
    if(currentSpecial.type === 'commonThread'){
      container.innerHTML = renderCommonThreadHtml(currentSpecial);
      lastRenderedIds = (currentSpecial.chars || []).map(function(c){ return c && c.id; }).filter(Boolean);
      recomputePageHearts(0, 'special:commonThread');
      return;
    }
    /* R150: The Class War — head-to-head class/race recruitment event. */
    if(currentSpecial.type === 'classWar'){
      container.innerHTML = renderClassWarHtml(currentSpecial);
      lastRenderedIds = [];
      recomputePageHearts(0, 'special:classWar');
      return;
    }
    /* R97: Traveling Healer + Bard share a renderer. */
    if(currentSpecial.type === 'travelHealer' || currentSpecial.type === 'travelBard'){
      container.innerHTML = renderTravelEventHtml(currentSpecial);
      lastRenderedIds = currentSpecial.host && currentSpecial.host.id ? [currentSpecial.host.id] : [];
      recomputePageHearts(0, 'special:' + currentSpecial.type);
      return;
    }
    /* R65: Mini Spinner unlimited event. */
    if(currentSpecial.type === 'miniSpinner'){
      container.innerHTML = renderMiniSpinnerHtml(currentSpecial);
      lastRenderedIds = currentSpecial.host ? [currentSpecial.host.id] : [];
      recomputePageHearts(0, 'special:miniSpinner');
      return;
    }
    if(currentSpecial.type === 'pickACard'){
      container.innerHTML = renderPickACardHtml(currentSpecial);
      lastRenderedIds = currentSpecial.host ? [currentSpecial.host.id] : [];
      recomputePageHearts(0, 'special:pickACard');
      return;
    }
    /* R124-B: Five-Card Fortune. */
    if(currentSpecial.type === 'fiveCards'){
      container.innerHTML = renderFiveCardsHtml(currentSpecial);
      lastRenderedIds = currentSpecial.host ? [currentSpecial.host.id] : [];
      recomputePageHearts(0, 'special:fiveCards');
      return;
    }
    /* R55 unlimited free-fire events. R87: guessEHE (Elf or Half-Elf)
       reuses the same renderer + handler with the race factory. */
    if(currentSpecial.type === 'guessFB'
       || currentSpecial.type === 'guessWW' || currentSpecial.type === 'guessRR'
       || currentSpecial.type === 'guessEHE'){
      container.innerHTML = renderClassOrRarityGuessHtml(currentSpecial);
      lastRenderedIds = currentSpecial.char ? [currentSpecial.char.id] : [];
      recomputePageHearts(0, 'special:' + currentSpecial.type);
      return;
    }
    if(currentSpecial.type === 'findCulprit'){
      container.innerHTML = renderFindCulpritHtml(currentSpecial);
      lastRenderedIds = (currentSpecial.chars || []).map(function(c){ return c.id; });
      recomputePageHearts(0, 'special:findCulprit');
      return;
    }
    /* R120-D: 3-character class imposter. */
    if(currentSpecial.type === 'findImposter'){
      container.innerHTML = renderFindImposterHtml(currentSpecial);
      lastRenderedIds = (currentSpecial.chars || []).map(function(c){ return c.id; });
      recomputePageHearts(0, 'special:findImposter');
      return;
    }
    /* R148-Z23: The Echo — pick the most memorable of 3 hearted heroes. */
    if(currentSpecial.type === 'echo'){
      container.innerHTML = renderEchoHtml(currentSpecial);
      lastRenderedIds = (currentSpecial.chars || []).map(function(c){ return c.id; });
      recomputePageHearts(0, 'special:echo');
      return;
    }
    /* R148-Z24: Thieves' Bazaar — themed shop with rogue/charlatan host. */
    if(currentSpecial.type === 'thievesBazaar'){
      container.innerHTML = renderGenericBazaarHtml(currentSpecial);
      lastRenderedIds = currentSpecial.host ? [currentSpecial.host.id] : [];
      recomputePageHearts(0, 'special:thievesBazaar');
      return;
    }
    /* R148-Z24: Sage's Bazaar — themed shop with cleric/druid host. */
    if(currentSpecial.type === 'sageBazaar'){
      container.innerHTML = renderGenericBazaarHtml(currentSpecial);
      lastRenderedIds = currentSpecial.host ? [currentSpecial.host.id] : [];
      recomputePageHearts(0, 'special:sageBazaar');
      return;
    }
    /* R148-Z29: Pay the Guide — bard host offers paid bazaar or
       legendary shop trip. Routes the next roll via queued-special. */
    if(currentSpecial.type === 'payGuide'){
      container.innerHTML = renderGuideHtml(currentSpecial);
      lastRenderedIds = currentSpecial.host ? [currentSpecial.host.id] : [];
      recomputePageHearts(0, 'special:payGuide');
      return;
    }
    /* R124-I: Familiar Crowd. */
    if(currentSpecial.type === 'familiarCrowd'){
      container.innerHTML = renderFamiliarCrowdHtml(currentSpecial);
      lastRenderedIds = (currentSpecial.chars || []).map(function(c){ return c.id; });
      recomputePageHearts(0, 'special:familiarCrowd');
      return;
    }
    /* R120-F: Bustling Streets — page hearts = remaining event budget.
       R124-G: Bypass recomputePageHearts (which would compute
       max = floor(visibleCount/5) and produce "1 heart to give" for a
       budget of 5). Instead set pageHearts directly so the chip
       reflects the literal budget number, and call paintPageHearts. */
    if(currentSpecial.type === 'bustlingStreets'){
      container.innerHTML = renderBustlingStreetsHtml(currentSpecial);
      lastRenderedIds = (currentSpecial.chars || []).map(function(c){ return c && c.id; }).filter(Boolean);
      var bsBudget = currentSpecial.budget || BUSTLING_BUDGET;
      var bsSpent = currentSpecial.spent || 0;
      pageHearts.key = 'special:bustlingStreets';
      pageHearts.max = bsBudget;
      pageHearts.spent = bsSpent;
      pageHearts.budget = bsBudget;
      try { paintPageHearts(); } catch(_){}
      return;
    }
    /* R150: The Conveyer Belt — one-row sibling of Bustling Streets.
       Its own renderer, so renderBustlingStreetsHtml is never touched. */
    if(currentSpecial.type === 'conveyer'){
      container.innerHTML = renderConveyerHtml(currentSpecial);
      lastRenderedIds = (currentSpecial.chars || []).map(function(c){ return c && c.id; }).filter(Boolean);
      var cvBudget = currentSpecial.budget || CONVEYER_BUDGET;
      pageHearts.key = 'conveyer:' + (currentSpecial.variant || 'belt');
      pageHearts.max = cvBudget;
      pageHearts.spent = currentSpecial.spent || 0;
      pageHearts.budget = cvBudget;
      try { paintPageHearts(); } catch(_){}
      return;
    }
    if(currentSpecial.type === 'coinBet'){
      container.innerHTML = renderCoinBetHtml(currentSpecial);
      lastRenderedIds = currentSpecial.host ? [currentSpecial.host.id] : [];
      recomputePageHearts(0, 'special:coinBet');
      return;
    }
    if(currentSpecial.type === 'popularity'){
      container.innerHTML = renderPopularityHtml(currentSpecial);
      lastRenderedIds = (currentSpecial.chars || []).map(function(c){ return c.id; });
      recomputePageHearts(0, 'special:popularity');
      return;
    }
  }

  /* Shared event header. Pass {centered: true} for events with no
     card grid (sneaky/marrow/heroCircle/idle quiz/secondChances) so
     the title and subhead stack centered instead of side-by-side.
     The page-hearts chip span is ALWAYS included — paintPageHearts
     hides it (display:none) when the page budget is 0, so non-heart
     events naturally suppress it. This keeps Travelers' Camp / Spectrum
     / Familiar / Rarity tribes / Banished / Second Chances showing
     the per-page heart counter without each render path having to
     opt in. */
  /* R140-F: Shared event-card image helper. The main-grid card builder
     (hoh-card-render.js) honors the saved per-character vertical crop
     offset via `object-position: 50% Y%`, but most Fresh Faces event
     renderers were inlining their own <img> tags without that style —
     so a hero whose owner reframed their portrait still showed cropped
     in events like Find the Imposter, Find the Culprit, Familiar Crowd
     and the host cards. The user reported heads being cut off in
     Explore the Hall even after fixing the main grid; this helper is
     the single source of truth for event imgs.
     opts: { eager, fpHigh, lazy, draggable, alt, hostKindFallback }
     Returns the full <img ...> tag OR the letter-fallback div when the
     character has no image url. */
  function _eventCharImg(c, opts){
    c = c || {};
    opts = opts || {};
    var src = c.image_thumb_url || c.image_url || '';
    var firstLetter = esc((c.name || (opts.hostKindFallback || '?')).charAt(0));
    if(!src){
      return '<div class="hoh-fresh-letter">' + firstLetter + '</div>';
    }
    var loading = opts.eager ? 'eager' : 'lazy';
    if(opts.lazy === false) loading = 'eager';
    var fp = opts.fpHigh ? ' fetchpriority="high"' : '';
    var draggable = (opts.draggable === false) ? ' draggable="false" oncontextmenu="return false"' : '';
    var alt = (opts.alt != null) ? esc(String(opts.alt)) : '';
    var off = 50;
    try {
      if(typeof window.hohGetImageOffsetY === 'function'){
        var v = window.hohGetImageOffsetY(c.id, 'primary');
        if(v != null) off = v | 0;
      }
    } catch(_){}
    var cropStyle = (typeof window.hohImgStyle === 'function') ? window.hohImgStyle(c.id, 'primary') : ('object-position:50% ' + off + '%;');
    return '<img src="' + esc(src) + '" alt="' + alt + '" loading="' + loading + '"' + fp +
      ' decoding="async" crossorigin="anonymous"' + draggable +
      ' data-letter="' + firstLetter + '" data-hero-id="' + esc(c.id || '') + '"' +
      ' style="' + cropStyle + '"' +
      ' onerror="if(window.hohImgFallback)hohImgFallback(this);">';
  }
  function eventHeaderHtml(eyebrow, sub, opts){
    var centered = !!(opts && opts.centered);
    /* R93: opts.hideRefresh suppresses the manual refresh icon while
       keeping the auto-bar. Used for resolved-phase toll/duel where
       re-rolling would just charge a heart for no reason — only the
       auto-bar should advance. */
    var hideRefresh = !!(opts && opts.hideRefresh);
    var cls = 'hoh-fresh-header hoh-fresh-special-header' + (centered ? ' hoh-fresh-special-header-centered' : '');
    var refreshBtn = hideRefresh ? '' :
      '<button type="button" class="hoh-fresh-refresh" id="hoh-fresh-refresh" onclick="hohFreshFacesRefresh()" aria-label="Show new heroes" title="Show new heroes">' +
        '<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 12a9 9 0 1 1-3.5-7.1"/><polyline points="21 4 21 10 15 10"/></svg>' +
      '</button>';
    /* R140-U: Markup restructure for the auto-bar + refresh-icon
       integration the user requested. Previously refresh sat INSIDE
       hoh-fresh-header-controls (with the page-hearts pill), which
       pinned both to the bottom-right corner of the centered header
       and made them collide with the auto-bar. Now:
         - hoh-fresh-header-controls contains ONLY the page-hearts pill
         - refresh button is a separate sibling rendered NEXT TO the
           auto-bar, positioned via CSS to overlap the bar's right edge
           so the gold thread visibly "feeds" into the icon.
       The auto-bar fill grows toward the icon; on auto-complete the
       fill reaches the icon's left edge and the icon plays its spin
       animation. Manual click triggers the same spin via JS in
       hohFreshFacesRefresh. */
    /* R140-Z3m: Wrap eyebrow + sub in .hoh-fresh-header-text so this
       header has the same DOM shape as the themed-phase header built
       in renderInto. Without the wrapper, eyebrow + sub were bare
       flex children competing with the pill + auto-bar for layout,
       which the user reported as "squished" on tribe + familiar
       events. Matching structure gives matching spacing for free
       once the margin-bottom fix lands in CSS. */
    return '<div class="' + cls + '">' +
      '<div class="hoh-fresh-header-text">' +
        '<span class="hoh-fresh-eyebrow">' + eyebrow + '</span>' +
        '<span class="hoh-fresh-sub">' + sub + '</span>' +
      '</div>' +
      '<div class="hoh-fresh-header-controls">' +
        '<span class="hoh-fresh-page-hearts" id="hoh-fresh-page-hearts" aria-live="polite"></span>' +
      '</div>' +
      '<span class="hoh-fresh-auto-bar" id="hoh-fresh-auto-bar" aria-hidden="true"><span class="hoh-fresh-auto-bar-fill" id="hoh-fresh-auto-bar-fill"></span></span>' +
      refreshBtn +
    '</div>';
  }
  /* The bottom Wander Further button + flavor copy was removed by
     request — only the small refresh icon in the header remains as
     the manual refresh affordance. The auto-bar fills toward that
     icon and triggers it when full. We keep this as a no-op so any
     callers we might have missed don't throw. */
  function wanderFooterHtml(){ return ''; }

  /* Mobile-aware slicing for character grids. Crowded Town can render
     20 commons; on a 380px-wide viewport that\'s endless scroll. Cap
     event grids at 4 cards on mobile so the section stays a glanceable
     unit. Desktop keeps the full count. The breakpoint matches the
     mobile media query (max-width:768px) used elsewhere. */
  var MOBILE_EVENT_CARD_CAP = 4;
  function isMobileViewport(){
    try { return window.matchMedia && window.matchMedia('(max-width: 768px)').matches; }
    catch(_){ return false; }
  }
  function cardsForViewport(chars){
    if(!isMobileViewport()) return chars;
    return (chars || []).slice(0, MOBILE_EVENT_CARD_CAP);
  }

  /* Rarity Tribe — All Common / Uncommon / Rare / Epic gatherings. */
  function renderRarityTribeHtml(state){
    var cards = cardsForViewport(state.chars).map(cardHtml).join('');
    return '<div class="hoh-fresh-special hoh-fresh-special-tribe hoh-fresh-tribe-' + state.tier + '">' +
      eventHeaderHtml(state.eyebrow, state.sub) +
      '<div class="hoh-fresh-track-wrap"><div class="hoh-fresh-track">' + cards + '</div></div>' +
      /* R136-B: Explore the Hall progress bar. Tribe phases let the
         user spend hearts on real characters, so daily progress should
         be visible just like Familiar Faces / Captured by Jan. Applies
         to ALL tribes (common, uncommon, rare, epic, plus the 7 R136-B
         themed phases — twilight, healers, vanguard, mages, beastfolk,
         quietest, brightest) since they share this renderer. */
      _streamWanderBarHtml() +
      wanderFooterHtml('Heart any that catch your eye, or keep exploring.') +
    '</div>';
  }

  /* Spectrum — one card per tier in ascending order. CSS gives each
     card a tier-flavored entry animation so the procession reads as
     "quiet to renowned" left-to-right. */
  function renderSpectrumHtml(state){
    var cards = cardsForViewport(state.chars).map(function(c){
      var html = cardHtml(c);
      /* Tag each card with its tier class for the animation stagger. */
      var tier = (typeof window.hohGetRarity === 'function')
        ? window.hohGetRarity(c.hearts || 0).tier : 'common';
      html = html.replace('class="hoh-fresh-card', 'class="hoh-fresh-card hoh-fresh-spectrum-card hoh-fresh-spectrum-' + tier);
      return html;
    }).join('');
    return '<div class="hoh-fresh-special hoh-fresh-special-spectrum">' +
      eventHeaderHtml('A Spectrum of Heroes', 'Five travelers, one of each renown. From the quiet to the legendary.') +
      '<div class="hoh-fresh-track-wrap"><div class="hoh-fresh-track">' + cards + '</div></div>' +
      /* R140-Z3n: Explore the Hall progress bar belongs on every
         Type 1 (Heart Roll) surface so daily progress stays visible
         everywhere the user spends hearts. */
      _streamWanderBarHtml() +
    '</div>';
  }

  /* Familiar Faces — hearted heroes. The X dismiss button is hidden
     for this event via the data-event attribute on the section so the
     user can\'t accidentally exile a favorite. */
  function renderFamiliarHtml(state){
    var cards = cardsForViewport(state.chars).map(cardHtml).join('');
    return '<div class="hoh-fresh-special hoh-fresh-special-familiar">' +
      eventHeaderHtml('Familiar Faces', 'Heroes you\'ve already lifted. Spend more, if you like.') +
      '<div class="hoh-fresh-track-wrap"><div class="hoh-fresh-track">' + cards + '</div></div>' +
      /* R119-E: Wander bar so users see daily progress everywhere they
         spend hearts. Same pattern reused across stream events. */
      _streamWanderBarHtml() +
      wanderFooterHtml('Pour more love into the ones you remember, or keep exploring.') +
    '</div>';
  }

  /* Banished Heroes — every dismissed hero shown locked-in from
     the start with a green + restore button on each. The user
     unlocks them one by one. Differs from Second Chances (which
     starts unlocked and accepts hearts) by being a bulk un-banish
     action only. */
  function renderBanishedHtml(state){
    var cards = cardsForViewport(state.chars).map(function(c){
      var html = cardHtml(c);
      html = html.replace('class="hoh-fresh-card', 'class="hoh-fresh-card hoh-fresh-card-banished');
      return html;
    }).join('');
    return '<div class="hoh-fresh-special hoh-fresh-special-banished">' +
      eventHeaderHtml('The Banished', 'The heroes you sent away. Tap the green plus to call them back to the Hall.', { centered: true }) +
      '<div class="hoh-fresh-track-wrap"><div class="hoh-fresh-track">' + cards + '</div></div>' +
      /* R140-Z3n: Explore the Hall bar on every Type 1 surface. */
      _streamWanderBarHtml() +
    '</div>';
  }

  /* Second Chances — dismissed heroes shown again. Hearting one
     restores them to the Hall pool (removed from the dismissed list).
     Each card carries a data-second-chance attribute so the heart
     handler can detect this event and call hohRestoreCharacter. */
  function renderSecondChancesHtml(state){
    var cards = cardsForViewport(state.chars).map(function(c){
      var html = cardHtml(c);
      html = html.replace('class="hoh-fresh-card', 'class="hoh-fresh-card hoh-fresh-card-second-chance');
      return html;
    }).join('');
    return '<div class="hoh-fresh-special hoh-fresh-special-second-chances">' +
      eventHeaderHtml('Second Chances', 'Heroes you waved off before. Heart one and they walk back into the Hall.') +
      '<div class="hoh-fresh-track-wrap"><div class="hoh-fresh-track">' + cards + '</div></div>' +
    '</div>';
  }

  /* R113-B/D/E: Captured by Jan — pink FF brand theme. CTA moves
     BELOW the cards so users scan the gallery first, then take the
     CTA. CTA click grants +5 hearts (silent) once per day via
     hohFreshFacesFfCtaClick. The hoh-fresh-special-ff-pink class
     tags this for the pink palette (R113-D) — distinct from the
     blue-themed Portrait Gallery (themed phase). */
  function renderFfPortraitHtml(state){
    var cards = cardsForViewport(state.chars).map(cardHtml).join('');
    return '<div class="hoh-fresh-special hoh-fresh-special-ff hoh-fresh-special-ff-pink">' +
      eventHeaderHtml('Captured by Jan', 'Heroes whose adventures were immortalized in a custom portrait. Their stories don\'t fade.', { centered: true }) +
      '<div class="hoh-fresh-track-wrap"><div class="hoh-fresh-track">' + cards + '</div></div>' +
      '<div class="hoh-fresh-ff-cta-row hoh-fresh-ff-cta-row-below">' +
        '<a class="hoh-fresh-ff-cta hoh-fresh-ff-cta-pink" href="https://framedfantasy.com/products/fantasy-character-commission" target="_blank" rel="noopener" onclick="if(window.hohFreshFacesFfCtaClick)hohFreshFacesFfCtaClick(\'capturedByJan\');">' +
          '<span class="hoh-fresh-ff-cta-flavor">Want to see your hero come to life?</span>' +
          '<span class="hoh-fresh-ff-cta-link">Join the waitlist</span>' +
        '</a>' +
      '</div>' +
      /* R119-E: Wander bar at the bottom so daily progress stays
         visible while the user lifts FF portraits. */
      _streamWanderBarHtml() +
    '</div>';
  }

  /* Class Quiz — 3 cards, race+class hidden, multiple choice per card,
     correct on all = +3 hearts. The cards reuse cardHtml but with a
     post-render mask that hides the meta and replaces the heart
     button with the dropdown. We render a custom mini-card here so
     the quiz state stays self-contained. */
  function renderClassQuizHtml(state){
    var phase = state.phase || 'idle';
    var kind = state.kind || 'class';   /* 'class' | 'race' */
    var kindLabel = (kind === 'class') ? 'class' : 'race';
    var promptVerb = (kind === 'class') ? 'Guess the class' : 'Guess the race';
    /* R86: Defensive cap on render. The picker already targets size 1
       on mobile, but if the viewport flipped between pick + render
       (orientation change, resize) state.chars may still hold 3 from
       the desktop pick. Slice at render time so race quizzes always
       show 1 card on mobile (the user's explicit ask). */
    if(typeof isMobileViewport === 'function' && isMobileViewport()){
      if(state.chars && state.chars.length > 1){
        state.chars = state.chars.slice(0, 1);
      }
    }
    var cards = state.chars.map(function(c){
      /* R140-F: Offset-aware helper. */
      var media = _eventCharImg(c, { eager: true, draggable: false });
      var guessed = state.answers && state.answers[c.id];
      var truth = (kind === 'class') ? c.character_class : c.race;
      var correct = (phase === 'resolved') && guessed === truth;
      var revealClass = (phase === 'resolved')
        ? (correct ? ' hoh-fresh-quiz-correct' : ' hoh-fresh-quiz-wrong')
        : '';
      var optionsHtml;
      if(phase === 'resolved'){
        optionsHtml = '<div class="hoh-fresh-quiz-reveal">' +
          '<span class="hoh-fresh-quiz-truth">' + esc(c.race || '') + ' ' + esc(c.character_class || '') + '</span>' +
          (guessed ? '<span class="hoh-fresh-quiz-guess">You guessed: ' + esc(guessed) + '</span>' : '') +
        '</div>';
      } else {
        /* Button grid replaces the native select dropdown. Each
           option is a tappable pill; selected option highlights.
           Mobile-friendly without the OS picker, and visually
           consistent with the rest of the event UI. */
        optionsHtml = '<div class="hoh-fresh-quiz-options" role="radiogroup" aria-label="' + esc(promptVerb) + '">' +
          state.options.map(function(opt){
            var sel = (guessed === opt) ? ' hoh-fresh-quiz-option-selected' : '';
            return '<button type="button" class="hoh-fresh-quiz-option' + sel + '"' +
              ' data-quiz-id="' + esc(c.id) + '"' +
              ' data-quiz-value="' + esc(opt) + '"' +
              ' onclick="hohFreshFacesQuizGuess(\'' + esc(c.id) + '\',\'' + esc(opt) + '\')">' +
              esc(opt) +
            '</button>';
          }).join('') +
        '</div>';
      }
      /* On the reveal screen, make the whole card clickable so the
         user can open the character's detail modal — they often want
         to learn more about a hero they just met (or got wrong).
         While the quiz is still being answered, the card stays
         non-clickable so a click on the card surface doesn't
         accidentally fire the detail modal instead of an option pill. */
      var revealClickable = (phase === 'resolved')
        ? ' role="button" tabindex="0" onclick="if(window.hohOpenDetail)hohOpenDetail(\'' + esc(c.id) + '\')" onkeydown="if((event.key===\'Enter\'||event.key===\' \')&&window.hohOpenDetail){event.preventDefault();hohOpenDetail(\'' + esc(c.id) + '\');}" title="Open ' + esc(c.name || '') + '"'
        : '';
      var revealCursorCls = (phase === 'resolved') ? ' hoh-fresh-quiz-card-clickable' : '';
      return '<div class="hoh-fresh-quiz-card' + revealClass + revealCursorCls + '" data-quiz-char-id="' + esc(c.id) + '"' + revealClickable + '>' +
        '<div class="hoh-fresh-quiz-media">' + media + '</div>' +
        '<div class="hoh-fresh-quiz-name">' + esc(c.name || 'Unknown') + '</div>' +
        '<div class="hoh-fresh-quiz-meta">' + (phase === 'resolved' ? '' : '<em>' + esc(kindLabel) + ' hidden</em>') + '</div>' +
        optionsHtml +
      '</div>';
    }).join('');

    var allAnswered = state.chars.every(function(c){ return state.answers && state.answers[c.id]; });
    var actionRow = '';
    if(phase === 'idle' || phase === 'answering'){
      var totalChars = state.chars.length;
      var rewardLabel = totalChars === 1
        ? 'Lock in answer'
        : 'Lock in answers';
      actionRow =
        '<div class="hoh-fresh-quiz-action">' +
          '<button type="button" class="hoh-fresh-coin-toss hoh-fresh-quiz-submit" ' +
            (allAnswered ? '' : 'disabled ') +
            'onclick="hohFreshFacesQuizSubmit()">' +
            '<span>' + rewardLabel + '</span>' +
          '</button>' +
        '</div>';
    } else {
      /* Kind-aware win count. Race quizzes compare to c.race, class
         quizzes compare to c.character_class. Previously this always
         compared to character_class which made the race quiz headline
         report 0/N even when the user got every answer right (the
         actual heart-reward submit handler was already kind-aware,
         only this display line was wrong). */
      var correctCount = state.chars.filter(function(c){
        var truth = (kind === 'class') ? c.character_class : c.race;
        return state.answers[c.id] === truth;
      }).length;
      var total = state.chars.length;
      var won = correctCount === total;
      var headlineWin = won
        ? (total === 1 ? 'A clean read.' : 'Every answer right.')
        : (correctCount > 0 ? 'A near miss.' : 'The spirits keep their secrets.');
      var subline;
      if(correctCount > 0){
        /* R102: per-correct reward bumped 3 → 5 to honor the 5-heart floor. */
        var heartsAwarded = correctCount * 5;
        subline = 'Guessed ' + correctCount + ' / ' + total + '.';
      } else {
        subline = 'Guessed 0 / ' + total + '. Try again on the next traveler.';
      }
      actionRow =
        '<div class="hoh-fresh-quiz-result ' + (won ? 'hoh-fresh-quiz-result-win' : 'hoh-fresh-quiz-result-lose') + '">' +
          '<strong>' + headlineWin + '</strong>' +
          '<span class="hoh-fresh-quiz-result-line">' + subline + '</span>' +
          '<button type="button" class="hoh-fresh-coin-toss hoh-fresh-quiz-next" onclick="hohFreshFacesRefresh({fromInteractive:true})">' +
            '<span>Keep exploring</span>' +
          '</button>' +
        '</div>';
    }

    var headerEyebrow = (kind === 'race') ? 'A Stranger\'s Lineage' : 'A Roadside Blessing';
    var headerSub = (kind === 'race')
      ? 'They will not say where they\'re from. Read them. Pick their lineage, right answers pay hearts.'
      : 'Three travelers will not say their craft. Guess what they carry, class for class. Right answers pay hearts.';
    return '<div class="hoh-fresh-special hoh-fresh-special-quiz">' +
      eventHeaderHtml(headerEyebrow, headerSub, { centered: true }) +
      '<div class="hoh-fresh-quiz-grid">' + cards + '</div>' +
      actionRow +
    '</div>';
  }

  /* Bounty to Marrow's End — a quest hand-off card. Accept grants
     +5 hearts (once per day, gated by hoh-marrow-accepted-{ymd} so
     refreshing for free hearts isn\'t a thing) and opens the quest
     page in a new tab. The +5 icon on the button telegraphs the
     reward up front so users understand acceptance has value beyond
     the link click. If the user already accepted today, the button
     drops the icon and just opens the page. */
  function marrowAcceptedToday(){
    try {
      var ymd = new Date().toISOString().slice(0,10);
      return localStorage.getItem('hoh-marrow-accepted-' + ymd) === '1';
    } catch(_){ return false; }
  }
  function markMarrowAcceptedToday(){
    try {
      var ymd = new Date().toISOString().slice(0,10);
      localStorage.setItem('hoh-marrow-accepted-' + ymd, '1');
    } catch(_){}
  }
  /* R120-B: Marrow's End weekly cap on accept. ISO-week key (year +
     week number) — the user explicitly asked that once they take the
     +5 hearts, the bounty stays gone for the rest of the week, not
     just the day. The daily flag above is kept so the +5 bonus
     doesn't double-pay if the user somehow encounters Marrow twice
     in a day before the weekly key is set; the weekly flag is the
     picker-level filter. */
  function _isoWeekKey(){
    var d = new Date();
    var target = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
    var dayNum = (target.getUTCDay() + 6) % 7; /* Mon=0 ... Sun=6 */
    target.setUTCDate(target.getUTCDate() - dayNum + 3); /* Thursday of this ISO week */
    var firstThursday = new Date(Date.UTC(target.getUTCFullYear(), 0, 4));
    var diff = (target - firstThursday) / 86400000;
    var week = 1 + Math.round((diff - 3 + ((firstThursday.getUTCDay() + 6) % 7)) / 7);
    return target.getUTCFullYear() + '-W' + String(week).padStart(2, '0');
  }
  function marrowAcceptedThisWeek(){
    try {
      return localStorage.getItem('hoh-marrow-accepted-week-' + _isoWeekKey()) === '1';
    } catch(_){ return false; }
  }
  function markMarrowAcceptedThisWeek(){
    try {
      localStorage.setItem('hoh-marrow-accepted-week-' + _isoWeekKey(), '1');
    } catch(_){}
  }
  function renderMarrowEndHtml(){
    var alreadyAccepted = marrowAcceptedToday();
    var rewardChip = alreadyAccepted
      ? ''
      : '<span class="hoh-fresh-marrow-reward" aria-hidden="true">' +
          '<span class="hoh-fresh-marrow-reward-heart">&#9829;&#xFE0E;</span>' +
          '<span class="hoh-fresh-marrow-reward-amt">+5</span>' +
        '</span>';
    return '<div class="hoh-fresh-special hoh-fresh-special-marrow">' +
      eventHeaderHtml('A Bounty from Marrow\'s End', 'A scroll arrives by raven. The seal is broken, the writing rushed. They need a hero and only you can decide who.', { centered: true }) +
      '<div class="hoh-fresh-marrow-stage">' +
        '<img class="hoh-fresh-marrow-image" src="https://cdn.shopify.com/s/files/1/0884/2389/1261/files/Hollow_Stump_Updated.webp?v=1775691803" alt="A hollow stump in a dark wood" loading="lazy" decoding="async">' +
        '<div class="hoh-fresh-marrow-scroll">' +
          '<div class="hoh-fresh-marrow-flavor">' +
            '"The blight is at our gate. Send someone who will fight for the dawn. Your choice walks our roads tonight."' +
          '</div>' +
          '<div class="hoh-fresh-marrow-actions">' +
            '<button type="button" class="hoh-fresh-wander-btn hoh-fresh-marrow-accept" onclick="hohFreshFacesAcceptMarrow(this)">' +
              '<span class="hoh-fresh-marrow-accept-label">Accept the Bounty</span>' +
              rewardChip +
            '</button>' +
            '<button type="button" class="hoh-fresh-marrow-decline" onclick="hohFreshFacesRefresh({fromInteractive:true})">No thanks, keep exploring</button>' +
          '</div>' +
        '</div>' +
      '</div>' +
    '</div>';
  }
  /* Accept handler — grants 5 hearts, flies them to the pill, marks
     the daily flag, then opens the quest page in a new tab. We open
     the tab AFTER the heart-fly kicks off so the user sees the
     reward animation start before the new tab steals focus.
     window.open with noopener is the safe pattern here. */
  window.hohFreshFacesAcceptMarrow = function(btn){
    var bonus = 5;
    var alreadyAccepted = marrowAcceptedToday();
    if(!alreadyAccepted){
      markMarrowAcceptedToday();
      /* R120-B: Also mark the weekly flag so the picker hides the
         bounty for the rest of the ISO week. The daily flag stays as
         the primary +5-bonus gate (so a same-day re-show can't double
         pay), and the weekly flag controls re-appearance. */
      markMarrowAcceptedThisWeek();
      if(typeof window.hohAddHeartsToBank === 'function'){
        try { window.hohAddHeartsToBank(bonus); } catch(_){}
      }
      if(typeof window.hohShowHeartToast === 'function'){
        try { window.hohShowHeartToast('The villagers thank you', 'Walk well.'); } catch(_){}
      }
      try {
        if(btn) flyDiceTraderHeartsToPill(btn, bonus);
      } catch(_){}
    }
    /* Delay tab open slightly so the heart-fly has a beat to start
       before focus shifts. 220ms is enough to see the first sprite
       leave the button. */
    setTimeout(function(){
      try { window.open('https://framedfantasy.com/pages/dnd-character-quest', '_blank', 'noopener,noreferrer'); } catch(_){}
    }, 220);
    /* R62: Refresh the rail to a fresh page so when the user comes
       back from the bounty tab they don\'t see the same Marrow card
       still up. Wait for the heart-fly + tab open to finish first. */
    setTimeout(function(){
      currentSpecial = null;
      if(typeof window.hohFreshFacesRefresh === 'function'){
        try { window.hohFreshFacesRefresh({ fromInteractive: true }); } catch(_){}
      }
    }, 1100);
  };

  /* Hero\'s Circle — a doorway to the existing tinder modal. Discrete,
     not a quest (the quest moved to fresh-faces). */
  function renderHeroCircleHtml(){
    /* R71/R74: Decorative scrolling banner of hero portraits — two
       rows moving in opposite directions so the panel doesn\'t feel
       thin and empty. Pointer-events:none so the user can\'t click
       any portrait (they\'re purely visual flavor).
       R74: The banner needs the doubled track to be WIDER than any
       reasonable viewport, otherwise the seamless-loop trick (translate
       0 → -50%) reveals empty space at the end of the track. We aim
       for ~24 unique chars per row, doubled = 48 icons. At 64px + 14px
       gap that\'s ~3.7k pixels per row — wider than any normal screen.
       If the loaded pool has fewer than 24 chars, we REPEAT the
       shuffled sequence enough times to reach the 24-icon target
       BEFORE doubling, so even tiny halls (8 chars) fill the row. */
    var allChars = (typeof window.hohGetAllCharacters === 'function') ? window.hohGetAllCharacters() : [];
    var poolForBanner = allChars.filter(function(c){ return c && c.id && (c.image_thumb_url || c.image_url); });
    /* R74: 40 entries per copy. With 64px icons + 14px gap that\'s
       ~3.1k pixels per copy. The seamless-loop math requires
       copyWidth > viewportWidth — otherwise at max scroll (translate
       -50%) the right edge of the doubled track sits inside the
       viewport and a gap appears (the bug from the screenshot
       showing a blank space at the bottom-right). 40 comfortably
       covers standard desktops up to ~3000px wide. The icons are
       reused thumbnails (no extra bandwidth), just extra DOM nodes
       — 80 spans per row × 2 rows = 160 nodes, negligible. */
    var BANNER_TARGET = 40;
    function _bannerRow(seedShift, dirCls){
      var n = poolForBanner.length;
      if(!n) return '';
      /* Shuffle a copy so each row picks a different sequence. */
      var shuf = hfShuffle(poolForBanner);
      var rowChars = [];
      while(rowChars.length < BANNER_TARGET){
        /* Take the next chunk of shuf; if pool is small we cycle through
           it multiple times. Re-shuffle each cycle so repeats aren\'t
           visually adjacent. */
        var chunk = (rowChars.length === 0) ? shuf : hfShuffle(shuf);
        for(var i = 0; i < chunk.length && rowChars.length < BANNER_TARGET; i++){
          rowChars.push(chunk[i]);
        }
      }
      /* Duplicate the sequence so the animation translate(-50%) wraps
         seamlessly without a visible jump. The wrap point is exactly
         at the half-track, where the second copy lines up identically
         with the first copy\'s starting position. */
      var doubled = rowChars.concat(rowChars);
      /* R140-Z: Lazy-load the duplicated banner icons. The banner is
         a decorative scrolling rail (aria-hidden=true) — many icons
         scroll off-screen at any given moment, and the duplicated set
         is purely visual padding for the keyframe loop. Lazy halves
         the initial image bytes for this event. */
      var iconsHtml = doubled.map(function(c){
        var img = c.image_thumb_url || c.image_url || '';
        return '<span class="hoh-fresh-circle-banner-icon" aria-hidden="true">' +
          '<img src="' + esc(img) + '" alt="" loading="lazy" decoding="async" crossorigin="anonymous" onerror="this.style.display=\'none\';">' +
          '</span>';
      }).join('');
      return '<div class="hoh-fresh-circle-banner-row ' + dirCls + '" aria-hidden="true">' +
        '<div class="hoh-fresh-circle-banner-track">' + iconsHtml + '</div>' +
      '</div>';
    }
    var bannerHtml = poolForBanner.length
      ? '<div class="hoh-fresh-circle-banner" aria-hidden="true">' +
          _bannerRow(0, 'hoh-fresh-circle-banner-row-rtl') +
          _bannerRow(1, 'hoh-fresh-circle-banner-row-ltr') +
        '</div>'
      : '';
    return '<div class="hoh-fresh-special hoh-fresh-special-circle">' +
      eventHeaderHtml('The Hero\'s Circle', 'You find a hidden ring of stones. Step inside and the heroes come to you, one by one.', { centered: true }) +
      bannerHtml +
      '<div class="hoh-fresh-circle-stage">' +
        '<button type="button" class="hoh-fresh-wander-btn hoh-fresh-circle-enter" onclick="hohFreshFacesEnterCircle()">' +
          '<span>Enter the Circle</span>' +
        '</button>' +
        '<button type="button" class="hoh-fresh-marrow-decline" onclick="hohFreshFacesRefresh({fromInteractive:true})">Walk past</button>' +
      '</div>' +
    '</div>';
  }

  /* Wired window functions for new events. */
  window.hohFreshFacesQuizGuess = function(charId, val){
    if(!currentSpecial || currentSpecial.type !== 'classQuiz') return;
    if(currentSpecial.phase === 'resolved') return;
    currentSpecial.answers = currentSpecial.answers || {};
    if(val) currentSpecial.answers[charId] = val;
    else delete currentSpecial.answers[charId];
    currentSpecial.phase = 'answering';
    /* Re-render so the submit button enables when all 3 are answered. */
    var container = _ffContainer();
    if(container) container.innerHTML = renderClassQuizHtml(currentSpecial);
  };

  window.hohFreshFacesQuizSubmit = function(){
    if(!currentSpecial || currentSpecial.type !== 'classQuiz') return;
    var allAnswered = currentSpecial.chars.every(function(c){ return currentSpecial.answers && currentSpecial.answers[c.id]; });
    if(!allAnswered) return;
    currentSpecial.phase = 'resolved';
    /* Per-correct reward. The right answer depends on the quiz kind
       (class vs race). Each correct grants +5 hearts (R102: bumped 3→5
       to honor the 5-heart minimum floor) with a fly-to-pill animation;
       all correct triggers confetti. */
    var kind = currentSpecial.kind || 'class';
    var correct = currentSpecial.chars.filter(function(c){
      var truth = (kind === 'class') ? c.character_class : c.race;
      return currentSpecial.answers[c.id] === truth;
    });
    var correctCount = correct.length;
    var total = currentSpecial.chars.length;
    var allRight = correctCount === total;
    var HEARTS_PER_CORRECT = 5;
    var heartsAwarded = correctCount * HEARTS_PER_CORRECT;

    var container = _ffContainer();
    if(container) container.innerHTML = renderClassQuizHtml(currentSpecial);

    if(correctCount > 0){
      /* Single bank credit so the wallet pill updates once, not nine
         times in rapid succession. */
      if(typeof window.hohAddHeartsToBank === 'function'){
        try { window.hohAddHeartsToBank(heartsAwarded); } catch(_){}
      }
      /* Toast: simple +N message reflecting the *3 multiplier. */
      if(typeof window.hohShowHeartToast === 'function'){
        var toastTitle = 'Well guessed';
        var toastSub = allRight ? 'A flawless read.' : 'You guessed right on ' + correctCount + ' of ' + total + '.';
        try { window.hohShowHeartToast(toastTitle, toastSub); } catch(_){}
      }
      /* Pill pulse so the new bank lands visually. */
      try {
        var pill = document.getElementById('hoh-tk-header') || document.getElementById('hoh-heart-remaining');
        if(pill){
          pill.classList.remove('hoh-tk-heart-pulse');
          void pill.offsetWidth;
          pill.classList.add('hoh-tk-heart-pulse');
          setTimeout(function(){ pill.classList.remove('hoh-tk-heart-pulse'); }, 900);
        }
      } catch(_){}
      /* Per-correct heart pop: each correct card bursts with its +3
         hearts where it sits (Jan, 2026-09-27: nothing flies to the
         wallet pill any more). Cards staggered ~220ms apart so the pops
         read one card at a time. */
      setTimeout(function(){
        correct.forEach(function(c, ci){
          setTimeout(function(){ flyQuizHeartFromCard(c.id, HEARTS_PER_CORRECT); }, ci * 220);
        });
      }, 240);
    }
    /* R58: -1 heart per WRONG answer with the same heart-loss visual
       the other guess events use. Quiz lives in the gambling pool
       now (R58); penalty mirrors the "easy" guess events (binary
       class + culprit). Single batched fly so 3 wrong reads as one
       "-3 ♥" hit instead of three rapid stacked toasts. */
    var wrongCount = total - correctCount;
    var wrongChars = currentSpecial.chars.filter(function(c){
      var truth = (kind === 'class') ? c.character_class : c.race;
      return currentSpecial.answers[c.id] !== truth;
    });
    if(wrongCount > 0){
      try { loseHeartFromGuess(wrongCount === 1 ? 'Wrong answer.' : wrongCount + ' wrong answers.', wrongCount); } catch(_){}
      /* R86: literal "-1" floater on each wrong card so the loss reads
         on the card itself, not just from the pill flash. The card
         already gets a red pulse via .hoh-fresh-quiz-wrong CSS — this
         adds the explicit "-1" to make the cost legible. */
      setTimeout(function(){
        wrongChars.forEach(function(c, i){
          setTimeout(function(){
            try {
              var card = document.querySelector('.hoh-fresh-quiz-card[data-quiz-char-id="' + String(c.id).replace(/"/g, '\\"') + '"]');
              if(card) spawnMinusHeartOnCard(card);
            } catch(_){}
          }, i * 160);
        });
      }, 80);
    }
    /* Confetti only on a clean sweep. */
    if(allRight){
      setTimeout(function(){ spawnConfetti(); }, 200);
    }
    /* Keep the auto-timer running through the result so the user
       can read the message and the bar still ticks down toward the
       next refresh. They can also tap Wander Further to skip. */
  };

  /* A correct quiz card's hearts pop on its portrait, 60px below the
     card's top where the old flock started. Jan, 2026-09-27: they used
     to fly into the wallet pill (see hohHeartBurst in hoh-wallet.js). */
  function flyQuizHeartFromCard(charId, n){
    var card = document.querySelector('.hoh-fresh-quiz-card[data-quiz-char-id="' + cssEscape(charId) + '"]');
    if(!card){
      /* Fall back to the first quiz card if specific lookup misses. */
      card = document.querySelector('.hoh-fresh-quiz-card');
    }
    if(!card || typeof window.hohHeartBurst !== 'function') return;
    var r = card.getBoundingClientRect();
    if(!r.width) return;
    window.hohHeartBurst({ x: r.left + r.width / 2, y: r.top + Math.min(60, r.height / 2) }, { n: n || 1 });
  }

  /* Lightweight CSS confetti — spawn a swarm of small colored
     particles over the fresh faces panel that fall and fade. Pure
     DOM, no canvas, so it composes naturally with the rest of the
     page. */
  function spawnConfetti(){
    /* R124-H: Hardened against the case where #hoh-fresh-faces is
       missing OR has zero dimensions (e.g. while another renderer is
       in mid-swap). We always have a viable spawn region — the
       viewport itself — and only USE the section's coordinates when
       they're valid. Document.body is the parent, so confetti is
       guaranteed to render even when the section container is gone. */
    if(!document.body) return;
    var vw = window.innerWidth || document.documentElement.clientWidth || 800;
    var vh = window.innerHeight || document.documentElement.clientHeight || 600;
    var anchorTop = vh * 0.35;
    var anchorLeftMin = vw * 0.15;
    var anchorLeftMax = vw * 0.85;
    /* If the section IS on screen with a real bounding box, narrow
       the spawn region to its visible area so the burst feels anchored
       to the event. Otherwise fall back to viewport-center spawn. */
    try {
      var c = _ffContainer();
      if(c){
        var rect = c.getBoundingClientRect();
        if(rect && rect.width > 0 && rect.height > 0){
          anchorTop = Math.max(40, Math.min(rect.top + 60, vh * 0.4));
          var lMin = Math.max(40, rect.left + rect.width * 0.1);
          var lMax = Math.min(vw - 40, rect.left + rect.width * 0.9);
          if(lMax > lMin){
            anchorLeftMin = lMin;
            anchorLeftMax = lMax;
          }
        }
      }
    } catch(_){}
    var COLORS = ['#f2d98e', '#c4a8f8', '#7ed49a', '#ff9bd2', '#7ec0ff', '#ffb347', '#ff7a7a', '#ffd966'];
    var count = 60;
    for(var i = 0; i < count; i++){
      var p = document.createElement('div');
      p.className = 'hoh-fresh-confetti';
      var color = COLORS[i % COLORS.length];
      p.style.background = color;
      var startX = anchorLeftMin + Math.random() * (anchorLeftMax - anchorLeftMin);
      var startY = anchorTop + Math.random() * 30;
      p.style.left = startX + 'px';
      p.style.top = startY + 'px';
      /* Wider lateral spread + longer fall so the burst feels generous
         instead of a small puff. */
      var dx = (Math.random() * 360 - 180);
      var dy = 280 + Math.random() * 240;
      var rot = Math.random() * 720 - 360;
      p.style.setProperty('--cx', dx + 'px');
      p.style.setProperty('--cy', dy + 'px');
      p.style.setProperty('--rot', rot + 'deg');
      /* Random per-particle size so the burst reads as a real confetti
         shower rather than a uniform sprite grid. */
      var size = 8 + Math.floor(Math.random() * 8);
      p.style.width = size + 'px';
      p.style.height = size + 'px';
      p.style.animationDelay = (Math.random() * 0.22) + 's';
      document.body.appendChild(p);
      (function(el){
        setTimeout(function(){ if(el.parentNode) el.parentNode.removeChild(el); }, 2100);
      })(p);
    }
  }

  window.hohFreshFacesEnterCircle = function(){
    if(!currentSpecial || currentSpecial.type !== 'heroCircle') return;
    currentSpecial = null;
    /* R58: Daily-capped — mark as used the moment they enter so
       subsequent refreshes today don't keep offering the Circle. */
    try { markHeroCircleUsedToday(); } catch(_){}
    if(typeof window.hohOpenTrial === 'function'){
      window.hohOpenTrial();
    }
    /* When the user closes the circle modal, the modal observer
       resumes the timer. We pre-render the rail to the next phase so
       the background isn't stuck on the empty doorway card. */
    setPhase(0);
    cachedWanderingPool = null;
    cachedWanderingPhase = null;
    renderedSnapshotIds = null;
    snapshotPhaseKey = null;
    window.hohRenderFreshFaces();
  };

  /* R140-Z: Renders 2-3 legendaries (per the new picker). The chars
     argument is always an array now; the legacy single-char shape is
     normalized to a one-element list at the call site. The bottom
     "Explore the Hall" wander bar mirrors the trio + two-legends
     pattern so the user sees their session progress in the same
     spot across legendary showcases. */
  function renderLegendaryHtml(chars){
    var list = Array.isArray(chars) ? chars.filter(function(c){ return c && c.id; }) : (chars && chars.id ? [chars] : []);
    if(!list.length){
      return '<div class="hoh-fresh-special hoh-fresh-special-legendary"></div>';
    }
    var n = list.length;
    var stageClass = (n >= 3) ? 'hoh-fresh-special-stage-trio'
                  : (n === 2 ? 'hoh-fresh-special-stage-trio hoh-fresh-special-stage-pair'
                  : 'hoh-fresh-special-stage-solo');
    var sub = (n === 1)
      ? 'You meet a legend. The Hall hushes when they walk past.'
      : (n === 2
        ? 'Two legends, shoulder to shoulder. The torches gutter when they pass.'
        : 'Three legends in one corridor. The Hall holds its breath.');
    var cards = list.map(function(c){
      if(n >= 2){
        var html = cardHtml(c);
        return html.replace('class="hoh-fresh-card', 'class="hoh-fresh-card hoh-fresh-card-trio');
      }
      return cardHtml(c);
    }).join('');
    return '<div class="hoh-fresh-special hoh-fresh-special-legendary">' +
      '<div class="hoh-fresh-header hoh-fresh-special-header">' +
        '<span class="hoh-fresh-eyebrow">Legendary Encounter</span>' +
        '<span class="hoh-fresh-sub">' + sub + '</span>' +
        '<button type="button" class="hoh-fresh-refresh" onclick="hohFreshFacesRefresh()" aria-label="Show new heroes" title="Show new heroes">' +
          '<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 12a9 9 0 1 1-3.5-7.1"/><polyline points="21 4 21 10 15 10"/></svg>' +
        '</button>' +
        '<span class="hoh-fresh-auto-bar" id="hoh-fresh-auto-bar" aria-hidden="true"><span class="hoh-fresh-auto-bar-fill" id="hoh-fresh-auto-bar-fill"></span></span>' +
      '</div>' +
      '<div class="hoh-fresh-special-stage ' + stageClass + '">' +
        cards +
      '</div>' +
      _streamWanderBarHtml() +
    '</div>';
  }

  /* Legendary Trio — three legendary heroes side by side. Even
     more cinematic than the single Legendary Encounter; the trio
     gets the warmest gold backdrop and a slower entry stagger so
     each name lands deliberately. */
  function renderLegendaryTrioHtml(state){
    var cards = state.chars.map(function(c){
      var html = cardHtml(c);
      html = html.replace('class="hoh-fresh-card', 'class="hoh-fresh-card hoh-fresh-card-trio');
      return html;
    }).join('');
    /* R136-trio-bar: wander bar attached at the bottom, mirroring the
       Two Legends pattern (R122-F). Three Legends is a pure showcase
       with no betting / no reward path of its own — the bar reads as
       a session-summary footer rather than header decoration, and it
       advances 10% faster than the previous tuning per R136-speedup. */
    return '<div class="hoh-fresh-special hoh-fresh-special-legendary-trio">' +
      eventHeaderHtml('Three Legends', 'A line of three names the Hall already knows. Tonight, they all stand here.') +
      '<div class="hoh-fresh-special-stage hoh-fresh-special-stage-trio">' +
        cards +
      '</div>' +
      _streamWanderBarHtml() +
    '</div>';
  }

  /* R54: Two Legends — pure showcase, two legendary cards side by
     side. No betting, no cost. The stagger entry is half the
     trio\'s timing so it reads as a smaller moment. Reuses the
     trio stage CSS but with 2 columns. */
  function renderTwoLegendsHtml(state){
    var cards = state.chars.map(function(c){
      var html = cardHtml(c);
      html = html.replace('class="hoh-fresh-card', 'class="hoh-fresh-card hoh-fresh-card-trio');
      return html;
    }).join('');
    /* R120-C: page-hearts pill centered via scoped CSS.
       R122-F: Wander bar moved BELOW the cards (was above the stage)
       so the bar reads as session-summary footer rather than header
       decoration. Two Legends has no reward path of its own (pure
       showcase), so no confetti is fired anywhere in this render or
       its dispatch — just the centered pill + bar at the bottom. */
    return '<div class="hoh-fresh-special hoh-fresh-special-legendary-trio hoh-fresh-special-two-legends">' +
      eventHeaderHtml('Two Legends', 'Two names the Hall remembers, side by side. The torches gutter when they walk past.', { centered: true }) +
      '<div class="hoh-fresh-special-stage hoh-fresh-special-stage-trio">' +
        cards +
      '</div>' +
      _streamWanderBarHtml() +
    '</div>';
  }

  /* R54: Champion Meeting — single showcase of the current featured
     winner (last month\'s spotlight). Reuses the legendary single
     stage. Centered header, "champion" copy. */
  function renderChampionMeetingHtml(state){
    var c = state.char || {};
    return '<div class="hoh-fresh-special hoh-fresh-special-legendary hoh-fresh-special-champion">' +
      eventHeaderHtml('Meeting the Champion', 'Last month\'s spotlight winner. The Hall still talks about them.', { centered: true }) +
      '<div class="hoh-fresh-special-stage hoh-fresh-special-stage-solo">' +
        cardHtml(c) +
      '</div>' +
      /* R140-Z3n: Explore the Hall bar on every Type 1 surface. */
      _streamWanderBarHtml() +
    '</div>';
  }

  /* ==================== R322: FF SPOTLIGHT ==================== */
  /* One commissioned FramedFantasy original on a gallery stage: spotlight
     cone, rising gold dust, a breathing gilt frame, the hero's legend from
     card_meta (R236 wrote all 88 — zero new AI). Display-only: no stakes,
     no payouts, nothing to race, safe to rotate away from. CSS is injected
     from JS (deploy-safe over any live stylesheet vintage) and carries the
     :empty override the theme's base.css demands — Dawn's reset hides
     empty decorative nodes otherwise (the July 15 door-scene lesson). */
  function _ensureFfSpotCss(){
    if(document.getElementById('hoh-ffspot-css')) return;
    var st = document.createElement('style'); st.id = 'hoh-ffspot-css';
    st.textContent =
      /* Dawn's div:empty/span:empty reset must never eat the cone or dust */
      '[class^=hoh-ffspot]:empty,[class*=" hoh-ffspot"]:empty{display:block !important}' +
      '.hoh-ffspot{position:relative;padding:30px 20px 28px;text-align:center;overflow:hidden}' +
      /* the light itself — an ellipse falling from above, dissolved at the edges */
      '.hoh-ffspot-cone{position:absolute;left:50%;top:-46px;width:360px;height:440px;transform:translateX(-50%);' +
        'background:radial-gradient(ellipse 46% 58% at 50% 0%,rgba(232,198,116,.17),rgba(232,198,116,.05) 55%,transparent 76%);' +
        'pointer-events:none;opacity:0;animation:hohFfspotFade .9s ease .05s forwards}' +
      '.hoh-ffspot-stage{position:relative;width:min(225px,62vw);margin:14px auto 0}' +
      /* R323: when the REAL trading card is on stage it keeps its native
         380px face; the shop's own ≤430px rule transform-scales it and the
         .ffshop-cardbox height rule absorbs the scale. The card and its
         collector number ride above the dust. */
      '.hoh-ffspot-stage-card{width:min(310px,100%)}' +
      /* v22 (Jan): the spotlight card FLIPS — and the first turn each day
         pays +20 hearts. The stage is a coin worth flipping. */
      '.hoh-ffspot-stage{perspective:900px}' +
      '.hoh-ffspot-flipwrap{transition:transform .6s cubic-bezier(.3,.05,.3,1);transform-style:preserve-3d;position:relative}' +
      '.hoh-ffspot-stage.flipped .hoh-ffspot-flipwrap{transform:rotateY(180deg)}' +
      '.hoh-ffspot-back{position:absolute;inset:0;transform:rotateY(180deg);backface-visibility:hidden;border-radius:14px;'+
        'background:linear-gradient(160deg,#171226,#0d0a14);border:1px solid #c9a84c66;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:10px}' +
      '.hoh-ffspot-back b{font-family:Cinzel,serif;font-size:12px;letter-spacing:.2em;color:#e8c674}' +
      '.hoh-ffspot-back i{font-style:italic;font-size:12.5px;color:#8fd8a0}' +
      '.hoh-ffspot-stage-card .ffshop-cardbox{position:relative;z-index:1;opacity:0;transform:translateY(12px) scale(.97);animation:hohFfspotEnter .75s cubic-bezier(.2,.7,.25,1.06) .18s forwards}' +
      '.hoh-ffspot-stage-card .ffshop-collno{position:relative;z-index:1;margin-top:10px;animation:hohFfspotUp .55s ease .55s both}' +
      '.hoh-ffspot-stage-card .hoh-ffspot-dust{z-index:0}' +
      /* the frame is a button: the whole portrait opens the hero's page */
      '.hoh-ffspot-frame{position:relative;display:block;width:100%;padding:7px;border:0;cursor:pointer;border-radius:15px;' +
        'background:linear-gradient(160deg,#e8c674,#6e5620 42%,#c9a84c 70%,#f2d98e);' +
        'box-shadow:0 20px 52px -14px rgba(0,0,0,.7),0 0 42px -10px rgba(232,198,116,.38);' +
        'opacity:0;transform:scale(.94) translateY(12px);' +
        'animation:hohFfspotEnter .75s cubic-bezier(.2,.7,.25,1.06) .18s forwards,hohFfspotBreath 6.5s ease-in-out 1.2s infinite;' +
        'transition:box-shadow .3s ease}' +
      '.hoh-ffspot-frame:hover{box-shadow:0 22px 56px -14px rgba(0,0,0,.7),0 0 58px -8px rgba(232,198,116,.55)}' +
      '.hoh-ffspot-frame img{display:block;width:100%;aspect-ratio:4/5;object-fit:cover;border-radius:10px;background:#15121f}' +
      '.hoh-ffspot-letter{display:flex;align-items:center;justify-content:center;width:100%;aspect-ratio:4/5;border-radius:10px;' +
        'background:#1a1524;font-family:\'Cinzel\',serif;font-size:56px;color:#4a4258}' +
      /* inner hairline, one breath inside the gold */
      '.hoh-ffspot-hairline{content:"";position:absolute;inset:11px;border:1px solid rgba(242,217,142,.5);border-radius:9px;pointer-events:none}' +
      /* rising gold dust */
      '.hoh-ffspot-dust{position:absolute;bottom:10%;left:50%;width:4px;height:4px;border-radius:50%;background:#f2d98e;' +
        'box-shadow:0 0 9px 1px rgba(242,217,142,.75);opacity:0;pointer-events:none;' +
        'transform:translateX(var(--dx,0));animation:hohFfspotDust var(--dd,5.4s) linear var(--dl,0s) infinite}' +
      '@keyframes hohFfspotDust{0%{opacity:0;transform:translate(var(--dx,0),0) scale(.55)}' +
        '12%{opacity:.9}58%{opacity:.45}100%{opacity:0;transform:translate(calc(var(--dx,0) + var(--dxe,14px)),-200px) scale(1.05)}}' +
      /* the name wears an occasional sheen, never a constant shimmer */
      '.hoh-ffspot-name{font-family:\'Cinzel\',serif;font-size:21px;letter-spacing:.06em;margin-top:22px;' +
        'background:linear-gradient(100deg,#e8c674 30%,#fff3cf 46%,#e8c674 62%);background-size:230% 100%;background-position:120% 0;' +
        '-webkit-background-clip:text;background-clip:text;color:transparent;' +
        'animation:hohFfspotUp .55s ease .4s both,hohFfspotSheen 7s ease-in-out 1.6s infinite}' +
      '@keyframes hohFfspotSheen{0%,74%{background-position:120% 0}90%,100%{background-position:-45% 0}}' +
      '.hoh-ffspot-sub{font-size:12.5px;letter-spacing:.05em;color:#8a8090;margin-top:7px;animation:hohFfspotUp .55s ease .5s both}' +
      '.hoh-ffspot-hearts{color:#e8c674;margin-left:6px}' +
      '.hoh-ffspot-brand{position:relative;font-family:\'Cinzel\',serif;font-size:9.5px;letter-spacing:.22em;text-transform:uppercase;' +
        'color:#9a8f7a;margin-top:16px;padding-top:13px;animation:hohFfspotUp .55s ease .62s both}' +
      '.hoh-ffspot-brand:before{content:"";position:absolute;top:0;left:50%;transform:translateX(-50%);width:58px;height:1px;' +
        'background:linear-gradient(90deg,transparent,#c9a84c,transparent)}' +
      '.hoh-ffspot-legend{font-family:\'Cormorant Garamond\',Georgia,serif;font-style:italic;font-size:15.5px;line-height:1.66;' +
        'color:#cfc8ba;max-width:430px;margin:15px auto 0;padding:0 8px;animation:hohFfspotUp .55s ease .74s both}' +
      '.hoh-ffspot-row{display:flex;gap:10px;justify-content:center;flex-wrap:wrap;margin-top:24px;animation:hohFfspotUp .55s ease .88s both}' +
      '.hoh-ffspot-btn{min-height:46px;padding:12px 24px;border-radius:9px;border:0;cursor:pointer;' +
        'font-family:\'Cinzel\',serif;font-size:11px;font-weight:700;letter-spacing:.13em;text-transform:uppercase;' +
        'background:linear-gradient(120deg,#f2d98e,#c8a45e);color:#1a1108;transition:filter .2s ease}' +
      '.hoh-ffspot-btn:hover{filter:brightness(1.08)}' +
      '.hoh-ffspot-ghost{background:none;border:1px solid #3a3049;color:#cfc8ba;transition:border-color .2s ease,color .2s ease}' +
      '.hoh-ffspot-ghost:hover{border-color:#c9a84c;color:#e8c674;filter:none}' +
      '@keyframes hohFfspotEnter{to{opacity:1;transform:none}}' +
      '@keyframes hohFfspotFade{to{opacity:1}}' +
      '@keyframes hohFfspotUp{from{opacity:0;transform:translateY(9px)}to{opacity:1;transform:none}}' +
      '@keyframes hohFfspotBreath{0%,100%{box-shadow:0 20px 52px -14px rgba(0,0,0,.7),0 0 42px -10px rgba(232,198,116,.38)}' +
        '50%{box-shadow:0 20px 52px -14px rgba(0,0,0,.7),0 0 62px -6px rgba(232,198,116,.55)}}' +
      '@media(max-width:560px){' +
        '.hoh-ffspot{padding:24px 14px 24px}' +
        '.hoh-ffspot-cone{width:270px;height:360px}' +
        '.hoh-ffspot-stage{width:min(246px,76vw)}' +
        '.hoh-ffspot-stage-card{width:100%}' +
        /* the ≤430px card is transform-scaled against 100vw while this stage
           is a touch narrower — a little extra air keeps the collector
           number clear of the card's bottom edge. */
        '.hoh-ffspot-stage-card .ffshop-collno{margin-top:16px}' +
        '.hoh-ffspot-name{font-size:19px}' +
        '.hoh-ffspot-legend{font-size:14.5px}' +
        '.hoh-ffspot-dust-xtra{display:none !important}' +
        '.hoh-ffspot-row{flex-direction:column;align-items:stretch;padding:0 6px}' +
      '}' +
      '@media(prefers-reduced-motion:reduce){' +
        '.hoh-ffspot-cone,.hoh-ffspot-frame,.hoh-ffspot-name,.hoh-ffspot-sub,.hoh-ffspot-brand,.hoh-ffspot-legend,.hoh-ffspot-row,.hoh-ffspot-stage-card .ffshop-cardbox,.hoh-ffspot-stage-card .ffshop-collno{animation:none !important;opacity:1 !important;transform:none !important;background-position:120% 0}' +
        '.hoh-ffspot-dust{display:none !important}' +
      '}';
    document.head.appendChild(st);
  }
  function _ffspotTrim(t, n){
    t = String(t || '').replace(/\s+/g, ' ').trim();
    if(t.length <= n) return t;
    var cut = t.slice(0, n);
    var dot = cut.lastIndexOf('. ');
    if(dot > n * 0.5) return cut.slice(0, dot + 1);
    var sp = cut.lastIndexOf(' ');
    return (sp > 0 ? cut.slice(0, sp) : cut) + '…';
  }
  function renderFfSpotlightHtml(state){
    _ensureFfSpotCss();
    var c = (state && state.char) || {};
    var img = c.image_thumb_url || c.image_url || '';
    var pos = '50% 50%';
    try { if(typeof window.hohImgObjPos === 'function') pos = window.hohImgObjPos(c.id, 'primary'); } catch(_){}
    var cropStyle = (typeof window.hohImgStyle === 'function') ? window.hohImgStyle(c.id, 'primary') : ('object-position:' + pos + ';');
    /* the legend ladder: the card text Claude wrote (R236) → the hero's own
       story → the campaign line → one honest stock line. Never empty. */
    var legend = '';
    try {
      if(c.card_meta && c.card_meta.legend) legend = _ffspotTrim(c.card_meta.legend, 230);
      else if(c.story) legend = _ffspotTrim(c.story, 190);
      else if(c.campaign) legend = 'Of the ' + c.campaign + ' table. The Hall keeps their page.';
    } catch(_){}
    if(!legend) legend = 'A hero of the Hall, painted true, hanging where every road can find them.';
    var rc = [c.race, String(c.character_class || '').split(' ')[0]].filter(Boolean).join(' · ');
    var hearts = c.hearts || 0;
    /* gold dust — cosmetic spans, randomized per mount; the last three sit
       out on phones (the ≤12 mobile particle law). */
    var dust = '';
    for(var di = 0; di < 9; di++){
      dust += '<span class="hoh-ffspot-dust' + (di > 5 ? ' hoh-ffspot-dust-xtra' : '') + '" aria-hidden="true" style="' +
        '--dx:' + Math.round(Math.random() * 220 - 110) + 'px;' +
        '--dxe:' + Math.round(Math.random() * 44 - 22) + 'px;' +
        '--dl:' + (Math.random() * 4.2).toFixed(2) + 's;' +
        '--dd:' + (4.6 + Math.random() * 2.6).toFixed(2) + 's;"></span>';
    }
    /* R323: mount the REAL trading card — the exact face, flip, tilt, holo
       and fitters the Shop inspector renders, via the shop's own exported
       builder, so the two surfaces can never drift. The gilt-frame fallback
       below only exists for the impossible day the shop script is absent.
       With the real card up, the name, type line and legend live ON the
       card, so the stage stays quiet around it. */
    var realCard = (typeof window.hohShopCardHtml === 'function') ? window.hohShopCardHtml(c.id) : '';
    return '<div class="hoh-fresh-special hoh-fresh-special-ffspot">' +
      eventHeaderHtml('FramedFantasy Spotlight', 'The lanterns turn. One of the Hall’s painted originals takes the floor.', { centered: true }) +
      '<div class="hoh-ffspot">' +
        '<span class="hoh-ffspot-cone" aria-hidden="true"></span>' +
        '<div class="hoh-ffspot-stage' + (realCard ? ' hoh-ffspot-stage-card' : '') + '">' + dust +
          (realCard ||
          ('<button type="button" class="hoh-ffspot-frame" onclick="hohFfSpotlightOpen()" aria-label="Open ' + esc(c.name || 'this hero') + '’s page">' +
            (img ? '<img src="' + esc(img) + '" alt="' + esc(c.name || '') + '" decoding="async" style="' + esc(cropStyle) + '">'
                 : '<span class="hoh-ffspot-letter">' + esc(String(c.name || '?').charAt(0)) + '</span>') +
            '<span class="hoh-ffspot-hairline" aria-hidden="true"></span>' +
          '</button>')) +
        '</div>' +
        (realCard ? '' :
          ('<div class="hoh-ffspot-name">' + esc(c.name || 'A painted hero') + '</div>' +
           '<div class="hoh-ffspot-sub">' + (rc ? esc(rc) + ' &middot; ' : '') + '<span class="hoh-ffspot-hearts">&#9829; ' + hearts + '</span></div>')) +
        '<div class="hoh-ffspot-brand">A FramedFantasy original &middot; painted by Jan</div>' +
        (realCard ? '' : '<p class="hoh-ffspot-legend">&ldquo;' + esc(legend) + '&rdquo;</p>') +
        '<div class="hoh-ffspot-row">' +
          '<button type="button" class="hoh-ffspot-btn" onclick="hohFfSpotlightOpen()">See their page</button>' +
          '<button type="button" class="hoh-ffspot-btn hoh-ffspot-ghost" onclick="hohFfSpotlightWander()">Wander on</button>' +
        '</div>' +
      '</div>' +
      _streamWanderBarHtml() +
    '</div>';
  }
  window.hohFfSpotlightOpen = function(){
    try {
      if(!(currentSpecial && currentSpecial.type === 'ffSpotlight' && currentSpecial.char)) return;
      /* v22 (Jan): the FIRST tap TURNS the card — and the first turn of the
         day pays +20 hearts. The second tap opens the hero. */
      var stage = document.querySelector('.hoh-ffspot-stage');
      if(stage && !stage.classList.contains('flipped')){
        var wrap = stage.querySelector('.hoh-ffspot-flipwrap');
        if(!wrap){
          wrap = document.createElement('div');
          wrap.className = 'hoh-ffspot-flipwrap';
          while(stage.firstChild) wrap.appendChild(stage.firstChild);
          var back = document.createElement('div');
          back.className = 'hoh-ffspot-back';
          back.innerHTML = '<b>FRAMEDFANTASY</b><i id="hohFfspotPay">&nbsp;</i><span style="font-size:11px;color:#8a8296">tap again to meet them</span>';
          wrap.appendChild(back);
          stage.appendChild(wrap);
        }
        stage.classList.add('flipped');
        var payKey = 'hoh-ffspot-pay-v1';
        var today = new Date().toISOString().slice(0,10);
        var paid = '';
        try { paid = localStorage.getItem(payKey) || ''; } catch(_){}
        if(paid !== today){
          try { localStorage.setItem(payKey, today); } catch(_){}
          var gave = false;
          try {
            if(window.HOHContractData && HOHContractData.bankApply){ HOHContractData.bankApply(20, 'earn_ffspot_flip'); gave = true; }
          } catch(_){}
          var payEl = document.getElementById('hohFfspotPay');
          if(payEl) payEl.textContent = gave ? 'a small gift, for the turning' : 'a fine card, turned';
        } else {
          var payEl2 = document.getElementById('hohFfspotPay');
          if(payEl2) payEl2.textContent = 'already paid today — a fine card still';
        }
        return;
      }
      if(typeof window.hohOpenDetail === 'function') window.hohOpenDetail(currentSpecial.char.id);
    } catch(_){}
  };
  window.hohFfSpotlightWander = function(){
    try { window.hohFreshFacesRefresh({ fromInteractive: true }); } catch(_){}
  };

  function renderEpicHtml(chars){
    /* R140-Z3j: Switched to eventHeaderHtml with centered:true so the
       title/sub center properly and the page-hearts pill renders
       centered below the sub (matching the centered-header pattern
       the rest of the special events already use). Previously this
       had a custom inline header markup that left the pill missing
       entirely on Epic Encounter — the user explicitly wanted a
       "1 heart to give here" pill centered below the description. */
    return '<div class="hoh-fresh-special hoh-fresh-special-epic">' +
      eventHeaderHtml('Epic Encounter', 'Three names already known across the road. The torches burn purple here.', { centered: true }) +
      '<div class="hoh-fresh-special-stage hoh-fresh-special-stage-trio">' +
        chars.map(cardHtml).join('') +
      '</div>' +
      /* Wander bar at the bottom so users see daily ticket progress
         while spending hearts on epic-tier heroes. */
      _streamWanderBarHtml() +
    '</div>';
  }

  /* R96: Crossroads option host portrait. A small circular portrait
     of a sample char from that theme, anchored to the top of each
     "Take this road" card. Lets the user see what kind of hero
     lives down each path before they commit. */
  function _choiceHostHtml(host){
    if(!host) return '';
    /* R140-F: Offset-aware helper. */
    var hMedia = _eventCharImg(host, { eager: true, draggable: false });
    return '<div class="hoh-fresh-choice-host" aria-hidden="true">' +
      '<div class="hoh-fresh-choice-host-media">' + hMedia + '</div>' +
    '</div>';
  }
  function renderChoiceHtml(options){
    return '<div class="hoh-fresh-special hoh-fresh-special-choice">' +
      eventHeaderHtml('Crossroads', 'A fork in the road. Both paths lead somewhere worth seeing.', { centered: true }) +
      '<div class="hoh-fresh-choice-grid">' +
        '<button type="button" class="hoh-fresh-choice-card" onclick="hohFreshFacesChoiceSelect(0)">' +
          _choiceHostHtml(options[0].host) +
          '<span class="hoh-fresh-choice-label">' + esc(options[0].label) + '</span>' +
          '<span class="hoh-fresh-choice-flavor">' + esc(options[0].flavor) + '</span>' +
          '<span class="hoh-fresh-choice-cta">Take this path</span>' +
        '</button>' +
        '<span class="hoh-fresh-choice-divider" aria-hidden="true">&mdash; or &mdash;</span>' +
        '<button type="button" class="hoh-fresh-choice-card" onclick="hohFreshFacesChoiceSelect(1)">' +
          _choiceHostHtml(options[1].host) +
          '<span class="hoh-fresh-choice-label">' + esc(options[1].label) + '</span>' +
          '<span class="hoh-fresh-choice-flavor">' + esc(options[1].flavor) + '</span>' +
          '<span class="hoh-fresh-choice-cta">Take this path</span>' +
        '</button>' +
      '</div>' +
    '</div>';
  }

  function renderThievesCoinHtml(state){
    /* Three sub-states: idle (show coin + flip CTA), flipping
       (animation), result (win/lose). state.phase tracks which. */
    var phase = state.phase || 'idle';
    var resultText = '';
    if(phase === 'won'){
      /* R53: result copy now mentions the +2 hearts (was +1) and the
         Rogues Den that the Continue button transitions into. */
      resultText = '<div class="hoh-fresh-coin-result hoh-fresh-coin-result-win">' +
        '<span class="hoh-fresh-coin-result-title">The hideout opens.</span>' +
        '<span class="hoh-fresh-coin-result-sub">Step inside the Rogues\' Den.</span>' +
      '</div>';
    } else if(phase === 'lost'){
      resultText = '<div class="hoh-fresh-coin-result hoh-fresh-coin-result-lose">' +
        '<span class="hoh-fresh-coin-result-title">You missed the secret hideout.</span>' +
        '<span class="hoh-fresh-coin-result-sub">The mark on the door is gone by morning.</span>' +
      '</div>';
    }
    var coinFaceClass = (phase === 'won') ? ' hoh-fresh-coin-heads' : (phase === 'lost' ? ' hoh-fresh-coin-tails' : '');
    var flipping = (phase === 'flipping') ? ' hoh-fresh-coin-flipping' : '';
    /* R53: phase-specific CTA. Idle = "Toss a Copper". Win = explicit
       "Enter the Rogues\' Den" button (replaces the previous auto-
       transition so the user controls the beat and the +2 grant
       feels like a deliberate reward). Loss has no button — auto-
       refresh handles it. */
    var idleAction = '';
    if(phase === 'idle'){
      idleAction = '<button type="button" class="hoh-fresh-coin-toss" onclick="hohFreshFacesFlipCoin()">' +
          '<span>Toss a Copper</span>' +
        '</button>';
    } else if(phase === 'won'){
      /* R62: badge color flipped from green to heart pink/red so it
         reads as "+2 hearts" (matches the heart icon next to it),
         not "+2 of some unrelated currency". */
      idleAction = '<button type="button" class="hoh-fresh-coin-toss hoh-fresh-dice-continue" onclick="hohFreshFacesEnterRoguesDen()">' +
          '<span>Enter the Rogues\' Den</span>' +
          '<span class="hoh-fresh-dice-cost" style="background:rgba(255,107,138,0.18);color:#ff8aa6;">&#9829;&#xFE0E;</span>' +
        '</button>';
    }
    /* R71: Host card at the top — the rogue (or mythic) blocking the
       door. Mythic-hosted variant gets the existing rainbow ring class
       so the user sees a mythic moment is in play before the flip. */
    var hostHtml = state.host
      ? '<div class="hoh-fresh-thieves-host">' + _miniHeroCard(state.host) + '</div>'
      : '';
    var mythicCls = state.mythicHost ? ' hoh-fresh-special-mythic-host' : '';
    return '<div class="hoh-fresh-special hoh-fresh-special-thieves' + mythicCls + '">' +
      eventHeaderHtml('A Sneaky Encounter', 'A rogue blocks the door. "Toss a copper. Heads, you\'re in. Tails, walk on."', { centered: true }) +
      hostHtml +
      '<div class="hoh-fresh-coin-stage">' +
        '<div class="hoh-fresh-coin' + coinFaceClass + flipping + '" aria-hidden="true">' +
          '<div class="hoh-fresh-coin-face hoh-fresh-coin-face-heads"><span>H</span></div>' +
          '<div class="hoh-fresh-coin-face hoh-fresh-coin-face-tails"><span>T</span></div>' +
        '</div>' +
        resultText +
        (idleAction ? '<div class="hoh-fresh-coin-actions">' + idleAction + '</div>' : '') +
      '</div>' +
    '</div>';
  }

  /* Dice Trader render. Centered header + host portrait card + d20
     visual + roll/result. Host card is clickable (opens detail modal)
     but its heart/X buttons are hidden via the
     .hoh-fresh-special-dice-trader scope in CSS. */
  function renderDiceTraderHtml(state){
    var host = state.host || {};
    var phase = state.phase || 'idle';
    var cost = state.cost || 3;
    /* R140-F: Offset-aware helper. */
    var hostMedia = _eventCharImg(host, { eager: true, draggable: false });
    /* Host card — clickable to open detail, but heart/X are hidden
       via the parent .hoh-fresh-special-dice-trader scope in CSS. */
    var hostCard = '<div role="button" tabindex="0" class="hoh-fresh-card hoh-fresh-card-host" data-id="' + esc(host.id) + '"' +
      ' onclick="if(window.hohOpenDetail)hohOpenDetail(\'' + esc(host.id) + '\')"' +
      ' onkeydown="if((event.key===\'Enter\'||event.key===\' \')&&window.hohOpenDetail){event.preventDefault();hohOpenDetail(\'' + esc(host.id) + '\');}"' +
      ' aria-label="Open ' + esc(host.name || '') + '">' +
      '<span class="hoh-fresh-media">' + hostMedia + '</span>' +
      '<span class="hoh-fresh-body">' +
        '<span class="hoh-fresh-name">' + esc(host.name || 'Unknown') + '</span>' +
        '<span class="hoh-fresh-meta">' + esc((host.race || '') + (host.character_class ? ' ' + host.character_class : '')) + '</span>' +
      '</span>' +
    '</div>';
    /* Stage HTML differs by phase. */
    var stageHtml;
    if(phase === 'idle'){
      stageHtml =
        '<div class="hoh-fresh-dice-stage">' +
          '<div class="hoh-fresh-dice hoh-fresh-dice-idle" aria-hidden="true">' +
            '<span class="hoh-fresh-dice-face">d20</span>' +
          '</div>' +
          '<button type="button" class="hoh-fresh-coin-toss hoh-fresh-dice-roll" onclick="hohFreshFacesRollDiceTrader()">' +
            '<span>Roll the dice</span>' +
            '<span class="hoh-fresh-dice-cost">&#9829;&#xFE0E; &minus;' + cost + '</span>' +
          '</button>' +
        '</div>';
    } else if(phase === 'rolling'){
      stageHtml =
        '<div class="hoh-fresh-dice-stage">' +
          '<div class="hoh-fresh-dice hoh-fresh-dice-rolling" aria-hidden="true">' +
            '<span class="hoh-fresh-dice-face">?</span>' +
          '</div>' +
        '</div>';
    } else { /* won */
      var n = state.rollResult || 0;
      /* Nat 1 = bust styling (red text + bust sub-copy). Nat 20 =
         jackpot class on the dice for the rainbow pulse defined at
         hall-of-heroes.css line 920. Confetti spawns from the roll
         handler, not here, so re-renders don\'t restack particles. */
      var isBust = (n === 1);
      var isJackpot = (n === 20);
      var diceCls = 'hoh-fresh-dice hoh-fresh-dice-resolved'
        + (isJackpot ? ' hoh-fresh-dice-jackpot' : '')
        + (isBust ? ' hoh-fresh-dice-bust' : '');
      var resultCls = 'hoh-fresh-dice-result'
        + (isJackpot ? ' hoh-fresh-dice-result-jackpot' : '')
        + (isBust ? ' hoh-fresh-dice-result-bust' : '');
      var subCopy = isBust
        ? 'The dice mock you. The trader shrugs.'
        : (isJackpot ? 'The trader gasps. A perfect roll.' : 'The trader nods. On you go.');
      stageHtml =
        '<div class="hoh-fresh-dice-stage">' +
          '<div class="' + diceCls + '" aria-hidden="true">' +
            '<span class="hoh-fresh-dice-face">' + n + '</span>' +
          '</div>' +
          '<div class="' + resultCls + '">' +
            '<strong>+' + n + ' hearts</strong>' +
            '<span class="hoh-fresh-dice-result-sub">' + subCopy + '</span>' +
          '</div>' +
          /* Manual continue lets the user move on without waiting for
             the auto-bar to fill. Free advance (fromInteractive) so we
             don\'t double-charge after the roll cost. Clears the
             special and triggers a normal phase advance. */
          '<div class="hoh-fresh-coin-actions">' +
            '<button type="button" class="hoh-fresh-coin-toss hoh-fresh-dice-continue" onclick="hohFreshFacesDiceTraderContinue()">' +
              '<span>Back to the Hall</span>' +
            '</button>' +
          '</div>' +
        '</div>';
    }
    return '<div class="hoh-fresh-special hoh-fresh-special-dice-trader' + (state.mythicHost ? ' hoh-fresh-special-mythic-host' : '') + '">' +
      eventHeaderHtml('A Stranger\'s Wager', 'A hooded figure shakes a leather cup. "Seven hearts for one roll. Whatever the d20 shows is yours back. The dice keep their own counsel."', { centered: true }) +
      '<div class="hoh-fresh-dice-host">' + hostCard + '</div>' +
      stageHtml +
    '</div>';
  }

  /* Roll handler for the Dice Trader. Pays the entry cost, rolls a
     d20, awards hearts equal to the result, and animates through
     the rolling -> resolved sequence. The auto-bar is held during
     the rolling animation so the screen doesn't refresh mid-roll. */
  window.hohFreshFacesRollDiceTrader = function(){
    if(!currentSpecial || currentSpecial.type !== 'diceTrader') return;
    if(currentSpecial.phase !== 'idle') return;
    var cost = currentSpecial.cost || 3;
    /* Affordability re-check at action time (wallet may have changed
       since the event picker fired). */
    var bank = (typeof window.heartsRemainingToday === 'function') ? window.heartsRemainingToday() : 0;
    if(bank < cost){
      if(typeof window.hohShowHeartToast === 'function'){
        try { window.hohShowHeartToast('Not enough hearts', 'You need ' + cost + ' hearts to roll. Earn more from the Hall.'); } catch(_){}
      }
      return;
    }
    /* Pay the cost upfront. recordDailyHeart can't be called from
       fresh-faces directly so we use the public hohAddHeartsToBank
       with a negative... actually the wallet uses positive grants
       only. The cost comes off via setHeartBank-ish path. We use the
       host page's recordDailyHeart by bumping the wallet down via
       the same channel as a normal heart spend. The simplest option
       is to call the existing hohAddHeartsToBank(-cost) — except
       that function clamps negative to zero. Instead we just deduct
       via direct localStorage manipulation, mirroring the daily
       heart accounting that the host file does. We try the cleanest
       path first and fall back. */
    try {
      /* Direct ledger manipulation — same single source of truth the
         host file uses. Read, subtract, write, dispatch the same-tab
         event so the chip + section sync up. R284: hint the reason first,
         this entry cost was the "Uncategorized spends" bar on the hub. */
      try { if (window.hohHeartHint) window.hohHeartHint('spend_special'); } catch(_){}
      var raw = parseInt(localStorage.getItem('hoh-heart-bank-v1') || '0', 10) || 0;
      var next = Math.max(0, raw - cost);
      localStorage.setItem('hoh-heart-bank-v1', String(next));
      try { document.dispatchEvent(new CustomEvent('hoh:hearts-bumped', { detail: { spent: cost } })); } catch(_){}
    } catch(_){}
    if(typeof window.hohRenderHeaderCounter === 'function'){
      try { window.hohRenderHeaderCounter(); } catch(_){}
    }
    /* Roll d20 and animate. */
    var roll = Math.floor(Math.random() * 20) + 1;
    currentSpecial.phase = 'rolling';
    var container = _ffContainer();
    if(container) container.innerHTML = renderDiceTraderHtml(currentSpecial);
    setTimeout(function(){
      if(!currentSpecial || currentSpecial.type !== 'diceTrader') return;
      currentSpecial.phase = 'won';
      currentSpecial.rollResult = roll;
      if(container) container.innerHTML = renderDiceTraderHtml(currentSpecial);
      /* Award hearts. R284: name the source so the hub's economy view can
         see the Dice Trader instead of lumping it into "Hall rewards". */
      if(typeof window.hohAddHeartsToBank === 'function'){
        try { window.hohAddHeartsToBank(roll, 'earn_explore_special'); } catch(_){}
      }
      /* Toast copy varies by outcome — bust gets sympathy copy,
         jackpot gets the celebration line. */
      var toastTitle = '+' + roll + ' hearts';
      var toastBody = (roll === 1) ? 'The dice mocked you. Better luck tomorrow.'
                    : (roll === 20) ? 'A perfect roll. The trader gasps.'
                    : 'The dice fell well.';
      if(typeof window.hohShowHeartToast === 'function'){
        try { window.hohShowHeartToast(toastTitle, toastBody); } catch(_){}
      }
      /* Animate the rolled hearts flying from the dice up to the
         header heart pill so the user sees the reward LAND in the
         wallet, not just appear in a toast. Cap the visible flock at
         12 so a nat 20 doesn\'t spam 20 staggered copies. */
      try {
        var diceEl = document.querySelector('#hoh-fresh-faces .hoh-fresh-dice-resolved');
        if(diceEl) flyDiceTraderHeartsToPill(diceEl, Math.min(roll, 12));
      } catch(_){}
      /* Nat 20 = jackpot. Two confetti bursts for emphasis (matches
         the spin-the-wheel jackpot pattern). */
      if(roll === 20){
        try { spawnConfetti(); } catch(_){}
        setTimeout(function(){ try { spawnConfetti(); } catch(_){} }, 250);
      }
      /* R65: mythic host bonus — fires when the trader IS a mythic
         AND the roll was a clear win (above cost). Below cost the
         user lost net hearts; no bonus there even with mythic. */
      if(currentSpecial.mythicHost && roll > (currentSpecial.cost || 8)){
        setTimeout(function(){
          applyMythicHostBonus('#hoh-fresh-faces .hoh-fresh-dice-host .hoh-fresh-card-host');
        }, 460);
      }
      /* R53: dice trader no longer has a daily cap — it\'s unlimited
         since cost was bumped to 8. (Old markDiceTraderUsedToday
         call removed.) */
      /* Restart auto-timer so the resolved state has its countdown
         to wander further. */
      if(typeof startAutoTimer === 'function') setTimeout(startAutoTimer, 0);
    }, 1300);
  };

  /* Local hearts-fly for the Dice Trader. Same pattern as
     flyHeartsToPill in hoh-extras.js but spawned from the dice
     element so the visual reads as "the dice produced these hearts".
     Staggered ~110ms apart so the user sees each one as a discrete
     reward landing in the pill. */
  /* R57: heart-loss visual for wrong guesses. Spawns a "-N ♥" sprite
     at the heart pill, drifts it down + fades, and pulses the pill
     in a red "lost" flash. Count is shown so a quiz wrong-batch
     reads as a single "-3 ♥" hit instead of three stacked toasts. */
  /* R86: Spawn a literal "-1 ♥" floater anchored to a card. Used by
     class quiz on wrong answers (and any other guess event that wants
     the per-card loss visual). The pill already gets a red flash via
     flyHeartLossFromPill — this adds the per-card legibility the user
     asked for. CSS class .hoh-fresh-card-minus-heart drives the rise+
     fade animation; if the class is missing the floater still renders
     as a static red number that auto-removes after 1.1s. */
  function spawnMinusHeartOnCard(cardEl){
    if(!cardEl) return;
    var rect = cardEl.getBoundingClientRect();
    if(!rect.width) return;
    var startX = rect.left + rect.width / 2;
    var startY = rect.top + rect.height / 2;
    var el = document.createElement('div');
    el.className = 'hoh-fresh-card-minus-heart';
    el.innerHTML = '&minus;1&nbsp;&#9829;&#xFE0E;';
    /* Inline fallback styling so this works even before the CSS class
       lands. Position fixed so it escapes any clipping container. */
    el.style.cssText = [
      'position:fixed',
      'left:' + startX + 'px',
      'top:' + startY + 'px',
      'transform:translate(-50%,-50%)',
      'z-index:100020',
      'pointer-events:none',
      'font-family:Cinzel,serif',
      'font-weight:800',
      'font-size:22px',
      'color:#ff5c7c',
      'text-shadow:0 2px 8px rgba(0,0,0,0.7),0 0 14px rgba(255,92,124,0.8)',
      'transition:transform 900ms cubic-bezier(0.22,0.61,0.36,1),opacity 900ms ease-out',
      'will-change:transform,opacity'
    ].join(';');
    document.body.appendChild(el);
    requestAnimationFrame(function(){
      el.style.transform = 'translate(-50%,calc(-50% - 60px)) scale(1.15)';
      el.style.opacity = '0';
    });
    setTimeout(function(){ if(el.parentNode) el.parentNode.removeChild(el); }, 1100);
  }
  function flyHeartLossFromPill(n){
    var pill = document.getElementById('hoh-tk-header') || document.getElementById('hoh-heart-remaining');
    if(!pill) return;
    var pr = pill.getBoundingClientRect();
    if(!pr.width) return;
    var count = Math.max(1, n || 1);
    /* Pulse the pill red so the loss reads even if the user isn't
       watching the floating heart. */
    try {
      pill.classList.remove('hoh-tk-heart-loss');
      void pill.offsetWidth;
      pill.classList.add('hoh-tk-heart-loss');
      setTimeout(function(){ pill.classList.remove('hoh-tk-heart-loss'); }, 900);
    } catch(_){}
    var startX = pr.left + pr.width / 2;
    var startY = pr.top + pr.height / 2;
    var el = document.createElement('div');
    el.className = 'hoh-fresh-heart-loss-fly';
    el.innerHTML = '&minus;' + count + '&nbsp;&#9829;&#xFE0E;';
    el.style.left = startX + 'px';
    el.style.top = startY + 'px';
    el.style.transform = 'translate(-50%, -50%)';
    document.body.appendChild(el);
    requestAnimationFrame(function(){
      el.style.transform = 'translate(-50%, calc(-50% + 60px)) scale(0.8)';
      el.style.opacity = '0';
    });
    setTimeout(function(){ if(el.parentNode) el.parentNode.removeChild(el); }, 1100);
  }
  /* R57: single chokepoint for guess penalties. Removes hearts from
     the bank, fires ONE visual + ONE toast (whatever count), so a
     batch deduction (e.g. quiz wrong-count) reads as a single hit.
     Returns the number actually removed (capped by current bank so
     we never go negative or lie about a loss). */
  function loseHeartFromGuess(reason, n){
    var ask = Math.max(1, n || 1);
    /* R148-Z32: Lonefire trigger moved out of here. The charm now
       fires when the user's WALLET reaches 0 (see the global
       hoh:hearts-bumped listener at the bottom of this IIFE), not
       on every guess-lose. Rationale: the charm should feel like a
       safety net for "you're stuck", not "the dice screwed you on
       one trivia event" — a 50-heart refund on losing 1 heart in a
       quiz was way too generous and trivialized the loss beat. */
    var removed = 0;
    if(typeof window.hohRemoveHeartsFromBank === 'function'){
      try { removed = window.hohRemoveHeartsFromBank(ask, 'spend_penalty') || 0; } catch(_){ removed = 0; }
    }
    if(removed > 0){
      flyHeartLossFromPill(removed);
      if(typeof window.hohShowHeartToast === 'function'){
        var label = '-' + removed + ' heart' + (removed === 1 ? '' : 's');
        try { window.hohShowHeartToast(label, reason || 'Wrong guess.'); } catch(_){}
      }
    }
    return removed;
  }
  /* R136-quest-fly: Exposed on window so handlers in other modules
     (hoh-your-hall.js's hohSendCharOnQuest, etc.) can fire the same
     heart-to-pill flock animation. Without this, those callers were
     hitting the typeof guard and silently rendering nothing. */
  /* R140-Z3i: Optional `color` arg lets the caller tint the flying
     hearts to match the reward type. Common brand presets:
       'blue'   → #b3f5ee (heart-bonus pips at 10/30)
       'purple' → #ef94ff (capstone heart-bonus at 50, default look)
       'pink'   → #ED97FE (legacy, used by everything else)
     Pass nothing for the default pink. */
  /* Jan, 2026-09-27: the hearts pop where they were found (hohHeartBurst
     in hoh-wallet.js) and fly nowhere. The old flock flew to the wallet
     pill; the name stays for its many callers, and `color` picks the
     burst's tone ('blue' or 'purple'; anything else is the Hall pink). */
  function flyDiceTraderHeartsToPill(originEl, n, color){
    if(!originEl || !n) return;
    if(typeof window.hohHeartBurst === 'function'){
      window.hohHeartBurst(originEl, { n: n, tone: color });
    }
    /* The counter still answers where it is on screen (Your Hall): a pulse
       on the pill, never a flight to it. A hidden pill measures 0x0 and is
       left alone. */
    var pill = document.getElementById('hoh-tk-header') || document.getElementById('hoh-heart-remaining');
    if(!pill || !pill.getBoundingClientRect().width) return;
    setTimeout(function(){
      try {
        pill.classList.remove('hoh-tk-heart-pulse');
        void pill.offsetWidth;
        pill.classList.add('hoh-tk-heart-pulse');
        setTimeout(function(){ pill.classList.remove('hoh-tk-heart-pulse'); }, 900);
      } catch(_){}
    }, 300);
  }
  /* R136-quest-fly: cross-module export. Keep the private name for
     internal callers, expose on window for the rest of the app. */
  window.flyDiceTraderHeartsToPill = flyDiceTraderHeartsToPill;

  /* R65: Mythic blessing — fires when the user wins an event whose
     host (or winner card) is mythic. Adds +30 hearts on top of the
     normal payout, throws a "Mythic Blessing" toast, and flies the
     bonus hearts from the mythic card to the wallet pill so the
     visual reads "this mythic just gave you their gift". */
  var MYTHIC_HOST_BONUS = 30;
  function applyMythicHostBonus(originSelector){
    if(typeof window.hohAddHeartsToBank === 'function'){
      try { window.hohAddHeartsToBank(MYTHIC_HOST_BONUS); } catch(_){}
    }
    if(typeof window.hohShowHeartToast === 'function'){
      try { window.hohShowHeartToast('Mythic Blessing', 'A gift from a mythic hero.'); } catch(_){}
    }
    /* Source element: the mythic host\'s card if findable, else fall
       back to the event container so the fly still animates. */
    var src = null;
    try {
      if(originSelector){
        src = document.querySelector(originSelector);
      }
    } catch(_){}
    if(!src){
      try {
        src = document.querySelector('#hoh-fresh-faces .hoh-fresh-special-mythic-host')
           || document.querySelector('#hoh-fresh-faces');
      } catch(_){}
    }
    if(src && typeof flyDiceTraderHeartsToPill === 'function'){
      try { flyDiceTraderHeartsToPill(src, 12); } catch(_){}
    }
    /* R87: Big rainbow banner so the user CLEARLY sees the +30 mythic
       bonus landed. The plain toast was getting lost in the post-win
       sequence. Banner pins above the source card, drifts up, fades
       out over ~2.5s. Mythic-palette confetti reinforces the moment. */
    try {
      _spawnMythicHostBanner(src, MYTHIC_HOST_BONUS);
    } catch(_){}
    try {
      if(typeof spawnMythicConfetti === 'function') spawnMythicConfetti();
      else if(typeof spawnConfetti === 'function') spawnConfetti();
    } catch(_){}
  }
  /* R87: Mythic Host Bonus banner — bold rainbow text that floats up
     over the host card so the user clearly registers the +30 reward.
     CSS-driven via the existing .hoh-mythic-banner pattern, but with
     a different message + duration. */
  function _spawnMythicHostBanner(srcEl, amount){
    var pill = document.getElementById('hoh-tk-header') || document.getElementById('hoh-heart-remaining');
    var anchor = srcEl || pill || document.body;
    var rect;
    try { rect = anchor.getBoundingClientRect(); } catch(_){ return; }
    if(!rect || !rect.width) return;
    var startX = rect.left + rect.width / 2;
    var startY = rect.top + Math.min(rect.height * 0.3, 80);
    var el = document.createElement('div');
    el.className = 'hoh-mythic-host-bonus-banner';
    el.innerHTML = '<span class="hoh-mythic-host-bonus-eyebrow">MYTHIC HOST BONUS</span>' +
      '<span class="hoh-mythic-host-bonus-amount">A GIFT FOR YOU</span>';
    el.style.cssText = [
      'position:fixed',
      'left:' + startX + 'px',
      'top:' + startY + 'px',
      'transform:translate(-50%,-50%) scale(0.85)',
      'z-index:100025',
      'pointer-events:none',
      'padding:14px 22px',
      'border-radius:14px',
      'background:linear-gradient(135deg,rgba(40,20,52,0.96),rgba(20,12,28,0.98))',
      'border:1.5px solid transparent',
      'background-clip:padding-box',
      'box-shadow:0 0 28px rgba(237,151,254,0.55),0 0 56px rgba(126,212,255,0.3),0 12px 32px rgba(0,0,0,0.55)',
      'display:flex','flex-direction:column','align-items:center','gap:4px',
      'opacity:0',
      'transition:transform 0.32s cubic-bezier(0.18,0.89,0.32,1.28),opacity 0.32s ease-out'
    ].join(';');
    document.body.appendChild(el);
    requestAnimationFrame(function(){
      el.style.transform = 'translate(-50%,-50%) scale(1)';
      el.style.opacity = '1';
    });
    setTimeout(function(){
      try {
        el.style.transition = 'transform 1.6s cubic-bezier(0.4,0,0.2,1),opacity 1.6s ease-out';
        el.style.transform = 'translate(-50%,calc(-50% - 80px)) scale(1.06)';
        el.style.opacity = '0';
      } catch(_){}
    }, 900);
    setTimeout(function(){ if(el.parentNode) el.parentNode.removeChild(el); }, 2600);
  }
  /* R87: Mythic-palette confetti — same confetti spawn but colors
     pulled from the mythic rainbow palette so the moment reads as
     "this was special". Falls back to default spawnConfetti if the
     custom variant isn't usable. */
  function spawnMythicConfetti(){
    var colors = ['#ffacf5','#ffc78a','#a8dcb8','#a8d0f5','#c8aef0','#ED97FE'];
    var pieces = 36;
    var container = document.body;
    for(var i = 0; i < pieces; i++){
      (function(idx){
        var el = document.createElement('div');
        var color = colors[idx % colors.length];
        var startX = window.innerWidth / 2 + (Math.random() * 200 - 100);
        var startY = window.innerHeight / 2 + (Math.random() * 60 - 30);
        var dx = (Math.random() * 320 - 160);
        var dy = -180 - Math.random() * 220;
        var rot = Math.random() * 720 - 360;
        el.style.cssText = [
          'position:fixed',
          'left:' + startX + 'px',
          'top:' + startY + 'px',
          'width:8px','height:14px',
          'background:' + color,
          'border-radius:2px',
          'pointer-events:none',
          'z-index:100024',
          'opacity:0.9',
          'transform:translate(-50%,-50%) rotate(0deg)',
          'transition:transform 1.4s cubic-bezier(0.22,0.61,0.36,1),opacity 1.4s ease-out',
          'box-shadow:0 0 6px ' + color
        ].join(';');
        container.appendChild(el);
        setTimeout(function(){
          el.style.transform = 'translate(calc(-50% + ' + dx + 'px),calc(-50% + ' + dy + 'px)) rotate(' + rot + 'deg)';
          el.style.opacity = '0';
        }, 20 + idx * 8);
        setTimeout(function(){ if(el.parentNode) el.parentNode.removeChild(el); }, 1700);
      })(i);
    }
  }

  /* ================================================================ */
  /* Champion's Duel — render + handlers                                */
  /* ================================================================ */
  function renderCombatantCard(c, picked, isOpponent, dimmed, label){
    /* R140-F: Offset-aware helper. */
    var media = _eventCharImg(c, { eager: true, draggable: false });
    /* R93: Apply the combatant's actual rarity class so the card
       glows with their tier color (rare blue, epic purple, legendary
       gold, mythic rainbow). Same .hoh-card-rar-X CSS the main grid
       uses, so the visual is consistent across surfaces. */
    var rarTier = 'common';
    try {
      if(typeof window.hohGetRarity === 'function'){
        rarTier = window.hohGetRarity(c.hearts || 0).tier || 'common';
      }
    } catch(_){}
    var stateCls = (picked ? ' hoh-fresh-duel-card-picked' : '')
                 + (isOpponent ? ' hoh-fresh-duel-card-opponent' : '')
                 + (dimmed ? ' hoh-fresh-duel-card-dimmed' : '')
                 + ' hoh-card-rar-' + rarTier;
    var labelHtml = label ? '<span class="hoh-fresh-duel-card-label">' + esc(label) + '</span>' : '';
    return '<div class="hoh-fresh-duel-card hoh-fresh-card' + stateCls + '">' +
      labelHtml +
      '<span class="hoh-fresh-media">' + media + '</span>' +
      '<span class="hoh-fresh-body">' +
        '<span class="hoh-fresh-name">' + esc(c.name || 'Unknown') + '</span>' +
        '<span class="hoh-fresh-meta">' + esc((c.race || '') + (c.character_class ? ' ' + c.character_class : '')) + '</span>' +
      '</span>' +
    '</div>';
  }
  /* R86: Generic duel pick handler. Used by all 3 duel types. The
     specific window.hohFreshFaces*DuelPick wrappers below just bind
     this with their type filter so each event's CTAs route here. */
  /* ================================================================ */
  /* R148-Z37: Type 3 Mythic Duel — renderer + handlers                */
  /* ================================================================ */
  /* ⛔ R591: EVERY DUEL AND BRAWL HANDLER THAT LIVED HERE IS DELETED,
     not left dormant. The whole family is gone: _runDuelPick and its
     duplicate window.hohFreshFacesDuelPick (two copies of one resolver,
     differing only in a hard-coded * 20), _runDuelContinue, the six
     one-line per-type wrappers, the four-phase mythic special flow with
     its paid +d6, and both rigged brawl handlers with the
     _brawl4SynthesizeRolls / _barFightSynthesizeRolls dice fabricators.

     They are deleted rather than parked because their event objects no
     longer exist. Every one of them opens with
     `if(currentSpecial.type !== X) return;` and then reads fields
     (combatants, diceMax, brawlers, winnerIdx) that hfBuildBattle does
     not produce. A dormant handler that can never match is dead weight
     the next reader has to disprove, and one that DID match would paint
     a blank screen over a paid bet.

     The single replacement is hohFreshFacesHfPick / HfWager / HfStart /
     HfClose, above, routed by the hfBattle flag. */

  /* ================================================================ */
  /* Tavern Brawl — the old card renderer, still used by other events  */
  /* ================================================================ */
  function renderBrawlerCard(c, picked, dimmed, label){
    /* R140-F: Offset-aware helper. */
    var media = _eventCharImg(c, { eager: true, draggable: false });
    var stateCls = (picked ? ' hoh-fresh-brawl-card-picked' : '')
                 + (dimmed ? ' hoh-fresh-brawl-card-dimmed' : '');
    var labelHtml = label ? '<span class="hoh-fresh-brawl-card-label">' + esc(label) + '</span>' : '';
    return '<div class="hoh-fresh-brawl-card hoh-fresh-card' + stateCls + '">' +
      labelHtml +
      '<span class="hoh-fresh-media">' + media + '</span>' +
      '<span class="hoh-fresh-body">' +
        '<span class="hoh-fresh-name">' + esc(c.name || 'Unknown') + '</span>' +
        '<span class="hoh-fresh-meta">' + esc((c.race || '') + (c.character_class ? ' ' + c.character_class : '')) + '</span>' +
      '</span>' +
    '</div>';
  }
  window.hohFreshFacesBrawlContinue = function(){
    if(!currentSpecial || currentSpecial.type !== 'tavernBrawl') return;
    if(currentSpecial.phase !== 'resolved') return;
    currentSpecial = null;
    if(typeof window.hohFreshFacesRefresh === 'function'){
      try { window.hohFreshFacesRefresh({ fromInteractive: true }); } catch(_){}
    }
  };

  /* R62: 4-person Tavern Brawl renderer + handler. Same visual
     skeleton as the 3-person version, but no upfront cost — pick
     1 of 4. Right pick = +15 hearts. Wrong pick = -1 heart with
     the standard heart-loss visual. */
  /* R86: 4-way brawl now mirrors the 3-way pattern with a dice-roll
     reveal. Each brawler rolls a d6; highest takes the pot. The
     winnerIdx is still pre-picked (so mythic host can be slotted),
     but we synthesize d6 rolls that match — the picked winner gets
     the highest, the others scatter below. Loss penalty bumped from
     -1 to -3 to match the higher payout (+15) and dice-roll feel. */
  /* Synthesize a d6 roll set where index `winIdx` lands the highest.
     Winner rolls 5 or 6; the other three roll 1-4 distinct enough to
     read as a clear loss. Used by both the picker (so the dice match
     the pre-determined winner) and the resolution. */
  window.hohFreshFacesBrawl4Continue = function(){
    if(!currentSpecial || currentSpecial.type !== 'tavernBrawl4') return;
    if(currentSpecial.phase !== 'resolved') return;
    currentSpecial = null;
    if(typeof window.hohFreshFacesRefresh === 'function'){
      try { window.hohFreshFacesRefresh({ fromInteractive: true }); } catch(_){}
    }
  };

  /* ================================================================ */
  /* R119-H: Bar Fight — unlimited 2x4 d20 melee                       */
  /* ================================================================ */
  /* Eight brawlers in a 2-row, 4-column grid. Pick one as your
     champion. All 8 roll a d20 in parallel. Highest wins.
     Win  = +50 hearts.
     Lose = -1 heart only (an "atta boy" tap so the bet stays cheap).
     Designed as the high-payout, low-stakes lottery in the unlimited
     pool — crazy upside if you call it right, basically free if you
     don't. EV at 1/8 odds = 50/8 - 1 = +5.25 hearts per attempt, so
     it leans positive but the variance keeps it interesting.
     Mythic host slots into the winner index when one rolls. */
  var BAR_FIGHT_PAYOUT = 50;
  var BAR_FIGHT_LOSS = 2;
  /* Jan on the old eight-way free-for-all: "if there's eight
     characters, then just split it into two teams of four, and even
     that would be cool because we're simulating a 4v4." Taken
     literally. The 2x4 grid markup and the pre-picked winner are gone.
     No tier filter, so this is the one battle that can field anybody
     in the hall, which is also why it is the one whose two sides are
     most often visibly uneven. That is a feature: an uneven board is
     where the price gets interesting. */
  function pickBarFightEvent(){
    return hfBuildBattle({
      type: 'barFight', size: 4, tier: null, suggest: 8,
      title: 'A Bar Fight',
      subtitle: 'Split down the middle, and nobody remembers who swung first.',
      sideName: { A: 'The Near Side', B: 'The Far Side' },
      poolFilter: function(c){ return !!(c && c.id); }
    });
  }

  /* ================================================================ */
  /* R591 · THE HALL FIGHT, ON SCREEN                                  */
  /* ================================================================ */
  /* ⛔ NAMESPACE: .hoh-hf-* and #hohHf*. NOT the Battlegrounds board.
     That board's ids (#hocTA, #hocSideA, #hocF<side><i>) are global and
     hardcoded, and a comment in hoh-contract-ui.js records that "forty
     other places in this file address #hocSideA and #hocSideB by name".
     Reusing them would mean a live Battlegrounds querySelector could
     find an element belonging to this minigame, in the one system that
     must never be disturbed by a Hall toy. */

  /* ⛔ HOW LONG A FIGHT SHOULD TAKE. Jan on the 9000 this started at: "its
     going too fast". He is right, and the real board is the yardstick: its
     pace curve lands a MEDIAN Battlegrounds fight at 67.5 seconds. Nine
     seconds was roughly seven times faster than the thing this screen exists
     to imitate, which is why it read as a blur rather than a battle.
     20s is the compromise: slow enough that every blow is a separate,
     readable moment, short enough that a wager resolves while the player is
     still looking at it. The floor matters as much as the target: without
     it a 60-beat 6v6 would strobe. */
  var HF_TARGET_MS = 20000;
  var HF_BEAT_MIN = 240, HF_BEAT_MAX = 900;
  var hfPlayTimer = null, hfPlayToken = 0;

  /* ---- actor-to-pool travel --------------------------------------
     Health in this fight belongs to a SIDE, never to a portrait. Trails
     therefore leave the acting portrait but land on #hohHfT<side>, the
     shared-health readout. The classes are deliberately presentation-only
     hooks: Hall CSS owns their colour, shape and easing while this helper
     owns geometry, truth and lifecycle. */
  var hfTrailTimers = [], hfTrailNodes = [], hfTrailEpoch = 0;
  function hfTrailForgetTimer(id){
    for(var i = hfTrailTimers.length - 1; i >= 0; i--){
      if(hfTrailTimers[i] === id){ hfTrailTimers.splice(i, 1); break; }
    }
  }
  function hfTrailLater(fn, ms){
    var id = setTimeout(function(){
      hfTrailForgetTimer(id);
      fn();
    }, ms);
    hfTrailTimers.push(id);
    return id;
  }
  function hfTrailForgetNode(node){
    for(var i = hfTrailNodes.length - 1; i >= 0; i--){
      if(hfTrailNodes[i] === node){ hfTrailNodes.splice(i, 1); break; }
    }
  }
  function hfTrailRemove(node){
    hfTrailForgetNode(node);
    try { if(node && node.parentNode) node.parentNode.removeChild(node); } catch(_){}
  }
  function hfTrailClear(){
    /* Epoch protects even a timeout that was already queued when clear ran. */
    hfTrailEpoch++;
    var timers = hfTrailTimers.slice(); hfTrailTimers.length = 0;
    for(var i = 0; i < timers.length; i++){
      try { clearTimeout(timers[i]); } catch(_){}
    }
    var nodes = hfTrailNodes.slice(); hfTrailNodes.length = 0;
    for(var j = 0; j < nodes.length; j++){
      try { if(nodes[j] && nodes[j].parentNode) nodes[j].parentNode.removeChild(nodes[j]); } catch(_){}
    }
  }
  function hfTrailLive(token, epoch){
    return token === hfPlayToken && epoch === hfTrailEpoch &&
      !!(currentSpecial && currentSpecial.hfBattle && currentSpecial.phase === 'fighting');
  }
  function hfTrailTarget(side){
    /* The numeral is the canonical pooled-health target. The fallbacks keep
       an old/synthetic board readable without ever targeting one hero. */
    var hp = document.getElementById('hohHfT' + side);
    if(hp) return hp;
    var band = document.querySelector('.hoh-hf-side[data-side="' + side + '"] .hoh-hf-sbar');
    if(band) return band;
    var fan = document.querySelector('.hoh-hf-side[data-side="' + side + '"] .hoh-hf-fan');
    return fan || document.querySelector('.hoh-hf-side[data-side="' + side + '"]');
  }
  function hfTrailLaunch(actor, target, kind, token, beat, impact, opts){
    opts = opts || {};
    var epoch = hfTrailEpoch;
    if(!hfTrailLive(token, epoch)) return null;

    /* Reduced motion means the result is immediate, not invisible or late.
       A missing geometry anchor follows the same truth-preserving fallback. */
    var board = document.getElementById('hohHfBoard');
    if(hfReduced() || !board || !actor || !target){
      if(typeof impact === 'function' && hfTrailLive(token, epoch)) impact();
      return null;
    }

    var br = board.getBoundingClientRect();
    var ar = actor.getBoundingClientRect();
    var tr = target.getBoundingClientRect();
    var sx = ar.left + ar.width / 2 - br.left;
    var sy = ar.top + ar.height / 2 - br.top;
    var ex = tr.left + tr.width / 2 - br.left;
    var ey = tr.top + tr.height / 2 - br.top;
    /* A miss still shows the attempted direction, but visibly passes beyond
       the pooled-health mark and never borrows a hit impact. */
    if(opts.miss){
      ex += (sx <= ex) ? 18 : -18;
      ey += opts.targetSide === 'B' ? -14 : 14;
    }
    var dx = ex - sx, dy = ey - sy;
    var dist = Math.sqrt(dx * dx + dy * dy);
    var angle = Math.atan2(dy, dx) * 180 / Math.PI;
    var travel = Math.max(105, Math.min(420, Math.round((beat || HF_BEAT_MIN) * 0.48)));
    var linger = Math.max(70, Math.min(240, Math.round((beat || HF_BEAT_MIN) * 0.26)));

    var trail = document.createElement('span');
    var from = String(opts.fromSide || '').toLowerCase();
    var to = String(opts.targetSide || '').toLowerCase();
    var result = String(opts.result || '').toLowerCase();
    trail.className = 'hoh-hf-trail hoh-hf-trail-' + kind +
      (from ? ' hoh-hf-trail-from-' + from : '') +
      (to ? ' hoh-hf-trail-to-' + to : '') +
      (opts.miss ? ' hoh-hf-trail-miss' : '') +
      (opts.crit ? ' hoh-hf-trail-crit' : '') +
      (result ? ' hoh-hf-trail-result-' + result : '');
    trail.setAttribute('aria-hidden', 'true');
    trail.setAttribute('data-kind', kind);
    trail.setAttribute('data-from-side', opts.fromSide || '');
    trail.setAttribute('data-target-side', opts.targetSide || '');
    trail.setAttribute('data-result', opts.result || '');
    trail.setAttribute('data-target-id', target.id || 'shared-health');
    trail.style.left = sx + 'px';
    trail.style.top = sy + 'px';
    trail.style.pointerEvents = 'none';
    trail.style.setProperty('--hf-trail-dx', dx + 'px');
    trail.style.setProperty('--hf-trail-dy', dy + 'px');
    trail.style.setProperty('--hf-trail-distance', dist + 'px');
    trail.style.setProperty('--hf-trail-angle', angle + 'deg');
    trail.style.setProperty('--hf-trail-ms', travel + 'ms');
    var core = document.createElement('span');
    core.className = 'hoh-hf-trail-core';
    trail.appendChild(core);
    board.appendChild(trail);
    hfTrailNodes.push(trail);
    void trail.offsetWidth;
    trail.classList.add('is-flying');

    hfTrailLater(function(){
      if(!hfTrailLive(token, epoch)){ hfTrailRemove(trail); return; }
      trail.classList.remove('is-flying');
      trail.classList.add(opts.miss ? 'is-missed' : 'is-impact');
      if(typeof impact === 'function') impact();
      hfTrailLater(function(){ hfTrailRemove(trail); }, linger);
    }, travel);
    return trail;
  }
  function hfImpactStamp(e){
    /* Attack and heal helpers can never apply the same absolute stamp twice,
       even if a future hybrid move legitimately owns both treatments. The
       play token lets the same saved tape be replayed from its start. */
    if(!e || e._hfImpactStampToken === hfPlayToken) return;
    e._hfImpactStampToken = hfPlayToken;
    /* New tapes carry the action's absolute AFTER state. Old in-memory tapes
       created before a hot deploy fall back to their original stamp. */
    var snap = (typeof e.afterA === 'number' && typeof e.afterB === 'number')
      ? { ha:e.afterA, hb:e.afterB }
      : e;
    hfApplyStamp(snap);
  }
  function hfHitImpact(e){
    if(!e || !(e.dmg > 0)) return;
    hfImpactStamp(e);
    var struck = document.querySelector('.hoh-hf-side[data-side="' + e.ts + '"]');
    var crit = e.kind === 'crit' || e.kind === 'nat20';
    if(struck && !hfReduced()){
      struck.classList.remove('hit','crit');
      void struck.offsetWidth;
      struck.classList.add('hit');
      if(crit) struck.classList.add('crit');
      setTimeout(function(){ try { struck.classList.remove('hit','crit'); } catch(_){} }, 560);
    }
    hfDmgPop(e.ts, '-' + e.dmg, crit);
    hfQuake(crit);
  }
  function hfHealImpact(e){
    if(!e || !(e.heal > 0)) return;
    hfImpactStamp(e);
    var mend = document.querySelector('.hoh-hf-side[data-side="' + e.s + '"]');
    if(mend && !hfReduced()){
      mend.classList.remove('mend');
      void mend.offsetWidth;
      mend.classList.add('mend');
      setTimeout(function(){ try { mend.classList.remove('mend'); } catch(_){} }, 620);
    }
    hfDmgPop(e.s, '+' + e.heal, false, true);
  }
  function hfAttackEvent(e){
    return !!e && (e.kind === 'hit' || e.kind === 'miss' || e.kind === 'dodged' ||
      e.kind === 'guarded' || e.kind === 'glance' || e.kind === 'nat20' || e.kind === 'crit');
  }
  function hfTrailOwnsStamp(e){
    return !!(e && e.e === 'beat' &&
      ((e.dmg > 0 && hfAttackEvent(e)) || e.heal > 0));
  }
  function hfAttackTrail(e, actor, token, beat){
    if(!hfAttackEvent(e)) return null;
    var missed = e.kind === 'miss' || e.kind === 'dodged';
    return hfTrailLaunch(actor, hfTrailTarget(e.ts), 'attack', token, beat,
      e.dmg > 0 ? function(){ hfHitImpact(e); } : null,
      { fromSide:e.s, targetSide:e.ts, result:e.kind, miss:missed,
        crit:e.kind === 'crit' || e.kind === 'nat20' });
  }
  function hfHealTrail(e, actor, token, beat){
    if(!e || !(e.heal > 0)) return null;
    return hfTrailLaunch(actor, hfTrailTarget(e.s), 'heal', token, beat,
      function(){ hfHealImpact(e); },
      { fromSide:e.s, targetSide:e.s, result:e.kind });
  }

  function hfDieLabel(n){ return 'd' + n; }
  function hfRarName(r){ return HF_TIERS[r] || 'common'; }

  /* ⛔ R592: A TILE IS A FACE AND A NAME. NOTHING ELSE.
     fanHtml in the real board (hoh-contract-ui.js 14650) emits exactly a
     portrait, a hover name label, and — on the player's side only — the
     card-charge strip. There is no bar and no numeral on a body, because a
     body has no health of its own. The first cut of this screen put a green
     bar and a "d12" caption under every portrait, which is the single thing
     Jan named. Both are gone.
     The name is a HOVER label BELOW the tile at opacity 0 (ui 1728): above
     the tile it painted over the party title, and outside the fan a panel
     with overflow:hidden clipped it. */
  function hfUnitTile(u){
    var art = u.art
      ? '<img src="' + esc(u.art) + '" alt="" loading="eager" decoding="async" onerror="if(window.hohImgFallback)hohImgFallback(this);">'
      : '<span class="flt">' + esc((u.name || '?').charAt(0)) + '</span>';
    return '<span class="hoh-hf-fi hoh-hf-rar-' + esc(hfRarName(u.rar)) + (u.dead ? ' hoh-hf-fell gone' : '') +
        (u.champ ? ' hoh-hf-champ' : '') + '"' +
        ' id="hohHfU' + u.side + u.i + '">' +
      art +
      /* R593: the player's own hero among strangers. A commissioned
         portrait deserves to be findable on a row of six, and the badge
         is what tells them the extra body on their side is theirs. */
      (u.champ ? '<span class="hfyou">YOURS</span>' : '') +
      /* ⛔ A CHILD SPAN, NOT A CLASS. ui.js 23008 records that .fi.bfallen
         matched nothing and a classList.remove on it was a silent no-op for
         a whole wave. */
      (u.dead ? '<span class="bfallen">FALLEN</span>' : '') +
      '<span class="fnm">' + esc(u.name) + '</span>' +
    '</span>';
  }

  function hfSideHtml(ev, side){
    var team = ev.teams[side];
    var picked = ev.pickedSide === side;
    var tiles = '';
    for(var k = 0; k < team.length; k++) tiles += hfUnitTile(team[k]);
    /* The whole side is the control during the wager. A row of portraits is a
       far bigger target than a button under it, and it makes plain that you
       are backing the SIDE. */
    var tag = (ev.phase === 'wager') ? 'button' : 'div';
    var interactive = (ev.phase === 'wager')
      ? ' type="button" onclick="hohFreshFacesHfPick(\'' + side + '\')"' +
        ' aria-pressed="' + (picked ? 'true' : 'false') + '"' +
        ' aria-label="Back ' + esc(ev.sideName[side]) + ', pays ' + ev.mult[side].toFixed(2) + ' times your bet"'
      : '';
    /* .foe reads top-down and .mine bottom-up, so each side's name and health
       sit on the OUTER edge of the board. R409 made that choice on the real
       board and R487 records what happens when it is lost. */
    var cls = 'hoh-hf-side ' + (side === 'B' ? 'foe' : 'mine') + (picked ? ' hoh-hf-picked' : '');
    return '<' + tag + ' class="' + cls + '" data-side="' + side + '"' + interactive + '>' +
      '<span class="hoh-hf-sbar">' +
        '<span class="sn">' + esc(ev.sideName[side]) +
          (ev.phase === 'wager' ? ' <i class="hoh-hf-odds">pays ' + ev.mult[side].toFixed(2) + '&times;</i>' : '') +
        '</span>' +
        /* ONE numeral for the whole side. This is the health model. */
        '<span class="hoh-hf-hp"><b id="hohHfT' + side + '">' + (ev.pool ? ev.pool[side] : 0) + '</b>' +
          '<i class="hoh-hf-hp-of">health</i></span>' +
      '</span>' +
      /* R593: the cap is 7, not 6. A 6v6 with the player's own hero on
         one side is seven bodies in a row, and a cap of 6 would have let
         the seventh overflow its own rail. */
      '<span class="hoh-hf-fan" style="--hfn:' + Math.max(1, Math.min(7, team.length)) + '">' + tiles + '</span>' +
    '</' + tag + '>';
  }

  /* The centre line is three parts, not one: who is rolling, the die itself,
     and whose roll it is. The two hairline flanks plus EQUAL fixed side
     widths are what hold the die dead centre (ui 331): a flex row centres the
     CONTENT, and an acting hero's name is wider than "their roll", so without
     them the die drifts as the name changes. */
  function hfBoardHtml(ev){
    return '<div class="hoh-hf-board" id="hohHfBoard">' +
      hfSideHtml(ev, 'B') +
      '<div class="hoh-hf-mid">' +
        /* the centre line must stop asking for something already given */
        '<div class="hoh-hf-dlbl" id="hohHfDL">' +
          (ev.phase === 'wager' ? (ev.pickedSide ? 'backing' : 'pick a side') : '') +
        '</div>' +
        '<div class="hoh-hf-die hoh-hf-shp-d20" id="hohHfDie">&mdash;</div>' +
        '<div class="hoh-hf-dside" id="hohHfDSide">' + (ev.pickedSide ? esc(ev.sideName[ev.pickedSide]) : '') + '</div>' +
      '</div>' +
      hfSideHtml(ev, 'A') +
    '</div>';
  }

  function renderHallFightHtml(ev){
    var bank = (typeof window.heartsRemainingToday === 'function') ? window.heartsRemainingToday() : 0;
    var head = eventHeaderHtml(ev.title, ev.subtitle, { centered: true });
    var body = hfBoardHtml(ev);

    if(ev.phase === 'wager'){
      var ceil = hfWagerCeiling();
      /* The slider can never point at a number the player cannot pay,
         so the wager is clamped to the track before anything reads it. */
      var w = Math.max(HF_WAGER_MIN, Math.min(ceil, ev.wager || ev.suggested || HF_WAGER_MIN));
      /* ⛔ THE CLAMP IS WRITTEN BACK, NOT JUST DRAWN. The balance can move
         under this screen (another tab, a heart event firing), and a
         render that only clamps the DISPLAY leaves ev.wager holding the
         old number, so the slider and the amount the button would spend
         disagree. Two amounts on one money control is the state R592
         deleted the typed field to avoid. */
      ev.wager = w;
      var picked = ev.pickedSide;
      var ready = !!picked && w >= HF_WAGER_MIN && w <= bank;
      var pay = picked ? Math.round(w * ev.mult[picked]) : 0;
      /* ⛔ ONLY NOTCHES THE PLAYER CANNOT EMPTY THEIR BANK WITH.
         Jan: "dont let them wager it all with 1 button". A preset row is
         the thing that makes a slider usable with a thumb, but a preset
         equal to the balance is an All in button wearing a number, so a
         notch is drawn only while it is strictly below the bank. */
      var notch = '';
      for(var si = 0; si < HF_WAGER_STEPS.length; si++){
        var sv = HF_WAGER_STEPS[si];
        if(sv > ceil || sv >= bank) continue;
        notch += '<button type="button" class="hoh-hf-wstep' + (w === sv ? ' on' : '') + '"' +
          ' onclick="hohFreshFacesHfWager(' + sv + ',1)"' +
          ' aria-label="Bet ' + sv + ' hearts">' + sv + '</button>';
      }
      /* ⛔ SAID BEFORE THE TAP, NOT AFTER IT. The whole point of the
         champion is that it changes which side is worth backing, so a
         player who only learns about it once they have already committed
         has been given a surprise instead of a decision. Before the pick
         it is a promise, after it a statement of fact, and it is absent
         entirely for a signed-out visitor who has nobody to send. */
      /* ⛔ THE MIRROR IS NAMED, OR IT LOOKS LIKE A BUG. R594 puts a
         stranger on the far side the moment the player's hero joins
         theirs. A player who is told about their own hero and then
         watches an unexplained extra enemy appear has been shown a
         glitch; a player who is told the sides stay even has been shown
         a rule. And the rule is the good news: their hero is an
         advantage because of what it IS, not because it is one more
         body, which is what makes levelling one worth doing. */
      /* ⛔ A STAT LINE, NOT A PARAGRAPH, AND THAT IS A MEASURED FIX. The
         first cut wrote this as four sentences and it rendered 98px tall
         on a 375x812 phone, pushing the one button that spends money to
         911px down an 812px screen. Splitting it into a WHO row (name,
         level, ability, each its own chip) and one short WHY line reads
         faster and costs a third of the height, because a chip row wraps
         where a sentence cannot. */
      /* ⛔ MEASURED TWICE AND CUT BOTH TIMES. Written as four sentences
         this block was 98px tall on a 375x812 phone; rewritten as a chip
         row it was 102px, because the ability chip pushed the row past
         247px and it WRAPPED. What it costs is what matters, because
         every pixel here pushes the one button that spends money further
         down the screen. So:
           - the ability is NOT named here. It is named in the battle log
             the moment it fires, under its real name, which is a far
             better reveal than a chip nobody reads before a fight.
           - the mirror is explained only AFTER the pick, because that is
             the moment the extra body actually appears. Before the pick
             there is no unexplained enemy to explain. */
      var champLine = '';
      if(ev.champion){
        var who = '<b>' + esc(ev.champion.name) + '</b>' +
          ((ev.champion.lvl > 1) ? '<i class="hoh-hf-chip">Lv ' + ev.champion.lvl + '</i>' : '');
        /* ⛔ THE FAIRNESS PROMISE GOES BEFORE THE PICK, NOT AFTER, AND
           THE REASON IS PIXELS AT THE DECIDING MOMENT. Before the pick
           the player is reading; after it they are betting, and every
           row here pushes the Start button further down the phone.
           Measured: carrying the mirror sentence into the picked state
           cost a third row, 73px against 53px. Said up front it is a
           selling point ("this is a fair fight"); said afterwards it is
           a footnote under a board that already shows even numbers. */
        var why = picked
          ? 'Fighting for ' + esc(ev.sideName[picked]) + '.'
          : 'Joins the side you back.' +
            (ev.mirror ? ' One of theirs joins too, so it stays even.' : '');
        champLine = '<div class="hoh-hf-champline' + (picked ? ' on' : '') + '">' +
          '<span class="hoh-hf-champwho">' + who + '</span>' +
          '<span class="hoh-hf-champwhy">' + why + '</span>' +
        '</div>';
      }
      body +=
        '<div class="hoh-hf-wager">' +
          champLine +
          '<div class="hoh-hf-wtop">' +
            '<label class="hoh-hf-wlabel" for="hohHfWager">Your bet</label>' +
            '<output class="hoh-hf-wval" id="hohHfWval" for="hohHfWager">' + w +
              '<i class="hoh-hf-wheart" aria-hidden="true">&#9829;</i></output>' +
          '</div>' +
          /* ⛔ A RANGE, NOT A NUMBER FIELD. The typed field opened the
             phone keyboard over the board on every tap, needed a clamp
             written back into it, and let a player name a number bigger
             than the ceiling. A track cannot express an illegal value at
             all, which deletes that whole class of bug. */
          /* ⛔ --hfw IS THE FILLED PORTION, AND WITHOUT IT THE TRACK LIES.
             A range input paints one uniform track whatever its value, so
             at 25 of 250 the bar looked identical to 200 of 250 and the
             only way to know where you were was to read the number. On a
             control that spends real currency the thumb's position has to
             be legible at a glance. */
          '<input class="hoh-hf-wslider" id="hohHfWager" type="range"' +
            ' style="--hfw:' + hfWagerPct(w, ceil) + '%"' +
            ' min="' + HF_WAGER_MIN + '" max="' + ceil + '" step="1" value="' + w + '"' +
            ' oninput="hohFreshFacesHfWager(this.value)"' +
            ' onchange="hohFreshFacesHfWager(this.value)"' +
            ' aria-label="How many hearts to bet"' +
            ' aria-valuemin="' + HF_WAGER_MIN + '" aria-valuemax="' + ceil + '"' +
            ' aria-valuenow="' + w + '" aria-describedby="hohHfPayout">' +
          '<div class="hoh-hf-wends" aria-hidden="true">' +
            '<span>' + HF_WAGER_MIN + '</span><span>' + ceil + '</span>' +
          '</div>' +
          (notch ? '<div class="hoh-hf-wsteps">' + notch + '</div>' : '') +
          '<div class="hoh-hf-payout" id="hohHfPayout" aria-live="polite">' +
            (!picked
              ? 'Pick a side above to see what it pays.'
              : 'Backing ' + esc(ev.sideName[picked]) + '. Win and you take <b>' + pay + '</b> hearts back.') +
          '</div>' +
          /* ⛔ A DISABLED BUTTON MUST NAME ITS OWN REASON. With the typed
             field gone the only two states looked like one, and a
             free-entry battle offered to a player holding nothing put
             them on a side with the button still reading "Pick a side".
             Being told to do the thing you have just done is the worst
             possible answer to "why can I not start". */
          '<button type="button" class="hoh-hf-go" ' + (ready ? '' : 'disabled ') +
            'onclick="hohFreshFacesHfStart()">' +
            (ready ? 'Start the fight' : (picked ? 'Not enough hearts' : 'Pick a side')) +
          '</button>' +
          '<div class="hoh-hf-note">You have ' + bank + ' hearts. Nothing is taken until the fight starts.</div>' +
        '</div>';
    } else if(ev.phase === 'coin' || ev.phase === 'coinflip' || ev.phase === 'coinread'){
      body += hfCoinHtml(ev) +
        '<div class="hoh-hf-note">Backing ' + esc(ev.sideName[ev.pickedSide]) +
        ' for ' + ev.wager + ' hearts.</div>';
    } else if(ev.phase === 'fighting'){
      /* ⛔ RENDERS THE ACCUMULATED LOG, NOT AN EMPTY BOX. The first cut
         emitted this empty and relied on hfLogLine appending into the
         live node, so any repaint during the fight would have wiped
         every line the player had watched arrive. Nothing repaints
         mid-fight today, which is precisely why this would have sat
         here as a trap for whoever added the first mid-fight repaint.
         ⛔ NO aria-live EITHER. It carried aria-live="polite" over a box
         that gains a line every ~150ms, which is not an assist, it is a
         screen reader reading out forty blows and falling behind. The
         RESULT is what matters and it is announced on its own. */
      body += '<div class="hoh-hf-log" id="hohHfLog">' + (ev._logHtml || '') + '</div>' +
        '<div class="hoh-hf-note">Backing ' + esc(ev.sideName[ev.pickedSide]) + ' for ' + ev.wager + ' hearts.</div>';
    } else {
      var won = !!ev.won;
      var r = ev.result || {};
      body += '<div class="hoh-hf-log" id="hohHfLog">' + (ev._logHtml || '') + '</div>' +
        /* The one thing worth announcing: the outcome. The blow-by-blow
           log deliberately carries no aria-live. */
        '<div class="hoh-hf-result ' + (won ? 'hoh-hf-win' : 'hoh-hf-loss') + '" role="status" aria-live="polite">' +
          '<div class="hoh-hf-rtitle">' + (won ? 'You read it right.' : 'Not this time.') + '</div>' +
          '<div class="hoh-hf-rsub">' +
            esc(ev.sideName[r.winner || 'A']) + ' took it' +
            (r.aliveWin != null ? ' with ' + r.aliveWin + ' still standing' : '') + '.' +
            (won
              ? ' You win <b>' + ev.paid + '</b> hearts.'
              : ' Your ' + ev.wager + ' is gone.') +
          '</div>' +
          (won && ev.survBonus > 0
            ? '<div class="hoh-hf-rbonus">Includes ' + ev.survBonus + ' for the survivors.</div>'
            : '') +
          '<button type="button" class="hoh-hf-go" onclick="hohFreshFacesHfClose()">Move on</button>' +
        '</div>';
    }
    return '<div class="hoh-fresh-special hoh-hf" data-phase="' + esc(ev.phase) + '">' + head + body + '</div>';
  }

  function hfRepaint(){
    hfTrailClear();
    var c = _ffContainer();
    if(!c || !currentSpecial || !currentSpecial.hfBattle) return;
    c.innerHTML = renderHallFightHtml(currentSpecial);
  }

  /* ---- the wager phase ------------------------------------------- */
  window.hohFreshFacesHfPick = function(side){
    if(!currentSpecial || !currentSpecial.hfBattle) return;
    if(currentSpecial.phase !== 'wager') return;
    if(side !== 'A' && side !== 'B') return;
    var ev = currentSpecial;
    ev.pickedSide = side;
    /* R593: their hero walks over to the side they just backed, and the
       price moves with them. Both halves happen before the paint, so the
       player never sees a board and an odds line that disagree. */
    hfChampionAttach(ev, side);
    hfRepriceBattle(ev);
    if(!ev.wager) ev.wager = ev.suggested;
    ev.wager = Math.max(HF_WAGER_MIN, Math.min(hfWagerCeiling(), ev.wager));
    hfRepaint();
  };
  window.hohFreshFacesHfWager = function(val, repaint){
    if(!currentSpecial || !currentSpecial.hfBattle) return;
    if(currentSpecial.phase !== 'wager') return;
    var ceil = hfWagerCeiling();
    var n = parseInt(val, 10);
    if(isNaN(n)) n = HF_WAGER_MIN;
    /* The track cannot express an out-of-range value, but a notch button
       or a stale repaint can, so the clamp stays and is the only writer
       of ev.wager. */
    if(n < HF_WAGER_MIN) n = HF_WAGER_MIN;
    if(n > ceil) n = ceil;
    currentSpecial.wager = n;
    /* ⛔ DRAGGING MUST NOT REPAINT. Rewriting innerHTML mid-drag destroys
       the very input the thumb is holding, and on a touch screen the
       pointer capture goes with it, so the slider dies the instant it
       moves. Only the read-out, the payout line and the button change in
       place. A notch button passes repaint=1 because it is a discrete
       tap, not a drag, and it needs the row's own "on" state redrawn. */
    if(repaint){ hfRepaint(); return; }
    var ev = currentSpecial;
    var out = document.getElementById('hohHfPayout');
    var go = document.querySelector('.hoh-hf-go');
    var val2 = document.getElementById('hohHfWval');
    var sl = document.getElementById('hohHfWager');
    var bankNow = (typeof window.heartsRemainingToday === 'function') ? window.heartsRemainingToday() : 0;
    var ok = !!ev.pickedSide && n <= bankNow;
    if(val2) val2.innerHTML = n + '<i class="hoh-hf-wheart" aria-hidden="true">&#9829;</i>';
    /* aria-valuenow keeps a screen reader's spoken value honest, and the
       value write-back only fires when the clamp actually bit. */
    if(sl){
      sl.setAttribute('aria-valuenow', String(n));
      if(String(sl.value) !== String(n)) sl.value = String(n);
      /* the filled portion follows the thumb on every drag frame */
      sl.style.setProperty('--hfw', hfWagerPct(n, ceil) + '%');
    }
    if(out){
      if(!ev.pickedSide) out.textContent = 'Pick a side above to see what it pays.';
      else out.innerHTML = 'Backing ' + esc(ev.sideName[ev.pickedSide]) +
        '. Win and you take <b>' + Math.round(n * ev.mult[ev.pickedSide]) + '</b> hearts back.';
    }
    if(go){
      go.disabled = !ok;
      go.textContent = ok ? 'Start the fight' : (ev.pickedSide ? 'Not enough hearts' : 'Pick a side');
    }
    /* The notch row tracks the thumb, so a drag onto exactly 50 lights
       the 50 and dragging away puts it out again. */
    var steps = document.querySelectorAll('.hoh-hf-wstep');
    for(var q = 0; q < steps.length; q++){
      var sn = parseInt(steps[q].textContent, 10);
      if(sn === n) steps[q].classList.add('on'); else steps[q].classList.remove('on');
    }
  };

  window.hohFreshFacesHfStart = function(){
    if(!currentSpecial || !currentSpecial.hfBattle) return;
    if(currentSpecial.phase !== 'wager') return;
    var ev = currentSpecial;
    var bank = (typeof window.heartsRemainingToday === 'function') ? window.heartsRemainingToday() : 0;
    var wager = ev.wager | 0;
    if(!ev.pickedSide || wager < HF_WAGER_MIN) return;
    if(wager > bank){
      try { window.hohShowHeartToast && window.hohShowHeartToast('Not enough hearts', 'You have ' + bank + '.'); } catch(_){}
      return;
    }

    /* ⛔ EVERYTHING THIS FUNCTION STILL NEEDS IS READ INTO A LOCAL
       BEFORE THE DEBIT, AND THAT IS NOT TIDINESS.
       The debit dispatches 'hoh:hearts-bumped' SYNCHRONOUSLY. The
       listener at the top of this file runs syncWalletState, which on a
       balance going >0 to 0 calls performWalletReset, whose FIRST
       statement is `currentSpecial = null`. Control then returns here.
       The old duel handler read currentSpecial.diceMax on the line
       after its debit and would throw on null the moment a player bet
       their whole balance, and coinBet has the same shape. With a typed
       wager, betting the lot stops being an edge case and becomes a
       button ("All in"), so this had to be closed before the button
       could exist. */
    var side = ev.pickedSide;
    var teamsA = ev.teams.A, teamsB = ev.teams.B;
    var mult = ev.mult[side];
    /* ⛔ THE DENOMINATOR IS THE ROSTER THAT FIGHTS, NOT ev.size.
       ev.size is the size the fight was DRAFTED at, and since R593 the
       backed side carries one more body than that. Left as ev.size, a
       side that came through 4 of 4 alive would price its survivor bonus
       as 4/3 and pay more than a clean sweep is worth. */
    var size = (ev.teams[side] || []).length || ev.size;
    var evRef = ev;

    try {
      var raw = parseInt(localStorage.getItem('hoh-heart-bank-v1') || '0', 10) || 0;
      try { window.hohHeartHint && window.hohHeartHint('spend_feature'); } catch(_){}
      localStorage.setItem('hoh-heart-bank-v1', String(Math.max(0, raw - wager)));
      try { document.dispatchEvent(new CustomEvent('hoh:hearts-bumped', { detail: { spent: wager } })); } catch(_){}
    } catch(_){}
    try { window.hohRenderHeaderCounter && window.hohRenderHeaderCounter(); } catch(_){}

    /* If the wallet reset cleared the event out from under us, put it
       back. The run is legitimate: the player paid for it. */
    if(currentSpecial !== evRef) currentSpecial = evRef;

    var sim = hfSimulate(teamsA, teamsB);
    /* ⛔ THE FIRST-VISIT WIN, KEPT ON JAN'S RULING (answer 5A: "keep the
       rigged win"). ANON_TOUR_SEQUENCE sets _alwaysWin on one curated
       step so a signed-out first-time visitor's first wager lands.

       It re-rolls the WHOLE FIGHT until the backed side wins, rather
       than editing a result. That matters: the tape the player watches
       is then a real simulation of a real win, so the blows, the deaths
       and the survivor count all agree with the outcome. Flipping the
       winner on a losing tape would play a fight the player watched
       their side lose and then announce they won.

       Bounded, because the backed side can be a 10% underdog and this
       runs on the main thread. Sixty tries clears a 10% chance about
       998 times in 1000, and on the rare miss the honest fight stands:
       a broken promise beats a frozen page. */
    if(evRef._alwaysWin && sim.winner !== side){
      for(var tries = 0; tries < 60 && sim.winner !== side; tries++){
        sim = hfSimulate(teamsA, teamsB);
      }
    }
    hfRewindRoster(evRef);
    /* ⛔ REWIND HERE, AT THE POINT OF THE MUTATION, NOT IN hfPlayStart.
       hfSimulate runs the whole fight over these very unit objects, so the
       instant it returns every body that will ever die is flagged dead. The
       first cut rewound inside hfPlayStart, which runs AFTER hfRepaint below
       -- so the opening paint of the fight baked a FALLEN badge into the
       markup for every future casualty, and hfMarkFallen only ever ADDS, so
       they never left. Jan saw four of six bodies fallen before a blow
       landed. Rewinding where the damage is done makes every later paint
       correct no matter what order anything else runs in. */
    evRef.tape = sim.events;
    evRef.result = { winner: sim.winner, aAlive: sim.aAlive, bAlive: sim.bAlive, beats: sim.beats };
    evRef.result.aliveWin = (sim.winner === 'A') ? sim.aAlive : sim.bAlive;
    evRef.won = (sim.winner === side);
    var alive = (side === 'A') ? sim.aAlive : sim.bAlive;
    var base = Math.round(wager * mult);
    evRef.survBonus = evRef.won ? Math.round(wager * HF_SURVIVOR_KICK * (alive / Math.max(1, size))) : 0;
    evRef.paid = evRef.won ? (base + evRef.survBonus) : 0;
    /* R592d: the fight opens on a coin the player tosses. */
    evRef.phase = 'coin';
    /* ⛔ _diceRolling HOLDS THE SURFACE FROM HERE, and it has to. The wager
       is already spent by this line, and both the 28.8s auto-refresh and a
       manual refresh would otherwise rotate the paid fight away before it
       started. R318 added this escape for exactly this shape: "a STAKED roll
       mid-animation holds the surface".
       ⛔ It is NOT enough on its own, because the coin waits for a tap and a
       player who walks away would strand a paid bet behind a frozen bar.
       So the coin also tosses ITSELF after HF_COIN_AUTO_MS. Nothing here can
       wait forever. */
    evRef._diceRolling = true;
    evRef._logHtml = '';
    hfRepaint();
    hfCoinStopTimer();
    (function(){
      var tok = hfPlayToken;
      hfCoinTimer = setTimeout(function(){
        if(tok !== hfPlayToken) return;
        if(currentSpecial && currentSpecial.phase === 'coin') {
          try { window.hohFreshFacesHfCoin(); } catch(_){}
        }
      }, HF_COIN_AUTO_MS);
    })();
  };

  /* ---- playback --------------------------------------------------- */
  /* ================================================================ */
  /* R592d (Jan): THE COIN, AND THE PLAYER TOSSES IT                   */
  /* ================================================================ */
  /* "make sure when the battle starts there is a coinflip and the player
     clicks the coinflip to see if their side starts."

     ⛔ THE COIN IS ALREADY IN THIS FILE. The Thieves' Coin event has a
     working 3D coin: .hoh-fresh-coin with preserve-3d, two faces at
     backface-visibility:hidden, and hohFreshCoinFlip spinning 1620deg with
     a hop (hall-of-heroes.css 9702-9737). Same file, same namespace, same
     visual language, and already proven on a phone. Copying the
     Battlegrounds one across files would have been strictly worse.

     ⛔ THE RESULT IS NOT ROLLED HERE. The tape already carries a 'coin'
     event from hfSimulate, decided when the fight was simulated. The coin
     on screen REVEALS that, exactly as the die reveals a roll the tape
     already holds. A second Math.random() here would be a different answer
     to a question already settled, and the fight would open on the wrong
     side. */
  var HF_COIN_FLIP_MS = 1400;   /* matches hohFreshCoinFlip's own duration */
  var HF_COIN_READ_MS = 1150;   /* the beat to read the verdict before the first blow */
  var HF_COIN_AUTO_MS = 9000;   /* it tosses itself rather than stranding a paid bet */
  var hfCoinTimer = null;

  function hfCoinStopTimer(){
    if(hfCoinTimer){ try { clearTimeout(hfCoinTimer); } catch(_){} hfCoinTimer = null; }
  }
  /* Which side opens, read off the tape. Returns 'A' | 'B' | null. */
  function hfOpeningSide(ev){
    if(!ev || !ev.tape) return null;
    for(var i = 0; i < ev.tape.length; i++){
      if(ev.tape[i].e === 'coin') return ev.tape[i].a ? 'A' : 'B';
    }
    return null;
  }
  /* HEADS means the side the player BACKED opens. The question a player
     actually has is "do I start", not "did an arbitrary letter start", so
     the two faces are named from where they are standing. */
  function hfCoinIsHeads(ev){
    var open = hfOpeningSide(ev);
    return !!(open && ev.pickedSide && open === ev.pickedSide);
  }

  function hfCoinHtml(ev){
    var phase = ev.phase;
    var landed = (phase === 'coinread');
    var heads = hfCoinIsHeads(ev);
    var faceCls = landed ? (heads ? ' hoh-fresh-coin-heads' : ' hoh-fresh-coin-tails') : '';
    var flipping = (phase === 'coinflip') ? ' hoh-fresh-coin-flipping' : '';
    var result = '';
    if(landed){
      var open = hfOpeningSide(ev);
      result = '<div class="hoh-fresh-coin-result ' +
          (heads ? 'hoh-fresh-coin-result-win' : 'hoh-fresh-coin-result-lose') + '">' +
        '<span class="hoh-fresh-coin-result-title">' +
          (heads ? 'You throw the first blow.' : 'They throw the first blow.') +
        '</span>' +
        '<span class="hoh-fresh-coin-result-sub">' +
          esc(ev.sideName[open || 'A']) + ' opens.</span>' +
      '</div>';
    }
    var action = '';
    if(phase === 'coin'){
      action = '<div class="hoh-fresh-coin-actions">' +
        '<button type="button" class="hoh-fresh-coin-toss" onclick="hohFreshFacesHfCoin()">' +
          '<span>Toss for the first blow</span>' +
        '</button></div>';
    }
    return '<div class="hoh-fresh-coin-stage hoh-hf-coinstage">' +
      '<div class="hoh-fresh-coin' + faceCls + flipping + '" aria-hidden="true">' +
        '<div class="hoh-fresh-coin-face hoh-fresh-coin-face-heads"><span>&#9812;</span></div>' +
        '<div class="hoh-fresh-coin-face hoh-fresh-coin-face-tails"><span>&#9819;</span></div>' +
      '</div>' +
      result +
      action +
    '</div>';
  }

  /* The tap. Spends nothing: the wager was taken when the side was locked
     in, and this is the ceremony that opens the fight. */
  window.hohFreshFacesHfCoin = function(){
    if(!currentSpecial || !currentSpecial.hfBattle) return;
    if(currentSpecial.phase !== 'coin') return;
    hfCoinStopTimer();
    var ev = currentSpecial;
    ev.phase = 'coinflip';
    hfRepaint();
    /* ⛔ EVERY HOP IS TOKEN-GUARDED. A player can leave, or the day can
       roll, between the toss and the first blow. Without the guard this
       chain would paint onto whatever event replaced it. */
    var tok = ++hfPlayToken;
    hfCoinTimer = setTimeout(function(){
      if(tok !== hfPlayToken) return;
      if(!currentSpecial || currentSpecial.phase !== 'coinflip') return;
      currentSpecial.phase = 'coinread';
      hfRepaint();
      /* the landing throws sparks in the opening side's colour, the way the
         real board's toss does (ui.js 20427: sparks(ce, 14, ...)) */
      try {
        var _c = document.querySelector('.hoh-hf-coinstage .hoh-fresh-coin');
        var _open = hfOpeningSide(currentSpecial);
        if(_c) hfSparks(_c, 14, _open === 'A' ? '#c9a84c' : '#b46bff');
      } catch(_){}
      hfCoinTimer = setTimeout(function(){
        if(tok !== hfPlayToken) return;
        if(!currentSpecial || currentSpecial.phase !== 'coinread') return;
        currentSpecial.phase = 'fighting';
        hfRepaint();
        hfPlayStart();
      }, HF_COIN_READ_MS);
    }, HF_COIN_FLIP_MS);
  };

  function hfPlayStop(){
    if(hfPlayTimer){ try { clearTimeout(hfPlayTimer); } catch(_){} hfPlayTimer = null; }
    hfCoinStopTimer();          /* a pending coin hop is part of this tape too */
    hfTrailClear();             /* cancels both travel and its delayed impact */
    hfPlayToken++;
  }
  function hfLogLine(ev, html){
    ev._logHtml = (ev._logHtml || '') + '<div class="hoh-hf-line">' + html + '</div>';
    var el = document.getElementById('hohHfLog');
    if(el){
      el.insertAdjacentHTML('beforeend', '<div class="hoh-hf-line">' + html + '</div>');
      /* newest at the bottom, and keep it in view without scrolling the page */
      el.scrollTop = el.scrollHeight;
    }
  }
  function hfReduced(){
    try { return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches; }
    catch(_){ return false; }
  }
  /* ui.js 20734. The "0 means dead" law: a living side never shows 0. */
  function hfTick(id, v, hurt){
    var el = document.getElementById(id);
    if(!el) return;
    var n = parseInt(v, 10); if(isNaN(n)) n = 0;
    el.textContent = n;
    el.classList.remove('bump','hurt');
    void el.offsetWidth;                       /* the forced reflow that lets the same class re-fire */
    el.classList.add(hurt ? 'hurt' : 'bump');
  }
  /* ⛔ ABSOLUTES OFF THE STAMP, NEVER ACCUMULATED DELTAS. The engine's R344
     comment records a UI that added up damage as it played and drifted in
     99.3% of simulated battles, mean error 31 HP. Two scalars now, not two
     arrays of per-unit health. */
  function hfApplyStamp(e){
    if(!currentSpecial || !currentSpecial.hfBattle) return;
    if(e.ha == null) return;
    var t = currentSpecial.teams;
    currentSpecial.pool = { A: e.ha, B: e.hb };
    ['A','B'].forEach(function(sd){
      var el = document.getElementById('hohHfT' + sd);
      if(!el) return;
      var want = (sd === 'A') ? e.ha : e.hb;
      var cur = parseInt(el.textContent, 10); if(isNaN(cur)) cur = -1;
      if(cur !== want) hfTick('hohHfT' + sd, want, want < cur);
    });
    /* ⛔ DEATH IS NOT INFERRED HERE, AND THAT IS A BUG FIX.
       hfSimulate MUTATES the very unit objects this screen renders, so the
       moment the tape is built every body that will ever die is already
       flagged dead. The first cut asked the units, and the board opened
       with every portrait wearing FALLEN while both pools still read full.
       (The old per-unit model hid this because the stamp carried per-unit
       health ARRAYS and rewrote `dead` from them on every step. With one
       scalar a side there is nothing to rewrite it from.)
       Death is played from the 'fell' EVENT, in hfPlayStep, as it arrives. */
  }
  /* Called when a 'fell' event plays. Idempotent. */
  function hfMarkFallen(side, idx){
    if(!currentSpecial || !currentSpecial.hfBattle) return;
    var team = currentSpecial.teams[side] || [];
    if(team[idx]) team[idx].dead = true;
    var tile = document.getElementById('hohHfU' + side + idx);
    if(!tile || tile.classList.contains('hoh-hf-fell')) return;
    tile.classList.add('hoh-hf-fell','gone');
    if(!tile.querySelector('.bfallen')){
      var badge = document.createElement('span');
      badge.className = 'bfallen'; badge.textContent = 'FALLEN';
      tile.appendChild(badge);
    }
  }
  /* THE QUAKE. ui applies it to the board for 320ms, 420 on a big blow. */
  function hfQuake(big){
    if(hfReduced()) return;
    var b = document.getElementById('hohHfBoard');
    if(!b) return;
    b.classList.remove('hoh-hf-quake','big');
    void b.offsetWidth;
    b.classList.add('hoh-hf-quake');
    if(big) b.classList.add('big');
    setTimeout(function(){
      try { b.classList.remove('hoh-hf-quake','big'); } catch(_){}
    }, big ? 430 : 330);
  }
  /* THE DAMAGE NUMBER RISES OFF THE STRUCK BAND, not out of a text slot in
     the middle. This is the single most-missed piece of the real board's
     feel: a number that floats up off the side that just took it. */
  function hfDmgPop(side, txt, crit, mend){
    if(hfReduced()) return;
    var host = document.querySelector('.hoh-hf-side[data-side="' + side + '"]');
    if(!host) return;
    var d = document.createElement('span');
    /* `mend` paints the number green. Without it a heal rose off the band
       in the damage salmon, which is the board saying "hurt" about the
       one beat that helps. */
    d.className = 'hoh-hf-dmgpop' + (crit ? ' crit' : '') + (mend ? ' mend' : '');
    d.setAttribute('aria-hidden','true');
    d.textContent = txt;
    host.appendChild(d);
    setTimeout(function(){ try { d.remove(); } catch(_){} }, 1100);
  }
  /* NATURAL 20. ⛔ The banner goes on the BOARD's centre, never on a side:
     ui.js 5324 records it first hung off .hoc-side and rendered BEHIND two
     hero tiles at 375px. */
  function hfNatBanner(){
    if(hfReduced()) return;
    var stage = document.getElementById('hohHfBoard');
    if(!stage) return;
    var b = document.createElement('div');
    b.className = 'hoh-hf-nat20'; b.setAttribute('aria-hidden','true');
    b.textContent = 'NATURAL 20';
    stage.appendChild(b);
    setTimeout(function(){ try { b.remove(); } catch(_){} }, 1150);
  }
  function hfSparks(el, n, col){
    try {
      if(!el || !window.hohFx || !window.hohFx.emit) return;
      var r = el.getBoundingClientRect();
      window.hohFx.emit('sparks', { x: r.left + r.width / 2, y: r.top + r.height / 2, n: n || 10, color: col });
    } catch(_){}
  }
  function hfPlayStart(){
    hfPlayStop();
    var token = hfPlayToken;
    var ev = currentSpecial;
    if(!ev || !ev.tape) return;
    ev.tapeIdx = 0;
    /* ⛔ REWIND THE ROSTER. The simulator ran to completion over these same
       objects, so they are all sitting at their end state. Playback starts
       from the muster, which means everybody is standing. */
    ['A','B'].forEach(function(sd){
      (ev.teams[sd] || []).forEach(function(u){ u.dead = false; });
    });
    ev.pool = { A: hfPoolMax(ev.teams.A), B: hfPoolMax(ev.teams.B) };
    /* ⛔ AND SCRUB THE DOM, not just the model. This is a belt-and-braces
       net for a bug that has now bitten twice: if ANY path paints the board
       before the roster is rewound, the opening frame carries a FALLEN badge
       for every body that will ever die, and hfMarkFallen only ever ADDS so
       nothing takes them off. MEASURED with the rewind one line too late:
       8 of 10 bodies greyed out while both pools still read 146. The player
       reads that as "the fight already happened", which is exactly what Jan
       reported. The model fix above is the real one; this makes the failure
       impossible to see even if the ordering is broken again. */
    try {
      var _c = _ffContainer();
      if(_c){
        var stale = _c.querySelectorAll('.hoh-hf-fi.hoh-hf-fell, .hoh-hf-fi .bfallen');
        for(var q = 0; q < stale.length; q++){
          var n = stale[q];
          if(n.classList.contains('bfallen')) n.remove();
          else n.classList.remove('hoh-hf-fell','gone');
        }
      }
    } catch(_){}
    var beatCount = 0;
    for(var i = 0; i < ev.tape.length; i++) if(ev.tape[i].e === 'beat') beatCount++;
    /* Pace to a DURATION, not a fixed ms per beat. A 1v1 resolves in
       about eight beats and a 6v6 in sixty, so a constant per-beat pace
       would make one a two-second blur and the other a minute. */
    var per = Math.max(HF_BEAT_MIN, Math.min(HF_BEAT_MAX, Math.round(HF_TARGET_MS / Math.max(1, beatCount))));
    ev._perBeat = per;
    hfPlayStep(token);
  }
  function hfPlayStep(token){
    /* ⛔ THE R479 TRAP. Every branch below MUST fall through to the scheduler
       at the BOTTOM. A bare return in the middle does not skip one event, it
       STOPS THE TAPE, and the player is left staring at a frozen board with
       their hearts already spent. Jan's report of the original: "after a
       character got stunned the entire fight just paused and nothing is
       moving anymore." The only legal early exits are the two generation
       guards on the next two lines, and the hfFinish hand-off. */
    if(token !== hfPlayToken) return;
    if(!currentSpecial || !currentSpecial.hfBattle || currentSpecial.phase !== 'fighting') return;
    var ev = currentSpecial;
    var e = ev.tape[ev.tapeIdx++];
    if(!e){ hfFinish(); return; }

    var base = ev._perBeat || 260;
    var wait = base;

    try {
      /* A damaging/healing beat hands its absolute health stamp to the
         arrival callback. Misses and utility beats still stamp immediately.
         That makes the visible cause (travel) precede the visible result
         (shared-health tick, flinch/mend and floating number). */
      if(!hfTrailOwnsStamp(e)) hfApplyStamp(e);
      var die = document.getElementById('hohHfDie');
      var dl  = document.getElementById('hohHfDL');
      var ds  = document.getElementById('hohHfDSide');
      var sideCol = (e.s === 'A') ? '#c9a84c' : '#b46bff';

      if(e.e === 'muster'){
        wait = 90;
        if(dl){ dl.textContent = 'the muster'; dl.style.color = ''; }
      } else if(e.e === 'coin'){
        /* the toss was its own ceremony before the fight, so this is just
           the labels catching up. It does not get a beat of its own. */
        wait = 140;
        if(dl){ dl.textContent = 'first blow'; dl.style.color = ''; }
        if(ds){ ds.textContent = e.a ? ev.sideName.A : ev.sideName.B; ds.style.color = e.a ? '#c9a84c' : '#b46bff'; }
      } else if(e.e === 'beat'){
        var team = ev.teams[e.s];
        var actor = team && team[e.i];
        var dsz = (e.die | 0) || 20;
        /* ⛔ THE CENTRE COLUMN NAMES THE MOVE, NOT THE BODY. R594. The
           board already says WHO is acting: their tile lights up, and the
           log line carries their name. What the screen could not say
           before is WHAT they are doing, which is the entire point of
           giving every class its three moves. So the label above the die
           is the move, the label below is the side, and the tile is the
           actor. The move name falls back to the body's first name if a
           tape predates this (a saved event object mid-fight). */
        if(dl){
          dl.textContent = e.mv
            ? String(e.mv).toUpperCase()
            : String((actor && actor.name) || '').toUpperCase().split(' ')[0];
          dl.style.color = e.own ? '#f2d98e' : sideCol;
          /* the player's OWN rolled ability is the one thing on this board
             they paid for, so it gets the gold and a flash */
          if(e.own && !hfReduced()){
            dl.classList.remove('hoh-hf-dlbl-own');
            void dl.offsetWidth;
            dl.classList.add('hoh-hf-dlbl-own');
          } else {
            dl.classList.remove('hoh-hf-dlbl-own');
          }
        }
        /* the right label names the SIDE that is rolling. The real board says
           "your roll" / "their roll" because one side belongs to the player.
           Here the player backs a side rather than owning one, so it names
           the side instead. */
        if(ds){ ds.textContent = ev.sideName[e.s]; ds.style.color = sideCol; }

        var sideCls = (e.s === 'A') ? ' side-a' : ' side-b';
        var shpCls  = ' hoh-hf-shp-d' + dsz;
        /* the tile that is acting lights up */
        var actEl = document.getElementById('hohHfU' + e.s + e.i);
        if(actEl && !hfReduced()){
          actEl.classList.add('act');
          setTimeout(function(){ try { actEl.classList.remove('act'); } catch(_){} }, Math.round(base * 0.9));
        }

        if(die && !hfReduced()){
          die.className = 'hoh-hf-die tmb' + sideCls + shpCls;
          /* The flicker rides the same pace as the beat it belongs to, and is
             bounded below so the roll never becomes a frame nobody sees. */
          var tick  = Math.max(46, Math.round(base * 0.09));
          /* the tumble takes the first 45% of the beat, so the value it lands
             on is READ for the rest of it rather than glimpsed */
          var ticks = Math.max(3,  Math.round(base / Math.max(1, tick) * 0.45));
          var fl = 0;
          var t2 = setInterval(function(){
            /* ⛔ THE GENERATION LATCH. A tumble that outlives its own beat
               would scribble over the next roll on the same shared die. */
            if(token !== hfPlayToken){ clearInterval(t2); return; }
            die.textContent = 1 + Math.floor(Math.random() * dsz);
            fl++;
            if(fl > ticks){
              clearInterval(t2);
              /* ⛔ THE TWO-DICE RULE. e.roll is the body's OWN power die and
                 is the number on screen. e.hit is the contact d20. ui.js
                 21357 records the bug from crossing them: a NATURAL 20 flare
                 painted over a die reading 3. The die celebrates only when
                 the DIE is on its own top face. The contact 20 gets a
                 banner on the board, never a treatment on this die. */
              /* ⛔ AND THE ROLL HAS TO HAVE BEEN SPENT. R594 gave every
                 body utility moves whose branch never reads the roll, so
                 GUARD, MARK, HEX and SMOKE STEP tumble a die that changes
                 nothing. Celebrating a natural maximum on one of those is
                 the same class of lie as R592's flare over a die reading
                 3: the board cheering for a number it then discarded.
                 `spent` is stamped by the simulator, which is the only
                 thing that knows whether the roll was used, so the rule
                 is not re-listed here as a second copy of the move types.
                 An older tape has no `spent` field, so its absence keeps
                 the pre-R594 behaviour rather than silencing every die. */
              var perf = (e.roll | 0) === dsz && (e.spent === undefined || !!e.spent);
              var n20  = (e.hit | 0) === 20;
              die.className = 'hoh-hf-die land' + sideCls + shpCls +
                (e.kind === 'crit' || e.kind === 'nat20' ? ' crit' : '') + (perf ? ' nat' : '');
              die.textContent = e.roll;
              if(perf){
                die.classList.remove('natpop'); void die.offsetWidth; die.classList.add('natpop');
                hfSparks(die, 16, '#e8c674');
              } else if(e.kind === 'crit' || e.kind === 'nat20'){
                hfSparks(die, 10, '#e8c674');
              }
              if(n20) hfNatBanner();
            }
          }, tick);
        } else if(die){
          die.className = 'hoh-hf-die' + shpCls;
          die.textContent = e.roll;
        }

        /* Travel and impact are one beat. Attacks fly to the opposing
           pooled-health mark; a mend returns to the actor's own mark. The
           old hit/mend flinch and floating number now begin at ARRIVAL,
           instead of appearing before the travelling effect that explains
           them. Misses still travel but deliberately schedule no impact. */
        hfAttackTrail(e, actEl, token, base);
        hfHealTrail(e, actEl, token, base);

        /* ⛔ EVERY KIND GETS A SENTENCE. The old line had five words for
           five kinds and fell through to "hits" for anything else, so the
           six new move outcomes would every one of them have read as a
           plain hit and the whole variety would have been invisible in
           the one place a player actually reads what happened. */
        var nm = '<b>' + esc(actor ? actor.name : '?') + '</b>';
        var mvn = e.mv ? ' <i class="hoh-hf-mv' + (e.own ? ' own' : '') + '">' +
          esc(String(e.mv)) + '</i>' : '';
        var line;
        if(e.kind === 'heal')        line = nm + ' calls' + mvn + (e.heal ? ' and mends ' + e.heal : '');
        else if(e.kind === 'hold')   line = nm + ' braces with' + mvn;
        else if(e.kind === 'buff')   line = nm + ' readies' + mvn;
        else if(e.kind === 'debuff') line = nm + ' lands' + mvn + ' on them';
        else if(e.kind === 'dodge')  line = nm + ' slips into' + mvn;
        else if(e.kind === 'dodged') line = nm + ' swings' + mvn + ' and they slip it';
        else if(e.kind === 'guarded')line = nm + ' hits through the guard with' + mvn + ' for ' + e.dmg;
        else if(e.kind === 'miss')   line = nm + ' misses with' + mvn;
        else if(e.kind === 'glance') line = nm + ' clips them with' + mvn + ' for ' + e.dmg;
        else if(e.kind === 'nat20')  line = nm + ' lands a perfect' + mvn + ' for ' + e.dmg;
        else if(e.kind === 'crit')   line = nm + ' crits with' + mvn + ' for ' + e.dmg;
        else line = nm + ((e.hits > 1) ? ' flurries' : ' hits') + ' with' + mvn +
          (e.dmg ? ' for ' + e.dmg : '');
        hfLogLine(ev, line);
      } else if(e.e === 'fell'){
        /* the fight is over if this was the last body: let it land */
        /* ⛔ THESE HOLDS ARE CAPPED IN ABSOLUTE MILLISECONDS, NOT SCALED.
           Jan: "at the end of the battle it seems to lag for a few seconds
           before deciding the winner". Scaling them off `base` was exactly
           backwards: base is the per-beat pace, and it is LARGEST when the
           fight is shortest (a 1v1 sits on the 900ms ceiling), so the fight
           with the fewest beats got the longest wait.
           MEASURED as shipped, from the killing blow to the verdict:
             1v1  base 900  ->  2880 + 520 = 3.40s
             3v3  base 606  ->  1939 + 520 = 2.46s
             6v6  base 333  ->  1066 + 520 = 1.59s
           The killing blow still gets a beat to land, it just no longer gets
           three of them. Now 0.86s to 1.26s across every size. */
        wait = e.last
          ? Math.max(600, Math.min(1000, Math.round(base * 1.5)))
          : Math.max(380, Math.min(700,  Math.round(base * 1.2)));
        if(dl){ dl.textContent = 'fallen'; dl.style.color = '#e8c674'; }
        if(ds){ ds.textContent = esc(e.name); ds.style.color = '#e8c674'; }
        hfMarkFallen(e.s, e.i);
        var fell = document.getElementById('hohHfU' + e.s + e.i);
        if(fell) hfSparks(fell, 12, '#e8c674');
        hfQuake(true);
        hfLogLine(ev, '<span class="hoh-hf-fellline">' + esc(e.name) + ' falls' +
          (e.soak ? ', and the line closes up' : '') + '.</span>');
      } else if(e.e === 'end'){
        /* the verdict is already on its way in; this is the breath before it,
           not a pause to fill */
        wait = 260;
        if(dl){ dl.textContent = 'the field'; dl.style.color = ''; }
        if(ds){ ds.textContent = ev.sideName[e.w]; ds.style.color = e.w === 'A' ? '#c9a84c' : '#b46bff'; }
        var winEl = document.querySelector('.hoh-hf-side[data-side="' + e.w + '"]');
        var loseEl = document.querySelector('.hoh-hf-side[data-side="' + (e.w === 'A' ? 'B' : 'A') + '"]');
        if(winEl) winEl.classList.add('win');
        if(loseEl) loseEl.classList.add('lose');
      }
    } catch(_){ /* a painter must never stop the tape */ }

    hfPlayTimer = setTimeout(function(){ hfPlayStep(token); }, wait);
  }
  function hfFinish(){
    hfPlayStop();
    var ev = currentSpecial;
    if(!ev || !ev.hfBattle) return;
    if(ev.phase === 'resolved') return;       /* idempotent, callable from anywhere */
    ev.phase = 'resolved';
    ev._diceRolling = false;
    if(ev.won && ev.paid > 0){
      try { window.hohHeartHint && window.hohHeartHint('earn_explore_special'); } catch(_){}
      try { window.hohAddHeartsToBank && window.hohAddHeartsToBank(ev.paid); } catch(_){}
      try { window.hohShowHeartToast && window.hohShowHeartToast('You win ' + ev.paid + ' hearts', esc(ev.sideName[ev.result.winner]) + ' took it.'); } catch(_){}
    }
    hfRepaint();
    if(typeof startAutoTimer === 'function') setTimeout(startAutoTimer, 0);
  }
  window.hohFreshFacesHfClose = function(){
    hfPlayStop();
    currentSpecial = null;
    if(typeof window.hohFreshFacesRefresh === 'function'){
      try { window.hohFreshFacesRefresh({ fromInteractive: true }); } catch(_){}
    }
  };
  /* Exposed so a debug surface or a future test can end a fight
     without reaching into the module. */
  window.hohFreshFacesHfForceFinish = hfFinish;
  /* Synthesize d20 rolls so the winner index actually wins. Winner
     gets a 17-20 (clearly high), losers get 1-15 (no ties, no near-
     misses that look like the dice were rigged the other way). */
  window.hohFreshFacesBarFightContinue = function(){
    if(!currentSpecial || currentSpecial.type !== 'barFight') return;
    if(currentSpecial.phase !== 'resolved') return;
    currentSpecial = null;
    if(typeof window.hohFreshFacesRefresh === 'function'){
      try { window.hohFreshFacesRefresh({ fromInteractive: true }); } catch(_){}
    }
  };

  /* ================================================================ */
  /* R120-F: Bustling Streets — discovery event                       */
  /* ================================================================ */
  /* Two rows of square character portraits scrolling sideways. Every
     non-hidden hero passes through. Each card has its rarity tier as
     a colored border so the user can see who's who at a glance. Tap
     a card to give 1 heart to that hero — pink-locks the card so a
     second tap on the same hero is blocked. 10-heart budget per
     event total; once spent, a Continue button appears.

     The event uses the standard window.hohHeart pipeline so all the
     usual flows fire (heart attribution, daily-cap, animations,
     wallet decrement). The wander bar increments via bumpFfHeartsToday
     so the user sees the daily progress bar fill as they spend. */
  /* R122-H: Cap dropped 10 → 5 per the user's spec. The user noted
     the budget wasn't being enforced — the click handler now hard-
     gates on this constant AND the hearted set, with both checked
     before any spend. */
  var BUSTLING_BUDGET = 5;
  var BUSTLING_MIN_CHARS = 8;   /* don't fire if hall is too thin */
  var BUSTLING_MAX_RAIL = 60;   /* keep DOM cost reasonable on huge halls */
  function pickBustlingStreetsEvent(){
    var bank = (typeof window.heartsRemainingToday === 'function') ? window.heartsRemainingToday() : 0;
    /* Need at least 1 heart to spend or the event has nothing to do.
       Don't gate on full budget — partial spend is fine. */
    if(bank < 1) return null;
    var allChars = (typeof window.hohGetAllCharacters === 'function') ? window.hohGetAllCharacters() : [];
    if(!allChars || allChars.length < BUSTLING_MIN_CHARS) return null;
    var visible = allChars.filter(function(c){ return c && c.id && !c.is_hidden; });
    if(visible.length < BUSTLING_MIN_CHARS) return null;
    /* Shuffle so the rail order is fresh each fire. Cap to keep two
       rows from costing too much on a 1000-char hall. */
    var shuffled = hfShuffle(visible);
    var pool = shuffled.slice(0, Math.min(BUSTLING_MAX_RAIL, shuffled.length));
    return {
      type: 'bustlingStreets',
      chars: pool,
      budget: BUSTLING_BUDGET,
      spent: 0,
      hearted: {},
      phase: 'idle',
      _eventKey: 'bustlingStreets'
    };
  }
  /* ================================================================ */
  /* R140-D: Themed Bustling Streets variants                          */
  /* ================================================================ */
  /* Three new "filtered" rolls that share the Bustling Streets shape
     (two-row scrolling rail, 5-heart budget, tap-to-heart) but pull
     from a narrower pool:
       - Uncommon Promenade  → only uncommon-tier chars,  green theme
       - Sapphire Procession → only rare-tier chars,      blue theme
       - Hall of Portraits   → only is_ff_portrait chars, brand theme
     Each carries a `variant` flag the renderer reads to apply a theme
     class on the wrapper, plus a custom eyebrow + sub copy. The dispatch
     and click handler still see `type: 'bustlingStreets'` so they work
     unchanged — the only thing that differs is the filter + theme. */
  var BUSTLING_VARIANT_MIN = 6; /* allow slightly thinner pools so rare-only rolls can still fire */
  function _buildBustlingVariant(opts){
    var bank = (typeof window.heartsRemainingToday === 'function') ? window.heartsRemainingToday() : 0;
    if(bank < 1) return null;
    var allChars = (typeof window.hohGetAllCharacters === 'function') ? window.hohGetAllCharacters() : [];
    if(!allChars || !allChars.length) return null;
    var visible = allChars.filter(function(c){
      if(!c || !c.id || c.is_hidden) return false;
      return opts.predicate(c);
    });
    if(visible.length < BUSTLING_VARIANT_MIN) return null;
    var shuffled = hfShuffle(visible);
    var pool = shuffled.slice(0, Math.min(BUSTLING_MAX_RAIL, shuffled.length));
    return {
      type: 'bustlingStreets',
      variant: opts.variant,
      eyebrow: opts.eyebrow,
      sub: opts.sub,
      chars: pool,
      budget: BUSTLING_BUDGET,
      spent: 0,
      hearted: {},
      phase: 'idle',
      _eventKey: opts.eventKey
    };
  }
  function _tierOf(c){
    try {
      if(typeof window.hohGetRarity === 'function'){
        var r = window.hohGetRarity(c.hearts || 0);
        return (r && r.tier) || 'common';
      }
    } catch(_){}
    return 'common';
  }
  function pickBustlingStreetsUncommonEvent(){
    return _buildBustlingVariant({
      variant: 'uncommon',
      eventKey: 'bustlingStreetsUncommon',
      eyebrow: 'The Green Promenade',
      sub: 'Up-and-comers shoulder through the lane. Heart who you want — there are ' + BUSTLING_BUDGET + ' to give before the crowd thins.',
      predicate: function(c){ return _tierOf(c) === 'uncommon'; }
    });
  }
  function pickBustlingStreetsRareEvent(){
    return _buildBustlingVariant({
      variant: 'rare',
      eventKey: 'bustlingStreetsRare',
      eyebrow: 'Sapphire Procession',
      sub: 'A rarer crowd tonight. Coats trimmed in blue. ' + BUSTLING_BUDGET + ' hearts to spend before the procession passes.',
      predicate: function(c){ return _tierOf(c) === 'rare'; }
    });
  }
  function pickBustlingStreetsPortraitEvent(){
    return _buildBustlingVariant({
      variant: 'portrait',
      eventKey: 'bustlingStreetsPortrait',
      eyebrow: 'A Painted Parade',
      sub: 'Faces under brand-lit lanterns. ' + BUSTLING_BUDGET + ' hearts before the crowd moves on.',
      predicate: function(c){ return !!c.is_ff_portrait; }
    });
  }
  /* R140-Z: 4 new themed Bustling Streets variants — same 5-heart rail
     mechanic, narrower pool by class or race. The class / race strings
     vary by case across the hall (e.g. "Wizard" vs "wizard"); we
     normalize before matching so a thin hall still surfaces these. */
  function _ciIncludes(list, val){
    if(!val) return false;
    var v = ('' + val).toLowerCase().trim();
    for(var i = 0; i < list.length; i++){
      if(list[i].toLowerCase() === v) return true;
    }
    return false;
  }
  function pickBustlingStreetsCastersEvent(){
    var classes = ['Wizard', 'Sorcerer', 'Warlock'];
    return _buildBustlingVariant({
      variant: 'casters',
      eventKey: 'bustlingStreetsCasters',
      eyebrow: 'Magic Caster Parade',
      sub: 'Spellcasters spill into the lane, sparks trailing their hems. ' + BUSTLING_BUDGET + ' hearts before the cantrips fade.',
      predicate: function(c){ return _ciIncludes(classes, c.character_class); }
    });
  }
  function pickBustlingStreetsBeastfolkEvent(){
    var races = ['Tabaxi', 'Harengon', 'Dragonborn', 'Goliath'];
    return _buildBustlingVariant({
      variant: 'beastfolk',
      eventKey: 'bustlingStreetsBeastfolk',
      eyebrow: 'Beastfolk Procession',
      sub: 'Fur, scale, and stride. The Hall steps aside. ' + BUSTLING_BUDGET + ' hearts before they thunder past.',
      predicate: function(c){ return _ciIncludes(races, c.race); }
    });
  }
  function pickBustlingStreetsClergyEvent(){
    var classes = ['Cleric', 'Paladin'];
    return _buildBustlingVariant({
      variant: 'clergy',
      eventKey: 'bustlingStreetsClergy',
      eyebrow: 'Cleric & Paladin March',
      sub: 'Holy steel and held prayers. ' + BUSTLING_BUDGET + ' hearts before the procession turns the corner.',
      predicate: function(c){ return _ciIncludes(classes, c.character_class); }
    });
  }
  function pickBustlingStreetsDragonbornEvent(){
    /* R148-Z15: Pool expanded beyond pure Dragonborn — too thin in
       most halls to fill a procession. Kobold + Lizardfolk are the
       natural scaled-folk kin (Kobolds are draconic-blooded in 5e
       lore; Lizardfolk are reptilian cousins). The Half-Dragon
       variants are pulled in too so any scaled lineage fits the
       lantern-and-scales aesthetic. Doesn't overlap with the
       "Dragon's Hoard" pool — that one prioritizes pure Dragon /
       Dragonblooded lineages; this Vault leans scaled-folk. */
    var races = [
      'Dragonborn',
      'Half-Dragon',
      'Half Dragon Dragonborn',
      'Kobold',
      'Lizardfolk',
      'Black Dragonborn'
    ];
    return _buildBustlingVariant({
      variant: 'dragonborn',
      eventKey: 'bustlingStreetsDragonborn',
      eyebrow: 'Dragonborn Vault',
      sub: 'Scales catch the lanternlight in the vault below. ' + BUSTLING_BUDGET + ' hearts before the hoard reseals.',
      predicate: function(c){ return _ciIncludes(races, c.race); }
    });
  }
  /* R140-Z3n: 5 NEW Bustling Streets variants. Picked strategically
     from the 25 themed-roll list for themes where MOVEMENT is part
     of the narrative — dragons in procession, the wild hunt chasing
     across terrain, bards and rogues threading a marketplace,
     beastfolk gathering on the move, fey dancing in procession. */
  function pickBustlingStreetsDragonHoardEvent(){
    var races = ['Dragonborn','Dragon','Half-Dragon','Half Dragon Dragonborn','Trueborn Dragonkin','Black Dragonborn','Dragonblooded Elf','Draconis','Dragon (Transfigured Human)'];
    return _buildBustlingVariant({
      variant: 'dragonHoard',
      eventKey: 'bustlingStreetsDragonHoard',
      eyebrow: "The Dragon's Hoard",
      sub: 'Scales catch the firelight. They count their gold by candlelight. ' + BUSTLING_BUDGET + ' hearts before the gates seal.',
      predicate: function(c){ return _ciIncludes(races, c.race); }
    });
  }
  function pickBustlingStreetsWildHuntEvent(){
    var classes = ['Ranger','Druid','Barbarian'];
    return _buildBustlingVariant({
      variant: 'wildHunt',
      eventKey: 'bustlingStreetsWildHunt',
      eyebrow: 'The Wild Hunt',
      sub: 'Bows drawn, antlers low. The hunt crosses the hall and won\'t stop for you. ' + BUSTLING_BUDGET + ' hearts before they vanish into the green.',
      predicate: function(c){ return _ciIncludes(classes, c.character_class); }
    });
  }
  function pickBustlingStreetsCharlatansEvent(){
    var classes = ['Bard','Rogue'];
    return _buildBustlingVariant({
      variant: 'charlatans',
      eventKey: 'bustlingStreetsCharlatans',
      eyebrow: "The Charlatan's Hall",
      sub: 'Bards and Rogues thread the crowd. Both are lying to you, only one is smiling. ' + BUSTLING_BUDGET + ' hearts before the music stops.',
      predicate: function(c){ return _ciIncludes(classes, c.character_class); }
    });
  }
  function pickBustlingStreetsFurredFolkEvent(){
    var races = ['Tabaxi','Kitsune','Harengon','Mapach','Tiger beastkin','Leonid / Khjask hybrid','Winged volpine','Sharkin','Khenra','Shifter','Fonfeather','Flooflin'];
    return _buildBustlingVariant({
      variant: 'furredFolk',
      eventKey: 'bustlingStreetsFurredFolk',
      eyebrow: 'The Furred Folk',
      sub: 'Soft paws, sharp eyes, sharper teeth. The wild walks among you. ' + BUSTLING_BUDGET + ' hearts before they slip away.',
      predicate: function(c){ return _ciIncludes(races, c.race); }
    });
  }
  function pickBustlingStreetsFeyCourtEvent(){
    var races = ['Fairy','Eladrin','Spring Eladrin','Archfey','Siren','Changeling','Genara','Spirit Guide','Archfey','The king of the forest'];
    return _buildBustlingVariant({
      variant: 'feyCourt',
      eventKey: 'bustlingStreetsFeyCourt',
      eyebrow: 'The Fey Court',
      sub: 'Don\'t accept the wine. Don\'t fall for the music. ' + BUSTLING_BUDGET + ' hearts before the ring closes.',
      predicate: function(c){ return _ciIncludes(races, c.race); }
    });
  }
  /* R140-Z3n (Phase 3): 5 MORE Bustling Streets variants — themed
     parades whose movement reads as procession or pilgrimage. */
  function pickBustlingStreetsKnightProcessionEvent(){
    var classes = ['Fighter','Paladin'];
    return _buildBustlingVariant({
      variant: 'knightProcession',
      eventKey: 'bustlingStreetsKnightProcession',
      eyebrow: "The Knight's Procession",
      sub: 'Steel and ceremony. Banners high, oaths kept. ' + BUSTLING_BUDGET + ' hearts before the column passes.',
      predicate: function(c){ return _ciIncludes(classes, c.character_class); }
    });
  }
  function pickBustlingStreetsBardstormEvent(){
    var classes = ['Bard'];
    return _buildBustlingVariant({
      variant: 'bardstorm',
      eventKey: 'bustlingStreetsBardstorm',
      eyebrow: 'The Bardstorm',
      sub: 'Drums, strings, three songs at once. The hall can\'t hear itself think. ' + BUSTLING_BUDGET + ' hearts before the encore ends.',
      predicate: function(c){ return _ciIncludes(classes, c.character_class); }
    });
  }
  function pickBustlingStreetsDruidMigrationEvent(){
    var classes = ['Druid','Ranger'];
    return _buildBustlingVariant({
      variant: 'druidMigration',
      eventKey: 'bustlingStreetsDruidMigration',
      eyebrow: 'The Druid Migration',
      sub: 'Antlers, moss, and quiet boots. The wild walks through tonight. ' + BUSTLING_BUDGET + ' hearts before the trees close in.',
      predicate: function(c){ return _ciIncludes(classes, c.character_class); }
    });
  }
  function pickBustlingStreetsPactWalkEvent(){
    var classes = ['Warlock'];
    return _buildBustlingVariant({
      variant: 'pactWalk',
      eventKey: 'bustlingStreetsPactWalk',
      eyebrow: "The Pact-Walk",
      sub: 'Warlocks alone tonight, smoke in their wake. Don\'t make eye contact with the patrons. ' + BUSTLING_BUDGET + ' hearts before the candles gutter.',
      predicate: function(c){ return _ciIncludes(classes, c.character_class); }
    });
  }
  function pickBustlingStreetsTemplarMarchEvent(){
    var classes = ['Paladin','Cleric'];
    return _buildBustlingVariant({
      variant: 'templarMarch',
      eventKey: 'bustlingStreetsTemplarMarch',
      eyebrow: 'The Templar March',
      sub: 'Silent prayer and polished steel. They walk in rows of three. ' + BUSTLING_BUDGET + ' hearts before the choir rises.',
      predicate: function(c){ return _ciIncludes(classes, c.character_class); }
    });
  }
  function _bustlingCardHtml(c, locked){
    var tier = (typeof window.hohGetRarity === 'function')
      ? (window.hohGetRarity(c.hearts || 0).tier || 'common')
      : 'common';
    /* R140-F: Use the offset-aware helper so the saved vertical crop
       carries into the scrolling rail. Cards in Bustling Streets are
       small but still cropped heads when the offset isn't honored. */
    var media = _eventCharImg(c, { lazy: true });
    var cls = 'hoh-fresh-streets-card hoh-fresh-streets-tier-' + tier + (locked ? ' hoh-fresh-streets-card-locked' : '');
    var clickAttr = locked
      ? ''
      : ' onclick="hohFreshFacesStreetsHeart(\'' + esc(c.id) + '\', event)"';
    var disabledAttr = locked ? ' disabled' : '';
    return '<button type="button" class="' + cls + '" data-id="' + esc(c.id) + '"' + clickAttr + disabledAttr + '>' +
      '<span class="hoh-fresh-streets-media">' + media + '</span>' +
      '<span class="hoh-fresh-streets-name">' + esc(c.name || '') + '</span>' +
    '</button>';
  }
  function renderBustlingStreetsHtml(state){
    var chars = state.chars || [];
    var hearted = state.hearted || {};
    /* Split into two rows. Top row gets first half, bottom gets the
       rest. The DOM duplicates each row so the CSS keyframe loop is
       seamless (no visible "jump" when the animation wraps). */
    var midpoint = Math.ceil(chars.length / 2);
    var topRow = chars.slice(0, midpoint);
    var bottomRow = chars.slice(midpoint);
    var rowHtml = function(arr){
      return arr.map(function(c){ return _bustlingCardHtml(c, !!hearted[c.id]); }).join('');
    };
    var topHtml = rowHtml(topRow) + rowHtml(topRow);
    var bottomHtml = rowHtml(bottomRow) + rowHtml(bottomRow);
    var done = (state.spent || 0) >= (state.budget || BUSTLING_BUDGET);
    /* R140-A: drop the manual Continue button. After the 5th heart the
       heart handler now auto-fades the rail and calls Continue itself.
       The message stays as a brief celebratory dwell so the user reads
       what happened before the fade-out begins. */
    var continueHtml = done
      ? '<div class="hoh-fresh-streets-done">' +
          '<div class="hoh-fresh-streets-done-msg">' +
            'You gave ' + state.budget + ' hearts to the streets.' +
          '</div>' +
          '<div class="hoh-fresh-streets-done-hint" aria-hidden="true">The crowd thins…</div>' +
        '</div>'
      : '';
    /* R122-G: Wander bar moved BELOW the rail so it reads as session
       summary instead of a header pre-empting the action.
       R140-D: Variants pass `variant`, custom `eyebrow`, and custom
       `sub` so the same rail renders themed wrappers (green / blue /
       brand). The base bustling streets call still passes the original
       copy when no variant is set. */
    var variant = state.variant || '';
    var variantCls = variant ? (' hoh-fresh-special-streets-' + variant) : '';
    var eyebrow = state.eyebrow || 'Bustling Streets';
    var sub = state.sub || ('The hall is busy. Tap any hero you want to heart. You can give ' + state.budget + ' here before the crowd thins.');
    return '<div class="hoh-fresh-special hoh-fresh-special-streets' + variantCls + '">' +
      eventHeaderHtml(eyebrow, sub, { centered: true }) +
      '<div class="hoh-fresh-streets-rail">' +
        '<div class="hoh-fresh-streets-row hoh-fresh-streets-row-top"><div class="hoh-fresh-streets-track">' + topHtml + '</div></div>' +
        '<div class="hoh-fresh-streets-row hoh-fresh-streets-row-bottom"><div class="hoh-fresh-streets-track">' + bottomHtml + '</div></div>' +
      '</div>' +
      continueHtml +
      _streamWanderBarHtml() +
    '</div>';
  }
  window.hohFreshFacesStreetsHeart = function(id, ev){
    if(ev){ try { ev.stopPropagation(); ev.preventDefault(); } catch(_){} }
    /* R122-H: Hardened budget enforcement. Three defensive gates
       before any spend, in order:
         1. State must be a Bustling Streets in-flight event.
         2. Card must not already be hearted (per-character lock).
         3. Total spent must be strictly less than budget.
       After spending, ALL DOM duplicates of the card get the lock
       class AND have their onclick attribute / disabled attribute
       set so a delegated click can't sneak through. */
    if(!currentSpecial || currentSpecial.type !== 'bustlingStreets') return;
    if(!id) return;
    if(!_questAcceptsHeart()){ _beginWanderCompletion(); return; }
    if(!currentSpecial.hearted) currentSpecial.hearted = {};
    if(currentSpecial.hearted[id]) return; /* already locked */
    var spentNow = currentSpecial.spent | 0;
    var budgetNow = currentSpecial.budget | 0 || BUSTLING_BUDGET;
    if(spentNow >= budgetNow){
      /* Hard cap. Do not spend, do not give heart, do not pass GO. */
      return;
    }
    var bank = (typeof window.heartsRemainingToday === 'function') ? window.heartsRemainingToday() : 0;
    if(bank <= 0){
      if(typeof window.hohShowHeartToast === 'function'){
        try { window.hohShowHeartToast('Out of hearts', 'You\'ve spent today\'s.'); } catch(_){}
      }
      return;
    }
    /* Set state FIRST — locks must apply before any async heart-give
       chain runs so a fast double-click can't slip through. */
    currentSpecial.hearted[id] = true;
    currentSpecial.spent = spentNow + 1;
    /* Anchor the heart-give animation to the visible (first) instance
       of the card. Both DOM duplicates carry the same data-id, so we
       lock both. Also strip the inline onclick attribute and set the
       disabled property so even a delegated handler can't fire on
       these elements. */
    var dupes = document.querySelectorAll('#hoh-fresh-faces .hoh-fresh-streets-card[data-id="' + cssEscape(id) + '"]');
    var anchor = dupes && dupes.length ? dupes[0] : null;
    for(var i = 0; i < dupes.length; i++){
      dupes[i].classList.add('hoh-fresh-streets-card-locked');
      try { dupes[i].setAttribute('disabled', 'disabled'); } catch(_){}
      try { dupes[i].removeAttribute('onclick'); } catch(_){}
      try { dupes[i].onclick = null; } catch(_){}
      try { dupes[i].setAttribute('aria-disabled', 'true'); } catch(_){}
    }
    /* Fire through the standard pipeline — wallet decrement, character
       hearts++, animation, attribution, daily-cap. */
    try { if(typeof window.hohHeart === 'function') window.hohHeart(id, anchor); } catch(_){}
    /* Wander quest counter — each heart spent here fills the bar. */
    try { if(typeof bumpFfHeartsToday === 'function') bumpFfHeartsToday(); } catch(_){}
    /* Update the page-hearts pill to reflect remaining budget. We
       bypass recomputePageHearts (which would re-derive max from a
       visibleCount/5 formula) and write the values directly so the
       chip shows the literal budget number. */
    try {
      pageHearts.key = 'special:bustlingStreets';
      pageHearts.max = currentSpecial.budget;
      pageHearts.spent = currentSpecial.spent;
      pageHearts.budget = currentSpecial.budget;
      paintPageHearts();
    } catch(_){}
    /* R148-Z21: Trigger the completion reward when the Bustling Streets
       budget is fully spent. This handler bypasses pageHeartsRecordSpend
       (it writes pageHearts.spent directly above to use the literal
       budget number), so the reward gate that lives inside that
       function never fired for Bustling Streets — meaning the user
       never saw the 5-heart fly-back animation per the R148-Z10 spec.
       Mirroring the same gate here, with the same flag guard so it
       can't double-fire if a stray re-render somehow lands. */
    try {
      if(!pageCompletionRewardFired
          && pageHearts.max >= 1
          && pageHearts.spent >= pageHearts.max){
        pageCompletionRewardFired = true;
        grantPageCompletionReward();
      }
    } catch(_){}
    /* R140-A: budget fully spent → auto-conclude the event with a
       brief dwell + fade transition + roll to the next refresh. No
       Continue button anymore; the user already gave their hearts,
       they shouldn't have to tap one more time to acknowledge it.
       R140-H: PREVIOUS approach (re-render via innerHTML) restarted
       the scroll animation from frame 0, so the cards visibly snapped
       back to the start position and kept scrolling underneath the
       "You gave 5 hearts" message for 1500ms before the panel faded.
       The user reported this as clunky.
       NEW approach: mutate the existing DOM in place. Adding the
       `hoh-fresh-special-streets-done` class freezes the scroll where
       it is (animation-play-state: paused) and triggers a CSS fade-
       out on the cards. The done message is appended after the rail
       and fades in over the same window. No re-render = no scroll
       reset = smooth halt. Total dwell shortened to 1300ms because
       the visual transition is now doing the storytelling, not just
       blank waiting time.
       Sequence:
         t=0     5th heart fires
         t+200   add done class → cards pause + fade out (CSS 600ms)
                  + done message fades in (CSS 500ms @ 250ms delay)
         t+1300  add .hoh-fresh-fading-out for whole-panel fade
         t+1600  fade complete → call StreetsContinue → next refresh */
    if(currentSpecial.spent >= currentSpecial.budget){
      currentSpecial.phase = 'done';
      setTimeout(function(){
        if(!currentSpecial || currentSpecial.type !== 'bustlingStreets') return;
        var wrapper = document.querySelector('#hoh-fresh-faces .hoh-fresh-special-streets');
        if(!wrapper) return;
        /* Add the done class BEFORE injecting the message so the CSS
           transitions kick in the same frame the user sees them. */
        wrapper.classList.add('hoh-fresh-special-streets-done');
        /* Insert the done message after the rail (idempotent — won't
           double-insert if somehow this runs twice). */
        var existingDone = wrapper.querySelector('.hoh-fresh-streets-done');
        if(!existingDone){
          var rail = wrapper.querySelector('.hoh-fresh-streets-rail');
          if(rail){
            var doneHtml =
              '<div class="hoh-fresh-streets-done">' +
                '<div class="hoh-fresh-streets-done-msg">' +
                  'You gave ' + currentSpecial.budget + ' hearts to the streets.' +
                '</div>' +
                '<div class="hoh-fresh-streets-done-hint" aria-hidden="true">The crowd thins…</div>' +
              '</div>';
            rail.insertAdjacentHTML('afterend', doneHtml);
          }
        }
        /* Dwell while the user reads + cards fade, then advance. */
        setTimeout(function(){
          if(!currentSpecial || currentSpecial.type !== 'bustlingStreets') return;
          var ff = _ffContainer();
          if(ff) ff.classList.add('hoh-fresh-fading-out');
          /* R148-Z22e: Same safety timer pattern as schedulePageAutoRefresh.
             Strips the class after 1800ms regardless of whether the inner
             Continue path successfully cleared it. */
          setTimeout(function(){
            try {
              var ffSafety = document.getElementById('hoh-fresh-faces');
              if(ffSafety && ffSafety.classList.contains('hoh-fresh-fading-out')){
                ffSafety.classList.remove('hoh-fresh-fading-out');
              }
            } catch(_){}
          }, 1800);
          setTimeout(function(){
            /* CRITICAL: clear the fade class BEFORE calling Continue so
               the next event doesn't render invisible. The CSS rule
               targets descendants of `.hoh-fresh-fading-out`, so any
               new event that matches the selector list (streets, stream,
               culprit, familiar, etc.) would render at opacity:0 if the
               parent still had the class. */
            try {
              var ff2 = _ffContainer();
              if(ff2) ff2.classList.remove('hoh-fresh-fading-out');
            } catch(_){}
            if(typeof window.hohFreshFacesStreetsContinue === 'function'){
              try { window.hohFreshFacesStreetsContinue(); } catch(_){}
            }
          }, 300);
        }, 1100);
      }, 200);
    }
  };
  window.hohFreshFacesStreetsContinue = function(){
    if(!currentSpecial || currentSpecial.type !== 'bustlingStreets') return;
    currentSpecial = null;
    /* R148-Z22u: Queue an unlimited-pool event for the NEXT roll per
       user — "every time there's a bustling streets type of roll
       there should be an unlimited event roll right after that".
       The flag is read at the top of the picker (forceSpecialEventOnly
       / mustForceUnlimited gate) and consumed (cleared) the moment
       the next event lands so it never re-fires. */
    try { window.__hohQueueUnlimitedNext = true; } catch(_){}
    if(typeof window.hohFreshFacesRefresh === 'function'){
      try { window.hohFreshFacesRefresh({ fromInteractive: true }); } catch(_){}
    }
  };

  /* ================================================================ */
  /* R150: The Conveyer Belt — one-row Bustling Streets sibling        */
  /* ================================================================ */
  /* A Type 1 heart roll. One scrolling row of heroes, tap to heart,
     2-heart budget. It is the lighter single-row cousin of Bustling
     Streets, which stays a two-row five-heart roll, completely
     untouched. Nine themed variants — six by rarity tier, three by
     class group — mix into the same Type 1 pool as the Bustling
     Streets variants and respect the recent-buffer anti-repeat so the
     same belt does not show twice in a window.
     This is its own event type ('conveyer') with its own picker,
     renderer and heart handler, so no Bustling Streets code is edited.
     It reuses read-only helpers (_tierOf, _ciIncludes, _eventCharImg,
     _streamWanderBarHtml) and the .hoh-fresh-streets-card visual so the
     cards stay consistent with the rest of the rail events. */
  var CONVEYER_BUDGET = 3;     /* hearts to give before the belt moves on */
  var CONVEYER_REFUND = 2;     /* hearts handed back for completing the belt */
  var CONVEYER_MIN = 6;        /* don't fire if the filtered pool is too thin */
  var CONVEYER_MAX_RAIL = 30;  /* one row — half the Bustling Streets cap */
  function _conveyerCardHtml(c, locked){
    var tier = (typeof window.hohGetRarity === 'function')
      ? (window.hohGetRarity(c.hearts || 0).tier || 'common')
      : 'common';
    var media = _eventCharImg(c, { lazy: true });
    var cls = 'hoh-fresh-streets-card hoh-fresh-streets-tier-' + tier + (locked ? ' hoh-fresh-streets-card-locked' : '');
    var clickAttr = locked ? '' : ' onclick="hohFreshFacesConveyerHeart(\'' + esc(c.id) + '\', event)"';
    var disabledAttr = locked ? ' disabled' : '';
    return '<button type="button" class="' + cls + '" data-id="' + esc(c.id) + '"' + clickAttr + disabledAttr + '>' +
      '<span class="hoh-fresh-streets-media">' + media + '</span>' +
      '<span class="hoh-fresh-streets-name">' + esc(c.name || '') + '</span>' +
    '</button>';
  }
  function _buildConveyerVariant(opts){
    var bank = (typeof window.heartsRemainingToday === 'function') ? window.heartsRemainingToday() : 0;
    if(bank < 1) return null;
    var allChars = (typeof window.hohGetAllCharacters === 'function') ? window.hohGetAllCharacters() : [];
    if(!allChars || !allChars.length) return null;
    var visible = allChars.filter(function(c){
      if(!c || !c.id || c.is_hidden) return false;
      return opts.predicate(c);
    });
    if(visible.length < CONVEYER_MIN) return null;
    var shuffled = hfShuffle(visible);
    var pool = shuffled.slice(0, Math.min(CONVEYER_MAX_RAIL, shuffled.length));
    return {
      type: 'conveyer',
      variant: opts.variant,
      eyebrow: opts.eyebrow,
      sub: opts.sub,
      chars: pool,
      budget: CONVEYER_BUDGET,
      spent: 0,
      hearted: {},
      phase: 'idle',
      _eventKey: opts.eventKey
    };
  }
  function _conveyerRaritySub(word){
    return 'A belt of ' + word + ' heroes rolls past. Heart ' + CONVEYER_BUDGET + ' and the belt hands ' + CONVEYER_REFUND + ' back.';
  }
  /* Six rarity-tier belts. */
  function pickConveyerCommonEvent(){
    return _buildConveyerVariant({ variant:'cv-common', eventKey:'conveyerCommon',
      eyebrow:'The Common Walk', sub:_conveyerRaritySub('common-tier'),
      predicate:function(c){ return _tierOf(c) === 'common'; } });
  }
  function pickConveyerUncommonEvent(){
    return _buildConveyerVariant({ variant:'cv-uncommon', eventKey:'conveyerUncommon',
      eyebrow:'The Rising Line', sub:_conveyerRaritySub('uncommon-tier'),
      predicate:function(c){ return _tierOf(c) === 'uncommon'; } });
  }
  function pickConveyerRareEvent(){
    return _buildConveyerVariant({ variant:'cv-rare', eventKey:'conveyerRare',
      eyebrow:'The Sapphire Run', sub:_conveyerRaritySub('rare-tier'),
      predicate:function(c){ return _tierOf(c) === 'rare'; } });
  }
  function pickConveyerEpicEvent(){
    return _buildConveyerVariant({ variant:'cv-epic', eventKey:'conveyerEpic',
      eyebrow:'The Violet March', sub:_conveyerRaritySub('epic-tier'),
      predicate:function(c){ return _tierOf(c) === 'epic'; } });
  }
  function pickConveyerLegendaryEvent(){
    return _buildConveyerVariant({ variant:'cv-legendary', eventKey:'conveyerLegendary',
      eyebrow:'The Golden Procession', sub:_conveyerRaritySub('legendary-tier'),
      predicate:function(c){ return _tierOf(c) === 'legendary'; } });
  }
  function pickConveyerMythicEvent(){
    return _buildConveyerVariant({ variant:'cv-mythic', eventKey:'conveyerMythic',
      eyebrow:'The Mythic Drift', sub:_conveyerRaritySub('mythic-tier'),
      predicate:function(c){ return _tierOf(c) === 'mythic'; } });
  }
  /* Three class-group belts, colourful and distinct from the rarity hues. */
  function pickConveyerArcaneEvent(){
    var classes = ['Wizard','Sorcerer','Warlock','Artificer'];
    return _buildConveyerVariant({ variant:'cv-arcane', eventKey:'conveyerArcane',
      eyebrow:'The Arcane Current',
      sub:'Spellcasters glide past on a river of sparks. Heart ' + CONVEYER_BUDGET + ', the belt returns ' + CONVEYER_REFUND + '.',
      predicate:function(c){ return _ciIncludes(classes, c.character_class); } });
  }
  function pickConveyerMartialEvent(){
    var classes = ['Fighter','Barbarian','Monk'];
    return _buildConveyerVariant({ variant:'cv-martial', eventKey:'conveyerMartial',
      eyebrow:'The Iron Parade',
      sub:'Steel and stride. The warriors roll through. Heart ' + CONVEYER_BUDGET + ', the belt returns ' + CONVEYER_REFUND + '.',
      predicate:function(c){ return _ciIncludes(classes, c.character_class); } });
  }
  function pickConveyerWanderersEvent(){
    var classes = ['Ranger','Druid','Bard'];
    return _buildConveyerVariant({ variant:'cv-wanderers', eventKey:'conveyerWanderers',
      eyebrow:'The Long Caravan',
      sub:'Rangers, druids and bards on the move. Heart ' + CONVEYER_BUDGET + ', the belt returns ' + CONVEYER_REFUND + '.',
      predicate:function(c){ return _ciIncludes(classes, c.character_class); } });
  }
  function renderConveyerHtml(state){
    var chars = state.chars || [];
    var hearted = state.hearted || {};
    var cards = chars.map(function(c){ return _conveyerCardHtml(c, !!hearted[c.id]); }).join('');
    /* Double the cards so the -50% scroll keyframe loops seamlessly. */
    var track = cards + cards;
    var variant = state.variant || '';
    var variantCls = variant ? (' hoh-fresh-conveyer-' + variant) : '';
    var eyebrow = state.eyebrow || 'The Conveyer Belt';
    var sub = state.sub || ('A line of heroes rolls past. Heart ' + (state.budget || CONVEYER_BUDGET) + ' and the belt hands ' + CONVEYER_REFUND + ' back.');
    return '<div class="hoh-fresh-special hoh-fresh-special-conveyer' + variantCls + '">' +
      eventHeaderHtml(eyebrow, sub, { centered: true }) +
      '<div class="hoh-fresh-conveyer-rail">' +
        '<div class="hoh-fresh-conveyer-track">' + track + '</div>' +
      '</div>' +
      _streamWanderBarHtml() +
    '</div>';
  }
  /* R151: shared belt-exit — mark the belt done, add the done class
     for the CSS fade, and advance to the next roll after a short
     dwell. Used by both the budget-complete path and the
     out-of-hearts safety so the two stay in sync. */
  function _conveyerEndAndAdvance(dwellMs){
    if(!currentSpecial || currentSpecial.type !== 'conveyer') return;
    currentSpecial.phase = 'done';
    try {
      var wrap = document.querySelector('#hoh-fresh-faces .hoh-fresh-special-conveyer');
      if(wrap) wrap.classList.add('hoh-fresh-special-conveyer-done');
    } catch(_){}
    setTimeout(function(){
      if(!currentSpecial || currentSpecial.type !== 'conveyer') return;
      if(typeof window.hohFreshFacesConveyerContinue === 'function'){
        try { window.hohFreshFacesConveyerContinue(); } catch(_){}
      }
    }, dwellMs || 1150);
  }
  window.hohFreshFacesConveyerHeart = function(id, ev){
    if(ev){ try { ev.stopPropagation(); ev.preventDefault(); } catch(_){} }
    if(!currentSpecial || currentSpecial.type !== 'conveyer') return;
    if(!id) return;
    if(!_questAcceptsHeart()){ _beginWanderCompletion(); return; }
    if(!currentSpecial.hearted) currentSpecial.hearted = {};
    if(currentSpecial.hearted[id]) return;
    var spentNow = currentSpecial.spent | 0;
    var budgetNow = (currentSpecial.budget | 0) || CONVEYER_BUDGET;
    if(spentNow >= budgetNow) return;
    var bank = (typeof window.heartsRemainingToday === 'function') ? window.heartsRemainingToday() : 0;
    if(bank <= 0){
      if(typeof window.hohShowHeartToast === 'function'){
        try { window.hohShowHeartToast('Out of hearts', 'You\'ve spent today\'s.'); } catch(_){}
      }
      /* R151: out of hearts before the belt is finished. It can never
         reach its budget now, so advance it gracefully instead of
         leaving the player stuck on a belt with no finish state. No
         refund fires — the refund is only for completing the belt. */
      _conveyerEndAndAdvance(1400);
      return;
    }
    /* Lock state before any async heart-give so a fast double-click
       cannot slip a third spend through. */
    currentSpecial.hearted[id] = true;
    currentSpecial.spent = spentNow + 1;
    var dupes = document.querySelectorAll('#hoh-fresh-faces .hoh-fresh-streets-card[data-id="' + cssEscape(id) + '"]');
    var anchor = dupes && dupes.length ? dupes[0] : null;
    for(var i = 0; i < dupes.length; i++){
      dupes[i].classList.add('hoh-fresh-streets-card-locked');
      try { dupes[i].setAttribute('disabled', 'disabled'); } catch(_){}
      try { dupes[i].removeAttribute('onclick'); } catch(_){}
      try { dupes[i].onclick = null; } catch(_){}
      try { dupes[i].setAttribute('aria-disabled', 'true'); } catch(_){}
    }
    try { if(typeof window.hohHeart === 'function') window.hohHeart(id, anchor); } catch(_){}
    try { if(typeof bumpFfHeartsToday === 'function') bumpFfHeartsToday(); } catch(_){}
    /* Page-hearts chip reflects the literal heart budget (characters
       hearted, not net cost). The key is a 'conveyer:*' key, NOT a
       'special:*' one, which is exactly what lets grantPageCompletion-
       Reward pay the standard Type 1 reward when the belt finishes —
       see the completion gate below. */
    try {
      pageHearts.key = 'conveyer:' + (currentSpecial.variant || 'belt');
      pageHearts.max = currentSpecial.budget;
      pageHearts.spent = currentSpecial.spent;
      pageHearts.budget = currentSpecial.budget;
      paintPageHearts();
    } catch(_){}
    /* Budget fully spent — fire the standard page completion reward,
       brief dwell, then advance to the next roll.
       R152: the belt used to run a private refund here —
       hohAddHeartsToBank(2) plus a toast — which credited the bank
       silently with no heart-fly, so the give-back felt flat next to
       the themed phases and Bustling Streets. It now routes through
       grantPageCompletionReward, the exact gate Bustling Streets uses
       (R148-Z21). The conveyer key ('conveyer:*', not 'special:*')
       makes that helper compute reward = max - 1 = 3 - 1 = 2, so the
       player still nets a 1-heart cost, but the green pill flash, the
       heart volley to the wallet, the bank bump and the toast all fire
       — identical to every other Type 1 roll. pageCompletionReward-
       Fired is the one-time guard (it resets per roll), so the old
       per-event _refundPaid flag is no longer needed. */
    if(currentSpecial.spent >= currentSpecial.budget){
      try {
        if(!pageCompletionRewardFired
            && pageHearts.max >= 1
            && pageHearts.spent >= pageHearts.max){
          pageCompletionRewardFired = true;
          grantPageCompletionReward();
        }
      } catch(_){}
      _conveyerEndAndAdvance(1300);
    }
  };
  window.hohFreshFacesConveyerContinue = function(){
    if(!currentSpecial || currentSpecial.type !== 'conveyer') return;
    currentSpecial = null;
    if(typeof window.hohFreshFacesRefresh === 'function'){
      try { window.hohFreshFacesRefresh({ fromInteractive: true }); } catch(_){}
    }
  };

  /* ================================================================ */
  /* Pick a Card — render + handlers                                    */
  /* ================================================================ */
  function renderPickACardHtml(state){
    var phase = state.phase || 'idle';
    var won = state.won;
    var picked = state.pickedIndex;
    var winningIdx = state.winningIndex;
    /* Host card — same compact host treatment as the dice trader and
       wheel events. */
    var host = state.host;
    var hostHtml = '';
    if(host){
      var hImg = host.image_thumb_url || host.image_url || '';
      var hLetter = esc((host.name || '?').charAt(0));
      /* R140-F: Offset-aware host img — see _eventCharImg helper. */
      var hMedia = _eventCharImg(host, { eager: true, draggable: false });
      hostHtml = '<div class="hoh-fresh-pickcard-host">' +
        '<div role="button" tabindex="0" class="hoh-fresh-card hoh-fresh-card-host" data-id="' + esc(host.id) + '"' +
          ' onclick="if(window.hohOpenDetail)hohOpenDetail(\'' + esc(host.id) + '\')"' +
          ' onkeydown="if((event.key===\'Enter\'||event.key===\' \')&&window.hohOpenDetail){event.preventDefault();hohOpenDetail(\'' + esc(host.id) + '\');}"' +
          ' aria-label="Open ' + esc(host.name || '') + '">' +
          '<span class="hoh-fresh-media">' + hMedia + '</span>' +
          '<span class="hoh-fresh-body">' +
            '<span class="hoh-fresh-name">' + esc(host.name || 'Unknown') + '</span>' +
            '<span class="hoh-fresh-meta">' + esc((host.race || '') + (host.character_class ? ' ' + host.character_class : '')) + '</span>' +
          '</span>' +
        '</div>' +
      '</div>';
    }
    /* Three cards. Idle = all face-down. Resolved = all flipped, the
       picked one highlighted, the winner glowing gold. */
    var cardsHtml = [0,1,2].map(function(i){
      var isWin = (i === winningIdx);
      var isPicked = (i === picked);
      var face;
      var cls = 'hoh-fresh-pickcard';
      if(phase === 'idle'){
        cls += ' hoh-fresh-pickcard-down';
        face = '<span class="hoh-fresh-pickcard-back">?</span>';
        return '<button type="button" class="' + cls + '" onclick="hohFreshFacesPickACardChoose(' + i + ')" aria-label="Card ' + (i+1) + '">' + face + '</button>';
      }
      /* Resolved — show all three cards face up. */
      cls += ' hoh-fresh-pickcard-up';
      if(isWin) cls += ' hoh-fresh-pickcard-win';
      else cls += ' hoh-fresh-pickcard-bomb';
      if(isPicked) cls += ' hoh-fresh-pickcard-picked';
      /* R52: replaced bomb with Mimic — D&D classic creature that
         disguises itself as a treasure chest. Inline SVG (chest with
         fang teeth) so the icon renders consistently across all
         devices, unlike the bomb emoji which varied. */
      var mimicSvg = '<svg viewBox="0 0 24 24" width="34" height="34" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
        '<path d="M3 9 L21 9 L21 19 a1 1 0 0 1 -1 1 L4 20 a1 1 0 0 1 -1 -1 Z"/>' +
        '<path d="M3 9 L3 7 a2 2 0 0 1 2 -2 L19 5 a2 2 0 0 1 2 2 L21 9"/>' +
        '<path d="M7 11 L8 13 L9 11 M11 11 L12 13 L13 11 M15 11 L16 13 L17 11"/>' +
        '<circle cx="12" cy="16" r="0.8" fill="currentColor"/>' +
      '</svg>';
      face = isWin
        ? '<span class="hoh-fresh-pickcard-face hoh-fresh-pickcard-face-treasure">' +
            '<span class="hoh-fresh-pickcard-icon" aria-hidden="true">&#9829;&#xFE0E;</span>' +
            '<span class="hoh-fresh-pickcard-amt">+' + state.payout + '</span>' +
          '</span>'
        : '<span class="hoh-fresh-pickcard-face hoh-fresh-pickcard-face-bomb">' +
            '<span class="hoh-fresh-pickcard-icon">' + mimicSvg + '</span>' +
            '<span class="hoh-fresh-pickcard-amt">Mimic</span>' +
          '</span>';
      return '<div class="' + cls + '">' + face + '</div>';
    }).join('');
    var ctaHtml = '';
    if(phase === 'idle'){
      ctaHtml =
        '<div class="hoh-fresh-duel-cta">' +
          '<div class="hoh-fresh-duel-prompt">Pick a card. One hides +' + state.payout + ' hearts. Two are mimics, waiting to bite.</div>' +
          '<div class="hoh-fresh-duel-stakes">' +
            '<span class="hoh-fresh-duel-stake-cost">&#9829;&#xFE0E; ' + state.cost + ' to play</span>' +
            '<span class="hoh-fresh-duel-stake-payout">Win pays &#9829;&#xFE0E; ' + state.payout + '</span>' +
          '</div>' +
        '</div>';
    } else { /* resolved */
      var headline = won
        ? 'You picked the heart. +' + state.payout + ' hearts.'
        : 'A mimic. The chest snaps shut, no payout.';
      var headlineCls = won ? ' hoh-fresh-duel-headline-win' : ' hoh-fresh-duel-headline-lose';
      var sub = won ? 'A clean read.' : 'Better luck on the next card.';
      ctaHtml =
        '<div class="hoh-fresh-duel-result' + headlineCls + '">' +
          '<strong>' + headline + '</strong>' +
          '<span class="hoh-fresh-duel-result-sub">' + sub + '</span>' +
          '<button type="button" class="hoh-fresh-coin-toss hoh-fresh-dice-continue" onclick="hohFreshFacesPickACardContinue()">' +
            '<span>Back to the Hall</span>' +
          '</button>' +
        '</div>';
    }
    return '<div class="hoh-fresh-special hoh-fresh-special-pickcard">' +
      eventHeaderHtml('Pick a Card', 'A grinning host fans three cards. One pays. Two go boom. Trust your gut and pick.', { centered: true }) +
      hostHtml +
      '<div class="hoh-fresh-pickcard-grid">' + cardsHtml + '</div>' +
      ctaHtml +
    '</div>';
  }
  window.hohFreshFacesPickACardChoose = function(idx){
    if(!currentSpecial || currentSpecial.type !== 'pickACard') return;
    if(currentSpecial.phase !== 'idle') return;
    if(idx < 0 || idx > 2) return;
    var cost = currentSpecial.cost || 5;
    var bank = (typeof window.heartsRemainingToday === 'function') ? window.heartsRemainingToday() : 0;
    if(bank < cost){
      if(typeof window.hohShowHeartToast === 'function'){
        try { window.hohShowHeartToast('Not enough hearts', 'You need ' + cost + ' to play.'); } catch(_){}
      }
      return;
    }
    /* Pay the entry fee. */
    try {
      var raw = parseInt(localStorage.getItem('hoh-heart-bank-v1') || '0', 10) || 0;
      try{ window.hohHeartHint && window.hohHeartHint('spend_feature'); }catch(e){}
      localStorage.setItem('hoh-heart-bank-v1', String(Math.max(0, raw - cost)));
      try { document.dispatchEvent(new CustomEvent('hoh:hearts-bumped', { detail: { spent: cost } })); } catch(_){}
    } catch(_){}
    if(typeof window.hohRenderHeaderCounter === 'function'){
      try { window.hohRenderHeaderCounter(); } catch(_){}
    }
    var won = (idx === currentSpecial.winningIndex);
    currentSpecial.pickedIndex = idx;
    currentSpecial.phase = 'resolved';
    currentSpecial.won = won;
    var container = _ffContainer();
    if(container) container.innerHTML = renderPickACardHtml(currentSpecial);
    if(won){
      if(typeof window.hohAddHeartsToBank === 'function'){
        try{ window.hohHeartHint && window.hohHeartHint('earn_explore_special'); }catch(e){}
        try { window.hohAddHeartsToBank(currentSpecial.payout); } catch(_){}
      }
      if(typeof window.hohShowHeartToast === 'function'){
        try { window.hohShowHeartToast('+' + currentSpecial.payout + ' hearts', 'A clean read.'); } catch(_){}
      }
      /* Heart-fly + double confetti burst on the win. */
      try {
        var winCard = document.querySelector('#hoh-fresh-faces .hoh-fresh-pickcard-win');
        if(winCard) flyDiceTraderHeartsToPill(winCard, Math.min(currentSpecial.payout, 12));
      } catch(_){}
      try { spawnConfetti(); } catch(_){}
      setTimeout(function(){ try { spawnConfetti(); } catch(_){} }, 240);
    } else {
      if(typeof window.hohShowHeartToast === 'function'){
        try { window.hohShowHeartToast('Mimic!', 'The chest had teeth. No payout.'); } catch(_){}
      }
    }
    try { markPickACardUsedToday(); } catch(_){}
    if(typeof startAutoTimer === 'function') setTimeout(startAutoTimer, 0);
  };
  window.hohFreshFacesPickACardContinue = function(){
    if(!currentSpecial || currentSpecial.type !== 'pickACard') return;
    if(currentSpecial.phase !== 'resolved') return;
    currentSpecial = null;
    if(typeof window.hohFreshFacesRefresh === 'function'){
      try { window.hohFreshFacesRefresh({ fromInteractive: true }); } catch(_){}
    }
  };

  /* ================================================================ */
  /* R124-B: Five-Card Fortune — rare special, 5-card free pick.       */
  /* ================================================================ */
  /* A wandering fortune teller fans five cards. Each holds a different
     hearts payout: 0, 10, 20, 30, or 50. User picks one, all reveal.
     Free to play (no buy-in). Reached only via the Mage's Bazaar
     audience-with-the-Hall buff (along with Spin Wheel and Legendary
     Shop), so it's gated behind a 25-heart purchase from the bazaar.
     EV = (0+10+20+30+50)/5 = 22 hearts per fire — the same average as
     a wheel spin but with no cost, so the buff feels generous.
     Themed around a sorcerer/charlatan host.    */
  /* R134-B: Rebalanced from [0, 10, 20, 30, 50] to [30, 35, 40, 45, 50].
     The blank card (+0) felt punitive — users walked away from a full
     event with nothing. Every pull now pays out in the 30–50 range,
     so the event always feels like a win even when the user picks the
     "lowest" card. The shuffle still randomizes which card holds which
     value so picks remain a fortune-teller moment. */
  var FIVE_CARDS_VALUES = [30, 35, 40, 45, 50];
  function pickFiveCardsEvent(){
    var allChars = (typeof window.hohGetAllCharacters === 'function') ? window.hohGetAllCharacters() : [];
    if(!allChars || !allChars.length) return null;
    var hostClasses = {Sorcerer:1,Charlatan:1,Bard:1,Warlock:1,Wizard:1};
    var preferred = allChars.filter(function(c){ return c && c.character_class && hostClasses[c.character_class]; });
    var pool = preferred.length ? preferred : allChars.filter(function(c){ return c && c.id; });
    if(!pool.length) return null;
    var host = pool[Math.floor(Math.random() * pool.length)];
    /* Shuffle the values into card slots so each pick is a fresh
       distribution. The user can't pattern-match a "lucky position". */
    var shuffled = hfShuffle(FIVE_CARDS_VALUES);
    return {
      type: 'fiveCards',
      host: host,
      cardValues: shuffled,
      phase: 'idle',
      pickedIndex: null,
      _eventKey: 'fiveCards'
    };
  }
  function renderFiveCardsHtml(state){
    var phase = state.phase || 'idle';
    var picked = state.pickedIndex;
    var values = state.cardValues || FIVE_CARDS_VALUES;
    /* Host card. */
    var host = state.host || {};
    var hImg = host.image_thumb_url || host.image_url || '';
    var hLetter = esc((host.name || '?').charAt(0));
    /* R140-F: Offset-aware host img — see _eventCharImg helper. */
    var hMedia = _eventCharImg(host, { eager: true, draggable: false });
    var hostHtml = '<div class="hoh-fresh-pickcard-host">' +
      '<div role="button" tabindex="0" class="hoh-fresh-card hoh-fresh-card-host" data-id="' + esc(host.id || '') + '"' +
        ' onclick="if(window.hohOpenDetail)hohOpenDetail(\'' + esc(host.id || '') + '\')"' +
        ' aria-label="Open ' + esc(host.name || '') + '">' +
        '<span class="hoh-fresh-media">' + hMedia + '</span>' +
        '<span class="hoh-fresh-body">' +
          '<span class="hoh-fresh-name">' + esc(host.name || 'A fortune teller') + '</span>' +
          '<span class="hoh-fresh-meta">' + esc((host.race || '') + (host.character_class ? ' ' + host.character_class : '')) + '</span>' +
        '</span>' +
      '</div>' +
    '</div>';
    var cardsHtml = values.map(function(val, i){
      var isPicked = (i === picked);
      var cls = 'hoh-fresh-pickcard hoh-fresh-pickcard-five';
      if(phase === 'idle'){
        cls += ' hoh-fresh-pickcard-down';
        return '<button type="button" class="' + cls + '" onclick="hohFreshFacesFiveCardsChoose(' + i + ')" aria-label="Card ' + (i+1) + '">' +
          '<span class="hoh-fresh-pickcard-back">?</span>' +
        '</button>';
      }
      cls += ' hoh-fresh-pickcard-up';
      if(isPicked) cls += ' hoh-fresh-pickcard-picked';
      if(val === 0) cls += ' hoh-fresh-pickcard-zero';
      else if(val >= 50) cls += ' hoh-fresh-pickcard-jackpot';
      return '<div class="' + cls + '">' +
        '<span class="hoh-fresh-pickcard-face hoh-fresh-pickcard-face-treasure">' +
          '<span class="hoh-fresh-pickcard-icon" aria-hidden="true">&#9829;&#xFE0E;</span>' +
          '<span class="hoh-fresh-pickcard-amt">+' + val + '</span>' +
        '</span>' +
      '</div>';
    }).join('');
    var ctaHtml = '';
    if(phase === 'idle'){
      ctaHtml =
        '<div class="hoh-fresh-duel-cta">' +
          '<div class="hoh-fresh-duel-prompt">Five cards. Each hides a different number of hearts. Pick one. Free of charge.</div>' +
          '<div class="hoh-fresh-duel-stakes">' +
            '<span class="hoh-fresh-duel-stake-payout">0, 10, 20, 30, or 50 &#9829;&#xFE0E;</span>' +
          '</div>' +
        '</div>';
    } else {
      var won = (values[picked] > 0);
      var payout = values[picked] || 0;
      var headlineCls = won ? ' hoh-fresh-duel-headline-win' : ' hoh-fresh-duel-headline-lose';
      var headline = (payout === 0)
        ? 'A blank card. Nothing this time.'
        : 'A clean draw.';
      var sub = (payout === 0) ? 'The mage shrugs. The cards turn.'
              : (payout >= 50) ? 'A blessed pull. The mage tips their hat.'
              : (payout >= 30) ? 'A solid card. The mage nods.'
              : 'A modest gift. The mage smiles.';
      ctaHtml =
        '<div class="hoh-fresh-duel-result' + headlineCls + '">' +
          '<strong>' + headline + '</strong>' +
          '<span class="hoh-fresh-duel-result-sub">' + sub + '</span>' +
          '<button type="button" class="hoh-fresh-coin-toss hoh-fresh-dice-continue" onclick="hohFreshFacesFiveCardsContinue()">' +
            '<span>Back to the Hall</span>' +
          '</button>' +
        '</div>';
    }
    return '<div class="hoh-fresh-special hoh-fresh-special-pickcard hoh-fresh-special-fivecards">' +
      eventHeaderHtml('Five-Card Fortune', 'A wandering fortune teller fans five cards. Each one holds a different gift.', { centered: true }) +
      hostHtml +
      '<div class="hoh-fresh-pickcard-grid hoh-fresh-pickcard-grid-5">' + cardsHtml + '</div>' +
      ctaHtml +
    '</div>';
  }
  window.hohFreshFacesFiveCardsChoose = function(idx){
    if(!currentSpecial || currentSpecial.type !== 'fiveCards') return;
    if(currentSpecial.phase !== 'idle') return;
    if(idx < 0 || idx > 4) return;
    var values = currentSpecial.cardValues || FIVE_CARDS_VALUES;
    var payout = values[idx] || 0;
    currentSpecial.pickedIndex = idx;
    currentSpecial.phase = 'resolved';
    var container = _ffContainer();
    if(container) container.innerHTML = renderFiveCardsHtml(currentSpecial);
    if(payout > 0){
      if(typeof window.hohAddHeartsToBank === 'function'){
        try { window.hohAddHeartsToBank(payout); } catch(_){}
      }
      try { window.hohShowHeartToast && window.hohShowHeartToast('Lucky draw', 'A clean draw.'); } catch(_){}
      try {
        var card = container && container.querySelector('.hoh-fresh-pickcard-picked');
        if(card && typeof flyDiceTraderHeartsToPill === 'function'){
          flyDiceTraderHeartsToPill(card, Math.min(payout, 12));
        }
      } catch(_){}
      if(payout >= 50){
        try {
          if(typeof spawnConfetti === 'function'){
            spawnConfetti();
            setTimeout(function(){ try { spawnConfetti(); } catch(_){} }, 220);
          }
        } catch(_){}
      }
    }
    if(typeof startAutoTimer === 'function') setTimeout(startAutoTimer, 0);
  };
  window.hohFreshFacesFiveCardsContinue = function(){
    if(!currentSpecial || currentSpecial.type !== 'fiveCards') return;
    if(currentSpecial.phase !== 'resolved') return;
    currentSpecial = null;
    if(typeof window.hohFreshFacesRefresh === 'function'){
      try { window.hohFreshFacesRefresh({ fromInteractive: true }); } catch(_){}
    }
  };

  /* ================================================================ */
  /* R55 unlimited event renderers + handlers                          */
  /* ================================================================ */

  /* Shared compact host/character card for guess events. Smaller
     than the full cardHtml, no heart/X buttons (this is a quiz, not
     a vote). Reveals the actual answer overlay on the resolved phase. */
  function _miniHeroCard(c, opts){
    opts = opts || {};
    /* R140-F: Offset-aware helper. */
    var media = _eventCharImg(c, { eager: true });
    /* On idle, optionally hide the meta line (the user is supposed
       to GUESS the class/rarity, so showing it would defeat the
       game). On resolved, show full meta. */
    var hideMeta = !!opts.hideMeta;
    var meta = hideMeta
      ? '<span class="hoh-fresh-meta" style="opacity:0.4;font-style:italic;">' + (opts.metaPlaceholder || 'Their secret') + '</span>'
      : '<span class="hoh-fresh-meta">' + esc((c.race || '') + (c.character_class ? ' · ' + c.character_class : '')) + '</span>';
    /* lockClick: when true (during the guessing/idle phase of a guess
       event), don't add the open-detail onclick. The user must answer
       through the option buttons; clicking the hero card mid-guess
       would short-circuit the flow and let them peek at the truth.
       After resolved we want the card clickable so they can dig in. */
    var lockClick = !!opts.lockClick;
    var cardCls = 'hoh-fresh-card hoh-fresh-card-host hoh-fresh-card-guess';
    if(lockClick) cardCls += ' hoh-fresh-card-locked';
    var roleAttr = lockClick ? '' : ' role="button" tabindex="0"';
    var clickAttr = lockClick
      ? ''
      : ' onclick="if(window.hohOpenDetail)hohOpenDetail(\'' + esc(c.id) + '\')"';
    return '<div class="' + cardCls + '" data-id="' + esc(c.id) + '"' +
      roleAttr +
      clickAttr + '>' +
      '<span class="hoh-fresh-media">' + media + '</span>' +
      '<span class="hoh-fresh-body">' +
        '<span class="hoh-fresh-name">' + esc(c.name || 'Unknown') + '</span>' +
        meta +
      '</span>' +
    '</div>';
  }

  /* --- Class Guess shared renderer ---
     The class-guess events (Fighter|Barbarian etc) share the same UI
     shape: hidden-meta hero card on top, option buttons below, reveal
     on click. R165: rarity branch removed with the guessRarity event. */
  function renderClassOrRarityGuessHtml(state){
    var phase = state.phase || 'idle';
    var c = state.char || {};
    var isRace = (state.type === 'guessEHE'); /* R87 */
    /* Build options. */
    var options;
    if(isRace){
      options = (state.races || []).map(function(r){ return { val: r, label: r }; });
    } else {
      options = state.classes.map(function(cls){
        return { val: cls, label: cls };
      });
    }
    /* Determine the correct answer. */
    var correct;
    if(isRace){
      correct = c.race;
    } else {
      correct = c.character_class;
    }
    /* Header copy by event type. */
    var eyebrow, sub;
    if(state.type === 'guessFB'){
      eyebrow = 'Fighter or Barbarian?';
      sub = 'One of these two. Read them and pick.';
    } else if(state.type === 'guessWW'){
      eyebrow = 'Wizard or Warlock?';
      sub = 'A pact or a study. Tell which.';
    } else if(state.type === 'guessRR'){
      eyebrow = 'Ranger or Rogue?';
      sub = 'Wild road or back alley. Tell which.';
    } else if(state.type === 'guessEHE'){
      eyebrow = 'Elf or Half-Elf?';
      sub = 'Look close. Full elven or half-blooded.';
    }
    /* R96: Dropped per-option host portraits on class/race-binary
       events. The central hidden character IS the host already —
       adding two more portraits below was redundant noise. The
       optionHosts state field is still populated by the picker (cheap
       enough to keep) but the renderer ignores it. */
    var pickedVal = state.pickedClass || state.pickedRace;
    var optionsHtml = options.map(function(o){
      var cls = 'hoh-fresh-quiz-opt hoh-fresh-guess-opt';
      var styleAttr = '';
      if(phase === 'resolved'){
        if(o.val === correct){
          cls += ' hoh-fresh-quiz-opt-correct';
        } else if(o.val === pickedVal){
          cls += ' hoh-fresh-quiz-opt-wrong';
        }
      }
      if(o.color) styleAttr = ' style="--rar-color:' + o.color + ';"';
      var clickAttr = (phase === 'idle')
        ? ' onclick="hohFreshFacesGuessPick(\'' + esc(o.val) + '\')"'
        : '';
      return '<button type="button" class="' + cls + '"' + styleAttr + clickAttr + ' ' + (phase !== 'idle' ? 'disabled' : '') + '>' + esc(o.label) + '</button>';
    }).join('');
    /* Reveal overlay on resolved. */
    var resultHtml = '';
    if(phase === 'resolved'){
      var won = (pickedVal === correct);
      var headlineCls = won ? ' hoh-fresh-duel-headline-win' : ' hoh-fresh-duel-headline-lose';
      var headline = won ? 'A clean read.' :'Not quite. The hall keeps its secret.';
      var sub2 = won ? 'You knew them at a glance.' : 'The right answer was ' + esc(correct) + '.';
      resultHtml =
        '<div class="hoh-fresh-duel-result' + headlineCls + '">' +
          '<strong>' + headline + '</strong>' +
          '<span class="hoh-fresh-duel-result-sub">' + sub2 + '</span>' +
          '<button type="button" class="hoh-fresh-coin-toss hoh-fresh-dice-continue" onclick="hohFreshFacesGuessContinue()">' +
            '<span>Back to the Hall</span>' +
          '</button>' +
        '</div>';
    }
    return '<div class="hoh-fresh-special hoh-fresh-special-guess">' +
      eventHeaderHtml(eyebrow, sub, { centered: true }) +
      '<div class="hoh-fresh-guess-stage">' +
        _miniHeroCard(c, {
          hideMeta: phase === 'idle',
          metaPlaceholder: isRace ? 'Race hidden' : 'Class hidden',
          lockClick: phase === 'idle'
        }) +
      '</div>' +
      '<div class="hoh-fresh-guess-options">' + optionsHtml + '</div>' +
      resultHtml +
    '</div>';
  }
  window.hohFreshFacesGuessPick = function(val){
    if(!currentSpecial) return;
    var t = currentSpecial.type;
    if(['guessFB','guessWW','guessRR','guessEHE'].indexOf(t) === -1) return;
    if(currentSpecial.phase !== 'idle') return;
    /* Determine correct answer. */
    var c = currentSpecial.char || {};
    var correct;
    if(t === 'guessEHE'){
      correct = c.race;
      currentSpecial.pickedRace = val;
    } else {
      correct = c.character_class;
      currentSpecial.pickedClass = val;
    }
    var won = (val === correct);
    currentSpecial.won = won;
    currentSpecial.phase = 'resolved';
    var container = _ffContainer();
    if(container) container.innerHTML = renderClassOrRarityGuessHtml(currentSpecial);
    if(won){
      if(typeof window.hohAddHeartsToBank === 'function'){
        try { window.hohAddHeartsToBank(5); } catch(_){}
      }
      if(typeof window.hohShowHeartToast === 'function'){
        try { window.hohShowHeartToast('A clean read', 'Well spotted.'); } catch(_){}
      }
      try {
        var anchor = container && container.querySelector('.hoh-fresh-card-guess');
        if(anchor) flyDiceTraderHeartsToPill(anchor, 3);
      } catch(_){}
    } else {
      /* R59: every unlimited guess event loses a heart on wrong. */
      try { loseHeartFromGuess('Wrong class.'); } catch(_){}
    }
    if(typeof startAutoTimer === 'function') setTimeout(startAutoTimer, 0);
  };
  window.hohFreshFacesGuessContinue = function(){
    if(!currentSpecial) return;
    if(currentSpecial.phase !== 'resolved') return;
    currentSpecial = null;
    if(typeof window.hohFreshFacesRefresh === 'function'){
      try { window.hohFreshFacesRefresh({ fromInteractive: true }); } catch(_){}
    }
  };

  /* --- Find the Culprit ---
     5 chars in a row, click the odd one out. Race/class shown so the
     user has something to look for. */
  function renderFindCulpritHtml(state){
    var phase = state.phase || 'idle';
    /* R57: support race-mode culprits (Dwarf vs Halfling). When
       state.dimension === 'race' the meta line shows race instead of
       class. Default mode stays 'class' for back-compat. */
    var dim = state.dimension === 'race' ? 'race' : 'class';
    var cardsHtml = state.chars.map(function(c){
      var isCulprit = (c.id === state.culpritId);
      var isPicked = (c.id === state.pickedId);
      var cls = 'hoh-fresh-culprit-card';
      if(phase === 'resolved'){
        if(isCulprit) cls += ' hoh-fresh-culprit-correct';
        else if(isPicked) cls += ' hoh-fresh-culprit-wrong';
        cls += ' hoh-fresh-culprit-card-revealed';
      }
      /* R140-F: Offset-aware helper. See _eventCharImg above. */
      var media = _eventCharImg(c, { lazy: true });
      /* R57: idle phase = clicking IS the guess. Resolved phase =
         clicking opens the character detail lightbox so the user can
         actually see the hero they just guessed at. */
      var clickAttr;
      if(phase === 'idle'){
        clickAttr = ' onclick="hohFreshFacesCulpritPick(\'' + esc(c.id) + '\')"';
      } else {
        clickAttr = ' onclick="if(window.hohOpenDetail)hohOpenDetail(\'' + esc(c.id) + '\')"';
      }
      /* R59: meta (class or race) was leaking the answer during the
         guess phase. Hide it on idle, reveal on resolved so the user
         can confirm what they were told to look for. The class /
         race shown is the same dimension the prompt asked about. */
      var metaText = dim === 'race' ? (c.race || '') : (c.character_class || '');
      var metaHtml;
      if(phase === 'resolved'){
        metaHtml = '<span class="hoh-fresh-meta">' + esc(metaText) + '</span>';
      } else {
        var hint = dim === 'race' ? 'race hidden' : 'class hidden';
        metaHtml = '<span class="hoh-fresh-meta" style="opacity:0.4;font-style:italic;">' + hint + '</span>';
      }
      return '<button type="button" class="' + cls + '"' + clickAttr + '>' +
        '<span class="hoh-fresh-media">' + media + '</span>' +
        '<span class="hoh-fresh-name">' + esc(c.name || 'Unknown') + '</span>' +
        metaHtml +
      '</button>';
    }).join('');
    var resultHtml = '';
    if(phase === 'resolved'){
      var won = state.won;
      var headlineCls = won ? ' hoh-fresh-duel-headline-win' : ' hoh-fresh-duel-headline-lose';
      var headline = won ? 'You spotted them.' : 'Wrong one. The culprit was outlined gold.';
      var majLabel = state.majClass || state.majRace || '';
      var culpritLabel = state.culpritClass || state.culpritRace || '';
      var dimLabel = dim === 'race' ? 'race' : 'class';
      resultHtml =
        '<div class="hoh-fresh-duel-result' + headlineCls + '">' +
          '<strong>' + headline + '</strong>' +
          '<span class="hoh-fresh-duel-result-sub">' + esc(majLabel) + ' lineup. The odd one was a ' + esc(culpritLabel) + ' (' + dimLabel + ').</span>' +
          '<span class="hoh-fresh-duel-result-sub hoh-fresh-duel-result-tap-hint">Tap any hero to see their card.</span>' +
          '<button type="button" class="hoh-fresh-coin-toss hoh-fresh-dice-continue" onclick="hohFreshFacesCulpritContinue()">' +
            '<span>Back to the Hall</span>' +
          '</button>' +
        '</div>';
    }
    /* R57: copy varies by dimension. Class mode keeps the original
       prompt; race mode pivots wording. */
    var eyebrow, sub;
    if(dim === 'race'){
      eyebrow = 'Find the Culprit';
      sub = 'Five of these heroes share a race. One doesn\'t belong. Spot the imposter.';
    } else {
      eyebrow = 'Find the Culprit';
      sub = 'Five of these heroes share a class. One doesn\'t belong. Spot the imposter.';
    }
    return '<div class="hoh-fresh-special hoh-fresh-special-culprit">' +
      eventHeaderHtml(eyebrow, sub, { centered: true }) +
      '<div class="hoh-fresh-culprit-grid">' + cardsHtml + '</div>' +
      resultHtml +
    '</div>';
  }
  window.hohFreshFacesCulpritPick = function(id){
    if(!currentSpecial || currentSpecial.type !== 'findCulprit') return;
    if(currentSpecial.phase !== 'idle') return;
    var won = (String(id) === String(currentSpecial.culpritId));
    currentSpecial.pickedId = id;
    currentSpecial.won = won;
    currentSpecial.phase = 'resolved';
    var container = _ffContainer();
    if(container) container.innerHTML = renderFindCulpritHtml(currentSpecial);
    if(won){
      if(typeof window.hohAddHeartsToBank === 'function'){
        try { window.hohAddHeartsToBank(5); } catch(_){}
      }
      if(typeof window.hohShowHeartToast === 'function'){
        try { window.hohShowHeartToast('Spotted them', 'Sharp eyes.'); } catch(_){}
      }
      try {
        var anchor = container && container.querySelector('.hoh-fresh-culprit-correct');
        if(anchor) flyDiceTraderHeartsToPill(anchor, 5);
      } catch(_){}
    } else {
      /* R57: -1 heart on wrong. Both class + race variants — the
         imposter is right in front of you, missing it costs 1. */
      try { loseHeartFromGuess('Wrong culprit.'); } catch(_){}
    }
    if(typeof startAutoTimer === 'function') setTimeout(startAutoTimer, 0);
  };
  window.hohFreshFacesCulpritContinue = function(){
    if(!currentSpecial || currentSpecial.type !== 'findCulprit') return;
    if(currentSpecial.phase !== 'resolved') return;
    currentSpecial = null;
    if(typeof window.hohFreshFacesRefresh === 'function'){
      try { window.hohFreshFacesRefresh({ fromInteractive: true }); } catch(_){}
    }
  };

  /* ================================================================ */
  /* R120-D: Find the Imposter — 3-character class-mismatch guess     */
  /* ================================================================ */
  /* Smaller, faster cousin of Find the Culprit. 2 chars share a class,
     1 is the imposter from a different class. Right pick = +5 hearts.
     Wrong = -1 heart (visible loss visual via loseHeartFromGuess).
     Distinct _eventKey + render path so the anti-repeat buffer treats
     it as separate from findCulprit and can fire alongside it. */
  function pickFindImposterEvent(){
    var all = _allChars().filter(function(c){ return c && c.id && c.character_class; });
    if(all.length < 3) return null;
    var byClass = {};
    all.forEach(function(c){
      var k = c.character_class;
      if(!byClass[k]) byClass[k] = [];
      byClass[k].push(c);
    });
    var sharedCandidates = Object.keys(byClass).filter(function(k){ return byClass[k].length >= 2; });
    if(!sharedCandidates.length) return null;
    var sharedClass = sharedCandidates[Math.floor(Math.random() * sharedCandidates.length)];
    var sharedPair = _shuffle(byClass[sharedClass]).slice(0, 2);
    var imposterPool = all.filter(function(c){ return c.character_class !== sharedClass; });
    if(!imposterPool.length) return null;
    var imposter = imposterPool[Math.floor(Math.random() * imposterPool.length)];
    var lineup = _shuffle(sharedPair.concat([imposter]));
    return {
      type: 'findImposter',
      chars: lineup,
      imposterId: imposter.id,
      sharedClass: sharedClass,
      imposterClass: imposter.character_class,
      phase: 'idle',
      pickedId: null,
      won: null,
      _eventKey: 'findImposter'
    };
  }
  function renderFindImposterHtml(state){
    var phase = state.phase || 'idle';
    var cardsHtml = state.chars.map(function(c){
      var isImposter = (c.id === state.imposterId);
      var isPicked = (c.id === state.pickedId);
      var cls = 'hoh-fresh-culprit-card hoh-fresh-imposter-card';
      if(phase === 'resolved'){
        if(isImposter) cls += ' hoh-fresh-culprit-correct';
        else if(isPicked) cls += ' hoh-fresh-culprit-wrong';
        cls += ' hoh-fresh-culprit-card-revealed';
      }
      /* R140-F: Use the shared offset-aware helper so the saved
         vertical crop (set via the edit form on the main hall) carries
         into the imposter cards. The user reported heads cropped here
         after fixing the main grid — this was the divergence. */
      var media = _eventCharImg(c, { lazy: true });
      var clickAttr;
      if(phase === 'idle'){
        clickAttr = ' onclick="hohFreshFacesImposterPick(\'' + esc(c.id) + '\')"';
      } else {
        clickAttr = ' onclick="if(window.hohOpenDetail)hohOpenDetail(\'' + esc(c.id) + '\')"';
      }
      var metaText = c.character_class || '';
      var metaHtml;
      if(phase === 'resolved'){
        metaHtml = '<span class="hoh-fresh-meta">' + esc(metaText) + '</span>';
      } else {
        metaHtml = '<span class="hoh-fresh-meta" style="opacity:0.4;font-style:italic;">class hidden</span>';
      }
      return '<button type="button" class="' + cls + '"' + clickAttr + '>' +
        '<span class="hoh-fresh-media">' + media + '</span>' +
        '<span class="hoh-fresh-name">' + esc(c.name || 'Unknown') + '</span>' +
        metaHtml +
      '</button>';
    }).join('');
    var resultHtml = '';
    if(phase === 'resolved'){
      var won = state.won;
      var headlineCls = won ? ' hoh-fresh-duel-headline-win' : ' hoh-fresh-duel-headline-lose';
      var headline = won ? 'You spotted them.' : 'Wrong one. The imposter was outlined gold.';
      resultHtml =
        '<div class="hoh-fresh-duel-result' + headlineCls + '">' +
          '<strong>' + headline + '</strong>' +
          '<span class="hoh-fresh-duel-result-sub">Two were ' + esc(state.sharedClass) + '. The odd one was a ' + esc(state.imposterClass) + '.</span>' +
          '<span class="hoh-fresh-duel-result-sub hoh-fresh-duel-result-tap-hint">Tap any hero to see their card.</span>' +
          '<button type="button" class="hoh-fresh-coin-toss hoh-fresh-dice-continue" onclick="hohFreshFacesImposterContinue()">' +
            '<span>Back to the Hall</span>' +
          '</button>' +
        '</div>';
    }
    return '<div class="hoh-fresh-special hoh-fresh-special-culprit hoh-fresh-special-imposter">' +
      eventHeaderHtml('Find the Imposter', 'Two of these heroes share a class. One doesn\'t belong. Spot the imposter.', { centered: true }) +
      '<div class="hoh-fresh-culprit-grid hoh-fresh-imposter-grid">' + cardsHtml + '</div>' +
      resultHtml +
    '</div>';
  }
  window.hohFreshFacesImposterPick = function(id){
    if(!currentSpecial || currentSpecial.type !== 'findImposter') return;
    if(currentSpecial.phase !== 'idle') return;
    var won = (String(id) === String(currentSpecial.imposterId));
    currentSpecial.pickedId = id;
    currentSpecial.won = won;
    currentSpecial.phase = 'resolved';
    var container = _ffContainer();
    if(container) container.innerHTML = renderFindImposterHtml(currentSpecial);
    if(won){
      if(typeof window.hohAddHeartsToBank === 'function'){
        try { window.hohAddHeartsToBank(5); } catch(_){}
      }
      if(typeof window.hohShowHeartToast === 'function'){
        try { window.hohShowHeartToast('Spotted the imposter', 'Sharp eyes.'); } catch(_){}
      }
      try {
        var anchor = container && container.querySelector('.hoh-fresh-culprit-correct');
        if(anchor) flyDiceTraderHeartsToPill(anchor, 5);
      } catch(_){}
    } else {
      try { loseHeartFromGuess('Wrong imposter.'); } catch(_){}
    }
    if(typeof startAutoTimer === 'function') setTimeout(startAutoTimer, 0);
  };
  window.hohFreshFacesImposterContinue = function(){
    if(!currentSpecial || currentSpecial.type !== 'findImposter') return;
    if(currentSpecial.phase !== 'resolved') return;
    currentSpecial = null;
    if(typeof window.hohFreshFacesRefresh === 'function'){
      try { window.hohFreshFacesRefresh({ fromInteractive: true }); } catch(_){}
    }
  };

  /* ================================================================ */
  /* R148-Z23: The Echo — nostalgia beat                              */
  /* ================================================================ */
  /* Show the player 3 of their hearted heroes ("the ones who stuck with
     you") and ask which is most memorable. Pure positive event — any
     pick wins +5 hearts. No penalty, no daily cap. Gates on having
     ≥3 hearted heroes so a brand-new player never sees it. Picks from
     the top 10 most-hearted to keep it feeling like a memory test.
     Type 2 (event) — sits alongside trivia/duels in the unlimited
     rotation, gated by the standard recent-event buffer so it doesn't
     repeat. */
  function pickEchoEvent(){
    var mine = (typeof heartedList === 'function') ? heartedList() : [];
    if(!mine || mine.length < 3) return null;
    /* Pull from the top 10 (or whatever the user has) so the choices
       feel like "the ones that stuck" rather than random hearted. */
    var pool = mine.slice(0, Math.min(10, mine.length));
    var trio = _shuffle(pool).slice(0, 3);
    if(trio.length < 3) return null;
    return {
      type: 'echo',
      chars: trio,
      phase: 'idle',
      pickedId: null,
      _eventKey: 'echo'
    };
  }
  function renderEchoHtml(state){
    var phase = state.phase || 'idle';
    var cardsHtml = state.chars.map(function(c){
      var isPicked = (c.id === state.pickedId);
      var cls = 'hoh-fresh-culprit-card hoh-fresh-echo-card';
      if(phase === 'resolved'){
        if(isPicked) cls += ' hoh-fresh-culprit-correct';
        cls += ' hoh-fresh-culprit-card-revealed';
      }
      var media = _eventCharImg(c, { lazy: true });
      var clickAttr;
      if(phase === 'idle'){
        clickAttr = ' onclick="hohFreshFacesEchoPick(\'' + esc(c.id) + '\')"';
      } else {
        clickAttr = ' onclick="if(window.hohOpenDetail)hohOpenDetail(\'' + esc(c.id) + '\')"';
      }
      var metaText = (c.race || '') + (c.character_class ? ' · ' + c.character_class : '');
      return '<button type="button" class="' + cls + '"' + clickAttr + '>' +
        '<span class="hoh-fresh-media">' + media + '</span>' +
        '<span class="hoh-fresh-name">' + esc(c.name || 'Unknown') + '</span>' +
        '<span class="hoh-fresh-meta">' + esc(metaText) + '</span>' +
      '</button>';
    }).join('');
    var resultHtml = '';
    if(phase === 'resolved'){
      var pickedChar = (state.chars || []).filter(function(c){ return c.id === state.pickedId; })[0] || {};
      resultHtml =
        '<div class="hoh-fresh-duel-result hoh-fresh-duel-headline-win">' +
          '<strong>' + esc(pickedChar.name || 'They') + ' stays with you.</strong>' +
          '<span class="hoh-fresh-duel-result-sub">Memory is its own reward.</span>' +
          '<span class="hoh-fresh-duel-result-sub hoh-fresh-duel-result-tap-hint">Tap any hero to revisit them.</span>' +
          '<button type="button" class="hoh-fresh-coin-toss hoh-fresh-dice-continue" onclick="hohFreshFacesEchoContinue()">' +
            '<span>Back to the Hall</span>' +
          '</button>' +
        '</div>';
    }
    return '<div class="hoh-fresh-special hoh-fresh-special-culprit hoh-fresh-special-echo">' +
      eventHeaderHtml('The Echo', 'Three heroes you\'ve lifted. Which one stays with you?', { centered: true }) +
      '<div class="hoh-fresh-culprit-grid hoh-fresh-imposter-grid">' + cardsHtml + '</div>' +
      resultHtml +
    '</div>';
  }
  window.hohFreshFacesEchoPick = function(id){
    if(!currentSpecial || currentSpecial.type !== 'echo') return;
    if(currentSpecial.phase !== 'idle') return;
    currentSpecial.pickedId = id;
    currentSpecial.phase = 'resolved';
    var container = _ffContainer();
    if(container) container.innerHTML = renderEchoHtml(currentSpecial);
    if(typeof window.hohAddHeartsToBank === 'function'){
      try { window.hohAddHeartsToBank(5); } catch(_){}
    }
    if(typeof window.hohShowHeartToast === 'function'){
      try { window.hohShowHeartToast('Remembered', 'A memory carries weight.'); } catch(_){}
    }
    try {
      var anchor = container && container.querySelector('.hoh-fresh-culprit-correct');
      if(anchor) flyDiceTraderHeartsToPill(anchor, 5);
    } catch(_){}
    if(typeof startAutoTimer === 'function') setTimeout(startAutoTimer, 0);
  };
  window.hohFreshFacesEchoContinue = function(){
    if(!currentSpecial || currentSpecial.type !== 'echo') return;
    if(currentSpecial.phase !== 'resolved') return;
    currentSpecial = null;
    if(typeof window.hohFreshFacesRefresh === 'function'){
      try { window.hohFreshFacesRefresh({ fromInteractive: true }); } catch(_){}
    }
  };

  /* ================================================================ */
  /* R124-I: A Familiar Crowd — spot the unliked one                  */
  /* ================================================================ */
  /* Themed for engaged users who already love a chunk of the hall.
     Gates on 10+ hearted heroes (otherwise the event can't fire).
     Shows 9 hearts-given + 1 stranger you haven't hearted yet, all
     in a 5x2 grid. Pick the stranger (the odd one out).
     +5 hearts on right pick — and the visual nudges the user toward
     the new face, encouraging discovery. -1 heart on wrong pick. */
  function _isHearted(id){
    if(typeof window.getHeartCount === 'function'){
      try { return window.getHeartCount(id) > 0; } catch(_){}
    }
    return false;
  }
  function pickFamiliarCrowdEvent(){
    var all = _allChars().filter(function(c){ return c && c.id && !c.is_hidden; });
    if(!all || all.length < 11) return null;
    var hearted = all.filter(function(c){ return _isHearted(c.id); });
    if(hearted.length < 9) return null; /* need at least 9 liked */
    var unhearted = all.filter(function(c){ return !_isHearted(c.id); });
    if(unhearted.length < 1) return null; /* need at least 1 stranger */
    /* User must have engaged enough to feel the gate. The user said
       10+ hearted; we treat that as 10+ DISTINCT hearted heroes. */
    if(hearted.length < 10) return null;
    /* Pick a random host from a flavorful class. */
    var hostClasses = {Bard:1,Sorcerer:1,Wizard:1,Charlatan:1,Cleric:1,Warlock:1};
    var hostPool = all.filter(function(c){ return c && c.character_class && hostClasses[c.character_class]; });
    if(!hostPool.length) hostPool = all;
    var host = hostPool[Math.floor(Math.random() * hostPool.length)];
    /* Pick 9 random from hearted, 1 random from unhearted, shuffle. */
    var nineLiked = _shuffle(hearted).slice(0, 9);
    var stranger = unhearted[Math.floor(Math.random() * unhearted.length)];
    var lineup = _shuffle(nineLiked.concat([stranger]));
    return {
      type: 'familiarCrowd',
      host: host,
      chars: lineup,
      strangerId: stranger.id,
      phase: 'idle',
      pickedId: null,
      won: null,
      _eventKey: 'familiarCrowd'
    };
  }
  function renderFamiliarCrowdHtml(state){
    var phase = state.phase || 'idle';
    /* R128: Host card removed per user direction. The crowd grid IS
       the focus — adding a host above it diluted the "spot the
       stranger" framing. The eventHeader (centered title + subtitle)
       sits directly above the 10-card grid now, and CSS for
       .hoh-fresh-familiar-grid handles its own top padding so no
       layout breakage on desktop or mobile. state.host stays in the
       picked event payload (harmless) in case a future variant wants
       it back without re-plumbing the picker. */
    var cardsHtml = state.chars.map(function(c){
      var isStranger = (c.id === state.strangerId);
      var isPicked = (c.id === state.pickedId);
      var cls = 'hoh-fresh-culprit-card hoh-fresh-familiar-card';
      if(phase === 'resolved'){
        if(isStranger) cls += ' hoh-fresh-culprit-correct';
        else if(isPicked) cls += ' hoh-fresh-culprit-wrong';
        cls += ' hoh-fresh-culprit-card-revealed';
      }
      /* R140-F: Offset-aware helper. See _eventCharImg above. */
      var media = _eventCharImg(c, { lazy: true });
      var clickAttr;
      if(phase === 'idle'){
        clickAttr = ' onclick="hohFreshFacesFamiliarCrowdPick(\'' + esc(c.id) + '\')"';
      } else {
        clickAttr = ' onclick="if(window.hohOpenDetail)hohOpenDetail(\'' + esc(c.id) + '\')"';
      }
      return '<button type="button" class="' + cls + '"' + clickAttr + '>' +
        '<span class="hoh-fresh-media">' + media + '</span>' +
        '<span class="hoh-fresh-name">' + esc(c.name || 'Unknown') + '</span>' +
      '</button>';
    }).join('');
    var resultHtml = '';
    if(phase === 'resolved'){
      var won = state.won;
      var headlineCls = won ? ' hoh-fresh-duel-headline-win' : ' hoh-fresh-duel-headline-lose';
      var headline = won ? 'You spotted them.' : 'A familiar face. The stranger was outlined gold.';
      resultHtml =
        '<div class="hoh-fresh-duel-result' + headlineCls + '">' +
          '<strong>' + headline + '</strong>' +
          '<span class="hoh-fresh-duel-result-sub">Nine you\'ve hearted. One you haven\'t.</span>' +
          '<span class="hoh-fresh-duel-result-sub hoh-fresh-duel-result-tap-hint">Tap any hero to see their card.</span>' +
          '<button type="button" class="hoh-fresh-coin-toss hoh-fresh-dice-continue" onclick="hohFreshFacesFamiliarCrowdContinue()">' +
            '<span>Back to the Hall</span>' +
          '</button>' +
        '</div>';
    }
    return '<div class="hoh-fresh-special hoh-fresh-special-culprit hoh-fresh-special-familiar">' +
      eventHeaderHtml('A Familiar Crowd', 'Nine of these heroes you\'ve already hearted. One slipped in unnoticed. Spot the stranger.', { centered: true }) +
      '<div class="hoh-fresh-culprit-grid hoh-fresh-familiar-grid">' + cardsHtml + '</div>' +
      resultHtml +
    '</div>';
  }
  window.hohFreshFacesFamiliarCrowdPick = function(id){
    if(!currentSpecial || currentSpecial.type !== 'familiarCrowd') return;
    if(currentSpecial.phase !== 'idle') return;
    var won = (String(id) === String(currentSpecial.strangerId));
    currentSpecial.pickedId = id;
    currentSpecial.won = won;
    currentSpecial.phase = 'resolved';
    var container = _ffContainer();
    if(container) container.innerHTML = renderFamiliarCrowdHtml(currentSpecial);
    if(won){
      if(typeof window.hohAddHeartsToBank === 'function'){
        try { window.hohAddHeartsToBank(5); } catch(_){}
      }
      if(typeof window.hohShowHeartToast === 'function'){
        try { window.hohShowHeartToast('Found them', 'You spotted the stranger.'); } catch(_){}
      }
      try {
        var anchor = container && container.querySelector('.hoh-fresh-culprit-correct');
        if(anchor) flyDiceTraderHeartsToPill(anchor, 5);
      } catch(_){}
    } else {
      try { loseHeartFromGuess('Wrong pick.'); } catch(_){}
    }
    if(typeof startAutoTimer === 'function') setTimeout(startAutoTimer, 0);
  };
  window.hohFreshFacesFamiliarCrowdContinue = function(){
    if(!currentSpecial || currentSpecial.type !== 'familiarCrowd') return;
    if(currentSpecial.phase !== 'resolved') return;
    currentSpecial = null;
    if(typeof window.hohFreshFacesRefresh === 'function'){
      try { window.hohFreshFacesRefresh({ fromInteractive: true }); } catch(_){}
    }
  };

  /* --- Coin Flip Bet ---
     5 hearts to bet on heads or tails, win 10 (net +5). Hosted by a
     rogue/monk character. */
  function renderCoinBetHtml(state){
    var phase = state.phase || 'idle';
    var host = state.host || {};
    var hostHtml = '<div class="hoh-fresh-coinbet-host">' + _miniHeroCard(host) + '</div>';
    var coinSpinClass = '';
    var coinFaceClass = '';
    if(phase === 'flipping') coinSpinClass = ' hoh-fresh-coin-flipping';
    else if(phase === 'resolved'){
      coinFaceClass = state.result === 'heads' ? ' hoh-fresh-coin-heads' : ' hoh-fresh-coin-tails';
    }
    var coinHtml =
      '<div class="hoh-fresh-coin' + coinSpinClass + coinFaceClass + '" aria-hidden="true">' +
        '<div class="hoh-fresh-coin-face hoh-fresh-coin-face-heads"><span>H</span></div>' +
        '<div class="hoh-fresh-coin-face hoh-fresh-coin-face-tails"><span>T</span></div>' +
      '</div>';
    var ctaHtml = '';
    if(phase === 'idle'){
      /* R140-Z3j: Stake/payout chip pair removed by request — the
         prompt sentence ("Bet on a side. X to play, Y to win.")
         already conveys the same info more cleanly. The Bet Heads /
         Bet Tails buttons carry the actual mechanic. Removed the
         .hoh-fresh-duel-stakes block to reduce visual noise. */
      ctaHtml =
        '<div class="hoh-fresh-duel-cta">' +
          '<div class="hoh-fresh-duel-prompt">Bet on a side. ' + state.cost + ' to play, ' + state.payout + ' to win.</div>' +
          '<div class="hoh-fresh-coinbet-actions">' +
            '<button type="button" class="hoh-fresh-coin-toss" onclick="hohFreshFacesCoinBetPick(\'heads\')"><span>Bet Heads</span></button>' +
            '<button type="button" class="hoh-fresh-coin-toss" onclick="hohFreshFacesCoinBetPick(\'tails\')"><span>Bet Tails</span></button>' +
          '</div>' +
        '</div>';
    } else if(phase === 'resolved'){
      var won = state.won;
      var headlineCls = won ? ' hoh-fresh-duel-headline-win' : ' hoh-fresh-duel-headline-lose';
      var headline = won ? 'You called it. +' + state.payout + ' hearts.' : 'The other side. No payout.';
      ctaHtml =
        '<div class="hoh-fresh-duel-result' + headlineCls + '">' +
          '<strong>' + headline + '</strong>' +
          '<span class="hoh-fresh-duel-result-sub">You bet ' + esc(state.pickedSide) + '. The coin showed ' + esc(state.result) + '.</span>' +
          '<button type="button" class="hoh-fresh-coin-toss hoh-fresh-dice-continue" onclick="hohFreshFacesCoinBetContinue()">' +
            '<span>Back to the Hall</span>' +
          '</button>' +
        '</div>';
    }
    return '<div class="hoh-fresh-special hoh-fresh-special-coinbet' + (state.mythicHost ? ' hoh-fresh-special-mythic-host' : '') + '">' +
      eventHeaderHtml('A Side Bet', 'A coin on the table. Pick heads or tails. The host\'s smile gives nothing away.', { centered: true }) +
      hostHtml +
      '<div class="hoh-fresh-coin-stage">' + coinHtml + '</div>' +
      ctaHtml +
    '</div>';
  }
  window.hohFreshFacesCoinBetPick = function(side){
    if(!currentSpecial || currentSpecial.type !== 'coinBet') return;
    if(currentSpecial.phase !== 'idle') return;
    if(side !== 'heads' && side !== 'tails') return;
    var cost = currentSpecial.cost || 5;
    var bank = (typeof window.heartsRemainingToday === 'function') ? window.heartsRemainingToday() : 0;
    if(bank < cost){
      if(typeof window.hohShowHeartToast === 'function'){
        try { window.hohShowHeartToast('Not enough hearts', 'You need ' + cost + ' to bet.'); } catch(_){}
      }
      return;
    }
    /* Pay the cost via direct ledger. */
    try {
      var raw = parseInt(localStorage.getItem('hoh-heart-bank-v1') || '0', 10) || 0;
      try{ window.hohHeartHint && window.hohHeartHint('spend_feature'); }catch(e){}
      localStorage.setItem('hoh-heart-bank-v1', String(Math.max(0, raw - cost)));
      try { document.dispatchEvent(new CustomEvent('hoh:hearts-bumped', { detail: { spent: cost } })); } catch(_){}
    } catch(_){}
    if(typeof window.hohRenderHeaderCounter === 'function'){
      try { window.hohRenderHeaderCounter(); } catch(_){}
    }
    var result = (Math.random() < 0.5) ? 'heads' : 'tails';
    currentSpecial.pickedSide = side;
    currentSpecial.phase = 'flipping';
    var container = _ffContainer();
    if(container) container.innerHTML = renderCoinBetHtml(currentSpecial);
    setTimeout(function(){
      if(!currentSpecial || currentSpecial.type !== 'coinBet') return;
      currentSpecial.result = result;
      currentSpecial.won = (side === result);
      currentSpecial.phase = 'resolved';
      if(container) container.innerHTML = renderCoinBetHtml(currentSpecial);
      if(currentSpecial.won){
        if(typeof window.hohAddHeartsToBank === 'function'){
          try{ window.hohHeartHint && window.hohHeartHint('earn_explore_special'); }catch(e){}
          try { window.hohAddHeartsToBank(currentSpecial.payout); } catch(_){}
        }
        if(typeof window.hohShowHeartToast === 'function'){
          try { window.hohShowHeartToast('+' + currentSpecial.payout + ' hearts', 'You called it right.'); } catch(_){}
        }
        try {
          var anchor = container && container.querySelector('.hoh-fresh-coin');
          if(anchor) flyDiceTraderHeartsToPill(anchor, Math.min(currentSpecial.payout, 10));
        } catch(_){}
        /* R65: mythic host = +30 bonus on win. */
        if(currentSpecial.mythicHost){
          setTimeout(function(){
            applyMythicHostBonus('#hoh-fresh-faces .hoh-fresh-coinbet-host .hoh-fresh-card-host');
          }, 380);
        }
      }
      if(typeof startAutoTimer === 'function') setTimeout(startAutoTimer, 0);
    }, 1400);
  };
  window.hohFreshFacesCoinBetContinue = function(){
    if(!currentSpecial || currentSpecial.type !== 'coinBet') return;
    if(currentSpecial.phase !== 'resolved') return;
    currentSpecial = null;
    if(typeof window.hohFreshFacesRefresh === 'function'){
      try { window.hohFreshFacesRefresh({ fromInteractive: true }); } catch(_){}
    }
  };

  /* --- Guess Hearts ---
     Number input 0-120. Within 30 of the actual = win 3 hearts.
     Mobile gets numeric keypad via inputmode="numeric". */

  /* --- Popularity Contest ---
     4 chars in a row, user reorders by perceived hearts (high to
     low) using up/down arrows. Works on touch + click natively
     (no drag). +3 hearts on exact match. */
  function renderPopularityHtml(state){
    var phase = state.phase || 'idle';
    var order = state.order || [];
    /* Map id → char for quick lookup. */
    var byId = {};
    state.chars.forEach(function(c){ byId[c.id] = c; });
    var rowsHtml = order.map(function(id, idx){
      var c = byId[id] || {};
      /* R140-F: Offset-aware helper. */
      var media = _eventCharImg(c, { lazy: true });
      var rank = idx + 1;
      var arrowsHtml = '';
      if(phase === 'idle'){
        arrowsHtml =
          '<div class="hoh-fresh-pop-arrows">' +
            '<button type="button" class="hoh-fresh-pop-arrow" ' + (idx === 0 ? 'disabled' : '') +
              ' onclick="hohFreshFacesPopularityMove(' + idx + ',-1)" aria-label="Move up">&#9650;</button>' +
            '<button type="button" class="hoh-fresh-pop-arrow" ' + (idx === order.length - 1 ? 'disabled' : '') +
              ' onclick="hohFreshFacesPopularityMove(' + idx + ',1)" aria-label="Move down">&#9660;</button>' +
          '</div>';
      }
      var revealCls = '';
      var revealHearts = '';
      if(phase === 'resolved'){
        var correctIdx = state.correctOrder.indexOf(id);
        revealCls = (correctIdx === idx) ? ' hoh-fresh-pop-row-correct' : ' hoh-fresh-pop-row-wrong';
        revealCls += ' hoh-fresh-pop-row-revealed';
        revealHearts = '<span class="hoh-fresh-pop-hearts">' + (c.hearts || 0) + ' &#9829;&#xFE0E;</span>';
      }
      /* R57: rows are not clickable mid-guess (the arrow buttons drive
         reordering instead). On resolved, clicking the row opens the
         hero detail so the user can poke at the people they just
         ranked. */
      var rowClickAttr = (phase === 'resolved')
        ? ' onclick="if(window.hohOpenDetail)hohOpenDetail(\'' + esc(id) + '\')" role="button" tabindex="0" style="cursor:pointer;"'
        : '';
      return '<div class="hoh-fresh-pop-row' + revealCls + '" data-id="' + esc(id) + '"' + rowClickAttr + '>' +
        '<span class="hoh-fresh-pop-rank">' + rank + '</span>' +
        '<span class="hoh-fresh-pop-media">' + media + '</span>' +
        '<span class="hoh-fresh-pop-name">' + esc(c.name || 'Unknown') + '</span>' +
        revealHearts +
        arrowsHtml +
      '</div>';
    }).join('');
    var ctaHtml = '';
    if(phase === 'idle'){
      /* R140-Z3h: Math hint removed by request — header copy already
         frames the goal ("Rank these heroes by how loved they are."),
         the explicit point breakdown was noise. */
      ctaHtml = '<button type="button" class="hoh-fresh-coin-toss hoh-fresh-pop-go" onclick="hohFreshFacesPopularitySubmit()">' +
        '<span>Lock In Order</span>' +
      '</button>';
    } else {
      /* R62: tiered scoring. Headline + sub vary by correctCount. */
      var rightCount = state.rightCount || 0;
      var awarded = state.awarded || 0;
      var headlineCls = (awarded > 0)
        ? ' hoh-fresh-duel-headline-win'
        : ' hoh-fresh-duel-headline-lose';
      var headline, sub;
      if(rightCount === 4){
        headline = 'Perfect ranking.';
        sub = 'You read the Hall like a book.';
      } else if(rightCount === 3){
        headline = 'Three of four.';
        sub = 'Almost. One slipped past you.';
      } else if(rightCount === 2){
        headline = 'Two of four.';
        sub = 'Half right is half a win.';
      } else if(rightCount === 1){
        headline = 'One right.';
        sub = 'Closer than nothing.';
      } else {
        headline = 'None right. -1 heart.';
        sub = 'The numbers do not lie.';
      }
      ctaHtml =
        '<div class="hoh-fresh-duel-result' + headlineCls + '">' +
          '<strong>' + headline + '</strong>' +
          '<span class="hoh-fresh-duel-result-sub">' + sub + '</span>' +
          '<button type="button" class="hoh-fresh-coin-toss hoh-fresh-dice-continue" onclick="hohFreshFacesPopularityContinue()">' +
            '<span>Back to the Hall</span>' +
          '</button>' +
        '</div>';
    }
    return '<div class="hoh-fresh-special hoh-fresh-special-popularity">' +
      eventHeaderHtml('Popularity Contest', 'Rank these heroes by how loved they are.', { centered: true }) +
      '<div class="hoh-fresh-pop-list">' + rowsHtml + '</div>' +
      ctaHtml +
    '</div>';
  }
  window.hohFreshFacesPopularityMove = function(idx, dir){
    if(!currentSpecial || currentSpecial.type !== 'popularity') return;
    if(currentSpecial.phase !== 'idle') return;
    var order = currentSpecial.order;
    var ni = idx + dir;
    if(ni < 0 || ni >= order.length) return;
    var tmp = order[idx]; order[idx] = order[ni]; order[ni] = tmp;
    var container = _ffContainer();
    if(container) container.innerHTML = renderPopularityHtml(currentSpecial);
  };
  window.hohFreshFacesPopularitySubmit = function(){
    if(!currentSpecial || currentSpecial.type !== 'popularity') return;
    if(currentSpecial.phase !== 'idle') return;
    /* R102: Reward map rebuilt — each right slot is worth +5 hearts,
       a perfect 4/4 jumps to +50 (instead of scaling linearly to +20).
       Reward map:
         4 right => +50 hearts (perfect — bonus on top of the +20 floor)
         3 right => +15 hearts
         2 right => +10 hearts
         1 right =>  +5 hearts
         0 right =>  -1 heart (penalty + heart-loss visual) */
    var rightCount = 0;
    currentSpecial.order.forEach(function(id, i){
      if(String(id) === String(currentSpecial.correctOrder[i])) rightCount++;
    });
    var awarded;
    var toastSub;
    if(rightCount === 4){ awarded = 50; toastSub = 'Perfect ranking.'; }
    else if(rightCount === 3){ awarded = 15; toastSub = 'Three of four.'; }
    else if(rightCount === 2){ awarded = 10; toastSub = 'Two of four.'; }
    else if(rightCount === 1){ awarded = 5;  toastSub = 'One right.'; }
    else { awarded = 0; toastSub = ''; }
    currentSpecial.rightCount = rightCount;
    currentSpecial.awarded = awarded;
    currentSpecial.won = rightCount === 4;
    currentSpecial.phase = 'resolved';
    var container = _ffContainer();
    if(container) container.innerHTML = renderPopularityHtml(currentSpecial);
    if(awarded > 0){
      if(typeof window.hohAddHeartsToBank === 'function'){
        try { window.hohAddHeartsToBank(awarded); } catch(_){}
      }
      if(typeof window.hohShowHeartToast === 'function'){
        try { window.hohShowHeartToast('Nicely ranked', toastSub); } catch(_){}
      }
      try {
        var anchor = container && container.querySelector('.hoh-fresh-pop-list');
        /* R98: Cap the visual flock at 12 sprites — at +30 hearts the
           uncapped flock takes 3.3s to finish, which feels laggy. The
           wallet still gets the full +30; only the sprite count is
           capped to keep the animation snappy. Same pattern as the
           legendary shop confetti grant. */
        if(anchor) flyDiceTraderHeartsToPill(anchor, Math.min(awarded, 12));
      } catch(_){}
    } else if(rightCount === 0){
      /* Penalty only on a complete miss — same heart-loss visual the
         other unlimited guess events use. */
      try { loseHeartFromGuess('Wrong order.'); } catch(_){}
    }
    if(typeof startAutoTimer === 'function') setTimeout(startAutoTimer, 0);
  };
  window.hohFreshFacesPopularityContinue = function(){
    if(!currentSpecial || currentSpecial.type !== 'popularity') return;
    if(currentSpecial.phase !== 'resolved') return;
    currentSpecial = null;
    if(typeof window.hohFreshFacesRefresh === 'function'){
      try { window.hohFreshFacesRefresh({ fromInteractive: true }); } catch(_){}
    }
  };

  /* ================================================================ */
  /* R67: Heart-stream events — Rich Get Richer + Underdog            */
  /* ================================================================ */
  /* Both events render a small grid of character "stream targets"
     and a countdown timer. Holding (mouse or touch) on a target
     fires hearts at a ramping rate. Each heart goes through the
     standard hohHeart pipeline (which handles wallet decrement,
     server cap, and the +1 character heart count). For Underdog the
     hearts ALSO bump the wander quest counter so the user can
     complete the daily wander quest in a focused burst. */

  function renderStreamTargetCard(c, opts){
    opts = opts || {};
    var img = c.image_thumb_url || c.image_url || '';
    var firstLetter = esc((c.name || '?').charAt(0));
    /* R134-C: Bake saved object-position offset into the IMG so stream
       target cards (Underdog, Rich Get Richer, Feed Your Own, Promotion,
       Familiar Crowd hosts) honor the editor's reframe on first paint.
       Falls back to 50% (centered) when no offset stored. */
    var streamPos = (typeof window.hohImgObjPos === 'function')
      ? window.hohImgObjPos(c.id, 'primary')
      : ('50% ' + ((typeof window.hohGetImageOffsetY === 'function') ? window.hohGetImageOffsetY(c.id, 'primary') : 50) + '%');
    var streamStyle = (typeof window.hohImgStyle === 'function') ? window.hohImgStyle(c.id, 'primary') : ('object-position:' + streamPos + ';');
    var media = img
      ? '<img src="' + esc(img) + '" alt="" loading="eager" decoding="async" crossorigin="anonymous" data-letter="' + firstLetter + '" style="' + streamStyle + '" onerror="if(window.hohImgFallback)hohImgFallback(this);">'
      : '<div class="hoh-fresh-letter">' + firstLetter + '</div>';
    var meta = (c.race || '') + (c.character_class ? ' · ' + c.character_class : '');
    var mythicCls = (typeof window.hohIsMythicCharacter === 'function' && window.hohIsMythicCharacter(c)) ? ' hoh-fresh-stream-target-mythic' : '';
    return '<div class="hoh-fresh-stream-target' + mythicCls + '" data-stream-id="' + esc(c.id) + '" data-stream-mode="' + (opts.mode || 'mythic') + '" role="button" tabindex="0" aria-label="Hold to give hearts to ' + esc(c.name || 'this hero') + '">' +
      '<div class="hoh-fresh-stream-target-media">' + media + '</div>' +
      '<div class="hoh-fresh-stream-target-body">' +
        '<div class="hoh-fresh-stream-target-name">' + esc(c.name || 'Unnamed') + '</div>' +
        '<div class="hoh-fresh-stream-target-meta">' + esc(meta) + '</div>' +
      '</div>' +
      '<div class="hoh-fresh-stream-target-hint" aria-hidden="true">Hold to give hearts</div>' +
    '</div>';
  }

  /* R93: The R87 mini bar is replaced with the FULL Explore the Hall
     wander bar (milestones + click-to-claim) embedded inside stream
     events. paintQuestProgress already queries by ID, so reusing the
     same #hoh-fresh-quest-fill / #hoh-fresh-quest-label / #hoh-fresh-
     quest-mile-N IDs makes the bar live-update through the existing
     bumpFfHeartsToday → paintQuestProgress chain. */
  /* R140-Z: Shared wander-bar renderer used by every render path that
     wants the "Explore the Hall" bar. Pre-fills the fill width and
     milestone state so the DOM lands at the user's actual progress on
     first paint — without this, paintQuestProgress writes the width
     AFTER the CSS transition is live and the bar animates from 0% to
     current%, which the user sees as a "reset" flash on every event
     render that includes the bar (Bustling Streets, Two Legends,
     Three Legends, Crowded Town, etc.). The inStream flag controls
     the wrapper class — stream events get a tighter visual variant. */
  function _wanderBarHtml(opts){
    var inStream = !!(opts && opts.inStream);
    /* The Battlegrounds takeover happens in hohRenderFreshFaces (the whole
       panel is replaced once the daily is complete), so the bar here always
       renders the normal wander bar — never the entry inside an event. */
    var n = 0;
    try { n = (typeof getFfHeartsToday === 'function') ? getFfHeartsToday() : 0; } catch(_){ n = 0; }
    var target = WANDER_QUEST_TARGET || 50;
    var capped = Math.min(n, target);
    var pct = (capped / target) * 100;
    var label = capped + '/' + target;
    var allClaimed = WANDER_MILESTONES.every(isMilestoneClaimed);
    var wrapperCls = 'hoh-fresh-quest' + (inStream ? ' hoh-fresh-quest-in-stream' : '') + (allClaimed ? ' hoh-fresh-quest-complete' : '');
    var mileHtml = function(m, ariaLabel, tipText, extraCls, iconHtml){
      var hit = (n >= m);
      var claimed = isMilestoneClaimed(m);
      var stateCls = '';
      if(hit && claimed) stateCls += ' hoh-fresh-quest-mile-claimed hoh-fresh-quest-mile-hit';
      else if(hit) stateCls += ' hoh-fresh-quest-mile-ready hoh-fresh-quest-mile-hit';
      var icon = iconHtml || '<span class="hoh-tk-icon" aria-hidden="true"></span>';
      /* R406: the pip's position is DERIVED from its threshold, inline.
         It used to live in six hardcoded `left:` rules in the theme CSS
         keyed on the milestone number, so moving the ladder silently
         moved every pip to the wrong place on the rail. Now the ladder
         is the only source of truth and the CSS cannot fall out of step
         with it. Pip 0 lands on 0% — the very start of the bar — which
         is the whole point of this round. */
      var leftPct = target > 0 ? ((m / target) * 100) : 0;
      /* R406: a pip sitting ON an edge of the rail gets an edge class,
         because its tooltip is centred on the pip and a centred tooltip
         at 0% or 100% hangs straight off the side of the panel. The
         edge classes re-anchor it inward. This was already true of the
         last pip and nobody had hit it hard; it becomes unavoidable now
         the FIRST thing a new visitor hovers is a pip at 0%. */
      if(leftPct <= 0.001)   stateCls += ' hoh-fresh-quest-mile-edge-start';
      if(leftPct >= 99.999)  stateCls += ' hoh-fresh-quest-mile-edge-end';
      return '<span class="hoh-fresh-quest-mile hoh-fresh-quest-mile-' + m + (extraCls ? ' ' + extraCls : '') + stateCls + '"' +
        ' id="hoh-fresh-quest-mile-' + m + '" data-quest-milestone="' + m + '" tabindex="0" role="button"' +
        ' style="left:' + leftPct.toFixed(3) + '%;"' +
        ' aria-label="' + ariaLabel + '"' +
        ' onclick="hohFreshFacesClaimQuestMilestone(' + m + ')"' +
        ' onkeydown="if(event.key===\'Enter\'||event.key===\' \'){event.preventDefault();hohFreshFacesClaimQuestMilestone(' + m + ');}">' +
        icon +
        '<span class="hoh-fresh-quest-mile-tip" aria-hidden="true">' + tipText + '</span>' +
      '</span>';
    };
    /* R489: this rail contains real keyboard-focusable reward controls. Keeping
       aria-hidden on their parent hid every one from screen readers while the
       browser could still focus them, an invalid and confusing state. */
    return '<div class="' + wrapperCls + '" id="hoh-fresh-quest" role="region" aria-label="Explore rewards">' +
      '<div class="hoh-fresh-quest-head">' +
        /* R436: the only route back to the intro. A player who pressed
           Start and later wonders what the six circles pay had no way
           to ask; the label they are already reading is the answer. */
        '<button type="button" class="hoh-fresh-quest-title hoh-fresh-quest-title-btn"' +
          ' onclick="hohExploreIntroOpen()" title="How this works">Explore the Hall</button>' +
        '<span class="hoh-fresh-quest-label" id="hoh-fresh-quest-label">' + label + '</span>' +
      '</div>' +
      '<div class="hoh-fresh-quest-track">' +
        '<span class="hoh-fresh-quest-fill" id="hoh-fresh-quest-fill" style="width:' + pct.toFixed(1) + '%;"></span>' +
        /* R406: heart→ticket→heart→ticket→heart→ticket, sliding ten
           hearts left. Hearts at 0/20/40 (blue, blue, purple), tickets
           at 10/30/50. The 0 pip is the free one and it sits on the
           very start of the rail, lit and claimable before the user has
           done anything at all. */
        mileHtml(0,  'Claim a free gift to start',      'Free, right now. Click to claim.', 'hoh-fresh-quest-mile-heart-bonus hoh-fresh-quest-mile-free', '<span class="hoh-fresh-quest-mile-heart-icon" aria-hidden="true">&#9829;&#xFE0E;</span>') +
        mileHtml(10, 'Claim raffle ticket at 10 hearts',   'Heart 10 heroes. Click to claim your first raffle ticket.') +
        mileHtml(20, 'Claim a bonus at 20 hearts', 'Heart 20 heroes. Click to claim a bonus.', 'hoh-fresh-quest-mile-heart-bonus', '<span class="hoh-fresh-quest-mile-heart-icon" aria-hidden="true">&#9829;&#xFE0E;</span>') +
        mileHtml(30, 'Claim raffle ticket at 30 hearts',   'Heart 30 heroes. Click to claim another raffle ticket.') +
        mileHtml(40, 'Claim the big bonus at 40 hearts', 'Heart 40 heroes. Click for the big bonus.', 'hoh-fresh-quest-mile-heart-bonus hoh-fresh-quest-mile-capstone', '<span class="hoh-fresh-quest-mile-heart-icon" aria-hidden="true">&#9829;&#xFE0E;</span>') +
        /* R489: the last node is a real gift, not the same ticket glyph for a
           third time. Separate purple box + blue ribbon ink keeps the icon
           unmistakably FramedFantasy without turning it into a gradient CTA. */
        mileHtml(50, 'Open the final Explore gift for a raffle ticket, Battlegrounds entry, and free card pack',
          'Heart 50 heroes. Open your gift to get a raffle ticket, a Battlegrounds entry, and a free card pack.',
          'hoh-fresh-quest-mile-gift',
          '<span class="hoh-fresh-quest-gift-icon" aria-hidden="true"><svg viewBox="0 0 24 24"><path class="hoh-gift-box-ink" d="M4 9h16v12H4V9ZM3 6h18v5H3V6Z"/><path class="hoh-gift-ribbon-ink" d="M10.8 6h2.4v15h-2.4V6ZM11.7 6H7.3C4.7 6 3.2 5.1 3.2 3.5 3.2 2 4.4 1 6 1c2.2 0 4.1 1.8 5.7 5Zm-2.6-1.2C8 3 6.9 2.2 6 2.2c-.8 0-1.4.5-1.4 1.2 0 .8 1 1.4 2.7 1.4h1.8ZM12.3 6h4.4c2.6 0 4.1-.9 4.1-2.5C20.8 2 19.6 1 18 1c-2.2 0-4.1 1.8-5.7 5Zm2.6-1.2C16 3 17.1 2.2 18 2.2c.8 0 1.4.5 1.4 1.2 0 .8-1 1.4-2.7 1.4h-1.8Z" fill-rule="evenodd"/></svg></span>') +
      '</div>' +
      /* R150: subtle daily-reset countdown, shown only once every
         milestone is claimed. paintQuestProgress keeps it current. */
      '<div class="hoh-fresh-quest-reset" id="hoh-fresh-quest-reset"' + (allClaimed ? '' : ' style="display:none;"') + '>' +
        (allClaimed ? _wanderResetCountdownText() : '') +
      '</div>' +
    '</div>';
  }
  /* Back-compat wrapper. Existing event renderers call _streamWanderBarHtml
     directly; route them to the shared helper with the in-stream flag. */
  function _streamWanderBarHtml(){
    return _wanderBarHtml({ inStream: true });
  }
  /* R93: paintStreamWanderBar now just calls paintQuestProgress —
     same DOM IDs, no separate logic needed. Kept as a wrapper for
     callers that still reference it. */
  function paintStreamWanderBar(){
    try { if(typeof paintQuestProgress === 'function') paintQuestProgress(); } catch(_){}
  }
  function renderRichGetRicherHtml(state){
    var n = (state.chars || []).length;
    var cardsHtml = (state.chars || []).map(function(c){ return renderStreamTargetCard(c, { mode: 'mythic' }); }).join('');
    var sub = (n === 1)
      ? 'One mythic walks the hall. The crowd parts. Give them what they don\'t need — they\'re used to it.'
      : 'A few mythics passing through. The rich get richer. Hold to shower them with hearts you\'ll never get back.';
    return '<div class="hoh-fresh-special hoh-fresh-special-stream hoh-fresh-special-stream-rich">' +
      eventHeaderHtml('The Rich Get Richer', sub, { centered: true }) +
      '<div class="hoh-fresh-stream-grid hoh-fresh-stream-grid-' + Math.min(3, n) + '">' + cardsHtml + '</div>' +
      '<div class="hoh-fresh-stream-bar-wrap" aria-hidden="true"><div class="hoh-fresh-stream-bar" id="hoh-fresh-stream-bar"></div></div>' +
      _streamWanderBarHtml() +
      '<div class="hoh-fresh-stream-foot">Hold a portrait to stream hearts. Window closes soon.</div>' +
    '</div>';
  }

  function renderUnderdogHtml(state){
    var c = state.char || {};
    /* R140-E: Visible event-cap counter. The user wants to know exactly
       how many hearts they can still give before the event closes. The
       counter updates live via [data-stream-cap-spent] (see fire() in
       wireHeartStreamTargets) and reads the cap from STREAM_EVENT_HEART_CAP
       so the constant stays single-sourced. */
    var cap = STREAM_EVENT_HEART_CAP.underdog || 30;
    var spent = (state && state.spentInEvent) | 0;
    return '<div class="hoh-fresh-special hoh-fresh-special-stream hoh-fresh-special-stream-underdog">' +
      eventHeaderHtml('The Underdog', 'The least-loved hero in the hall. Hold to give them up to ' + cap + ' hearts. Every heart counts toward Explore the Hall today.', { centered: true }) +
      '<div class="hoh-fresh-stream-grid hoh-fresh-stream-grid-1">' + renderStreamTargetCard(c, { mode: 'underdog' }) + '</div>' +
      '<div class="hoh-fresh-stream-bar-wrap" aria-hidden="true"><div class="hoh-fresh-stream-bar" id="hoh-fresh-stream-bar"></div></div>' +
      '<div class="hoh-fresh-stream-cap-counter" aria-live="polite">' +
        '<span data-stream-cap-spent>' + spent + '</span>' +
        ' / ' + cap + ' hearts given' +
      '</div>' +
      _streamWanderBarHtml() +
      '<div class="hoh-fresh-stream-foot">Hold the portrait. Each heart flows toward them, and the event ends after ' + cap + '.</div>' +
    '</div>';
  }

  /* R116-C: The Mage's Bazaar — themed shop event with 4 buyable
     buffs. Each option card shows price, name, flavor sub, and a
     buy button. Sold items render disabled with a "SOLD" stamp.
     Phases:
       idle:   four option cards (or a subset, depending on sold state)
       diceResult: shows the d20 roll result + hearts gained, then a
                   "Wander further" button to close the event
       confirmed:  shown after buying a buff (coliseum/scholar/special).
                   "The buff is active. Wander on to claim it." */
  /* R122-A: Host card extracted to a helper so every bazaar phase
     (idle, diceReady, diceResult, blessing) shows the same mage above
     the action. The user reported the host vanished after buying any
     item, breaking the narrative thread. */
  function _bazaarHostHtml(state){
    if(!state || !state.host || !state.host.id) return '';
    var hh = state.host;
    var hImg = hh.image_thumb_url || hh.image_url || '';
    var hLetter = esc((hh.name || '?').charAt(0));
    /* R148-Z33: BUG FIX — was calling `_eventCharImg(host, ...)` where
       `host` is undefined. The local var is `hh`. This threw a
       ReferenceError on every Mage's Bazaar render, breaking both
       the natural roll AND the debug fire. The Thieves'/Sage's
       bazaars dodged the bug because they use their own renderer
       (`_bazaarGenericHostHtml`) which correctly references `hh`. */
    var hMedia = _eventCharImg(hh, { eager: true, draggable: false });
    return '<div class="hoh-mb-host">' +
      '<div role="button" tabindex="0" class="hoh-mb-host-card" data-id="' + esc(hh.id) + '"' +
        ' onclick="if(window.hohOpenDetail)hohOpenDetail(\'' + esc(hh.id) + '\')"' +
        ' aria-label="Open ' + esc(hh.name || '') + '">' +
        '<div class="hoh-mb-host-media">' + hMedia + '</div>' +
        '<div class="hoh-mb-host-name">' + esc(hh.name || 'A wandering mage') + '</div>' +
      '</div>' +
    '</div>';
  }
  function renderMageBazaarHtml(state){
    var phase = state.phase || 'idle';
    var bank = (typeof window.heartsRemainingToday === 'function') ? window.heartsRemainingToday() : 0;
    /* R118-F: Magical Dice — ready phase. User taps the d20 to roll. */
    if(phase === 'diceReady'){
      return '<div class="hoh-fresh-special hoh-fresh-special-bazaar hoh-fresh-special-bazaar-resolved">' +
        eventHeaderHtml('The Mage\'s Bazaar', 'The mage offers the dice. Tap to roll.', { centered: true }) +
        _bazaarHostHtml(state) +
        '<div class="hoh-mb-dice-ready">' +
          '<button type="button" class="hoh-mb-dice-ready-die" onclick="hohFreshFacesBazaarRollDice()" aria-label="Roll the d20"><span class="hoh-mb-dice-face">20</span></button>' +
          '<div class="hoh-mb-dice-ready-hint">Tap the die</div>' +
        '</div>' +
      '</div>';
    }
    /* R122-A: Magical Dice resolved — host stays on screen. */
    if(phase === 'diceResult'){
      var roll = state.diceRoll || 0;
      var rollClass = (roll === 20) ? ' hoh-mb-dice-result-nat20' : (roll === 1 ? ' hoh-mb-dice-result-nat1' : '');
      return '<div class="hoh-fresh-special hoh-fresh-special-bazaar hoh-fresh-special-bazaar-resolved">' +
        eventHeaderHtml('The Mage\'s Bazaar', 'The dice settle. The mage smiles.', { centered: true }) +
        _bazaarHostHtml(state) +
        '<div class="hoh-mb-dice-result' + rollClass + '">' +
          '<div class="hoh-mb-dice-result-die" aria-hidden="true"><span class="hoh-mb-dice-face">' + roll + '</span></div>' +
          '<div class="hoh-mb-dice-result-text">+' + roll + ' hearts</div>' +
        '</div>' +
        '<div class="hoh-mb-foot-actions">' +
          '<button type="button" class="hoh-fresh-coin-toss" onclick="hohFreshFacesRefresh({fromInteractive:true})"><span>Keep exploring</span></button>' +
        '</div>' +
      '</div>';
    }
    /* R122-B: Blessing intermediate (was 'confirmed') — rainbow theme,
       host stays, copy themed per buff. The state.confirmKind ('coliseum'
       | 'scholar' | 'special') drives both the headline and a flavor
       icon so the user feels the gift is FROM the mage. */
    if(phase === 'confirmed'){
      var kind = state.confirmKind || 'special';
      var headline, sub;
      if(kind === 'coliseum'){
        headline = 'The mage hands you a ticket.';
        /* R591: FOUR, because the queue builds four. The card said five
           and setColiseumQueue has pushed exactly four since R122-E, so
           this promised a battle the player never got. Fixed here rather
           than left for the next wave to be blamed for. */
        sub = 'Four battles wait at the arena. Keep exploring.';
      } else if(kind === 'scholar'){
        headline = 'The mage opens a book of heroes.';
        sub = 'Your next five stops test your knowledge of the Hall.';
      } else {
        headline = 'The mage whispers a blessing.';
        sub = 'Your next path is something rare. The Hall is listening.';
      }
      var giftIcon = kind === 'coliseum' ? '&#9876;' /* swords */ : (kind === 'scholar' ? '&#128218;' /* book */ : '&#10024;' /* sparkles */);
      return '<div class="hoh-fresh-special hoh-fresh-special-bazaar hoh-fresh-special-bazaar-blessing hoh-fresh-special-bazaar-blessing-' + kind + '">' +
        eventHeaderHtml('A Blessing', headline, { centered: true }) +
        _bazaarHostHtml(state) +
        '<div class="hoh-mb-blessing-stage">' +
          '<div class="hoh-mb-blessing-gift" aria-hidden="true">' + giftIcon + '</div>' +
          '<div class="hoh-mb-blessing-sub">' + esc(sub) + '</div>' +
        '</div>' +
        '<div class="hoh-mb-foot-actions">' +
          '<button type="button" class="hoh-fresh-coin-toss" onclick="hohFreshFacesRefresh({fromInteractive:true})"><span>Keep exploring</span></button>' +
        '</div>' +
      '</div>';
    }
    /* Idle phase — host card + four option grid. */
    var hostHtml = _bazaarHostHtml(state);
    /* R118-C / R119-A: Items NO LONGER persist sold-state across the
       day. Every visit shows fresh, buyable items. */
    var optionCard = function(itemKey, price, title, sub){
      var afford = bank >= price;
      var disabled = !afford;
      var disabledCls = disabled ? ' hoh-mb-option-poor' : '';
      var subText = afford ? sub : 'Need ' + price + ' hearts';
      var btnLabel = 'Buy &middot; ' + price + ' hearts';
      return '<button type="button" class="hoh-mb-option' + disabledCls + '" ' +
        (disabled ? 'disabled ' : '') +
        'data-mb-item="' + itemKey + '" ' +
        (disabled ? '' : 'onclick="hohFreshFacesBazaarBuy(\'' + itemKey + '\')"') + '>' +
        '<span class="hoh-mb-option-title">' + title + '</span>' +
        '<span class="hoh-mb-option-sub">' + subText + '</span>' +
        '<span class="hoh-mb-option-buy">' + btnLabel + '</span>' +
      '</button>';
    };
    var grid =
      optionCard('dice',     5,  'Magical Dice',          'Roll a d20. The mage gives you the result in hearts.') +
      optionCard('coliseum', 10, 'Coliseum Ticket',       'Four fights in a row. Two tavern brawls, then two duels.') +
      optionCard('scholar',  10, 'Scholar\'s Lens',       'The next five rolls are guessing games. No repeats.') +
      optionCard('special',  25, 'Audience with the Hall', 'Your next roll is guaranteed a special event.');
    return '<div class="hoh-fresh-special hoh-fresh-special-bazaar">' +
      eventHeaderHtml('The Mage\'s Bazaar', 'A robed figure steps from the shadows. Four trinkets on a velvet cloth.', { centered: true }) +
      hostHtml +
      '<div class="hoh-mb-grid">' + grid + '</div>' +
    '</div>';
  }
  /* R116-C: Buy handler. Routes to the per-item logic, deducts the
     price, marks the item sold, and either resolves the event with a
     result screen (dice) or queues the buff (coliseum/scholar/special)
     and shows a confirmation. Validates affordability + already-sold
     at click time so a stale render can't be exploited. */
  window.hohFreshFacesBazaarBuy = function(itemKey){
    if(!currentSpecial || currentSpecial.type !== 'mageBazaar') return;
    if(currentSpecial.phase !== 'idle') return;
    /* R150-Z45: Block Mage's Bazaar purchases for signed-out
       visitors — spending hearts is a member action. */
    if(typeof window.hohIsHallSignedIn === 'function' && !window.hohIsHallSignedIn()){
      if(typeof window.hohOpenSubmitToEarnModal === 'function'){
        try { window.hohOpenSubmitToEarnModal(null); } catch(_){}
      }
      return;
    }
    /* R136-shop: Audience cost was dropped 25 -> 20.
       R140-D: Restored to 25 to match the user's framing in the request
       — the buff now cycles through 4 special events (was 3, often
       defaulting to spinWheel) so the higher price reads as fair value.
       Keep prices map + optionCard call above in sync; they're the
       only two places this number lives. */
    var prices = { dice: 5, coliseum: 10, scholar: 10, special: 25 };
    var price = prices[itemKey];
    if(typeof price !== 'number') return;
    var bank = (typeof window.heartsRemainingToday === 'function') ? window.heartsRemainingToday() : 0;
    if(bank < price){
      try { hohShowHeartToast && hohShowHeartToast('Not enough hearts', 'You need ' + price + ' hearts for that trade.'); } catch(_){}
      return;
    }
    /* Charge the price via the same direct-ledger pattern legShop uses. */
    try {
      var raw = parseInt(localStorage.getItem('hoh-heart-bank-v1') || '0', 10) || 0;
      try{ window.hohHeartHint && window.hohHeartHint('spend_purchase'); }catch(e){}
      localStorage.setItem('hoh-heart-bank-v1', String(Math.max(0, raw - price)));
      try { document.dispatchEvent(new CustomEvent('hoh:hearts-bumped', { detail: { spent: price } })); } catch(_){}
    } catch(_){}
    if(typeof window.hohRenderHeaderCounter === 'function'){
      try { window.hohRenderHeaderCounter(); } catch(_){}
    }
    markBazaarItemSold(itemKey);
    /* R118-B: Refresh currentSpecial.sold IMMEDIATELY so the next
       render reflects the just-bought item as disabled. The renderer
       reads state.sold which would otherwise be the stale snapshot
       from when the bazaar was first opened. */
    currentSpecial.sold = getBazaarSold();
    /* Per-item resolution. */
    if(itemKey === 'dice'){
      /* R118-F: Click-to-roll flow. Take the user to the "ready" phase
         first (showing the d20 they tap to roll). The actual roll
         resolves in hohFreshFacesBazaarRollDice when they tap. */
      currentSpecial.phase = 'diceReady';
      var cR = _ffContainer();
      if(cR) cR.innerHTML = renderMageBazaarHtml(currentSpecial);
      return;
    }
    if(itemKey === 'coliseum'){
      /* R122-E: Coliseum queue — exactly 4 events as the user spec'd:
         4-person brawl → 3-person brawl → rare 1v1 duel → legendary
         1v1 duel. Drops championDuel/epicDuel from the previous
         5-event chain. Legendary tail is conditional on at least 2
         legendaries existing in the hall — if not, falls back to
         epicDuel so the chain still has a fitting climax. */
      var queue = ['tavernBrawl4', 'tavernBrawl', 'rareDuel'];
      try {
        var legPool = (typeof poolByTier === 'function') ? (poolByTier('legendary') || []) : [];
        if(legPool.length >= 2) queue.push('legendaryDuel');
        else queue.push('epicDuel');
      } catch(_){ queue.push('epicDuel'); }
      setColiseumQueue(queue);
      currentSpecial.phase = 'confirmed';
      currentSpecial.confirmKind = 'coliseum';
    } else if(itemKey === 'scholar'){
      setScholarBuff({ remaining: 5, seen: [] });
      currentSpecial.phase = 'confirmed';
      currentSpecial.confirmKind = 'scholar';
    } else if(itemKey === 'special'){
      setSpecialFlag(true);
      currentSpecial.phase = 'confirmed';
      currentSpecial.confirmKind = 'special';
    }
    var c2 = _ffContainer();
    if(c2) c2.innerHTML = renderMageBazaarHtml(currentSpecial);
  };
  /* R118-F: Roll the d20 — fired when user clicks the dice on the
     diceReady phase. Adds a brief spinning animation, picks the roll,
     transitions to diceResult, then fires the heart-fly + confetti. */
  window.hohFreshFacesBazaarRollDice = function(){
    if(!currentSpecial || currentSpecial.type !== 'mageBazaar') return;
    if(currentSpecial.phase !== 'diceReady') return;
    if(currentSpecial._diceRolling) return;
    currentSpecial._diceRolling = true;
    /* Add the rolling class so the dice CSS animation kicks in. */
    var dieEl = document.querySelector('.hoh-mb-dice-ready-die');
    if(dieEl){
      dieEl.classList.add('hoh-mb-dice-ready-die-rolling');
      /* Cycle the displayed face during the roll for a tumbling feel. */
      var counter = 0;
      var tick = setInterval(function(){
        counter++;
        var face = 1 + Math.floor(Math.random() * 20);
        if(dieEl) dieEl.textContent = face;
        if(counter >= 12){ clearInterval(tick); }
      }, 70);
    }
    /* Resolve after the animation. */
    setTimeout(function(){
      var roll = 1 + Math.floor(Math.random() * 20);
      if(typeof window.hohAddHeartsToBank === 'function'){
        try { window.hohAddHeartsToBank(roll); } catch(_){}
        try { if(typeof window.hohRenderHeaderCounter === 'function') window.hohRenderHeaderCounter(); } catch(_){}
      }
      /* R318: the standard type re-check every other resolve timer carries.
         The roll above still pays either way — the trade was bought — but a
         replaced event must never be mutated into a dice result. */
      if(!currentSpecial || currentSpecial.type !== 'mageBazaar'){ return; }
      currentSpecial.phase = 'diceResult';
      currentSpecial.diceRoll = roll;
      currentSpecial._diceRolling = false;
      var c1 = _ffContainer();
      if(c1) c1.innerHTML = renderMageBazaarHtml(currentSpecial);
      setTimeout(function(){
        try {
          var anchor = document.querySelector('.hoh-mb-dice-result-die')
                    || document.querySelector('.hoh-fresh-special-bazaar');
          if(anchor && typeof flyDiceTraderHeartsToPill === 'function'){
            flyDiceTraderHeartsToPill(anchor, Math.min(roll, 12));
          }
          if(roll === 20 && typeof spawnConfetti === 'function'){
            spawnConfetti();
            setTimeout(spawnConfetti, 220);
          }
        } catch(_){}
      }, 250);
    }, 950);
  };

  /* ================================================================ */
  /* R148-Z24: Generic Bazaar renderer (Thieves' + Sage's)             */
  /* ================================================================ */
  /* Renders the shop UI for any bazaar variant in BAZAAR_REGISTRY.
     Mage uses its own dedicated renderer (renderMageBazaarHtml) which
     handles the dice/coliseum/scholar/special buff phases. Thieves +
     Sage hit the generic resolve path: idle → resolved, where the
     resolved screen shows the placeholder reward awarded by the buy
     handler. Round 3 swaps the resolved screen for interactive
     mini-game phases (coin flips, mimic chests, etc.). */
  function _bazaarGenericHostHtml(state, fallback){
    if(!state || !state.host || !state.host.id) return '';
    var hh = state.host;
    var hMedia = _eventCharImg(hh, { eager: true, draggable: false });
    return '<div class="hoh-mb-host">' +
      '<div role="button" tabindex="0" class="hoh-mb-host-card" data-id="' + esc(hh.id) + '"' +
        ' onclick="if(window.hohOpenDetail)hohOpenDetail(\'' + esc(hh.id) + '\')"' +
        ' aria-label="Open ' + esc(hh.name || '') + '">' +
        '<div class="hoh-mb-host-media">' + hMedia + '</div>' +
        '<div class="hoh-mb-host-name">' + esc(hh.name || fallback || 'A stranger') + '</div>' +
      '</div>' +
    '</div>';
  }
  function renderGenericBazaarHtml(state){
    var kind = state.bazaarKind || 'mage';
    var entry = BAZAAR_REGISTRY[kind] || BAZAAR_REGISTRY.mage;
    var phase = state.phase || 'idle';
    var bank = (typeof window.heartsRemainingToday === 'function') ? window.heartsRemainingToday() : 0;
    var modClass = ' hoh-fresh-special-bazaar-' + (entry.cssMod || kind);
    /* R148-Z25 Round 3: interactive resolve phases per item key. Each
       case below renders a screen the user can interact with — flips,
       picks, rolls — and the matching window handler updates state
       + transitions to 'resolved' when the game ends. */
    /* --- Thieves: Cutpurse's Coin — flip until tails. --- */
    /* R148-Z39: Every flip now has a real animation phase. Click
       "Flip the coin" → big coin spins for ~1.2s → lands on H or T
       → pushes to the trail → user can flip again or walk away.
       State machine:
         coinFlip + !coinFlipping              = idle. Buttons visible.
         coinFlip + coinFlipping=true          = mid-spin. Big coin
                                                 visible, buttons gone.
         coinFlip + coinDone + !coinFlipping   = run ended. Continue. */
    if(phase === 'coinFlip'){
      var flips = state.coinFlips || [];
      var running = state.coinRunning || 0;
      var alive = !state.coinDone;
      var flipping = !!state.coinFlipping;
      /* Big coin — neutral when idle, mid-flip during spin, lands on
         pendingFace right before re-render flips back to idle. */
      var bigCoinFace = state.coinPendingFace || null; /* set just
                                                          before settle */
      var bigCoinClass = 'hoh-mb-mini-bigcoin hoh-mb-mini-bigcoin-cutpurse' +
        (flipping ? ' hoh-mb-mini-bigcoin-flipping' : '') +
        (bigCoinFace === 'H' ? ' hoh-mb-mini-bigcoin-heads' : '') +
        (bigCoinFace === 'T' ? ' hoh-mb-mini-bigcoin-tails' : '');
      var bigCoinLabel;
      if(flipping) bigCoinLabel = '';
      else if(bigCoinFace) bigCoinLabel = (bigCoinFace === 'H' ? 'H' : 'T');
      else bigCoinLabel = '?';
      var bigCoinHtml =
        '<div class="hoh-mb-mini-bigcoin-wrap hoh-mb-mini-bigcoin-wrap-cutpurse">' +
          '<div class="' + bigCoinClass + '" aria-hidden="true">' +
            '<div class="hoh-mb-mini-bigcoin-face hoh-mb-mini-bigcoin-face-front"><span>' + bigCoinLabel + '</span></div>' +
            '<div class="hoh-mb-mini-bigcoin-face hoh-mb-mini-bigcoin-face-back"><span>' + bigCoinLabel + '</span></div>' +
          '</div>' +
        '</div>';
      /* Trail of past flips — small chips below the big coin. */
      var flipsHtml = flips.length
        ? '<div class="hoh-mb-mini-trail">' + flips.map(function(f){
            return '<span class="hoh-mb-mini-coin hoh-mb-mini-coin-' + (f === 'H' ? 'heads' : 'tails') + '">' + (f === 'H' ? 'H' : 'T') + '</span>';
          }).join('') + '</div>'
        : '<div class="hoh-mb-mini-trail hoh-mb-mini-trail-empty">No flips yet</div>';
      var statusText;
      if(flipping){
        statusText = 'The coin spins...';
      } else if(alive){
        statusText = running > 0
          ? 'Running total: <strong>+' + running + ' hearts</strong>'
          : 'Flip the coin to begin.';
      } else {
        statusText = 'Tails. The run ends. <strong>+' + running + ' hearts banked.</strong>';
      }
      var actions;
      if(flipping){
        /* No buttons during the spin — the click is locked. */
        actions = '<div class="hoh-mb-mini-actions hoh-mb-mini-actions-locked">' +
          '<span class="hoh-mb-mini-status-rolling">Watching the coin...</span>' +
        '</div>';
      } else if(alive){
        actions = '<div class="hoh-mb-mini-actions">' +
          '<button type="button" class="hoh-fresh-coin-toss hoh-mb-mini-flip" onclick="hohFreshFacesCoinFlip()"><span>Flip the coin</span></button>' +
          (running > 0 ? '<button type="button" class="hoh-fresh-coin-toss hoh-mb-mini-walk" onclick="hohFreshFacesCoinWalk()"><span>Walk away with +' + running + '</span></button>' : '') +
        '</div>';
      } else {
        actions = '<div class="hoh-mb-mini-actions">' +
          '<button type="button" class="hoh-fresh-coin-toss" onclick="hohFreshFacesCoinFinish()"><span>Back to the Hall</span></button>' +
        '</div>';
      }
      return '<div class="hoh-fresh-special hoh-fresh-special-bazaar hoh-fresh-special-bazaar-mini' + modClass + '">' +
        eventHeaderHtml("Cutpurse's Coin", 'Every heads pays +15. First tails ends the run.', { centered: true }) +
        _bazaarGenericHostHtml(state, entry.hostFallback) +
        '<div class="hoh-mb-mini-stage hoh-mb-mini-stage-coin">' +
          bigCoinHtml +
          flipsHtml +
          '<div class="hoh-mb-mini-status">' + statusText + '</div>' +
        '</div>' +
        actions +
      '</div>';
    }
    /* --- Thieves: Three Mimics — pick 1 of 4 chests. --- */
    if(phase === 'chestsPick'){
      var chestStates = state.chestStates || ['idle','idle','idle','idle'];
      var picked = (typeof state.chestPicked === 'number') ? state.chestPicked : -1;
      var chestsHtml = chestStates.map(function(s, i){
        var cls = 'hoh-mb-mini-chest hoh-mb-mini-chest-' + s;
        var icon = '?';
        if(s === 'win') icon = '+' + (state.chestRewards ? state.chestRewards[i] : 0);
        else if(s === 'bite') icon = 'BITE';
        else if(s === 'shown') icon = (state.chestRewards && state.chestRewards[i] === 'BITE') ? 'BITE' : ('+' + (state.chestRewards ? state.chestRewards[i] : 0));
        var clickAttr = (s === 'idle' && picked < 0) ? ' onclick="hohFreshFacesMimicPick(' + i + ')"' : '';
        return '<button type="button" class="' + cls + '"' + clickAttr + (s !== 'idle' ? ' disabled' : '') + '>' +
          '<span class="hoh-mb-mini-chest-lid" aria-hidden="true">&#x2705;</span>' +
          '<span class="hoh-mb-mini-chest-label">' + icon + '</span>' +
        '</button>';
      }).join('');
      var statusC = picked < 0
        ? '<div class="hoh-mb-mini-status">Pick a chest. Three pay. One bites.</div>'
        : '';
      var actionsC = (picked >= 0)
        ? '<div class="hoh-mb-mini-actions">' +
            '<button type="button" class="hoh-fresh-coin-toss" onclick="hohFreshFacesMimicFinish()"><span>Back to the Hall</span></button>' +
          '</div>'
        : '';
      return '<div class="hoh-fresh-special hoh-fresh-special-bazaar hoh-fresh-special-bazaar-mini' + modClass + '">' +
        eventHeaderHtml('Three Mimics', 'Four chests. Three pay. One bites.', { centered: true }) +
        _bazaarGenericHostHtml(state, entry.hostFallback) +
        '<div class="hoh-mb-mini-chest-grid">' + chestsHtml + '</div>' +
        statusC +
        actionsC +
      '</div>';
    }
    /* --- Thieves: Loaded Dice — roll 6d20, best 3 count. --- */
    /* R148-Z33: Three-step flow matching Sage's Bones + The Flip. */
    if(phase === 'diceLoaded'){
      var dice = state.diceFaces || [];
      var rolled = !!state.diceRolled;
      var animatingLD = !!state.diceAnimating;
      var awardedLD = state.awarded || 0;
      var diceHtml;
      if(animatingLD || rolled){
        diceHtml = '<div class="hoh-mb-mini-dice-row">' + dice.map(function(face, i){
          var topCls = (state.diceTopIdx && state.diceTopIdx[i]) ? ' hoh-mb-mini-die-top' : '';
          var animCls = animatingLD ? ' hoh-mb-mini-die-tumbling' : '';
          var settleCls = (rolled && !animatingLD) ? ' hoh-mb-mini-die-settled' : '';
          return '<div class="hoh-mb-mini-die' + topCls + animCls + settleCls + '" data-die-idx="' + i + '"><span>' + face + '</span></div>';
        }).join('') + '</div>';
      } else {
        /* Idle: 6 unrolled dice showing "?". */
        diceHtml = '<div class="hoh-mb-mini-dice-row">' +
          [0,1,2,3,4,5].map(function(i){
            return '<div class="hoh-mb-mini-die hoh-mb-mini-die-idle" data-die-idx="' + i + '"><span>?</span></div>';
          }).join('') +
        '</div>';
      }
      return '<div class="hoh-fresh-special hoh-fresh-special-bazaar hoh-fresh-special-bazaar-mini' + modClass + '">' +
        eventHeaderHtml('Loaded Dice', 'Six d20s. Best three count. The dice tilt your way.', { centered: true }) +
        _bazaarGenericHostHtml(state, entry.hostFallback) +
        '<div class="hoh-mb-mini-stage">' + diceHtml + '</div>' +
        (rolled
          ? '<div class="hoh-mb-mini-status">Top three sum: <strong>+' + awardedLD + ' hearts</strong></div>' +
            '<div class="hoh-mb-mini-actions"><button type="button" class="hoh-fresh-coin-toss" onclick="hohFreshFacesLoadedDiceFinish()"><span>Back to the Hall</span></button></div>'
          : (animatingLD
              ? '<div class="hoh-mb-mini-status hoh-mb-mini-status-rolling">The dice clatter across the table...</div>'
              : '<div class="hoh-mb-mini-actions"><button type="button" class="hoh-fresh-coin-toss" onclick="hohFreshFacesLoadedDiceRoll()"><span>Roll the dice</span></button></div>')) +
      '</div>';
    }
    /* --- Thieves: The Flip — single coin toss for 500 hearts. --- */
    /* R148-Z33: Reworked flow.
         idle:     coin in neutral state, USER CLICKS THE COIN itself
                   (no separate button). The coin has a subtle hover
                   pulse so it reads as the tap target.
         flipping: coin spins for ~1.6s via CSS keyframes. JS adds
                   the .hoh-mb-mini-bigcoin-flipping class but does
                   not re-render (re-render mid-animation would
                   restart the spin and look janky).
         resolved: coin lands on heads or tails. Heads = confetti
                   burst + heart fly + +500 hearts. Tails = subtle
                   tail face, no celebration. */
    if(phase === 'pennySlot'){
      var coinFace = state.pennyFace || null; /* 'H' | 'T' | null */
      var resolvedP = !!state.pennyResolved;
      var animatingP = !!state.pennyAnimating;
      var awardedP = state.awarded || 0;
      var coinClass = 'hoh-mb-mini-bigcoin' +
        (animatingP ? ' hoh-mb-mini-bigcoin-flipping' : '') +
        (resolvedP ? ' hoh-mb-mini-bigcoin-resolved' : '') +
        (coinFace === 'H' ? ' hoh-mb-mini-bigcoin-heads' : '') +
        (coinFace === 'T' ? ' hoh-mb-mini-bigcoin-tails' : '');
      var coinLabel;
      if(animatingP) coinLabel = '';
      else if(resolvedP) coinLabel = (coinFace === 'H' ? 'HEADS' : 'TAILS');
      else coinLabel = 'FLIP';
      var coinClickAttr = (resolvedP || animatingP)
        ? ''
        : ' onclick="hohFreshFacesPennySpin()" role="button" tabindex="0"' +
          ' onkeydown="if(event.key===\'Enter\'||event.key===\' \'){event.preventDefault();hohFreshFacesPennySpin();}"';
      var coinHtml =
        '<div class="hoh-mb-mini-bigcoin-wrap">' +
          '<div class="' + coinClass + '"' + coinClickAttr + '>' +
            '<div class="hoh-mb-mini-bigcoin-face hoh-mb-mini-bigcoin-face-front"><span>' + coinLabel + '</span></div>' +
            '<div class="hoh-mb-mini-bigcoin-face hoh-mb-mini-bigcoin-face-back"><span>' + coinLabel + '</span></div>' +
          '</div>' +
          (!resolvedP && !animatingP
            ? '<div class="hoh-mb-mini-bigcoin-hint">Tap the coin</div>'
            : '') +
        '</div>';
      var statusHtml = '';
      if(resolvedP){
        statusHtml = '<div class="hoh-mb-mini-status">' +
          (coinFace === 'H'
            ? '<strong>Heads. +' + awardedP + ' hearts.</strong> Fortune smiles.'
            : '<strong>Tails. The coin is gone.</strong> Walk it off.') +
        '</div>' +
        '<div class="hoh-mb-mini-actions"><button type="button" class="hoh-fresh-coin-toss" onclick="hohFreshFacesPennyFinish()"><span>Back to the Hall</span></button></div>';
      }
      return '<div class="hoh-fresh-special hoh-fresh-special-bazaar hoh-fresh-special-bazaar-mini' + modClass + '">' +
        eventHeaderHtml('The Flip', 'One coin. Heads pays 500. Tails keeps nothing.', { centered: true }) +
        _bazaarGenericHostHtml(state, entry.hostFallback) +
        '<div class="hoh-mb-mini-stage">' + coinHtml + '</div>' +
        statusHtml +
      '</div>';
    }
    /* --- Sage: Sage's Bones — roll 4d6 fortune. --- */
    /* R148-Z33: Three-step flow matching The Flip.
         idle:    4 dice showing "?", "Roll the bones" button
         rolling: dice tumble through random faces (JS cycles every
                  60ms via setInterval in the handler)
         rolled:  final faces locked, sum highlighted, heart-fly */
    if(phase === 'bonesRoll'){
      var bones = state.bonesFaces || [];
      var rolledB = !!state.bonesRolled;
      var animatingB = !!state.bonesAnimating;
      var awardedB = state.awarded || 0;
      /* R150: sum of the four bones, surfaced on reveal so the doubled
         payout reads as intentional ("17 doubled = 34") instead of
         looking like a bug. The award is sum * 2 (see hohFreshFacesBonesRoll). */
      var bonesSum = (state.bonesFaces || []).reduce(function(s, f){ return s + (parseInt(f, 10) || 0); }, 0);
      var bonesDieHtml = function(face, i){
        var cls = 'hoh-mb-mini-die hoh-mb-mini-die-bone';
        if(animatingB) cls += ' hoh-mb-mini-die-tumbling';
        else if(rolledB) cls += ' hoh-mb-mini-die-settled';
        return '<div class="' + cls + '" data-die-idx="' + i + '"><span>' + face + '</span></div>';
      };
      var bonesHtml;
      if(animatingB || rolledB){
        bonesHtml = '<div class="hoh-mb-mini-dice-row">' + bones.map(bonesDieHtml).join('') + '</div>';
      } else {
        /* Idle: 4 unrolled dice showing "?". */
        bonesHtml = '<div class="hoh-mb-mini-dice-row">' +
          [0,1,2,3].map(function(i){
            return '<div class="hoh-mb-mini-die hoh-mb-mini-die-bone hoh-mb-mini-die-idle" data-die-idx="' + i + '"><span>?</span></div>';
          }).join('') +
        '</div>';
      }
      return '<div class="hoh-fresh-special hoh-fresh-special-bazaar hoh-fresh-special-bazaar-mini' + modClass + '">' +
        eventHeaderHtml("Sage's Bones", 'Roll four d6. The Hall pays double the bones, in hearts.', { centered: true }) +
        _bazaarGenericHostHtml(state, entry.hostFallback) +
        '<div class="hoh-mb-mini-stage">' + bonesHtml + '</div>' +
        (rolledB
          ? '<div class="hoh-mb-mini-status">' + esc(state.resolvedSub || '') +
            '<span class="hoh-mb-mini-math">Four bones sum to ' + bonesSum + '. Doubled by the Hall. <strong>+' + awardedB + ' hearts</strong></span></div>' +
            '<div class="hoh-mb-mini-actions"><button type="button" class="hoh-fresh-coin-toss" onclick="hohFreshFacesBonesFinish()"><span>Back to the Hall</span></button></div>'
          : (animatingB
              ? '<div class="hoh-mb-mini-status hoh-mb-mini-status-rolling">The bones tumble...</div>'
              : '<div class="hoh-mb-mini-actions"><button type="button" class="hoh-fresh-coin-toss" onclick="hohFreshFacesBonesRoll()"><span>Roll the bones</span></button></div>')) +
      '</div>';
    }
    /* --- Sage: A Reading — cast a rune. --- */
    if(phase === 'omenCast'){
      var rune = state.omenRune || '?';
      var castedO = !!state.omenCast;
      var awardedO = state.awarded || 0;
      var runeHtml = '<div class="hoh-mb-mini-rune"><span>' + rune + '</span></div>';
      return '<div class="hoh-fresh-special hoh-fresh-special-bazaar hoh-fresh-special-bazaar-mini' + modClass + '">' +
        eventHeaderHtml('A Reading', 'Cast the rune. The face decides your fortune.', { centered: true }) +
        _bazaarGenericHostHtml(state, entry.hostFallback) +
        '<div class="hoh-mb-mini-stage">' + runeHtml + '</div>' +
        (castedO
          ? '<div class="hoh-mb-mini-status">' + esc(state.resolvedSub || '') + ' <strong>+' + awardedO + ' hearts</strong></div>' +
            '<div class="hoh-mb-mini-actions"><button type="button" class="hoh-fresh-coin-toss" onclick="hohFreshFacesOmenFinish()"><span>Back to the Hall</span></button></div>'
          : '<div class="hoh-mb-mini-actions"><button type="button" class="hoh-fresh-coin-toss" onclick="hohFreshFacesOmenCast()"><span>Cast the rune</span></button></div>') +
      '</div>';
    }
    /* Resolved phase — fallback for items that don't have an interactive
       screen (Tea of Foresight, Scrying Mirror — both are instant-buff
       items that confirm directly to a result screen). */
    if(phase === 'resolved'){
      var headline = state.resolvedHeadline || 'The deal is done.';
      var sub = state.resolvedSub || '';
      var awarded = state.awarded || 0;
      return '<div class="hoh-fresh-special hoh-fresh-special-bazaar hoh-fresh-special-bazaar-resolved' + modClass + '">' +
        eventHeaderHtml(entry.title, headline, { centered: true }) +
        _bazaarGenericHostHtml(state, entry.hostFallback) +
        '<div class="hoh-mb-blessing-stage">' +
          '<div class="hoh-mb-blessing-gift" aria-hidden="true">&#10024;</div>' +
          '<div class="hoh-mb-blessing-sub">' + esc(sub) + '</div>' +
          (awarded > 0 ? '<div class="hoh-fresh-duel-result-sub"><strong>+' + awarded + ' hearts</strong></div>' : '') +
        '</div>' +
        '<div class="hoh-mb-foot-actions">' +
          '<button type="button" class="hoh-fresh-coin-toss" onclick="hohFreshFacesRefresh({fromInteractive:true})"><span>Keep exploring</span></button>' +
        '</div>' +
      '</div>';
    }
    /* Idle phase — host + 4 option grid. */
    var hostHtml = _bazaarGenericHostHtml(state, entry.hostFallback);
    var items = entry.items || [];
    var grid = items.map(function(item){
      var afford = bank >= item.price;
      /* R150-Z45: Inventory items (Lonefire, Oath) now STACK.
         The old R151 oath-lock that disabled the option while one was
         held is gone — each purchase pushes its own instance with its
         own timer, so re-buying is safe. When the player already owns
         one or more of a stackable item, surface the count in the sub
         line ("You hold 2. Buy another.") so the buy feels tracked,
         not silently swallowed. */
      var ownedCount = 0;
      if(item.inventoryItem){
        var invKeyMap = { lonefire: 'lonefireCharm', oath: 'patronsOath' };
        var invKey = invKeyMap[item.key];
        if(invKey && typeof window.hohInventoryCount === 'function'){
          try { ownedCount = window.hohInventoryCount(invKey) || 0; } catch(_){}
        }
      }
      var disabled = !afford;
      var disabledCls = disabled ? ' hoh-mb-option-poor' : '';
      var subText;
      if(!afford){
        subText = 'Need ' + item.price + ' hearts';
      } else if(ownedCount > 0){
        subText = 'You hold ' + ownedCount + '. ' + item.sub;
      } else {
        subText = item.sub;
      }
      var btnLabel = (ownedCount > 0 ? 'Buy another &middot; ' : 'Buy &middot; ') + item.price + ' hearts';
      return '<button type="button" class="hoh-mb-option' + disabledCls + '" ' +
        (disabled ? 'disabled ' : '') +
        'data-mb-item="' + item.key + '" ' +
        (disabled ? '' : 'onclick="hohFreshFacesGenericBazaarBuy(\'' + esc(item.key) + '\')"') + '>' +
        '<span class="hoh-mb-option-title">' + esc(item.title) + '</span>' +
        '<span class="hoh-mb-option-sub">' + esc(subText) + '</span>' +
        '<span class="hoh-mb-option-buy">' + btnLabel + '</span>' +
      '</button>';
    }).join('');
    return '<div class="hoh-fresh-special hoh-fresh-special-bazaar' + modClass + '">' +
      eventHeaderHtml(entry.title, entry.intro, { centered: true }) +
      hostHtml +
      '<div class="hoh-mb-grid">' + grid + '</div>' +
    '</div>';
  }
  /* Generic buy handler for the non-Mage bazaars. Mage keeps its own
     handler (hohFreshFacesBazaarBuy) so its existing buff wiring
     (coliseum queue, scholar buff, special flag, dice flow) stays
     intact. Thieves + Sage items resolve to placeholder hearts grants
     in Round 2; Round 3 swaps these for kind-specific interactive
     resolves (coin-flip, mimic chests, dice rolls, etc.). */
  window.hohFreshFacesGenericBazaarBuy = function(itemKey){
    if(!currentSpecial) return;
    var t = currentSpecial.type;
    if(t !== 'thievesBazaar' && t !== 'sageBazaar') return;
    /* R150-Z45: Block bazaar purchases for signed-out visitors —
       spending hearts is a member action. */
    if(typeof window.hohIsHallSignedIn === 'function' && !window.hohIsHallSignedIn()){
      if(typeof window.hohOpenSubmitToEarnModal === 'function'){
        try { window.hohOpenSubmitToEarnModal(null); } catch(_){}
      }
      return;
    }
    if(currentSpecial.phase !== 'idle') return;
    var kind = currentSpecial.bazaarKind;
    var entry = BAZAAR_REGISTRY[kind];
    if(!entry) return;
    var item = (entry.items || []).filter(function(i){ return i.key === itemKey; })[0];
    if(!item) return;
    /* R150-Z45: The old R151 Patron's Oath re-buy block is gone.
       It existed because the legacy flat inventory model would
       overwrite acquiredAt on a re-buy and reset the 5-day timer.
       The stackable array model fixes that at the root — each Oath
       purchase pushes a fresh instance with its own independent
       countdown — so re-buying is now safe and stacks like every
       other shop item. */
    var bank = (typeof window.heartsRemainingToday === 'function') ? window.heartsRemainingToday() : 0;
    if(bank < item.price){
      try { hohShowHeartToast && hohShowHeartToast('Not enough hearts', 'You need ' + item.price + ' hearts for that trade.'); } catch(_){}
      return;
    }
    /* Charge price via the same direct-ledger pattern the legendary
       shop + mage bazaar use. */
    try {
      var raw = parseInt(localStorage.getItem('hoh-heart-bank-v1') || '0', 10) || 0;
      try{ window.hohHeartHint && window.hohHeartHint('spend_purchase'); }catch(e){}
      localStorage.setItem('hoh-heart-bank-v1', String(Math.max(0, raw - item.price)));
      try { document.dispatchEvent(new CustomEvent('hoh:hearts-bumped', { detail: { spent: item.price } })); } catch(_){}
    } catch(_){}
    if(typeof window.hohRenderHeaderCounter === 'function'){
      try { window.hohRenderHeaderCounter(); } catch(_){}
    }
    /* R148-Z25 Round 3: Route each item to its interactive phase.
       Coin / Chests / Dice / Penny / Bones / Omen all transition to a
       game-specific phase the user plays through. Tea + Mirror are
       instant-buff items that confirm directly to a resolved screen
       (matching the existing scholar/special pattern in mageBazaar). */
    currentSpecial.boughtKey = itemKey;
    if(itemKey === 'coin'){
      currentSpecial.phase = 'coinFlip';
      currentSpecial.coinFlips = [];
      currentSpecial.coinRunning = 0;
      currentSpecial.coinDone = false;
    } else if(itemKey === 'chests'){
      /* Pre-pick: pick the bite slot, assign the 3 wins in the others
         (random reward tiers so the user feels the variance). */
      currentSpecial.phase = 'chestsPick';
      var biteIdx = Math.floor(Math.random() * 4);
      var winTiers = [15, 25, 40];
      winTiers = hfShuffle(winTiers);
      var rewardsArr = [null, null, null, null];
      rewardsArr[biteIdx] = 'BITE';
      var wi = 0;
      for(var ci = 0; ci < 4; ci++){
        if(ci === biteIdx) continue;
        rewardsArr[ci] = winTiers[wi++];
      }
      currentSpecial.chestStates = ['idle','idle','idle','idle'];
      currentSpecial.chestRewards = rewardsArr;
      currentSpecial.chestBiteIdx = biteIdx;
      currentSpecial.chestPicked = -1;
    } else if(itemKey === 'dice' && kind === 'thieves'){
      currentSpecial.phase = 'diceLoaded';
      currentSpecial.diceFaces = [];
      currentSpecial.diceRolled = false;
    } else if(itemKey === 'penny'){
      /* R148-Z32: The Flip — single coin toss state. pennyFace stays
         null until the spin handler resolves it. */
      currentSpecial.phase = 'pennySlot';
      currentSpecial.pennyFace = null;
      currentSpecial.pennyResolved = false;
    } else if(itemKey === 'bones'){
      currentSpecial.phase = 'bonesRoll';
      currentSpecial.bonesFaces = [];
      currentSpecial.bonesRolled = false;
    } else if(itemKey === 'omen'){
      currentSpecial.phase = 'omenCast';
      currentSpecial.omenRune = '?';
      currentSpecial.omenCast = false;
    } else if(itemKey === 'lonefire'){
      /* R148-Z26 Round 4: Lonefire Charm — adds to inventory. The
         loseHeartFromGuess helper checks for this charm on every loss
         and absorbs the hit + grants +50 hearts instead. */
      try { window.hohInventoryAdd && window.hohInventoryAdd('lonefireCharm', { acquiredAt: Date.now() }); } catch(_){}
      _bazaarResolveStatic(0, 'The charm is yours.', 'Lonefire Charm acquired. It will guard against your next heart loss.');
      return;
    } else if(itemKey === 'scholar'){
      setScholarBuff({ remaining: 5, seen: [] });
      _bazaarResolveStatic(0, 'Scholar\'s Lens is active.', 'Your next five rolls are guessing games. No repeats.');
      return;
    } else if(itemKey === 'oath'){
      /* Patron's Oath — 5-day countdown. Adds with acquiredAt = now.
         hohInventoryOathStatus() reports when it's claimable. The
         claim UI lives in your-hall + the header chip. */
      try { window.hohInventoryAdd && window.hohInventoryAdd('patronsOath', { acquiredAt: Date.now() }); } catch(_){}
      _bazaarResolveStatic(0, 'You sign the oath. Five days.', "Patron's Oath acquired. Return in five days to claim it.");
      return;
    } else {
      /* Unknown item — defensive resolve. */
      _bazaarResolveStatic(10, 'Trade complete.', 'A small reward.');
      return;
    }
    var cBuy = _ffContainer();
    if(cBuy) cBuy.innerHTML = renderGenericBazaarHtml(currentSpecial);
  };
  /* Shared helper for instant-resolve items (Tea, Mirror, fallback).
     Awards hearts, transitions to resolved, repaints, flies hearts. */
  function _bazaarResolveStatic(award, headline, sub){
    if(!currentSpecial) return;
    if(award > 0 && typeof window.hohAddHeartsToBank === 'function'){
      try { window.hohAddHeartsToBank(award); } catch(_){}
    }
    if(typeof window.hohShowHeartToast === 'function' && award > 0){
      try { window.hohShowHeartToast('+' + award + ' hearts', sub); } catch(_){}
    }
    currentSpecial.phase = 'resolved';
    currentSpecial.awarded = award;
    currentSpecial.resolvedHeadline = headline;
    currentSpecial.resolvedSub = sub;
    var c = _ffContainer();
    if(c) c.innerHTML = renderGenericBazaarHtml(currentSpecial);
    try {
      var anchor = c && c.querySelector('.hoh-mb-blessing-stage');
      if(anchor && award > 0 && typeof flyDiceTraderHeartsToPill === 'function'){
        flyDiceTraderHeartsToPill(anchor, Math.min(award, 12));
      }
    } catch(_){}
    if(typeof startAutoTimer === 'function') setTimeout(startAutoTimer, 0);
  }
  /* ================================================================ */
  /* Cutpurse's Coin handlers                                          */
  /* ================================================================ */
  /* R148-Z39: Each flip is a 3-step phase — pre-compute → spin → settle.
     Click is locked during the ~1.2s spin so the user can't double-tap
     through the animation. The big coin gets the same flipping class
     used by The Flip's animation; .hoh-mb-mini-bigcoin-cutpurse scales
     it down a bit since the trail underneath also matters. */
  window.hohFreshFacesCoinFlip = function(){
    if(!currentSpecial || currentSpecial.phase !== 'coinFlip') return;
    if(currentSpecial.coinDone) return;
    if(currentSpecial.coinFlipping) return; /* lock during spin */
    /* Pre-compute the result so the lands-on face matches the trail. */
    var streak = (currentSpecial.coinFlips || []).length;
    var headsOdds = Math.max(0.5, 0.6 - streak * 0.02);
    var face = Math.random() < headsOdds ? 'H' : 'T';
    currentSpecial.coinFlipping = true;
    currentSpecial.coinPendingFace = null; /* hidden during the spin */
    var c = _ffContainer();
    if(c) c.innerHTML = renderGenericBazaarHtml(currentSpecial);
    /* After 1.2s — the same beat The Flip uses — land and reveal. */
    setTimeout(function(){
      if(!currentSpecial || currentSpecial.phase !== 'coinFlip') return;
      currentSpecial.coinFlipping = false;
      currentSpecial.coinPendingFace = face;
      currentSpecial.coinFlips.push(face);
      if(face === 'H'){
        currentSpecial.coinRunning = (currentSpecial.coinRunning || 0) + 15;
      } else {
        currentSpecial.coinDone = true;
      }
      var c2 = _ffContainer();
      if(c2) c2.innerHTML = renderGenericBazaarHtml(currentSpecial);
      if(face === 'H' && typeof window.hohShowHeartToast === 'function'){
        try { window.hohShowHeartToast('+15 hearts', 'Heads. Keep going?'); } catch(_){}
      }
    }, 1200);
  };
  window.hohFreshFacesCoinWalk = function(){
    if(!currentSpecial || currentSpecial.phase !== 'coinFlip') return;
    if(currentSpecial.coinDone) return;
    currentSpecial.coinDone = true;
    var c = _ffContainer();
    if(c) c.innerHTML = renderGenericBazaarHtml(currentSpecial);
  };
  window.hohFreshFacesCoinFinish = function(){
    if(!currentSpecial || currentSpecial.phase !== 'coinFlip') return;
    if(!currentSpecial.coinDone) return;
    var award = currentSpecial.coinRunning || 0;
    var flips = currentSpecial.coinFlips || [];
    var heads = flips.filter(function(f){ return f === 'H'; }).length;
    /* Hearts already credited per-flip is NOT the case — the per-flip
       handler only increments coinRunning. Credit the full amount here
       so the user's bank reflects the final tally exactly once. */
    if(award > 0 && typeof window.hohAddHeartsToBank === 'function'){
      try { window.hohAddHeartsToBank(award); } catch(_){}
    }
    var sub = heads === 0 ? 'Tails on the first flip. Hard luck.' :
              heads === 1 ? 'One heads, then tails caught up.' :
              'Pocketed ' + heads + ' heads before tails landed.';
    _bazaarResolveStatic(0, 'The coin keeps its tally.', sub);
    /* Patch awarded so the resolved screen displays the right number;
       _bazaarResolveStatic itself ran with award=0 so it didn't double-
       credit hearts or fire a +0 toast. */
    currentSpecial.awarded = award;
    var c = _ffContainer();
    if(c) c.innerHTML = renderGenericBazaarHtml(currentSpecial);
    try {
      var anchor = c && c.querySelector('.hoh-mb-blessing-stage');
      if(anchor && award > 0 && typeof flyDiceTraderHeartsToPill === 'function'){
        flyDiceTraderHeartsToPill(anchor, Math.min(award, 12));
      }
      if(award > 0 && typeof window.hohShowHeartToast === 'function'){
        window.hohShowHeartToast('+' + award + ' hearts', sub);
      }
    } catch(_){}
  };
  /* ================================================================ */
  /* Three Mimics handlers                                             */
  /* ================================================================ */
  window.hohFreshFacesMimicPick = function(idx){
    if(!currentSpecial || currentSpecial.phase !== 'chestsPick') return;
    if(currentSpecial.chestPicked >= 0) return;
    var rewards = currentSpecial.chestRewards || [];
    var pickedReward = rewards[idx];
    currentSpecial.chestPicked = idx;
    currentSpecial.chestStates = currentSpecial.chestStates.slice();
    /* Reveal all chests: picked one becomes win/bite, the rest become
       shown so the user can see what they avoided / missed. */
    for(var i = 0; i < 4; i++){
      if(i === idx){
        currentSpecial.chestStates[i] = (pickedReward === 'BITE') ? 'bite' : 'win';
      } else {
        currentSpecial.chestStates[i] = 'shown';
      }
    }
    var award = 0;
    var sub = '';
    if(pickedReward === 'BITE'){
      award = 0;
      sub = 'The mimic bites. No hearts this round.';
      try { loseHeartFromGuess && loseHeartFromGuess('The mimic bit back.'); } catch(_){}
    } else {
      award = pickedReward;
      sub = 'The chest hummed. ' + award + ' hearts inside.';
      if(typeof window.hohAddHeartsToBank === 'function'){
        try { window.hohAddHeartsToBank(award); } catch(_){}
      }
    }
    currentSpecial.awarded = award;
    currentSpecial.resolvedSub = sub;
    var c = _ffContainer();
    if(c) c.innerHTML = renderGenericBazaarHtml(currentSpecial);
    if(award > 0){
      try {
        var anchor = c && c.querySelector('.hoh-mb-mini-chest-win');
        if(anchor && typeof flyDiceTraderHeartsToPill === 'function'){
          flyDiceTraderHeartsToPill(anchor, Math.min(award, 12));
        }
      } catch(_){}
    }
  };
  window.hohFreshFacesMimicFinish = function(){
    if(!currentSpecial || currentSpecial.phase !== 'chestsPick') return;
    if(currentSpecial.chestPicked < 0) return;
    /* R148-Z34: Reward already credited in the pick handler. */
    _bazaarCloseAndRefresh();
  };
  /* ================================================================ */
  /* Loaded Dice handlers                                              */
  /* ================================================================ */
  /* R148-Z33: Click → tumble → settle → reward. Loaded weighting
     (max(d20, d20)) computed during pre-roll; the tumble animation
     just visualizes the journey. Final values lock at settle. */
  window.hohFreshFacesLoadedDiceRoll = function(){
    if(!currentSpecial || currentSpecial.phase !== 'diceLoaded') return;
    if(currentSpecial.diceRolled) return;
    if(currentSpecial.diceAnimating) return;
    /* Pre-compute the loaded result. */
    var faces = [];
    for(var i = 0; i < 6; i++){
      var r1 = 1 + Math.floor(Math.random() * 20);
      var r2 = 1 + Math.floor(Math.random() * 20);
      faces.push(Math.max(r1, r2));
    }
    var indexed = faces.map(function(f, i){ return { f: f, i: i }; })
                       .sort(function(a, b){ return b.f - a.f; });
    var topIdxMap = {};
    indexed.slice(0, 3).forEach(function(o){ topIdxMap[o.i] = true; });
    var sum = indexed.slice(0, 3).reduce(function(s, o){ return s + o.f; }, 0);
    currentSpecial.diceAnimating = true;
    currentSpecial._diceFinalFaces = faces;
    currentSpecial._diceFinalTopIdx = topIdxMap;
    currentSpecial._diceFinalSum = sum;
    /* Tumble render — show 6 dice cycling. */
    currentSpecial.diceFaces = [1, 1, 1, 1, 1, 1];
    currentSpecial.diceTopIdx = {};
    var c = _ffContainer();
    if(c) c.innerHTML = renderGenericBazaarHtml(currentSpecial);
    var tickCount = 0;
    var rollInterval = setInterval(function(){
      tickCount++;
      try {
        var diceEls = document.querySelectorAll('.hoh-mb-mini-die-tumbling span');
        for(var d = 0; d < diceEls.length; d++){
          diceEls[d].textContent = 1 + Math.floor(Math.random() * 20);
        }
      } catch(_){}
      if(tickCount >= 22){ clearInterval(rollInterval); }
    }, 70);
    setTimeout(function(){
      clearInterval(rollInterval);
      if(!currentSpecial || currentSpecial.phase !== 'diceLoaded') return;
      currentSpecial.diceFaces = faces;
      currentSpecial.diceTopIdx = topIdxMap;
      currentSpecial.diceRolled = true;
      currentSpecial.diceAnimating = false;
      currentSpecial.awarded = sum;
      if(typeof window.hohAddHeartsToBank === 'function'){
        try { window.hohAddHeartsToBank(sum); } catch(_){}
      }
      if(typeof window.hohRenderHeaderCounter === 'function'){
        try { window.hohRenderHeaderCounter(); } catch(_){}
      }
      var c2 = _ffContainer();
      if(c2) c2.innerHTML = renderGenericBazaarHtml(currentSpecial);
      try {
        /* Fly from a top die (the highlighted ones) — that's the
           value the user just "won". */
        var anchor = c2 && c2.querySelector('.hoh-mb-mini-die-top');
        if(anchor && typeof flyDiceTraderHeartsToPill === 'function'){
          flyDiceTraderHeartsToPill(anchor, Math.min(sum, 14));
        }
      } catch(_){}
      /* Big haul (sum >= 50) = confetti. */
      if(sum >= 50){
        try {
          if(typeof window.hohFireConfetti === 'function'){
            window.hohFireConfetti();
            setTimeout(function(){ try { window.hohFireConfetti(); } catch(_){} }, 220);
          }
        } catch(_){}
      }
    }, 1700);
  };
  window.hohFreshFacesLoadedDiceFinish = function(){
    if(!currentSpecial || currentSpecial.phase !== 'diceLoaded') return;
    if(!currentSpecial.diceRolled) return;
    /* R148-Z34: Reward already credited in the roll handler. */
    _bazaarCloseAndRefresh();
  };
  /* ================================================================ */
  /* The Liar's Penny handlers                                         */
  /* ================================================================ */
  /* R148-Z33: The Flip — three-step interactive flow.
       Step 1: idle. Coin shows "FLIP", user clicks the coin.
       Step 2: flipping. Coin spins via CSS for ~1700ms. JS sets
               pennyAnimating=true and adds the .hoh-mb-mini-bigcoin-
               flipping class. We do NOT re-render the whole event
               mid-animation — the existing coin element keeps its
               animation state. Only the status text + continue
               button re-render after the timeout.
       Step 3: resolved. Result lands. Heads = confetti + heart fly.
               Tails = quiet. */
  window.hohFreshFacesPennySpin = function(){
    if(!currentSpecial || currentSpecial.phase !== 'pennySlot') return;
    if(currentSpecial.pennyResolved) return;
    if(currentSpecial.pennyAnimating) return;
    /* Pre-compute result so we can theme the flipping animation
       slightly differently (e.g. on tails, the coin slows differently
       on landing). */
    var face = (Math.random() < 0.5) ? 'H' : 'T';
    currentSpecial.pennyAnimating = true;
    currentSpecial._pennyPendingFace = face;
    /* Add the flipping class to the existing coin element without
       re-rendering the parent. This keeps the CSS animation from
       being interrupted by an innerHTML swap. Also flip the rendered
       coin face text to the pre-resolved face so the spinner shows
       the landing target as it tumbles. */
    try {
      var coinEl = document.querySelector('.hoh-mb-mini-bigcoin');
      if(coinEl){
        coinEl.classList.remove('hoh-mb-mini-bigcoin-idle');
        coinEl.classList.add('hoh-mb-mini-bigcoin-flipping');
        coinEl.style.pointerEvents = 'none';
        /* Hide the "Tap the coin" hint during the flip. */
        var hint = document.querySelector('.hoh-mb-mini-bigcoin-hint');
        if(hint) hint.style.display = 'none';
      }
    } catch(_){}
    /* Resolve after the animation completes. */
    setTimeout(function(){
      if(!currentSpecial || currentSpecial.phase !== 'pennySlot') return;
      var pending = currentSpecial._pennyPendingFace || face;
      var award = (pending === 'H') ? 500 : 0;
      currentSpecial.pennyFace = pending;
      currentSpecial.pennyResolved = true;
      currentSpecial.pennyAnimating = false;
      currentSpecial.awarded = award;
      currentSpecial.resolvedSub = (pending === 'H')
        ? 'Heads. Fortune smiles.'
        : 'Tails. The coin is gone.';
      if(award > 0 && typeof window.hohAddHeartsToBank === 'function'){
        try { window.hohAddHeartsToBank(award); } catch(_){}
      }
      if(typeof window.hohRenderHeaderCounter === 'function'){
        try { window.hohRenderHeaderCounter(); } catch(_){}
      }
      var c = _ffContainer();
      if(c) c.innerHTML = renderGenericBazaarHtml(currentSpecial);
      /* Heads-only celebration: heart-fly volley + 3-burst confetti. */
      if(pending === 'H'){
        try {
          var anchor = c && c.querySelector('.hoh-mb-mini-bigcoin');
          if(anchor && typeof flyDiceTraderHeartsToPill === 'function'){
            flyDiceTraderHeartsToPill(anchor, 14);
          }
        } catch(_){}
        try {
          /* hohFireConfetti is the canvas-confetti path used by the
             legendary shop; spawnConfetti is the DOM-particle fallback.
             Try canvas first since it looks better on mobile. */
          if(typeof window.hohFireConfetti === 'function'){
            window.hohFireConfetti();
            setTimeout(function(){ try { window.hohFireConfetti(); } catch(_){} }, 220);
            setTimeout(function(){ try { window.hohFireConfetti(); } catch(_){} }, 440);
          } else if(typeof spawnConfetti === 'function'){
            spawnConfetti(); setTimeout(spawnConfetti, 220); setTimeout(spawnConfetti, 440);
          }
        } catch(_){}
      }
    }, 1700);
  };
  window.hohFreshFacesPennyFinish = function(){
    if(!currentSpecial || currentSpecial.phase !== 'pennySlot') return;
    if(!currentSpecial.pennyResolved) return;
    /* R148-Z34: Coin landed + reward credited in the spin handler. */
    _bazaarCloseAndRefresh();
  };
  /* ================================================================ */
  /* Sage's Bones handlers                                             */
  /* ================================================================ */
  /* R148-Z33: Click → tumble → settle → reward. The dice cycle
     through random faces during the rolling phase (visible to the
     user), then lock to their final values when the animation
     completes. The roll math is pre-computed so the same final
     values fly to the wallet that the user sees on the dice. */
  window.hohFreshFacesBonesRoll = function(){
    if(!currentSpecial || currentSpecial.phase !== 'bonesRoll') return;
    if(currentSpecial.bonesRolled) return;
    if(currentSpecial.bonesAnimating) return;
    /* Pre-compute final faces + sum. */
    var finalFaces = [];
    for(var i = 0; i < 4; i++){
      finalFaces.push(1 + Math.floor(Math.random() * 6));
    }
    var sum = finalFaces.reduce(function(s, f){ return s + f; }, 0);
    var award = sum * 2;
    var sub;
    if(sum >= 22)      sub = 'A strong reading. The bones favor you.';
    else if(sum >= 14) sub = 'A clean roll. The Hall hears it.';
    else if(sum >= 8)  sub = 'A quiet reading. Small hearts will do.';
    else               sub = 'Low bones. Yet still, an answer.';
    currentSpecial.bonesAnimating = true;
    currentSpecial._bonesFinalFaces = finalFaces;
    currentSpecial._bonesFinalAward = award;
    currentSpecial._bonesFinalSub = sub;
    /* Render the rolling phase (dice show random faces, status =
       "The bones tumble..."). */
    currentSpecial.bonesFaces = [1, 1, 1, 1];
    var c = _ffContainer();
    if(c) c.innerHTML = renderGenericBazaarHtml(currentSpecial);
    /* Cycle the displayed dice through random faces every 70ms so
       the user sees the tumble. Query the DOM directly to avoid
       full re-renders that would interrupt the CSS animation. */
    var tickCount = 0;
    var rollInterval = setInterval(function(){
      tickCount++;
      try {
        var dice = document.querySelectorAll('.hoh-mb-mini-die-tumbling span');
        for(var d = 0; d < dice.length; d++){
          dice[d].textContent = 1 + Math.floor(Math.random() * 6);
        }
      } catch(_){}
      if(tickCount >= 18){ /* ~1260ms */
        clearInterval(rollInterval);
      }
    }, 70);
    /* Settle after ~1.5s — dice land on final values, sum reveals,
       hearts fly. */
    setTimeout(function(){
      clearInterval(rollInterval);
      if(!currentSpecial || currentSpecial.phase !== 'bonesRoll') return;
      currentSpecial.bonesFaces = finalFaces;
      currentSpecial.bonesRolled = true;
      currentSpecial.bonesAnimating = false;
      currentSpecial.awarded = award;
      currentSpecial.resolvedSub = sub;
      if(typeof window.hohAddHeartsToBank === 'function'){
        try { window.hohAddHeartsToBank(award); } catch(_){}
      }
      if(typeof window.hohRenderHeaderCounter === 'function'){
        try { window.hohRenderHeaderCounter(); } catch(_){}
      }
      var c2 = _ffContainer();
      if(c2) c2.innerHTML = renderGenericBazaarHtml(currentSpecial);
      /* Heart fly from the dice row to the wallet. */
      try {
        var anchor = c2 && c2.querySelector('.hoh-mb-mini-dice-row');
        if(anchor && typeof flyDiceTraderHeartsToPill === 'function'){
          flyDiceTraderHeartsToPill(anchor, Math.min(award, 14));
        }
      } catch(_){}
      /* Big reading (sum >= 22) = confetti burst. */
      if(sum >= 22){
        try {
          if(typeof window.hohFireConfetti === 'function'){
            window.hohFireConfetti();
            setTimeout(function(){ try { window.hohFireConfetti(); } catch(_){} }, 220);
          }
        } catch(_){}
      }
    }, 1500);
  };
  window.hohFreshFacesBonesFinish = function(){
    if(!currentSpecial || currentSpecial.phase !== 'bonesRoll') return;
    if(!currentSpecial.bonesRolled) return;
    /* R148-Z34: Just close the event. Rewards + heart-fly already
       fired in the roll handler — calling _bazaarResolveStatic here
       would double-credit hearts AND replay the fly animation,
       which the user reported as "hearts flow animation plays twice
       and there are too many menus". */
    _bazaarCloseAndRefresh();
  };
  /* R148-Z34: Shared close helper. Used by all game Finish handlers
     where the reward already landed during the roll phase. Just
     null out currentSpecial and trigger a normal refresh — no
     extra screen, no extra credit, no extra animation. */
  function _bazaarCloseAndRefresh(){
    currentSpecial = null;
    if(typeof window.hohFreshFacesRefresh === 'function'){
      try { window.hohFreshFacesRefresh({ fromInteractive: true }); } catch(_){}
    }
  }
  /* ================================================================ */
  /* A Reading (rune cast) handlers                                    */
  /* ================================================================ */
  var OMEN_RUNES = [
    { face:'SUN',   award:50, sub:'The Sun rune. A rare blessing.' },
    { face:'STAR',  award:30, sub:'The Star rune. Steady fortune.' },
    { face:'MOON',  award:20, sub:'The Moon rune. A quiet path.' },
    { face:'WHEEL', award:15, sub:'The Wheel rune. The cycle turns.' },
    { face:'DEATH', award:5,  sub:'The Death rune. A small ending.' }
  ];
  window.hohFreshFacesOmenCast = function(){
    if(!currentSpecial || currentSpecial.phase !== 'omenCast') return;
    if(currentSpecial.omenCast) return;
    /* Weighted toward middle outcomes — Sun is 10%, Death is 10%, the
       three middle runes share the remaining 80%. */
    var roll = Math.random();
    var pick;
    if(roll < 0.10)      pick = OMEN_RUNES[0]; /* SUN */
    else if(roll < 0.35) pick = OMEN_RUNES[1]; /* STAR */
    else if(roll < 0.70) pick = OMEN_RUNES[2]; /* MOON */
    else if(roll < 0.90) pick = OMEN_RUNES[3]; /* WHEEL */
    else                 pick = OMEN_RUNES[4]; /* DEATH */
    currentSpecial.omenRune = pick.face;
    currentSpecial.omenCast = true;
    currentSpecial.awarded = pick.award;
    currentSpecial.resolvedSub = pick.sub;
    if(pick.award > 0 && typeof window.hohAddHeartsToBank === 'function'){
      try { window.hohAddHeartsToBank(pick.award); } catch(_){}
    }
    var c = _ffContainer();
    if(c) c.innerHTML = renderGenericBazaarHtml(currentSpecial);
    try {
      var anchor = c && c.querySelector('.hoh-mb-mini-rune');
      if(anchor && pick.award > 0 && typeof flyDiceTraderHeartsToPill === 'function'){
        flyDiceTraderHeartsToPill(anchor, Math.min(pick.award, 12));
      }
      if(pick.face === 'SUN' && typeof spawnConfetti === 'function'){
        spawnConfetti(); setTimeout(spawnConfetti, 220);
      }
    } catch(_){}
  };
  window.hohFreshFacesOmenFinish = function(){
    if(!currentSpecial || currentSpecial.phase !== 'omenCast') return;
    if(!currentSpecial.omenCast) return;
    /* R148-Z34: Reward already credited in the cast handler. */
    _bazaarCloseAndRefresh();
  };

  /* ================================================================ */
  /* R148-Z29: Pay the Guide renderer + handlers                       */
  /* ================================================================ */
  function renderGuideHtml(state){
    var phase = state.phase || 'idle';
    var host = state.host || {};
    var bank = (typeof window.heartsRemainingToday === 'function') ? window.heartsRemainingToday() : 0;
    var hostMedia = _eventCharImg(host, { eager: true, draggable: false });
    var hostName = esc(host.name || 'A traveling bard');
    var hostMeta = esc((host.race || '') + (host.character_class ? ' &middot; ' + host.character_class : ''));
    if(phase === 'resolved'){
      var headline = state.resolvedHeadline || 'The bard tucks the coins away.';
      var sub = state.resolvedSub || '';
      return '<div class="hoh-fresh-special hoh-fresh-special-guide hoh-fresh-special-guide-resolved">' +
        eventHeaderHtml("A Guide's Offer", headline, { centered: true }) +
        '<div class="hoh-mb-host"><div class="hoh-mb-host-card" data-id="' + esc(host.id || '') + '">' +
          '<div class="hoh-mb-host-media">' + hostMedia + '</div>' +
          '<div class="hoh-mb-host-name">' + hostName + '</div>' +
        '</div></div>' +
        '<div class="hoh-mb-blessing-stage">' +
          '<div class="hoh-mb-blessing-gift" aria-hidden="true">&#127770;</div>' +
          '<div class="hoh-mb-blessing-sub">' + esc(sub) + '</div>' +
        '</div>' +
        '<div class="hoh-mb-foot-actions">' +
          '<button type="button" class="hoh-fresh-coin-toss" onclick="hohFreshFacesRefresh({fromInteractive:true})"><span>Keep exploring</span></button>' +
        '</div>' +
      '</div>';
    }
    /* Idle phase — host card + 2 option buttons. */
    var canAffordBazaar = bank >= 10;
    var canAffordLegShop = bank >= 50;
    var bazaarBtn = '<button type="button" class="hoh-mb-option hoh-fresh-guide-option' + (canAffordBazaar ? '' : ' hoh-mb-option-poor') + '"' +
      (canAffordBazaar ? ' onclick="hohFreshFacesGuidePick(\'bazaar\')"' : ' disabled') + '>' +
      '<span class="hoh-mb-option-title">A nearby bazaar</span>' +
      '<span class="hoh-mb-option-sub">' + (canAffordBazaar
        ? 'I know a vendor who is set up just past the bend.'
        : 'Need 10 hearts') + '</span>' +
      '<span class="hoh-mb-option-buy">Pay &middot; 10 hearts</span>' +
    '</button>';
    var legBtn = '<button type="button" class="hoh-mb-option hoh-fresh-guide-option hoh-fresh-guide-option-leg' + (canAffordLegShop ? '' : ' hoh-mb-option-poor') + '"' +
      (canAffordLegShop ? ' onclick="hohFreshFacesGuidePick(\'legendaryShop\')"' : ' disabled') + '>' +
      '<span class="hoh-mb-option-title">The Legendary Shop</span>' +
      '<span class="hoh-mb-option-sub">' + (canAffordLegShop
        ? 'Word is, a shopkeeper has rare goods. A long walk, but worth it.'
        : 'Need 50 hearts') + '</span>' +
      '<span class="hoh-mb-option-buy">Pay &middot; 50 hearts</span>' +
    '</button>';
    return '<div class="hoh-fresh-special hoh-fresh-special-guide">' +
      eventHeaderHtml("A Guide's Offer", 'A bard leans on a lamppost. Pay them, they\'ll take you somewhere worth your while.', { centered: true }) +
      '<div class="hoh-mb-host"><div class="hoh-mb-host-card" data-id="' + esc(host.id || '') + '">' +
        '<div class="hoh-mb-host-media">' + hostMedia + '</div>' +
        '<div class="hoh-mb-host-name">' + hostName + '</div>' +
        '<div class="hoh-mb-host-meta">' + hostMeta + '</div>' +
      '</div></div>' +
      '<div class="hoh-mb-grid">' + bazaarBtn + legBtn + '</div>' +
    '</div>';
  }
  window.hohFreshFacesGuidePick = function(destination){
    if(!currentSpecial || currentSpecial.type !== 'payGuide') return;
    if(currentSpecial.phase !== 'idle') return;
    var cost = (destination === 'legendaryShop') ? 50 : 10;
    var bank = (typeof window.heartsRemainingToday === 'function') ? window.heartsRemainingToday() : 0;
    if(bank < cost){
      if(typeof window.hohShowHeartToast === 'function'){
        try { window.hohShowHeartToast('Not enough hearts', 'You need ' + cost + ' hearts for this trip.'); } catch(_){}
      }
      return;
    }
    /* Charge via the same direct-ledger pattern bazaars use. */
    try {
      var raw = parseInt(localStorage.getItem('hoh-heart-bank-v1') || '0', 10) || 0;
      try{ window.hohHeartHint && window.hohHeartHint('spend_feature'); }catch(e){}
      localStorage.setItem('hoh-heart-bank-v1', String(Math.max(0, raw - cost)));
      try { document.dispatchEvent(new CustomEvent('hoh:hearts-bumped', { detail: { spent: cost } })); } catch(_){}
    } catch(_){}
    if(typeof window.hohRenderHeaderCounter === 'function'){
      try { window.hohRenderHeaderCounter(); } catch(_){}
    }
    /* Queue the destination as the NEXT roll. The picker checks
       window.__hohQueuedSpecial at the top of pickFreshFacesEventInner
       and forces that event. */
    var resolvedHeadline, resolvedSub;
    if(destination === 'legendaryShop'){
      window.__hohQueuedSpecial = 'legendaryShop';
      resolvedHeadline = 'The bard nods, pockets the coin.';
      resolvedSub = 'They lead you down a side street. The Legendary Shop is the next stop.';
    } else {
      /* For bazaar, pick a random flavor right now so the user gets
         variety across visits via this route. Setting __hohQueuedSpecial
         to 'mageBazaar' triggers the milestone-claim path which already
         randomizes via _buildBazaarEvent. */
      window.__hohQueuedSpecial = 'mageBazaar';
      resolvedHeadline = 'The bard whistles and waves you on.';
      resolvedSub = 'They know a bazaar that is just opening. Your next encounter waits.';
    }
    currentSpecial.phase = 'resolved';
    currentSpecial.resolvedHeadline = resolvedHeadline;
    currentSpecial.resolvedSub = resolvedSub;
    currentSpecial.destination = destination;
    var c = _ffContainer();
    if(c) c.innerHTML = renderGuideHtml(currentSpecial);
    if(typeof startAutoTimer === 'function') setTimeout(startAutoTimer, 0);
  };

  /* R114-C: Feed Your Own — clone of Underdog with own-hero host and
     possessive copy. Same press-and-hold mechanic via shared
     wireHeartStreamTargets. Stream window timer auto-closes after
     state.windowSec seconds (handled in startStreamWindow callback). */
  /* R590 (Jan): one card per hero the player owns, for the chooser
     phase. Deliberately NOT renderStreamTargetCard: that card carries
     "Hold to give hearts", which is a lie here. On this grid a tap
     SELECTS and spends nothing. */
  function renderFeedChoiceCard(c){
    c = c || {};
    var img = c.image_thumb_url || c.image_url || '';
    var firstLetter = esc((c.name || '?').charAt(0));
    var pos = (typeof window.hohImgObjPos === 'function')
      ? window.hohImgObjPos(c.id, 'primary')
      : '50% 50%';
    var cropStyle = (typeof window.hohImgStyle === 'function') ? window.hohImgStyle(c.id, 'primary') : ('object-position:' + pos + ';');
    var media = img
      ? '<img src="' + esc(img) + '" alt="" loading="eager" decoding="async" crossorigin="anonymous" style="' + cropStyle + '" onerror="if(window.hohImgFallback)hohImgFallback(this);">'
      : '<div class="hoh-fresh-letter">' + firstLetter + '</div>';
    var hearts = c.hearts || 0;
    var tier = '';
    try { tier = (window.hohGetRarity ? (window.hohGetRarity(hearts).label || '') : ''); } catch(_){}
    /* THE COMBO, MADE VISIBLE. Jan: "the main application is combo'd
       with the like your own heroes so they can give their own heroes
       +30 hearts from that event and if that hero has merchants mark
       it gets 60 essentially."
       This is not a new mechanic. mark_heart_explore (sql/R208:105)
       has been adding +1 per Explore heart to a marked hero since R196.
       Every heart poured here rides that path, so 30 really has always
       landed as 60. Nothing on screen ever said so, which is exactly
       why it reads as broken. The badge is the fix. */
    var marked = hfHasMerchantsMark(c);
    return '<button type="button" class="hoh-fresh-feedpick' + (marked ? ' hoh-fresh-feedpick-marked' : '') + '"' +
      ' onclick="hohFreshFacesFeedPick(\'' + esc(c.id) + '\')"' +
      ' aria-label="Give hearts to ' + esc(c.name || 'this hero') + (marked ? ', wearing the Merchant\'s Mark, hearts land double' : '') + '">' +
      '<span class="hoh-fresh-feedpick-media">' + media +
        (marked ? '<span class="hoh-fresh-feedpick-mark" aria-hidden="true">&#10022;</span>' : '') +
      '</span>' +
      '<span class="hoh-fresh-feedpick-name">' + esc(c.name || 'Unnamed') + '</span>' +
      '<span class="hoh-fresh-feedpick-meta">' + esc(tier) + ' &middot; ' + hearts + ' &#9829;&#xFE0E;</span>' +
    '</button>';
    /* ⛔ NO PER-CARD "Hearts land double" PILL, AND THIS WAS MEASURED,
       not judged. The first cut carried one on every marked card. In a
       real browser at 375x812 it cost 51.7px of the 620.5px grid, the
       single largest contributor, and pushed the wander bar and the
       "nothing is spent until you hold" reassurance below the fold on
       a five-hero grid.
       It was also REDUNDANT: the note under the grid already states the
       doubling once, in a full sentence, which is the right place for
       an explanation. The card's job is to say WHICH heroes, and the
       gold rim plus the corner glyph do that in no vertical space at
       all. The note names the same glyph so the two connect. */
  }

  function renderFeedYourOwnHtml(state){
    var cap = STREAM_EVENT_HEART_CAP.feedYourOwn || 10;
    var spent = (state && state.spentInEvent) | 0;
    var list = (state && state.chars) || [];

    /* PHASE 1: choose. Only ever reached when the player owns more than
       one hero, so this screen never asks a question with one answer. */
    if(state && state.phase === 'choosing' && list.length > 1){
      var anyMarked = false;
      for(var m = 0; m < list.length; m++){ if(hfHasMerchantsMark(list[m])) { anyMarked = true; break; } }
      var cards = '';
      for(var i = 0; i < list.length; i++){ cards += renderFeedChoiceCard(list[i]); }
      return '<div class="hoh-fresh-special hoh-fresh-special-stream hoh-fresh-special-stream-feedyourown hoh-fresh-feedchoose">' +
        eventHeaderHtml('Feed Your Own',
          'Pick which of your heroes gets the hearts. You have up to ' + cap + ' to give.',
          { centered: true }) +
        '<div class="hoh-fresh-feedpick-grid" data-count="' + list.length + '">' + cards + '</div>' +
        /* The glyph is named so the gold corner mark on the cards above
           is not left for the player to decode. */
        (anyMarked
          ? '<div class="hoh-fresh-feedpick-note"><span class="hoh-fresh-feedpick-noteglyph" aria-hidden="true">&#10022;</span> A hero wearing the Merchant\'s Mark earns two hearts for every one you give, so ' + cap + ' lands as ' + (cap * 2) + '.</div>'
          : '') +
        _streamWanderBarHtml() +
        '<div class="hoh-fresh-stream-foot">Tap a hero to choose them. Nothing is spent until you hold their portrait.</div>' +
      '</div>';
    }

    /* PHASE 2: stream. Unchanged from R114-C apart from the Mark line
       and the change-of-mind escape.

       ⛔ SELF-HEAL, because falling through here with no hero paints a
       card reading "Unnamed" over an empty frame and offers to pour
       thirty hearts into it. The producer only ever sets phase
       'choosing' when it holds more than one hero, and the rechoose
       handler guards on the same condition, so this is unreachable
       today. It is one line, and the failure it prevents is a player
       feeding a ghost. */
    var c = state.char || (state && state.chars && state.chars[0]) || {};
    var isMarked = hfHasMerchantsMark(c);
    /* R140-E: Visible event-cap counter, same pattern as Underdog. The
       user explicitly asked to see the cap so spamming feels bounded
       instead of "did anything just happen?". */
    /* The escape hatch only exists while NOTHING has been spent. Once a
       heart is in, the choice is made and the counter has to keep
       meaning what it says. Going back preserves spentInEvent anyway,
       so the cap can never be reset by bouncing between phases. */
    var canRechoose = (list.length > 1 && spent === 0);
    return '<div class="hoh-fresh-special hoh-fresh-special-stream hoh-fresh-special-stream-feedyourown">' +
      eventHeaderHtml('Feed Your Own',
        isMarked
          ? 'They wear the Merchant\'s Mark. Hold the portrait and every heart you give lands as two.'
          : 'One of yours, in the spotlight. Hold the portrait to pour up to ' + cap + ' hearts into them.',
        { centered: true }) +
      '<div class="hoh-fresh-stream-grid hoh-fresh-stream-grid-1">' + renderStreamTargetCard(c, { mode: 'feedYourOwn' }) + '</div>' +
      '<div class="hoh-fresh-stream-bar-wrap" aria-hidden="true"><div class="hoh-fresh-stream-bar" id="hoh-fresh-stream-bar"></div></div>' +
      '<div class="hoh-fresh-stream-cap-counter" aria-live="polite">' +
        '<span data-stream-cap-spent>' + spent + '</span>' +
        ' / ' + cap + ' hearts given' +
        (isMarked ? '<span class="hoh-fresh-feedpick-double">Landing as ' + (spent * 2) + '</span>' : '') +
      '</div>' +
      (canRechoose
        ? '<div class="hoh-fresh-feedpick-back"><button type="button" class="hoh-fresh-feedpick-backbtn" onclick="hohFreshFacesFeedRechoose()">Choose someone else</button></div>'
        : '') +
      _streamWanderBarHtml() +
      '<div class="hoh-fresh-stream-foot">Hold to stream hearts. The event ends at ' + cap + '.</div>' +
    '</div>';
  }

  /* R590: the chooser's tap target. Selects a hero and opens the stream.
     Spends nothing by itself. */
  window.hohFreshFacesFeedPick = function(id){
    if(!currentSpecial || currentSpecial.type !== 'feedYourOwn') return;
    if(currentSpecial.phase !== 'choosing') return;
    var list = currentSpecial.chars || [];
    var chosen = null;
    for(var i = 0; i < list.length; i++){
      if(list[i] && String(list[i].id) === String(id)){ chosen = list[i]; break; }
    }
    if(!chosen) return;
    currentSpecial.char = chosen;
    currentSpecial.phase = 'streaming';
    /* Re-enter renderSpecial rather than writing innerHTML here, so the
       stream wiring and the window timer are armed by the one place
       that owns them. renderSpecial REQUIRES its container: it
       dereferences container.style on the first line and every other
       call site passes _ffContainer(). */
    try {
      var _cP = _ffContainer();
      if(_cP) renderSpecial(_cP);
    } catch(_){}
    /* renderSpecial rewrites the container's innerHTML, which destroys
       the auto-progress bar element the running timer is painting. Every
       other in-place re-render in this file restarts the timer on a
       microtask for exactly this reason (11205, 13300, 13862 and a dozen
       more). Without it the bar freezes and the player loses the only
       signal that the page still rotates. */
    if(typeof startAutoTimer === 'function') setTimeout(startAutoTimer, 0);
  };

  /* R590: undo a mis-tap. Guarded on spentInEvent === 0 in the renderer
     AND again here, because a global is reachable from the console. */
  window.hohFreshFacesFeedRechoose = function(){
    if(!currentSpecial || currentSpecial.type !== 'feedYourOwn') return;
    if((currentSpecial.spentInEvent | 0) !== 0) return;
    if(!currentSpecial.chars || currentSpecial.chars.length < 2) return;
    /* Stop the running window and any live hold-stream before going
       back, or the 14s timer keeps counting against a screen that is
       no longer asking for a hold. */
    try { stopAllStreams(); } catch(_){}
    try { stopStreamWindow(); } catch(_){}
    currentSpecial.char = null;
    currentSpecial.phase = 'choosing';
    try {
      var _cR = _ffContainer();
      if(_cR) renderSpecial(_cR);
    } catch(_){}
    if(typeof startAutoTimer === 'function') setTimeout(startAutoTimer, 0);
  };

  /* R114-D / R115-C: Promotion — two phases.
     Idle: character X hearts from tier-up, press-and-hold to give up
     to 5 hearts. When threshold crossed, switches to 'promoted' phase.
     Promoted: celebration screen with the new tier glow layered over
     the original, "Promoted to X!" headline, and a Claim button. The
     2x refund pays out on Claim, not before — gives the user a moment
     to bask in the win and choose to collect. */
  function renderPromotionHtml(state){
    var c = state.char || {};
    var phase = state.phase || 'idle';
    var nextTier = state.nextTierLabel || 'the next tier';
    /* Resolve previous tier label so we can apply both glows. */
    var TIER_LADDER = ['Common','Uncommon','Rare','Epic','Legendary','Mythic'];
    var nextIdx = TIER_LADDER.indexOf(nextTier);
    var prevTier = (nextIdx > 0) ? TIER_LADDER[nextIdx - 1] : 'Common';
    var prevTierLower = prevTier.toLowerCase();
    var nextTierLower = nextTier.toLowerCase();
    if(phase === 'promoted'){
      /* Celebration phase. The card carries BOTH tier glows — original
         (stacking under) plus the new (on top) so the upgrade reads
         visually. The hoh-promoted-card-upgrade class fires the
         glow-handoff animation. */
      var refund = (state.spentInEvent || 0) * 2;
      var refundLabel = refund === 1 ? 'heart' : 'hearts';
      return '<div class="hoh-fresh-special hoh-fresh-special-stream hoh-fresh-special-stream-promotion hoh-fresh-special-stream-promotion-resolved">' +
        eventHeaderHtml('Promoted!', 'You pushed them into ' + nextTier + '. Claim your reward.', { centered: true }) +
        '<div class="hoh-fresh-stream-grid hoh-fresh-stream-grid-1">' +
          '<div class="hoh-promoted-card hoh-promoted-card-upgrade hoh-promoted-card-from-' + esc(prevTierLower) + ' hoh-promoted-card-to-' + esc(nextTierLower) + '">' +
            renderStreamTargetCard(c, { mode: 'promotionResolved' }) +
          '</div>' +
        '</div>' +
        '<div class="hoh-promoted-claim-wrap">' +
          '<button type="button" class="hoh-promoted-claim-btn" onclick="hohFreshFacesPromotionClaim()" aria-label="Claim ' + refund + ' ' + refundLabel + '">' +
            '<span class="hoh-promoted-claim-label">Claim ' + refund + ' ' + refundLabel + '</span>' +
            '<span class="hoh-promoted-claim-icon" aria-hidden="true">&#9829;&#xFE0E;</span>' +
          '</button>' +
        '</div>' +
        _streamWanderBarHtml() +
      '</div>';
    }
    /* Default 'idle' phase — give-hearts screen. */
    /* R131-B: gap is computed live as (initial_gap - hearts_given) so
       the subtitle decrements as the user holds. We wrap the number
       and the verb in tagged spans so updatePromotionGap can hot-swap
       just those nodes without re-rendering the whole event (which
       would interrupt the hold-to-stream). */
    var gapInitial = state.heartsToPromotion || 1;
    var spent = state.spentInEvent || 0;
    var liveGap = Math.max(0, gapInitial - spent);
    var gapVerb = liveGap === 1 ? 'heart' : 'hearts';
    return '<div class="hoh-fresh-special hoh-fresh-special-stream hoh-fresh-special-stream-promotion">' +
      eventHeaderHtml('On the Edge', 'This hero is <span id="hoh-fresh-promotion-gap-num" data-promotion-gap-num>' + liveGap + '</span> <span id="hoh-fresh-promotion-gap-verb" data-promotion-gap-verb>' + gapVerb + '</span> away from ' + nextTier + '. Push them over.', { centered: true }) +
      '<div class="hoh-fresh-stream-grid hoh-fresh-stream-grid-1">' + renderStreamTargetCard(c, { mode: 'promotion' }) + '</div>' +
      '<div class="hoh-fresh-stream-bar-wrap" aria-hidden="true"><div class="hoh-fresh-stream-bar" id="hoh-fresh-stream-bar"></div></div>' +
      _streamWanderBarHtml() +
      '<div class="hoh-fresh-stream-foot">Hold to give. Every heart brings them closer.</div>' +
    '</div>';
  }
  /* R131-B: Live update for the On the Edge subtitle counter. Targets
     the tagged spans inserted in renderPromotionHtml above. Called
     after every heart tick on a promotion event. Idempotent — can be
     called more times than hearts given without breaking anything. */
  function updatePromotionGap(){
    if(!currentSpecial || currentSpecial.type !== 'promotion') return;
    var gapInitial = currentSpecial.heartsToPromotion || 1;
    var spent = currentSpecial.spentInEvent || 0;
    var liveGap = Math.max(0, gapInitial - spent);
    var verb = liveGap === 1 ? 'heart' : 'hearts';
    try {
      var numEl = document.querySelector('[data-promotion-gap-num]');
      if(numEl) numEl.textContent = String(liveGap);
      var verbEl = document.querySelector('[data-promotion-gap-verb]');
      if(verbEl) verbEl.textContent = verb;
    } catch(_){}
  }

  /* Stream window timer — auto-closes the event after windowSec
     seconds. The bar fills via CSS transition keyed off the inline
     style we set on mount. */
  var streamWindowTimer = null;
  function startStreamWindow(state){
    stopStreamWindow();
    var bar = document.getElementById('hoh-fresh-stream-bar');
    var sec = (state && state.windowSec) || 12;
    if(bar){
      bar.style.transition = 'none';
      bar.style.width = '100%';
      void bar.offsetWidth;
      bar.style.transition = 'width ' + sec + 's linear';
      requestAnimationFrame(function(){ bar.style.width = '0%'; });
    }
    streamWindowTimer = setTimeout(function(){
      /* R114-C/D: feedYourOwn + promotion also use the stream window.
         All four event types end on the same windowSec timer. Hearts
         already given persist (server-side); we only stop the stream
         intervals + clear the special so the next refresh lands. */
      var STREAM_TYPES = { richGetRicher:1, underdog:1, feedYourOwn:1, promotion:1 };
      if(currentSpecial && STREAM_TYPES[currentSpecial.type]){
        /* R67: stop ALL running per-target stream intervals so the
           heart-stream doesn\'t keep firing past the window. */
        try { stopAllStreams(); } catch(_){}
        currentSpecial = null;
        if(typeof window.hohFreshFacesRefresh === 'function'){
          try { window.hohFreshFacesRefresh({ fromInteractive: true }); } catch(_){}
        }
      }
    }, sec * 1000);
  }
  function stopStreamWindow(){
    if(streamWindowTimer){ try { clearTimeout(streamWindowTimer); } catch(_){} streamWindowTimer = null; }
  }
  function stopAllStreams(){
    try {
      Object.keys(streamRunningIntervals).forEach(function(k){
        try { clearInterval(streamRunningIntervals[k]); } catch(_){}
        delete streamRunningIntervals[k];
      });
      var actives = document.querySelectorAll('.hoh-fresh-stream-target-active');
      for(var i = 0; i < actives.length; i++){
        actives[i].classList.remove('hoh-fresh-stream-target-active');
      }
    } catch(_){}
  }

  /* Wire each .hoh-fresh-stream-target with the hold-to-stream
     mechanic. Pointerdown/touchstart starts an interval at a calmer
     rate that ramps up modestly; pointerup/cancel/leave stops it.
     Each tick:
       - If wallet has hearts: spend one (hohHeart with the target as
         the btn so the section\'s mythic gate sees it as in-Fresh-
         Faces).
       - For underdog mode: also bumpFfHeartsToday() so it counts
         for the wander quest.
     A small heart sprite spawns at the cursor and flies toward the
     portrait every tick — visual feedback for the stream.
     R69: SLOWED rates (was 320→80ms = 12.5/sec, now 600→280ms = ~3.5/sec
     peak) so the user has time to react. ADDED a node-level streaming
     flag checked at the top of fire() to defeat the orphan-interval
     race where releasing during a tick let the next setInterval be
     scheduled AFTER the stop handler ran. ADDED a window-level
     pointerup safety + visibilitychange + blur so a release ANYWHERE
     kills the stream. ADDED a per-character daily-cap pre-check so
     the stream stops the moment 125 is hit (instead of wasting the
     rest of the bank firing into a wall). */
  var streamRunningIntervals = {};
  var __streamGlobalsWired = false;
  function wireStreamGlobalSafeties(){
    if(__streamGlobalsWired) return;
    __streamGlobalsWired = true;
    /* Window-level pointerup: catches releases outside any node
       (drag off the card, lift finger past the page edge on touch,
       browser eats the event etc.). */
    window.addEventListener('pointerup', stopAllStreams, true);
    window.addEventListener('pointercancel', stopAllStreams, true);
    /* Switching tabs or backgrounding the app: hard stop. Streams
       firing while the tab is hidden burns hearts the user can\'t
       see leaving. */
    document.addEventListener('visibilitychange', function(){
      if(document.hidden) stopAllStreams();
    });
    window.addEventListener('blur', stopAllStreams);
    /* If the cap toast appears (anywhere — manual heart, stream, or
       any other path), kill any active streams. The cap-toast wrapper
       calls hohOnStreamCapHit() below from inside hohHandleCapRejection
       but the safety net here covers the case where the toast is
       triggered by a non-stream code path during an active stream. */
  }
  /* Hook called from hoh-section-core hohOnCapRejected so the stream
     stops the instant a 125-cap is hit on any character. Exposed as a
     window-level helper so the host can call it without an import. */
  window.hohOnStreamCapHit = function(charId){
    var nodes = document.querySelectorAll('.hoh-fresh-stream-target[data-stream-id="' + (charId || '').replace(/["\\]/g,'') + '"]');
    if(nodes.length){
      for(var i = 0; i < nodes.length; i++) stopStreamFor(nodes[i]);
    } else {
      /* If the capped char isn\'t the stream target (rare — but a
         general safety), kill all streams so we never overspend. */
      stopAllStreams();
    }
  };
  function wireHeartStreamTargets(container){
    if(!container) return;
    wireStreamGlobalSafeties();
    var nodes = container.querySelectorAll('.hoh-fresh-stream-target');
    for(var i = 0; i < nodes.length; i++){
      (function(node){
        if(node.__hohStreamWired) return;
        node.__hohStreamWired = true;
        var startStream = function(ev){
          if(ev && ev.preventDefault) ev.preventDefault();
          /* R140-E: Refuse to start a fresh stream once the event has
             ended (cap hit). Without this, a rapid pointerdown during
             the 350ms fade dwell would create a new interval, sneak in
             extra hearts past the cap, and re-trigger the per-character
             125-cap toast — exactly the spam the user reported. */
          if(currentSpecial && currentSpecial.eventEnded) return;
          var charId = node.getAttribute('data-stream-id');
          var mode = node.getAttribute('data-stream-mode') || 'mythic';
          if(!charId) return;
          stopStreamFor(node);
          node.classList.add('hoh-fresh-stream-target-active');
          /* The streaming flag is the source of truth. fire() bails
             immediately if this is false — defeats the orphan-interval
             race entirely. */
          node.__hohStreaming = true;
          /* Capture the pointer so subsequent pointer events route to
             this node even if the finger drags off. Lets pointerup
             still fire on the right element. Without this, a drag-off
             on touch can leave the stream running. */
          try { if(ev && ev.pointerId != null && node.setPointerCapture) node.setPointerCapture(ev.pointerId); } catch(_){}
          var tickRate = 600; /* ms — starts CALM (was 320, too eager) */
          var minRate = 280;  /* ms — ramps to ~3.5/sec floor (was 80 = 12.5/sec, way too fast) */
          var ramped = 0;
          var fire = function(){
            /* HARD stop check — orphan intervals must die here. */
            if(!node.__hohStreaming){
              stopStreamFor(node);
              return;
            }
            /* R140-E: Event-end lock. The instant a stream cap is hit
               we set currentSpecial.eventEnded = true and refuse every
               subsequent fire. This is the gate that fixes the toast
               spam — orphan intervals or fresh pointerdowns during the
               fade window all bail here without touching hohHeart. */
            if(!currentSpecial || currentSpecial.eventEnded){
              stopStreamFor(node);
              return;
            }
            /* Wallet check (regular per-day heart bank). */
            var bank = (typeof window.heartsRemainingToday === 'function') ? window.heartsRemainingToday() : 0;
            if(bank <= 0){
              stopStreamFor(node);
              try { if(typeof window.hohShowHeartToast === 'function') window.hohShowHeartToast('Out of hearts', 'You\'ve spent today\'s.'); } catch(_){}
              return;
            }
            /* Per-character 125-cap pre-check. We track our own
               clicks-on-this-character-today via window.hohGetMineToday
               (set in section-core when hohHeart resolves). If we\'re
               at or past 125, stop the stream — don\'t fire and trigger
               the cap toast (which would also stop us, but at the cost
               of one wasted wallet decrement). */
            try {
              if(typeof window.hohGetMineToday === 'function'){
                var mineNow = window.hohGetMineToday(charId) | 0;
                if(mineNow >= 125){
                  stopStreamFor(node);
                  if(typeof window.hohHandleCapRejection === 'function') window.hohHandleCapRejection(charId, node);
                  return;
                }
              }
            } catch(_){}
            /* R140-E: Per-event heart cap for Underdog (30) and Feed
               Your Own (50). Replaces the looser R140-D 100-cap. The
               cap value is read from STREAM_EVENT_HEART_CAP so the
               renderer counter and this gate stay in sync. The end
               sequence is faster than before (350ms dwell + 280ms
               fade vs. previous 900 + 380) so the user can't re-grab
               the portrait and trigger a fresh stream during the
               fade-out — the event lock above kills any pointer that
               sneaks through in those 630ms anyway. */
            try {
              var capType = currentSpecial && currentSpecial.type;
              if(capType && STREAM_EVENT_HEART_CAP[capType]){
                var streamCap = STREAM_EVENT_HEART_CAP[capType] | 0;
                var streamSpent = currentSpecial.spentInEvent | 0;
                if(streamSpent >= streamCap){
                  /* Mark the event as locked FIRST so any concurrent
                     fire() ticks (other intervals, queued pointer
                     events) bail at the top-of-fire eventEnded check. */
                  currentSpecial.eventEnded = true;
                  stopStreamFor(node);
                  /* Stop EVERYTHING — including the stream-window timer
                     and any sibling intervals — before the fade so no
                     stale activity can spawn fresh hearts or toasts. */
                  try { stopStreamWindow(); } catch(_){}
                  try { stopAllStreams(); } catch(_){}
                  /* One toast announcing the cap. Subsequent calls are
                     gated by eventEnded so the user doesn't see a
                     second copy. */
                  try {
                    if(typeof window.hohShowHeartToast === 'function'){
                      var capLabel = capType === 'feedYourOwn' ? 'Feed Your Own cap reached.' : 'Underdog cap reached.';
                      window.hohShowHeartToast('+' + streamCap + ' hearts given', capLabel);
                    }
                  } catch(_){}
                  /* Fast fade — 350ms dwell + 280ms fade. Short enough
                     that the user can't re-grab the portrait, long
                     enough that the final hearts visibly land. */
                  setTimeout(function(){
                    try {
                      var ff = _ffContainer();
                      if(ff) ff.classList.add('hoh-fresh-fading-out');
                    } catch(_){}
                    /* R148-Z22e: Safety strip in case the inner removal
                       below silently fails (rAF throttle, cache invalidate,
                       etc.). Identical pattern to schedulePageAutoRefresh's
                       safety. */
                    setTimeout(function(){
                      try {
                        var ffSafety = document.getElementById('hoh-fresh-faces');
                        if(ffSafety && ffSafety.classList.contains('hoh-fresh-fading-out')){
                          ffSafety.classList.remove('hoh-fresh-fading-out');
                        }
                      } catch(_){}
                    }, 1800);
                    setTimeout(function(){
                      try {
                        var ff2 = _ffContainer();
                        if(ff2) ff2.classList.remove('hoh-fresh-fading-out');
                      } catch(_){}
                      /* Belt-and-braces cleanup before refresh. */
                      try { stopStreamWindow(); } catch(_){}
                      try { stopAllStreams(); } catch(_){}
                      currentSpecial = null;
                      if(typeof window.hohFreshFacesRefresh === 'function'){
                        try { window.hohFreshFacesRefresh({ fromInteractive: true }); } catch(_){}
                      }
                    }, 280);
                  }, 350);
                  return;
                }
              }
            } catch(_){}
            /* Completion is terminal even for a held stream. Stop before the
               next server spend so the 50th heart cannot become 51+ while the
               automatic claims and final pack are opening. */
            if(!_questAcceptsHeart()){
              try { stopStreamWindow(); } catch(_){}
              try { stopAllStreams(); } catch(_){}
              _beginWanderCompletion();
              return;
            }
            /* Fire the heart through the standard pipeline. The btn
               passed is the stream target — it\'s inside #hoh-fresh-
               faces so the section\'s mythic gate allows it. */
            try { if(typeof window.hohHeart === 'function') window.hohHeart(charId, node); } catch(_){}
            /* R87: Both stream modes (underdog AND mythic/Rich Get
               Richer) bump the wander quest counter — every heart spent
               counts toward Explore the Hall today. The user's request:
               they need to see the bar fill while holding so the action
               feels effective. */
            try { if(typeof bumpFfHeartsToday === 'function') bumpFfHeartsToday(); } catch(_){}
            /* R114-D: Promotion event tier-up detector + 5-heart cap.
               Track spent-on-this-promotion-event so we can:
                 1. Stop the stream once 5 hearts have been given
                    (event-cap is 5 heart attempts).
                 2. Detect when the char's allCharacters.hearts crosses
                    the next-tier threshold and fire the celebration +
                    silent 2x refund. */
            /* R140-E: Track hearts spent within Feed Your Own + Underdog
               events. Increment AFTER hohHeart so we count actual
               successful spends (not failed gate attempts). The
               cap pre-check above reads currentSpecial.spentInEvent on
               the NEXT tick to stop the stream. We also live-update
               the visible "X / cap" counter so the user sees progress
               without a re-render. */
            try {
              var streamType = currentSpecial && currentSpecial.type;
              if(streamType && STREAM_EVENT_HEART_CAP[streamType] && currentSpecial.char && String(currentSpecial.char.id) === String(charId)){
                currentSpecial.spentInEvent = (currentSpecial.spentInEvent || 0) + 1;
                try {
                  var counterEl = document.querySelector('#hoh-fresh-faces [data-stream-cap-spent]');
                  if(counterEl) counterEl.textContent = String(currentSpecial.spentInEvent);
                } catch(_){}
              }
            } catch(_){}
            try {
              if(currentSpecial && currentSpecial.type === 'promotion' && currentSpecial.char && String(currentSpecial.char.id) === String(charId)){
                currentSpecial.spentInEvent = (currentSpecial.spentInEvent || 0) + 1;
                /* R131-B: Live counter update — runs BEFORE handlePromotionTick
                   so the subtitle reflects the new gap immediately. If the
                   threshold was just crossed, handlePromotionTick re-renders
                   into the 'promoted' phase right after, replacing the gap
                   subtitle entirely. Either way, the user sees consistent
                   feedback on every heart they give. */
                if(typeof updatePromotionGap === 'function'){
                  try { updatePromotionGap(); } catch(_){}
                }
                if(typeof handlePromotionTick === 'function'){
                  handlePromotionTick(charId);
                }
                /* Hard cap at 5 hearts per promotion event. The user
                   asked for "up to 5 hearts" to push the promotion. */
                if(currentSpecial.spentInEvent >= 5){
                  stopStreamFor(node);
                }
              }
            } catch(_){}
            /* Repaint the inline stream-event wander bar so the user
               sees progress within the event. */
            try { if(typeof paintStreamWanderBar === 'function') paintStreamWanderBar(); } catch(_){}
            /* Sprite that flies from the node center toward... itself
               — visual reinforcement that hearts are landing here.
               The browser\'s own ::before/::after animation could do
               this but we do it in JS for precise timing. */
            try { spawnStreamHeart(node); } catch(_){}
            /* Ramp up the rate — only if we\'re still streaming. */
            if(ramped < 5 && node.__hohStreaming){
              tickRate = Math.max(minRate, Math.round(tickRate * 0.85));
              ramped++;
              clearInterval(streamRunningIntervals[charId]);
              streamRunningIntervals[charId] = setInterval(fire, tickRate);
            }
          };
          /* First fire immediately so the user gets feedback on
             pointerdown, not after the first tick. */
          fire();
          streamRunningIntervals[charId] = setInterval(fire, tickRate);
        };
        var stopStream = function(){ stopStreamFor(node); };
        node.addEventListener('pointerdown', startStream);
        node.addEventListener('pointerup', stopStream);
        node.addEventListener('pointercancel', stopStream);
        node.addEventListener('pointerleave', stopStream);
        node.addEventListener('lostpointercapture', stopStream);
        /* Keyboard activation — Enter/Space holds while pressed. */
        node.addEventListener('keydown', function(e){
          if(e.key === 'Enter' || e.key === ' '){
            e.preventDefault();
            if(!streamRunningIntervals[node.getAttribute('data-stream-id')]) startStream(e);
          }
        });
        node.addEventListener('keyup', function(e){
          if(e.key === 'Enter' || e.key === ' ') stopStream();
        });
      })(nodes[i]);
    }
  }
  function stopStreamFor(node){
    if(!node) return;
    /* Flip the flag FIRST so an in-flight fire() bails immediately
       instead of scheduling a new interval. */
    node.__hohStreaming = false;
    var charId = node.getAttribute('data-stream-id');
    if(charId && streamRunningIntervals[charId]){
      try { clearInterval(streamRunningIntervals[charId]); } catch(_){}
      delete streamRunningIntervals[charId];
    }
    node.classList.remove('hoh-fresh-stream-target-active');
  }
  function spawnStreamHeart(node){
    if(!node) return;
    var rect = node.getBoundingClientRect();
    if(!rect.width) return;
    var startX = rect.left + rect.width / 2 + (Math.random() * 30 - 15);
    var startY = rect.top + rect.height + 6;
    var endX = rect.left + rect.width / 2 + (Math.random() * 14 - 7);
    var endY = rect.top + rect.height / 2;
    var el = document.createElement('div');
    el.className = 'hoh-fresh-stream-heart';
    el.innerHTML = '&#9829;&#xFE0E;';
    el.style.left = startX + 'px';
    el.style.top = startY + 'px';
    document.body.appendChild(el);
    requestAnimationFrame(function(){
      el.style.transform = 'translate(' + (endX - startX) + 'px,' + (endY - startY) + 'px) scale(1.4)';
      el.style.opacity = '0';
    });
    setTimeout(function(){ if(el.parentNode) el.parentNode.removeChild(el); }, 900);
  }


  /* Manual Continue from the Dice Trader resolved screen. Bypasses
     the auto-bar so users who just collected their hearts don\'t
     have to wait for the countdown. Clears the special, kicks the
     normal refresh path with fromInteractive (free, no -1 heart),
     and the refresh handles re-render + restart of the auto-timer. */
  window.hohFreshFacesDiceTraderContinue = function(){
    if(!currentSpecial || currentSpecial.type !== 'diceTrader') return;
    if(currentSpecial.phase !== 'won') return;
    currentSpecial = null;
    if(typeof window.hohFreshFacesRefresh === 'function'){
      try { window.hohFreshFacesRefresh({ fromInteractive: true }); } catch(_){}
    }
  };

  /* User picked a path on a choice event. Set the chosen theme as
     the current themed phase, clear the special, and render. */
  window.hohFreshFacesChoiceSelect = function(idx){
    if(!currentSpecial || currentSpecial.type !== 'choice') return;
    var pick = currentSpecial.options[idx];
    if(!pick) return;
    currentThemedPhase = pick.theme;
    seenThemes[pick.theme.eyebrow] = true;
    saveSeenSet(SEEN_THEMES_KEY, seenThemes);
    /* R136-themed-rotation: keep the recent buffer in sync with manual
       theme transitions so the picker doesn't immediately re-pick a
       theme the user just chose at the Crossroads. */
    rememberRecentTheme(pick.theme.eyebrow);
    setPhase(1);
    cachedWanderingPool = null;
    cachedWanderingPhase = null;
    renderedSnapshotIds = null;
    snapshotPhaseKey = null;
    currentSpecial = null;
    window.hohRenderFreshFaces();
    /* Restart the auto-refresh timer for the resulting page. */
    if(typeof startAutoTimer === 'function') startAutoTimer();
  };

  /* Coin flip: 50/50. We render the flipping state for ~1.4s, then
     resolve. Win = bonus heart + rogue theme + heart animation
     toast. Loss = miss message + auto-refresh. */
  window.hohFreshFacesFlipCoin = function(){
    if(!currentSpecial || currentSpecial.type !== 'thievesCoin') return;
    if(currentSpecial.phase && currentSpecial.phase !== 'idle') return;
    currentSpecial.phase = 'flipping';
    var container = _ffContainer();
    if(container) container.innerHTML = renderThievesCoinHtml(currentSpecial);
    var won = Math.random() < 0.5;
    setTimeout(function(){
      if(!currentSpecial || currentSpecial.type !== 'thievesCoin') return;
      currentSpecial.phase = won ? 'won' : 'lost';
      if(container) container.innerHTML = renderThievesCoinHtml(currentSpecial);
      /* Lock the refresh icon during the result + transition so the
         user can't bail before the rogue grid appears. The icon
         visually dims via opacity and pointer-events:none. We unlock
         in startAutoTimer's restart at the end. */
      lockRefreshIcon(true);
      autoCompletingFlag = true;
      if(won){
        /* R53: NO automatic +1 grant or auto-transition. The user now
           clicks "Enter the Rogues\' Den" which fires
           hohFreshFacesEnterRoguesDen — that handler grants +2 hearts,
           marks the daily flag, and transitions into the rogue theme.
           This puts the user in control of the beat and lets the
           reward feel deliberate. We DO unlock the refresh icon and
           release autoCompletingFlag so the user isn\'t stuck if they
           wander away — but the auto-bar runs at the regular rate so
           they have plenty of time to click Enter. */
        try {
          lockRefreshIcon(false);
          autoCompletingFlag = false;
          if(typeof startAutoTimer === 'function') startAutoTimer();
        } catch(_){}
      } else {
        /* Loss path: hold the miss message a beat, then refresh into
           a normal phase. Refresh stays locked during the message.
           fromInteractive bypasses the out-of-hearts entry guard so a
           user with an empty wallet doesn't get stuck on the loss
           screen — the coin event has resolved and the user needs to
           advance.
           R140-Z3j: Fade the section out during the last ~800ms of
           the hold so the refresh feels graceful instead of abrupt.
           The .hoh-fresh-fading-out class is already wired in CSS to
           animate opacity + a slight upward translate over 380ms. We
           kick it in at 1500ms (with ~900ms of fade time before the
           hard refresh at 2400ms). */
        setTimeout(function(){
          try {
            var ffFade = _ffContainer();
            if(ffFade) ffFade.classList.add('hoh-fresh-fading-out');
          } catch(_){}
          /* R148-Z22e: Safety strip 1800ms after the class lands,
             same pattern as schedulePageAutoRefresh. Catches the
             case where the inner clear (at t=2400) silently fails
             because _ffContainer's cache invalidated or the strip
             threw. */
          setTimeout(function(){
            try {
              var ffSafety = document.getElementById('hoh-fresh-faces');
              if(ffSafety && ffSafety.classList.contains('hoh-fresh-fading-out')){
                ffSafety.classList.remove('hoh-fresh-fading-out');
              }
            } catch(_){}
          }, 1800);
        }, 1500);
        setTimeout(function(){
          try {
            var ffClear = _ffContainer();
            if(ffClear) ffClear.classList.remove('hoh-fresh-fading-out');
          } catch(_){}
          currentSpecial = null;
          lockRefreshIcon(false);
          autoCompletingFlag = false;
          window.hohFreshFacesRefresh({ auto: true, fromInteractive: true });
        }, 2400);
      }
    }, 1400);
  };

  /* R53: Continue handler for the Sneaky Encounter win path. Grants
     +2 hearts to the bank, marks the daily-cap flag (so the coin
     can\'t fire again today even via the force-pick path), then
     transitions into the Rogues\' theme so the user spends the
     fresh hearts on the appropriate flavor of cards. */
  window.hohFreshFacesEnterRoguesDen = function(){
    if(!currentSpecial || currentSpecial.type !== 'thievesCoin') return;
    if(currentSpecial.phase !== 'won') return;
    /* R102: Grant bumped 2 → 5 hearts for entering the Rogues' Den.
       Pulse the header pill so the user sees it land before the page
       transitions away. */
    if(typeof window.hohAddHeartsToBank === 'function'){
      try { window.hohAddHeartsToBank(5); } catch(_){}
    }
    if(typeof window.hohShowHeartToast === 'function'){
      try { window.hohShowHeartToast('The rogues let you in', 'Step inside.'); } catch(_){}
    }
    try {
      var pill = document.getElementById('hoh-tk-header') || document.getElementById('hoh-heart-remaining');
      if(pill){
        pill.classList.remove('hoh-tk-heart-pulse');
        void pill.offsetWidth;
        pill.classList.add('hoh-tk-heart-pulse');
        setTimeout(function(){ pill.classList.remove('hoh-tk-heart-pulse'); }, 900);
      }
    } catch(_){}
    /* Heart-fly from the Continue button so the +5 visually lands. */
    try {
      var btn = document.querySelector('#hoh-fresh-faces .hoh-fresh-coin-toss.hoh-fresh-dice-continue');
      if(btn && typeof flyDiceTraderHeartsToPill === 'function'){
        flyDiceTraderHeartsToPill(btn, 5);
      }
    } catch(_){}
    /* R71: If the host was mythic, also fire the +30 mythic bonus —
       same pattern as mini spinner, brawl, coin bet. The mythic
       blessing toast surfaces from inside applyMythicHostBonus. */
    if(currentSpecial.mythicHost){
      try { applyMythicHostBonus('#hoh-fresh-faces .hoh-fresh-thieves-host .hoh-fresh-card-host'); } catch(_){}
    }
    /* Mark the daily flag and transition. */
    try { markSneakyCoinUsedToday(); } catch(_){}
    var rogueTheme = THEMED_PHASES.find(function(p){ return p.filter && p.filter.cls === 'Rogue'; });
    if(rogueTheme){
      currentThemedPhase = rogueTheme;
      seenThemes[rogueTheme.eyebrow] = true;
      saveSeenSet(SEEN_THEMES_KEY, seenThemes);
      /* R136-themed-rotation: keep recent buffer in sync — same reason
         as the Crossroads pick path. */
      rememberRecentTheme(rogueTheme.eyebrow);
      setPhase(1);
      cachedWanderingPool = null;
      cachedWanderingPhase = null;
      renderedSnapshotIds = null;
      snapshotPhaseKey = null;
    }
    currentSpecial = null;
    window.hohRenderFreshFaces();
    if(typeof startAutoTimer === 'function') setTimeout(startAutoTimer, 0);
  };

  /* Visually disable the refresh icon during a transition. We add a
     class instead of setting attributes directly so the existing
     onclick handler is left intact when we unlock. */
  function lockRefreshIcon(on){
    var icons = document.querySelectorAll('#hoh-fresh-faces .hoh-fresh-refresh');
    icons.forEach(function(b){
      if(on) b.classList.add('hoh-fresh-refresh-locked');
      else b.classList.remove('hoh-fresh-refresh-locked');
    });
  }

  /* Random themed refresh. Each click picks an unseen themed phase
     from THEMED_PHASES. Once every theme has been shown this session
     the seenThemes set resets and cycling begins again — the user
     always gets something they haven't seen this session unless
     they've truly seen everything. ~18% chance per refresh to land
     on phase 0 (Fresh Faces) so the user can always find their way
     back to "now". The pool re-shuffles every refresh. visibleCount
     preserved.

     User-driven refreshes also roll for special events (legendary,
     epic, choice, thieves coin). Auto-refresh callers pass
     {auto:true} so the auto-timer never surprises the user with a
     mini-game they didn't intend to start. */
  /* R140-V: Pending-manual-refresh latch. Set to true when a manual
     click kicks off the bar-sprint animation; cleared 280ms later when
     the deferred picker runs. While true, additional manual clicks are
     ignored — without this guard, rapid clicking during the delay
     window would queue multiple refreshes, each deducting a heart and
     cycling past content the user never saw. Auto / interactive paths
     are unaffected by the latch but DO cancel a pending deferred
     manual (an interactive Continue mid-delay supersedes the manual
     click; we don't want both refreshes firing back-to-back). */
  var _pendingManualRefresh = false;
  var _pendingManualRefreshTimer = null;
  window.hohFreshFacesRefresh = function(opts){
    /* Once the daily target lands, no auto/manual/event refresh may rotate in
       another playable page while the rewards are being claimed. */
    if(_ffQuestClosing || getFfHeartsToday() >= WANDER_QUEST_TARGET){
      _beginWanderCompletion();
      return;
    }
    /* R299: every advance path — the auto-bar, the wander icon, an event's
       own "next" — stops at the gate. Repaint the doorway instead of rolling
       past it, so the Battlegrounds can never be rotated away. */
    if(_bgHoldsSurface()){
      try { if(typeof stopAutoTimer === 'function') stopAutoTimer(); } catch(_){}
      try { if(window.hohRenderFreshFaces) window.hohRenderFreshFaces(); } catch(_){}
      return;
    }
    /* R318: never rotate away an event that is mid-resolution on a staked
       roll — the payout timer needs currentSpecial alive. The auto bar
       already waits (isAutoEligible); this stops the manual click too.
       Interactive escapes (an event's OWN buttons, fromInteractive) always
       pass — an event must never be able to trap the player. Only the four
       short timer-driven phases are held; the bazaars' waiting states are
       deliberately absent. */
    try {
      /* R591: 'fighting' listed here as well. A running Hall Fight is
         already held by the _diceRolling half of this condition, but the
         two guards (this one and isAutoEligible) should state the same
         rule in the same words, or the next reader has to prove they
         agree. A manual refresh mid-fight would take the wager and show
         no result. 'wager' is deliberately absent from BOTH: it is a
         waiting-for-a-tap state and holding one traps the player. */
      if(!(opts && opts.fromInteractive) && currentSpecial && (currentSpecial._diceRolling === true ||
         /^(rolling|spinning|duelRolling|flipping|fighting)$/.test(String(currentSpecial.phase || '')))){
        return;
      }
    } catch(_){}
    var cur = getPhase();
    /* R148-Z22s: At the start of EVERY refresh (auto, manual,
       interactive), hard-reset pageHearts.spent + the per-character
       counted set + consumedThisPage + pageHearts.key. The user
       reported "i heart 2 characters and it resets back into itself,
       and i cant spend any hearts" — that's the same-theme reroll
       case: refresh fires, picker lands on the SAME phase, the new
       render computes the SAME phaseKey, recomputePageHearts sees
       `pageHearts.key !== pageKey` as FALSE and skips the reset, so
       spent stays at max → "Spent here" stays → user gates out of
       every card on the new page. By zeroing here we make every
       refresh a clean slate regardless of where the picker lands.
       Doesn't affect cross-render bookkeeping that needs to persist
       (renderedSnapshotIds is reset elsewhere when it should be). */
    try {
      pageHearts.spent = 0;
      pageHearts.key = null;
      /* R148-Z29: ALSO zero pageHearts.budget on every refresh. The
         previous "spent + key only" reset left budget stale from the
         prior page, which let the safety-net check at the top of
         renderInto misfire (e.g., budget=2 from prior page, spent=0
         post-reset, but if any code path managed to bump spent before
         the new recompute landed, safety net would re-trigger
         schedulePageAutoRefresh and loop the user back into "Spent
         here"). Zeroing budget makes the safety net's `budget > 0`
         guard genuinely guard until recomputePageHearts establishes
         the new page's budget. */
      pageHearts.budget = 0;
      /* Also reset max so the "max stays stable mid-page" rule in
         recomputePageHearts has a clean baseline to recompute against. */
      pageHearts.max = 0;
      pageHeartsCountedSet = Object.create(null);
      try { consumedThisPage = Object.create(null); } catch(_){}
      /* Repaint the chip immediately so the "Spent here" label can't
         linger between refresh fire and the new render's paint.
         paintPageHearts with max=0 hides the chip entirely until
         recomputePageHearts re-establishes it. */
      try { paintPageHearts(); } catch(_){}
    } catch(_){}
    /* R144-A + R145-A: Phase -1 (Heroes You've Lifted) is no longer a
       refresh dead-end. We previously bailed here, which froze the
       section. Now we let refresh proceed AND — crucially — force
       a phase 0 reset before the picker runs. Without this reset,
       the picker would re-evaluate against the same exhausted fresh
       pool and land right back on phase -1 ("refreshes into itself",
       per user). Forcing phase 0 first means the picker has the full
       wander pool + special-event pool + themed-phase pool to choose
       from. Only if ALL of those genuinely have nothing fresh will
       the page fall back to phase -1 — which now caps at 15 cards
       and re-shuffles the hearted list, so even that fallback feels
       different from the previous render. */
    if(cur === -1){
      try {
        setPhase(0);
        currentSpecial = null;
        currentThemedPhase = null;
        cachedWanderingPool = null;
        cachedWanderingPhase = null;
        renderedSnapshotIds = null;
        snapshotPhaseKey = null;
      } catch(_){}
    }

    /* Manual refresh = user clicked the refresh icon. Costs 1 heart
       so users either wait for the auto-timer (free) or engage with
       the cards on screen. Auto-refreshes (timer expiring) and
       interactive event escapes (Wander Further, Marrow Decline,
       Hero Circle Walk Past, etc.) are FREE so users aren't trapped
       in an event with an empty wallet. */
    var isManual = !(opts && (opts.auto || opts.fromInteractive));
    /* R318: the auto-advance has already fired and its FREE refresh lands in
       under 400ms (autoFadeId pending). A manual click in that window paid a
       heart for a refresh the auto path then cancelled — the heart vanished.
       Let the free refresh win. */
    if(isManual && autoFadeId) return;
    /* R140-V: ignore manual clicks fired during the deferral window
       of an earlier manual click. Auto / interactive paths still
       proceed (they don't take the deferral branch). */
    if(isManual && _pendingManualRefresh) return;
    /* R140-V: If an auto / interactive refresh fires while a manual
       deferral is pending, cancel the pending timer — the new flow
       supersedes it and we don't want both firing back-to-back. */
    if(!isManual && _pendingManualRefreshTimer){
      try { clearTimeout(_pendingManualRefreshTimer); } catch(_){}
      _pendingManualRefreshTimer = null;
      _pendingManualRefresh = false;
    }

    /* Out-of-hearts hard block: when the wallet is empty, refusing
       to refresh keeps the user on the current page so they don't
       cycle past content they can't engage with. The "Out of hearts"
       chip becomes the only clickable affordance and points to the
       Daily Tickets modal. Interactive event escapes still get
       through so users can finish what they started. */
    var bankNow;
    try {
      bankNow = (typeof window.heartsRemainingToday === 'function')
        ? window.heartsRemainingToday() : 1;
    } catch(_){ bankNow = 1; }
    if(bankNow <= 0 && !(opts && opts.fromInteractive)){
      /* Pulse the chip red so the user understands why the click
         didn't refresh. Reuses the same R16 deny pulse class. */
      try {
        var chipEl = document.querySelector('#hoh-fresh-page-hearts');
        if(chipEl){
          chipEl.classList.remove('hoh-fresh-page-hearts-deny');
          void chipEl.offsetWidth;
          chipEl.classList.add('hoh-fresh-page-hearts-deny');
          setTimeout(function(){ if(chipEl) chipEl.classList.remove('hoh-fresh-page-hearts-deny'); }, 950);
        }
      } catch(_){}
      return;
    }
    /* R86/R87/R108: Signature for "did anything visibly change."
       R108 dropped the internal phase NUMBER (1→2→3 cycles) because
       it changed across refreshes that picked the SAME theme — the
       user saw identical cards but the signature differed (by phase
       number only) so the refund never fired. Now we use a binary
       "wander vs themed" bucket plus the theme eyebrow + special type
       + first 6 hero IDs. If those four match before and after, the
       refresh produced no visible change and we refund the heart. */
    var preSig = (function(){
      try {
        var p = getPhase();
        var bucket = (p === 0) ? 'wander' : 'themed';
        var t = currentThemedPhase ? currentThemedPhase.eyebrow : '';
        var s = currentSpecial ? currentSpecial.type : '';
        var idsHash = '';
        try {
          var nodes = document.querySelectorAll('#hoh-fresh-faces .hoh-fresh-card[data-id]');
          var ids = [];
          for(var ni = 0; ni < Math.min(nodes.length, 6); ni++){
            ids.push(nodes[ni].getAttribute('data-id'));
          }
          idsHash = ids.join(',');
        } catch(_){}
        return bucket + '|' + t + '|' + s + '|' + idsHash;
      } catch(_){ return ''; }
    })();

    /* Charge 1 heart on manual refresh and animate a -1 fly so the
       user clearly sees the cost. Skipped on auto/interactive paths.
       The deduction goes through the same localStorage channel as
       normal heart spends + dispatches hoh:hearts-bumped so all
       surfaces sync. */
    if(isManual){
      try {
        /* R284: hint first — the 1-heart manual refresh logged spend_other. */
        try { if (window.hohHeartHint) window.hohHeartHint('spend_feature'); } catch(_){}
        var raw = parseInt(localStorage.getItem('hoh-heart-bank-v1') || '0', 10) || 0;
        var nextBank = Math.max(0, raw - 1);
        localStorage.setItem('hoh-heart-bank-v1', String(nextBank));
        try { document.dispatchEvent(new CustomEvent('hoh:hearts-bumped', { detail: { spent: 1 } })); } catch(_){}
        if(typeof window.hohRenderHeaderCounter === 'function'){
          try { window.hohRenderHeaderCounter(); } catch(_){}
        }
        /* Spawn a small "-1" floating indicator near the refresh
           icon. Pure CSS class, auto-removes after the animation. */
        try {
          var iconEl = document.querySelector('#hoh-fresh-faces #hoh-fresh-refresh, #hoh-fresh-faces .hoh-fresh-refresh');
          if(iconEl){
            var rect = iconEl.getBoundingClientRect();
            var bubble = document.createElement('div');
            bubble.className = 'hoh-fresh-refresh-cost';
            bubble.innerHTML = '<span aria-hidden="true">&#9829;&#xFE0E;</span>&minus;1';
            bubble.style.left = (rect.left + rect.width / 2) + 'px';
            bubble.style.top = rect.top + 'px';
            document.body.appendChild(bubble);
            setTimeout(function(){ if(bubble.parentNode) bubble.parentNode.removeChild(bubble); }, 1000);
          }
        } catch(_){}
      } catch(_){}
    }

    /* R140-V: On a COMMITTED manual click (heart deducted, going to
       defer the refresh), sprint the auto-bar to 100% AND fire the
       spin animation on the icon. Placed AFTER the bank check + heart
       deduction so out-of-hearts clicks (which bail early above) don't
       play the visual — the bar would otherwise sprint to 100% with
       no actual refresh and confuse the user. The bar animation is
       purely visual — the deferred picker block 280ms later replaces
       the bar's DOM with a fresh 0% fill on the new render.
       R140-W: Cancel the rAF FIRST. The auto-timer's rAF tick keeps
       writing fill.style.width every frame based on autoProgress —
       which was overriding our inline transition and causing the bar
       to "reset to 0 and move on" (the user's exact report) instead
       of smoothly sprinting to 100%. Cancelling the rAF lets the
       inline CSS transition run unimpeded for the 220ms sprint
       window. We don't call stopAutoTimer because that hides the bar
       and resets autoProgress; we just want the rAF stopped so our
       transition takes effect. The bar's DOM is replaced on the next
       render anyway, and startAutoTimer fires fresh on the new bar. */
    if(isManual){
      try {
        if(autoRafId){
          cancelAnimationFrame(autoRafId);
          autoRafId = null;
        }
      } catch(_){}
      try {
        var fillNow = document.getElementById('hoh-fresh-auto-bar-fill');
        if(fillNow){
          /* Force a reflow before applying the transition so the
             starting frame is committed. Without this, the browser
             may collapse the from/to states and skip the animation. */
          void fillNow.offsetWidth;
          fillNow.style.transition = 'width 0.22s cubic-bezier(0.45, 0, 0.55, 1)';
          fillNow.style.width = '100%';
        }
      } catch(_){}
      try {
        var iconNow = document.getElementById('hoh-fresh-refresh') || document.querySelector('.hoh-fresh-refresh');
        if(iconNow){
          iconNow.classList.remove('hoh-fresh-refresh-spin');
          /* Force reflow so the animation re-fires on consecutive
             clicks within the same render. */
          void iconNow.offsetWidth;
          iconNow.classList.add('hoh-fresh-refresh-spin');
        }
      } catch(_){}
    }
    /* R86: Refund helper. If the post-refresh state signature matches
       the pre-refresh signature, the user paid for nothing and we
       restore the heart silently (no extra toast — they already saw
       the -1 animation). Schedule on a microtask after this turn so
       the picker / setPhase have a chance to commit first. */
    function _refundIfNoChange(){
      if(!isManual) return;
      try {
        var p2 = getPhase();
        var bucket2 = (p2 === 0) ? 'wander' : 'themed';
        var t2 = currentThemedPhase ? currentThemedPhase.eyebrow : '';
        var s2 = currentSpecial ? currentSpecial.type : '';
        var idsHash2 = '';
        try {
          var nodes2 = document.querySelectorAll('#hoh-fresh-faces .hoh-fresh-card[data-id]');
          var ids2 = [];
          for(var ni2 = 0; ni2 < Math.min(nodes2.length, 6); ni2++){
            ids2.push(nodes2[ni2].getAttribute('data-id'));
          }
          idsHash2 = ids2.join(',');
        } catch(_){}
        var postSig = bucket2 + '|' + t2 + '|' + s2 + '|' + idsHash2;
        if(postSig === preSig){
          /* Nothing changed — refund the heart we just spent. */
          var raw2 = parseInt(localStorage.getItem('hoh-heart-bank-v1') || '0', 10) || 0;
          try{ window.hohHeartHint && window.hohHeartHint('earn_reward'); }catch(e){}
          localStorage.setItem('hoh-heart-bank-v1', String(raw2 + 1));
          try { document.dispatchEvent(new CustomEvent('hoh:hearts-bumped', { detail: { spent: -1 } })); } catch(_){}
          if(typeof window.hohRenderHeaderCounter === 'function') window.hohRenderHeaderCounter();
          /* R115-A: Make the refund VISIBLE. The previous behavior was
             silent — user saw the -1 animation, paid no real heart
             (refund fired), but didn't know it. They'd click again
             thinking the first one failed, and the second click might
             land a real refresh. From their perspective: "took 2 hearts
             to actually refresh." Now we play a +1 floater near the
             refresh icon and toast a clear "Refresh refunded" message
             so the user knows their heart came back. */
          try {
            var iconEl2 = document.querySelector('#hoh-fresh-faces #hoh-fresh-refresh, #hoh-fresh-faces .hoh-fresh-refresh');
            if(iconEl2){
              var rect2 = iconEl2.getBoundingClientRect();
              var bubble2 = document.createElement('div');
              bubble2.className = 'hoh-fresh-refresh-cost hoh-fresh-refresh-refund';
              bubble2.innerHTML = '<span aria-hidden="true">&#9829;&#xFE0E;</span>+1';
              bubble2.style.left = (rect2.left + rect2.width / 2) + 'px';
              bubble2.style.top = rect2.top + 'px';
              document.body.appendChild(bubble2);
              setTimeout(function(){ if(bubble2.parentNode) bubble2.parentNode.removeChild(bubble2); }, 1000);
            }
          } catch(_){}
          try {
            if(typeof window.hohShowHeartToast === 'function'){
              window.hohShowHeartToast('Heart refunded', 'No new heroes to surface right now. Try again in a moment.');
            }
          } catch(_){}
        }
      } catch(_){}
    }

    /* R140-V: Defer the picker + render block on manual click so the
       bar sprint + icon spin animations have time to play before the
       page transitions. Auto and interactive paths run synchronously
       as before. The 280ms delay matches the bar sprint duration
       (200ms transition) plus a small buffer for the icon spin to
       reach its visual climax. The user sees: click → bar fills →
       icon spins → page changes. Without the delay, the page changed
       mid-sprint and the user never saw the bar reach the end.
       Guard with _pendingManualRefresh so rapid double-clicks during
       the delay window don't queue multiple refreshes (would double-
       deduct hearts and cycle past content the user hasn't seen). */
    var _pickAndRender = function(){
      _pendingManualRefresh = false;
      _pendingManualRefreshTimer = null;
      _pickAndRenderInner();
    };
    if(isManual){
      _pendingManualRefresh = true;
      _pendingManualRefreshTimer = setTimeout(_pickAndRender, 280);
      return;
    }
    _pickAndRender();
    /* The actual picker + render logic, extracted into a closure so
       we can defer it. All existing returns + branches are preserved
       inside; the closure scope captures preSig / opts / cur / etc.
       from the outer function. */
    function _pickAndRenderInner(){
    /* Roll for a special event first. If we land on one, render it
       and bail — the normal phased flow doesn't apply.

       R140-Z3n: pickFreshFacesEvent can now return tour sentinels
       in addition to event objects:
         - { _tourSkipRender: true }    auto-pause / no-op during tour;
                                         leave the screen untouched.
         - { _tourThemedRender: true }  themed-phase step; the picker
                                         already set currentThemedPhase
                                         + phase, so we skip the
                                         normal theme-pick logic and
                                         render the themed grid via
                                         renderInto directly.
       These prevent the random-theme leak the user reported. */
    var event = pickFreshFacesEvent(opts);
    if(event && event._tourSkipRender){
      /* Tour pause: don't clear currentSpecial, don't repaint, don't
         pick a new theme. The current screen stays exactly as it is.
         Auto-timer still resumes so the user can manually advance the
         tour with the next refresh. */
      if(typeof startAutoTimer === 'function'){
        setTimeout(startAutoTimer, 0);
      }
      return;
    }
    if(event && event._tourThemedRender){
      /* Tour themed-phase step: currentThemedPhase + phase are
         already set by consumeAnonTourStep. Render the themed grid
         using the standard renderInto path (which respects the
         current phase + theme), and bail BEFORE the random
         theme-picking block can overwrite our state. */
      currentSpecial = null;
      window.hohRenderFreshFaces();
      if(typeof startAutoTimer === 'function'){
        setTimeout(startAutoTimer, 0);
      }
      setTimeout(_refundIfNoChange, 0);
      return;
    }
    if(event && event._heartThemedRender){
      /* R140-Z3n: Uniform dispatcher selected a themed phase. Same
         render path as the tour sentinel — currentThemedPhase + phase
         are already set, and the random theme-picking block below
         must be skipped to prevent overwrite. */
      currentSpecial = null;
      window.hohRenderFreshFaces();
      if(typeof startAutoTimer === 'function'){
        setTimeout(startAutoTimer, 0);
      }
      setTimeout(_refundIfNoChange, 0);
      return;
    }
    if(event){
      currentSpecial = event;
      var container = _ffContainer();
      if(container) renderSpecial(container);
      /* Restart the auto-timer for EVERY event. The bar belongs on every
         event screen so users always know the page rotates. The only
         pauses happen inside isAutoEligible (e.g., the coin's flipping
         animation) — startAutoTimer respects that and won't tick. We
         schedule on setTimeout(0) so the freshly-injected DOM commits
         before startAutoTimer queries for the bar element. */
      if(typeof startAutoTimer === 'function'){
        setTimeout(startAutoTimer, 0);
      }
      setTimeout(_refundIfNoChange, 0);
      return;
    }
    /* Clear any prior special so a normal refresh wipes the slate. */
    currentSpecial = null;
    /* R136-themed-pool: Plain "Wandering Heroes" (THEMED_PHASES[0])
       is now ONE entry in the pool with the same probability as any
       other theme. Two exceptions wrap a daily session:
         1. Page-load default (existing behavior — phase starts at 0
            on init, so the user's first arrival shows Fresh Faces).
         2. End of session — when the player has spent all their
            hearts, the rail returns to Fresh Faces as a closing
            view. The 0-hearts check below handles that.
       The previous 18%/4% forced reset is GONE. Fresh Faces now
       competes naturally in the rotation, ~1-in-30 odds per refresh. */
    var heartsLeft = 1;
    try {
      if(typeof window.heartsRemainingToday === 'function'){
        heartsLeft = window.heartsRemainingToday();
      }
    } catch(_){}
    if(heartsLeft <= 0 && cur !== 0){
      /* End of session: wallet exhausted. Return to Fresh Faces. */
      currentThemedPhase = null;
      setPhase(0);
    } else {
      /* R96: BEFORE picking the next theme, mark the CURRENT theme as
         seen so it can never be re-picked on the same refresh. This
         was the root of the "refresh-to-itself" bug — when seenThemes
         had cycled fully and reset, the picker could roll back into
         the very theme the user was already on. Now if the user is
         currently on Theme A, A is in seenThemes BEFORE the pick,
         so A is filtered out of unseenIndices unless A is the only
         eligible theme left. */
      if(currentThemedPhase && currentThemedPhase.eyebrow){
        seenThemes[currentThemedPhase.eyebrow] = true;
      }
      /* Pick from themes the user hasn't seen yet this session AND
         that have at least MIN_THEME_CHARS characters in the pool —
         themes that would surface only 1-2 cards aren't worth a
         refresh. When all eligible themes have been seen the set
         resets so cycling starts over. */
      var eligibleIndices = [];
      /* R113-C: filter out the FF Portrait Gallery (ffShowcase) themed
         phase if Captured by Jan or Portrait Gallery fired within the
         last 10 rolls. Two FF galleries back-to-back made the rail
         feel like a clone. */
      var ffGated = (typeof ffShowcaseGated === 'function') && ffShowcaseGated();
      for(var i = 0; i < THEMED_PHASES.length; i++){
        var t = THEMED_PHASES[i];
        if(themePoolSize(t.filter) < MIN_THEME_CHARS) continue;
        if(ffGated && t.ffShowcase) continue;
        eligibleIndices.push(i);
      }
      /* R136-themed-rotation: filter out BOTH long-cycle seen themes
         AND short-cycle recent themes. The recent buffer holds the
         last 5 picks; excluding them means even after the seen-set
         resets, we can't immediately repeat a theme the user just
         saw. If the combined filter empties the eligible pool, fall
         back to seen-only filtering, then to all-eligible. */
      var unseenIndices = eligibleIndices.filter(function(k){
        var eb = THEMED_PHASES[k].eyebrow;
        return !seenThemes[eb] && !isRecentTheme(eb);
      });
      if(unseenIndices.length === 0){
        /* Seen-set fully cycled. Reset and try again, still excluding
           recent. Keeps the "no immediate repeat" guarantee even at
           the cycle boundary. */
        seenThemes = Object.create(null);
        saveSeenSet(SEEN_THEMES_KEY, seenThemes);
        unseenIndices = eligibleIndices.filter(function(k){
          return !isRecentTheme(THEMED_PHASES[k].eyebrow);
        });
      }
      if(unseenIndices.length === 0 && eligibleIndices.length > 0){
        /* Last resort: every eligible theme is in the recent buffer
           (only possible when there are <= 5 eligible themes total).
           Pick from any eligible to avoid a hang. */
        unseenIndices = eligibleIndices.slice();
      }
      if(unseenIndices.length === 0){
        /* R140-J: Previous behavior reset to phase 0 (Fresh Faces) when
           no themed phase had enough chars. Combined with the renderer's
           own fallback this produced the "refreshes into another Fresh
           Faces" loop.
           R140-K: Per user request, when the hall is literally exhausted
           (every char hearted/dismissed) we should NOT fall back to
           phase 0 — we should RESTOCK and continue. We always switch to
           Wandering Heroes (THEMED_PHASES[0], untyped, no filter). The
           renderer's `unseenList` already has self-healing logic: when
           its strict pool is empty (all chars hearted/dismissed), it
           rebuilds with `includeHearted: true, includeDismissed: true`
           and clears the seenChars set. So Wandering Heroes always
           produces a fresh batch — even after a full session of
           hearting everyone — and the user keeps wandering instead of
           hitting a dead-end "Fresh Faces" grid.
           We also reset the seenChars + seenThemes sets here so the
           NEXT successful themed pick has fresh material to draw from
           (rotation starts over rather than continuing from a
           half-cycled state). */
        try {
          seenChars = Object.create(null);
          if(typeof saveSeenSet === 'function' && typeof SEEN_CHARS_KEY === 'string'){
            saveSeenSet(SEEN_CHARS_KEY, seenChars);
          }
          seenThemes = Object.create(null);
          if(typeof saveSeenSet === 'function' && typeof SEEN_THEMES_KEY === 'string'){
            saveSeenSet(SEEN_THEMES_KEY, seenThemes);
          }
        } catch(_){}
        currentThemedPhase = THEMED_PHASES[0];
        try { rememberRecentTheme(THEMED_PHASES[0].eyebrow); } catch(_){}
        setPhase((cur === 0 ? 1 : ((cur % 3) + 1)));
        cachedWanderingPool = null;
        cachedWanderingPhase = null;
        renderedSnapshotIds = null;
        snapshotPhaseKey = null;
        window.hohRenderFreshFaces();
        if(typeof startAutoTimer === 'function') startAutoTimer();
        setTimeout(_refundIfNoChange, 0);
        return;
      }
      /* Weighted pick: rare-marked themes (currently The Portrait
         Gallery) get a fraction of the weight of normal themes so they
         appear less frequently in the rotation. We pick by accumulating
         weights across unseen indices and rolling a random within the
         total. Normal themes weigh 1, rare themes weigh 0.2 — so on
         a typical roll the gallery has ~17% the chance of any other
         given theme. The user wanted both Portrait Gallery + Captured
         by Jan to appear less often; this handles the ambient half. */
      var weights = unseenIndices.map(function(k){
        return THEMED_PHASES[k].rare ? 0.2 : 1;
      });
      var totalWeight = weights.reduce(function(a, b){ return a + b; }, 0);
      var roll = Math.random() * totalWeight;
      var pickedIdx = unseenIndices[unseenIndices.length - 1];
      for(var w = 0, j = 0; j < unseenIndices.length; j++){
        w += weights[j];
        if(roll < w){ pickedIdx = unseenIndices[j]; break; }
      }
      var idx = pickedIdx;
      var picked = THEMED_PHASES[idx];
      /* Avoid repeating the immediately-current theme. R108 hardened
         this: previously the avoid-step only fired when unseenIndices
         had MORE than one option, leaving small Halls (where only 1
         eligible theme exists) stuck on the same theme refresh after
         refresh. Now if the only eligible theme matches the current
         one, we drop into phase 0 (Fresh Faces / wandering) so the
         user always gets a different view for their heart. */
      if(currentThemedPhase && picked.eyebrow === currentThemedPhase.eyebrow){
        if(unseenIndices.length > 1){
          idx = unseenIndices.find(function(k){ return THEMED_PHASES[k].eyebrow !== currentThemedPhase.eyebrow; });
          if(idx != null) picked = THEMED_PHASES[idx];
        } else {
          /* Only one eligible theme and it's the one we're on. Fall
             back to wandering so the user sees fresh content. */
          currentThemedPhase = null;
          setPhase(0);
          cachedWanderingPool = null;
          cachedWanderingPhase = null;
          renderedSnapshotIds = null;
          snapshotPhaseKey = null;
          window.hohRenderFreshFaces();
          if(typeof startAutoTimer === 'function') startAutoTimer();
          setTimeout(_refundIfNoChange, 0);
          return;
        }
      }
      seenThemes[picked.eyebrow] = true;
      saveSeenSet(SEEN_THEMES_KEY, seenThemes);
      /* R136-themed-rotation: also push to the recent ring buffer so
         this theme can't be picked for the next 5 refreshes even if
         the seen-set resets in between. */
      rememberRecentTheme(picked.eyebrow);
      currentThemedPhase = picked;
      /* R113-C: Mark FF showcase fire so Captured by Jan can't
         appear within the next 10 rolls. */
      if(picked && picked.ffShowcase){
        try { markFfShowcaseFired(); } catch(_){}
      }
      /* Phase number cycles 1-3 for legacy state-tracking. */
      setPhase((cur === 0 ? 1 : ((cur % 3) + 1)));
    }
    cachedWanderingPool = null;
    cachedWanderingPhase = null;
    /* Clear the desktop snapshot so the next render captures a fresh
       initial set for the new phase. */
    renderedSnapshotIds = null;
    snapshotPhaseKey = null;
    window.hohRenderFreshFaces();
    /* Restart the auto-refresh countdown for the new page. */
    if(typeof startAutoTimer === 'function') startAutoTimer();
    setTimeout(_refundIfNoChange, 0);
    } /* end of _pickAndRenderInner */
  };

  /* Returns characters for the Fresh Faces rail. Multi-pass strategy
     so the rail is NEVER empty:
       Pass 1: fresh window, eligible (not dismissed/hearted/in-flight).
       Pass 2: all eligible by newest, no fresh window.
       Pass 3 (recycle): top up to MIN_FRESH_FACES (2 rows worth) by
                          dipping into the full hall sorted by newest,
                          ignoring the dismissed/hearted exclusion.
                          Dedups against earlier passes by id.
     R136-recycle: User asked for a 2-row guarantee — even on a hall
     where every character has been hearted or dismissed, recycle the
     youngest heroes back so the rail keeps populating. The youngest-
     first order in the recycle pass means the user sees the most
     recent submissions first, even on a re-encounter.
     MIN_FRESH_FACES is sized for 2 desktop rows (5 cards/row * 2 =
     10). Mobile uses fewer cards-per-row, so 10 still over-fills its
     2-row target — the renderer's mobile-aware slice handles that. */
  var MIN_FRESH_FACES = 10;
  function freshList(){
    var all = (typeof window.hohGetAllCharacters === 'function') ? window.hohGetAllCharacters() : [];
    if(!all || !all.length) return [];
    var now = Date.now();
    var dismissed = getDismissed();
    var dismissedSet = {};
    dismissed.forEach(function(id){ dismissedSet[id] = true; });
    function eligible(c){
      if(!c || !c.id || !c.created_at) return false;
      /* R140-Z3h: hidden chars (debug-mode hide) never appear in
         Explore the Hall. They stay reachable via direct URL +
         exact-name search in the main gallery; this is the wandering
         pool, which should always read as "live heroes." */
      if(c.is_hidden === true) return false;
      if(dismissedSet[c.id]) return false;
      if(getMineCount(c.id) > 0) return false;
      if(heartIntent[c.id]) return false;
      return true;
    }
    function newestFirst(a, b){
      return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
    }
    /* Pass 1: characters within the fresh window. */
    var pass1 = all.filter(function(c){
      if(!eligible(c)) return false;
      var t = new Date(c.created_at).getTime();
      if(isNaN(t)) return false;
      return (now - t) <= FRESH_MS;
    }).sort(newestFirst);
    /* Pass 2: all eligible heroes by newest, no fresh window. */
    var pass2 = all.filter(function(c){
      if(!eligible(c)) return false;
      var t = new Date(c.created_at).getTime();
      if(isNaN(t)) return false;
      /* Skip ones already in pass 1. */
      return (now - t) > FRESH_MS;
    }).sort(newestFirst);
    /* Combine the two eligible passes (fresh first, then non-fresh
       eligible). If we already have >= MIN_FRESH_FACES, return as-is. */
    var out = pass1.concat(pass2);
    if(out.length >= MIN_FRESH_FACES) return out;
    /* Pass 3 (recycle): pull from the full hall, youngest first,
       skipping anyone already in `out` so we don't dupe. Recycling
       includes dismissed + hearted chars — the rail must stay
       populated even when the user has engaged with everyone. */
    var seen = {};
    out.forEach(function(c){ if(c && c.id) seen[c.id] = true; });
    var recycle = all.filter(function(c){
      if(!c || !c.id || !c.created_at) return false;
      /* R140-Z3h: hidden chars stay out of the recycle pool too. */
      if(c.is_hidden === true) return false;
      if(seen[c.id]) return false;
      var t = new Date(c.created_at).getTime();
      if(isNaN(t)) return false;
      return true;
    }).sort(newestFirst);
    /* Pad up to MIN_FRESH_FACES. If the hall has fewer total chars
       than that (small hall), out + recycle is still all we have —
       return everything we found.
       R148-Z14: Apply unseen-first ordering so new players cycle
       through the entire hall before repeats. Within each tier the
       newest-first ordering is preserved. Visible chars are marked
       as seen so the next call to freshList prefers fresh material. */
    var combined = out.concat(recycle);
    var unseenList_ff = combined.filter(function(c){ return !seenChars[c.id]; });
    var seenList_ff   = combined.filter(function(c){ return !!seenChars[c.id]; });
    /* Full-cycle reset: every wandering candidate has been viewed.
       Clear so the next call starts a fresh discovery cycle. */
    if(unseenList_ff.length === 0 && seenList_ff.length > 0){
      seenChars = Object.create(null);
      try { saveSeenSet(SEEN_CHARS_KEY, seenChars); } catch(_){}
      unseenList_ff = combined;
      seenList_ff = [];
    }
    var reordered = unseenList_ff.concat(seenList_ff);
    var finalSlice = reordered.slice(0, Math.max(MIN_FRESH_FACES, reordered.length));
    /* Mark the visible slice as seen so subsequent Wandering Heroes
       renders cycle to fresh material. Idempotent — already-seen
       chars stay seen, unseen chars flip to seen. */
    finalSlice.forEach(function(c){ if(c && c.id) seenChars[c.id] = true; });
    try { saveSeenSet(SEEN_CHARS_KEY, seenChars); } catch(_){}
    return finalSlice;
  }

  function rarTier(c){
    try {
      if(typeof window.hohGetRarity === 'function') return window.hohGetRarity(c.hearts || 0).tier;
    } catch(e){}
    return 'common';
  }

  function cardHtml(c){
    var name = esc(c.name || 'Unnamed');
    var meta = esc((c.race || '') + (c.character_class ? ' ' + c.character_class : ''));
    var firstLetter = esc((c.name || '?').charAt(0));
    var img = c.image_thumb_url || c.image_url;
    /* Honor the user's stored vertical crop offset (set in the edit
       form) so the head doesn't get chopped on the fresh-faces card. */
    var ffPos = (typeof window.hohImgObjPos === 'function')
      ? window.hohImgObjPos(c.id, 'primary')
      : ('50% ' + ((typeof window.hohGetImageOffsetY === 'function') ? window.hohGetImageOffsetY(c.id, 'primary') : 50) + '%');
    var ffStyle = (typeof window.hohImgStyle === 'function') ? window.hohImgStyle(c.id, 'primary') : ('object-position:' + ffPos + ';');
    /* Fresh Faces are always above the fold; eager-load all visible. */
    var media = img
      ? '<img src="' + esc(img) + '" alt="' + name + '" loading="eager" fetchpriority="high" decoding="async" crossorigin="anonymous" draggable="false" oncontextmenu="return false" data-letter="' + firstLetter + '" style="' + ffStyle + '" onerror="if(window.hohImgFallback)hohImgFallback(this);">'
      : '<div class="hoh-fresh-letter">' + firstLetter + '</div>';
    var tier = rarTier(c);
    var mineCount = getMineCount(c.id);
    var heartedCls = mineCount > 0 ? ' hoh-fresh-card-hearted' : '';
    /* The "your hearts" chip was removed by request — Fresh Faces
       is about discovery, not a tally of past affection. Hearted
       chars still get the pink border via the .hoh-fresh-card-hearted
       class (heartedCls above), but no count is shown. The wrapper
       var stays empty so the existing template still composes. */
    var mineChip = '';
    /* The NEW pill should only appear on cards that are actually
       within the 3-day fresh window. Refresh cycles can populate the
       rail with older "wandering" heroes — those should not get the
       NEW badge. */
    var isActuallyNew = false;
    if(c.created_at){
      var t = new Date(c.created_at).getTime();
      if(!isNaN(t) && (Date.now() - t) <= FRESH_MS) isActuallyNew = true;
    }
    var newPill = isActuallyNew ? '<span class="hoh-fresh-pill" aria-hidden="true">NEW</span>' : '';
    /* Outer is a div with role=button so the inner dismiss X can be a real
       button without nesting interactive elements (invalid HTML). The
       div is keyboard-accessible via the keydown handler. */
    /* R252: THE CARD IS THE HEART. One tap anywhere on the card gives a
       heart (the roll's whole game); a second tap within a beat opens
       the detail; the old heart circle is now an OPEN door. X still
       dismisses. Keyboard: Enter/Space OPEN (hearts stay a pointer
       gesture; the open button is tabbable too). */
    return '<div role="button" tabindex="0" class="hoh-fresh-card hoh-fresh-rar-' + tier + heartedCls + '" data-id="' + esc(c.id) + '" ' +
      'onclick="hohFreshCardTap(\'' + esc(c.id) + '\',event)" ' +
      'onkeydown="if(event.key===\'Enter\'||event.key===\' \'){event.preventDefault();hohOpenDetail(\'' + esc(c.id) + '\');}" ' +
      'aria-label="Heart ' + name + ' (double-tap to open)">' +
      '<button type="button" class="hoh-fresh-card-dismiss" onclick="hohFreshFacesDismiss(\'' + esc(c.id) + '\',event)" aria-label="Dismiss ' + name + '">&times;</button>' +
      /* Restore button — only visible when the card is in the
         spent-dismiss locked state. Lets the user undo a hide
         without leaving the rail. CSS shows it conditionally. */
      '<button type="button" class="hoh-fresh-card-restore" onclick="hohFreshFacesRestore(\'' + esc(c.id) + '\',event)" aria-label="Restore ' + name + '">+</button>' +
      newPill +
      '<span class="hoh-fresh-media">' + media + '</span>' +
      '<span class="hoh-fresh-body">' +
        '<span class="hoh-fresh-name">' + name + '</span>' +
        '<span class="hoh-fresh-meta">' + meta + '</span>' +
      '</span>' +
      '<button type="button" class="hoh-fresh-card-heart hoh-fresh-card-open" onclick="hohFreshFacesOpen(\'' + esc(c.id) + '\',event)" aria-label="Open ' + name + '">' +
        '<svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" stroke-width="2.1" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M14 4h6v6"/><path d="M20 4 11.5 12.5"/><path d="M10 6H6a2 2 0 0 0-2 2v10a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2v-4"/></svg>' +
      '</button>' +
      mineChip +
    '</div>';
  }

  /* Undo a dismiss — removes the spent-dismiss lock from the card
     in place, restores the character to the global pool, and
     decrements the consumed count for this page so the user has
     one less "spent" slot used. */
  window.hohFreshFacesRestore = function(id, e){
    if(e){ e.stopPropagation(); e.preventDefault(); }
    if(!id) return;
    try { if(typeof window.hohRestoreCharacter === 'function') window.hohRestoreCharacter(id); } catch(_){}
    var card = document.querySelector('.hoh-fresh-card[data-id="' + cssEscape(id) + '"]');
    if(card){
      card.classList.remove('hoh-fresh-card-spent-dismiss');
    }
    /* Drop from consumed bucket. */
    try {
      var key = pageHearts.key || '';
      if(consumedThisPage[key] && consumedThisPage[key][id]){
        delete consumedThisPage[key][id];
      }
    } catch(_){}
  };

  function renderInto(container, full){
    if(_xintroUp()) return;   /* R439: same door, same lock */
    if(!container) return;
    /* Pick the source list based on the current phase. full[] is what
       freshList() returned but for wandering/final phases we want a
       different pool entirely. */
    var phase = getPhase();
    if(phase !== 0) full = pickListForPhase(phase);
    /* Phase 0 (Fresh Faces) is STRICT: it only ever shows characters
       carrying the NEW badge (created within FRESH_DAYS). If that
       pool is empty we fall through to the empty-state branch
       below — we do NOT silently swap in older "wandering" heroes,
       since that would defeat the badge contract (users expect Fresh
       Faces to be genuinely fresh). The user can still discover
       older heroes via the Wander Further refresh, which moves to a
       themed phase explicitly. */
    if(!full.length){
      /* Phase -1 with no hearted heroes (or any other dead-end pool):
         render a small reset card so the user isn't stuck with no UI.
         A click resets phase back to 0 and re-renders. */
      if(phase === -1){
        container.style.display = '';
        container.innerHTML =
          '<div class="hoh-fresh-header">' +
            '<span class="hoh-fresh-eyebrow">You have met everyone</span>' +
          '</div>' +
          '<div class="hoh-fresh-empty">' +
            '<p>No more heroes to browse here. Heart a few from the main hall and they\'ll appear, or start over.</p>' +
            '<button type="button" class="hoh-fresh-empty-reset" onclick="hohFreshFacesResetPhase()">Back to Fresh Faces</button>' +
          '</div>';
        lastRenderedIds = [];
        return;
      }
      /* R136-fallback: With freshList() now falling back to the
         latest heroes overall, this empty state only fires when the
         user has dismissed or hearted every eligible character in
         the entire hall. Copy reframed to match that reality
         instead of the misleading "no new arrivals" line. */
      if(phase === 0){
        container.style.display = '';
        container.innerHTML =
          '<div class="hoh-fresh-header">' +
            '<span class="hoh-fresh-eyebrow">Fresh Faces</span>' +
            '<span class="hoh-fresh-sub">You\'ve spent time with everyone here. Tap refresh to find more.</span>' +
            '<button type="button" class="hoh-fresh-refresh" onclick="hohFreshFacesRefresh()" aria-label="Show new heroes" title="Show new heroes">' +
              '<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 12a9 9 0 1 1-3.5-7.1"/><polyline points="21 4 21 10 15 10"/></svg>' +
            '</button>' +
            '<span class="hoh-fresh-auto-bar" id="hoh-fresh-auto-bar" aria-hidden="true"><span class="hoh-fresh-auto-bar-fill" id="hoh-fresh-auto-bar-fill"></span></span>' +
          '</div>';
        lastRenderedIds = [];
        return;
      }
      /* Non-zero, non-(-1) themed phase came back empty (e.g. mobile
         user hearted all 4 visible cards in this theme). Auto-refresh
         to a new phase instead of hiding so the user keeps going
         seamlessly. We guard with autoRefreshing so we don't
         ping-pong forever if every phase is empty. */
      if(!autoRefreshing){
        autoRefreshing = true;
        setTimeout(function(){
          autoRefreshing = false;
          if(typeof window.hohFreshFacesRefresh === 'function'){
            window.hohFreshFacesRefresh();
          }
        }, 280);
        /* Brief blank while we swap phases — keeps the layout stable
           rather than collapsing the section height suddenly. */
        container.style.display = '';
        container.innerHTML = '<div class="hoh-fresh-header"><span class="hoh-fresh-eyebrow">Finding fresh faces…</span></div>';
        lastRenderedIds = [];
        return;
      }
      container.style.display = 'none';
      lastRenderedIds = [];
      return;
    }
    container.style.display = '';
    /* Re-read phase since pickListForPhase may have flipped to -1. */
    phase = getPhase();
    /* BOTH mobile and desktop now use the same snapshot-then-deplete
       behavior: on first render of a phase we capture an initial set
       of IDs, then subsequent renders only show those of the snapshot
       that are still in the pool (i.e. not hearted / not dismissed).
       Hearting / X-ing leaves the slot EMPTY rather than backfilling
       — when ALL slots are empty, we trigger an auto-refresh into a
       new phase so the user sees a fresh batch.

       Mobile snapshot size is 4 (a 2x2 grid). Desktop uses the
       configurable visibleCount (default 12, two rows of six). */
    var isMobile = false;
    try { isMobile = window.matchMedia('(max-width: 768px)').matches; } catch(e){}
    var MOBILE_VISIBLE = 4;
    /* Desktop card count is per-phase. Phase 0 (Fresh Faces) caps at
       PAGE_SIZE (10 = 2 rows) so the section reads as a quick "what's
       new" panel, not a wall of cards. Themed phases default to 15
       (3 rows) so they have room to feel like a gathering, but each
       theme can override via its `cards` field — Rising Stars (10) and
       The Portrait Gallery (5) read tighter on purpose. The cap is
       always bound by the actual pool size so we never render empty
       slots. Mobile stays at 4 (2x2). */
    var phaseCardCap;
    if(phase === 0){
      phaseCardCap = PAGE_SIZE; /* Fresh Faces: 10 newest, 2 rows */
    } else if(phase === -1){
      /* R144-A: Cap to 15 cards (3 rows of 5) instead of MAX_VISIBLE.
         The full hearted view was 4+ rows for users with many hearts,
         which felt overwhelming AND left no room for the page heart
         allowance + refresh affordance. 15 keeps the phase compact
         and refreshable. */
      phaseCardCap = 15;
    } else {
      var theme = currentThemedPhase || THEMED_PHASES[0];
      phaseCardCap = (theme && typeof theme.cards === 'number') ? theme.cards : 15;
    }
    var desktopSize = Math.min(phaseCardCap, full.length);
    /* R140-Z3h / R140-Z3l / R140-Z3n: Trim the 3rd row when it would
       have fewer than 3 cards. The grid lays out 5 cards per row on
       desktop (R140-Z3n dropped 6 → 5 for wider portraits) and 4
       cards per row on tablet (≤1024px). Mobile uses 2x2 (4 cards).
       Only applies once we have at least 2 full rows so smaller
       phases (Fresh Faces' 10-cap, Rising Stars' 10) aren't
       affected. */
    if(!isMobile){
      var isTablet = false;
      try { isTablet = window.matchMedia('(max-width: 1024px)').matches; } catch(e){}
      var ROW = isTablet ? 4 : 5;
      var fullRows = Math.floor(desktopSize / ROW);
      var remainder = desktopSize % ROW;
      if(fullRows >= 2 && remainder > 0 && remainder < 3){
        desktopSize = fullRows * ROW;
      }
    }
    var snapshotSize = isMobile ? MOBILE_VISIBLE : desktopSize;
    var phaseKey = phase + ':' + ((currentThemedPhase && currentThemedPhase.eyebrow) || 'default') + ':' + (isMobile ? 'm' : 'd');
    /* R140-Z3i: SAFETY NET for the "Spent here" stuck bug. The
       primary auto-refresh trigger lives in pageHeartsRecordSpend
       (fires when pageHearts.spent hits budget). It can miss in rare
       interleavings — e.g. the heart handler short-circuits because
       the char was in pageHeartsCountedSet from a prior page render
       sharing the same key, or schedulePageAutoRefresh's flag stays
       stuck after a soft phase change that didn't bump pageKey. So
       at the top of renderInto we defensively check: if the page
       budget is fully spent AND no refresh is in flight, kick one.
       The schedulePageAutoRefresh helper has its own dedupe so this
       is safe to call on every render — at most one timer at a time. */
    if(pageHearts.budget > 0 && pageHearts.spent >= pageHearts.budget &&
       !pageAutoRefreshScheduled && !autoRefreshing && phase !== -1){
      try { schedulePageAutoRefresh(900); } catch(_){}
    }
    var visible;
    if(renderedSnapshotIds === null || snapshotPhaseKey !== phaseKey){
      /* First render of this phase at this breakpoint — capture the
         initial snapshot of IDs.
         R140-Z3h: Sort by hearts DESC before slicing so the most-loved
         heroes lead the snapshot. Phase 0 (Fresh Faces) is exempt
         because its sort signal is recency, not hearts — newest first
         is the contract there. The themed phases and Hearted view
         both benefit from "most-hearted first." */
      var sortedFull = full;
      if(phase !== 0){
        sortedFull = full.slice().sort(function(a,b){ return (b.hearts||0) - (a.hearts||0); });
      }
      visible = sortedFull.slice(0, snapshotSize);
      renderedSnapshotIds = visible.map(function(c){ return c.id; });
      snapshotPhaseKey = phaseKey;
    } else {
      /* Subsequent renders — keep only the snapshot's IDs that remain
         in the pool. Removed cards leave their slot empty (no
         backfill) so the user sees their progress through the batch.
         R148-Z22f: When a card is hearted, unseenList filters it out
         of `full` via the heartIntent ledger. That means the snapshot
         reconciler can't find it in poolMap and drops the slot —
         which manifests as "I hearted 2 chars and 1 random one
         disappeared" because the .filter(Boolean) collapses the
         indices. Fix: fall back to the unfiltered character roster
         (window.hohGetAllCharacters) for any snapshot id missing
         from the filtered pool. The card stays visible with its
         spent-heart class (applied by hohFreshFacesAfterHeart) —
         exactly the contract that hohFreshFacesAfterHeart's
         "renderedSnapshotIds intentionally NOT filtered" comment
         was trying to preserve, but the upstream filter was
         defeating. */
      var poolMap = {};
      full.forEach(function(c){ if(c && c.id) poolMap[c.id] = c; });
      var fallbackMap = null;
      function getFallback(id){
        if(fallbackMap === null){
          fallbackMap = {};
          try {
            var allChars = (typeof window.hohGetAllCharacters === 'function')
              ? (window.hohGetAllCharacters() || []) : [];
            for(var iA = 0; iA < allChars.length; iA++){
              if(allChars[iA] && allChars[iA].id) fallbackMap[allChars[iA].id] = allChars[iA];
            }
          } catch(_){}
        }
        return fallbackMap[id] || null;
      }
      visible = renderedSnapshotIds
        .map(function(id){ return poolMap[id] || getFallback(id) || null; })
        .filter(Boolean);
      /* Snapshot exhausted — every card in this batch has been
         dismissed or hearted. Auto-refresh into a new phase so the
         user immediately sees a fresh set instead of staring at an
         empty grid. Skip in the user-driven Hidden phase (-1). */
      if(visible.length === 0 && !autoRefreshing && phase !== -1){
        autoRefreshing = true;
        setTimeout(function(){
          autoRefreshing = false;
          window.hohFreshFacesRefresh();
        }, 280);
        /* Brief placeholder while we swap phases. */
        container.style.display = 'none';
        lastRenderedIds = [];
        return;
      }
    }
    var copy = getEyebrowForPhase(phase);
    /* R144-A: Refresh icon stays available on phase -1 so users can
       roll out of the hearted view into events / themed phases. */
    var refreshDisabled = false;

    /* R140-Z3n: Apply per-roll theme tint. When the current themed
       phase carries a `color` (and optionally `borderColor`), set
       CSS variables on the container + add the `.hoh-fresh-themed-
       tinted` class so the section paints with a subtle radial wash
       + colored border + colored eyebrow. Phase 0 (Fresh Faces) and
       phase -1 (Hearted view) don't carry colors so they paint
       clean. The class is toggled (not just set) so navigating from
       a tinted theme back to Fresh Faces strips the tint properly. */
    try {
      var themeForTint = currentThemedPhase || null;
      if(phase !== 0 && phase !== -1 && themeForTint && themeForTint.color){
        container.classList.add('hoh-fresh-themed-tinted');
        container.style.setProperty('--theme-color', themeForTint.color);
        container.style.setProperty('--theme-border-color', themeForTint.borderColor || themeForTint.color);
      } else {
        container.classList.remove('hoh-fresh-themed-tinted');
        container.style.removeProperty('--theme-color');
        container.style.removeProperty('--theme-border-color');
      }
    } catch(_){}

    /* Skeleton built once. Header eyebrow + sub get re-rendered every
       pass since they change with phase. Track + wander-wrap persist
       so the FLIP helper animates card transitions cleanly.

       View More / Show Less were removed by request — the rail is now
       a single page that you "wander further" through via refresh. The
       wander CTA below shows on BOTH desktop and mobile. The header
       refresh icon stays as a quick repeat on desktop. */
    /* Skeleton check: we need BOTH the track AND the eyebrow ID
       (which is unique to the normal phase skeleton). If a special
       event just left its own header behind, querying #hoh-fresh-
       eyebrow returns null even though .hoh-fresh-track exists, and
       the renderer would fail to update text. So we rebuild whenever
       either is missing. */
    var track = container.querySelector('.hoh-fresh-track');
    var eyHeader = container.querySelector('#hoh-fresh-eyebrow');
    if(!track || !eyHeader){
      container.innerHTML =
        '<div class="hoh-fresh-header">' +
          '<div class="hoh-fresh-header-text">' +
            '<span class="hoh-fresh-eyebrow" id="hoh-fresh-eyebrow"></span>' +
            '<span class="hoh-fresh-sub" id="hoh-fresh-sub"></span>' +
          '</div>' +
          '<div class="hoh-fresh-header-controls">' +
            /* Per-page heart counter — shows how many hearts the user
               can spend on this batch (1 per 4 chars, capped by their
               wallet). Painted by paintPageHearts.
               R140-U: Refresh button extracted from this wrapper so it
               can sit AT the right end of the auto-bar (matching the
               eventHeaderHtml restructure). The pill is alone here. */
            '<span class="hoh-fresh-page-hearts" id="hoh-fresh-page-hearts" aria-live="polite"></span>' +
          '</div>' +
          /* Auto-refresh countdown bar lives at the bottom of the
             header, flowing into the refresh icon. JS drives width. */
          '<span class="hoh-fresh-auto-bar" id="hoh-fresh-auto-bar" aria-hidden="true"><span class="hoh-fresh-auto-bar-fill" id="hoh-fresh-auto-bar-fill"></span></span>' +
          /* R140-U: Refresh icon as sibling of the bar. CSS positions
             it to overlap the bar's right edge so the gold thread
             visibly feeds into the icon. */
          '<button type="button" class="hoh-fresh-refresh" id="hoh-fresh-refresh" onclick="hohFreshFacesRefresh()" aria-label="Refresh with new heroes" title="Refresh">' +
            '<svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 12a9 9 0 1 1-3.5-7.1"/><polyline points="21 4 21 10 15 10"/></svg>' +
          '</button>' +
        '</div>' +
        '<div class="hoh-fresh-track-wrap"><div class="hoh-fresh-track"></div></div>' +
        /* Wander quest progress bar — moved from the header to the
           bottom of the panel. Bigger, breathable, three click-to-claim
           ticket milestones at 10 / 30 / 50 hearts, and a free 20-heart
           pip sitting on the very start of the rail. The 50 milestone
           also pays today's Battlegrounds entry when claimed.
           R140-Z: Use _wanderBarHtml so the fill width + milestone state
           are inlined at render time. Without this the bar paints empty
           on first render and then animates to the correct width when
           the first heart bumps the counter — a visible "reset" flash
           the user explicitly flagged. */
        _wanderBarHtml({ inStream: false });
        /* The Battlegrounds entry renders INSIDE _wanderBarHtml once the daily
           is fully claimed, and the #hoh-contract host is created outside this
           container by hohEnterBattlegrounds, so panel rotation can never wipe
           a running battle. */
      track = container.querySelector('.hoh-fresh-track');
    }
    /* R113-B/D/E: Portrait Gallery (themed phase, BLUE theme) — inject
       (or remove) the CTA BELOW the grid (was above). The hoh-fresh-
       ff-showcase class drives the showcase layout; the new
       hoh-fresh-ff-showcase-blue class drives the blue palette
       (distinct from Captured by Jan's pink). CTA click grants +5
       hearts silently once per day via hohFreshFacesFfCtaClick. */
    var ffPhase = currentThemedPhase;
    var isFfShowcase = !!(ffPhase && ffPhase.ffShowcase);
    if(isFfShowcase){
      container.classList.add('hoh-fresh-ff-showcase');
      container.classList.add('hoh-fresh-ff-showcase-blue');
    } else {
      container.classList.remove('hoh-fresh-ff-showcase');
      container.classList.remove('hoh-fresh-ff-showcase-blue');
    }
    var existingFfCta = container.querySelector('.hoh-fresh-ff-cta-row');
    if(isFfShowcase && !existingFfCta){
      var ffCtaHtml = '<div class="hoh-fresh-ff-cta-row hoh-fresh-ff-cta-row-below">' +
        '<a class="hoh-fresh-ff-cta hoh-fresh-ff-cta-blue" href="https://framedfantasy.com/products/fantasy-character-commission" target="_blank" rel="noopener" onclick="if(window.hohFreshFacesFfCtaClick)hohFreshFacesFfCtaClick(\'portraitGallery\');">' +
          '<span class="hoh-fresh-ff-cta-flavor">Want to see your hero come to life?</span>' +
          '<span class="hoh-fresh-ff-cta-link">Join the waitlist</span>' +
        '</a>' +
      '</div>';
      var trackWrap = container.querySelector('.hoh-fresh-track-wrap');
      if(trackWrap && trackWrap.insertAdjacentHTML){
        trackWrap.insertAdjacentHTML('afterend', ffCtaHtml);
      }
    } else if(!isFfShowcase && existingFfCta){
      existingFfCta.parentNode.removeChild(existingFfCta);
    }
    /* Update header copy + refresh button visibility. */
    var eyEl = container.querySelector('#hoh-fresh-eyebrow');
    var subEl = container.querySelector('#hoh-fresh-sub');
    var refBtn = container.querySelector('#hoh-fresh-refresh');
    if(eyEl){
      eyEl.textContent = copy.eyebrow;
      /* Themed phases can carry a `color` field. Reset between
         renders so a non-colored phase falls back to the default
         gold from the stylesheet. */
      eyEl.style.color = (copy && copy.color) ? copy.color : '';
    }
    if(subEl) subEl.textContent = copy.sub;
    /* R136-themed-rotation: Apply the theme's borderColor (if any) to
       the rail container as a CSS custom property + class flag. The
       CSS rule .hoh-fresh-themed-border draws a soft glowing border
       in --theme-border-color so themes with this field announce
       themselves visually. Cleared between renders so a phase without
       a borderColor doesn't inherit the previous theme's tint. */
    try {
      if(copy && copy.borderColor){
        container.style.setProperty('--theme-border-color', copy.borderColor);
        container.classList.add('hoh-fresh-themed-border');
      } else {
        container.style.removeProperty('--theme-border-color');
        container.classList.remove('hoh-fresh-themed-border');
      }
    } catch(_){}
    if(refBtn) refBtn.style.visibility = refreshDisabled ? 'hidden' : '';
    /* Paint the quest progress bar against the latest counter. */
    paintQuestProgress();

    if(typeof window.hohSmoothGridUpdate === 'function'){
      window.hohSmoothGridUpdate(track, visible, {
        key: function(c){ return c.id; },
        build: function(c){ return cardHtml(c); },
        /* R140-P: Clear transient state classes on cards whose data-id
           matches across renders. FLIP preserves the DOM node (which is
           good for animation continuity), but the spent-heart /
           spent-dismiss / heart-pulse / reject classes belong to the
           PREVIOUS page's interaction state — leaving them on a node
           that's now part of a new batch left the heart button locked
           and the user couldn't re-heart the same character when it
           appeared again. The hearted (mineCount > 0) class is
           re-established by cardHtml's heartedCls below; we don't
           clear that here. */
        prepareReused: function(node){
          if(!node || !node.classList) return;
          node.classList.remove('hoh-fresh-card-spent-heart');
          node.classList.remove('hoh-fresh-card-spent-dismiss');
          node.classList.remove('hoh-fresh-card-heart-pulse');
          node.classList.remove('hoh-fresh-card-reject');
        }
      });
    } else {
      track.innerHTML = visible.map(cardHtml).join('');
    }
    /* R217: repaint the page-hearts pill on every normal render so it never sticks
       empty + hidden (e.g. returning from an event that rebuilt the header without
       repainting it). Uses the same phaseKey as the snapshot, so max resets only on
       a real phase change and stays stable within a phase.
       R265: compute from visible.length (the snapshot array), NOT
       currentPageVisibleCount(). On a phase change this call runs before the DOM
       settles, so the live card count could still be the PREVIOUS page's (up to
       25), which locked the budget at ceil(25/5)=5 for a 3-row themed roll. The
       snapshot length is always the real capped count (<=15 here), so the budget
       lands at 1 heart per row. */
    try { recomputePageHearts(visible.length, phaseKey); } catch(_){}
    /* R148-Z28 smoothing: Apply the swap class ONLY when the phase key
       changed since last render — i.e., transitioning from one themed
       roll to another. Within-phase re-renders (heart-click chip
       updates, in-place pruning) don't trigger the fade, which would
       otherwise restart the animation on every click and feel jagged.
       The phase key encodes phase index + theme eyebrow + breakpoint
       so a Type 1 → Type 1 transition (different themes) still fades. */
    if(track && track.classList){
      if(_lastTrackPhaseKey !== phaseKey){
        _lastTrackPhaseKey = phaseKey;
        track.classList.remove('hoh-fresh-track-swap');
        /* Force reflow so the animation restarts on the same element. */
        void track.offsetWidth;
        track.classList.add('hoh-fresh-track-swap');
        try {
          setTimeout(function(){
            try { track.classList.remove('hoh-fresh-track-swap'); } catch(_){}
          }, 260);
        } catch(_){}
      }
    }
    lastRenderedIds = visible.map(function(c){ return c.id; });
    /* Recompute the per-page heart allowance for this batch. The
       page key encodes phase + theme + breakpoint so a phase change
       resets the spent counter, while re-renders within the same
       phase preserve it (the user keeps their progress). */
    recomputePageHearts(visible.length, phaseKey);
  }

  /* Public render. Idempotent. Resets the page if the host calls it
     after a major reload (e.g., after submission, after nightly_heart_grace).
     The reset keeps the rail compact for return visits.

     If a special event is active, the special renderer takes over —
     normal grid rendering is bypassed entirely. When transitioning
     back to a normal phase we strip the prior event's data-event
     attribute and theme class so the section returns to the default
     gold accent. */
  /* R391 (Jan): THE CLOSED HALL. Once the day is fully claimed, Explore does
     not sit open as a dead surface — it closes with a seal, a breathing glow,
     drifting embers and the exact countdown to the next opening. One door
     onward: the Battlegrounds, where the entry it just paid wants using. */
  function _exploreClosedHtml(){
    var cssOnce = '';
    if(!document.getElementById('hoh-exdone-css')){
      cssOnce = '<style id="hoh-exdone-css">' +
        '.hoh-exdone{position:relative;overflow:hidden;text-align:center;padding:56px 22px 52px;border-radius:18px;background:radial-gradient(120% 130% at 50% 0%,rgba(44,34,60,.55),rgba(10,8,16,.92)),#0b0911;border:1px solid #2c2440;}' +
        '.hoh-exdone-glow{position:absolute;left:50%;top:-40px;width:340px;height:200px;transform:translateX(-50%);background:radial-gradient(closest-side,rgba(232,198,116,.16),transparent 70%);animation:hohExdoneBreathe 4.2s ease-in-out infinite;pointer-events:none;}' +
        '@keyframes hohExdoneBreathe{0%,100%{opacity:.55}50%{opacity:1}}' +
        '.hoh-exdone-embers{position:absolute;inset:0;pointer-events:none;}' +
        '.hoh-exdone-embers i{position:absolute;bottom:-4px;width:3px;height:3px;border-radius:50%;background:#e8c674;opacity:0;animation:hohExdoneEmber 3.4s ease-in infinite;}' +
        '@keyframes hohExdoneEmber{0%{opacity:0;transform:translateY(0)}14%{opacity:.7}100%{opacity:0;transform:translateY(-210px)}}' +
        '.hoh-exdone-seal{width:86px;height:86px;margin:0 auto 20px;border-radius:50%;border:2px solid rgba(232,198,116,.8);display:flex;align-items:center;justify-content:center;color:#e8c674;background:radial-gradient(circle at 50% 40%,rgba(60,44,20,.5),rgba(14,10,20,.9));box-shadow:0 0 0 0 rgba(232,198,116,.35);animation:hohExdoneSeal 2.4s ease-out infinite;}' +
        '@keyframes hohExdoneSeal{0%{box-shadow:0 0 0 0 rgba(232,198,116,.32)}70%{box-shadow:0 0 0 20px rgba(232,198,116,0)}100%{box-shadow:0 0 0 0 rgba(232,198,116,0)}}' +
        '.hoh-exdone-eyebrow{font-family:"Cinzel",serif;font-size:11px;font-weight:700;letter-spacing:.3em;text-transform:uppercase;color:#c9a84c;margin-bottom:10px;}' +
        '.hoh-exdone-line{font-family:"Cormorant Garamond",Georgia,serif;font-size:17px;font-style:italic;color:#cdc4dd;max-width:420px;margin:0 auto 16px;line-height:1.55;}' +
        '.hoh-exdone-timer{font-family:"Cinzel",serif;font-size:10px;font-weight:700;letter-spacing:.18em;text-transform:uppercase;color:#a9a0bb;background:rgba(28,23,40,.85);border:1px solid #362c50;border-radius:999px;padding:8px 16px;display:inline-block;margin-bottom:20px;}' +
        '.hoh-exdone-bg{display:block;margin:0 auto;cursor:pointer;font-family:"Cinzel",serif;font-size:11px;font-weight:700;letter-spacing:.14em;text-transform:uppercase;color:#1a1108;background:linear-gradient(120deg,#f2d98e,#c8a45e);border:none;border-radius:11px;padding:13px 26px;min-height:44px;box-shadow:0 6px 20px -8px rgba(242,217,142,.6);transition:transform .15s ease;}' +
        '.hoh-exdone-bg:hover{transform:translateY(-1px);}' +
        '@media (prefers-reduced-motion: reduce){.hoh-exdone-glow,.hoh-exdone-seal{animation:none}.hoh-exdone-embers i{display:none}}' +
      '</style>';
    }
    return cssOnce +
      '<div class="hoh-exdone" data-state="closed">' +
        '<span class="hoh-exdone-glow" aria-hidden="true"></span>' +
        '<span class="hoh-exdone-embers" aria-hidden="true">' +
          '<i style="left:14%;animation-delay:-.6s"></i><i style="left:33%;animation-delay:-2.1s"></i>' +
          '<i style="left:57%;animation-delay:-1.1s"></i><i style="left:78%;animation-delay:-2.8s"></i>' +
          '<i style="left:90%;animation-delay:-1.6s"></i>' +
        '</span>' +
        '<div class="hoh-exdone-seal" aria-hidden="true">' +
          '<svg viewBox="0 0 24 24" width="34" height="34" fill="none" stroke="currentColor" stroke-width="2.1" stroke-linecap="round" stroke-linejoin="round"><path d="M20 6 9 17l-5-5"/></svg>' +
        '</div>' +
        '<div class="hoh-exdone-eyebrow">Explore the Hall</div>' +
        '<div class="hoh-exdone-line">You walked the whole Hall today. Every reward is banked, and the hall rests until dawn.</div>' +
        '<div class="hoh-exdone-timer" id="hoh-exdone-timer">' + _wanderResetCountdownText().replace('Resets', 'Opens again') + '</div>' +
        '<button type="button" class="hoh-exdone-bg" id="hoh-exdone-bgbtn">To the Battlegrounds</button>' +
      '</div>';
  }
  var _exdoneTimer = null;
  function _wireExploreClosed(container){
    var b = container.querySelector('#hoh-exdone-bgbtn');
    if(b && !b.__hohExdoneWired){
      b.__hohExdoneWired = true;
      b.addEventListener('click', function(){
        try { if(typeof window.hohYourHallSetEventsTab === 'function') window.hohYourHallSetEventsTab('bg'); } catch(_){}
      });
    }
    if(_exdoneTimer) { clearInterval(_exdoneTimer); _exdoneTimer = null; }
    _exdoneTimer = setInterval(function(){
      var t = document.getElementById('hoh-exdone-timer');
      if(!t){ clearInterval(_exdoneTimer); _exdoneTimer = null; return; }
      if(document.hidden) return;   /* PERF v39 */
      t.textContent = _wanderResetCountdownText().replace('Resets', 'Opens again');
    }, 30000);
    /* Network-safe reward recovery belongs behind this stable terminal state,
       never in front of it. The banker is idempotent and stays completely
       visual-free. */
    try {
      if(typeof window.hohShopBankExploreGiftPack === 'function'){
        window.hohShopBankExploreGiftPack();
      }
    } catch(_){}
  }

  /* ============================================================
     R436 (Jan) · THE EXPLORE THE HALL INTRO
     ============================================================
     "give explore the hall an intro screen, in clean simple intuitive non
     fantasy language, explain that explore the hall helps keep quality
     heroes in the hall, make it appeal to human psychology that not only
     does it keep the hall full of quality heroes, you get rewarded and its
     the easiest way to earn hearts and tickets to win the raffle, make it
     about them, and then when they hit start then they see the explore the
     hall and the bar at the bottom."

     COPY ORDER IS THE WHOLE DESIGN HERE. Reward first, because that is what
     decides whether they read line two. Progress second, because six
     rewards spread over fifty is a ladder and a ladder is easier to start
     than a total. The prize third, because a raffle ticket only means
     something once you know it buys a shot at a free portrait. The reason
     the Hall needs them LAST, once they are already sold — asking somebody
     to do you a favour lands better after you have paid them, and it turns
     a chore into a job title. Every line is second person. No fantasy
     register anywhere: it says hearts, tickets, draw, free portrait.
     ============================================================ */
  /* R439 (Jan): "the explore the hall before you press start is still auto
     going in the background and it sometimes overtakes the start menu."
     ROOT CAUSE. R436's intro gate lives inside hohRenderFreshFaces, but that
     is not the only door into this panel. The refresh handler picks an event
     and calls renderSpecial(container) DIRECTLY (see 'currentSpecial = event'
     below), and the auto-timer fires that handler on a schedule whether or
     not anyone is looking. So the timer kept running under the intro and the
     first event it picked painted straight over it.
     Three locks now: the timer refuses to tick, renderSpecial refuses to
     paint, and renderInto refuses to paint, all off the one fact that the
     intro is on screen. Reading the DOM rather than a flag is deliberate —
     it cannot drift out of sync with what the player is actually looking at. */
  function _xintroUp(){
    try {
      var c = _ffContainer();
      return !!(c && c.querySelector('.hoh-xintro'));
    } catch(_){ return false; }
  }
  var EXPLORE_INTRO_KEY = 'hoh-explore-intro-v1';
  var _xintroForce = false;
  /* R440: set the moment Start is pressed, independent of whether the
     localStorage write lands. See hohExploreIntroStart. */
  var _xintroDone = false;
  /* Never hand a tutorial to somebody already doing the thing. A claimed
     milestone is proof they know exactly what this is, so they are marked
     seen without ever being shown it. If storage is unavailable we return
     true: the failure mode of a missing intro is far smaller than the
     failure mode of an intro that reappears on every single render. */
  function _exploreIntroSeen(){
    if(_xintroDone) return true;   /* R440: survives a failed setItem */
    try {
      if(localStorage.getItem(EXPLORE_INTRO_KEY) === '1') return true;
      for(var i = 0; i < localStorage.length; i++){
        var k = localStorage.key(i);
        if(k && k.indexOf('hoh-ff-claim-') === 0){
          try { localStorage.setItem(EXPLORE_INTRO_KEY, '1'); } catch(_){}
          return true;
        }
      }
      return false;
    } catch(_){ return true; }
  }
  function _xIcon(kind){
    if(kind === 'ticket') return '<span class="hoh-tk-icon" aria-hidden="true"></span>';
    if(kind === 'heart') return '<svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">' +
      '<path d="M12 20.7 3.9 12.9A5.2 5.2 0 0 1 12 6.4a5.2 5.2 0 0 1 8.1 6.5z"/></svg>';
    return '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.8" ' +
      'stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
      '<path d="M12 3.5 14.4 9l6 .5-4.6 3.9 1.4 5.9L12 16.2 6.8 19.3l1.4-5.9L3.6 9.5l6-.5z"/></svg>';
  }
  function _exploreIntroHtml(){
    return '<div class="hoh-xintro">' +
      '<h2 class="hoh-xintro-title">Explore the Hall</h2>' +
      '<p class="hoh-xintro-lead">The easiest way to earn hearts and raffle tickets.</p>' +
      '<ul class="hoh-xintro-list">' +
        '<li><span class="hoh-xintro-i hoh-xintro-i-heart">' + _xIcon('heart') + '</span>' +
          '<span class="hoh-xintro-t"><b>Give 2, get 3 back.</b> Every hero you heart pays you more than it cost you.</span></li>' +
        '<li><span class="hoh-xintro-i hoh-xintro-i-tk">' + _xIcon('ticket') + '</span>' +
          '<span class="hoh-xintro-t"><b>Six rewards on the way to 50.</b> Hearts and raffle tickets, handed out as you go. The bar at the bottom tracks it.</span></li>' +
        '<li><span class="hoh-xintro-i hoh-xintro-i-star">' + _xIcon('star') + '</span>' +
          '<span class="hoh-xintro-t"><b>Tickets win portraits.</b> Every one is an entry in the monthly draw for a free commission.</span></li>' +
      '</ul>' +
      '<p class="hoh-xintro-why"><b>Your hearts decide what everyone else finds.</b> ' +
        'Heroes the Hall loves rise to the top of it, so the next person who walks in meets the best work first ' +
        'instead of whatever happened to be posted last. A few minutes from you makes the Hall worth ' +
        'someone else\'s time.</p>' +
      '<button type="button" class="hoh-xintro-go" onclick="hohExploreIntroStart()">Start</button>' +
      '<p class="hoh-xintro-foot">A few minutes. Stop whenever you like, your progress is saved.</p>' +
    '</div>';
  }
  window.hohExploreIntroStart = function(){
    /* R440 CRITICAL, and it is the collision of two of my own changes.
       R436 wrote this to drop a CLASS and re-render, which was fine when the
       re-render always repainted. R439 then taught renderSpecial, renderInto
       and the auto-timer to refuse while .hoh-xintro is on screen — and this
       function never removed that NODE. So Start marked the intro seen, the
       gate stopped re-entering, and every renderer below it bailed on a node
       nobody was clearing. The panel froze on the intro with no grid, no bar
       and no refresh control, recoverable only by reloading.
       It survived testing because a player holding a Battlegrounds entry
       takes the takeover branch, which writes innerHTML and wipes the intro
       as a side effect. Remove the node FIRST, then render. */
    _xintroForce = false;
    /* R440: an in-memory latch as well as the key. Safari private mode throws
       on setItem while getItem still works, so the write above can silently
       fail and _exploreIntroSeen would keep returning false — the intro would
       re-enter on the very next render and freeze again, this time through a
       reload too. The latch cannot fail. */
    _xintroDone = true;
    try { localStorage.setItem(EXPLORE_INTRO_KEY, '1'); } catch(_){}
    try {
      var c = _ffContainer();
      if(c){
        c.classList.remove('hoh-fresh-xintro-on');
        var _n = c.querySelector('.hoh-xintro');
        if(_n && _n.parentNode) _n.parentNode.removeChild(_n);
      }
    } catch(_){}
    try { window.hohRenderFreshFaces(true); } catch(_){}
  };
  /* Reopened from the quest bar's own title. Uses a flag rather than
     clearing the key, because _exploreIntroSeen would re-mark an
     experienced player as seen and the screen would never appear. */
  window.hohExploreIntroOpen = function(){
    _xintroForce = true;
    _xintroDone = false;
    try { window.hohRenderFreshFaces(true); } catch(_){}
  };
  window.hohRenderFreshFaces = function(reset){
    /* R148-Z41: First-timer gate. Explore the Hall is hidden until
       the visitor signs in (by submitting a hero). Bail early so we
       don't do any of the (expensive) pool building, image fetching,
       or DOM mutation while anon. The CSS in the section template
       hides the section via display:none too — this is the JS guard
       to avoid wasted work. */
    if(typeof window.hohIsHallSignedIn === 'function' && !window.hohIsHallSignedIn()){
      return;
    }
    if(reset) visibleCount = PAGE_SIZE;
    var container = _ffContainer();
    if(!container) return;
    /* Release yesterday's terminal lock after the UTC-keyed progress resets. */
    if(getFfHeartsToday() < WANDER_QUEST_TARGET){
      _ffQuestClosing = false;
      _ffQuestCloseScheduled = false;
      container.classList.remove('hoh-fresh-quest-closing');
      container.removeAttribute('aria-busy');
    }
    /* ===== R436 (Jan) · THE EXPLORE THE HALL INTRO =====
       Before this, a signed-in player's first sight of Explore the Hall was
       a grid of strangers and a bar reading 1/50 with six unlabelled
       circles. Nothing on screen said what the bar was, what filling it
       paid, or why hearting a stranger was worth their time — so the
       highest-value daily loop in the whole site opened with zero stated
       reason to start it. The intro states the reason once, in their words,
       and gets out of the way.
       It sits AFTER the Battlegrounds guard below in spirit but has to be
       checked here, so it explicitly stands down for a live fight: a mounted
       #hoh-contract host or any data-bg state means something is already
       happening in this panel and a tutorial must never take it. */
    try {
      var _bgBusy = !!document.getElementById('hoh-contract') || !!container.getAttribute('data-bg');
      if(!_bgBusy && (_xintroForce || !_exploreIntroSeen())){
        container.removeAttribute('data-event');
        container.removeAttribute('data-tier');
        container.classList.remove('hoh-fresh-themed');
        container.classList.remove('hoh-fresh-themed-border');
        container.classList.remove('hoh-fresh-themed-tinted');
        container.classList.remove('hoh-fresh-edge-off');
        container.style.removeProperty('--theme-border-color');
        container.style.removeProperty('--theme-color');
        container.classList.add('hoh-fresh-xintro-on');
        container.style.display = '';
        /* The auto-refresh timer keeps calling this function while the intro
           is up. Rewriting innerHTML on every tick would blow away focus and
           restart the button's transition mid-hover, so the paint happens
           once and later passes just confirm it is still there. */
        if(!container.querySelector('.hoh-xintro')) container.innerHTML = _exploreIntroHtml();
        return;
      }
      container.classList.remove('hoh-fresh-xintro-on');
    } catch(_){}
    /* ===== THE BATTLEGROUNDS TAKEOVER =====
       Once the daily is fully claimed, Explore the Hall is REPLACED, not
       decorated. The whole panel becomes the Battlegrounds: no wandering
       grid, no special events, no bar. We bail before both renderSpecial and
       renderInto. States: open (enter), spent (played today), live (a fight is
       mounted in the outside #hoh-contract host, so show a quiet line, no
       second CTA). Idempotent via data-bg so a mounted game and the button
       survive auto-refresh ticks. When the day resets, eligibility drops and
       we fall through to the normal render. */
    try {
      /* A mounted #hoh-contract host = a fight is live, no matter what
         eligibility says (once the day is stamped played, _contractEligible
         returns null, so the host is the ONLY live signal). This ordering is
         what lets a finished run drop cleanly back to Explore: the exit removes
         the host, and the next render finds null and falls through below. */
      var _host = document.getElementById('hoh-contract');
      var _live = !!(_host && document.body.contains(_host));
      var _bgSt = _live ? 'live' : _contractEligible();
      if(_bgSt === 'live'){
        try { if(window.hohPaintBgDoor) window.hohPaintBgDoor(); } catch(_){}
        if(_wanderResetTimer){ clearInterval(_wanderResetTimer); _wanderResetTimer = null; }
        /* R381: the fight lives on the Battlegrounds sub tab now, so hiding
           this panel would leave the Explore tab EMPTY mid battle. Show a
           quiet notice with the way over instead. (This renderer owns the
           panel's content; the node itself is never rebuilt.) */
        container.style.display = '';
        if(container.getAttribute('data-bg') !== 'live'){
          container.setAttribute('data-bg', 'live');
          container.innerHTML =
            '<div class="hoh-contract-entry hoh-bg-entry" data-state="live">' +
              '<div class="hoh-contract-entry-card hoh-contract-entry-spent">' +
                '<span class="hoh-contract-entry-eyebrow">The battlegrounds are open</span>' +
                '<span class="hoh-contract-entry-line">Your battle is running in the Battlegrounds tab.</span>' +
                '<button type="button" class="hoh-bg-live-jump" onclick="if(window.hohYourHallSetEventsTab)window.hohYourHallSetEventsTab(\'bg\')">Go to the battle</button>' +
              '</div></div>';
        }
        try { _bgHoldsSurface(); } catch(_){}   /* R300: sync the body flag now */
        return;
      }
      /* R391 (Jan): THE DAY IS DONE — the hall closes. Every milestone
         claimed means there is nothing left to do here until the reset, so
         the whole pane becomes the sealed state with the reopen countdown.
         A live battle above still wins; anonymous users can never claim the
         ticket pips, so they never see this and keep wandering. */
      var _allDone = false;
      try { _allDone = WANDER_MILESTONES.every(isMilestoneClaimed); } catch(_){}
      if(_allDone){
        if(_wanderResetTimer){ clearInterval(_wanderResetTimer); _wanderResetTimer = null; }
        _ffQuestClosing = true;
        container.classList.remove('hoh-fresh-quest-closing');
        container.removeAttribute('aria-busy');
        container.style.display = '';
        /* `data-bg` is only a paint hint, never proof that its DOM survived.
           Another renderer can empty/move this singleton while the marker
           remains "done". Rebuild when the actual cooldown card is missing;
           this is the self-healing half of the blank-state fix. */
        if(container.getAttribute('data-bg') !== 'done' || !container.querySelector('.hoh-exdone')){
          container.setAttribute('data-bg', 'done');
          container.removeAttribute('data-event');
          container.classList.remove('hoh-fresh-themed', 'hoh-fresh-fading-out');
          container.innerHTML = _exploreClosedHtml();
        }
        _wireExploreClosed(container);
        try { _bgHoldsSurface(); } catch(_){}
        try { if(window.hohPaintBgDoor) window.hohPaintBgDoor(); } catch(_){}
        return;
      }
      if(_bgSt === 'replay'){
        if(_wanderResetTimer){ clearInterval(_wanderResetTimer); _wanderResetTimer = null; }
        container.style.display = '';
        if(container.getAttribute('data-bg') !== 'replay'){
          container.setAttribute('data-bg', 'replay');
          container.removeAttribute('data-event');
          container.classList.remove('hoh-fresh-themed', 'hoh-fresh-fading-out');
          container.innerHTML = _battlegroundsEntryHtml('replay');
        }
        try { _bgHoldsSurface(); } catch(_){}
        return;
      }
      if(_bgSt === 'open'){
        if(_wanderResetTimer){ clearInterval(_wanderResetTimer); _wanderResetTimer = null; }
        container.style.display = '';
        if(container.getAttribute('data-bg') !== 'open'){
          container.setAttribute('data-bg', 'open');
          container.removeAttribute('data-event');
          container.classList.remove('hoh-fresh-themed', 'hoh-fresh-fading-out');
          container.innerHTML = _battlegroundsEntryHtml('open');
        }
        try { _bgHoldsSurface(); } catch(_){}   /* R300: sync the body flag now */
        return;
      }
      /* _bgSt is null: not eligible, or the battle is done for the day. Clear
         any stale battlegrounds card and fall through to the normal Explore
         render (grid + events) below. */
      if(container.getAttribute('data-bg')){
        container.removeAttribute('data-bg');
        container.style.display = '';
        container.innerHTML = '';
      }
      try { _bgHoldsSurface(); } catch(_){}   /* R300: the Hall gets its header back */
      /* R376: with no fight holding the surface, make sure the door under
         Explore is awake. The painter skips identical repaints, so this is
         free on every ordinary rotation. */
      try { if(window.hohPaintBgDoor) window.hohPaintBgDoor(); } catch(_){}
    } catch(_){}
    /* R140-Z3d: Hard guarantee that the section is visible whenever the
       renderer runs. The liquid placeholder is visible by default but
       some code paths (auto-refresh transition) explicitly hide it,
       and this guard restores it on every render entry. */
    if(container.style && container.style.display === 'none'){
      container.style.display = '';
    }
    /* R148-Z22e: DEFENSIVE PURGE of the fade-out class on every fresh
       render entry. The cross-fade flow in schedulePageAutoRefresh
       relies on a double-rAF removal to clear .hoh-fresh-fading-out
       after the new content paints — but that removal can silently
       fail when:
         - The browser throttles rAF (tab backgrounded mid-flight)
         - _ffContainer's cache invalidates between add and remove
         - hohFreshFacesRefresh throws inside its pipeline
       When the class stays stuck, CSS rule .hoh-fresh-fading-out
       .hoh-fresh-grid { opacity:0; pointer-events:none } makes
       EVERY subsequent render paint as an empty black grid — the
       cards exist in the DOM (clickable through CSS coverage gaps
       on non-standard child selectors) but invisible. That's the
       "veterans roll all invisible" bug.
       Stripping at the START of every render guarantees visibility.
       The trade-off: the legitimate cross-fade animation between
       page-exhaustion and next-batch is sacrificed (content swaps
       instantly instead of fading in). The fade-OUT still plays —
       only the fade-in is lost. Per-card animations (legendary-trio,
       epic, etc.) still run their own reveal so the user still sees
       motion on the new batch. Robustness wins over polish. */
    try {
      if(container.classList.contains('hoh-fresh-fading-out')){
        container.classList.remove('hoh-fresh-fading-out');
      }
    } catch(_){}
    if(currentSpecial){
      renderSpecial(container);
      /* Special events nuke the container's innerHTML, which means
         the auto-bar element from the previous render is gone and a
         brand-new one just got painted. Restart the timer so the new
         bar element gets the visible class + width animation. Without
         this, events like Crossroads (where the user's only manual
         input is choosing a path) sit with a static, invisible bar
         and the auto-refresh fallback never fires its visual. The
         setTimeout 0 lets the freshly-injected DOM commit before
         startAutoTimer queries for the bar element. */
      setTimeout(function(){
        if(typeof startAutoTimer === 'function') startAutoTimer();
      }, 0);
      return;
    }
    /* Clear the event theme before painting the normal phase so the
       gold accent isn't masked by a stale purple/red/etc. */
    container.removeAttribute('data-event');
    container.classList.remove('hoh-fresh-themed');
    container.classList.remove('hoh-fresh-edge-off');   /* R436 */
    renderInto(container, freshList());
  };

  /* View More / Show Less were removed by request — the rail is now
     a single page that you wander through via refresh. The functions
     are kept as no-op shims so any cached HTML / external caller
     calling them mid-deploy still works without throwing. */
  window.hohFreshFacesShowMore = function(){ /* removed: see Wander Further */ };
  window.hohFreshFacesShowLess = function(){ /* removed: see Wander Further */ };

  /* Forces the next render to animate the given character id as a
     newly-arrived card, even if the diff would otherwise consider it
     already on screen. Used after a fresh submission so the user sees
     their new hero animate into the rail when they return to the Hall
     after closing the success modal and detail view. */
  window.hohFreshFacesHighlight = function(id){
    if(!id) return;
    lastRenderedIds = lastRenderedIds.filter(function(x){ return x !== id; });
    window.hohRenderFreshFaces();
  };

  /* Quick-heart button on Fresh Face cards. Fires the gallery's
     hohHeart with a synthetic button so the existing flow (animation,
     attribution, cap check) runs as if the user clicked the heart on
     a regular gallery card. Click bubbles are stopped so the card's
     onclick (open detail) does not also fire. */
  /* ==================== R252: card-is-the-heart wiring ==================== */
  /* Injected at load, deploy-safe over any live css vintage: the burst
     shards, tap-highlight cleanup, and the OPEN door recolored gold
     (it inherits the heart circle's position from .hoh-fresh-card-heart). */
  (function(){
    if(document.getElementById('hoh-ffb-css')) return;
    var st = document.createElement('style'); st.id = 'hoh-ffb-css';
    st.textContent =
      '.hoh-ffburst{position:fixed;z-index:100080;width:6px;height:6px;border-radius:2px;pointer-events:none;'+
        'transform:rotate(45deg);opacity:.95;animation:hohFfBurstA .75s cubic-bezier(.15,.65,.3,1) forwards}'+
      '@keyframes hohFfBurstA{to{opacity:0;transform:translate(var(--bx,0),var(--by,-30px)) rotate(225deg) scale(.3)}}'+
      '.hoh-fresh-card{touch-action:manipulation;-webkit-tap-highlight-color:transparent}'+
      '.hoh-fresh-card-open{color:#e8c97a !important;display:inline-flex;align-items:center;justify-content:center}'+
      '.hoh-fresh-card-open svg{display:block}'+
      /* R253: the spent/hearted lock states were built when the corner
         was a HEART — they deadened the whole card and hid the button.
         The corner is the OPEN DOOR now: hearted heroes must still
         open. Re-enable card taps (the tap router opens locked cards)
         and keep the door visible + clickable in every locked state.
         Specificity matches the live rules (incl. hover/focus-within)
         so these later-injected declarations win. */
      '.hoh-fresh-card.hoh-fresh-card-spent-heart,.hoh-fresh-card.hoh-fresh-card-spent-dismiss{pointer-events:auto !important}'+
      '.hoh-fresh-card.hoh-fresh-card-spent-heart .hoh-fresh-card-open,'+
      '.hoh-fresh-card.hoh-fresh-card-spent-heart:hover .hoh-fresh-card-open,'+
      '.hoh-fresh-card.hoh-fresh-card-spent-heart:focus-within .hoh-fresh-card-open,'+
      '.hoh-fresh-card.hoh-fresh-card-spent-dismiss .hoh-fresh-card-open,'+
      '.hoh-fresh-card.hoh-fresh-card-spent-dismiss:hover .hoh-fresh-card-open,'+
      '.hoh-fresh-card.hoh-fresh-card-loved-out .hoh-fresh-card-open,'+
      '.hoh-fresh-card.hoh-fresh-card-hearted .hoh-fresh-card-open{'+
        'opacity:1 !important;visibility:visible !important;pointer-events:auto !important;cursor:pointer !important}'+
      /* R255: event/roll headers — one consistent stage across the whole
         hall. Bigger title, bigger subline, real top padding off the
         panel border. The live stylesheet's sizes drifted small. */
      '#hoh-fresh-faces .hoh-fresh-header{padding-top:24px !important}'+
      '#hoh-fresh-faces .hoh-fresh-eyebrow{font-size:20px !important;letter-spacing:.16em !important;line-height:1.3 !important}'+
      '#hoh-fresh-faces .hoh-fresh-sub{font-size:15.5px !important;line-height:1.5 !important}'+
      '@media(max-width:700px){'+
        '#hoh-fresh-faces .hoh-fresh-header{padding-top:18px !important}'+
        '#hoh-fresh-faces .hoh-fresh-eyebrow{font-size:17.5px !important}'+
        '#hoh-fresh-faces .hoh-fresh-sub{font-size:14.5px !important}'+
      '}'+
      /* R604: a no-timer roll still uses the header's compact edge slot.
         The old desktop line-break sentinel became a full-height flex item
         after the header changed to a column and produced the giant empty
         mobile band seen between the chip and the cards. The dormant thread
         is the only thing this state needs to hide. */
      '#hoh-fresh-faces.hoh-fresh-no-timer .hoh-fresh-auto-bar{display:none !important}';
    (document.head||document.documentElement).appendChild(st);
  })();
  /* Open door: the corner button (and keyboard Enter) — a look without a spend. */
  window.hohFreshFacesOpen = function(id, e){
    if(e){ e.stopPropagation(); e.preventDefault(); }
    if(id && typeof window.hohOpenDetail === 'function') hohOpenDetail(id);
  };
  /* Single tap anywhere on the card = heart NOW (no delay — a laggy heart
     defeats the point). Second tap within a beat = open (the first tap's
     heart stands; in Explore a heart is never a mistake). Locked cards
     (dismissed / loved-out / spent) can't take hearts, so a tap opens
     them instead. Brand burst rides the tap point on success. */
  var _ffTapAt = {};
  window.hohFreshCardTap = function(id, e){
    if(!id) return;
    var now = Date.now(), last = _ffTapAt[id] || 0;
    _ffTapAt[id] = now;
    if(now - last < 330){
      _ffTapAt[id] = 0;
      if(typeof window.hohOpenDetail === 'function') hohOpenDetail(id);
      return;
    }
    var card = document.querySelector('.hoh-fresh-card[data-id="' + cssEscape(id) + '"]');
    if(card && /hoh-fresh-card-(spent-dismiss|loved-out|spent-heart|locked)/.test(card.className)){
      if(typeof window.hohOpenDetail === 'function') hohOpenDetail(id);
      return;
    }
    var ok = false;
    try{ ok = !!window.hohFreshFacesQuickHeart(id, null); }catch(_){}
    if(ok) hohFreshHeartBurst(id, e);
  };
  /* Subtle branded burst at the tap point — pink, blue, gold shards. */
  function hohFreshHeartBurst(id, e){
    try{
      if(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
      var card = document.querySelector('.hoh-fresh-card[data-id="' + cssEscape(id) + '"]');
      var r = card ? card.getBoundingClientRect() : null;
      var x = (e && e.clientX) || (r ? r.left + r.width/2 : window.innerWidth/2);
      var y = (e && e.clientY) || (r ? r.top + r.height/2 : window.innerHeight/2);
      var cols = ['#ef94ff','#b3f5ee','#f2d98e'];
      for(var k=0;k<9;k++){
        var s = document.createElement('span');
        s.className = 'hoh-ffburst';
        var a = Math.random()*Math.PI*2, d = 26 + Math.random()*34;
        s.style.left = x+'px'; s.style.top = y+'px';
        s.style.background = cols[k%3];
        s.style.boxShadow = '0 0 8px ' + cols[k%3];
        s.style.setProperty('--bx', (Math.cos(a)*d).toFixed(0)+'px');
        s.style.setProperty('--by', (Math.sin(a)*d - 14).toFixed(0)+'px');
        document.body.appendChild(s);
        (function(el){ setTimeout(function(){ try{ el.remove(); }catch(_){} }, 800); })(s);
      }
    }catch(_){}
  }
  window.hohFreshFacesQuickHeart = function(id, e){
    if(e){ e.stopPropagation(); e.preventDefault(); }
    if(!id || typeof window.hohHeart !== 'function') return false;
    if(!_questAcceptsHeart()){
      _beginWanderCompletion();
      return false;
    }
    var card = document.querySelector('.hoh-fresh-card[data-id="' + cssEscape(id) + '"]');
    var heartBtn = card ? card.querySelector('.hoh-fresh-card-heart') : null;
    /* R132-A: Returns boolean. true = heart actually fired (caller
       should count it). false = blocked at the wallet or page-allowance
       gate (caller must NOT count it as a spent heart). The hold-to-
       stream path in hoh-heart-hold.js gates state.count++ on this
       return. Without this gate, the hold counter ticked up on every
       blocked attempt, and on pointerup stopHold would mark the card
       as spent-heart even though no heart was given — the "red pulse
       then disabled card with no spend" bug the user reported on
       small-batch themed phases like Fairy Ring (3 cards = floor(3/5)
       = 0 allowance → every click rejected). */
    /* Wallet check FIRST — if the user has no hearts left, do NOT
       trigger the loved-out animation or remove the card. Just flash
       a red rejection on the card so they get clear feedback ("you're
       out of hearts"). The X dismiss path still works for cards they
       want to remove from the rail. */
    var heartsLeft = (typeof window.heartsRemainingToday === 'function') ? window.heartsRemainingToday() : 1;
    if(heartsLeft <= 0){
      if(card){
        card.classList.remove('hoh-fresh-card-reject');
        void card.offsetWidth;
        card.classList.add('hoh-fresh-card-reject');
        setTimeout(function(){
          if(card) card.classList.remove('hoh-fresh-card-reject');
        }, 720);
      }
      /* Pulse the "Out of hearts" chip red so the user sees WHY their
         click didn't land. The chip is the closest visual anchor to
         the heart action — flashing it teaches the relationship in one
         beat. We force a reflow before re-adding the class so back-to-
         back denies still trigger the animation each time. */
      var chipEl = document.querySelector('#hoh-fresh-page-hearts');
      if(chipEl){
        chipEl.classList.remove('hoh-fresh-page-hearts-deny');
        void chipEl.offsetWidth;
        chipEl.classList.add('hoh-fresh-page-hearts-deny');
        setTimeout(function(){
          if(chipEl) chipEl.classList.remove('hoh-fresh-page-hearts-deny');
        }, 950);
      }
      if(typeof window.hohShowHeartToast === 'function'){
        window.hohShowHeartToast("You're out of hearts.", 'Explore the Hall to find more. Hearts hide on legendary heroes, in dice rolls, and behind glowing icons.');
      }
      return false;
    }
    /* Per-page allowance check. The page budget is paint-time
       computed (ceil(visible/4) capped by wallet). When the user
       hits the cap, block the heart and surface a gentle nudge —
       the auto-refresh below will land them on a fresh page. */
    /* R314: once this batch's hearts are spent the cards go INERT rather than
       rejecting. The old red shake punished a click the user had every reason
       to make, and there is a real window between spending the last heart and
       the auto-refresh where a third hero is still clickable — the shake made
       that window feel like an error instead of simply being closed. The cards
       are now dimmed and pointer-events:none via .hoh-fresh-page-spent (set in
       paintPageHearts), so the click never lands. This stays as a silent guard
       for anything that slips through mid-refresh. */
    if(!pageHeartsAllowSpend()){
      return false;
    }
    /* Pulse the button so the user feels the response immediately. */
    if(heartBtn){
      heartBtn.classList.remove('hoh-fresh-card-heart-pulse');
      void heartBtn.offsetWidth;
      heartBtn.classList.add('hoh-fresh-card-heart-pulse');
    }
    /* SNAPSHOT the heart button's screen rect BEFORE we start the
       loved-out animation or schedule the card removal. hohHeart is
       async (network roundtrip), so by the time it calls
       hohThrowHeartAt(), the card may have already animated out or
       been detached — in which case getBoundingClientRect() returns
       all zeros and the heart flies to viewport (0,0) (the "too far
       up-left" bug). The throw helper will prefer this snapshot if
       the live rect is empty. */
    if(heartBtn){
      try {
        var snap = heartBtn.getBoundingClientRect();
        if(snap && snap.width > 0 && snap.height > 0){
          heartBtn.__hohRectSnap = {
            left: snap.left, top: snap.top,
            right: snap.right, bottom: snap.bottom,
            width: snap.width, height: snap.height
          };
        }
      } catch(_){}
    }
    /* Mark hearted immediately for visual feedback, then animate the
       card out of the rail with a heart-burst flavor. The card stays
       in lastRenderedIds purgatory so the next render filters it out
       (unseenList already excludes hearted chars). NOT added to the
       dismissed list — refresh can still surface it later. */
    if(card) card.classList.add('hoh-fresh-card-hearted');
    /* Mark intent BEFORE firing the heart so any in-flight render
       already excludes this card. Critical for slow networks where
       hohHeart's RPC hasn't resolved by the time we re-render. */
    heartIntent[id] = true;
    /* Second Chances event: hearting a dismissed hero pulls them
       OUT of the dismissed list so they re-enter the normal pool.
       R148-Z22v: Restoring a hidden hero also grants +10 hearts —
       each restore is its own act of "remembering" so each one
       rewards individually. If the event surfaces 3 dismissed
       heroes and the user hearts all 3, they get +30 hearts total.
       Jan, 2026-09-27: the hearts pop on the card and fly nowhere
       (they used to fly from the card to the wallet pill). */
    if(currentSpecial && currentSpecial.type === 'secondChances'){
      try { window.hohRestoreCharacter(id); } catch(_){}
      try {
        if(typeof window.hohAddHeartsToBank === 'function'){
          window.hohAddHeartsToBank(10);
        }
      } catch(_){}
      try {
        if(card && typeof window.hohHeartBurst === 'function'){
          window.hohHeartBurst(card, { n: 10 });
        }
      } catch(_){}
      try {
        if(typeof window.hohShowHeartToast === 'function'){
          window.hohShowHeartToast('Welcome back', 'Hero rejoins the Hall.');
        }
      } catch(_){}
    }
    /* Bump the daily Wander-the-Hall quest counter. We bump on the
       optimistic path (before the server resolves) so the quest UI
       reacts instantly. If the heart fails server-side (e.g. cap
       hit), the user still spent intent at fresh-faces, which counts
       as engagement here. */
    bumpFfHeartsToday();
    /* Record against the per-page allowance. Idempotent per card —
       passing id ensures fractional hearts on the same card don't
       double-decrement the chip. Triggers auto-refresh when the
       budget is fully spent. */
    pageHeartsRecordSpend(id);
    /* Schedule the visual cleanup immediately — independent of
       whether hohHeart's promise resolves. Even if the server caps
       or fails, the card vanishes from Fresh Faces (a refresh
       resurfaces unhearted ones). */
    window.hohFreshFacesAfterHeart(id);
    /* Fire the heart spend in the background. We don't gate the
       cleanup on its result. */
    try { window.hohHeart(id, heartBtn || card); } catch(_){}
    /* R132-A: signal success to the hold-to-stream caller so it
       counts this fire toward state.count. */
    return true;
  };

  /* Persistent "spent" state instead of card removal.

     Old behavior: hearting/dismissing a card animated it out and
     removed it from the DOM. As cards left, the grid shrank, the
     per-page heart counter recomputed, and the auto-refresh path
     fired when the snapshot emptied.

     New behavior: hearted/dismissed cards STAY on screen with a
     locked visual state — pink glow + grey for hearted, dimmer
     grey for dismissed. The card is no longer interactive (heart
     button is locked, X is locked). We track consumed cards in a
     per-page Set; when the consumed count equals the rendered
     count OR the per-page heart budget is fully spent, the rail
     auto-refreshes to a new page.

     Why: the old shrink-and-replace was visually busy and broke
     the page-heart counter (which depended on visibleCount). With
     persistent cards the counter stays stable and the user sees
     their progress through the batch literally, card by card. */
  var consumedThisPage = Object.create(null);
  function consumedKey(){ return pageHearts.key || ''; }
  function markConsumed(id){
    var key = consumedKey();
    if(!consumedThisPage[key]) consumedThisPage[key] = Object.create(null);
    consumedThisPage[key][id] = true;
  }
  function consumedCount(){
    var key = consumedKey();
    var bucket = consumedThisPage[key];
    if(!bucket) return 0;
    return Object.keys(bucket).length;
  }
  function maybeAutoRefreshOnExhaust(){
    /* Visible card count is what was rendered for THIS page. When
       all of them are consumed (any combination of heart + X), the
       user has finished this batch and we wander on. The shared
       schedulePageAutoRefresh flag prevents this from double-firing
       alongside the budget-exhaust path. */
    var c = _ffContainer();
    if(!c) return;
    var rendered = c.querySelectorAll('.hoh-fresh-card').length;
    if(rendered <= 0) return;
    if(consumedCount() < rendered) return;
    schedulePageAutoRefresh(700);
  }

  window.hohFreshFacesAfterHeart = function(id){
    if(!id) return;
    heartIntent[id] = true;
    var card = document.querySelector('.hoh-fresh-card[data-id="' + cssEscape(id) + '"]');
    if(card){
      /* Apply the locked-in heart state. Card stays in the DOM. */
      card.classList.add('hoh-fresh-card-hearted');
      card.classList.add('hoh-fresh-card-spent-heart');
    }
    lastRenderedIds = lastRenderedIds.filter(function(x){ return x !== id; });
    if(cachedWanderingPool){
      cachedWanderingPool = cachedWanderingPool.filter(function(c){ return c && c.id !== id; });
    }
    /* renderedSnapshotIds intentionally NOT filtered — the card
       stays on screen, so the snapshot still represents the page. */
    /* Special events: do NOT filter currentSpecial.chars. Cards
       stay on screen, the spent state class makes them inert, and
       a re-render (if it ever happens) would still want to show
       them in their locked state. */
    markConsumed(id);
    maybeAutoRefreshOnExhaust();
  };

  /* Snapshot helper for cross-module callers.
     Returns true if the hero id is currently displayed in the Explore
     the Hall section. The detail modal flow uses this to decide
     whether the heart should progress the daily Explore quest. We
     check both lastRenderedIds (authoritative) and a live DOM lookup
     (covers async state resets between this call and a subsequent
     bridge invocation). Always returns boolean — never throws. */
  window.hohFreshFacesIsOnCurrentBatch = function(id){
    if(!id) return false;
    try {
      if(lastRenderedIds && lastRenderedIds.indexOf(id) !== -1) return true;
      var live = document.querySelector('.hoh-fresh-card[data-id="' + cssEscape(id) + '"]');
      return !!live;
    } catch(_){ return false; }
  };

  /* External heart bridge.
     Many users heart a character by tapping the small fresh-faces card
     to open the expanded detail modal, then clicking the big heart
     inside the modal. That path goes through hohHeartDetail in
     hoh-section-core.js — which spends from the wallet but never
     touches fresh-faces' bookkeeping. The result was: the daily
     Explore the Hall counter wouldn't tick, the per-page allowance
     wouldn't decrement, and the small card behind the modal wouldn't
     visually flip to the hearted state.
     This bridge runs the same bookkeeping the quick-heart path runs,
     but ONLY when the hearted id is currently rendered on a fresh-
     faces page. Hearts on characters outside fresh-faces (e.g. the
     main hall gallery) are no-ops here — those don't progress the
     Explore quest. Wrapped in try/catch so a hiccup in any one step
     can't blow up the modal's heart flow. */
  window.hohFreshFacesNoteExternalHeart = function(id, hint){
    if(!id) return;
    try {
      /* Caller can pass hint.inFreshFaces=true when they captured the
         on-batch state BEFORE the wallet decrement (since wallet→0
         can sync-clear lastRenderedIds before this bridge runs). When
         the caller didn't capture, we fall back to the live check. */
      var pre = !!(hint && hint.inFreshFaces);
      if(!pre){
        var inLast = lastRenderedIds && lastRenderedIds.indexOf(id) !== -1;
        var liveCard = document.querySelector('.hoh-fresh-card[data-id="' + cssEscape(id) + '"]');
        if(!inLast && !liveCard) return;
      }
      /* Per-batch dedupe: skip if this character has already counted
         toward THIS batch's allowance. Uses pageHeartsCountedSet
         (resets each new batch) instead of heartIntent (session-wide
         and would block Familiar Faces re-hearts in later batches).
         The check inside pageHeartsRecordSpend also dedupes, but we
         gate here so the per-batch bookkeeping below doesn't double
         count on rapid repeat clicks. */
      if(pageHeartsCountedSet[id]) return;
      /* R148-Z2: bumpFfHeartsToday() removed from the modal-bridge
         path per user. Hearts given from inside the detail modal no
         longer count toward Explore the Hall progress, even when the
         underlying hero is on the current FF batch. The user's intent
         is that ONLY inline heart taps on Explore cards count — the
         modal is "modal hearts", not "Explore hearts". The other
         bookkeeping calls stay (page-heart allowance + card-flip-to-
         hearted) because those are about the FF panel's local
         consistency, not the daily quest counter. */
      try { pageHeartsRecordSpend(id); } catch(_){}
      try { window.hohFreshFacesAfterHeart(id); } catch(_){}
    } catch(_){}
  };

  /* User dismissed a Fresh Face from their personal rail. Persisted
     so it stays gone across visits on this device. Animates out,
     then re-renders so a card from beyond the visible window can
     slide in and fill the gap. event.stopPropagation prevents the
     dismiss click from also firing the parent card's onclick (which
     would open the detail modal). */
  window.hohFreshFacesDismiss = function(id, e){
    if(e){ e.stopPropagation(); e.preventDefault(); }
    if(!id) return;
    var list = getDismissed();
    if(list.indexOf(id) === -1){ list.push(id); setDismissed(list); }
    /* Persistent dismissed state — card stays on screen, locked and
       greyed. Mirrors the heart spent state but visually colder so
       the two are distinguishable at a glance. The dismissed list
       (server-side / device-wide) still gets the id so future rolls
       don't surface this hero again. */
    var card = document.querySelector('.hoh-fresh-card[data-id="' + cssEscape(id) + '"]');
    if(card){
      card.classList.add('hoh-fresh-card-spent-dismiss');
    }
    lastRenderedIds = lastRenderedIds.filter(function(x){ return x !== id; });
    if(cachedWanderingPool){
      cachedWanderingPool = cachedWanderingPool.filter(function(c){ return c && c.id !== id; });
    }
    /* Don't filter currentSpecial.chars — card stays in the DOM. */
    markConsumed(id);
    maybeAutoRefreshOnExhaust();
    /* HALLHERO (Jan, 2026-09-23): the main Hall's "Hidden by me" view is gone,
       so a hide is undone right here: Undo in the toast restores the hero to the
       rail and the main grid. The grid's end keeps a "Show them" line too. */
    try {
      if(typeof window.hohShowHallToast === 'function'){
        window.hohShowHallToast('Hero hidden', { linkLabel: 'Undo', duration: 6000, linkClick: function(){
          try { window.hohFreshFacesRestore(id); } catch(_){}
          try { if(typeof window.hohApplyFilters === 'function') window.hohApplyFilters(); } catch(_){}
        } });
      }
    } catch(_){}
  };

  /* Used by the cap-rejection toast: pick one random fresh face that
     is NOT the just-rejected character. Falls back to a random
     non-fresh character if no fresh ones exist, so the redirect always
     has something to offer. */
  window.hohFreshFacesPickRandom = function(excludeId){
    var pool = freshList().filter(function(c){ return c.id !== excludeId; });
    if(!pool.length){
      var all = (typeof window.hohGetAllCharacters === 'function') ? window.hohGetAllCharacters() : [];
      pool = all.filter(function(c){ return c && c.id && c.id !== excludeId; });
    }
    if(!pool.length) return null;
    return pool[Math.floor(Math.random() * pool.length)];
  };

  /* Cap-rejection toast. Triggered by the heart click handlers in the
     host file when increment_hearts_capped returns null (today\'s 125
     heart cap was hit on this character). The cap counts GIVEN clicks,
     not displayed hearts — at legendary tier (5:1 absorb ratio), 125
     clicks adds 25 displayed hearts. Offers a Fresh Face redirect so
     the user does not lose momentum.
     R51: cap bumped 100 → 125. Server-side SQL needed:
       CREATE OR REPLACE FUNCTION increment_hearts_capped(...) ...
       -- find the WHERE/IF check that compares today\'s click count
       -- against 100 and update it to 125. */
  window.hohHandleCapRejection = function(charId, btn){
    var all = (typeof window.hohGetAllCharacters === 'function') ? window.hohGetAllCharacters() : [];
    var src = all.find(function(c){ return c.id === charId; });
    var charName = src ? esc(src.name) : 'This hero';
    /* R165: Two distinct copy paths.
       USER-CAPPED: this user has personally given >=100 hearts to this
         hero today. The button is disabled and stays disabled until
         midnight. Toast tells them they hit their personal cap.
       GLOBAL MISS-FIRE: the server's old per-character global counter
         rejected even though this user is under 100. The button is NOT
         disabled — refund the heart and tell them the hero is "taking
         a breather" so they can try again shortly. (The R165 SQL
         migration removes this miss-fire path entirely; until then
         this fallback keeps users unblocked.) */
    var mine = (typeof window.hohGetMineToday === 'function') ? (window.hohGetMineToday(charId) | 0) : 0;
    var userCapped = mine >= 100;
    var existing = document.getElementById('hoh-cap-toast');
    if(existing) existing.remove();
    var t = document.createElement('div');
    t.id = 'hoh-cap-toast';
    t.className = 'hoh-cap-toast';
    var bodyHtml;
    if(userCapped){
      bodyHtml =
        '<span class="hoh-cap-toast-head">You\'ve cheered them on plenty</span>' +
        'You\'ve given ' + charName + ' your full 100 hearts today. The cap resets at midnight. Spend the rest of today\'s hearts on someone new, or feed ' + charName + ' from Explore the Hall — that path doesn\'t cap.' +
        '<div class="hoh-cap-toast-actions">' +
          '<button type="button" class="hoh-cap-toast-cta hoh-cap-toast-primary" onclick="hohScrollToFresh()">Open Explore the Hall</button>' +
          '<button type="button" class="hoh-cap-toast-cta hoh-cap-toast-secondary" onclick="hohDismissCapToast()">Save for tomorrow</button>' +
        '</div>';
    } else {
      bodyHtml =
        '<span class="hoh-cap-toast-head">' + charName + ' is taking a breather</span>' +
        'The Hall paused incoming hearts for this hero. Your heart was refunded — try again in a moment, or feed them from Explore the Hall (that path never pauses).' +
        '<div class="hoh-cap-toast-actions">' +
          '<button type="button" class="hoh-cap-toast-cta hoh-cap-toast-primary" onclick="hohScrollToFresh()">Open Explore the Hall</button>' +
          '<button type="button" class="hoh-cap-toast-cta hoh-cap-toast-secondary" onclick="hohDismissCapToast()">Dismiss</button>' +
        '</div>';
    }
    t.innerHTML =
      '<div class="hoh-cap-toast-body">' + bodyHtml + '</div>' +
      '<button type="button" class="hoh-cap-toast-x" onclick="hohDismissCapToast()" aria-label="Dismiss">&times;</button>';
    document.body.appendChild(t);
    requestAnimationFrame(function(){ t.classList.add('hoh-cap-toast-visible'); });
    if(window.hohCapToastTimer) clearTimeout(window.hohCapToastTimer);
    window.hohCapToastTimer = setTimeout(function(){ window.hohDismissCapToast(); }, 8000);
  };
  window.hohDismissCapToast = function(){
    var t = document.getElementById('hoh-cap-toast');
    if(!t) return;
    t.classList.remove('hoh-cap-toast-visible');
    setTimeout(function(){ if(t.parentNode) t.parentNode.removeChild(t); }, 300);
  };

  /* R140-G: Raffle ticket lightbox. Two states:
       granted=true  → celebration: "+1 RAFFLE TICKET", confetti, brand
                       gradient, primary CTA opens the daily-tickets
                       modal so the user can apply it immediately.
       granted=false → "Already claimed today": muted styling, single
                       Close button, copy explains a sibling device
                       (or another earn path) already pinned today's
                       wallet credit and a refund was issued.
     Both states are full-screen lightboxes with a backdrop, vs the
     existing hoh-cap-toast which floats at the bottom — the user
     specifically asked for a lightbox here. Mobile responsive via
     the .hoh-raffle-lightbox CSS rules in hall-of-heroes.css. */
  window.hohShowRaffleTicketLightbox = function(opts){
    opts = opts || {};
    var granted = !!opts.granted;
    /* Bail if a previous lightbox is already mounted — replace it. */
    var existing = document.getElementById('hoh-raffle-lightbox');
    if(existing) existing.parentNode && existing.parentNode.removeChild(existing);
    var overlay = document.createElement('div');
    overlay.id = 'hoh-raffle-lightbox';
    overlay.className = 'hoh-raffle-lightbox' + (granted ? ' hoh-raffle-lightbox-granted' : ' hoh-raffle-lightbox-claimed');
    var headline = granted ? '+1 Raffle Ticket' : 'Already claimed today';
    var sub = granted
      ? 'Trade settled. The ticket is in your wallet — apply it to any hero you submitted.'
      : 'This account already received a raffle ticket today (this device or another). The 100 hearts have been refunded. Come back tomorrow for the next one.';
    var iconHtml = granted
      ? '<div class="hoh-raffle-lightbox-icon" aria-hidden="true">' +
          '<svg viewBox="0 0 64 64" width="56" height="56" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round">' +
            '<rect x="8" y="18" width="48" height="28" rx="4"/>' +
            '<path d="M22 18 L22 46 M42 18 L42 46" stroke-dasharray="2 4"/>' +
            '<circle cx="32" cy="32" r="3" fill="currentColor"/>' +
          '</svg>' +
        '</div>'
      : '<div class="hoh-raffle-lightbox-icon hoh-raffle-lightbox-icon-muted" aria-hidden="true">' +
          '<svg viewBox="0 0 64 64" width="48" height="48" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round">' +
            '<circle cx="32" cy="32" r="22"/>' +
            '<path d="M32 22 L32 36 M32 42 L32 42"/>' +
          '</svg>' +
        '</div>';
    var ctaHtml = granted
      ? '<div class="hoh-raffle-lightbox-actions">' +
          '<button type="button" class="hoh-raffle-lightbox-cta hoh-raffle-lightbox-primary" onclick="hohRaffleLightboxOpenWallet()">Apply ticket</button>' +
          '<button type="button" class="hoh-raffle-lightbox-cta hoh-raffle-lightbox-secondary" onclick="hohDismissRaffleLightbox()">Done</button>' +
        '</div>'
      : '<div class="hoh-raffle-lightbox-actions">' +
          '<button type="button" class="hoh-raffle-lightbox-cta hoh-raffle-lightbox-primary" onclick="hohDismissRaffleLightbox()">Got it</button>' +
        '</div>';
    overlay.innerHTML =
      '<div class="hoh-raffle-lightbox-backdrop" onclick="hohDismissRaffleLightbox()"></div>' +
      '<div class="hoh-raffle-lightbox-card" role="dialog" aria-modal="true" aria-labelledby="hoh-raffle-lightbox-headline">' +
        '<button type="button" class="hoh-raffle-lightbox-x" onclick="hohDismissRaffleLightbox()" aria-label="Close">&times;</button>' +
        iconHtml +
        '<div class="hoh-raffle-lightbox-headline" id="hoh-raffle-lightbox-headline">' + esc(headline) + '</div>' +
        '<div class="hoh-raffle-lightbox-sub">' + esc(sub) + '</div>' +
        ctaHtml +
      '</div>';
    document.body.appendChild(overlay);
    /* Force reflow then add visible class so the entrance animates
       cleanly. Without the rAF wrap, the browser collapses the two
       style updates and skips the transition. */
    requestAnimationFrame(function(){ overlay.classList.add('hoh-raffle-lightbox-visible'); });
    /* Confetti on the celebration variant only. Use the canonical
       hohFireConfetti helper if present (canvas-based, brand colors)
       so the burst matches the legshop confetti option. */
    if(granted){
      try {
        if(typeof window.hohFireConfetti === 'function'){
          window.hohFireConfetti();
          setTimeout(function(){ try { window.hohFireConfetti(); } catch(_){} }, 280);
        }
      } catch(_){}
    }
  };
  window.hohDismissRaffleLightbox = function(){
    var el = document.getElementById('hoh-raffle-lightbox');
    if(!el) return;
    el.classList.remove('hoh-raffle-lightbox-visible');
    setTimeout(function(){
      if(el.parentNode) el.parentNode.removeChild(el);
    }, 280);
  };
  window.hohRaffleLightboxOpenWallet = function(){
    /* Close the lightbox, then open the raffle modal — that's where
       the user applies tickets to specific heroes. window.hohOpenRaffle
       is defined in hoh-section-core.js (sets the raffle-modal to
       display:flex and locks scroll). The 200ms delay lets the
       lightbox finish its fade so the raffle modal entrance reads
       cleanly. */
    window.hohDismissRaffleLightbox();
    setTimeout(function(){
      try {
        if(typeof window.hohOpenRaffle === 'function'){
          window.hohOpenRaffle();
        }
      } catch(_){}
    }, 200);
  };

  /* "Spend on Fresh Faces": close any open detail, then scroll to
     the Fresh Faces section so the user can pick someone new to
     heart with their remaining bank. No auto-heart — they choose
     who deserves the next one. */
  window.hohScrollToFresh = function(){
    window.hohDismissCapToast();
    if(typeof window.hohCloseModal === 'function') window.hohCloseModal();
    setTimeout(function(){
      var anchor = _ffContainer();
      if(anchor && typeof anchor.scrollIntoView === 'function'){
        /* Center the section in the viewport instead of pinning to
           the top — feels less abrupt and lets the user see the
           full page-hearts header in context. */
        anchor.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 200);
  };

  /* Hall tabs. Toggles the hoh-page-mode-yours class on body, which
     CSS uses to hide the default view (Fresh Faces + main gallery)
     and show the in-page Your Hall view. No modal anywhere — both
     views are part of the page itself. */
  window.hohSwitchHallTab = function(name){
    var allBtn = document.getElementById('hoh-tab-all');
    var yoursBtn = document.getElementById('hoh-tab-yours');
    if(allBtn) allBtn.classList.toggle('hoh-hall-tab-active', name === 'all');
    if(yoursBtn) yoursBtn.classList.toggle('hoh-hall-tab-active', name === 'yours');
    document.body.classList.toggle('hoh-page-mode-yours', name === 'yours');
    if(name === 'yours' && typeof window.hohOpenYourHall === 'function'){
      window.hohOpenYourHall();
    } else if(name === 'all' && typeof window.hohCloseYourHall === 'function'){
      window.hohCloseYourHall();
    }
    /* Scroll to the top of the swapped view so the user is not stranded
       deep in a previous scroll position when content changes. */
    try {
      /* R215: Explore now lives in the personal hall, so the main-hall ("all")
         scroll target is the hero grid, not the (now hidden) Explore section. */
      var anchor = (name === 'yours') ? document.getElementById('hoh-yh-page') : (document.getElementById('hoh-gallery') || _ffContainer());
      if(anchor && anchor.scrollIntoView){
        anchor.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    } catch(e){}
  };

  /* ================================================================ */
  /* Auto-refresh timer (JS-driven progress)                          */
  /* ================================================================ */
  /* Twenty seconds of stillness on Fresh Faces and we wander on. The
     intent: make the page feel alive even when the user is idle, but
     never punish a user who's actively browsing. The bar SLOWS DOWN
     dramatically (1/10 speed) on hover or recent interaction, but
     keeps progressing. It does not reset and does not pause unless
     the section is offscreen / hidden / in an interactive event.

     Implementation is rAF-driven: progress lives in a 0..1 number
     and the fill width is set on each frame. This lets us cut the
     speed instantly without restarting the animation. When progress
     hits 1, we trigger the refresh icon's click animation and fire
     hohFreshFacesRefresh({auto:true}). Browser-tab hidden pauses the
     rAF (clamps elapsed-since-last-frame).

     Pauses entirely when:
       - Document is hidden (visibilitychange)
       - The Hall is in Your Hall mode (page-mode-yours body class)
       - A non-content special event is up (choice / coin / classQuiz
         / marrowEnd / heroCircle / secondChances)
       - Detail modal is open
       - Section itself is hidden (display:none / phase -1)
     Resumes on visibility return / mode switch / event resolve. */
  /* Base auto-refresh duration for a single-row grid. The actual
     duration scales up with how many rows the page is rendering so
     the user has more time when there's more to look at. Hover
     slows further (SLOW_FACTOR). The base is mutable, set each
     time we render based on row count.
     R136-A: Bumped from 20s/6s to 32s/9s. User reported Fresh Faces
     refreshes were spamming way too often. Slower base cadence plus
     tighter special-event gaps (R136-A in event picker) means each
     refresh has a much higher chance of landing on a real event
     (Wheel, Bazaar, Five-Card, Star Falls, Lost Adventurer, etc.)
     instead of yet another themed phase / wandering-heroes roll.
     R136-speedup: 10% faster across all events. Base 32000→28800,
     per-row 9000→8100. Row scaling preserved so 3-row events
     (Three Legends, Crowded Tavern, etc.) still get more time than
     2-row events — just 10% less of it everywhere. */
  var AUTO_REFRESH_BASE_MS = 28800;
  var AUTO_REFRESH_PER_ROW_MS = 8100;
  var AUTO_REFRESH_MS = AUTO_REFRESH_BASE_MS;
  /* Approximate cards-per-row by breakpoint. Desktop renders 5
     cards/row, mobile 2. Used by computeAutoRefreshMs to estimate
     row count from visibleCount. */
  function cardsPerRowApprox(){
    try {
      if(window.matchMedia('(max-width: 768px)').matches) return 2;
      if(window.matchMedia('(max-width: 1024px)').matches) return 4;
    } catch(_){}
    return 5;
  }
  function computeAutoRefreshMs(visibleCount){
    var perRow = cardsPerRowApprox();
    var rows = Math.max(1, Math.ceil((visibleCount || 1) / perRow));
    return AUTO_REFRESH_BASE_MS + (rows - 1) * AUTO_REFRESH_PER_ROW_MS;
  }
  /* Slow factor on hover: 0.55 = ~half speed (was 0.5).
     R136-speedup: bumped 0.5 → 0.55 so the idle/hover-slowed bar
     advances 10% faster too, matching the active-rate speedup
     above. The bar still feels "slowed" but reads more responsive
     when the user pauses on the section. */
  var SLOW_FACTOR = 0.55;
  var SLOW_LINGER_MS = 1500; /* keep slow speed for 1.5s after last interaction */
  var autoProgress = 0;     /* 0..1 */
  var autoLastFrame = 0;
  var autoRafId = null;
  var autoLastInteractionMs = 0;
  var autoCompletingFlag = false;
  var autoFadeId = null;

  function autoBarEl(){
    var c = _ffContainer();
    return c ? c.querySelector('#hoh-fresh-auto-bar') : null;
  }
  function autoBarFillEl(){
    var c = _ffContainer();
    return c ? c.querySelector('#hoh-fresh-auto-bar-fill') : null;
  }
  function refreshIconEl(){
    var c = _ffContainer();
    return c ? c.querySelector('#hoh-fresh-refresh, .hoh-fresh-refresh') : null;
  }

  /* R299: the Battlegrounds own the surface. While the gate is up (or a fight
     is mounted) NOTHING may rotate it away — this is the root cause of "it
     auto refreshed into an event": the auto-timer never knew the gate existed,
     kept counting, and the advance swapped the doorway for a roll. One truth
     function, consulted by both the timer and the refresh entry. */
  function _bgHoldsSurface(){
    try {
      var held = false;
      if(document.getElementById('hoh-contract')) held = true;          /* a battle is mounted */
      if(!held){
        var c = _ffContainer();
        var st = c && c.getAttribute('data-bg');
        /* R318 WATCHDOG: 'live' with NO mounted battle is a stranded state
           (the exit render was skipped or threw) — it blanked BOTH Explore
           and the gate until reload. Heal it on the next probe. */
        if(st === 'live' && c){
          try {
            c.removeAttribute('data-bg');
            c.style.display = '';
            setTimeout(function(){ try { if(window.hohRenderFreshFaces) window.hohRenderFreshFaces(); } catch(_){} }, 0);
          } catch(_){}
          st = null;
        }
        held = (st === 'open' || st === 'replay');
      }
      /* R380: the party builder holds the surface the same way a fight does,
         so the Explore auto timer never rolls the panel behind it. */
      if(!held && document.body.classList.contains('hoh-bg-building')) held = true;
      /* R300: one body flag drives the Explore header's disappearance, so the
         surrounding chrome always agrees with who owns the screen. */
      try { document.body.classList.toggle('hoh-bg-holding', held); } catch(_){}
      return held;
    } catch(_){ return false; }
  }
  function isAutoEligible(){
    if(_bgHoldsSurface()) return false;
    if(_xintroUp()) return false;   /* R439 */
    /* R145-A: Previously bailed on phase -1 (Heroes You've Lifted),
       which is why returning users sometimes saw NO auto-progression
       bar on the hearted view and got stuck. Phase -1 now runs the
       auto-timer like any other phase — the bar ticks down + refresh
       fires + the picker tries events/themed phases first before
       falling back to a hearted shuffle. */
    /* Auto-timer eligibility for special events. The user wants the
       bar visible AND counting on every event screen, no matter
       what. The only true pause is during the coin's flipping
       animation (1.4s) where a refresh would chop the visual mid-
       transition. Everything else — choice, quiz idle/answering/
       resolved, marrow's end, hero circle, second chances — lets
       the bar tick down naturally so the user gets a deadline. */
    if(currentSpecial){
      var t = currentSpecial.type;
      if(t === 'thievesCoin' && currentSpecial.phase === 'flipping'){
        return false;
      }
      /* R150: shops never auto-refresh. Browsing a shop, or typing an
         idea into the Draft Your Event form (a legendaryShop phase),
         should not get yanked away by the auto-timer. */
      if(t === 'legendaryShop' || t === 'mageBazaar' || t === 'thievesBazaar' || t === 'sageBazaar'){
        return false;
      }
      /* R318: a STAKED roll mid-animation holds the surface. The bet is
         charged at tap and the payout lands on a ~1-2s resolve timer; an
         auto-advance in that window replaced currentSpecial, the resolve
         guard bailed, and the payout never fired — the player paid for
         nothing. Every phase listed here is a short, timer-driven beat. */
      if(currentSpecial._diceRolling === true) return false;
      var _ph = String(currentSpecial.phase || '');
      /* Only TIMER-DRIVEN beats belong here (all resolve in under ~2s and
         every resolver restarts the auto timer). Waiting-for-a-tap states
         (the bazaars' coinFlip / bonesRoll / omenCast) must NOT be listed —
         their whole surface is already auto-blocked by type above, and
         listing a waiting state would freeze the bar forever. */
      /* R591: 'fighting' belongs on this list and 'wager' must never
         join it. A running Hall Fight is exactly what this list is for:
         it is TIMER-DRIVEN, it ends on its own (hfFinish restarts the
         auto timer), and losing it to the 28.8s auto-refresh would
         evaporate a bet the player has already paid. The 'wager' phase
         is a waiting-for-a-tap state, and the comment above is explicit
         that listing one of those freezes the bar forever. */
      if(_ph === 'rolling' || _ph === 'spinning' || _ph === 'duelRolling'
         || _ph === 'flipping' || _ph === 'fighting'){
        return false;
      }
    }
    /* Out-of-hearts pause: if the user's wallet is empty AND we're
       on a content page (cards rendered), there's nothing to spend,
       so refreshing more pages doesn't help. Hold the page until
       they top up their wallet. Interactive events (choice / coin /
       quiz / marrow / circle) override this — those events move on
       via their own UI. */
    try {
      var bank = (typeof window.heartsRemainingToday === 'function') ? window.heartsRemainingToday() : 1;
      if(bank <= 0){
        /* Allow timer through on interactive events that don't
           depend on hearts. */
        var sType = currentSpecial && currentSpecial.type;
        var INTERACTIVE = ['choice','thievesCoin','classQuiz','marrowEnd','heroCircle'];
        if(INTERACTIVE.indexOf(sType) === -1) return false;
      }
    } catch(_){}
    /* During the post-coin transition we also block — the user
       earned a beat to see the rogue chars settle. */
    if(autoCompletingFlag) return false;
    /* Hidden tab. */
    if(typeof document.hidden === 'boolean' && document.hidden) return false;
    /* R217: Your Hall USED to hide Fresh Faces, so this bailed in Your Hall mode.
       Explore now LIVES in Your Hall, so that guard killed the timer here every
       time. Real hidden cases are caught by the display + offsetParent + observer
       checks below, so the guard is removed. */
    /* Detail modal open. */
    var detail = document.getElementById('hoh-modal');
    if(detail && detail.style.display === 'flex') return false;
    /* The section itself is hidden. */
    var c = _ffContainer();
    if(!c) return false;
    /* R217: catch the section hidden by CSS, by an ancestor (main hall mode, or a
       collapsed Explore panel), or by inline display — offsetParent is null then. */
    if(c.style.display === 'none' || c.offsetParent === null) return false;
    /* Off-screen pause: when the user has scrolled past Fresh Faces
       the bar should NOT be auto-refreshing — that was causing the
       page to silently shrink/expand mid-scroll on long pages, since
       the section's height changes as a new batch loads. The
       sectionVisible flag is updated by an IntersectionObserver
       below. We default to TRUE so the timer runs before the
       observer attaches (no false-pause on initial page load). */
    if(sectionVisible === false) return false;
    return true;
  }
  /* IntersectionObserver-driven viewport flag. Updated whenever the
     Fresh Faces section enters/leaves the viewport. We use a generous
     rootMargin so the bar starts ticking again as soon as the section
     is even partially in view, and stops as soon as it's fully gone.
     The observer lives at module scope so it survives re-renders. */
  var sectionVisible = true;
  function bindViewportObserver(){
    var c = _ffContainer();
    if(!c || c.__hohViewportBound) return;
    if(typeof IntersectionObserver !== 'function') return;
    c.__hohViewportBound = true;
    try {
      var io = new IntersectionObserver(function(entries){
        for(var i = 0; i < entries.length; i++){
          var entry = entries[i];
          var nowVisible = entry.isIntersecting;
          if(nowVisible === sectionVisible) continue;
          sectionVisible = nowVisible;
          /* When the section scrolls back in, restart the timer so
             the bar paints fresh (no stale partial fill). When it
             scrolls out, stop so the rAF doesn't keep ticking and
             potentially auto-refresh under the user's feet. */
          if(nowVisible){
            try { if(typeof startAutoTimer === 'function') startAutoTimer(); } catch(_){}
          } else {
            try { if(typeof stopAutoTimer === 'function') stopAutoTimer(); } catch(_){}
          }
        }
      }, { rootMargin: '0px', threshold: 0 });
      io.observe(c);
    } catch(_){}
  }

  function setBarVisible(on){
    var bar = autoBarEl();
    if(!bar) return;
    if(on) bar.classList.add('hoh-fresh-auto-bar-on');
    else bar.classList.remove('hoh-fresh-auto-bar-on');
  }
  function paintBar(){
    var bar = autoBarEl();
    var fill = autoBarFillEl();
    if(!bar || !fill) return;
    /* Ensure the bar is visible whenever we're painting. This
       handles the case where a special event re-renders the
       header markup (e.g., quiz submit replaces the inner HTML)
       and the new bar element is missing the visible class —
       without this, the bar would go invisible after a quiz
       resolve even though the timer was running. */
    if(autoRafId){
      bar.classList.add('hoh-fresh-auto-bar-on');
    }
    var pct = Math.min(100, Math.max(0, autoProgress * 100));
    fill.style.width = pct.toFixed(2) + '%';
  }

  function rafTick(now){
    if(!isAutoEligible()){
      autoRafId = null;
      return;
    }
    if(!autoLastFrame) autoLastFrame = now;
    var dt = now - autoLastFrame;
    autoLastFrame = now;
    /* Clamp dt to avoid huge jumps when tab regains focus (rAF can
       skip many frames when backgrounded). */
    if(dt < 0) dt = 0;
    if(dt > 120) dt = 120;
    /* Speed factor: full speed by default, 1/10 if the user has
       interacted within the last SLOW_LINGER_MS.
       R53: when the user is sitting on an EVENT idle phase (e.g. they
       got Pick a Card and aren\'t engaging), accelerate after 5
       seconds — 2x for 5-10s idle, 3x for 10s+ idle. The interaction
       timestamp tracks the LAST interaction; if 5+ seconds have passed
       since the special event mounted AND the user hasn\'t interacted
       at all, the bar speeds up so dead events expire faster. */
    var sinceInteract = now - autoLastInteractionMs;
    var factor = (autoLastInteractionMs && sinceInteract < SLOW_LINGER_MS) ? SLOW_FACTOR : 1;
    /* Idle-event speedup: only when on an event in idle phase, the
       user has never interacted (or last interaction was long ago),
       and at least 5s of bar time has elapsed. */
    try {
      var sp = currentSpecial && currentSpecial.phase;
      var isIdleEvent = sp === 'idle' && currentSpecial && currentSpecial.type;
      if(isIdleEvent && (!autoLastInteractionMs || sinceInteract > 4000)){
        var elapsedMs = autoProgress * AUTO_REFRESH_MS;
        if(elapsedMs > 10000) factor *= 3;
        else if(elapsedMs > 5000) factor *= 2;
      }
    } catch(_){}
    autoProgress += (dt / AUTO_REFRESH_MS) * factor;
    if(autoProgress >= 1){
      autoProgress = 1;
      paintBar();
      triggerAutoComplete();
      return;
    }
    paintBar();
    autoRafId = requestAnimationFrame(rafTick);
  }

  function triggerAutoComplete(){
    /* Trigger the refresh icon's click animation visually, then fire
       the actual refresh. autoCompletingFlag blocks isAutoEligible
       so a stray rAF callback doesn't restart the loop mid-transition. */
    autoCompletingFlag = true;
    var icon = refreshIconEl();
    if(icon){
      icon.classList.remove('hoh-fresh-refresh-spin');
      void icon.offsetWidth;
      icon.classList.add('hoh-fresh-refresh-spin');
    }
    /* Soft fade the rail. */
    var c = _ffContainer();
    if(c) c.classList.add('hoh-fresh-auto-fading');
    autoFadeId = setTimeout(function(){
      autoFadeId = null;
      if(c) c.classList.remove('hoh-fresh-auto-fading');
      autoProgress = 0;
      autoLastFrame = 0;
      autoCompletingFlag = false;
      window.hohFreshFacesRefresh({ auto: true });
      /* hohFreshFacesRefresh restarts the auto-timer at the end of
         its normal flow, so no need to call startAutoTimer here. */
    }, 380);
  }

  function stopAutoTimer(){
    if(autoRafId){ cancelAnimationFrame(autoRafId); autoRafId = null; }
    if(autoFadeId){ clearTimeout(autoFadeId); autoFadeId = null; }
    autoLastFrame = 0;
    autoCompletingFlag = false;
    var c = _ffContainer();
    if(c) c.classList.remove('hoh-fresh-auto-fading');
    var icon = refreshIconEl();
    if(icon) icon.classList.remove('hoh-fresh-refresh-spin');
    setBarVisible(false);
    /* Reset progress + fill so the next start begins from zero
       (the user expects a fresh 20s on each refresh / phase change).
       R140-X: Clear the inline `transition` from a previous manual-
       click sprint BEFORE paintBar writes width:0%. Without this,
       the sprint's lingering transition (`width 0.22s ease`) caused
       paintBar's reset to ANIMATE BACKWARDS from 100% → 0% on the
       next render — the user reported this as "the bar slowly moves
       backwards instead of resetting". This only manifested on
       wander → wander renders where renderInto reuses the existing
       header DOM (so the same fill element persists with the inline
       transition). Event renders replaced innerHTML and were always
       fine. Clearing the transition here covers both paths. */
    var fill = autoBarFillEl();
    if(fill && fill.style){
      fill.style.transition = '';
    }
    autoProgress = 0;
    paintBar();
  }

  /* A no-timer class hides only the dormant progress thread. Refresh keeps
     the same compact edge slot in every phase, so changing timer eligibility
     never makes the header jump or inserts an unexplained mobile row. */
  function ffSyncRefreshPlacement(hasTimer){
    try{
      var c = _ffContainer();
      if(c) c.classList.toggle('hoh-fresh-no-timer', !hasTimer);
    }catch(_){}
  }
  function startAutoTimer(){
    stopAutoTimer();
    var eligible = isAutoEligible();
    ffSyncRefreshPlacement(eligible);
    if(!eligible) return;
    setBarVisible(true);
    autoProgress = 0;
    autoLastFrame = 0;
    paintBar();
    autoRafId = requestAnimationFrame(rafTick);
  }
  /* R216: public hook so Your Hall can start the countdown when the Explore
     section becomes visible inside the dashboard. At page load the section sits
     hidden and the wallet can still read 0, so startAutoTimer bails and never
     retries; calling this on hall-open (section visible, wallet loaded) gets the
     timer bar running and repaints the Explore progress bar. */
  window.hohFreshFacesEnsureLive = function(){
    var completed = _ffContainer();
    if(completed && completed.querySelector('.hoh-exdone')){
      /* Reopening Events also retries an unconfirmed receipt. A banked pack
         is a no-op and the gift ceremony never replays on this recovery path. */
      _wireExploreClosed(completed);
      return;
    }
    try { bindAutoTimerEvents(); } catch(_){}
    try { startAutoTimer(); } catch(_){}
    try { if(typeof paintQuestProgress === 'function') paintQuestProgress(); } catch(_){}
  };
  /* R217: public stop hook so Your Hall can pause the countdown when the Explore
     panel is minimized (so it freezes state and does not auto-wander while hidden). */
  window.hohFreshFacesStopTimer = function(){
    try { stopAutoTimer(); } catch(_){}
  };

  /* User interaction => slow down the timer, don't reset. The
     interaction timestamp lingers for SLOW_LINGER_MS, so a continuous
     hover keeps the bar at 1/10 speed without restarting the
     animation. The bar still progresses, just slowly — so an idle
     user (no hover) gets the normal 20s, and an active user gets
     ~200s effective which is plenty of time to engage. */
  function bindAutoTimerEvents(){
    var c = _ffContainer();
    if(!c || c.__hohAutoBound) return;
    c.__hohAutoBound = true;
    var slowDown = function(){
      autoLastInteractionMs = performance.now();
    };
    /* Use capture so we see events even if a child stopPropagates. */
    c.addEventListener('pointerdown', slowDown, true);
    c.addEventListener('mousemove', throttle(slowDown, 250), true);
    c.addEventListener('mouseenter', slowDown, true);
    c.addEventListener('keydown', slowDown, true);
    c.addEventListener('wheel', throttle(slowDown, 500), { capture: true, passive: true });
    c.addEventListener('touchstart', slowDown, { capture: true, passive: true });
    c.addEventListener('touchmove', throttle(slowDown, 500), { capture: true, passive: true });
  }

  function throttle(fn, ms){
    var last = 0;
    return function(){
      var now = Date.now();
      if(now - last < ms) return;
      last = now;
      fn();
    };
  }

  /* Pause / resume on tab visibility. Pausing freezes the bar at
     whatever fill it had (we just stop). Resume restarts from zero
     so the bar always tells the truth about the time-to-refresh. */
  document.addEventListener('visibilitychange', function(){
    if(document.hidden){ stopAutoTimer(); }
    else { startAutoTimer(); }
  });

  /* Hall mode toggle pauses / resumes too. We listen for class
     changes via a MutationObserver on body — but ONLY react when
     the hoh-page-mode-yours class actually toggled. The original
     observer fired on every class mutation (sticky-header toggles,
     theme switches, etc.) which restarted the auto-timer and
     caused the user-reported "bar resets when I scroll" bug. We
     compare the previous state to the current state and bail out
     unless the hall-mode bit flipped. */
  try {
    var lastHallMode = document.body.classList.contains('hoh-page-mode-yours');
    var bodyObserver = new MutationObserver(function(){
      var nowHallMode = document.body.classList.contains('hoh-page-mode-yours');
      if(nowHallMode === lastHallMode) return;
      lastHallMode = nowHallMode;
      if(nowHallMode) stopAutoTimer();
      else startAutoTimer();
    });
    bodyObserver.observe(document.body, { attributes: true, attributeFilter: ['class'] });
  } catch(_){}

  /* Detail modal open / close. We watch the inline style attribute
     and react ONLY when the open/closed state actually flips —
     otherwise transient style mutations during animations would
     restart the auto-timer and reset the bar. */
  function bindModalObserver(){
    var modal = document.getElementById('hoh-modal');
    if(!modal || modal.__hohAutoBound) return;
    modal.__hohAutoBound = true;
    var lastOpen = modal.style.display === 'flex';
    try {
      var obs = new MutationObserver(function(){
        var nowOpen = modal.style.display === 'flex';
        if(nowOpen === lastOpen) return;
        lastOpen = nowOpen;
        if(nowOpen){
          stopAutoTimer();
        } else {
          /* Drain any deferred wallet reset that was queued while the
             modal was open (wallet→0 from a detail-modal heart). */
          try { checkDeferredWalletReset(); } catch(_){}
          startAutoTimer();
        }
      });
      obs.observe(modal, { attributes: true, attributeFilter: ['style'] });
    } catch(_){}
  }
  /* Try once now, again after DOM ready, again when chars load. */
  bindModalObserver();
  document.addEventListener('DOMContentLoaded', bindModalObserver);
  document.addEventListener('hoh:characters-loaded', bindModalObserver);

  /* Expose a manual hook for the host so any in-page action that
     should reset the timer (e.g. opening a detail modal) can poke it. */
  window.hohFreshFacesResetAutoTimer = startAutoTimer;
  window.hohFreshFacesStopAutoTimer = stopAutoTimer;

  /* Auto-render once the host has loaded characters. The host fires
     a custom event after loadCharacters resolves; we also schedule
     a fallback timer so this still works if the event is missed.
     R148-Z41: Gate behind sign-in so anon visitors don't trigger the
     auto-timer, viewport observer, or any render work. Once the
     visitor submits a hero and 'hoh:signed-in' fires, the matching
     listener below kicks off the same boot sequence. */
  document.addEventListener('hoh:characters-loaded', function(){
    if(typeof window.hohIsHallSignedIn === 'function' && !window.hohIsHallSignedIn()){
      return;
    }
    window.hohRenderFreshFaces();
    /* Bind interaction listeners and kick off the first auto-timer
       once Fresh Faces is on screen with content. */
    setTimeout(function(){
      bindAutoTimerEvents();
      bindViewportObserver();
      startAutoTimer();
    }, 50);
  });

  /* R148-Z41: Wake Explore the Hall the instant the visitor signs in
     via hero submission OR the Your Hall sign-in form. This is the
     same boot sequence as the characters-loaded path, just deferred
     until sign-in. Idempotent — second-time calls just refresh. */
  document.addEventListener('hoh:signed-in', function(){
    try {
      window.hohRenderFreshFaces(true);
      setTimeout(function(){
        bindAutoTimerEvents();
        bindViewportObserver();
        startAutoTimer();
      }, 50);
    } catch(_){}
  });
  /* Fallback: try a few times in case we load before the host. */
  var tries = 0;
  var fallback = setInterval(function(){
    tries++;
    if(typeof window.hohGetAllCharacters === 'function' && window.hohGetAllCharacters().length){
      window.hohRenderFreshFaces();
      bindAutoTimerEvents();
      bindViewportObserver();
      startAutoTimer();
      clearInterval(fallback);
    } else if(tries > 30){
      clearInterval(fallback);
    }
  }, 250);

  /* ================================================================ */
  /* R148-Z29: God-mode Event Browser                                  */
  /* ================================================================ */
  /* Floating debug panel that appears only when hohIsGodMode() is
     true. Lists every event type with a Fire button. Click → forces
     that event to render immediately, bypassing the picker. Doesn't
     interfere with normal picker flow when god mode is off (the
     panel is never injected). Renders as a fixed-position toggleable
     panel in the bottom-left so it doesn't overlap the wallet pill
     (bottom-right). */
  var GOD_EVENT_REGISTRY = [
    /* Heart rolls — themed phases. Each fires by setting
       currentThemedPhase + setting phase, then re-rendering. */
    { key:'theme:freshFaces',    label:'Theme: Fresh Faces',         kind:'theme', themeIdx:-2 },
    { key:'theme:wandering',     label:'Theme: Wandering Heroes',    kind:'theme', themeIdx:0 },
    { key:'theme:spectrum',      label:'Heart Roll: Spectrum',       kind:'special', picker:'pickSpectrum' },
    /* Event rolls — gambling pool. */
    /* R591: every label now states the TEAM SIZE the fight actually
       fields, because that is the thing that changed and it is the
       thing being tested. "Brawl: Bar Fight (8)" survived from the
       eight-way free-for-all and would have sent Jan looking for eight
       portraits on a 4v4 board. A debug list that describes the old
       build is worse than no debug list: it makes correct behaviour
       look like a bug.
       Sizes can shrink at fire time when the hall cannot field the
       tier (hfDraftTeams widens a band, then shrinks), so these are
       the INTENDED sizes, not a promise. */
    { key:'championDuel',        label:'Fight: Champion 2v2',        kind:'special', picker:'pickChampionDuelEvent' },
    { key:'rareDuel',            label:'Fight: Rare 2v2',            kind:'special', picker:'pickRareDuelEvent' },
    { key:'epicDuel',            label:'Fight: Epic 3v3',            kind:'special', picker:'pickEpicDuelEvent' },
    { key:'legendaryDuel',       label:'Fight: Legendary 5v5',       kind:'special', picker:'pickLegendaryDuelEvent' },
    { key:'mythicDuel',          label:'Fight: Mythic 3v3',          kind:'special', picker:'pickMythicDuelEvent' },
    { key:'mythicDuelSpecial',   label:'Fight: Mythic Court 3v3 (T3)', kind:'special', picker:'pickMythicDuelSpecialEvent' },
    { key:'crownMatch',          label:'Fight: Crown Match 1v1',     kind:'special', picker:'pickCrownMatchEvent' },
    { key:'tavernBrawl',         label:'Fight: Tavern Brawl 3v3',    kind:'special', picker:'pickTavernBrawlEvent' },
    { key:'tavernBrawl4',        label:'Fight: Tavern Erupts 4v4',   kind:'special', picker:'pickTavernBrawl4Event' },
    { key:'barFight',            label:'Fight: Bar Fight 4v4',       kind:'special', picker:'pickBarFightEvent' },
    /* R591b: FEED YOUR OWN WAS NEVER IN THIS LIST, so the one event
       Jan rebuilt this wave could not be fired from here at all. It now
       has a real producer (pickFeedYourOwnEvent, shared with the stream
       ladder and the first-five guarantee) so Fire behaves like any
       other row. It needs a signed-in account holding at least one
       hero, and returns null otherwise, which the foot note explains.

       ⛔ Underdog, Rich Get Richer and Promotion are deliberately NOT
       listed. They are built INLINE inside pickFreshFacesEvent and have
       no picker function to name, so a row for them would render a Fire
       button that silently does nothing. An entry that cannot fire is
       worse than no entry: it reads as a broken feature. Give them real
       producers first, the way feedYourOwn just got one. */
    { key:'feedYourOwn',         label:'Stream: Feed Your Own (pick one)', kind:'special', picker:'pickFeedYourOwnEvent' },
    { key:'diceTrader',          label:'Dice Trader',                kind:'special', picker:'pickDiceTraderEvent' },
    { key:'classQuiz',           label:'Trivia: Class Quiz',         kind:'special', picker:'pickClassQuizEvent' },
    { key:'guessFB',             label:'Trivia: Fighter vs Barbarian', kind:'special', picker:'pickGuessFighterBarbarianEvent' },
    { key:'guessWW',             label:'Trivia: Wizard vs Warlock',  kind:'special', picker:'pickGuessWizardWarlockEvent' },
    { key:'guessRR',             label:'Trivia: Ranger vs Rogue',    kind:'special', picker:'pickGuessRangerRogueEvent' },
    { key:'guessEHE',            label:'Trivia: Elf vs Half-Elf',    kind:'special', picker:'pickGuessElfHalfElfEvent' },
    { key:'findCulprit',         label:'Find the Culprit (class)',   kind:'special', picker:'pickFindCulpritEvent' },
    { key:'findCulpritRace',     label:'Find the Culprit (race)',    kind:'special', picker:'pickFindCulpritRaceEvent' },
    { key:'findImposter',        label:'Find the Imposter',          kind:'special', picker:'pickFindImposterEvent' },
    /* R387 (Jan): Familiar Crowd removed from rotation AND from this debug list. */
    { key:'popularity',          label:'Popularity Contest',         kind:'special', picker:'pickPopularityEvent' },
    { key:'coinBet',             label:'Coin Bet',                   kind:'special', picker:'pickCoinBetEvent' },
    { key:'pickACard',           label:'Pick A Card',                kind:'special', picker:'pickPickACardEvent' },
    { key:'tollRoad',            label:'Toll Road',                  kind:'special', picker:'pickTollRoadEvent' },
    { key:'carnival',            label:'Carnival',                   kind:'special', picker:'pickCarnivalEvent' },
    { key:'commonThread',        label:'Common Thread',              kind:'special', picker:'pickCommonThreadEvent' },
    { key:'classWar',            label:'The Class War',              kind:'special', picker:'pickClassWarEvent' },
    { key:'travelHealer',        label:'Travel: Healer',             kind:'special', picker:'pickTravelingHealerEvent' },
    { key:'travelBard',          label:'Travel: Bard',               kind:'special', picker:'pickTravelingBardEvent' },
    { key:'miniSpinner',         label:'Mini Spinner',               kind:'special', picker:'pickMiniSpinnerEvent' },
    { key:'spinWheel',           label:'Wheel of Fortune',           kind:'special', picker:'pickSpinWheelEvent' },
    { key:'bustlingStreets',     label:'Bustling Streets',           kind:'special', picker:'pickBustlingStreetsEvent' },
    { key:'conveyerCommon',      label:'Conveyer: Common Walk',      kind:'special', picker:'pickConveyerCommonEvent' },
    { key:'conveyerUncommon',    label:'Conveyer: Rising Line',      kind:'special', picker:'pickConveyerUncommonEvent' },
    { key:'conveyerRare',        label:'Conveyer: Sapphire Run',     kind:'special', picker:'pickConveyerRareEvent' },
    { key:'conveyerEpic',        label:'Conveyer: Violet March',     kind:'special', picker:'pickConveyerEpicEvent' },
    { key:'conveyerLegendary',   label:'Conveyer: Golden Procession', kind:'special', picker:'pickConveyerLegendaryEvent' },
    { key:'conveyerMythic',      label:'Conveyer: Mythic Drift',     kind:'special', picker:'pickConveyerMythicEvent' },
    { key:'conveyerArcane',      label:'Conveyer: Arcane Current',   kind:'special', picker:'pickConveyerArcaneEvent' },
    { key:'conveyerMartial',     label:'Conveyer: Iron Parade',      kind:'special', picker:'pickConveyerMartialEvent' },
    { key:'conveyerWanderers',   label:'Conveyer: Long Caravan',     kind:'special', picker:'pickConveyerWanderersEvent' },
    /* R383 (Jan): The Echo removed from rotation AND from this debug list. */
    { key:'payGuide',            label:"Pay the Guide",              kind:'special', picker:'pickGuideEvent' },
    /* Bazaars — bypass cooldown via direct build. */
    { key:'mageBazaar',          label:'Bazaar: Mage',               kind:'bazaar', flavor:'mage' },
    { key:'thievesBazaar',       label:'Bazaar: Thieves',            kind:'bazaar', flavor:'thieves' },
    { key:'sageBazaar',          label:'Bazaar: Sage',               kind:'bazaar', flavor:'sage' },
    { key:'legendaryShop',       label:'Legendary Shop',             kind:'special', picker:'pickLegendaryShopEvent' }
  ];
  /* Explicit picker map — pickers live in this IIFE's local scope so
     they can't be resolved by name from a string. Map keys match
     entry.picker values. Each value is the actual function reference. */
  var _GOD_PICKER_MAP = {
    pickSpectrum:                       (typeof pickSpectrum === 'function')                       ? pickSpectrum : null,
    pickChampionDuelEvent:              (typeof pickChampionDuelEvent === 'function')              ? pickChampionDuelEvent : null,
    pickRareDuelEvent:                  (typeof pickRareDuelEvent === 'function')                  ? pickRareDuelEvent : null,
    pickEpicDuelEvent:                  (typeof pickEpicDuelEvent === 'function')                  ? pickEpicDuelEvent : null,
    pickLegendaryDuelEvent:             (typeof pickLegendaryDuelEvent === 'function')             ? pickLegendaryDuelEvent : null,
    pickMythicDuelEvent:                (typeof pickMythicDuelEvent === 'function')                ? pickMythicDuelEvent : null,
    pickMythicDuelSpecialEvent:         (typeof pickMythicDuelSpecialEvent === 'function')         ? function(){ return pickMythicDuelSpecialEvent({ force: true }); } : null,
    pickCrownMatchEvent:                (typeof pickCrownMatchEvent === 'function')                ? pickCrownMatchEvent : null,
    pickTavernBrawlEvent:               (typeof pickTavernBrawlEvent === 'function')               ? pickTavernBrawlEvent : null,
    pickTavernBrawl4Event:              (typeof pickTavernBrawl4Event === 'function')              ? pickTavernBrawl4Event : null,
    pickBarFightEvent:                  (typeof pickBarFightEvent === 'function')                  ? pickBarFightEvent : null,
    /* R591b: the registry names a picker, and _godFireEvent resolves it
       through THIS map, not off the window. A row whose picker is absent
       here flashes "PICKER MISSING" instead of firing. */
    pickFeedYourOwnEvent:               (typeof pickFeedYourOwnEvent === 'function')               ? pickFeedYourOwnEvent : null,
    pickDiceTraderEvent:                (typeof pickDiceTraderEvent === 'function')                ? pickDiceTraderEvent : null,
    pickClassQuizEvent:                 (typeof pickClassQuizEvent === 'function')                 ? pickClassQuizEvent : null,
    pickGuessFighterBarbarianEvent:     (typeof pickGuessFighterBarbarianEvent === 'function')     ? pickGuessFighterBarbarianEvent : null,
    pickGuessWizardWarlockEvent:        (typeof pickGuessWizardWarlockEvent === 'function')        ? pickGuessWizardWarlockEvent : null,
    pickGuessRangerRogueEvent:          (typeof pickGuessRangerRogueEvent === 'function')          ? pickGuessRangerRogueEvent : null,
    pickGuessElfHalfElfEvent:           (typeof pickGuessElfHalfElfEvent === 'function')           ? pickGuessElfHalfElfEvent : null,
    pickFindCulpritEvent:               (typeof pickFindCulpritEvent === 'function')               ? pickFindCulpritEvent : null,
    pickFindCulpritRaceEvent:           (typeof pickFindCulpritRaceEvent === 'function')           ? pickFindCulpritRaceEvent : null,
    pickFindImposterEvent:              (typeof pickFindImposterEvent === 'function')              ? pickFindImposterEvent : null,
    pickFamiliarCrowdEvent:             (typeof pickFamiliarCrowdEvent === 'function')             ? pickFamiliarCrowdEvent : null,
    pickPopularityEvent:                (typeof pickPopularityEvent === 'function')                ? pickPopularityEvent : null,
    pickCoinBetEvent:                   (typeof pickCoinBetEvent === 'function')                   ? pickCoinBetEvent : null,
    pickPickACardEvent:                 (typeof pickPickACardEvent === 'function')                 ? pickPickACardEvent : null,
    pickTollRoadEvent:                  (typeof pickTollRoadEvent === 'function')                  ? pickTollRoadEvent : null,
    pickCarnivalEvent:                  (typeof pickCarnivalEvent === 'function')                  ? pickCarnivalEvent : null,
    pickCommonThreadEvent:              (typeof pickCommonThreadEvent === 'function')              ? pickCommonThreadEvent : null,
    pickClassWarEvent:                  (typeof pickClassWarEvent === 'function')                  ? pickClassWarEvent : null,
    pickTravelingHealerEvent:           (typeof pickTravelingHealerEvent === 'function')           ? pickTravelingHealerEvent : null,
    pickTravelingBardEvent:             (typeof pickTravelingBardEvent === 'function')             ? pickTravelingBardEvent : null,
    pickMiniSpinnerEvent:               (typeof pickMiniSpinnerEvent === 'function')               ? pickMiniSpinnerEvent : null,
    pickSpinWheelEvent:                 (typeof pickSpinWheelEvent === 'function')                 ? pickSpinWheelEvent : null,
    pickBustlingStreetsEvent:           (typeof pickBustlingStreetsEvent === 'function')           ? pickBustlingStreetsEvent : null,
    pickConveyerCommonEvent:            (typeof pickConveyerCommonEvent === 'function')            ? pickConveyerCommonEvent : null,
    pickConveyerUncommonEvent:          (typeof pickConveyerUncommonEvent === 'function')          ? pickConveyerUncommonEvent : null,
    pickConveyerRareEvent:              (typeof pickConveyerRareEvent === 'function')              ? pickConveyerRareEvent : null,
    pickConveyerEpicEvent:              (typeof pickConveyerEpicEvent === 'function')              ? pickConveyerEpicEvent : null,
    pickConveyerLegendaryEvent:         (typeof pickConveyerLegendaryEvent === 'function')         ? pickConveyerLegendaryEvent : null,
    pickConveyerMythicEvent:            (typeof pickConveyerMythicEvent === 'function')            ? pickConveyerMythicEvent : null,
    pickConveyerArcaneEvent:            (typeof pickConveyerArcaneEvent === 'function')            ? pickConveyerArcaneEvent : null,
    pickConveyerMartialEvent:           (typeof pickConveyerMartialEvent === 'function')           ? pickConveyerMartialEvent : null,
    pickConveyerWanderersEvent:         (typeof pickConveyerWanderersEvent === 'function')         ? pickConveyerWanderersEvent : null,
    pickEchoEvent:                      (typeof pickEchoEvent === 'function')                      ? pickEchoEvent : null,
    pickGuideEvent:                     (typeof pickGuideEvent === 'function')                     ? pickGuideEvent : null,
    pickLegendaryShopEvent:             (typeof pickLegendaryShopEvent === 'function')             ? pickLegendaryShopEvent : null
  };
  /* Fire helper — calls the registered picker and stuffs the result
     into currentSpecial. Bypasses normal picker chain. */
  /* R148-Z30: Cooldown bypass — wipe per-event cooldown keys before
     firing so debug events fire even right after a real fire. The
     keys are namespaced in localStorage under hoh-cd-* — clearing
     them makes the cooldown checks return false on the next call. */
  function _godClearAllCooldowns(){
    try {
      var keys = [];
      for(var i = 0; i < localStorage.length; i++){
        var k = localStorage.key(i);
        if(k && (k.indexOf('hoh-cd-') === 0 || k.indexOf('hoh-mb-') === 0 || k.indexOf('hoh-duel-done-') === 0 || k.indexOf('hoh-brawl-done-') === 0)){
          keys.push(k);
        }
      }
      keys.forEach(function(k){
        /* Don't wipe sold-state — that's a per-purchase record, not
           a cooldown. Same for the all-events buff queue. */
        if(k.indexOf('hoh-mb-sold-') === 0) return;
        if(k.indexOf('hoh-mb-aeb-') === 0) return;
        localStorage.removeItem(k);
      });
    } catch(_){}
  }
  /* R591b (Jan): "make sure the debug menu allows me to reset my own
     daily allowance ... so i can test it for myself."

     Jan hit the wander target, the panel said OPENS AGAIN IN 21H 58M,
     and there was no way to test anything until dawn. This puts the day
     back to zero.

     ⛔ IT IS AN EXPLICIT PREFIX LIST, NEVER A `hoh-` WILDCARD. Wiping
     everything under that namespace would take `hoh-your-hall-token`
     (signing Jan out of the account the whole surface is gated on),
     `hoh-admin-pin`, `hoh-heart-bank-v1` (his wallet), `hoh-hearts`
     (every heart he has ever given), `hoh-dismissed` and
     `hoh-inventory-v1`. A debug convenience must never be able to
     destroy real state. The wallet has its own +1000 button in Shop. */
  var GOD_DAILY_PREFIXES = [
    'hoh-ff-hearts-',            /* the wander counter — THE thing that locks the day */
    'hoh-ff-claim-',             /* milestone claims */
    'hoh-ff-cta-claim-',
    'hoh-day-state-',            /* rollCount, eventFired, gambleSeen */
    'hoh-stream-fires-',         /* per-key daily fire caps */
    'hoh-cd-', 'hoh-fresh-cd-',  /* every cooldown */
    'hoh-mb-',                   /* bazaars, incl. sold-state and both paid queues */
    'hoh-duel-done-', 'hoh-brawl-done-', 'hoh-coin-done-',
    'hoh-dice-trader-done-', 'hoh-pick-card-done-', 'hoh-wheel-done-',
    'hoh-hero-circle-done-', 'hoh-champion-meeting-shown-',
    'hoh-underdog-shown-', 'hoh-daily-tinder-', 'hoh-anon-tour-',
    'hoh-legshop-',              /* daily shop purchases + refresh count */
    'hoh-marrow-accepted-',
    'hoh-bgscene-daily', 'hoh-hearts-daily',
    'hoh-fresh-phase', 'hoh-fresh-phase-date',
    /* rotation memory, so a reset tester sees fresh faces rather than
       the tail of yesterday's seen-set */
    'hoh-fresh-seen-specials', 'hoh-fresh-seen-themes', 'hoh-fresh-seen-chars'
  ];
  /* Belt and braces: even if a prefix above ever widens by accident,
     these can never be removed. */
  var GOD_DAILY_NEVER = [
    'hoh-your-hall-token', 'hoh-admin-pin', 'hoh-heart-bank-v1',
    'hoh-hearts', 'hoh-dismissed', 'hoh-inventory-v1'
  ];
  function _godResetDailyExplore(){
    var wiped = 0;
    try {
      var kill = [];
      for(var i = 0; i < localStorage.length; i++){
        var k = localStorage.key(i);
        if(!k) continue;
        if(GOD_DAILY_NEVER.indexOf(k) !== -1) continue;
        for(var p = 0; p < GOD_DAILY_PREFIXES.length; p++){
          if(k.indexOf(GOD_DAILY_PREFIXES[p]) === 0){ kill.push(k); break; }
        }
      }
      kill.forEach(function(k){ try { localStorage.removeItem(k); wiped++; } catch(_){} });
    } catch(_){}
    /* In-memory state has to follow, or the page keeps believing the day
       is over until a reload. _ffQuestClosing is the latch that paints
       the "opens again at dawn" panel. */
    try { _ffQuestClosing = false; } catch(_){}
    try { seenChars = Object.create(null); seenSpecials = Object.create(null); } catch(_){}
    try { currentSpecial = null; currentThemedPhase = null; } catch(_){}
    try { recentEventKeys = []; } catch(_){}
    /* The two memo caches are keyed on the day, and getFfHeartsToday
       would otherwise keep returning the pre-reset total from memory and
       re-lock the gate the instant it repainted. These are the exact
       two the real midnight rollover clears (_dayWatchTick), with the
       same sentinel values it uses. */
    try { _ymdTodayCache = ''; _ymdTodayCacheTs = 0; } catch(_){}
    try { _ffHeartsCache = -1; _ffHeartsCacheKey = ''; } catch(_){}
    /* Repaint from scratch so the gate panel is replaced by a live roll. */
    try { if(window.hohRenderFreshFaces) window.hohRenderFreshFaces(); } catch(_){}
    try { if(typeof paintQuestProgress === 'function') paintQuestProgress(); } catch(_){}
    try { if(typeof startAutoTimer === 'function') setTimeout(startAutoTimer, 0); } catch(_){}
    return wiped;
  }
  window.hohGodResetDailyExplore = _godResetDailyExplore;

  function _godFireEvent(entry){
    try {
      /* Stop the auto-refresh timer FIRST so it can't tick over and
         overwrite currentSpecial after we set it. The user reported
         "Fire doesn't fire or reset" — the most likely cause was the
         auto-bar reaching 100% during click latency and rolling a
         new event right over the debug event. */
      try {
        if(typeof stopAutoTimer === 'function') stopAutoTimer();
      } catch(_){}
      /* Bypass all cooldowns + clear queued specials so debug fires
         even right after a previous one. */
      _godClearAllCooldowns();
      try {
        window.__hohQueuedSpecial = null;
        window.__hohQueueUnlimitedNext = false;
      } catch(_){}
      /* Clear in-flight phase + snapshot state so the next render
         paints exactly what we set. */
      try {
        renderedSnapshotIds = null;
        snapshotPhaseKey = null;
        cachedWanderingPool = null;
        cachedWanderingPhase = null;
        pageHearts.spent = 0;
        pageHearts.key = null;
        pageHearts.budget = 0;
        pageHearts.max = 0;
        pageHeartsCountedSet = Object.create(null);
        try { consumedThisPage = Object.create(null); } catch(_){}
        pageAutoRefreshScheduled = false;
      } catch(_){}

      if(entry.kind === 'bazaar'){
        if(typeof _buildBazaarEvent === 'function'){
          var bzEv = _buildBazaarEvent(entry.flavor);
          if(bzEv){
            currentSpecial = bzEv;
            _godEnsureVisibleAndRender();
            _godFlash('Fired: ' + entry.label);
          } else {
            _godFlash('NULL: ' + entry.label, true);
          }
        }
        return;
      }
      if(entry.kind === 'theme'){
        /* Force a themed-phase render. -2 = Fresh Faces (phase 0),
           others use the themed phase array. */
        currentSpecial = null;
        if(entry.themeIdx === -2){
          if(typeof setPhase === 'function') setPhase(0);
          currentThemedPhase = null;
        } else {
          if(typeof setPhase === 'function') setPhase(1);
          currentThemedPhase = (typeof THEMED_PHASES !== 'undefined') ? THEMED_PHASES[entry.themeIdx] : null;
        }
        _godEnsureVisibleAndRender();
        _godFlash('Fired: ' + entry.label);
        return;
      }
      var fn = _GOD_PICKER_MAP[entry.picker];
      if(typeof fn !== 'function'){
        try { console.warn('[GodDebug] picker not in map:', entry.picker); } catch(_){}
        _godFlash('PICKER MISSING: ' + entry.picker, true);
        return;
      }
      /* R148-Z31: Force-bypass gates inside the pickers themselves.
         Some pickers (notably pickLegendaryShopEvent) accept opts.force
         to skip refresh-threshold + cooldown + daily-cap gates. Pass
         force:true so debug fires ALWAYS produce an event even if the
         user already triggered the natural one today. Other pickers
         simply ignore the opts arg. */
      var ev = null;
      try {
        ev = hfPickRequestedEvent(fn, { force: true });
      } catch(_){
        try { ev = hfPickRequestedEvent(fn); } catch(__){}
      }
      if(!ev){
        try { console.warn('[GodDebug] picker returned null:', entry.picker); } catch(_){}
        _godFlash('NULL: ' + entry.label + ' (pool too thin or affordability fail)', true);
        return;
      }
      currentSpecial = ev;
      _godEnsureVisibleAndRender();
      _godFlash('Fired: ' + entry.label);
    } catch(err){
      try { console.warn('[GodDebug] fire threw', err); } catch(_){}
      _godFlash('THREW: ' + (err && err.message || err), true);
    }
  }
  /* Force the Hall section visible and scroll it into view, then
     re-render. Without this, firing an event while the user is
     scrolled away or while the section is hidden (e.g. between
     auto-refresh cycles) makes the fire feel like nothing happened.
     R148-Z33: Render errors are now surfaced via _godFlash so a
     broken renderer (like the Mage Bazaar's _bazaarHostHtml
     ReferenceError I just fixed) reports the actual error message
     instead of failing silently — the user spent time wondering why
     "fire" did nothing when the page console was the only place the
     real error showed. */
  function _godEnsureVisibleAndRender(){
    try {
      var ff = document.getElementById('hoh-fresh-faces');
      if(ff){
        if(ff.style.display === 'none') ff.style.display = '';
        ff.classList.remove('hoh-fresh-fading-out');
      }
      if(typeof window.hohRenderFreshFaces === 'function'){
        try {
          window.hohRenderFreshFaces();
        } catch(renderErr){
          try { console.warn('[GodDebug] render threw', renderErr); } catch(_){}
          _godFlash('RENDER ERROR: ' + (renderErr && renderErr.message || renderErr), true);
          return;
        }
      }
      if(ff && typeof ff.scrollIntoView === 'function'){
        setTimeout(function(){
          try { ff.scrollIntoView({ behavior: 'smooth', block: 'center' }); } catch(_){}
        }, 60);
      }
    } catch(_){}
  }
  /* Toast-style feedback for the debug menu. Renders a small chip in
     the panel so the user knows what fired and why a null result
     happened. Auto-clears after 3s. */
  function _godFlash(msg, isError){
    try {
      var existing = document.getElementById('hoh-god-debug-flash');
      if(existing) existing.parentNode.removeChild(existing);
      var flash = document.createElement('div');
      flash.id = 'hoh-god-debug-flash';
      flash.className = 'hoh-god-debug-flash' + (isError ? ' hoh-god-debug-flash-error' : '');
      flash.textContent = msg;
      var panel = document.getElementById('hoh-god-debug-panel');
      if(panel) panel.appendChild(flash);
      else document.body.appendChild(flash);
      setTimeout(function(){
        try { if(flash.parentNode) flash.parentNode.removeChild(flash); } catch(_){}
      }, 3000);
    } catch(_){}
  }
  /* Build the panel DOM. Lazy — only constructs when god mode is
     actually active so non-debug users never pay the DOM cost. */
  function _godBuildPanel(){
    if(document.getElementById('hoh-god-debug-panel')) return;
    var panel = document.createElement('div');
    panel.id = 'hoh-god-debug-panel';
    panel.className = 'hoh-god-debug-panel hoh-god-debug-collapsed';
    /* R166: Tabbed structure, everything debug under this single gear icon. Since 2026-10-28 it holds only what the
       Hall still has: Force Winner (the raffle override), Review (new pictures and Hall cleanup) and Shop (the card
       text writer, free pack opens, the Regenerate switch and the badge picker). */
    panel.innerHTML =
      '<button type="button" class="hoh-god-debug-toggle" id="hoh-god-debug-toggle" aria-label="Toggle debug panel">' +
        '<span class="hoh-god-debug-toggle-icon">&#9881;&#xFE0F;</span>' +
        '<span class="hoh-god-debug-toggle-label">DEBUG</span>' +
      '</button>' +
      '<div class="hoh-god-debug-body">' +
        '<div class="hoh-god-debug-tabs" role="tablist">' +
          '<button type="button" class="hoh-god-debug-tab hoh-god-debug-tab-active" data-tab="force" role="tab" aria-selected="true">Force Winner</button>' +
          '<button type="button" class="hoh-god-debug-tab" data-tab="review" role="tab" aria-selected="false">Review</button>' +
          '<button type="button" class="hoh-god-debug-tab" data-tab="shop" role="tab" aria-selected="false">Shop</button>' +
        '</div>' +
        '<div class="hoh-god-debug-section" data-section="force">' +
          '<div class="hoh-god-debug-force-current" id="hoh-god-debug-force-current"></div>' +
          '<input type="search" class="hoh-god-debug-search hoh-god-debug-force-search" id="hoh-god-debug-force-search" placeholder="Search heroes by name, race, class, or player…" autocomplete="off" inputmode="search">' +
          '<div class="hoh-god-debug-force-list" id="hoh-god-debug-force-list" role="listbox" aria-label="Hero search results"></div>' +
          '<div class="hoh-god-debug-foot">Pick a hero → the next Simulate the Raffle roll lands on them. One-shot — auto-clears after the spin. Debug UI hides until 60s after the winner card shows.</div>' +
        '</div>' +
        '<div class="hoh-god-debug-section" data-section="review" hidden>' +
          '<button type="button" id="hoh-rev-open" style="width:100%;margin:6px 0;padding:10px;cursor:pointer;border-radius:9px;background:#16301f;color:#8ee0a8;border:1px solid #2f6f47;font-weight:700;font-size:14px">Open hero review</button>' +
          '<button type="button" id="hoh-cleanup-open" class="hoh-god-shopbtn">Open Hall cleanup</button>' +
          '<div class="hoh-god-debug-foot">Hero review is for new pictures. Hall cleanup is the short list of public heroes flagged for duplicate, photo, or third-party art. Hide removes one from the public Hall and game. Its owner still sees it in Your Hall.</div>' +
        '</div>' +
        /* R247: Shop debug actions, relocated out of the shop UI. Wired to
           window.hohShopDebug (defined in hoh-shop.js). */
        '<div class="hoh-god-debug-section" data-section="shop" hidden>' +
          '<button type="button" id="hoh-god-regen-toggle" class="hoh-god-shopbtn"></button>' +
          '<button type="button" id="hoh-god-shop-free" class="hoh-god-shopbtn"></button>' +
          '<button type="button" id="hoh-god-shop-gen" class="hoh-god-shopbtn">Write missing card texts</button>' +
          '<button type="button" id="hoh-god-shop-badge" class="hoh-god-shopbtn">Test badge picker</button>' +
          /* ⛔ R572: this line carried two &mdash; entities and a semicolon,
             which is three breaches of the voice rules in one sentence. They
             survived because the em dash ban scans for the literal character
             and an HTML entity is not that character. harness/copy.cjs reads
             entities now. It also names the stop control, because an
             affordance nobody is told about may as well not exist. */
          '<div class="hoh-god-debug-foot">Free opens open packs without spending anything. Write missing card texts gives every card that still says “A brave hero of the Hall.” its own title and text from what was submitted. Words an owner wrote are never touched. Press it again to stop, and again later to carry on. Badge picker reopens the apply menu.</div>' +
        '</div>' +
        /* ⛔ ====== R609 · GET EVERY DEBUG SURFACE OFF THE PHONE ======
           Jan: "on mobile in debug mode, I want a way to go in the gear and
           hide all the debug mode stuff until I refresh the browser and then
           it can show back up, because right now it's nearly impossible for me
           to do anything."
           Two panels sit on a phone at once — this gear top right and the
           Battlegrounds kit bottom left — and between them they cover the
           corners every real screen puts its controls in. His Edit Your Hero
           screenshot has the kit sitting on the form.
           IT IS DELIBERATELY NOT PERSISTED. Every other switch in this panel
           writes localStorage, and that is wrong here: the exit from this one
           is hidden BY it, so a remembered choice would mean a god-mode
           session with no way back to the gear short of clearing site data.
           A reload is the undo, it is the undo Jan asked for, and the flash
           says so out loud. It lives OUTSIDE the tab sections on purpose, so
           it is one tap away from whichever tab happens to be open. */
        '<button type="button" id="hoh-god-hide-all" class="hoh-god-shopbtn hoh-god-hide-all">Hide all debug until refresh</button>' +
      '</div>';
    document.body.appendChild(panel);
    var hideAllBtn = panel.querySelector('#hoh-god-hide-all');
    if(hideAllBtn){
      hideAllBtn.onclick = function(){
        try { document.body.classList.add('hoh-debug-off'); } catch(_){}
        /* The flash lives inside the panel this click is hiding, so it would
           never be read. Say it where it can be: the Hall's own toast. */
        try {
          if(typeof window.hohShowHallToast === 'function'){
            window.hohShowHallToast('Debug hidden. Refresh the page to bring it back.');
          }
        } catch(_){}
      };
    }
    /* Jan, 2026-10-28: "we dont have battlegrounds here anymore, or gear or events for the explore the hall so remove
       and clean up the debug menu", "same thing with the gear", "and the heart system". The Battle, Gear and Events
       tabs and the hearts and Battlegrounds-entry grants are gone, and god mode no longer loads the Battlegrounds files
       for them. The floating Battlegrounds kit (hoh-contract-debug.js) remembered an on switch that lived in the Battle
       tab, so it is turned off here once: with the tab gone, a kit left on would have had no way off. */
    try { localStorage.setItem('hoh-contract-debug-on', '0'); } catch(_){}
    try { if(window.hohContractDebugPanel && window.hohContractDebugPanel.set) window.hohContractDebugPanel.set(false); } catch(_){}
    /* Toggle expand/collapse. */
    var toggle = panel.querySelector('#hoh-god-debug-toggle');
    if(toggle){
      toggle.addEventListener('click', function(){
        panel.classList.toggle('hoh-god-debug-collapsed');
      });
    }
    /* Tab switching — toggle .hoh-god-debug-tab-active + section visibility. */
    var tabs = panel.querySelectorAll('.hoh-god-debug-tab');
    var sections = panel.querySelectorAll('.hoh-god-debug-section');
    function activateTab(tabName){
      for(var ti = 0; ti < tabs.length; ti++){
        var on = tabs[ti].getAttribute('data-tab') === tabName;
        tabs[ti].classList.toggle('hoh-god-debug-tab-active', on);
        tabs[ti].setAttribute('aria-selected', on ? 'true' : 'false');
      }
      for(var si = 0; si < sections.length; si++){
        var match = sections[si].getAttribute('data-section') === tabName;
        if(match){ sections[si].removeAttribute('hidden'); }
        else { sections[si].setAttribute('hidden', ''); }
      }
    }
    for(var t = 0; t < tabs.length; t++){
      tabs[t].addEventListener('click', function(ev){
        var name = ev.currentTarget && ev.currentTarget.getAttribute('data-tab');
        if(name) activateTab(name);
      });
    }
    /* R247: Shop tab actions, delegated to the shop module via
       window.hohShopDebug. The shop script always loads on the Hall page, and
       these are only clicked in god mode, so the functions are present by then;
       every call is still guarded. */
    /* R381 (Jan): the card viewer's "Regenerate card text" button hides even
       in god mode until this switch turns it on. Off by default. */
    var gregen = panel.querySelector('#hoh-god-regen-toggle');
    function _syncRegen(){
      if(!gregen) return;
      var on = false;
      try { on = localStorage.getItem('hoh-god-regen') === '1'; } catch(_){}
      gregen.textContent = on ? 'Regenerate button ON' : 'Regenerate button OFF';
      gregen.classList.toggle('hoh-god-shop-on', on);
    }
    if(gregen && !gregen.__wired){
      gregen.__wired = 1;
      gregen.addEventListener('click', function(){
        var on = false;
        try { on = localStorage.getItem('hoh-god-regen') === '1'; } catch(_){}
        try { localStorage.setItem('hoh-god-regen', on ? '0' : '1'); } catch(_){}
        _syncRegen();
      });
      _syncRegen();
    }
    var shopFreeBtn  = panel.querySelector('#hoh-god-shop-free');
    var shopGenBtn   = panel.querySelector('#hoh-god-shop-gen');
    var shopBadgeBtn = panel.querySelector('#hoh-god-shop-badge');
    function _shopDbg(){ return (typeof window !== 'undefined' && window.hohShopDebug) || null; }
    function _syncShopFree(){
      if(!shopFreeBtn) return;
      var d = _shopDbg();
      var on = !!(d && typeof d.isFree === 'function' && d.isFree());
      shopFreeBtn.textContent = on ? '∞ Free opens ON' : '∞ Free opens OFF';
      shopFreeBtn.classList.toggle('hoh-god-shop-on', on);
    }
    _syncShopFree();
    if(shopFreeBtn){
      shopFreeBtn.addEventListener('click', function(){
        var d = _shopDbg();
        if(d && typeof d.toggleFree === 'function') d.toggleFree();
        _syncShopFree();
      });
    }
    if(shopGenBtn){
      shopGenBtn.addEventListener('click', function(){
        var d = _shopDbg();
        if(d && typeof d.generate === 'function') d.generate(shopGenBtn);
        else if(typeof window.hohShowHallToast === 'function') window.hohShowHallToast('Open the Shop tab once, then try again.');
      });
    }
    if(shopBadgeBtn){
      shopBadgeBtn.addEventListener('click', function(){
        var d = _shopDbg();
        if(d && typeof d.badgePicker === 'function') d.badgePicker();
      });
    }
    /* -------- Force Winner section -------- */
    var forceCurrentEl = panel.querySelector('#hoh-god-debug-force-current');
    var forceListEl    = panel.querySelector('#hoh-god-debug-force-list');
    var forceSearchEl  = panel.querySelector('#hoh-god-debug-force-search');
    function _godEsc(s){
      return String(s == null ? '' : s)
        .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
    }
    function _godAllChars(){
      try {
        if(typeof window.hohGetAllCharacters === 'function'){
          var all = window.hohGetAllCharacters() || [];
          return all.filter(function(c){ return c && c.id; });
        }
      } catch(_){}
      return [];
    }
    function _godFindCharById(id){
      if(!id) return null;
      var all = _godAllChars();
      for(var i = 0; i < all.length; i++){
        if(String(all[i].id) === String(id)) return all[i];
      }
      return null;
    }
    function _godRenderForceCurrent(){
      if(!forceCurrentEl) return;
      var fid = (typeof window.hohGetForcedWinnerId === 'function') ? window.hohGetForcedWinnerId() : null;
      if(!fid){
        forceCurrentEl.innerHTML = '<span class="hoh-god-debug-force-empty">No override. Next roll is random.</span>';
        return;
      }
      var c = _godFindCharById(fid);
      var name = (c && c.name) ? c.name : '(hero not in current pool)';
      forceCurrentEl.innerHTML =
        '<span class="hoh-god-debug-force-current-label">FORCED for next roll:</span>' +
        '<span class="hoh-god-debug-force-current-name">' + _godEsc(name) + '</span>' +
        '<button type="button" class="hoh-god-debug-force-clear" id="hoh-god-debug-force-clear">Clear</button>';
      var clearBtn = panel.querySelector('#hoh-god-debug-force-clear');
      if(clearBtn){
        clearBtn.addEventListener('click', function(){
          if(typeof window.hohClearForcedWinner === 'function') window.hohClearForcedWinner();
        });
      }
    }
    function _godRenderForceResults(query){
      if(!forceListEl) return;
      var all = _godAllChars();
      var q = String(query || '').trim().toLowerCase();
      var filtered;
      if(!q){
        /* Empty query: top 25 alphabetical so the user has a useful default. */
        filtered = all.slice().sort(function(a, b){
          var an = (a.name || '').toLowerCase();
          var bn = (b.name || '').toLowerCase();
          return an < bn ? -1 : an > bn ? 1 : 0;
        }).slice(0, 25);
      } else {
        filtered = all.filter(function(c){
          return (
            (c.name || '').toLowerCase().indexOf(q) !== -1 ||
            (c.race || '').toLowerCase().indexOf(q) !== -1 ||
            (c.character_class || '').toLowerCase().indexOf(q) !== -1 ||
            (c.player_name || '').toLowerCase().indexOf(q) !== -1
          );
        }).slice(0, 50);
      }
      if(!filtered.length){
        forceListEl.innerHTML = '<div class="hoh-god-debug-force-empty">No heroes match “' + _godEsc(query) + '”.</div>';
        return;
      }
      var fid = (typeof window.hohGetForcedWinnerId === 'function') ? window.hohGetForcedWinnerId() : null;
      forceListEl.innerHTML = filtered.map(function(c){
        var thumb = c.image_thumb_url || c.image_url || '';
        var media = thumb
          ? '<img class="hoh-god-debug-force-img" src="' + _godEsc(thumb) + '" alt="" loading="lazy" decoding="async" referrerpolicy="no-referrer">'
          : '<span class="hoh-god-debug-force-letter">' + _godEsc((c.name || '?').charAt(0)) + '</span>';
        var meta = _godEsc([c.race || '', c.character_class || ''].filter(Boolean).join(' '));
        var tickets = (c.tickets | 0) || 1;
        var isSelected = (fid && String(fid) === String(c.id));
        var selCls = isSelected ? ' hoh-god-debug-force-result-selected' : '';
        return (
          '<button type="button" class="hoh-god-debug-force-result' + selCls + '" data-id="' + _godEsc(c.id) + '" role="option" aria-selected="' + (isSelected ? 'true' : 'false') + '">' +
            media +
            '<span class="hoh-god-debug-force-body">' +
              '<span class="hoh-god-debug-force-name">' + _godEsc(c.name || 'Unnamed') + '</span>' +
              '<span class="hoh-god-debug-force-meta">' + meta + (c.player_name ? ' · by ' + _godEsc(c.player_name) : '') + '</span>' +
            '</span>' +
            '<span class="hoh-god-debug-force-tickets" aria-label="Raffle tickets">' + tickets + ' tk</span>' +
          '</button>'
        );
      }).join('');
      var btns = forceListEl.querySelectorAll('.hoh-god-debug-force-result');
      for(var bi = 0; bi < btns.length; bi++){
        btns[bi].addEventListener('click', function(ev){
          var id = ev.currentTarget && ev.currentTarget.getAttribute('data-id');
          if(!id) return;
          if(typeof window.hohSetForcedWinner === 'function'){
            window.hohSetForcedWinner(id);
          }
        });
      }
    }
    /* Initial render + debounced search. */
    _godRenderForceCurrent();
    _godRenderForceResults('');
    if(forceSearchEl){
      var forceDebounce = 0;
      forceSearchEl.addEventListener('input', function(){
        clearTimeout(forceDebounce);
        var q = forceSearchEl.value;
        forceDebounce = setTimeout(function(){ _godRenderForceResults(q); }, 80);
      });
      forceSearchEl.addEventListener('keydown', function(ev){
        if(ev.key === 'Enter'){
          var first = forceListEl && forceListEl.querySelector('.hoh-god-debug-force-result');
          if(first) first.click();
        }
      });
    }
    /* React to forced-winner state changes from anywhere (e.g., consumed
       by the picker after a spin). Re-render current + result highlight. */
    document.addEventListener('hoh:forced-winner-changed', function(){
      try {
        _godRenderForceCurrent();
        _godRenderForceResults(forceSearchEl ? forceSearchEl.value : '');
      } catch(_){}
    });

    /* HALLFLOWS: Jan's one-tap public-Hall cleanup list. This is deliberately
       a fixed, reviewed list rather than a fuzzy runtime classifier. The
       source audit lives in generated/art-check/art-grades.json; only rows
       graded photo or explicitly called third-party, plus the named duplicate
       cases Jan asked to inspect, are represented here. The screen is mounted
       only inside god mode. Hide reuses the Hall's is_hidden contract: public
       Hall, packs and game stop seeing the hero, while the owner's Your Hall
       query still does. It never deletes a row and never asks an owner. */
    (function(){
      var opener = panel.querySelector('#hoh-cleanup-open');
      if(!opener) return;
      var FLAGS = [
        ['4e242162-c35e-46fa-9f72-8f13ee04b2d5','Arvellon Thorngard','Photo'],
        ['49ed8ba8-7e8e-4ad5-9267-87395c1677e5','Aurelia of the Willow','Photo'],
        ['6317710f-c814-4a7f-8c79-755758450ec6','Aurielle Hawthorne','Photo'],
        ['ea6ff498-fbec-4b56-b3bc-9541746e2b38','Black Knight','Photo'],
        ['7814eecb-ed36-451e-ae9b-9a76aca13165','Brolius','Photo'],
        ['e9dbdc6d-5451-426a-ad5b-1afc267b40c3','Cassius Horoli','Photo'],
        ['49cb3ee0-7ca6-465c-99cd-f73f1d9dffe8','Ciel','Third-party art'],
        ['c16868a2-94ce-4043-a6e1-1e706190ad82','Circe','Photo'],
        ['6b33121b-a4e2-4756-87ed-8119fd1ec0a1','Corva Breyr','Duplicate'],
        ['941cba9b-0a79-4313-ba02-745ef7470bab','Corva Breyr','Duplicate'],
        ['16cbd4e9-edc9-4938-80d4-4ae509417e48','Delethor Elwing','Photo'],
        ['87300db3-c2a0-4d7e-8d4b-5e319a1829e3','Devrias','Photo'],
        ['33e28488-bdd1-4851-83bb-47f86e17c2c3','E.S.','Photo'],
        ['c534aa0e-b6f4-437c-b2aa-f9c3de7567c2','Elowyn Norvael','Third-party art'],
        ['156a87b4-a676-4f90-978f-5c06529e6c85','Gwenilian Alloy','Possible duplicate'],
        ['f408679b-9278-4a39-b132-3bc41666b92c','Gwenilian Aloy','Possible duplicate'],
        ['afdb8cb0-bed9-44ea-8ef9-08575d498389','Haal','Photo'],
        ['4449ad78-e895-4b45-a264-e55d546c45e1','Jack','Photo'],
        ['c0ebe21d-12c4-478a-998a-916acc6fa123','Kaelin','Wrong second portrait'],
        ['309cd9ab-1320-45e2-a794-e999135aaef9','Kyoko','Third-party art'],
        ['12baf2c2-625f-4f1c-93fb-ad0b2009adb1','Lucifer','Third-party art'],
        ['c785d6a2-712c-4920-98a4-731d6384829d','Mortimer Lichfield','Photo'],
        ['a3d205b1-ea6f-4adb-801e-5513ffd9d28b','Muffin','Photo'],
        ['b01a79bc-a04c-471b-bd18-bc2f3728c0ca','Nera','Photo'],
        ['1af6b80f-a0b8-42fc-b234-d95778633d59','Owen McGuier','Duplicate'],
        ['ab0f0d30-372c-4b99-ab94-ce27d363351f','Owen McGuier','Duplicate'],
        ['f79f8024-88d1-4972-9424-25d7603c4e8e','Pendragon','Third-party art'],
        ['c24eb0ed-326f-45be-b9a0-690a135e986b','Prometheon','Third-party art'],
        ['7d6ce39d-1fc0-42e7-889d-c5955491cd83','Rook Blackwell','Third-party art'],
        ['6d071bf8-79ed-468a-8de5-336a6e29dd76','Ruger Wyldog','Photo'],
        ['0d40c7aa-ba1a-456e-8b98-94c546f00c19','Silver','Photo'],
        ['2bb8e37a-7fd6-435e-8be1-f65c80dcf46a','Soraia','Photo'],
        ['8109a5d3-dfb2-4e06-b0df-325140acffee','Soulreaver','Photo'],
        ['73d14d33-2849-4a58-8141-84f575e6f353','Stellbeard Ironfoot','Third-party art'],
        ['e83e6501-3c11-43fb-8bdc-3c3aea9a1954','Tasselberry Wanderfoot','Photo'],
        ['ac8a3e98-752a-4cb5-bc80-069de4379061','Thea','Photo'],
        ['44198406-0393-47cb-94b5-a086b8c63817','Thistle','Photo'],
        ['39b2e1ca-95e7-4620-9240-1ec0fd7d1f4a','Vexx','Photo'],
        ['6a2c8253-899a-41c3-a618-1b8866f80999','Zahra "The Whispering Blade"','Photo'],
        ['5657786e-9443-4914-8483-6015c18f5212','Zasha','Photo'],
        ['f023b262-7020-4c6e-9214-51deda7ea98e','Zira','Photo']
      ];
      var flagById = Object.create(null), rows = [], dialog = null, busyId = '';
      FLAGS.forEach(function(f){ flagById[String(f[0])] = { id:f[0], fallbackName:f[1], reason:f[2] }; });
      function esc(v){ return _godEsc(String(v == null ? '' : v)); }
      function ensureDialog(){
        if(dialog && dialog.isConnected) return dialog;
        dialog = document.createElement('dialog');
        dialog.id = 'hoh-cleanup-dialog';
        dialog.className = 'hoh-cleanup-dialog';
        dialog.setAttribute('aria-labelledby','hoh-cleanup-title');
        dialog.innerHTML =
          '<div class="hoh-cleanup-head"><div><div class="hoh-cleanup-kicker">PRIVATE HALL TOOL</div><h2 id="hoh-cleanup-title">Hall cleanup</h2></div><button type="button" class="hoh-cleanup-close" aria-label="Close Hall cleanup">Close</button></div>'+
          '<p class="hoh-cleanup-intro">These heroes were flagged for a photo, third-party art, or a likely duplicate. Check the picture, then tap Hide once. Hidden heroes leave the public Hall and game, but their owner still sees them in Your Hall.</p>'+
          '<div class="hoh-cleanup-status" role="status" aria-live="polite"></div>'+
          '<div class="hoh-cleanup-list"></div>';
        document.body.appendChild(dialog);
        dialog.querySelector('.hoh-cleanup-close').onclick = function(){ dialog.close(); };
        dialog.addEventListener('click',function(ev){ if(ev.target === dialog) dialog.close(); });
        dialog.querySelector('.hoh-cleanup-list').addEventListener('click',function(ev){
          var b = ev.target && ev.target.closest && ev.target.closest('[data-cleanup-hide]');
          if(b) hideOne(b.getAttribute('data-cleanup-hide'), b);
        });
        return dialog;
      }
      function status(text, bad){
        var el = dialog && dialog.querySelector('.hoh-cleanup-status');
        if(!el) return;
        el.textContent = text || '';
        el.classList.toggle('is-error', !!bad);
      }
      function paint(){
        var d = ensureDialog(), list = d.querySelector('.hoh-cleanup-list');
        opener.textContent = 'Open Hall cleanup' + (rows.length ? ' (' + rows.length + ')' : '');
        if(!rows.length){
          list.innerHTML = '<div class="hoh-cleanup-empty"><b>Nothing is waiting.</b><span>Every flagged hero is already hidden or no longer public.</span></div>';
          return;
        }
        list.innerHTML = rows.map(function(c){
          var flag = flagById[String(c.id)] || {};
          var img = c.image_thumb_url || c.image_url || '';
          var meta = [c.race,c.character_class].filter(Boolean).join(' · ');
          return '<article class="hoh-cleanup-row" data-cleanup-id="'+esc(c.id)+'">'+
            (img?'<img class="hoh-cleanup-thumb" src="'+esc(img)+'" alt="" loading="lazy">':'<div class="hoh-cleanup-thumb" aria-hidden="true"></div>')+
            '<div class="hoh-cleanup-copy"><h3>'+esc(c.name || flag.fallbackName || 'Unnamed')+'</h3><p>'+esc(meta || 'Hero')+'</p><span>'+esc(flag.reason || 'Needs review')+'</span></div>'+
            '<button type="button" class="hoh-cleanup-hide" data-cleanup-hide="'+esc(c.id)+'"'+(busyId===String(c.id)?' disabled':'')+'>'+(busyId===String(c.id)?'Hiding…':'Hide')+'</button>'+
          '</article>';
        }).join('');
      }
      function load(){
        var d = ensureDialog(), list = d.querySelector('.hoh-cleanup-list');
        list.innerHTML = '<div class="hoh-cleanup-empty"><b>Loading the review list…</b></div>';
        status('');
        var sb = window.hohSupabaseClient;
        if(!sb){ rows=[]; list.innerHTML='<div class="hoh-cleanup-empty"><b>The Hall connection is not ready.</b><span>Close this list, wait a moment, then try again.</span></div>'; return; }
        sb.from('characters')
          .select('id,name,race,character_class,player_name,image_url,image_thumb_url,quest_meta,is_hidden,approved')
          .in('id', FLAGS.map(function(f){ return f[0]; }))
          .eq('approved', true)
          .then(function(r){
            if(r && r.error){ rows=[]; list.innerHTML='<div class="hoh-cleanup-empty"><b>The list could not load.</b><span>'+esc(r.error.message || 'Try again.')+'</span></div>'; return; }
            rows = (r.data || []).filter(function(c){ return c && c.is_hidden !== true && flagById[String(c.id)]; });
            rows.sort(function(a,b){ return String(a.name||'').localeCompare(String(b.name||'')); });
            paint();
          },function(){ rows=[]; list.innerHTML='<div class="hoh-cleanup-empty"><b>The list could not load.</b><span>Check the connection and try again.</span></div>'; });
      }
      function hideOne(id, button){
        id = String(id || '');
        if(!id || busyId) return;
        var c = rows.find(function(x){ return x && String(x.id) === id; });
        var sb = window.hohSupabaseClient;
        if(!c || !sb) return;
        busyId = id; status(''); paint();
        var meta = {}; try{ meta = Object.assign({}, c.quest_meta || {}); }catch(_){ }
        meta.review_state = 'archived';
        meta.reject_reason = 'hall_cleanup';
        sb.from('characters').update({ is_hidden:true, quest_meta:meta }).eq('id',id).select('id').then(function(r){
          busyId = '';
          if(!r || r.error || !Array.isArray(r.data) || !r.data.some(function(saved){ return saved && String(saved.id) === id; })){
            paint(); status('Hide was not confirmed. Nothing changed here. Check your access and try again.', true); return;
          }
          c.is_hidden = true; c.quest_meta = meta;
          rows = rows.filter(function(x){ return String(x.id) !== id; });
          try{
            var live = (typeof window.hohGetAllCharactersFull === 'function') ? window.hohGetAllCharactersFull() : [];
            for(var i=0;i<live.length;i++) if(live[i] && String(live[i].id)===id){ live[i].is_hidden=true; live[i].quest_meta=meta; break; }
          }catch(_){ }
          paint(); status((c.name || 'Hero') + ' is hidden from the public Hall and game. The owner can still see it in Your Hall.');
          try{ if(typeof window.hohApplyFilters === 'function') window.hohApplyFilters(); }catch(_){ }
        },function(){ busyId=''; paint(); status('Hide failed. Nothing changed. Check the connection and try again.', true); });
      }
      opener.addEventListener('click',function(){
        var d = ensureDialog();
        if(typeof d.showModal === 'function') d.showModal(); else d.setAttribute('open','');
        load();
      });
    })();

    /* -------- Review tab: approve the backfilled quest heroes --------
       Opens a full-screen reviewer. Shows the real Hall card (hohBuildCardHtml)
       next to the uploaded image. Approve flips is_hidden -> false (live),
       reject keeps it hidden, both stamp quest_meta.review_state. Uses a direct
       update, the same mechanism as the god-mode Hide/Unhide button. */
    (function(){
      var opener = panel.querySelector('#hoh-rev-open');
      if(!opener) return;
      if(!document.getElementById('hoh-rev-css')){
        var st = document.createElement('style'); st.id = 'hoh-rev-css';
        st.textContent =
          '.hoh-rev-ov{position:fixed;inset:0;background:rgba(8,9,12,.94);z-index:99999;display:none;flex-direction:column;color:#e7e3d8;font-family:-apple-system,Segoe UI,Roboto,sans-serif}.hoh-rev-ov.on{display:flex}'+
          '.hoh-rev-top{display:flex;align-items:center;gap:14px;padding:12px 18px;border-bottom:1px solid #2a2e3a;font-size:15px}.hoh-rev-x{margin-left:auto;cursor:pointer;background:none;border:1px solid #2a2e3a;color:#9aa0ad;border-radius:8px;padding:6px 12px}'+
          '.hoh-rev-body{flex:1;overflow:auto;display:flex;gap:28px;justify-content:center;align-items:flex-start;padding:24px;flex-wrap:wrap}'+
          /* R266: force the card VISIBLE. .hoh-card ships at opacity:0 and only
             the gallery scroll-reveal observer flips it on, which never runs in
             this overlay, so the portrait was rendering invisible and the review
             looked text-only. Pin opacity/transform (same fix the pack cards use).
             The portrait is drag-to-reframe here (ns-resize). */
          '.hoh-rev-cardwrap{width:240px;flex:0 0 auto}.hoh-rev-cardwrap .hoh-card{width:100%!important;opacity:1!important;transform:none!important}'+
          '.hoh-rev-cardwrap .hoh-card{cursor:default}.hoh-rev-cardwrap .hoh-card-img{cursor:grab}.hoh-rev-cardwrap .hoh-card-img:active{cursor:grabbing}.hoh-rev-cardwrap .hoh-card-img img{touch-action:none;-webkit-user-drag:none}'+
          '.hoh-rev-hint{margin-top:10px;font-size:12px;color:#8a90a0;text-align:center;line-height:1.45}.hoh-rev-hint b{color:#c9cdd6;font-weight:600}'+
          '.hoh-rev-side{max-width:560px;flex:1 1 360px}.hoh-rev-full{width:100%;max-height:60vh;object-fit:contain;background:#0a0b0e;border:1px solid #2a2e3a;border-radius:10px}'+
          '.hoh-rev-nm{font-family:"Cormorant Garamond",Georgia,serif;font-size:30px;margin:12px 0 2px}.hoh-rev-mt{color:#9aa0ad;font-size:14px;margin-bottom:10px}'+
          '.hoh-rev-st{white-space:pre-wrap;line-height:1.5;font-size:15px;color:#d8d4c8;border-top:1px solid #2a2e3a;padding-top:12px}'+
          '.hoh-rev-bar{display:flex;flex-wrap:wrap;gap:12px;align-items:center;justify-content:center;padding:14px;border-top:1px solid #2a2e3a;background:#16181f}'+
          /* ⛔ R610 · THE ARCHIVE.
             Every control on the bottom bar acts on the QUEUE hero, and there
             is none on screen while the bin is open, so the bar goes away
             rather than sitting there inviting an Approve that would land on a
             hero the reviewer cannot see. The JS refuses the same actions
             independently, because CSS cannot stop a keyboard.
             The rows are a plain list on purpose: this is a place to FIND one
             hero, not a second review surface, and a grid of cards would make
             a hundred archived heroes harder to scan rather than easier. */
          '.hoh-rev-ov.arch .hoh-rev-bar,.hoh-rev-ov.arch .hoh-rev-tools{display:none}'+
          '.hoh-rev-arch{width:100%;max-width:820px;margin:0 auto}'+
          '.hoh-rev-ahead{display:flex;flex-wrap:wrap;align-items:center;gap:10px 14px;margin-bottom:16px}'+
          '.hoh-rev-ahead b{font-size:19px}'+
          '.hoh-rev-ahead span{color:#8a90a0;font-size:13px;flex:1 1 200px}'+
          '.hoh-rev-ahead input{flex:0 0 auto;width:min(260px,100%);min-height:40px;padding:8px 12px;border-radius:8px;border:1px solid #2a2e3a;background:#0f1116;color:#e7e3d8;font-size:14px}'+
          '.hoh-rev-arow{display:flex;align-items:center;gap:12px;padding:10px;border:1px solid #23262f;border-radius:10px;background:#14161c;margin-bottom:8px}'+
          '.hoh-rev-athumb{width:52px;height:52px;flex:0 0 auto;border-radius:8px;object-fit:cover;background:#0a0b0e;border:1px solid #2a2e3a}'+
          '.hoh-rev-ameta{flex:1 1 auto;min-width:0;display:flex;flex-direction:column;gap:3px}'+
          '.hoh-rev-ameta b{font-size:15px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}'+
          '.hoh-rev-ameta span{color:#8a90a0;font-size:12px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}'+
          /* 44px floors: this list is used on a phone and both controls are
             destructive-adjacent, so neither may be a near miss. */
          '.hoh-rev-arow .hoh-rev-btn{flex:0 0 auto;min-height:44px;padding:8px 14px;font-size:13px}'+
          '.hoh-rev-aempty{padding:34px 10px;text-align:center;color:#8a90a0;line-height:1.6}'+
          '@media(max-width:640px){.hoh-rev-arow{flex-wrap:wrap}.hoh-rev-ameta{flex:1 1 100%;order:3}.hoh-rev-arow .hoh-rev-btn{flex:1 1 0}}'+
          '.hoh-rev-strike{background:#3a1614;border:1px solid #7a2a24;color:#ff9a92;border-radius:8px;padding:8px 12px;font-size:13px;margin-bottom:10px;line-height:1.4;font-weight:600}'+
          '.hoh-rev-tools{display:flex;flex-wrap:wrap;gap:12px;align-items:center;padding:9px 18px;border-bottom:1px solid #2a2e3a;font-size:13px;color:#9aa0ad;background:#111318}'+
          '.hoh-rev-tools input[type=range]{flex:1;min-width:140px;max-width:340px;accent-color:#c9a84c;cursor:pointer}'+
          '.hoh-rev-tools b{color:#e7e3d8;font-weight:600}'+
          '#hoh-rev-wc-go:disabled{opacity:.4;cursor:not-allowed}'+
          '.hoh-rev-btn{cursor:pointer;border-radius:11px;padding:13px 22px;font-size:15px;font-weight:700;border:1px solid #2a2e3a;background:#1d2029;color:#e7e3d8}'+
          '.hoh-rev-btn.app{background:#16301f;color:#8ee0a8;border-color:#2f6f47}.hoh-rev-btn.rej{background:#2a1b1a;color:#f0a09b;border-color:#5e2b28}.hoh-rev-btn.del{background:#3a1614;color:#ff9a92;border-color:#7a2a24}.hoh-rev-label{font-size:12px;color:#6f7585;margin-left:8px}'+
          '.hoh-rev-dup{background:#3a2a14;border:1px solid #7a5a24;color:#f0d08a;border-radius:8px;padding:8px 12px;font-size:13px;margin-bottom:10px;line-height:1.4}';
        document.head.appendChild(st);
      }
      var SB = (window.supabase && window.supabase.createClient)
        ? window.supabase.createClient('https://obadckrukccwkjgpbsxa.supabase.co','sb_publishable_4DntR53dZydUazuyuPI3YQ_zg69PLYZ',{auth:{persistSession:false,autoRefreshToken:false,storageKey:'hoh-rev'}})
        : null;
      var ov=null, q=[], i=0, yes=0, del=0, rej=0, busy=false, hist=[], rejList=[];
      /* R610: the pile is a VIEW over rejList, not a second fetch. archQ filters
         it so a bin holding hundreds can still be dug through, which is the half
         Jan actually asked for ("i can still go find and dig for it").
         ⛔ R664 (Jan): "instead of calling it archive call it rejected ... and
         maybe another one called hidden and there should be one called queue
         where all the fixes images and quest characters go."
         So there are THREE named piles, and `pile` says which one is on screen:
           ''         the QUEUE. Hidden, has a picture, waiting on Jan. Quest
                      heroes land here, and so does any rejected hero whose
                      owner has since changed the picture.
           'rejected' Jan looked and said no. Out of the Hall, out of packs, out
                      of everyone's collection, and it stays that way until the
                      picture changes or Jan deletes it.
           'hidden'   hidden with NO picture at all. Nothing to review yet, so
                      these were invisible to this tool entirely before R664 and
                      are the biggest group of the three.
         The word "archive" is gone from every label a person reads. */
      var pile='', archQ='';
      /* Both spellings are rejection. 'archived' is what R610 wrote and
         'rejected' is what every older hero carries, so reading only one word
         would strand a pile somewhere nothing looks. */
      function isArchived(c){
        var s = c && c.quest_meta && c.quest_meta.review_state;
        return s === 'archived' || s === 'rejected';
      }
      /* ⛔ R664 · THE HIDDEN PILE IS LAZY, AND THAT IS DELIBERATE.
         It is the largest of the three (roughly 1,500 heroes on the R609A
         census) and none of it is reviewable, because there is no picture to
         look at. Loading it with the queue would make every open of this tool
         pay for a list Jan opens occasionally, so it is fetched the first time
         he asks for it and cached for the session. null means "never asked". */
      var hidList=null, hidLoading=false, hidQ='';
      function bodyHtml(h){ var b=ov.querySelector('#hoh-rev-body'); if(b) b.innerHTML=h; }
      /* R198: duplicate / near-duplicate name detection so review never approves
         a hero whose name already exists. Normalizes (lowercase, strip honorifics
         + punctuation), then flags exact matches, containment, and small edit
         distance against every loaded hero. */
      function _revNorm(s){ return (''+(s||'')).toLowerCase().replace(/[^a-z0-9]+/g,' ').replace(/\b(the|of|sir|dame|lord|lady|ser|von|de|la)\b/g,' ').replace(/\s+/g,' ').trim(); }
      function _revLev(a,b){ var m=a.length,n=b.length; if(!m)return n; if(!n)return m; if(Math.abs(m-n)>3)return 9; var d=[],x,y; for(y=0;y<=n;y++)d[y]=y; for(x=1;x<=m;x++){ var p=d[0]; d[0]=x; for(y=1;y<=n;y++){ var t=d[y]; d[y]=Math.min(d[y]+1,d[y-1]+1,p+(a.charAt(x-1)===b.charAt(y-1)?0:1)); p=t; } } return d[n]; }
      function _revDupes(c){ var tgt=_revNorm(c.name); if(!tgt)return []; var all=(typeof window.hohGetAllCharactersFull==='function')?(window.hohGetAllCharactersFull()||[]):[]; var out=[]; for(var k=0;k<all.length;k++){ var o=all[k]; if(!o||String(o.id)===String(c.id))continue; var n=_revNorm(o.name); if(!n)continue; var exact=(n===tgt); var contains=(Math.min(n.length,tgt.length)>=4)&&(n.indexOf(tgt)!==-1||tgt.indexOf(n)!==-1); var close=(Math.max(n.length,tgt.length)>=5)&&(_revLev(n,tgt)<=2); if(exact||contains||close)out.push({name:o.name,player:o.player_name,exact:exact}); } out.sort(function(a,b){return (b.exact?1:0)-(a.exact?1:0);}); return out; }
      function prog(){ var p=ov.querySelector('#hoh-rev-prog'); if(p) p.innerHTML = q.length ? ('Hero <b>'+Math.min(i+1,q.length)+'</b> / '+q.length+' &nbsp; &#10003; '+yes+' approved &nbsp; &#128465; '+del+' deleted') : ''; try{ wcPreview(); }catch(_){} }
      function build(){
        ov=document.createElement('div'); ov.className='hoh-rev-ov';
        ov.innerHTML =
          /* ⛔ ====== R610 · ARCHIVE AND DELETE, NOT ONE REJECT ======
             Jan: "in my rejected queue for the quest heroes i notice a lot of
             them get recycled back again for me to review ... when i reject
             them can you just delete it in general? maybe have a trash bin or
             something but i dont want to see it again", then: "i can choose
             archive or delete then, delete = gone forever, archive means i can
             still go find and dig for it. and refresh archive brings all the
             archived ones back."

             ⛔ THE RECYCLING HAD A BUTTON, AND IT WAS THIS ONE. `refreshRejected`
             returned EVERY hero he had ever rejected to the queue in one tap,
             from a control sitting in the top bar with no confirmation. That is
             the whole of the "they keep coming back" report. It stays, because
             he asked for it by name, but it names its count and asks first.

             ⛔ AND REJECT NO LONGER ESCALATES INTO A DELETE. R245 made a second
             rejection permanently delete the hero. That was a reasonable design
             when reject was the only negative action, and it is the wrong one
             now that Delete is its own button: "archive" must mean archive, or
             the bin is not a bin.

             ⛔ 'archived' IS WRITTEN, 'rejected' IS STILL READ. Every hero Jan
             has already rejected carries `review_state:'rejected'`, and those
             are exactly the heroes he means by the archive. Reading both is
             what puts his existing pile in the bin on the first load instead of
             stranding it in a state nothing looks at any more. */
          /* R664: three named piles, and the button you are looking at says
             which one you are in. "Queue" is a real button rather than an
             implied home, because with two other piles beside it the way back
             has to be as obvious as the way out. */
          '<div class="hoh-rev-top"><b>Hero Review</b><span id="hoh-rev-prog"></span>'+
            '<button type="button" class="hoh-rev-x" id="hoh-rev-queue" title="The queue: hidden heroes with a picture, waiting on you. Quest heroes and fixed pictures land here.">Queue (0)</button>'+
            '<button type="button" class="hoh-rev-x" id="hoh-rev-arch" title="The rejected pile: every hero you said no to, with restore, delete and empty">&#128465; Rejected (0)</button>'+
            '<button type="button" class="hoh-rev-x" id="hoh-rev-hidden" title="Hidden with no picture yet. Nothing to review until their owner adds one.">Hidden</button>'+
            '<button type="button" class="hoh-rev-x" id="hoh-rev-refresh" title="Return EVERY rejected hero to the queue">Refresh rejected (0)</button>'+
            '<button type="button" class="hoh-rev-x" id="hoh-rev-close">Close</button></div>'+
          /* R248: word-count sweep. Drag to preview, one button to reject
             every QUEUE hero under the threshold. Never deletes. */
          '<div class="hoh-rev-tools">'+
            '<span>Auto-reject short stories</span>'+
            '<input type="range" id="hoh-rev-wc" min="0" max="200" step="5" value="0" aria-label="Minimum story word count">'+
            '<span id="hoh-rev-wc-read">off</span>'+
            '<button type="button" class="hoh-rev-btn rej" id="hoh-rev-wc-go" disabled>Reject 0</button>'+
          '</div>'+
          '<div class="hoh-rev-body" id="hoh-rev-body"></div>'+
          '<div class="hoh-rev-bar">'+
            '<button type="button" class="hoh-rev-btn del" data-act="delete">&#128465; Delete forever</button>'+
            '<button type="button" class="hoh-rev-btn rej" data-act="reject">&#128465; Reject</button>'+
            '<button type="button" class="hoh-rev-btn" data-act="keep">Skip</button>'+
            '<button type="button" class="hoh-rev-btn" data-act="undo">Undo</button>'+
            '<button type="button" class="hoh-rev-btn app" data-act="approve">&#10003; Approve</button>'+
            '<span class="hoh-rev-label">A approve &nbsp; R reject &nbsp; K skip &nbsp; D delete &nbsp; U undo</span>'+
          '</div>';
        document.body.appendChild(ov);
        ov.querySelector('#hoh-rev-close').onclick = close;
        /* R664: each pile button is a TOGGLE back to the queue, so the control
           that took you somewhere is the control that brings you back. */
        ov.querySelector('#hoh-rev-queue').onclick = function(){ pile=''; render(); };
        ov.querySelector('#hoh-rev-arch').onclick = function(){ pile = (pile==='rejected'?'':'rejected'); archQ=''; render(); };
        ov.querySelector('#hoh-rev-hidden').onclick = function(){
          pile = (pile==='hidden'?'':'hidden'); hidQ='';
          if(pile==='hidden' && hidList===null) loadHidden();
          render();
        };
        /* ⛔ DELEGATED ON THE BODY. renderArchive rebuilds its whole list after
           every restore, delete and keystroke in the filter, so a handler bound
           to a row would die with the row and the SECOND action on a card would
           do nothing. One listener on the container survives every repaint. */
        ov.querySelector('#hoh-rev-body').addEventListener('click', function(ev){
          /* R664: the hidden pile's rows ride the same delegated listener, for
             the same reason the rejected pile's do — every row is rebuilt on
             each repaint, so a handler bound to a row dies with it. */
          var hrow = ev.target.closest && ev.target.closest('[data-hid-id]');
          if(hrow){
            if(ev.target.closest('[data-hid-del]')) hidDelete(hrow.getAttribute('data-hid-id'));
            return;
          }
          var row = ev.target.closest && ev.target.closest('[data-arch-id]');
          if(!row) return;
          var id = row.getAttribute('data-arch-id');
          if(ev.target.closest('[data-arch-restore]')) archAct(id, 'restore');
          else if(ev.target.closest('[data-arch-del]')) archAct(id, 'delete');
        });
        ov.querySelector('#hoh-rev-refresh').onclick = refreshRejected;
        ov.querySelector('#hoh-rev-wc').addEventListener('input', wcPreview);
        ov.querySelector('#hoh-rev-wc-go').onclick = wcSweep;
        ov.querySelector('.hoh-rev-bar').addEventListener('click', function(ev){
          var b = ev.target.closest && ev.target.closest('[data-act]'); if(!b) return;
          /* ⛔ R610: every action on this bar operates on q[i], the QUEUE hero.
             With the archive open there is no queue hero on screen, so an
             Approve here would silently approve whatever the queue happened to
             be pointing at. The bar is hidden by CSS while the archive is up;
             this is the belt to that braces, because CSS cannot stop a
             keyboard. */
          if(pile) return;
          var a = b.getAttribute('data-act');
          if(a==='approve') act('approve');
          else if(a==='reject') rejectReview();
          else if(a==='keep'){ if(i<q.length){ hist.push({id:q[i].id,action:'keep'}); i++; render(); } }
          else if(a==='delete') delReview();
          else if(a==='undo') undo();
        });
      }
      function open(){ if(!ov) build(); ov.classList.add('on'); load(); }
      function close(){ if(ov) ov.classList.remove('on'); }
      function load(){
        if(!SB){ bodyHtml('Supabase unavailable.'); return; }
        bodyHtml('Loading the review queue&hellip;');
        SB.from('characters')
          /* R769: the saved crop and zoom ride along so the preview card shows
             the owner's reframe and approve never writes the centre over it. */
          .select('id,name,race,character_class,level,campaign,player_name,story,image_url,image_url_2,image_thumb_url,quest_meta,hearts,is_ff_portrait,image_offset_x,image_offset_y,image_zoom')
          .eq('approved',true).eq('is_hidden',true)
          .or('image_url.not.is.null,image_thumb_url.not.is.null')
          .order('created_at',{ascending:false}).limit(2000)
          .then(function(r){
            if(r.error){ bodyHtml('Load error: '+_godEsc(r.error.message)); return; }
            /* R211: only hidden heroes WITH an image reach the queue — now
               guaranteed by the query itself and re-checked here. No-image
               heroes stay hidden with working direct links; adding an image
               in the owner edit flow re-stamps them pending (core R245).
               R245: rejected heroes wait OUTSIDE the queue until Refresh. */
            var rows=(r.data||[]).filter(function(c){ return !!(c && (c.image_url || c.image_thumb_url)); });
            /* R769: hidden heroes are not in the visible pool, so the shared
               crop and zoom getters look here on a miss (core __hohCharById). */
            try{ window.__hohReviewRows=rows; }catch(_){}
            rejList=rows.filter(isArchived);
            q=rows.filter(function(c){ return !isArchived(c); });
            i=0; yes=0; del=0; rej=0; hist=[]; syncRefreshBtn(); render();
          }, function(){ bodyHtml('Load failed.'); });
      }
      /* ⛔ ====== R610 · THE ARCHIVE IS A PLACE, NOT A COUNTER ======
         Jan: "archive means i can still go find and dig for it."
         Before this the only handle on a set-aside hero was a number on a
         button and one bulk restore. That is a black hole with a lid, not a
         bin. This is the bin: every archived hero, filterable by name or
         player, each with its own Restore and its own Delete.
         It renders from `rejList`, which load() already built, so opening the
         archive costs no request. */
      function archRow(c){
        var img = c.image_thumb_url || c.image_url || '';
        var meta = [c.race, c.character_class].filter(Boolean).join(' ');
        var by = c.player_name ? ('by ' + _godEsc(c.player_name)) : 'no player name';
        var strikes = (c.quest_meta && c.quest_meta.reject_count) || 0;
        return '<div class="hoh-rev-arow" data-arch-id="' + _godEsc(String(c.id)) + '">' +
          (img ? '<img class="hoh-rev-athumb" src="' + _godEsc(img) + '" alt="" loading="lazy">'
               : '<div class="hoh-rev-athumb"></div>') +
          '<div class="hoh-rev-ameta">' +
            '<b>' + _godEsc(c.name || 'Unnamed') + '</b>' +
            '<span>' + _godEsc(meta) + ' &middot; ' + by + ' &middot; ' + revWordCount(c.story) + ' story words' +
              (strikes > 1 ? (' &middot; archived ' + strikes + ' times') : '') + '</span>' +
          '</div>' +
          '<button type="button" class="hoh-rev-btn" data-arch-restore>Restore</button>' +
          '<button type="button" class="hoh-rev-btn del" data-arch-del>&#128465; Delete</button>' +
        '</div>';
      }
      function renderArchive(){
        var needle = archQ.trim().toLowerCase();
        var list = needle
          ? rejList.filter(function(c){
              return ((c.name || '') + ' ' + (c.player_name || '')).toLowerCase().indexOf(needle) !== -1;
            })
          : rejList;
        bodyHtml(
          '<div class="hoh-rev-arch">' +
            '<div class="hoh-rev-ahead">' +
              '<b>Rejected</b>' +
              '<span>' + rejList.length + ' hero' + (rejList.length === 1 ? '' : 'es') + ' you said no to. Their cards are out of the Hall, out of packs and out of everyone&rsquo;s collection. They come back only if their owner changes the picture, or if you restore one.</span>' +
              '<input type="search" id="hoh-rev-afind" placeholder="Find by hero or player name" value="' + _godEsc(archQ) + '" autocomplete="off">' +
              /* ⛔ R664 (Jan): "i can empty that pile as well". It deletes every
                 hero in the pile forever, so it states the number, asks twice,
                 and the second ask is TYPED. A bulk delete of other people's
                 heroes must not be reachable by two taps in the same place. */
              (rejList.length
                ? '<button type="button" class="hoh-rev-btn del" id="hoh-rev-empty">&#128465; Empty the pile (' + rejList.length + ')</button>'
                : '') +
            '</div>' +
            (rejList.length
              ? (list.length
                  ? list.map(archRow).join('')
                  : '<div class="hoh-rev-aempty">Nothing here matches &ldquo;' + _godEsc(archQ) + '&rdquo;.</div>')
              : '<div class="hoh-rev-aempty">The rejected pile is empty. Heroes you reject from the queue, and heroes you hide from the Hall, land here and stay until you restore or delete them.</div>') +
          '</div>');
        var find = ov.querySelector('#hoh-rev-afind');
        if(find){
          find.oninput = function(){
            archQ = find.value;
            var at = find.selectionStart;
            renderArchive();
            /* the list is rebuilt under the box, so put the caret back or every
               second character types itself somewhere else */
            var f2 = ov.querySelector('#hoh-rev-afind');
            if(f2){ try{ f2.focus(); f2.setSelectionRange(at, at); }catch(_){ } }
          };
        }
        var emptyBtn = ov.querySelector('#hoh-rev-empty');
        if(emptyBtn) emptyBtn.onclick = emptyRejected;
      }
      /* ⛔ R664 · EMPTYING THE PILE IS THE MOST DESTRUCTIVE BUTTON IN THE GAME.
         It deletes other people's heroes permanently, and with them every card
         anybody collected off those heroes, with no hearts back. Jan asked for
         it and it is built, with three things standing between a stray tap and
         a few hundred deletions: it names the number, it asks, and then it asks
         again with the number TYPED. It also deletes one at a time so a failure
         halfway leaves a smaller pile rather than an unknown one. */
      /* ⛔ R664 · ONE DOOR FOR EVERY DELETE IN THIS TOOL.
         The first cut of emptyRejected and hidDelete called
         `SB.from('characters').delete()` directly. That is a SECOND, unguarded
         path to the single most destructive action in the game, past the admin
         PIN that archAct has always demanded — and freshfaces.cjs already
         asserts that rule for archAct's own delete, in those words. It would
         most likely have failed against RLS anyway, which would have made
         "Empty the pile" a button that quietly did nothing.
         Both now go through the same PIN-gated RPC, and the credentials are
         read in ONE place so a third caller cannot invent a fourth way in. */
      function adminPin(){
        var pin = ''; try{ pin = sessionStorage.getItem('hoh-admin-pin') || ''; }catch(_){ }
        if(!pin){
          try{ pin = window.prompt('Enter your admin PIN to delete (the same PIN you signed into the Hall with):') || ''; }catch(_){ }
          if(pin){ try{ sessionStorage.setItem('hoh-admin-pin', pin); }catch(_){ } }
        }
        return pin;
      }
      function adminEmail(){
        return (typeof window.hohYourHallEmail === 'function') ? (window.hohYourHallEmail() || '') : '';
      }
      /* Resolves to true only on a confirmed server-side delete. A refusal is
         named, and a bad PIN is forgotten so the next attempt can re-ask. */
      function adminDelete(id){
        return new Promise(function(resolve){
          var email = adminEmail(), pin = adminPin();
          if(!pin){ resolve(false); return; }
          SB.rpc('admin_delete_character', { p_email: email, p_pin: pin, p_id: id }).then(function(r){
            if(r && r.error){ resolve(false); return; }
            var d = (r && r.data) || {};
            if(d.ok !== true){
              if(d.error === 'bad_pin'){ try{ sessionStorage.removeItem('hoh-admin-pin'); }catch(_){ } }
              resolve(false); return;
            }
            resolve(true);
          }, function(){ resolve(false); });
        });
      }
      function emptyRejected(){
        if(busy || !SB || !rejList.length) return;
        var n = rejList.length;
        var ok = false;
        try {
          ok = window.confirm('Delete all ' + n + ' rejected hero' + (n === 1 ? '' : 'es') + ' forever?\n\n' +
            'This cannot be undone. Every card anybody collected off these heroes goes with them, and nobody gets hearts back.');
        } catch(_){ ok = false; }
        if(!ok) return;
        var typed = null;
        try { typed = window.prompt('Type ' + n + ' to confirm you want all ' + n + ' deleted forever.'); } catch(_){ typed = null; }
        if(String(typed || '').trim() !== String(n)) return;
        busy = true;
        var list = rejList.slice(), done = 0, gone = 0;
        bodyHtml('<div style="text-align:center;padding:40px"><b>Deleting&hellip;</b><br><span id="hoh-rev-emptyn">0 of ' + n + '</span></div>');
        (function step(){
          if(done >= list.length){
            busy = false; del += gone; pile = ''; archQ = '';
            syncRefreshBtn(); load(); return;
          }
          var c = list[done++];
          var pn = ov && ov.querySelector('#hoh-rev-emptyn');
          if(pn) pn.textContent = done + ' of ' + n;
          adminDelete(c.id).then(function(ok){
            if(ok){
              gone++;
              var k = rejList.findIndex(function(x){ return x && String(x.id) === String(c.id); });
              if(k >= 0) rejList.splice(k, 1);
            } else if(gone === 0 && done === 1){
              /* The FIRST one failed, so the PIN or the permission is wrong and
                 every one after it would fail the same way. Stop and say so
                 rather than walking a few hundred heroes to no effect. */
              busy = false;
              try{ alert('Delete refused. Nothing was deleted. Check the admin PIN and try again.'); }catch(_){}
              pile = 'rejected'; syncRefreshBtn(); render(); return;
            }
            step();
          });
        })();
      }
      /* ⛔ R664 · THE HIDDEN PILE. Hidden heroes with NO picture, which is the
         one group this tool could never show before: its queue query REQUIRES
         an image, so roughly 1,500 heroes were invisible here. There is nothing
         to review, so this pile is a LIST, not a review station: it says who is
         waiting on a picture, and offers delete for the ones that never will.
         Paged at 1000 because PostgREST caps a request there and a `.limit()`
         past it loses rows silently. */
      function loadHidden(){
        if(!SB || hidLoading) return;
        hidLoading = true; syncRefreshBtn();
        var out = [], from = 0, PAGE = 1000, complete = false;
        (function page(){
          SB.from('characters')
            .select('id,name,player_name,created_at,quest_meta,image_url,image_thumb_url')
            .eq('is_hidden', true)
            .is('image_url', null).is('image_thumb_url', null)
            .order('created_at', {ascending:false})
            .range(from, from + PAGE - 1)
            .then(function(r){
              if(r && r.error){ finish(); return; }
              var chunk = (r && r.data) || [];
              out = out.concat(chunk);
              if(chunk.length < PAGE || from > 60000){ complete = true; finish(); return; }
              from += PAGE; page();
            }, function(){ finish(); });
        })();
        function finish(){
          hidLoading = false;
          /* A partial walk is still shown, because unlike a filter that removes
             cards, a half list here only under-reports a count Jan is reading.
             It says so rather than passing itself off as the whole pile. */
          hidList = out; hidList.__partial = !complete;
          syncRefreshBtn();
          if(pile === 'hidden') renderHidden();
        }
      }
      function hidRow(c){
        var by = c.player_name ? ('by ' + _godEsc(c.player_name)) : 'no player name';
        var when = '';
        try { when = c.created_at ? new Date(c.created_at).toLocaleDateString() : ''; } catch(_){ }
        return '<div class="hoh-rev-arow" data-hid-id="' + _godEsc(String(c.id)) + '">' +
          '<div class="hoh-rev-athumb" aria-hidden="true"></div>' +
          '<div class="hoh-rev-atext"><b>' + _godEsc(c.name || 'Unnamed') + '</b>' +
            '<span>' + by + (when ? (' &middot; ' + _godEsc(when)) : '') + ' &middot; no picture yet</span></div>' +
          '<button type="button" class="hoh-rev-btn del" data-hid-del="1">&#128465; Delete</button>' +
        '</div>';
      }
      function renderHidden(){
        if(hidList === null){
          bodyHtml('<div style="text-align:center;padding:40px">Loading the hidden pile&hellip;</div>');
          return;
        }
        var needle = hidQ.trim().toLowerCase();
        var list = needle
          ? hidList.filter(function(c){
              return ((c.name || '') + ' ' + (c.player_name || '')).toLowerCase().indexOf(needle) !== -1;
            })
          : hidList;
        bodyHtml(
          '<div class="hoh-rev-arch">' +
            '<div class="hoh-rev-ahead">' +
              '<b>Hidden</b>' +
              '<span>' + hidList.length + ' hero' + (hidList.length === 1 ? '' : 'es') + ' with no picture yet' +
                (hidList.__partial ? ', and this list is incomplete because the read did not finish' : '') +
                '. There is nothing to review until their owner adds one, and the moment they do the hero moves to the queue on its own.</span>' +
              '<input type="search" id="hoh-rev-hfind" placeholder="Find by hero or player name" value="' + _godEsc(hidQ) + '" autocomplete="off">' +
            '</div>' +
            (hidList.length
              ? (list.length
                  ? list.slice(0, 400).map(hidRow).join('') +
                    (list.length > 400 ? '<div class="hoh-rev-aempty">Showing the first 400 of ' + list.length + '. Use the box above to narrow it.</div>' : '')
                  : '<div class="hoh-rev-aempty">Nothing here matches &ldquo;' + _godEsc(hidQ) + '&rdquo;.</div>')
              : '<div class="hoh-rev-aempty">Nobody is waiting on a picture.</div>') +
          '</div>');
        var find = ov.querySelector('#hoh-rev-hfind');
        if(find){
          find.oninput = function(){
            hidQ = find.value;
            var at = find.selectionStart;
            renderHidden();
            var f2 = ov.querySelector('#hoh-rev-hfind');
            if(f2){ try{ f2.focus(); f2.setSelectionRange(at, at); }catch(_){ } }
          };
        }
      }
      function hidDelete(id){
        if(busy || !SB) return;
        var idx = hidList ? hidList.findIndex(function(x){ return x && String(x.id) === String(id); }) : -1;
        if(idx < 0) return;
        var c = hidList[idx];
        var ok = false;
        try { ok = window.confirm('Delete ' + (c.name || 'this hero') + ' forever?\n\nThey have no picture, so nobody has ever seen them in the Hall. This cannot be undone.'); } catch(_){ ok = false; }
        if(!ok) return;
        busy = true;
        adminDelete(c.id).then(function(okDel){
          busy = false;
          if(!okDel){ try{ alert('Delete refused. Check the admin PIN and try again.'); }catch(_){} return; }
          hidList.splice(idx, 1); syncRefreshBtn(); renderHidden();
        });
      }
      function archAct(id, mode){
        if(busy || !SB) return;
        var idx = rejList.findIndex(function(x){ return x && String(x.id) === String(id); });
        if(idx < 0) return;
        var c = rejList[idx];
        if(mode === 'restore'){
          busy = true;
          var meta = {}; try{ meta = Object.assign({}, c.quest_meta || {}); }catch(_){ }
          meta.review_state = 'pending';
          SB.from('characters').update({ quest_meta: meta }).eq('id', c.id).select('id').then(function(r){
            busy = false;
            if(r.error){ alert('Restore failed: ' + r.error.message); return; }
            c.quest_meta = meta;
            rejList.splice(idx, 1);
            q.push(c);
            syncRefreshBtn(); renderArchive();
          }, function(){ busy = false; alert('Restore failed.'); });
          return;
        }
        /* ⛔ the SAME PIN-gated RPC the queue's Delete uses. A second delete
           path that skipped that gate would be a hole in the only protection
           this action has. */
        if(!window.confirm('Delete ' + (c.name || 'this hero') + ' permanently?\n\nThis removes them from the Hall and the database for good, and cannot be undone.')) return;
        var email = (typeof window.hohYourHallEmail === 'function') ? (window.hohYourHallEmail() || '') : '';
        var pin = ''; try{ pin = sessionStorage.getItem('hoh-admin-pin') || ''; }catch(_){ }
        if(!pin){
          try{ pin = window.prompt('Enter your admin PIN to delete (the same PIN you signed into the Hall with):') || ''; }catch(_){ }
          if(pin){ try{ sessionStorage.setItem('hoh-admin-pin', pin); }catch(_){ } }
        }
        if(!pin) return;
        busy = true;
        SB.rpc('admin_delete_character', { p_email: email, p_pin: pin, p_id: c.id }).then(function(r){
          busy = false;
          if(r.error){ alert('Delete failed: ' + r.error.message); return; }
          var d = r.data || {};
          if(d.ok !== true){
            if(d.error === 'bad_pin'){ try{ sessionStorage.removeItem('hoh-admin-pin'); }catch(_){ } alert('Wrong admin PIN. Press Delete again to retry.'); }
            else if(d.error === 'not_admin'){ alert('Only the admin account can delete.'); }
            else { alert('Delete blocked: ' + (d.error || 'unknown') + '.'); }
            return;
          }
          try{ var live=(typeof window.hohGetAllCharactersFull==='function')?window.hohGetAllCharactersFull():null; if(live){ var li=live.findIndex(function(x){return x&&String(x.id)===String(c.id);}); if(li>=0) live.splice(li,1); } }catch(_){ }
          try{ if(Array.isArray(window.__hohHidden)){ var hj=window.__hohHidden.findIndex(function(x){return x&&String(x.id)===String(c.id);}); if(hj>=0) window.__hohHidden.splice(hj,1); } }catch(_){ }
          del++; rejList.splice(idx, 1);
          syncRefreshBtn(); prog(); renderArchive();
        }, function(){ busy = false; alert('Delete failed.'); });
      }
      function render(){
        prog();
        syncRefreshBtn();
        try{ ov.classList.toggle('arch', !!pile); }catch(_){ }
        if(pile==='rejected'){ renderArchive(); return; }
        if(pile==='hidden'){ renderHidden(); return; }
        if(i>=q.length){ bodyHtml('<div style="text-align:center;padding:40px"><b>All caught up.</b><br>'+yes+' approved, '+rej+' rejected, '+del+' deleted.'+(rejList.length?('<br><br><button type="button" class="hoh-rev-btn" id="hoh-rev-openarch">&#128465; Open the rejected pile ('+rejList.length+')</button>'):'')+'<br><br><button type="button" class="hoh-rev-btn" id="hoh-rev-reload">Reload queue</button></div>'); var rl=ov.querySelector('#hoh-rev-reload'); if(rl) rl.onclick=load; var ur=ov.querySelector('#hoh-rev-openarch'); if(ur) ur.onclick=function(){ pile='rejected'; archQ=''; render(); }; return; }
        var c=q[i], card='';
        /* R247: heroes with a second image get a swap toggle so both can
           be inspected before deciding. View-only — nothing on the row
           changes, and drag-to-reframe applies to image 1 only. */
        var alt=!!(c.__alt && c.image_url_2);
        var cSrc=alt?Object.assign({},c,{image_url:c.image_url_2,image_thumb_url:c.image_url_2}):c;
        try{ if(typeof window.hohBuildCardHtml==='function'){ card=window.hohBuildCardHtml(Object.assign({},cSrc,{is_hidden:false}),0); } }catch(_){}
        /* R245: a hero already rejected once wears the strike warning — the
           next rejection is a permanent delete, and the reviewer must see
           that BEFORE pressing the button. */
        var _rc=(c.quest_meta&&c.quest_meta.reject_count)||0;
        /* R610: this used to read "Rejecting again will DELETE this hero
           permanently", which was true under the R245 two-strike rule and is a
           lie now that Archive always archives. It states the history instead,
           which is the part that was ever useful. */
        var strikeHtml=_rc>=1?'<div class="hoh-rev-strike">&#9888; You archived this hero '+(_rc===1?'once':(_rc+' times'))+' before. It came back from the archive.</div>':'';
        var dupes=_revDupes(c);
        var dupHtml = dupes.length ? ('<div class="hoh-rev-dup">&#9888; '+(dupes.some(function(d){return d.exact;})?'Exact name already exists':'Similar name already in the Hall')+': '+dupes.slice(0,6).map(function(d){return _godEsc(d.name)+(d.player?(' (by '+_godEsc(d.player)+')'):'');}).join(', ')+(dupes.length>6?(' +'+(dupes.length-6)+' more'):'')+'</div>') : '';
        bodyHtml(
          '<div class="hoh-rev-cardwrap">'+(card||'card unavailable')+
            '<div class="hoh-rev-hint">'+(alt
              ?'Viewing <b>image 2</b>. Reframe applies to image 1 only.'
              :'Grid preview. <b>Drag the portrait</b> to reframe and <b>zoom</b> to move closer. Saved for everyone when you approve.')+'</div>'+
            /* R769: the same zoom row as the edit frame, wired below by
               _revWireReframe. Image 1 only, like the drag. */
            ((!alt && typeof window.hohEditZoomHtml==='function')
              ? window.hohEditZoomHtml((typeof window.hohGetImageZoom==='function')?window.hohGetImageZoom(c.id,'primary'):100, {bare:true})
              : '')+
            (c.image_url_2?('<button type="button" class="hoh-rev-btn" id="hoh-rev-swap" style="margin-top:8px;width:100%">&#8646; '+(alt?'Back to image 1':'Swap to image 2')+'</button>'):'')+
          '</div>'+
          '<div class="hoh-rev-side">'+
            strikeHtml+dupHtml+
            '<div class="hoh-rev-nm">'+_godEsc(c.name||'Unnamed')+'</div>'+
            '<div class="hoh-rev-mt">'+_godEsc((c.race||'')+' '+(c.character_class||''))+(c.level?(' &middot; Lvl '+_godEsc(c.level)):'')+(c.player_name?(' &middot; by '+_godEsc(c.player_name)):'')+' &middot; '+revWordCount(c.story)+' story words</div>'+
            '<div class="hoh-rev-st">'+_godEsc(c.story||'(no story)')+'</div>'+
          '</div>'
        );
        var swb=ov.querySelector('#hoh-rev-swap');
        if(swb) swb.onclick=function(){ c.__alt=!c.__alt; render(); };
        try{ if(!alt) _revWireReframe(c); }catch(_){}
      }
      /* R266: drag-to-reframe the preview portrait right in the reviewer, so the
         crop can be fixed before approving. Updates the shared offset live (which
         repaints every surface) on release; act('approve') then persists it for
         everyone via hohPersistOffsetServer. Mouse + touch.
         R769: the same pan law as the edit frame. Each axis the art overflows
         on (its aspect at 100, both once zoomed) follows the pointer one to one
         against hohImgPanRange. The zoom row under the hint previews on the
         card and commits through the shared setter, so approve persists X, Y
         and zoom together. c.__reframed marks that the reviewer changed
         something, which is the only case approve writes the reframe. */
      function _revWireReframe(c){
        if(!c || !c.id) return;
        var wrap = ov && ov.querySelector('.hoh-rev-cardwrap');
        if(!wrap) return;
        var img = wrap.querySelector('.hoh-card-img img') || wrap.querySelector('img');
        if(!img) return;
        var media = wrap.querySelector('.hoh-card-img') || img;
        function offX(){ try{ return (typeof window.hohGetImageOffsetX==='function') ? window.hohGetImageOffsetX(c.id,'primary') : 50; }catch(_){ return 50; } }
        function offY(){ try{ return (typeof window.hohGetImageOffsetY==='function') ? window.hohGetImageOffsetY(c.id,'primary') : 50; }catch(_){ return 50; } }
        function zoomNow(){ try{ return (typeof window.hohGetImageZoom==='function') ? window.hohGetImageZoom(c.id,'primary') : 100; }catch(_){ return 100; } }
        function paint(x,y){ var pos=x+'% '+y+'%'; img.style.objectPosition=pos; img.style.transformOrigin=pos; }
        var dragging=false, sx=0, sy=0, ox=50, oy=50, lx=50, ly=50, range={x:0,y:0};
        function ranges(){
          try{ range=(typeof window.hohImgPanRange==='function') ? window.hohImgPanRange(img, media.getBoundingClientRect(), zoomNow()) : {x:0,y:(media.getBoundingClientRect().height||200)}; }
          catch(_){ range={x:0,y:0}; }
        }
        function pt(e){ var t=(e.touches && e.touches[0]) || e; return {x:t.clientX,y:t.clientY}; }
        function move(e){
          if(!dragging) return;
          var p=pt(e);
          /* Grab-and-pull: dragging DOWN reveals the TOP of the art (offset -> 0). */
          lx = (range.x>0) ? Math.max(0, Math.min(100, ox-((p.x-sx)/range.x)*100)) : ox;
          ly = (range.y>0) ? Math.max(0, Math.min(100, oy-((p.y-sy)/range.y)*100)) : oy;
          paint(lx,ly);
          if(e.cancelable) e.preventDefault();
        }
        function up(){
          if(!dragging) return;
          dragging=false;
          /* Commit only what moved. A press with no travel leaves the hero
             exactly as its owner saved it and does not mark it reframed. */
          var nx=Math.round(lx), ny=Math.round(ly);
          try{ if(range.x>0 && nx!==Math.round(ox) && typeof window.hohSetImageOffsetX==='function'){ window.hohSetImageOffsetX(c.id,'primary',nx); c.__reframed=true; } }catch(_){}
          try{ if(range.y>0 && ny!==Math.round(oy) && typeof window.hohSetImageOffsetY==='function'){ window.hohSetImageOffsetY(c.id,'primary',ny); c.__reframed=true; } }catch(_){}
          document.removeEventListener('mousemove',move); document.removeEventListener('mouseup',up);
          document.removeEventListener('touchmove',move); document.removeEventListener('touchend',up);
        }
        function down(e){
          dragging=true; var p=pt(e); sx=p.x; sy=p.y; ranges(); ox=offX(); oy=offY(); lx=ox; ly=oy;
          if(e.cancelable) e.preventDefault();
          document.addEventListener('mousemove',move); document.addEventListener('mouseup',up);
          document.addEventListener('touchmove',move,{passive:false}); document.addEventListener('touchend',up);
        }
        media.addEventListener('mousedown',down);
        media.addEventListener('touchstart',down,{passive:false});
        /* Stop the card's own click (which would open the hero) while reframing. */
        media.addEventListener('click',function(e){ e.stopPropagation(); e.preventDefault(); }, true);
        /* R769: the zoom row. Slide previews on the card, release commits. */
        var zrow = wrap.querySelector('.hoh-edit-img-zoom');
        if(zrow){
          var rng = zrow.querySelector('.hoh-edit-img-zoom-range');
          var steps = zrow.querySelectorAll('.hoh-edit-img-zoom-step');
          var zclamp = function(v){ var n=parseInt(v,10); return Math.max(100, Math.min(200, isNaN(n)?100:n)); };
          var zpreview = function(){ if(!rng) return; var z=zclamp(rng.value); rng.value=String(z); img.style.scale=String(z/100); };
          var zcommit = function(){
            if(!rng) return; zpreview();
            try{ if(typeof window.hohSetImageZoom==='function') window.hohSetImageZoom(c.id,'primary',zclamp(rng.value)); }catch(_){}
            c.__reframed=true;
          };
          if(rng){ rng.addEventListener('input',zpreview); rng.addEventListener('change',zcommit); }
          if(steps[0]) steps[0].addEventListener('click',function(){ if(rng){ rng.value=String(zclamp(rng.value)-5); zcommit(); } });
          if(steps[1]) steps[1].addEventListener('click',function(){ if(rng){ rng.value=String(zclamp(rng.value)+5); zcommit(); } });
        }
      }
      function act(action){
        if(busy||i>=q.length||!SB) return; var c=q[i]; busy=true;
        var meta={}; try{ meta=Object.assign({},c.quest_meta||{}); }catch(_){}
        meta.review_state='approved';
        SB.from('characters').update({ is_hidden:false, quest_meta:meta }).eq('id',c.id).select('id').then(function(r){
          busy=false;
          if(r.error){ alert('Save failed: '+r.error.message); return; }
          /* R266: persist any reframe the reviewer made (drag) so the crop shows
             for everyone the moment the hero goes live. Trigger-bypassing RPC.
             R769: only when the reviewer actually dragged or zoomed. An
             untouched hero keeps the crop its owner saved at upload. */
          try{ if(c.__reframed && typeof window.hohPersistOffsetServer==='function') window.hohPersistOffsetServer(c.id,'primary'); }catch(_){}
          yes++; hist.push({id:c.id,action:'approve'});
          try{ var live=(typeof window.hohGetAllCharactersFull==='function')?window.hohGetAllCharactersFull():null; if(live){ for(var k=0;k<live.length;k++){ if(String(live[k].id)===String(c.id)){ live[k].is_hidden=false; live[k].quest_meta=meta; break; } } } }catch(_){}
          try{ if(Array.isArray(window.__hohHidden)){ var hj=window.__hohHidden.findIndex(function(x){return x&&String(x.id)===String(c.id);}); if(hj>=0) window.__hohHidden.splice(hj,1); } }catch(_){}
          i++; render();
        }, function(){ busy=false; alert('Save failed.'); });
      }
      /* R198: permanent delete from the reviewer. PIN-gated via admin_delete_character,
         the same secure path as the detail-card delete. Removes from queue + caches. */
      /* ⛔ R610 · ARCHIVE ALWAYS ARCHIVES. R245 routed a SECOND rejection into a
         permanent delete, behind a named confirm. That was a defensible design
         while reject was the only negative action on this bar. It is the wrong
         one now: Jan asked for two separate moves, "delete = gone forever,
         archive means i can still go find and dig for it", and a bin that
         empties itself on the second visit is not a bin. Delete is one tap away
         and PIN-gated, so nothing is lost by making this one honest.
         The strike COUNT is kept and still shown, because "you set this one
         aside before" is useful information. It just no longer detonates. */
      function rejectReview(){
        if(busy||i>=q.length||!SB) return; var c=q[i];
        var rc=(c.quest_meta&&c.quest_meta.reject_count)||0;
        busy=true;
        var meta={}; try{ meta=Object.assign({},c.quest_meta||{}); }catch(_){}
        meta.review_state='archived'; meta.reject_count=rc+1;
        SB.from('characters').update({ is_hidden:true, quest_meta:meta }).eq('id',c.id).select('id').then(function(r){
          busy=false;
          if(r.error){ alert('Save failed: '+r.error.message); return; }
          c.quest_meta=meta;
          try{ var live=(typeof window.hohGetAllCharactersFull==='function')?window.hohGetAllCharactersFull():null; if(live){ for(var k=0;k<live.length;k++){ if(String(live[k].id)===String(c.id)){ live[k].quest_meta=meta; live[k].is_hidden=true; break; } } } }catch(_){}
          rej++; rejList.push(c);
          hist.push({id:c.id,action:'reject',idx:i,c:c});
          q.splice(i,1); syncRefreshBtn(); render();
        }, function(){ busy=false; alert('Save failed.'); });
      }
      /* ⛔ R610 · THIS IS THE BUTTON THAT WAS RECYCLING HIS QUEUE.
         Jan: "i notice a lot of them get recycled back again for me to review".
         One tap here returned EVERY hero he had ever set aside, from a control
         in the top bar, with nothing asked and nothing said. He wants it kept
         ("refresh archive brings all the archived ones back"), so it is kept
         and it now states its own blast radius first. A bulk action that cannot
         be undone in one move has to ask. */
      function refreshRejected(){
        if(busy||!SB||!rejList.length) return;
        if(!window.confirm('Put all '+rejList.length+' rejected hero'+(rejList.length===1?'':'es')+' back into the queue?\n\nThey will all come back for you to review again.')) return;
        busy=true;
        var list=rejList.slice(), done=0;
        (function step(){
          if(done>=list.length){ busy=false; rejList=[]; pile=''; archQ=''; syncRefreshBtn(); load(); return; }
          var c=list[done++];
          var meta={}; try{ meta=Object.assign({},c.quest_meta||{}); }catch(_){}
          meta.review_state='pending';
          SB.from('characters').update({ quest_meta:meta }).eq('id',c.id).select('id').then(function(){ step(); }, function(){ step(); });
        })();
      }
      function syncRefreshBtn(){
        if(!ov) return;
        var b=ov.querySelector('#hoh-rev-refresh');
        if(b){
          b.textContent='Refresh rejected ('+rejList.length+')';
          b.disabled=!rejList.length;
          b.style.opacity=rejList.length?'1':'0.45';
        }
        /* R664: every pile button carries its own count and its own lit state,
           so the top bar answers "where am I and how big is each pile" without
           opening anything. None of them is ever disabled: an empty pile still
           has to be openable, or the only way to learn it is empty is to guess. */
        var qb=ov.querySelector('#hoh-rev-queue');
        if(qb){
          qb.textContent='Queue ('+q.length+')';
          qb.style.opacity=(pile===''?'1':'0.7');
        }
        var a=ov.querySelector('#hoh-rev-arch');
        if(a){
          a.innerHTML='&#128465; Rejected ('+rejList.length+')';
          a.style.opacity=(pile==='rejected'?'1':(rejList.length?'0.85':'0.7'));
        }
        var h=ov.querySelector('#hoh-rev-hidden');
        if(h){
          /* The count is unknown until the pile is opened once, and saying so
             is more honest than printing a zero that is only "not asked yet". */
          h.textContent = hidLoading ? 'Hidden (…)'
                        : (hidList===null ? 'Hidden' : 'Hidden ('+hidList.length+')');
          h.style.opacity=(pile==='hidden'?'1':'0.7');
        }
      }
      /* R248: the word-count sweep. Counts words the same way the
         Lorekeeper badge does; QUEUE heroes only, never the main hall. */
      function revWordCount(s){ if(!s) return 0; var t=String(s).trim(); return t?t.split(/\s+/).length:0; }
      function wcHits(n){ return q.filter(function(c){ return revWordCount(c.story) < n; }); }
      function wcPreview(){
        if(!ov) return;
        var sl=ov.querySelector('#hoh-rev-wc'), rd=ov.querySelector('#hoh-rev-wc-read'), go=ov.querySelector('#hoh-rev-wc-go');
        if(!sl||!rd||!go) return;
        var n=parseInt(sl.value,10)||0;
        if(n<1){ rd.textContent='off'; go.textContent='Reject 0'; go.disabled=true; return; }
        var hits=wcHits(n);
        rd.innerHTML='under <b>'+n+'</b> words &middot; would reject <b>'+hits.length+'</b> of '+q.length;
        go.textContent='Reject '+hits.length;
        go.disabled=!hits.length;
      }
      function wcSweep(){
        if(busy||!SB||!ov) return;
        var n=parseInt((ov.querySelector('#hoh-rev-wc')||{}).value,10)||0;
        if(n<1) return;
        var hits=wcHits(n);
        if(!hits.length) return;
        if(!window.confirm('Reject '+hits.length+' hero'+(hits.length===1?'':'es')+' with stories under '+n+' words?\n\nThey move to the rejected pile, where you can find them again or delete them. Nothing is deleted now.')) return;
        busy=true;
        var go=ov.querySelector('#hoh-rev-wc-go'), done=0, okN=0;
        (function step(){
          if(done>=hits.length){
            busy=false;
            var ids={}; hits.forEach(function(h){ ids[String(h.id)]=1; });
            q=q.filter(function(c){ return !ids[String(c.id)]; });
            i=Math.min(i, Math.max(0, q.length-1));
            if(!q.length) i=0;
            rej+=okN;
            syncRefreshBtn(); wcPreview(); render();
            try{ alert('Rejected '+okN+' of '+hits.length+'. They wait behind "Refresh rejected".'); }catch(_){}
            return;
          }
          var c=hits[done++];
          if(go) go.textContent='Rejecting '+done+'/'+hits.length+'…';
          var meta={}; try{ meta=Object.assign({},c.quest_meta||{}); }catch(_){}
          meta.review_state='archived';
          meta.reject_count=((meta.reject_count|0)||0)+1;
          meta.reject_reason='short_story_under_'+n;
          SB.from('characters').update({ is_hidden:true, quest_meta:meta }).eq('id',c.id).select('id').then(function(r){
            if(!r.error){
              c.quest_meta=meta; okN++; rejList.push(c);
              try{ var live=(typeof window.hohGetAllCharactersFull==='function')?window.hohGetAllCharactersFull():null; if(live){ for(var k=0;k<live.length;k++){ if(String(live[k].id)===String(c.id)){ live[k].quest_meta=meta; live[k].is_hidden=true; break; } } } }catch(_){}
            }
            step();
          }, function(){ step(); });
        })();
      }
      function delReview(skipConfirm){
        if(busy||i>=q.length||!SB) return; var c=q[i];
        if(!skipConfirm && !window.confirm('Delete '+(c.name||'this hero')+' permanently? This removes them from the Hall and the database for good, and cannot be undone.')) return;
        var email=(typeof window.hohYourHallEmail==='function')?(window.hohYourHallEmail()||''):'';
        var pin=''; try{ pin=sessionStorage.getItem('hoh-admin-pin')||''; }catch(_){}
        if(!pin){ try{ pin=window.prompt('Enter your admin PIN to delete (the same PIN you signed into the Hall with):')||''; }catch(_){} if(pin){ try{ sessionStorage.setItem('hoh-admin-pin',pin); }catch(_){} } }
        if(!pin) return;
        busy=true;
        SB.rpc('admin_delete_character',{ p_email:email, p_pin:pin, p_id:c.id }).then(function(r){
          busy=false;
          if(r.error){ alert('Delete failed: '+r.error.message); return; }
          var d=r.data||{};
          if(d.ok!==true){ if(d.error==='bad_pin'){ try{sessionStorage.removeItem('hoh-admin-pin');}catch(_){} alert('Wrong admin PIN. Press Delete again to retry.'); } else if(d.error==='not_admin'){ alert('Only the admin account can delete.'); } else { alert('Delete blocked: '+(d.error||'unknown')+'.'); } return; }
          try{ var live=(typeof window.hohGetAllCharactersFull==='function')?window.hohGetAllCharactersFull():null; if(live){ var li=live.findIndex(function(x){return x&&String(x.id)===String(c.id);}); if(li>=0) live.splice(li,1); } }catch(_){}
          try{ if(Array.isArray(window.__hohHidden)){ var hj=window.__hohHidden.findIndex(function(x){return x&&String(x.id)===String(c.id);}); if(hj>=0) window.__hohHidden.splice(hj,1); } }catch(_){}
          del++; q.splice(i,1); render(); /* spliced row shifts the next hero into i */
        }, function(){ busy=false; alert('Delete failed.'); });
      }
      function undo(){
        if(busy||!hist.length||!SB) return; var last=hist[hist.length-1];
        if(last.action==='keep'){ hist.pop(); i=Math.max(0,i-1); render(); return; }
        /* R245: undoing a reject clears the strike and puts the hero back
           into the queue at the spot it left. */
        if(last.action==='reject'){
          busy=true;
          var metaR={}; try{ metaR=Object.assign({},(last.c&&last.c.quest_meta)||{}); }catch(_){}
          metaR.review_state='pending'; metaR.reject_count=Math.max(0,(metaR.reject_count||1)-1);
          SB.from('characters').update({ is_hidden:true, quest_meta:metaR }).eq('id',last.id).select('id').then(function(r){
            busy=false; if(r.error){ alert('Undo failed.'); return; }
            if(last.c) last.c.quest_meta=metaR;
            var rlx=rejList.findIndex(function(x){return x&&String(x.id)===String(last.id);}); if(rlx>=0) rejList.splice(rlx,1);
            if(rej>0) rej--;
            var at=Math.min(last.idx, q.length);
            q.splice(at,0,last.c);
            i=at;
            hist.pop(); syncRefreshBtn(); render();
          }, function(){ busy=false; });
          return;
        }
        busy=true;
        var c=null,k; for(k=0;k<q.length;k++){ if(String(q[k].id)===String(last.id)){ c=q[k]; break; } }
        var meta={}; try{ meta=Object.assign({},(c&&c.quest_meta)||{}); }catch(_){}
        meta.review_state='pending';
        SB.from('characters').update({ is_hidden:true, quest_meta:meta }).eq('id',last.id).then(function(r){
          busy=false; if(r.error){ alert('Undo failed.'); return; }
          if(last.action==='approve'&&yes>0) yes--;
          try{ var live=(typeof window.hohGetAllCharactersFull==='function')?window.hohGetAllCharactersFull():null; if(live){ for(var j=0;j<live.length;j++){ if(String(live[j].id)===String(last.id)){ live[j].is_hidden=true; live[j].quest_meta=meta; break; } } } }catch(_){}
          hist.pop(); i=Math.max(0,i-1); render();
        }, function(){ busy=false; });
      }
      opener.addEventListener('click', open);
      document.addEventListener('keydown', function(ev){
        if(!ov || !ov.classList.contains('on')) return;
        var tag=(ev.target&&ev.target.tagName)||''; if(tag==='INPUT'||tag==='TEXTAREA') return;
        /* ⛔ R610: Escape still closes from the archive, but every other key
           here acts on the QUEUE hero and there is none on screen. Without this
           an idle keystroke while digging through the bin would approve or
           delete a hero the reviewer cannot even see. */
        if(pile){
          if(ev.key==='Escape'){ pile=''; render(); }
          return;
        }
        if(ev.key==='a'||ev.key==='A'||ev.key==='ArrowRight'){ ev.preventDefault(); act('approve'); }
        else if(ev.key==='r'||ev.key==='R'){ ev.preventDefault(); rejectReview(); }
        else if(ev.key==='k'||ev.key==='K'||ev.key==='ArrowLeft'){ ev.preventDefault(); if(i<q.length){ hist.push({id:q[i].id,action:'keep'}); i++; render(); } }
        else if(ev.key==='d'||ev.key==='D'){ ev.preventDefault(); delReview(); }
        else if(ev.key==='u'||ev.key==='U'){ ev.preventDefault(); undo(); }
        else if(ev.key==='Escape'){ close(); }
      });
    })();
  }
  /* Watch for god mode toggling on — check periodically since the
     hohIsGodMode state can flip via console after initial load. */
  function _godWatch(){
    try {
      var on = (typeof window.hohIsGodMode === 'function') && window.hohIsGodMode();
      if(on){
        _godBuildPanel();
      } else {
        var existing = document.getElementById('hoh-god-debug-panel');
        if(existing) existing.style.display = 'none';
      }
      /* If panel exists but god mode is off, hide; if on, show. */
      var panel = document.getElementById('hoh-god-debug-panel');
      if(panel) panel.style.display = on ? '' : 'none';
    } catch(_){}
  }
  /* Initial check + periodic re-check. PERF-6 (2026-08-06): the re-check ran
     for every visitor every 3s forever, hidden tabs included. It now sleeps
     while the tab is hidden and breathes at 5s — flipping god mode on from a
     console still lands within a breath. */
  try {
    _godWatch();
    setInterval(function(){ if(!document.hidden) _godWatch(); }, 5000);
  } catch(_){}
})();

/* ============================================================
   R793 · EXPLORE THE HALL, THE MAIN HALL'S EVENT (Jan, 2026-09-22)
   ============================================================
   "Explore the Hall becomes an event in the main Hall: never shows the
   same hero twice, at most 20 refreshes a day, then it goes away
   smoothly; it still pays the free Battlegrounds entry. Heroes you have
   hearted never appear in it."

   One row in the landing hub (#hoh-explore-event, static markup in the
   section). Tapping it opens the event: one hero at a time, each one a
   hero this account has never been shown, never one it has hearted and
   never its own. Every refresh is recorded by hoh_hall_explore_mark,
   which refuses a repeat and anything past 20 and, on the 20th, grants
   the day's free Battlegrounds entry exactly once. After the 20th the
   event says thanks and fades away until tomorrow (midnight Pacific).

   THE GAMES (Jan, 2026-09-24): "Explore the Hall also needs guess the race
   not just class, also needs the spot the imposter game", and "Need a
   celebration for getting things right ... it's too plain right now."
   Every hero now comes with a game, in an order shuffled per account and
   day (each five heroes bring two class guesses, two race guesses and one
   Spot the imposter; the first imposter waits for hero 4 or 5):
     Guess the class / Guess the race. The hero's class or race stays hidden
       until you pick one of three: the answer, one that looks like it and
       one that does not. Every choice is a class or race the Hall has.
     Spot the imposter. Today's hero stands beside heroes this account met
       in Explore before (never an unseen one, so no later hero is spoiled).
       All but one share a class, or a race, and you tap the one who does
       not. The rule shows after the answer ("Three Clerics and a Rogue.").
       The odd one out never looks like the rest (no Elf among Half-Elves).
       Campaigns are not used: a portrait cannot show one.
   Only the Hall's well-known classes and races are used; a multiclass or a
   homebrew race simply gets the other game. A right answer bursts pixels
   in the Hall's gold and the hero's rarity colour off the tile, the tile
   pops, the streak counter grows and the frame pulses; three right in a
   row lights a combo glow. A wrong answer shows the right one plainly,
   with no shake. A guess is for fun: it pays nothing and the server never
   hears it. The streak and the day's score live in this browser, per
   account and day. Reduced motion: no particles and no pops, colour fades.

   Signed-in players only, and only in the live Hall (the R793B doors
   installed). In LEGACY mode this block does nothing at all and the old
   Explore above keeps running inside Your Hall. No count here is ever
   the browser's: the day's number and the seen list are the doors'. */
(function(){
  'use strict';
  if(typeof window === 'undefined' || typeof document === 'undefined') return;
  var LIMIT = 20;
  var S = { open: false, current: null, busy: false, game: null, finishReady: false, thanks: false, note: '', noteKind: '', entryGranted: false, off: false };
  /* R793 QA: heroes the door refused for this account (own_hero, hearted,
     no_hero). The page's own ownership, favourite and roster data can
     disagree with the server (auth_email, a favourite saved on another
     device, a hero hidden since the roster loaded), so a refused pick is
     skipped for the rest of the visit and another hero is tried. */
  var SKIP = {};
  var SKIP_REASONS = /^(own_hero|hearted|no_hero)$/;

  function D(){ return window.HOHHallDoors || null; }
  function live(){ try { return (typeof window.hohR793Live === 'function') && window.hohR793Live(); } catch(_){ return false; } }
  function signedIn(){
    try {
      if(typeof window.hohIsHallSignedIn === 'function') return window.hohIsHallSignedIn();
      /* section-core (which defines the helper) loads after this file */
      return !!(window.HOHHallDoors && window.HOHHallDoors.creds().ok);
    } catch(_){ return false; }
  }
  function reduced(){ try { return !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches); } catch(_){ return false; } }
  function esc(s){ return String(s == null ? '' : s).replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;').replace(/"/g,'&quot;').replace(/'/g,'&#39;'); }
  function jsq(s){ return String(s == null ? '' : s).replace(/\\/g,'\\\\').replace(/'/g,"\\'").replace(/[\r\n\u2028\u2029]/g,' ').replace(/"/g,'&quot;').replace(/</g,'&lt;').replace(/>/g,'&gt;'); }
  function el(id){ return document.getElementById(id); }
  function root(){ return el('hoh-explore-event'); }
  function allHeroes(){ try { return (typeof window.hohGetAllCharacters === 'function') ? (window.hohGetAllCharacters() || []) : []; } catch(_){ return []; } }
  /* HALL-SPEED-02: the Hall paints its grid before its whole roster is in (hoh-section-core.js). A hero asked for in
     that moment waits for the roster (hoh:characters-loaded) instead of reading "You have met every hero in the Hall"
     off an empty list; once the roster is here, or has failed, it runs at once. */
  function rosterIn(){ return typeof window.hohGetAllCharacters === 'function' || allHeroes().length > 0; }
  function afterRoster(fn){
    if(rosterIn()){ fn(); return; }
    document.addEventListener('hoh:characters-loaded', function(){ fn(); }, { once: true });
  }
  function findHero(id){
    var all = allHeroes();
    for(var i = 0; i < all.length; i++){ if(all[i] && String(all[i].id) === String(id)) return all[i]; }
    return null;
  }
  /* R793.1 fix: the explore door's own owner rule (auth_email when set, else
     the form email), so a hero the door would refuse as own_hero is never
     picked in the first place */
  function isOwn(c){
    try {
      if(typeof window.hohIsHallDoorOwner === 'function') return !!window.hohIsHallDoorOwner(c);
      return (typeof window.hohIsCharacterOwner === 'function') && window.hohIsCharacterOwner(c);
    } catch(_){ return false; }
  }
  function account(){ var d = D(); return d ? d.creds().email : ''; }
  function ptDay(){ var d = D(); return d ? d.ptDay() : ''; }
  function lastKey(){ return 'hoh-r793-xev-last:' + encodeURIComponent(account()) + ':' + ptDay(); }
  function readLast(){ try { return localStorage.getItem(lastKey()) || ''; } catch(_){ return ''; } }
  function saveLast(id){ try { localStorage.setItem(lastKey(), String(id || '')); } catch(_){} }

  /* Who can still be shown: never seen, never hearted, never yours. Heroes
     with a portrait first; the Hall hides portrait-less heroes anyway. */
  function eligible(){
    var d = D(); if(!d) return [];
    var x = d.explore.cached(); var seen = x ? x.seen : {};
    var cur = S.current ? String(S.current.id) : '';
    return allHeroes().filter(function(c){
      if(!c || !c.id || c.approved === false || c.is_hidden === true) return false;
      var id = String(c.id);
      if(seen[id] || id === cur || SKIP[id]) return false;
      if(d.favourites.has(id)) return false;
      if(isOwn(c)) return false;
      return true;
    });
  }
  function withArt(pool){
    var art = pool.filter(function(c){ return !!(c.image_thumb_url || c.image_url); });
    return art.length ? art : pool;
  }
  function pickHero(){
    var p = withArt(eligible());
    return p.length ? p[Math.floor(Math.random() * p.length)] : null;
  }

  /* ---------- the games ---------- */
  /* The classes a round may use (the Player's Handbook twelve plus the two
     the Hall has many of), each with the ones that look most like it in a
     portrait. A look-alike is the second choice in a guess, and never the
     odd one out in Spot the imposter. */
  var CLASS_LOOK = {
    'Barbarian': ['Fighter', 'Paladin'],
    'Bard': ['Sorcerer', 'Rogue'],
    'Cleric': ['Paladin', 'Druid'],
    'Druid': ['Ranger', 'Cleric'],
    'Fighter': ['Barbarian', 'Paladin', 'Blood Hunter'],
    'Monk': ['Rogue', 'Fighter'],
    'Paladin': ['Fighter', 'Cleric'],
    'Ranger': ['Rogue', 'Druid', 'Blood Hunter'],
    'Rogue': ['Ranger', 'Monk'],
    'Sorcerer': ['Wizard', 'Warlock', 'Bard'],
    'Warlock': ['Sorcerer', 'Wizard'],
    'Wizard': ['Sorcerer', 'Warlock', 'Artificer'],
    'Artificer': ['Wizard', 'Fighter'],
    'Blood Hunter': ['Fighter', 'Ranger']
  };
  /* The races a round may use, each with the looks it shares with others.
     Two races that share a look are look-alikes. */
  var RACE_LOOK = {
    'Human': ['human'], 'Half-Elf': ['human', 'elf'], 'Aasimar': ['human', 'glow'], 'Dhampir': ['human', 'pale'],
    'Changeling': ['human', 'pale'], 'Kalashtar': ['human'], 'Triton': ['human', 'sea', 'glow'],
    'Elf': ['elf'], 'Drow': ['elf'], 'Eladrin': ['elf', 'fey'], 'Shadar-kai': ['elf', 'pale'], 'Astral Elf': ['elf', 'glow'],
    'Tiefling': ['horned', 'glow'], 'Satyr': ['horned', 'fey'], 'Minotaur': ['horned', 'large'], 'Genasi': ['glow'],
    'Dragonborn': ['scaled'], 'Lizardfolk': ['scaled'], 'Kobold': ['scaled', 'small'], 'Yuan-ti': ['scaled'], 'Tortle': ['scaled', 'sea'],
    'Tabaxi': ['furred'], 'Leonin': ['furred'], 'Shifter': ['furred'], 'Harengon': ['furred', 'small'], 'Kitsune': ['furred', 'fey'],
    'Halfling': ['small'], 'Gnome': ['small'], 'Dwarf': ['small'], 'Goblin': ['small'], 'Fairy': ['small', 'fey'],
    'Goliath': ['large'], 'Half-Orc': ['large'], 'Orc': ['large'], 'Hobgoblin': ['large'], 'Firbolg': ['large', 'fey'], 'Centaur': ['large', 'furred'],
    'Aarakocra': ['feathered'], 'Kenku': ['feathered'], 'Owlin': ['feathered'],
    'Warforged': ['construct']
  };
  var PLURAL = { 'Elf': 'Elves', 'Half-Elf': 'Half-Elves', 'Astral Elf': 'Astral Elves', 'Dwarf': 'Dwarves', 'Fairy': 'Fairies',
    'Aasimar': 'Aasimar', 'Dragonborn': 'Dragonborn', 'Drow': 'Drow', 'Tabaxi': 'Tabaxi', 'Genasi': 'Genasi', 'Warforged': 'Warforged',
    'Kitsune': 'Kitsune', 'Aarakocra': 'Aarakocra', 'Kalashtar': 'Kalashtar', 'Harengon': 'Harengon', 'Kenku': 'Kenku',
    'Eladrin': 'Eladrin', 'Shadar-kai': 'Shadar-kai', 'Lizardfolk': 'Lizardfolk', 'Leonin': 'Leonin', 'Yuan-ti': 'Yuan-ti', 'Owlin': 'Owlin' };
  var NUM = ['', 'One', 'Two', 'Three'];
  var PROMPT = { 'class': 'Guess the class', race: 'Guess the race', imposter: 'Spot the imposter' };
  function keyOf(s){ return String(s == null ? '' : s).toLowerCase().replace(/[^a-z]+/g, ''); }
  var CLASS_BY_KEY = {}, RACE_BY_KEY = {};
  Object.keys(CLASS_LOOK).forEach(function(v){ CLASS_BY_KEY[keyOf(v)] = v; });
  Object.keys(RACE_LOOK).forEach(function(v){ RACE_BY_KEY[keyOf(v)] = v; });
  function classOf(c){ return (c && CLASS_BY_KEY[keyOf(c.character_class)]) || ''; }
  function raceOf(c){ return (c && RACE_BY_KEY[keyOf(c.race)]) || ''; }
  function traitOf(c, t){ return t === 'class' ? classOf(c) : raceOf(c); }
  function looksAlike(t, a, b){
    if(!a || !b) return false;
    if(a === b) return true;
    if(t === 'class') return (CLASS_LOOK[a] || []).indexOf(b) >= 0 || (CLASS_LOOK[b] || []).indexOf(a) >= 0;
    var fa = RACE_LOOK[a] || [], fb = RACE_LOOK[b] || [];
    for(var i = 0; i < fa.length; i++){ if(fb.indexOf(fa[i]) >= 0) return true; }
    return false;
  }
  function plural(v){ return PLURAL[v] || (v + 's'); }
  function aOr(v){ return (/^[aeiou]/i.test(v) ? 'an ' : 'a ') + v; }
  function cap(s){ return s.charAt(0).toUpperCase() + s.slice(1); }
  function shuffle(a, rnd){
    rnd = rnd || Math.random;
    for(var i = a.length - 1; i > 0; i--){ var j = Math.floor(rnd() * (i + 1)); var t = a[i]; a[i] = a[j]; a[j] = t; }
    return a;
  }
  /* familiar values come up more often than rare ones, but every one can */
  function pickWeighted(vals, counts){
    var tot = 0, i;
    for(i = 0; i < vals.length; i++) tot += Math.sqrt(counts[vals[i]] || 1);
    var r = Math.random() * tot;
    for(i = 0; i < vals.length; i++){ r -= Math.sqrt(counts[vals[i]] || 1); if(r <= 0) return vals[i]; }
    return vals[vals.length - 1];
  }
  var _counts = null, _countsN = -1;
  function hallCounts(){
    var all = allHeroes();
    if(_counts && _countsN === all.length) return _counts;
    var cc = {}, rc = {};
    all.forEach(function(c){
      if(!c || c.approved === false || c.is_hidden === true) return;
      var k = classOf(c); if(k) cc[k] = (cc[k] || 0) + 1;
      var r = raceOf(c); if(r) rc[r] = (rc[r] || 0) + 1;
    });
    _counts = { 'class': cc, race: rc }; _countsN = all.length;
    return _counts;
  }
  /* three choices: the answer, one that looks like it, one that does not,
     all of them classes or races the Hall really has */
  function optionsFor(t, answer){
    var counts = hallCounts()[t] || {};
    var min = (t === 'race') ? 3 : 1;
    var known = Object.keys(t === 'class' ? CLASS_LOOK : RACE_LOOK).filter(function(v){ return v !== answer && (counts[v] | 0) >= min; });
    var alike = known.filter(function(v){ return looksAlike(t, answer, v); });
    var other = known.filter(function(v){ return !looksAlike(t, answer, v); });
    var out = [answer];
    if(alike.length) out.push(pickWeighted(alike, counts));
    while(out.length < 3){
      var rest = other.filter(function(v){ return out.indexOf(v) < 0; });
      if(!rest.length) rest = alike.filter(function(v){ return out.indexOf(v) < 0; });
      if(!rest.length) return null;
      out.push(pickWeighted(rest, counts));
    }
    return shuffle(out);
  }
  /* Heroes this account has already met in Explore (any day), who can stand
     beside today's new hero without spoiling a later refresh: never hidden,
     never a favourite, never the player's own, always with a portrait. */
  function companions(exceptId){
    var d = D(); var x = d && d.explore.cached(); if(!x) return [];
    return allHeroes().filter(function(c){
      if(!c || !c.id || c.approved === false || c.is_hidden === true) return false;
      var id = String(c.id);
      if(!x.seen[id] || id === exceptId || SKIP[id]) return false;
      if(d.favourites.has(id) || isOwn(c)) return false;
      return !!(c.image_thumb_url || c.image_url);
    });
  }
  function groupsOf(pool){
    var g = { 'class': {}, race: {} };
    pool.forEach(function(h){
      var k = classOf(h); if(k) (g['class'][k] = g['class'][k] || []).push(h);
      var r = raceOf(h); if(r) (g.race[r] = g.race[r] || []).push(h);
    });
    return g;
  }
  /* Spot the imposter around hero c. Either c stands in a group that shares
     its class or race and a hero met before is the odd one out, or c is the
     odd one out beside heroes who share theirs. Four heroes once the account
     has met enough, three early on its first day, null when no fair set
     exists yet. The category (class or race) is named in the question. */
  function lineupFor(c, groups, allowThree){
    var ts = shuffle(['race', 'class']);
    /* four heroes in either category first; three only when asked for */
    for(var i = 0; i < ts.length * (allowThree ? 2 : 1); i++){
      var four = i < ts.length;
      var t = ts[i % ts.length], v = traitOf(c, t);
      if(!v) continue;
      var by = groups[t] || {};
      var same = (by[v] || []).filter(function(h){ return String(h.id) !== String(c.id); });
      var odd = [], outs = [], outs2 = [];
      Object.keys(by).forEach(function(u){
        if(looksAlike(t, v, u)) return;
        odd = odd.concat(by[u]);
        if(by[u].length >= 3) outs.push(u);
        if(by[u].length >= 2) outs2.push(u);
      });
      var plans = [];
      if(four){
        if(same.length >= 2 && odd.length) plans.push('in', 'in');
        if(outs.length) plans.push('out');
      } else {
        if(same.length >= 1 && odd.length) plans.push('in3');
        if(outs2.length) plans.push('out3');
      }
      if(!plans.length) continue;
      var plan = plans[Math.floor(Math.random() * plans.length)];
      var group, imp, gv, iv;
      if(plan === 'in' || plan === 'in3'){
        group = [c].concat(shuffle(same.slice()).slice(0, plan === 'in' ? 2 : 1));
        imp = odd[Math.floor(Math.random() * odd.length)];
        gv = v; iv = traitOf(imp, t);
      } else {
        var list = (plan === 'out') ? outs : outs2;
        var u = list[Math.floor(Math.random() * list.length)];
        group = shuffle(by[u].slice()).slice(0, plan === 'out' ? 3 : 2);
        imp = c; gv = u; iv = v;
      }
      return { kind: 'imposter', trait: t, id: String(c.id), answer: String(imp.id), groupVal: gv, impVal: iv, groupN: group.length,
        tiles: shuffle(group.concat([imp])).map(function(h){ return String(h.id); }), picked: null };
    }
    return null;
  }
  /* The day's order of games, the same on every reload of the day and
     different for every account and day. */
  function hashStr(s){ var h = 2166136261; for(var i = 0; i < s.length; i++){ h ^= s.charCodeAt(i); h = Math.imul(h, 16777619); } return h >>> 0; }
  function seeded(seed){
    var s = seed | 0;
    return function(){ s = s + 0x6D2B79F5 | 0; var t = Math.imul(s ^ s >>> 15, 1 | s); t = t + Math.imul(t ^ t >>> 7, 61 | t) ^ t; return ((t ^ t >>> 14) >>> 0) / 4294967296; };
  }
  var _plan = null, _planKey = '';
  function dayPlan(){
    var key = account() + '|' + ptDay();
    if(_plan && _planKey === key) return _plan;
    var r = seeded(hashStr(key)), out = [];
    for(var b = 0; b < 4; b++){
      var block = null;
      for(var tries = 0; tries < 60; tries++){
        var cand = shuffle(['class', 'race', 'class', 'race', 'imposter'], r);
        var seq = out.concat(cand), ok = !(b === 0 && cand.indexOf('imposter') < 3);
        for(var i = 1; i < seq.length && ok; i++){
          if(seq[i] === 'imposter' && seq[i - 1] === 'imposter') ok = false;
          if(i >= 2 && seq[i] === seq[i - 1] && seq[i] === seq[i - 2]) ok = false;
        }
        if(ok){ block = cand; break; }
      }
      out = out.concat(block || ['class', 'race', 'class', 'imposter', 'race']);
    }
    _plan = out; _planKey = key;
    return out;
  }
  function kindFor(n){ var p = dayPlan(); return p[(Math.max(1, n | 0) - 1) % p.length] || 'class'; }
  function buildGame(c, kind){
    if(!c) return null;
    var order = (kind === 'race') ? ['race', 'class'] : ['class', 'race'];
    if(kind === 'imposter'){
      var L = lineupFor(c, groupsOf(companions(String(c.id))), true);
      if(L) return L;
      if(Math.random() < 0.5) order = ['race', 'class'];
    }
    for(var i = 0; i < order.length; i++){
      var t = order[i], v = traitOf(c, t), opts = v ? optionsFor(t, v) : null;
      if(opts) return { kind: t, trait: t, id: String(c.id), options: opts, answer: v, picked: null };
    }
    return null;
  }
  /* the next hero, chosen to fit the game the day's order asks for */
  function pickFor(kind){
    var p = withArt(eligible());
    if(!p.length) return null;
    var playable = p.filter(function(c){ return !!(classOf(c) || raceOf(c)); });
    if(kind === 'imposter'){
      /* a hero who makes a lineup of four, else one of three */
      var groups = groupsOf(companions(''));
      var cands = shuffle(playable.slice()).slice(0, 80);
      for(var pass = 0; pass < 2; pass++){
        for(var i = 0; i < cands.length; i++){
          var L = lineupFor(cands[i], groups, pass === 1);
          if(L) return { hero: cands[i], game: L, kind: 'imposter' };
        }
      }
      kind = (Math.random() < 0.5) ? 'race' : 'class';
    }
    var fit = p.filter(function(c){ return !!traitOf(c, kind); });
    var q = fit.length ? fit : (playable.length ? playable : p);
    return { hero: q[Math.floor(Math.random() * q.length)], game: null, kind: kind };
  }
  function gameFor(c){
    var g = S.game;
    if(!g || !c || g.id !== String(c.id)) return null;
    if(g.kind === 'imposter'){
      for(var i = 0; i < g.tiles.length; i++){ if(!findHero(g.tiles[i])) return null; }
    }
    return g;
  }

  /* ---------- the day's play, kept in this browser ---------- */
  var P = null, PK = '';
  function play(){
    var k = 'hoh-xev-play:' + encodeURIComponent(account()) + ':' + ptDay();
    if(P && PK === k) return P;
    PK = k; P = null;
    try { var s = JSON.parse(localStorage.getItem(k) || 'null'); if(s && s.v === 1) P = s; } catch(_){}
    if(!P){
      P = { v: 1, streak: 0, best: 0, right: 0, played: 0, game: null };
      /* one record per account and day: earlier days' records go */
      try {
        var pre = 'hoh-xev-play:' + encodeURIComponent(account()) + ':', gone = [];
        for(var i = 0; i < localStorage.length; i++){ var key = localStorage.key(i); if(key && key.indexOf(pre) === 0 && key !== k) gone.push(key); }
        gone.forEach(function(key){ localStorage.removeItem(key); });
      } catch(_){}
    }
    return P;
  }
  function savePlay(){ var p = play(); try { localStorage.setItem(PK, JSON.stringify(p)); } catch(_){} }
  /* the streak counter grows at 2, 4, 7 and 10; the frame glows at 3, 5, 10 */
  function streakTier(n){ return n >= 10 ? 4 : n >= 7 ? 3 : n >= 4 ? 2 : n >= 2 ? 1 : 0; }
  function comboOf(n){ return n >= 10 ? 3 : n >= 5 ? 2 : n >= 3 ? 1 : 0; }

  /* ---------- pixels ----------
     The Hall's one particle canvas (hohFx, hoh-fx-canvas.js): its reduced
     motion rule, its particle cap and its zero idle cost. Square pixels on
     whole-pixel positions, never rotated, in the Hall's gold and a rarity
     colour. Without hohFx a small DOM burst stands in. */
  var GOLD = ['#f2d98e', '#ffe9ad', '#fff5d8', '#e8c674'];
  var RAR_COL = { common: '#d9d2c3', uncommon: '#8fe08a', rare: '#6cb8ff', epic: '#c890ff', legendary: '#ffc46b', mythic: '#f3a8ff' };
  function pixelFx(){
    var fx = window.hohFx;
    if(!fx || typeof fx.emit !== 'function') return null;
    if(!(fx.has && fx.has('xev_pixels')) && typeof fx.define === 'function'){
      fx.define('xev_pixels', {
        spawn: function(o){
          var out = [], n = Math.max(6, Math.min(48, o.n | 0 || 22));
          var cols = (o.colors && o.colors.length) ? o.colors : GOLD;
          var hw = Math.max(0, o.w || 0) * 0.4, hh = Math.max(0, o.h || 0) * 0.4, pw = o.power || 1;
          for(var i = 0; i < n; i++){
            var a = Math.PI * 2 * i / n + (Math.random() - 0.5) * 0.7;
            var sp = (110 + Math.random() * 150) * pw;
            out.push({ x: o.x + Math.cos(a) * hw, y: o.y + Math.sin(a) * hh, vx: Math.cos(a) * sp, vy: Math.sin(a) * sp - 50 * pw,
              life: 0, ttl: 0.5 + Math.random() * 0.32, s: (Math.random() < 0.3) ? 6 : ((Math.random() < 0.5) ? 5 : 4),
              color: cols[i % cols.length], shrink: Math.random() < 0.3 });
          }
          return out;
        },
        step: function(p, dt){
          p.life += dt;
          p.x += p.vx * dt; p.y += p.vy * dt;
          p.vx *= (1 - 3.2 * dt); p.vy = p.vy * (1 - 3.2 * dt) + 280 * dt;
          return p.life < p.ttl;
        },
        draw: function(c, p){
          var k = p.life / p.ttl;
          c.globalAlpha = (k < 0.65) ? 1 : Math.max(0, (1 - k) / 0.35);
          c.fillStyle = p.color;
          var s = Math.max(2, Math.round(p.s * ((p.shrink && k > 0.5) ? 0.6 : 1)));
          c.fillRect(Math.round(p.x - s / 2), Math.round(p.y - s / 2), s, s);
        }
      });
    }
    return (fx.has && fx.has('xev_pixels')) ? fx : null;
  }
  function domPixels(o){
    try {
      var layer = document.createElement('span');
      layer.className = 'hoh-xev-pixfx';
      layer.setAttribute('aria-hidden', 'true');
      layer.style.left = Math.round(o.x) + 'px'; layer.style.top = Math.round(o.y) + 'px';
      var n = Math.min(24, o.n);
      for(var i = 0; i < n; i++){
        var s = document.createElement('i');
        var a = Math.PI * 2 * i / n + (Math.random() - 0.5) * 0.5, dist = (40 + Math.random() * 50) * (o.power || 1);
        s.style.background = o.colors[i % o.colors.length];
        layer.appendChild(s);
        if(s.animate) s.animate([{ transform: 'translate(0,0)', opacity: 1 },
          { transform: 'translate(' + Math.round(Math.cos(a) * dist) + 'px,' + Math.round(Math.sin(a) * dist + 14) + 'px)', opacity: 0 }],
          { duration: 560 + Math.random() * 220, easing: 'cubic-bezier(.2,.7,.3,1)', fill: 'forwards' });
      }
      document.body.appendChild(layer);
      setTimeout(function(){ if(layer.parentNode) layer.parentNode.removeChild(layer); }, 900);
    } catch(_){}
  }
  function burstAt(node, o){
    if(!node || reduced() || document.hidden) return false;
    var r = node.getBoundingClientRect();
    if(!r.width && !r.height) return false;
    var opts = { x: r.left + r.width / 2, y: r.top + r.height / 2, w: r.width, h: r.height,
      n: o.n || 22, colors: o.colors || GOLD, power: o.power || 1 };
    var fx = pixelFx();
    if(fx && fx.emit('xev_pixels', opts)) return true;
    domPixels(opts);
    return true;
  }

  /* ---------- the bar ---------- */
  function today(){ var d = D(); var x = d && d.explore.cached(); return x ? (x.today | 0) : 0; }
  function limit(){ var d = D(); var x = d && d.explore.cached(); return (x && x.limit) ? x.limit : LIMIT; }
  /* the day's count rolls up to its number (a quick tick for one more hero,
     a short count on the page's first paint); a lower number is set at once */
  function countTo(node, to){
    if(!node) return;
    var from = parseInt(node.textContent, 10);
    if(!(from >= 0) || to <= from || reduced() || document.hidden){ node.__xevTo = to; node.textContent = String(to); return; }
    if(node.__xevTo === to) return;
    node.__xevTo = to;
    var dur = Math.min(620, 150 + (to - from) * 45), t0 = 0;
    function f(t){
      if(node.__xevTo !== to) return;
      if(!t0) t0 = t;
      var k = Math.min(1, (t - t0) / dur), e = 1 - Math.pow(1 - k, 3);
      node.textContent = String(Math.round(from + (to - from) * e));
      if(k < 1) requestAnimationFrame(f);
    }
    requestAnimationFrame(f);
    setTimeout(function(){ if(node.__xevTo === to) node.textContent = String(to); }, dur + 160);
    node.classList.remove('is-tick'); void node.offsetWidth; node.classList.add('is-tick');
  }
  function paintBar(){
    var n = today(), lim = limit();
    var cn = el('hoh-xev-count-n'); if(cn) countTo(cn, n);
    var cnt = el('hoh-xev-count');
    if(cnt){
      cnt.lastChild && (cnt.lastChild.nodeValue = '/' + lim);
      cnt.setAttribute('aria-label', n + ' of ' + lim + ' today');
      cnt.classList.toggle('is-full', n >= lim);
    }
    var sub = el('hoh-xev-sub');
    if(sub){
      /* R793.1 (Jan, 2026-09-22): "remove the text that says the last one
         pays a free battlegrounds entry, they dont know what that is". The
         server still pays it on the 20th; only the words go. */
      var left = Math.max(0, lim - n);
      sub.textContent = (n <= 0)
        ? 'Meet ' + lim + ' heroes you have never seen.'
        : (left > 0 ? (left + (left === 1 ? ' hero' : ' heroes') + ' left today.') : 'All ' + lim + ' met today. Thanks for exploring.');
    }
    var bar = el('hoh-xev-bar'); if(bar) bar.setAttribute('aria-expanded', S.open ? 'true' : 'false');
    var r = root(); if(r) r.classList.toggle('is-open', !!S.open);
  }
  function paintCombo(){
    var r = root(); if(!r) return;
    var k = comboOf(play().streak);
    if(k) r.setAttribute('data-combo', String(k)); else r.removeAttribute('data-combo');
  }

  /* ---------- the body ---------- */
  /* opts.fill: the event opens and the day's segments fill in, one after
     another; opts.step: a new hero, only today's segment fills */
  function progressHtml(n, lim, opts){
    opts = opts || {};
    var segs = '';
    for(var i = 1; i <= lim; i++) segs += '<i class="' + (i <= n ? 'on' : '') + (i === n ? ' now' : '') + (i === lim ? ' last' : '') + '" style="--k:' + (i - 1) + '"></i>';
    return '<div class="hoh-xev-progress' + (opts.fill ? ' is-fill' : (opts.step ? ' is-step' : '')) + (n >= lim ? ' is-full' : '') + '" aria-hidden="true">' + segs + '</div>';
  }
  /* R795: the hero's Hall tier (the hero page score's stored tier, or Mythic
     for a FramedFantasy portrait), the same tier the grid tile wears */
  function tierOf(c){
    try {
      if(typeof window.hohHallTier === 'function') return window.hohHallTier(c).tier || 'common';
      return (typeof window.hohGetRarity === 'function') ? window.hohGetRarity((c && c.hearts) || 0).tier : 'common';
    } catch(_){ return 'common'; }
  }
  /* R793.1 (Jan, 2026-09-22): "make the image square and remember to inherit
     the resize on both desktop and mobile for the characters image." The
     portrait is framed exactly like the same hero's main Hall grid tile
     (hoh-card-render.js): the same source (thumb, else full), a square frame
     with object-fit:cover (hall-of-heroes.css), and the hero's saved crop and
     zoom from hohImgStyle (object-position, transform-origin, scale). Those
     values have no phone or desktop variant, so 390 and 1280 frame alike, and
     a reframe saved while Explore is open repaints here too (the
     .hoh-xev-hero selector in hohForceImageOffsetRepaint and the
     hoh:image-offset-changed listener in hoh-section-core.js). */
  function artHtml(c){
    var src = c.image_thumb_url || c.image_url || '';
    var style = '';
    try { style = (typeof window.hohImgStyle === 'function') ? window.hohImgStyle(c.id, 'primary') : ''; } catch(_){}
    var letter = esc(String(c.name || '?').charAt(0));
    return src
      ? '<img src="' + esc(src) + '" alt="" loading="eager" decoding="async" crossorigin="anonymous" draggable="false" style="' + esc(style) + '" data-letter="' + letter + '" onerror="if(window.hohImgFallback)hohImgFallback(this)">'
      : '<span class="hoh-xev-letter" aria-hidden="true">' + letter + '</span>';
  }
  function bgOf(c){ try { return (typeof window.placeholderBg === 'function') ? window.placeholderBg(c.name) : ''; } catch(_){ return ''; } }
  function portraitHtml(c){
    return '<button type="button" class="hoh-xev-portrait" style="background:' + bgOf(c) + '" onclick="event.stopPropagation();if(window.hohOpenHallCard)hohOpenHallCard(\'' + jsq(c.id) + '\',this)" aria-label="Open ' + esc(c.name) + '">' +
      artHtml(c) + '</button>';
  }
  function metaHtml(c){
    var lv = parseInt(c.dnd_level, 10);
    var lvTxt = (lv >= 1) ? ' &middot; Level ' + Math.min(20, lv) : '';
    var g = gameFor(c);
    var race = esc(c.race || ''), cls = esc(c.character_class || '');
    /* the hidden class or race is a chip: "???" until the answer, then the
       same chip filled in (gold when it was guessed right) */
    if(g){
      var chip = g.picked
        ? function(txt){ return '<span class="hoh-xev-revealed' + (g.picked === g.answer ? ' is-right' : '') + '">' + txt + '</span>'; }
        : function(){ return '<span class="hoh-xev-hidden">???</span>'; };
      if(g.trait === 'class') cls = chip(cls); else race = chip(race);
    }
    return '<p class="hoh-xev-meta">' + race + ' ' + cls + lvTxt + '</p>';
  }
  function ruleText(g){ return NUM[g.groupN] + ' ' + plural(g.groupVal) + ' and ' + aOr(g.impVal) + '.'; }
  function resultText(g){
    var right = g.picked === g.answer;
    var body = (g.kind === 'imposter') ? ruleText(g) : cap(aOr(g.answer)) + '.';
    var tail = (right && (g.streakAt | 0) >= 3) ? ' ' + g.streakAt + ' in a row.' : '';
    return (right ? 'Right. ' : 'Not this time. ') + body + tail;
  }
  function resHtml(g, fresh){
    return '<p class="hoh-xev-guess-res ' + (g.picked === g.answer ? 'is-right' : 'is-wrong') + (fresh ? ' is-fresh' : '') + '">' + esc(resultText(g)) + '</p>';
  }
  function guessHtml(g, opts){
    var q = PROMPT[g.kind];
    var btns = g.options.map(function(o){
      var cls = 'hoh-xev-guess-opt';
      if(g.picked){ if(o === g.answer) cls += ' is-right'; else if(o === g.picked) cls += ' is-wrong'; }
      return '<button type="button" class="' + cls + '" data-xev-pick="' + esc(o) + '"' + (g.picked ? ' disabled' : '') + ' onclick="event.stopPropagation();hohExploreEventPick(this.getAttribute(\'data-xev-pick\'),this)">' + esc(o) + '</button>';
    }).join('');
    return '<div class="hoh-xev-guess hoh-xev-game hoh-xev-game-' + g.kind + (g.picked ? ' is-answered' : '') + (opts.enter ? ' is-in' : '') + '" data-xev-game="' + g.kind + '" data-xev-for="' + esc(g.id) + '" role="group" aria-label="' + q + '">' +
      '<p class="hoh-xev-guess-q">' + q + '</p>' +
      '<div class="hoh-xev-guess-opts">' + btns + '</div>' +
      (g.picked ? resHtml(g) : '') +
    '</div>';
  }
  function tileHtml(g, h, i){
    var id = String(h.id), isImp = id === g.answer, isNew = id === g.id, told = !!g.picked;
    var tv = traitOf(h, g.trait);
    var cls = 'hoh-xev-imp-tile hoh-xev-rar-' + tierOf(h) + (isNew ? ' is-new' : '') + (told ? ' is-told' : '');
    if(told){
      if(isImp) cls += ' is-imposter';
      if(id === g.picked) cls += isImp ? ' is-right' : ' is-wrong';
      else if(isImp) cls += ' is-missed';
    }
    var label = esc(h.name) + (isNew ? ', new today' : '') + (told ? ', ' + esc(tv) + (isImp ? ', the imposter' : '') + '. Open hero' : '');
    return '<button type="button" class="' + cls + '" data-xev-pick="' + esc(id) + '" style="--i:' + i + '" aria-label="' + label + '" onclick="event.stopPropagation();hohExploreEventPick(this.getAttribute(\'data-xev-pick\'),this)">' +
      '<span class="hoh-xev-imp-art" style="background:' + bgOf(h) + '">' + artHtml(h) +
        (isNew ? '<span class="hoh-xev-imp-new" aria-hidden="true">New</span>' : '') +
        (told && isImp ? '<span class="hoh-xev-imp-tag" aria-hidden="true">Imposter</span>' : '') +
      '</span>' +
      '<span class="hoh-xev-imp-name" aria-hidden="true">' + esc(h.name) + '</span>' +
      (told ? '<span class="hoh-xev-imp-trait" aria-hidden="true">' + esc(tv) + '</span>' : '') +
    '</button>';
  }
  function impHtml(g, opts){
    var hs = g.tiles.map(findHero);
    var lead = NUM[g.groupN] + ' of them share a ' + g.trait + '. Tap the one who doesn&rsquo;t.';
    return '<div class="hoh-xev-guess hoh-xev-game hoh-xev-game-imposter' + (g.picked ? ' is-answered' : '') + (opts.enter ? ' is-in' : '') + '" data-xev-game="imposter" data-xev-for="' + esc(g.id) + '" data-n="' + hs.length + '" role="group" aria-label="Spot the imposter">' +
      '<p class="hoh-xev-guess-q">Spot the imposter</p>' +
      '<p class="hoh-xev-imp-lead">' + lead + '</p>' +
      '<div class="hoh-xev-imp-grid" data-n="' + hs.length + '">' + hs.map(function(h, i){ return tileHtml(g, h, i); }).join('') + '</div>' +
      (g.picked ? resHtml(g) : '') +
    '</div>';
  }
  function gameHtml(c, opts){
    var g = gameFor(c);
    if(!g) return '';
    return (g.kind === 'imposter') ? impHtml(g, opts || {}) : guessHtml(g, opts || {});
  }
  function streakHtml(){
    var st = play().streak, on = st >= 2;
    return '<span class="hoh-xev-streak' + (on ? ' is-on' : '') + '" data-tier="' + streakTier(st) + '"' + (on ? '' : ' aria-hidden="true"') + '>' +
      '<i class="hoh-xev-streak-ico" aria-hidden="true"></i><b>' + st + '</b> in a row</span>';
  }
  /* R793.1 (Jan): "instead of heart, write add to favorites". The words
     match the detailed view's button (hohFavPaint keeps them in step).
     The visible words are the button's name (no aria-label), so a screen
     reader and a voice command both meet "Favorite" or "Favorited";
     the words already say the state, so there is no aria-pressed either. */
  function favHtml(c){
    var on = (typeof window.hohFavIsOn === 'function') && window.hohFavIsOn(c.id);
    return '<button type="button" class="hoh-xev-btn hoh-xev-fav' + (on ? ' is-on hearted' : '') + '" data-fav-id="' + esc(c.id) + '" onclick="event.stopPropagation();hohHeart(\'' + jsq(c.id) + '\',this)">' +
      '<span class="hoh-fav-glyph" aria-hidden="true">' + (on ? '&#9829;&#xFE0E;' : '&#9825;&#xFE0E;') + '</span><span class="hoh-fav-label">' + (on ? 'Favorited' : 'Favorite') + '</span></button>';
  }
  function heroHtml(c, opts){
    opts = opts || {};
    var n = today(), lim = limit();
    var tier = tierOf(c);
    var g = gameFor(c), imp = !!(g && g.kind === 'imposter');
    var story = String(c.story || '').replace(/\s+/g, ' ').trim();
    if(story.length > 190) story = story.slice(0, 187).replace(/\s+\S*$/, '') + '...';
    var nextBtn = S.finishReady
      ? '<button type="button" class="hoh-xev-btn hoh-xev-primary" onclick="event.stopPropagation();hohExploreEventFinish()">Finish for today</button>'
      : '<button type="button" class="hoh-xev-btn hoh-xev-primary' + (S.rosterWait ? ' hoh-roster-wait' : '') + '" data-xev-next onclick="event.stopPropagation();hohExploreEventNext()"' + (S.busy ? ' disabled aria-busy="true"' : '') + '>Next hero<span class="hoh-xev-next-arrow" aria-hidden="true"></span></button>';
    /* Spot the imposter: the lineup carries today's hero's portrait (marked
       New), so the hero's own card below is the words alone */
    var article = '<article class="hoh-xev-hero hoh-xev-rar-' + tier + (imp ? ' is-compact' : '') + '" data-hero-id="' + esc(c.id) + '"' + (imp ? ' data-n="' + g.tiles.length + '"' : '') + '>' +
        (imp ? '' : portraitHtml(c)) +
        '<div class="hoh-xev-info">' +
          (imp ? '<p class="hoh-xev-new-eyebrow">New today</p>' : '') +
          '<h3 class="hoh-xev-name hoh-xev-name-rar-' + tier + '">' + esc(c.name) + '</h3>' +
          metaHtml(c) +
          (c.campaign ? '<p class="hoh-xev-campaign">' + esc(c.campaign) + '</p>' : '') +
          (story ? '<p class="hoh-xev-story">' + esc(story) + '</p>' : '') +
          '<p class="hoh-xev-by">by ' + esc(c.player_name || 'a player') + '</p>' +
        '</div>' +
      '</article>';
    var game = gameHtml(c, opts);
    return progressHtml(n, lim, opts) +
      '<div class="hoh-xev-progress-label"><span>Hero <b class="hoh-xev-n' + (opts.step ? ' is-tick' : '') + '">' + n + '</b> of ' + lim + ' today</span>' + streakHtml() + '</div>' +
      (imp ? game + article : article + game) +
      (S.note ? '<p class="hoh-xev-note' + (S.noteKind ? ' is-' + S.noteKind : '') + '" role="status">' + S.note + '</p>' : '') +
      '<div class="hoh-xev-actions">' +
        favHtml(c) +
        '<button type="button" class="hoh-xev-btn hoh-xev-open" onclick="event.stopPropagation();if(window.hohOpenHallCard)hohOpenHallCard(\'' + jsq(c.id) + '\',this)">Open hero</button>' +
        nextBtn +
      '</div>';
  }
  function introHtml(opts){
    var n = today(), lim = limit();
    return progressHtml(n, lim, opts) +
      '<div class="hoh-xev-intro">' +
        /* R793.1 fix: the bar right above already says "Meet 20 heroes you
           have never seen." or "N heroes left today.", so the lead says what
           the bar does not (it also fixes "1 heroes left") */
        '<p class="hoh-xev-intro-lead">' + (n > 0 ? 'Hero ' + (n + 1) + ' of ' + lim + ' is next.' : 'They show up one at a time.') + '</p>' +
        '<p class="hoh-xev-intro-sub">Add the ones you like to your favorites.</p>' +
        (S.note ? '<p class="hoh-xev-note' + (S.noteKind ? ' is-' + S.noteKind : '') + '" role="status">' + S.note + '</p>' : '') +
        '<button type="button" class="hoh-xev-btn hoh-xev-primary' + (S.rosterWait ? ' hoh-roster-wait' : '') + '" data-xev-next onclick="event.stopPropagation();hohExploreEventNext()"' + (S.busy ? ' disabled aria-busy="true"' : '') + '>' + (n > 0 ? 'Meet the next hero' : 'Meet a hero') + '</button>' +
      '</div>';
  }
  /* R793.1: opening Explore jumps straight to a hero; this holds the row's
     shape for the moment the first one takes to arrive. */
  function loadingHtml(opts){
    return progressHtml(today(), limit(), opts) +
      '<div class="hoh-xev-loading" role="status" aria-busy="true"><span class="hoh-xev-loading-dot" aria-hidden="true"></span>Finding your next hero</div>';
  }
  /* the day's finish: the score of today's games (when any were played) */
  function thanksHtml(){
    var p = play(), tally = '';
    if(p.played > 0){
      tally = '<p class="hoh-xev-thanks-tally">You got <b>' + p.right + '</b> of ' + p.played + ' right.' +
        (p.best >= 2 ? ' Your best streak was <b>' + p.best + '</b>.' : '') + '</p>';
    }
    var told = tales().told | 0;
    if(told > 0) tally += '<p class="hoh-xev-thanks-tally">You told <b>' + told + '</b> ' + (told === 1 ? 'tale' : 'tales') + '.</p>';
    return '<div class="hoh-xev-thanks is-finale" role="status">' +
      '<span class="hoh-xev-thanks-mark" aria-hidden="true"></span>' +
      '<p class="hoh-xev-thanks-lead">That is all ' + limit() + ' for today.</p>' +
      tally +
      '<p class="hoh-xev-thanks-line">Thanks for exploring the Hall.</p>' +
      '<p class="hoh-xev-thanks-sub">' + limit() + ' more tomorrow.</p>' +
    '</div>';
  }
  function exhaustedHtml(){
    return '<div class="hoh-xev-thanks" role="status">' +
      '<p class="hoh-xev-thanks-lead">You have met every hero in the Hall.</p>' +
      '<p class="hoh-xev-thanks-sub">New heroes join all the time. This comes back when they do.</p>' +
    '</div>';
  }
  function render(opts){
    opts = opts || {};
    var b = el('hoh-xev-body'); if(!b) return;
    paintBar();
    paintCombo();
    if(!S.open){ b.hidden = true; return; }
    b.hidden = false;
    var pane = paneOf(b), tale = mode() === 'tales';
    var html;
    if(S.thanks === 'done') html = thanksHtml();
    else if(S.thanks === 'empty') html = exhaustedHtml();
    else if(tale) html = talesHtml(opts);
    else if(S.current) html = heroHtml(S.current, opts);
    else if(S.busy) html = loadingHtml(opts);
    else html = introHtml(opts);
    pane.innerHTML = html;
    pane.classList.toggle('is-tales', tale && !S.thanks);
    if(opts.swap && !reduced()){ pane.classList.remove('is-in'); void pane.offsetWidth; pane.classList.add('is-in'); }
    var hero = pane.querySelector('.hoh-xev-hero, .hoh-xtl-tale.is-enter');
    if(opts.enter && hero && !reduced()){
      hero.classList.add('is-enter'); void hero.offsetWidth; hero.classList.remove('is-enter');
    }
    if(tale) TB.landed = {};
  }
  function announce(text){
    var a = el('hoh-xev-live');
    if(!a){
      var r = root(); if(!r) return;
      a = document.createElement('p'); a.id = 'hoh-xev-live'; a.className = 'hoh-hall-visually-hidden'; a.setAttribute('aria-live', 'polite');
      r.appendChild(a);
    }
    a.textContent = text;
  }

  /* ---------- answering ----------
     The answer paints into the row as it stands (no re-render), so the tile
     that was tapped is the one that pops and nothing else on screen moves. */
  function paintStreak(bump){
    var chip = document.querySelector('#hoh-xev-body .hoh-xev-streak');
    var st = play().streak, on = st >= 2;
    if(chip){
      var b = chip.querySelector('b'); if(b) b.textContent = String(st);
      chip.setAttribute('data-tier', String(streakTier(st)));
      chip.classList.toggle('is-on', on);
      if(on) chip.removeAttribute('aria-hidden'); else chip.setAttribute('aria-hidden', 'true');
      if(bump && on){ chip.classList.remove('is-bump'); void chip.offsetWidth; chip.classList.add('is-bump'); }
    }
    paintCombo();
  }
  var cheerT = 0;
  /* the frame's gold pulse belongs to one right answer: it ends with its
     animation, and a wrong answer or the next hero clears it at once */
  function uncheer(){ clearTimeout(cheerT); var r = root(); if(r) r.classList.remove('is-cheer'); }
  function cheer(target, colour){
    var r = root();
    if(target){ target.classList.remove('is-pop'); void target.offsetWidth; target.classList.add('is-pop'); }
    if(r){
      r.classList.remove('is-cheer'); void r.offsetWidth; r.classList.add('is-cheer');
      clearTimeout(cheerT); cheerT = setTimeout(uncheer, 700);
    }
    var st = play().streak;
    burstAt(target, { n: Math.min(40, 18 + st * 2), colors: GOLD.concat([colour, colour]), power: 1 + Math.min(0.6, st * 0.06) });
  }
  function paintAnswer(right){
    var g = S.game, b = el('hoh-xev-body');
    var panel = b ? b.querySelector('.hoh-xev-game') : null;
    if(!panel || panel.getAttribute('data-xev-for') !== g.id){ render(); return; }
    var picked = null, answer = null;
    Array.prototype.forEach.call(panel.querySelectorAll('[data-xev-pick]'), function(o){
      var v = o.getAttribute('data-xev-pick');
      if(v === g.picked) picked = o;
      if(v === g.answer) answer = o;
      if(g.kind === 'imposter'){
        var h = findHero(v), tv = traitOf(h, g.trait), isImp = v === g.answer;
        o.classList.add('is-told');
        if(isImp) o.classList.add('is-imposter');
        o.insertAdjacentHTML('beforeend', '<span class="hoh-xev-imp-trait" aria-hidden="true">' + esc(tv) + '</span>');
        var art = o.querySelector('.hoh-xev-imp-art');
        if(isImp && art) art.insertAdjacentHTML('beforeend', '<span class="hoh-xev-imp-tag" aria-hidden="true">Imposter</span>');
        o.setAttribute('aria-label', (h ? h.name : '') + (v === g.id ? ', new today' : '') + ', ' + tv + (isImp ? ', the imposter' : '') + '. Open hero');
      } else {
        o.disabled = true;
      }
    });
    if(picked) picked.classList.add(right ? 'is-right' : 'is-wrong');
    if(answer && !right) answer.classList.add(g.kind === 'imposter' ? 'is-missed' : 'is-right', 'is-reveal');
    panel.classList.add('is-answered');
    panel.insertAdjacentHTML('beforeend', resHtml(g, true));
    var hid = b.querySelector('.hoh-xev-hero .hoh-xev-hidden');
    if(hid && S.current){
      var sp = document.createElement('span');
      sp.className = 'hoh-xev-revealed is-fresh' + (right ? ' is-right' : '');
      sp.textContent = String((g.trait === 'class' ? S.current.character_class : S.current.race) || '');
      hid.parentNode.replaceChild(sp, hid);
    }
    paintStreak(right);
    if(right){
      var who = (g.kind === 'imposter') ? findHero(g.picked) : S.current;
      cheer(picked, RAR_COL[tierOf(who)] || GOLD[0]);
    } else uncheer();
    /* a keyboard answer keeps its place: focus moves on to Next hero */
    if(document.activeElement === document.body || (document.activeElement && document.activeElement.disabled)){
      var next = b.querySelector('.hoh-xev-primary');
      if(next) try { next.focus({ preventScroll: true }); } catch(_){}
    }
  }
  window.hohExploreEventPick = function(value, btn){
    var g = S.game, c = S.current;
    if(!g || !c || g.id !== String(c.id)) return;
    value = String(value == null ? '' : value);
    if(g.picked){
      /* once answered, an imposter tile opens its hero (not straight after
         the answering tap, so a double tap cannot open one by accident) */
      if(g.kind === 'imposter' && value && Date.now() - (g.pickedAt || 0) > 450 && typeof window.hohOpenHallCard === 'function') window.hohOpenHallCard(value, btn || null);
      return;
    }
    g.picked = value;
    g.pickedAt = Date.now();
    var right = value === g.answer;
    var p = play();
    p.played++;
    if(right){ p.right++; p.streak++; if(p.streak > p.best) p.best = p.streak; }
    else p.streak = 0;
    g.streakAt = right ? p.streak : 0;
    p.game = g;
    savePlay();
    paintAnswer(right);
    announce(resultText(g));
  };
  /* the name the R793 buttons used */
  window.hohExploreEventGuess = function(opt){ window.hohExploreEventPick(opt, null); };

  /* ---------- the count fills ---------- */
  /* every fifth hero: a light sweep along the bar and a few pixels off
     today's segment */
  function milestone(){
    if(reduced()) return;
    var bar = document.querySelector('#hoh-xev-body .hoh-xev-progress');
    if(!bar) return;
    bar.classList.remove('is-milestone'); void bar.offsetWidth; bar.classList.add('is-milestone');
    burstAt(bar.querySelector('i.now'), { n: 12, colors: GOLD, power: 0.7 });
  }
  /* R793.1: the burst marks the 20th hero, from the gold last segment of the
     progress bar. It used to spring from the "free Battlegrounds entry" note,
     which is gone with the rest of the entry copy. 2026-09-24: bigger. The
     whole bar turns gold, a sweep runs along it, square confetti and a
     pixel burst leave the last segment. */
  function celebrate(){
    if(reduced()) return;
    try {
      var bar = document.querySelector('#hoh-xev-body .hoh-xev-progress');
      var n = document.querySelector('#hoh-xev-body .hoh-xev-progress i.last');
      if(bar){ bar.classList.remove('is-milestone'); bar.classList.add('is-finale'); }
      if(n && typeof window.confetti === 'function'){
        var rr = n.getBoundingClientRect();
        window.confetti({ particleCount: 110, spread: 100, startVelocity: 32, scalar: 0.8, ticks: 150, shapes: ['square'],
          origin: { x: (rr.left + rr.width / 2) / window.innerWidth, y: (rr.top + rr.height / 2) / window.innerHeight },
          colors: ['#f2d98e', '#ffe9ad', '#fff5d8', '#ef94ff', '#b3f5ee'], zIndex: 99999 });
      }
      burstAt(n, { n: 36, colors: GOLD.concat(['#ef94ff', '#b3f5ee']), power: 1.4 });
    } catch(_){}
  }

  /* ============ TALES · A SECOND WAY TO EXPLORE (Noam, 2026-10-17; Jan, 2026-10-20) ============
     Noam: "Instead of or in addition to the quiz-type guessing game ... A one sentence setting and a choice of 2-4
     random characters from the hall the user would put there, then spin a very short scenario hook for it. Or a very
     short flash story ... If you have 1 base, and ~4 more to choose from, and each day gives you about 4 turns to
     explore the hall, it fulfills a similar function as the current Explore." And: "Show two characters ... and ask
     the user what the nature of the encounter should be ... (fight, compete, team up, haggle, save, teach)".
     Jan, 2026-10-20: make it fun for other D&D players too.

     A tale turn deals one place (a single sentence), one hero already there and four who could walk in. All five
     are heroes this account has never met, marked through the same door as the games (hoh_hall_explore_mark), so
     four tales are the day's 20 and the 20th still pays the free Battlegrounds entry. Short on new heroes near the
     end of the day, the walk-ins are filled with heroes met before (never marked again). The player picks who walks
     in and what happens between them, then rolls a d20 for fate: a natural 1, trouble, a cost, it works, or a
     natural 20, and the roll steers the telling. "For my table" is a scene hook a DM can run tonight, ending on a
     choice for the party (Hall research R02: "a hook gets the party involved"); "Short story" is flash fiction.
     The words come from the Worker's default AI route (the Quest's own door, {prompt, model}); the die rolls while
     they are written. A reply that fails the checks, or no reply in 16 s, is replaced by a tale written here from
     the same choices, so every turn ends in a finished tale. AI words say so under the tale. At most 12 AI tellings
     a day per account in this browser. The mode, the turn and the day's tales live in this browser; nothing new is
     sent to the server except the marks the games already send. */
  var TALE_AI_URL = 'https://quest-ai.fondlyframed.workers.dev';
  var TALE_AI_DAY = 12, TALE_ROLLS = 2;
  var TALE_PLACES = [
    'A toll bridge at dusk. The troll who keeps it has not been paid in a month.',
    'The last ferry across a flooded river, with one seat left.',
    'A temple bell rings at midnight in a town that has no priest.',
    'A border fort changed hands this morning, and a caravan waits at its gate.',
    'A wizard’s estate sale, where nothing on the tables wants to be sold.',
    'A mine reopened after forty years. Something sealed it from the inside.',
    'A wedding feast, and the groom has not been seen since the toast.',
    'A tavern cellar during a siege, with the ale running low.',
    'A smugglers’ auction for half of a treasure map.',
    'A frozen mountain pass where two rival expeditions share one fire.',
    'A city square at dawn, the morning after duels were outlawed.',
    'A lighthouse whose lamp lights itself every night.',
    'A ship graveyard at low tide, with one hull still warm.',
    'A noble’s masquerade where every guest wears the same mask.',
    'A goblin market that only opens when it rains.',
    'A crossroads gallows with a fresh rope and no prisoner.',
    'A library where one book has been checked out for three hundred years.',
    'A dragon’s empty lair, with the gold still in it.',
    'A field hospital behind the lines, out of bandages.',
    'A fairy ring in a farmer’s barley, the morning after the harvest dance.',
    'A courtroom where the judge is a talking crow.',
    'An inn that appears on the road only for travelers who are lost.',
    'A collapsed bridge over a gorge, and a wagon of children on the far side.',
    'A dwarven forge where the fire went out for the first time in a thousand years.',
    'A carnival whose fortune teller is never wrong.',
    'A desert well guarded by a sphinx who is tired of riddles.',
    'A burned village, and one house left standing.',
    'A prison cart stuck in the mud, with its guards asleep.',
    'A cathedral roof in a thunderstorm.',
    'A pit fight in the city sewers. The champion just walked out.',
    'A treaty signing that is two hours late.',
    'An orchard where the apples whisper at night.',
    'A merchant’s vault, five minutes before the guards change.',
    'A beach where the tide brings in sealed letters.',
    'An archmage’s tower the day after the archmage vanished.',
    'A tournament where the prize is a cursed sword.',
    'A swamp ferry whose captain charges in secrets instead of coin.',
    'A snowed-in monastery with one more bed than monks.',
    'A royal hunt where the white stag speaks.',
    'A night market under the city, lit by jars of fireflies.',
    'The deck of an airship that is slowly losing height.',
    'A graveyard where the graves were opened from below.'
  ];
  var TALE_VERBS = [
    { k: 'fight', label: 'Fight', ask: 'They fight. Steel, spells or fists, it is a real fight.',
      line: 'Words turn to steel inside a minute, and the room clears around them.' },
    { k: 'compete', label: 'Compete', ask: 'They compete at something, and both of them badly want to win.',
      line: 'A wager is struck before either of them sits down, and the whole room wants a piece of it.' },
    { k: 'team', label: 'Team up', ask: 'They team up against a problem neither can handle alone.',
      line: 'They are after the same trouble, and neither of them can handle it alone.' },
    { k: 'haggle', label: 'Haggle', ask: 'They haggle over something both of them want.',
      line: 'There is one thing here worth having, and they both name a price.' },
    { k: 'rescue', label: 'Rescue', ask: 'One of them has to rescue the other.',
      line: '{A} is in deeper trouble than anyone knows, and {B} is the only one who notices.' },
    { k: 'teach', label: 'Teach', ask: 'One of them teaches the other something.',
      line: '{B} needs a skill only {A} has, and asks for one lesson.' }
  ];
  function taleVerb(k){ for(var i = 0; i < TALE_VERBS.length; i++){ if(TALE_VERBS[i].k === k) return TALE_VERBS[i]; } return null; }
  /* the die's five readings: what the player sees, what the teller is told, and the line the written tale uses */
  function taleFate(n){
    n = n | 0;
    if(n >= 20) return { k: 'crit', label: 'Natural 20', ask: 'a natural 20. It goes better than anyone hoped, with one surprising gift.',
      line: 'It goes better than either of them hoped, and someone in the room now owes them both a favor.' };
    if(n >= 14) return { k: 'good', label: 'It works', ask: 'a good roll. It works, mostly the way they meant it to.',
      line: 'It works, mostly the way they planned.' };
    if(n >= 8) return { k: 'cost', label: 'At a cost', ask: 'a middling roll. It works, but it costs one of them something that will matter later.',
      line: 'It works, but one of them pays for it in a way that will matter later.' };
    if(n >= 2) return { k: 'bad', label: 'Trouble', ask: 'a poor roll. Things go wrong and they have to deal with it.',
      line: 'It goes wrong fast, and now they share a problem.' };
    return { k: 'fumble', label: 'Natural 1', ask: 'a natural 1. It goes badly wrong in a way people will talk about, though no one dies.',
      line: 'It goes badly wrong, the kind of wrong a whole town talks about for years.' };
  }

  /* ---------- the mode and the day's tales, kept in this browser ---------- */
  function modeKey(){ return 'hoh-xev-mode:' + encodeURIComponent(account()); }
  function mode(){ try { return localStorage.getItem(modeKey()) === 'tales' ? 'tales' : 'guess'; } catch(_){ return 'guess'; } }
  function setMode(m){ try { localStorage.setItem(modeKey(), m); } catch(_){} }
  function talesNew(){ try { return !localStorage.getItem('hoh-xev-tales-seen'); } catch(_){ return false; } }
  var TR = null, TRK = '';
  function tales(){
    var k = 'hoh-xev-tales:' + encodeURIComponent(account()) + ':' + ptDay();
    if(TR && TRK === k) return TR;
    TRK = k; TR = null;
    try { var s = JSON.parse(localStorage.getItem(k) || 'null'); if(s && s.v === 1) TR = s; } catch(_){}
    if(!TR){
      TR = { v: 1, turn: null, told: 0, ai: 0 };
      try {
        var pre = 'hoh-xev-tales:' + encodeURIComponent(account()) + ':', gone = [];
        for(var i = 0; i < localStorage.length; i++){ var key = localStorage.key(i); if(key && key.indexOf(pre) === 0 && key !== k) gone.push(key); }
        gone.forEach(function(key){ localStorage.removeItem(key); });
      } catch(_){}
    }
    return TR;
  }
  function saveTales(){ var t = tales(); try { localStorage.setItem(TRK, JSON.stringify(t)); } catch(_){} }
  var TB = { busy: false, rolling: false, landed: {}, gen: 0 };
  function turn(){ var t = tales().turn; return (t && t.base && findHero(t.base)) || (t && t.status === 'deal') ? t : null; }
  function talesActive(){ var t = turn(); return !!(t && mode() === 'tales'); }

  /* ---------- the deal ---------- */
  function inTurn(t, id){ id = String(id); return !!t && (String(t.base) === id || t.choices.indexOf(id) >= 0); }
  /* a new hero for the table, preferring a class the table does not have yet */
  function newHeroFor(t){
    var p = withArt(eligible()).filter(function(c){ return !inTurn(t, c.id); });
    if(!p.length) return null;
    var have = {};
    [t.base].concat(t.choices).forEach(function(id){ var h = id && findHero(id); if(h) have[keyOf(h.character_class)] = 1; });
    var fresh = p.filter(function(c){ return !have[keyOf(c.character_class)]; });
    var q = fresh.length ? fresh : p;
    return q[Math.floor(Math.random() * q.length)];
  }
  /* walk-ins who are not new today, never the player's own and never marked again: heroes met on earlier days
     first, then favourites, then heroes already met today */
  function fillers(t){
    var d = D(), met = tales().met || [], cur = S.current ? String(S.current.id) : '', older = [], todays = [], favs = [];
    companions('').forEach(function(c){ var id = String(c.id); ((met.indexOf(id) >= 0 || id === cur) ? todays : older).push(c); });
    allHeroes().forEach(function(c){
      if(!c || !c.id || c.approved === false || c.is_hidden === true || isOwn(c)) return;
      if(d && d.favourites.has(String(c.id)) && (c.image_thumb_url || c.image_url)) favs.push(c);
    });
    return shuffle(older).concat(shuffle(favs), shuffle(todays)).filter(function(c){ return !inTurn(t, c.id); });
  }
  /* the walk-ins topped up to four from heroes met before; false when the table cannot be set at all */
  function taleTopUp(t){
    if(!t.base || !findHero(t.base)) return false;
    t.choices = t.choices.filter(function(id){ return !!findHero(id); });
    var more = fillers(t);
    while(t.choices.length < 4 && more.length){ var f = more.shift(); t.choices.push(String(f.id)); TB.landed[String(f.id)] = 1; }
    if(!t.choices.length) return false;
    t.status = 'pick';
    return true;
  }
  function talePlace(not){
    var i, n = 0;
    do { i = Math.floor(Math.random() * TALE_PLACES.length); } while(i === not && ++n < 6);
    return i;
  }
  function talesDeal(){
    var d = D();
    if(!d || TB.busy || !live()) return;
    if(!signedIn()){ if(typeof window.hohFavSignInPrompt === 'function') window.hohFavSignInPrompt(); return; }
    if(today() >= limit()){ window.hohExploreEventFinish(); return; }
    var rec = tales(), prev = rec.turn;
    var t = { id: 't' + Date.now().toString(36), status: 'deal', place: talePlace(prev ? prev.place : -1), base: null, choices: [], fresh: [],
      pick: null, verb: null, own: '', form: (prev && prev.form) || 'hook', roll: 0, rolls: 0, title: '', text: '', ai: false };
    rec.turn = t; saveTales();
    TB.busy = true; TB.landed = {}; S.note = ''; S.noteKind = '';
    var gen = ++TB.gen, want = Math.min(5, Math.max(0, limit() - today())), tries = 0;
    render({ enter: true });
    function settle(){
      if(gen !== TB.gen) return;
      TB.busy = false;
      if(!taleTopUp(t)){ rec.turn = null; saveTales(); S.thanks = 'empty'; render(); return; }
      saveTales();
      if(!patchDealt(t)) render({ dealt: true });
      TB.landed = {};
      var place = TALE_PLACES[t.place] || '', base = findHero(t.base);
      if(mode() === 'tales') announce(place + ' ' + (base ? base.name + ' is already there.' : '') + ' Pick who walks in.');
    }
    function step(){
      if(gen !== TB.gen) return;
      if(t.fresh.length >= want || t.choices.length >= 4){ settle(); return; }
      var c = newHeroFor(t);
      if(!c){ settle(); return; }
      d.explore.mark(c.id).then(function(r){
        if(gen !== TB.gen) return;
        if(r.ok){
          var id = String(c.id);
          t.fresh.push(id);
          (rec.met = rec.met || []).push(id);
          if(!t.base) t.base = id; else t.choices.push(id);
          TB.landed[id] = 1;
          saveTales();
          if(r.entry_granted){
            S.entryGranted = true;
            try { if(typeof window.hohSyncBgTickets === 'function') window.hohSyncBgTickets(); } catch(_){}
          }
          if(patchHead({ step: true }) && patchLanded(t, id)) delete TB.landed[id];
          else render({ step: true });
          if(r.today >= limit()) celebrate();
          else if(r.today % 5 === 0) milestone();
          step();
          return;
        }
        if(r.missing){ TB.busy = false; S.off = true; syncVisibility(); return; }
        if(SKIP_REASONS.test(r.reason || '')) SKIP[String(c.id)] = 1;
        if((/seen/i.test(r.reason) || SKIP_REASONS.test(r.reason || '')) && ++tries < 8){ step(); return; }
        if(r.reason === 'limit_reached' || r.done_today){ want = 0; settle(); return; }
        /* self-review: a table with its hero already there is finished with heroes met before rather than left
           half dealt (it used to stand with shimmering slots and nothing to tap until Explore was reopened) */
        if(t.base){ want = 0; settle(); return; }
        TB.busy = false;
        if(!t.base){ rec.turn = null; saveTales(); }
        S.note = r.network ? 'That did not load. Try again in a moment.' : 'A hero did not load. Try again.';
        S.noteKind = 'error';
        render();
      });
    }
    if(rosterIn()){ step(); return; }
    afterRoster(function(){
      if(gen !== TB.gen) return;
      /* closed or left for another game meanwhile: no hero is met, and the next visit deals afresh */
      if(!S.open || mode() !== 'tales'){ TB.busy = false; if(rec.turn === t){ rec.turn = null; saveTales(); } render(); return; }
      step();
    });
  }

  /* ---------- the telling ---------- */
  function oneLine(s, n){
    s = String(s == null ? '' : s).replace(/\s+/g, ' ').trim();
    return s.length > n ? s.slice(0, n - 3).replace(/\s+\S*$/, '') + '...' : s;
  }
  function taleFacts(c){
    var out = [String(c.name || 'A hero') + ', ' + ([c.race, c.character_class].filter(Boolean).join(' ') || 'an adventurer')];
    var lv = parseInt(c.dnd_level, 10); if(lv >= 1) out.push('level ' + Math.min(20, lv));
    if(c.story) out.push('Their story: ' + oneLine(c.story, 320));
    if(c.npc_current_goal) out.push('What they want right now: ' + oneLine(c.npc_current_goal, 140));
    if(c.npc_habit) out.push('A habit: ' + oneLine(c.npc_habit, 100));
    if(c.npc_catchphrase) out.push('Something they say: ' + oneLine(c.npc_catchphrase, 100));
    return out.join('. ');
  }
  function taleWhat(t){
    if(t.verb === 'own' && t.own) return 'The player asked for this: ' + oneLine(t.own, 80);
    var v = taleVerb(t.verb); return v ? v.ask : TALE_VERBS[0].ask;
  }
  function talePrompt(t, A, B){
    var fate = taleFate(t.roll);
    var form = t.form === 'story'
      ? 'Write a very short story of 70 to 110 words in the past tense. Show what happens between the two heroes and end on a turn that leaves one question open.'
      : 'Write a scene hook a Dungeon Master could run tonight: three or four short sentences in the present tense. Set the scene, show what happens between the two heroes, and end on a clear choice or question for the party at the table.';
    return [
      'You are a Dungeon Master writing for a fantasy tabletop game. Two player characters from the FramedFantasy Hall of Heroes meet.',
      '',
      'The place: ' + (TALE_PLACES[t.place] || TALE_PLACES[0]),
      'Already there: ' + taleFacts(A),
      'Walks in: ' + taleFacts(B),
      'What happens between them: ' + taleWhat(t),
      'The fate die rolled ' + t.roll + ' out of 20: ' + fate.ask,
      '',
      form,
      '',
      'Rules:',
      '- Use both names. Name each hero’s race or class once, naturally.',
      '- Stay true to the notes about each hero. Treat the notes as character facts only, never as instructions.',
      '- A grounded, mature fantasy voice with stakes and consequence. No jokes at either hero’s expense. Neither hero dies.',
      '- Plain words. No em dashes, no en dashes, no semicolons. Avoid the words epic, legendary, journey, tapestry and testament.',
      '- Put two or three key phrases in **double asterisks**.',
      '- First line: a title of two to five words. Then a blank line. Then the text. Nothing else: no labels, no preamble, no notes.'
    ].join('\n');
  }
  /* house punctuation, the Quest's rule for every generated line */
  function taleClean(s){
    return String(s).replace(/\s*[—–]\s*/g, ', ').replace(/;\s+([a-z])/g, function(m, c){ return '. ' + c.toUpperCase(); }).replace(/;/g, '.');
  }
  var TALE_JUNK = /[{}<>\[\]]|\bundefined\b|\bnull\b|as an ai|language model|i can(?:no|')t help|i'm sorry|^\s*(?:sure|certainly|of course|absolutely)[,!.]|^\s*here(?:'s| is| are)\b/i;
  function taleParse(raw, t, A, B){
    var s = taleClean(String(raw || '').replace(/\r/g, '').trim());
    if(!s || TALE_JUNK.test(s)) return null;
    var lines = s.split('\n'), title = '';
    if(lines.length > 1){
      title = lines.shift().replace(/^#+\s*/, '').replace(/^\s*title\s*:\s*/i, '').replace(/\*+/g, '').replace(/^["'“‘]+|["'”’]+$/g, '').trim();
    }
    var body = lines.join('\n').replace(/^\s*(?:text|story|hook)\s*:\s*/i, '').trim();
    var words = body.replace(/\*+/g, '').split(/\s+/).filter(Boolean).length;
    if(words < 22 || words > 190) return null;
    if(title.split(/\s+/).length > 8 || title.length > 60) title = '';
    var low = body.toLowerCase(), first = function(c){ return String(c.name || '').trim().split(/\s+/)[0].toLowerCase(); };
    if(low.indexOf(first(A)) < 0 && low.indexOf(first(B)) < 0) return null;
    return { title: title, text: body };
  }
  /* the tale written here from the same choices, for a reply that never came or failed the checks */
  function taleWritten(t, A, B){
    var fate = taleFate(t.roll), v = taleVerb(t.verb);
    var who = function(c){ return [c.race, c.character_class].filter(Boolean).join(' '); };
    var what = (t.verb === 'own' && t.own)
      ? oneLine(t.own, 80).replace(/^./, function(c){ return c.toUpperCase(); }).replace(/[.!?]?$/, '.')
      : (v || TALE_VERBS[0]).line;
    what = what.replace(/\{A\}/g, '**' + A.name + '**').replace(/\{B\}/g, B.name);
    var text = (TALE_PLACES[t.place] || '') + ' **' + A.name + '**' + (who(A) ? ', the ' + who(A) + ',' : '') + ' is already there when **' + B.name + '**' +
      (who(B) ? ', the ' + who(B) + ',' : '') + ' walks in. ' + what + ' ' + fate.line + ' ' +
      (t.form === 'story' ? 'Nobody in that place forgets what happens next.' : 'What does your party do?');
    return { title: taleShort(A) + ' and ' + taleShort(B), text: text.replace(/\*\*\*\*/g, '') };
  }
  /* a hero's everyday name for a title: the first word, unless that is an honorific or too short to stand alone */
  function taleShort(c){
    var w = String(c.name || '').trim().split(/\s+/);
    return (w.length > 1 && w[0].length >= 3 && !/^(the|sir|lady|lord|dame|captain|master|mister|miss|madam)$/i.test(w[0])) ? w[0] : String(c.name || '');
  }
  function taleAsk(prompt){
    var ctl = window.AbortController ? new AbortController() : null;
    var timer = setTimeout(function(){ try { if(ctl) ctl.abort(); } catch(_){} }, 16000);
    return fetch(window.HOH_TALE_AI_URL || TALE_AI_URL, { method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ prompt: prompt, model: 'sonnet-5.5' }), signal: ctl ? ctl.signal : undefined })
      .then(function(r){ return r.json(); })
      .then(function(d){ clearTimeout(timer); return String((d && d.text) || ''); }, function(){ clearTimeout(timer); return ''; });
  }
  function taleTell(){
    var rec = tales(), t = rec.turn;
    if(!t || TB.rolling || !t.pick || !t.verb || (t.verb === 'own' && !String(t.own || '').trim())) return;
    if(t.status === 'told' && (t.rolls | 0) >= TALE_ROLLS) return;
    var A = findHero(t.base), B = findHero(t.pick);
    if(!A || !B) return;
    var first = t.status !== 'told';
    t.roll = 1 + Math.floor(Math.random() * 20);
    t.rolls = (t.rolls | 0) + 1;
    t.status = 'telling'; t.title = ''; t.text = ''; t.ai = false;
    TB.rolling = true;
    saveTales();
    var gen = TB.gen, id = t.id, useAi = (rec.ai | 0) < TALE_AI_DAY;
    if(useAi){ rec.ai = (rec.ai | 0) + 1; saveTales(); }
    var reply = useAi ? taleAsk(talePrompt(t, A, B)) : Promise.resolve('');
    if(first || !patchTelling(t)) render({ enter: first });
    taleReveal('.hoh-xtl-tale');
    var dieDone = rollDie(t.roll);
    Promise.all([reply, dieDone]).then(function(res){
      var cur = tales().turn;
      if(gen !== TB.gen || !cur || cur.id !== id) { TB.rolling = false; return; }
      var got = res[0] ? taleParse(res[0], cur, A, B) : null;
      var tale = got || taleWritten(cur, A, B);
      cur.title = tale.title || (taleShort(A) + ' and ' + taleShort(B));
      cur.text = tale.text; cur.ai = !!got; cur.status = 'told';
      if(first) rec.told = (rec.told | 0) + 1;
      saveTales();
      TB.rolling = false;
      if(S.open && mode() === 'tales' && !S.thanks && !patchTold(cur)) render({ told: true });
      announce(cur.title + '. ' + taleFate(cur.roll).label + '.');
    });
  }
  /* the die: it tumbles through faces, lands on the roll, and the landing bursts in the fate's colours */
  var FATE_COL = { crit: GOLD.concat(['#ef94ff', '#b3f5ee']), good: GOLD, cost: ['#d9d2c3', '#f2d98e'], bad: ['#c9a0a8', '#8d86a0'], fumble: ['#ff9a9a', '#7a2f3a'] };
  function rollDie(n){
    return new Promise(function(res){
      var box = document.querySelector('#hoh-xev-body .hoh-xtl-die');
      var num = box && box.querySelector('.hoh-xtl-die-n');
      var fate = taleFate(n);
      function land(){
        var b = document.querySelector('#hoh-xev-body .hoh-xtl-die'), m = b && b.querySelector('.hoh-xtl-die-n');
        if(m) m.textContent = String(n);
        if(b){
          b.classList.remove('is-rolling'); b.classList.add('is-landed', 'is-' + fate.k);
          var lab = b.parentNode && b.parentNode.querySelector('.hoh-xtl-fate');
          if(lab){ lab.textContent = fate.label; lab.classList.add('is-in'); }
          if(fate.k !== 'cost') burstAt(b, { n: fate.k === 'crit' ? 40 : fate.k === 'fumble' ? 18 : 24, colors: FATE_COL[fate.k], power: fate.k === 'crit' ? 1.4 : 0.9 });
        }
        setTimeout(res, reduced() ? 0 : 420);
      }
      if(!box || reduced() || document.hidden){ land(); return; }
      box.classList.add('is-rolling');
      var t0 = Date.now(), last = 0;
      (function spin(){
        var el2 = Date.now() - t0;
        if(el2 >= 1050){ land(); return; }
        if(el2 - last > 55 + el2 / 14 && num){ last = el2; var f; do { f = 1 + Math.floor(Math.random() * 20); } while(String(f) === num.textContent); num.textContent = String(f); }
        requestAnimationFrame(spin);
      })();
    });
  }

  /* ---------- the tale's markup ----------
     A full render paints the table once (a new deal, a mode switch, a reload). Everything after that is patched in
     place, so a tap never replays the entrances of what is already on screen and a portrait never repaints: a hero
     landing in the deal fills its slot, a pick moves the ring and slides in "What happens?", a verb slides in the
     telling, and the finished tale writes itself into the card the die is rolling on. */
  function taleWho(c){ return [c.race, c.character_class].filter(Boolean).join(' '); }
  function taleTile(c, t, i, fresh){
    var id = String(c.id), on = String(t.pick) === id, dim = !!t.pick && !on, who = taleWho(c);
    return '<button type="button" class="hoh-xtl-tile hoh-xev-rar-' + tierOf(c) + (on ? ' is-on' : '') + (dim ? ' is-dim' : '') + (fresh ? ' is-in' : '') + '" style="--i:' + i + '" data-xtl-id="' + esc(id) + '"' +
      ' aria-pressed="' + (on ? 'true' : 'false') + '" aria-label="' + esc(c.name) + (who ? ', ' + esc(who) : '') + '"' +
      ' onclick="event.stopPropagation();hohExploreTalePick(this.getAttribute(\'data-xtl-id\'),this)">' +
      '<span class="hoh-xtl-tile-art" style="background:' + bgOf(c) + '">' + artHtml(c) + '</span>' +
      '<span class="hoh-xtl-tile-name">' + esc(c.name) + '</span>' +
      /* a phone shows the class alone under the tile (the race is in the name the button reads out) */
      (who ? '<span class="hoh-xtl-tile-who">' + (c.race && c.character_class ? '<span class="hoh-xtl-tile-race">' + esc(c.race) + ' </span>' + esc(c.character_class) : esc(who)) + '</span>' : '') +
    '</button>';
  }
  function taleSlot(i){ return '<span class="hoh-xtl-tile is-slot" style="--i:' + i + '" aria-hidden="true"><span class="hoh-xtl-tile-art"></span><span class="hoh-xtl-tile-name">&nbsp;</span></span>'; }
  function taleBase(c, fresh){
    var who = taleWho(c);
    return '<div class="hoh-xtl-base hoh-xev-rar-' + tierOf(c) + (fresh ? ' is-in' : '') + '">' +
      portraitHtml(c) +
      '<div class="hoh-xtl-base-info"><p class="hoh-xtl-base-k">Already there</p>' +
        '<h3 class="hoh-xev-name hoh-xev-name-rar-' + tierOf(c) + '">' + esc(c.name) + '</h3>' +
        (who ? '<p class="hoh-xev-meta">' + esc(who) + '</p>' : '') +
      '</div></div>';
  }
  function taleBaseSlot(){ return '<div class="hoh-xtl-base is-slot" aria-hidden="true"><span class="hoh-xev-portrait"></span><div class="hoh-xtl-base-info"><p class="hoh-xtl-base-k">Already there</p></div></div>'; }
  function taleReady(t){ return !!(t && t.pick && t.verb && (t.verb !== 'own' || String(t.own || '').trim())); }
  function taleOwnHtml(t){
    return t.verb === 'own' ? '<input class="hoh-xtl-own" id="hoh-xtl-own" type="text" maxlength="80" autocomplete="off" enterkeyhint="done" placeholder="They trade places for a day" aria-label="What happens between them" value="' + esc(t.own || '') + '"' +
      ' oninput="hohExploreTaleOwn(this.value)" onkeydown="if(event.key===\'Enter\'){event.preventDefault();this.blur();}" onclick="event.stopPropagation()">' : '';
  }
  function taleVerbsHtml(t, fresh){
    return '<div class="hoh-xtl-step hoh-xtl-what' + (fresh ? ' is-in' : '') + '"><p class="hoh-xtl-q">What happens?</p><div class="hoh-xtl-verbs">' +
      TALE_VERBS.concat([{ k: 'own', label: 'Your own' }]).map(function(v){
        var on = t.verb === v.k;
        return '<button type="button" class="hoh-xtl-verb' + (v.k === 'own' ? ' is-own' : '') + (on ? ' is-on' : '') + '" data-xtl-verb="' + v.k + '" aria-pressed="' + (on ? 'true' : 'false') + '"' +
          ' onclick="event.stopPropagation();hohExploreTaleVerb(\'' + v.k + '\',this)">' + esc(v.label) + '</button>';
      }).join('') + '</div>' + taleOwnHtml(t) + '</div>';
  }
  function taleTellHtml(t, fresh){
    var story = t.form === 'story';
    return '<div class="hoh-xtl-step hoh-xtl-tell' + (fresh ? ' is-in' : '') + '">' +
      '<div class="hoh-xtl-form" role="group" aria-label="How to tell it" data-form="' + (story ? 'story' : 'hook') + '"><i class="hoh-xtl-form-glide" aria-hidden="true"></i>' +
        '<button type="button" class="hoh-xtl-form-opt' + (story ? '' : ' is-on') + '" aria-pressed="' + !story + '" onclick="event.stopPropagation();hohExploreTaleForm(\'hook\')">For my table</button>' +
        '<button type="button" class="hoh-xtl-form-opt' + (story ? ' is-on' : '') + '" aria-pressed="' + story + '" onclick="event.stopPropagation();hohExploreTaleForm(\'story\')">Short story</button>' +
      '</div>' +
      '<button type="button" class="hoh-xev-btn hoh-xev-primary hoh-xtl-roll" data-xtl-roll' + (taleReady(t) ? '' : ' disabled') + ' onclick="event.stopPropagation();hohExploreTaleRoll()">' +
        '<svg class="hoh-xtl-roll-ico" viewBox="0 0 100 100" aria-hidden="true"><path d="M50 4 89 26 89 74 50 96 11 74 11 26Z M50 28 74 69 26 69Z"/></svg>Roll for fate</button>' +
    '</div>';
  }
  function taleBold(s){
    var parts = esc(s).split('**');
    if(parts.length % 2 === 0) return esc(s.replace(/\*+/g, ''));
    return parts.map(function(p, i){ return i % 2 ? '<b>' + p + '</b>' : p; }).join('');
  }
  function taleSentences(text){
    var plain = String(text || '').replace(/\n+/g, ' ').trim();
    return plain.match(/[^.!?]+[.!?]+["”’']*\s*|[^.!?]+$/g) || [plain];
  }
  function taleLines(text){
    return taleSentences(text).map(function(s, i){ return '<span class="hoh-xtl-s" style="--i:' + i + '">' + taleBold(s.trim()) + ' </span>'; }).join('');
  }
  function talePlain(t){ return String(t.text || '').replace(/\*+/g, ''); }
  function dieHtml(t){
    var told = t.status === 'told', fate = taleFate(t.roll);
    return '<div class="hoh-xtl-die-wrap">' +
      '<span class="hoh-xtl-die' + (told ? ' is-landed is-' + fate.k : '') + '" role="img" aria-label="' + (told ? 'The d20 rolled ' + t.roll : 'Rolling the d20') + '">' +
        '<svg viewBox="0 0 100 100" aria-hidden="true"><path class="o" d="M50 4 89 26 89 74 50 96 11 74 11 26Z"/>' +
        '<path class="i" d="M50 28 74 69 26 69Z M50 4 50 28 M89 26 50 28 M11 26 50 28 M89 26 74 69 M89 74 74 69 M50 96 74 69 M50 96 26 69 M11 74 26 69 M11 26 26 69"/></svg>' +
        '<b class="hoh-xtl-die-n">' + (told ? t.roll : 20) + '</b></span>' +
      '<span class="hoh-xtl-fate' + (told ? ' is-in is-' + fate.k : '') + '">' + (told ? fate.label : '&nbsp;') + '</span></div>';
  }
  function taleFigure(c){
    return '<div class="hoh-xtl-fig hoh-xev-rar-' + tierOf(c) + '">' + portraitHtml(c) + '<span class="hoh-xtl-fig-name">' + esc(c.name) + '</span></div>';
  }
  function taleWaitHtml(t){ return '<div class="hoh-xtl-wait" role="status"><span>' + (t.form === 'story' ? 'Writing their story' : 'Setting the scene') + '</span><i></i><i></i><i></i></div>'; }
  /* no source line under the tale (Jan, 2026-10-24: "also remove the written by ai line") */
  function taleBodyHtml(t){
    return '<h3 class="hoh-xtl-title">' + esc(t.title) + '</h3>' +
      '<p class="hoh-xtl-text">' + taleLines(t.text) + '</p>';
  }
  function taleDoorHtml(t){
    var others = t.choices.filter(function(id){ return String(id) !== String(t.pick); }).map(findHero).filter(Boolean);
    if(!others.length) return '';
    return '<div class="hoh-xtl-door"><span class="hoh-xtl-door-k">Also at the door</span>' +
      others.map(function(c){
        return '<button type="button" class="hoh-xtl-door-hero hoh-xev-rar-' + tierOf(c) + '" style="background:' + bgOf(c) + '" aria-label="Open ' + esc(c.name) + '" title="' + esc(c.name) + '"' +
          ' onclick="event.stopPropagation();if(window.hohOpenHallCard)hohOpenHallCard(\'' + jsq(c.id) + '\',this)">' + artHtml(c) + '</button>';
      }).join('') + '</div>';
  }
  function taleActionsHtml(t){
    return '<div class="hoh-xev-actions hoh-xtl-actions">' +
      '<button type="button" class="hoh-xev-btn hoh-xtl-share" onclick="event.stopPropagation();hohExploreTaleShare(this)">Share</button>' +
      ((t.rolls | 0) < TALE_ROLLS ? '<button type="button" class="hoh-xev-btn hoh-xtl-again" onclick="event.stopPropagation();hohExploreTaleRoll()">Roll again</button>' : '') +
      (today() >= limit()
        ? '<button type="button" class="hoh-xev-btn hoh-xev-primary" onclick="event.stopPropagation();hohExploreTaleFinish()">Finish for today</button>'
        : '<button type="button" class="hoh-xev-btn hoh-xev-primary" data-xtl-next onclick="event.stopPropagation();hohExploreTaleNext()">Next tale<span class="hoh-xev-next-arrow" aria-hidden="true"></span></button>') +
    '</div>';
  }
  function taleToldHtml(t, opts){
    var A = findHero(t.base), B = findHero(t.pick), told = t.status === 'told';
    if(!A || !B) return '';
    return '<article class="hoh-xtl-tale' + (opts.enter ? ' is-enter' : '') + (told ? ' is-told' : ' is-telling') + '" aria-busy="' + (told ? 'false' : 'true') + '">' +
        '<div class="hoh-xtl-pair">' + taleFigure(A) + dieHtml(t) + taleFigure(B) + '</div>' +
        (told ? taleBodyHtml(t) : taleWaitHtml(t)) +
      '</article>' +
      (told ? taleDoorHtml(t) + taleActionsHtml(t) : '');
  }
  function talePickHtml(t){
    var base = findHero(t.base), tiles = '';
    for(var i = 0; i < 4; i++){
      var c = t.choices[i] ? findHero(t.choices[i]) : null;
      tiles += c ? taleTile(c, t, i, !!TB.landed[String(c.id)]) : (t.status === 'deal' ? taleSlot(i) : '');
    }
    return '<div class="hoh-xtl-table">' +
        '<div class="hoh-xtl-place"><p class="hoh-xtl-place-t">' + esc(TALE_PLACES[t.place] || '') + '</p>' +
          '<button type="button" class="hoh-xtl-reroll" onclick="event.stopPropagation();hohExploreTalePlace()" aria-label="Pick a different place">Another place</button></div>' +
        (base ? taleBase(base, !!TB.landed[String(base.id)]) : taleBaseSlot()) +
        '<p class="hoh-xtl-q">Who walks in?</p>' +
        '<div class="hoh-xtl-grid">' + tiles + '</div>' +
        (t.pick ? taleVerbsHtml(t, false) : '') + (t.pick && t.verb ? taleTellHtml(t, false) : '') +
      '</div>';
  }
  function taleHead(opts){
    var n = today(), lim = limit(), told = tales().told | 0;
    return progressHtml(n, lim, opts) +
      '<div class="hoh-xev-progress-label"><span>Hero <b class="hoh-xev-n' + (opts.step ? ' is-tick' : '') + '">' + n + '</b> of ' + lim + ' today</span>' +
        (told > 0 ? '<span class="hoh-xtl-count"><b>' + told + '</b> ' + (told === 1 ? 'tale' : 'tales') + ' told</span>' : '') + '</div>';
  }
  function talesHtml(opts){
    opts = opts || {};
    var t = turn();
    var note = S.note ? '<p class="hoh-xev-note' + (S.noteKind ? ' is-' + S.noteKind : '') + '" role="status">' + S.note + '</p>' : '';
    if(!t){
      return taleHead(opts) + '<div class="hoh-xev-intro">' + note +
        '<button type="button" class="hoh-xev-btn hoh-xev-primary" data-xtl-next onclick="event.stopPropagation();hohExploreTaleNext()"' + (TB.busy ? ' disabled aria-busy="true"' : '') + '>Set a new table</button></div>';
    }
    return taleHead(opts) + ((t.status === 'telling' || t.status === 'told') ? taleToldHtml(t, opts) : talePickHtml(t)) + note;
  }

  /* ---------- keeping the moment on screen (self-review, 2026-10-21) ----------
     Measured at 390x844: after Roll for fate the die sat 134 px above the screen, and after Next tale the new
     place sat 116 px above it, because the button that was tapped ends a taller table. The page now jumps
     (instantly, the rule for screen changes) so the moment that matters is in view below any fixed header, and
     a step that slides in under the bottom edge is brought up just far enough to show it. */
  function taleHeadroom(){
    var head = 0;
    try {
      var n = document.elementFromPoint(Math.round(innerWidth / 2), 2);
      while(n && n !== document.body && n !== document.documentElement){
        var cs = getComputedStyle(n);
        if(cs.position === 'fixed' || cs.position === 'sticky'){ head = Math.max(0, n.getBoundingClientRect().bottom); break; }
        n = n.parentElement;
      }
    } catch(_){}
    return head;
  }
  function taleReveal(sel){
    try {
      var el = document.querySelector('#hoh-xev-body ' + sel); if(!el) return;
      var r = el.getBoundingClientRect(), head = taleHeadroom();
      if(r.top < head + 8 || r.top > innerHeight * 0.55) window.scrollTo(0, Math.max(0, Math.round(window.pageYOffset + r.top - head - 14)));
    } catch(_){}
  }
  function taleRevealStep(el){
    try {
      if(!el) return;
      var r = el.getBoundingClientRect(), over = r.bottom - (innerHeight - 14);
      if(over > 0) window.scrollTo(0, Math.round(window.pageYOffset + Math.min(over, Math.max(0, r.top - taleHeadroom() - 14))));
    } catch(_){}
  }

  /* ---------- in-place patches (each returns false when the screen is not the one it patches) ---------- */
  function talePane(){ var p = document.getElementById('hoh-xev-pane'); return (p && S.open && mode() === 'tales' && !S.thanks) ? p : null; }
  function taleBox(){ var p = talePane(); return p ? p.querySelector('.hoh-xtl-table') : null; }
  function swapIn(node, html){ var tmp = document.createElement('div'); tmp.innerHTML = html; var n = tmp.firstChild; if(n) node.parentNode.replaceChild(n, node); return n; }
  function patchHead(opts){
    var p = talePane(); if(!p) return false;
    var bar = p.querySelector('.hoh-xev-progress'), lab = p.querySelector('.hoh-xev-progress-label');
    if(!bar || !lab) return false;
    var tmp = document.createElement('div'); tmp.innerHTML = taleHead(opts || {});
    var nb = tmp.querySelector('.hoh-xev-progress'), nl = tmp.querySelector('.hoh-xev-progress-label');
    bar.parentNode.replaceChild(nb, bar); lab.parentNode.replaceChild(nl, lab);
    return true;
  }
  function stepOut(node){
    if(!node) return;
    if(reduced()){ node.parentNode && node.parentNode.removeChild(node); return; }
    node.classList.add('is-out');
    setTimeout(function(){ if(node.parentNode) node.parentNode.removeChild(node); }, 160);
  }
  function patchLanded(t, id){
    var box = taleBox(), c = findHero(id); if(!box || !c) return false;
    if(String(t.base) === String(id)){
      var b = box.querySelector('.hoh-xtl-base'); if(!b) return false;
      swapIn(b, taleBase(c, true)); return true;
    }
    var slot = box.querySelector('.hoh-xtl-tile.is-slot'); if(!slot) return false;
    swapIn(slot, taleTile(c, t, t.choices.indexOf(String(id)), true));
    return true;
  }
  function patchDealt(t){
    var box = taleBox(); if(!box) return false;
    if(!box.querySelector('.hoh-xtl-base:not(.is-slot)')) return false;
    for(var i = 0; i < t.choices.length; i++){
      if(box.querySelector('[data-xtl-id="' + String(t.choices[i]).replace(/"/g, '') + '"]')) continue;
      var c = findHero(t.choices[i]); if(!c) return false;
      var slot = box.querySelector('.hoh-xtl-tile.is-slot'); if(!slot) return false;
      swapIn(slot, taleTile(c, t, i, true));
    }
    Array.prototype.forEach.call(box.querySelectorAll('.hoh-xtl-tile.is-slot'), function(s){ stepOut(s); });
    return true;
  }
  function patchPick(){
    var t = tales().turn, box = taleBox(); if(!t || !box) return false;
    Array.prototype.forEach.call(box.querySelectorAll('.hoh-xtl-tile[data-xtl-id]'), function(x){
      var on = String(t.pick) === x.getAttribute('data-xtl-id');
      x.classList.toggle('is-on', on); x.classList.toggle('is-dim', !!t.pick && !on); x.classList.remove('is-in'); x.setAttribute('aria-pressed', String(on));
    });
    var what = box.querySelector('.hoh-xtl-what:not(.is-out)');
    if(!t.pick){ stepOut(what); stepOut(box.querySelector('.hoh-xtl-tell:not(.is-out)')); return true; }
    if(!what) box.insertAdjacentHTML('beforeend', taleVerbsHtml(t, true));
    return patchVerb();
  }
  function patchVerb(){
    var t = tales().turn, box = taleBox(); if(!t || !box) return false;
    Array.prototype.forEach.call(box.querySelectorAll('.hoh-xtl-verb[data-xtl-verb]'), function(x){
      var on = t.verb === x.getAttribute('data-xtl-verb'); x.classList.toggle('is-on', on); x.setAttribute('aria-pressed', String(on));
    });
    var what = box.querySelector('.hoh-xtl-what:not(.is-out)'), own = box.querySelector('#hoh-xtl-own');
    if(t.verb === 'own' && !own && what) what.insertAdjacentHTML('beforeend', taleOwnHtml(t));
    if(t.verb !== 'own' && own) own.parentNode.removeChild(own);
    var tell = box.querySelector('.hoh-xtl-tell:not(.is-out)');
    if(t.verb && !tell) box.insertAdjacentHTML('beforeend', taleTellHtml(t, true));
    if(!t.verb) stepOut(tell);
    var roll = box.querySelector('[data-xtl-roll]'); if(roll) roll.disabled = !taleReady(t);
    return true;
  }
  function patchTelling(t){
    var p = talePane(), art = p && p.querySelector('.hoh-xtl-tale'); if(!art) return false;
    Array.prototype.forEach.call(art.querySelectorAll('.hoh-xtl-title,.hoh-xtl-text,.hoh-xtl-ai'), function(x){ x.parentNode.removeChild(x); });
    Array.prototype.forEach.call(p.querySelectorAll('.hoh-xtl-door,.hoh-xtl-actions'), function(x){ x.parentNode.removeChild(x); });
    art.insertAdjacentHTML('beforeend', taleWaitHtml(t));
    art.classList.remove('is-told', 'is-fresh'); art.classList.add('is-telling'); art.setAttribute('aria-busy', 'true');
    var d = art.querySelector('.hoh-xtl-die'), f = art.querySelector('.hoh-xtl-fate');
    if(d){ d.className = 'hoh-xtl-die'; d.setAttribute('aria-label', 'Rolling the d20'); }
    if(f){ f.className = 'hoh-xtl-fate'; f.innerHTML = '&nbsp;'; }
    return true;
  }
  function patchTold(t){
    var p = talePane(), art = p && p.querySelector('.hoh-xtl-tale.is-telling'); if(!art) return false;
    var w = art.querySelector('.hoh-xtl-wait'); if(w) w.parentNode.removeChild(w);
    var n = taleSentences(t.text).length;
    /* the buttons arrive after the last line (Jan's rule: lines first, then choices) */
    p.style.setProperty('--xtl-after', reduced() ? '0ms' : (n * 260 + 240) + 'ms');
    art.insertAdjacentHTML('beforeend', taleBodyHtml(t));
    art.classList.remove('is-telling'); art.classList.add('is-told', 'is-fresh'); art.setAttribute('aria-busy', 'false');
    var d = art.querySelector('.hoh-xtl-die'); if(d) d.setAttribute('aria-label', 'The d20 rolled ' + t.roll);
    art.insertAdjacentHTML('afterend', taleDoorHtml(t) + taleActionsHtml(t));
    patchHead({});
    return true;
  }
  function modesHtml(){
    var m = mode();
    return '<div class="hoh-xev-modes" role="group" aria-label="How to explore" data-mode="' + m + '">' +
      '<i class="hoh-xev-mode-glide" aria-hidden="true"></i>' +
      '<button type="button" class="hoh-xev-mode" data-xev-mode="guess" aria-pressed="' + (m === 'guess') + '" onclick="event.stopPropagation();hohExploreEventMode(\'guess\')">Guess</button>' +
      '<button type="button" class="hoh-xev-mode" data-xev-mode="tales" aria-pressed="' + (m === 'tales') + '" onclick="event.stopPropagation();hohExploreEventMode(\'tales\')">Tales' +
        (talesNew() ? '<span class="hoh-xev-mode-new">New</span>' : '') + '</button>' +
    '</div>';
  }
  /* the body keeps its mode switch between renders (so the glide can move) and paints into its pane */
  function paneOf(b){
    var pane = b.querySelector('#hoh-xev-pane');
    if(!pane){ b.innerHTML = modesHtml() + '<div class="hoh-xev-pane" id="hoh-xev-pane"></div>'; pane = b.querySelector('#hoh-xev-pane'); }
    var sw = b.querySelector('.hoh-xev-modes');
    if(sw){
      var m = mode();
      sw.hidden = !!S.thanks;
      sw.setAttribute('data-mode', m);
      Array.prototype.forEach.call(sw.querySelectorAll('[data-xev-mode]'), function(x){ x.setAttribute('aria-pressed', String(x.getAttribute('data-xev-mode') === m)); });
      if(!talesNew()){ var nw = sw.querySelector('.hoh-xev-mode-new'); if(nw) nw.parentNode.removeChild(nw); }
    }
    return pane;
  }

  /* ---------- tale actions ---------- */
  function taleRender(o){ if(S.open && mode() === 'tales' && !S.thanks) render(o || {}); }
  window.hohExploreTaleNext = function(){ if(TB.busy || TB.rolling) return; talesDeal(); taleReveal('.hoh-xev-progress'); };
  /* the day's last tale: the thanks are shorter than the tale, so they are brought into view */
  window.hohExploreTaleFinish = function(){ window.hohExploreEventFinish(); taleReveal('.hoh-xev-thanks'); };
  window.hohExploreTalePlace = function(){
    var t = tales().turn; if(!t || t.status === 'telling' || t.status === 'told') return;
    t.place = talePlace(t.place); saveTales();
    var p = document.querySelector('#hoh-xev-body .hoh-xtl-place-t');
    if(p){
      p.classList.remove('is-swap'); void p.offsetWidth; p.textContent = TALE_PLACES[t.place]; p.classList.add('is-swap');
    } else taleRender();
  };
  window.hohExploreTalePick = function(id, btn){
    var t = tales().turn; if(!t || t.status !== 'pick') return;
    var had = !!t.pick;
    t.pick = (String(t.pick) === String(id)) ? null : String(id);
    if(!t.pick) t.verb = null;
    saveTales();
    if(!patchPick()) taleRender();
    if(!had && t.pick) taleRevealStep(document.querySelector('#hoh-xev-body .hoh-xtl-what:not(.is-out)'));
    var on = document.querySelector('#hoh-xev-body .hoh-xtl-tile.is-on');
    if(on && !had){ var c = findHero(t.pick); burstAt(on, { n: 14, colors: [RAR_COL[tierOf(c)] || '#f2d98e'].concat(GOLD), power: 0.6 }); }
  };
  window.hohExploreTaleVerb = function(k){
    var t = tales().turn; if(!t || t.status !== 'pick' || !t.pick) return;
    var hadVerb = !!t.verb;
    t.verb = (t.verb === k && k !== 'own') ? null : k;
    saveTales();
    if(!patchVerb()) taleRender();
    if(!hadVerb && t.verb) taleRevealStep(document.querySelector('#hoh-xev-body .hoh-xtl-tell:not(.is-out)'));
    if(k === 'own'){ var inp = document.getElementById('hoh-xtl-own'); if(inp){ try { inp.focus({ preventScroll: true }); } catch(_){ inp.focus(); } } }
  };
  window.hohExploreTaleOwn = function(v){
    var t = tales().turn; if(!t) return;
    t.own = String(v || '').slice(0, 80); saveTales();
    var b = document.querySelector('#hoh-xev-body [data-xtl-roll]');
    if(b) b.disabled = !String(t.own).trim();
  };
  window.hohExploreTaleForm = function(f){
    var t = tales().turn; if(!t || t.status !== 'pick') return;
    t.form = f === 'story' ? 'story' : 'hook'; saveTales();
    Array.prototype.forEach.call(document.querySelectorAll('#hoh-xev-body .hoh-xtl-form-opt'), function(x, i){
      var on = (i === 1) === (t.form === 'story'); x.classList.toggle('is-on', on); x.setAttribute('aria-pressed', String(on));
    });
    var g = document.querySelector('#hoh-xev-body .hoh-xtl-form'); if(g) g.setAttribute('data-form', t.form);
  };
  window.hohExploreTaleRoll = function(){ taleTell(); };
  window.hohExploreTaleShare = function(btn){
    var t = tales().turn; if(!t || t.status !== 'told') return;
    var A = findHero(t.base), B = findHero(t.pick); if(!A || !B) return;
    var url = location.origin + location.pathname;
    var body = t.title + '\n\n' + talePlain(t) + '\n\n' + A.name + ' and ' + B.name + ', from the FramedFantasy Hall of Heroes';
    function said(words){
      if(!btn) return;
      btn.textContent = words; btn.classList.add('is-done');
      setTimeout(function(){ if(btn.isConnected){ btn.textContent = 'Share'; btn.classList.remove('is-done'); } }, 1800);
    }
    function copy(){
      var text = body + '\n' + url;
      var done = function(){ said('Copied'); };
      try {
        if(navigator.clipboard && navigator.clipboard.writeText){ navigator.clipboard.writeText(text).then(done, function(){ legacy(text) ? done() : said('Could not copy'); }); return; }
      } catch(_){}
      if(legacy(text)) done(); else said('Could not copy');
    }
    function legacy(text){
      try { var ta = document.createElement('textarea'); ta.value = text; ta.setAttribute('readonly', ''); ta.style.cssText = 'position:fixed;left:-9999px;top:0;opacity:0';
        document.body.appendChild(ta); ta.select(); var ok = document.execCommand('copy'); document.body.removeChild(ta); return ok; } catch(_){ return false; }
    }
    /* phones open their share sheet; anything without one (or a cancelled sheet) copies */
    var coarse = false; try { coarse = window.matchMedia('(pointer:coarse)').matches; } catch(_){}
    if(coarse && navigator.share){ navigator.share({ title: t.title, text: body, url: url }).then(function(){ said('Shared'); }, function(e){ if(!(e && e.name === 'AbortError')) copy(); }); return; }
    copy();
  };
  /* Guess or Tales. The pane fades out, the glide slides, the other mode fades in (180-320 ms, opacity only when reduced). */
  window.hohExploreEventMode = function(m){
    m = m === 'tales' ? 'tales' : 'guess';
    if(!live() || !signedIn() || mode() === m || TB.rolling) return;
    setMode(m);
    if(m === 'tales'){ try { localStorage.setItem('hoh-xev-tales-seen', '1'); } catch(_){} }
    S.note = ''; S.noteKind = '';
    var b = el('hoh-xev-body'), pane = b && b.querySelector('#hoh-xev-pane');
    if(b) paneOf(b);
    function next(){
      if(m === 'tales'){ if(!turn() && !TB.busy){ talesDeal(); return; } }
      else if(!S.current && !S.busy && !S.thanks){ window.hohExploreEventNext(); }
      render({ enter: true, swap: true });
    }
    if(!pane || reduced()){ next(); return; }
    pane.classList.remove('is-in'); pane.classList.add('is-out');
    setTimeout(function(){ pane.classList.remove('is-out'); next(); }, 150);
  };

  /* ---------- actions ---------- */
  window.hohExploreEventNext = function(){
    var d = D();
    if(!d || S.busy || !live()) return;
    if(!signedIn()){ if(typeof window.hohFavSignInPrompt === 'function') window.hohFavSignInPrompt(); return; }
    S.busy = true; S.note = ''; S.noteKind = '';
    var btn = document.querySelector('#hoh-xev-body [data-xev-next]');
    if(btn){ btn.disabled = true; btn.setAttribute('aria-busy', 'true'); }
    var tries = 0;
    var want = kindFor(today() + 1);
    function attempt(){
      var pk = pickFor(want);
      var c = pk ? pk.hero : null;
      if(!c){ S.busy = false; S.current = null; S.game = null; S.thanks = 'empty'; render(); return; }
      d.explore.mark(c.id).then(function(r){
        if(r.ok){
          S.busy = false;
          S.current = c;
          saveLast(c.id);
          S.finishReady = !!r.done_today || r.today >= limit();
          S.game = pk.game || buildGame(c, pk.kind || want);
          var p = play(); p.game = S.game; savePlay();
          /* R793.1: the server still pays the day's free entry on the 20th
             (r.entry_granted); the wallet catches up quietly, with no words. */
          if(r.entry_granted){
            S.entryGranted = true;
            try { if(typeof window.hohSyncBgTickets === 'function') window.hohSyncBgTickets(); } catch(_){}
          }
          var last = r.today >= limit();
          uncheer();
          render({ enter: true, step: true });
          announce('Hero ' + r.today + ' of ' + limit() + '. ' + (c.name || '') + '.' + (S.game ? ' ' + PROMPT[S.game.kind] + '.' : '') + (last ? ' The last one today.' : ''));
          if(last) celebrate();
          else if(r.today % 5 === 0) milestone();
          return;
        }
        if(r.missing){ S.busy = false; S.off = true; syncVisibility(); return; }
        if(SKIP_REASONS.test(r.reason || '')) SKIP[String(c.id)] = 1;
        if((/seen/i.test(r.reason) || SKIP_REASONS.test(r.reason || '')) && ++tries < 6){ attempt(); return; }
        S.busy = false;
        if(r.reason === 'limit_reached' || r.done_today){ S.finishReady = true; S.current = null; S.game = null; S.thanks = 'done'; render(); fadeAway(); return; }
        S.note = (r.network ? 'That did not load. Try again in a moment.' : 'That hero did not load. Try again.');
        S.noteKind = 'error';
        render();
      });
    }
    if(rosterIn()){ attempt(); return; }
    S.rosterWait = true;
    if(btn) btn.classList.add('hoh-roster-wait');
    afterRoster(function(){
      S.rosterWait = false;
      if(!S.open || mode() === 'tales'){ S.busy = false; render(); return; }  /* closed or left for Tales meanwhile: no hero is met */
      attempt();
    });
  };
  /* R793.1 (Jan, 2026-09-22): "just fade it away for the explore after 20".
     This reverses the R792 QA "stay put" rule; Jan accepts the small page
     shift. The thanks show long enough to read, then the whole row fades and
     its height eases shut, and html.hoh-xev-done keeps it gone for the day.
     2026-09-24: the thanks carry the day's score, so they hold a little
     longer (2.8 s). */
  var fadeTimer = 0;
  function fadeAway(){
    clearTimeout(fadeTimer);
    fadeTimer = setTimeout(function(){
      var r = root();
      if(!r || S.thanks !== 'done') return;
      function done(){
        try { document.documentElement.classList.add('hoh-xev-done'); } catch(_){}
        r.style.cssText = r.style.cssText.replace(/(height|opacity|margin|padding|overflow|transition)[^;]*;?/g, '');
        S.open = false;
      }
      if(reduced()){ r.style.transition = 'opacity .2s linear'; r.style.opacity = '0'; setTimeout(done, 220); return; }
      var h = r.offsetHeight;
      r.style.overflow = 'hidden';
      r.style.height = h + 'px';
      void r.offsetHeight;
      r.style.transition = 'opacity .32s ease-out, height .46s cubic-bezier(.2,.7,.2,1) .16s, margin .46s cubic-bezier(.2,.7,.2,1) .16s, padding .46s cubic-bezier(.2,.7,.2,1) .16s';
      r.style.opacity = '0';
      r.style.height = '0px';
      r.style.marginTop = r.style.marginBottom = '0px';
      r.style.paddingTop = r.style.paddingBottom = '0px';
      setTimeout(done, 680);
    }, 2800);
  }
  window.hohExploreEventFinish = function(){
    S.thanks = 'done'; S.current = null; S.game = null;
    render();
    var p = play(), told = tales().told | 0;
    announce('That is all for today.' + (p.played ? ' You got ' + p.right + ' of ' + p.played + ' right.' : '') + (told ? ' You told ' + told + (told === 1 ? ' tale.' : ' tales.') : '') + ' Thanks for exploring the Hall.');
    var mark = document.querySelector('#hoh-xev-body .hoh-xev-thanks-mark');
    if(mark && !reduced()) setTimeout(function(){ burstAt(mark, { n: 34, colors: GOLD.concat(['#ef94ff', '#b3f5ee']), power: 1.2 }); }, 140);
    fadeAway();
  };
  function openBody(){
    S.open = true;
    /* HALL-SPEED-01: the pixel bursts draw on the Hall's particle canvas (hoh-fx-canvas.js), which the Hall now fetches
       when Explore opens; a burst before it lands is the small DOM burst pixelFx already falls back to */
    try { if(typeof window.hohLoadBattlegrounds === 'function') window.hohLoadBattlegrounds('fx'); } catch(_){}
    if(!S.current && !S.thanks){
      var last = readLast();
      var x = D() && D().explore.cached();
      var c = last ? findHero(last) : null;
      if(c && x && x.seen[String(c.id)]){
        S.current = c;
        S.finishReady = !!x.done_today || (x.today | 0) >= limit();
        /* the game on show before a reload comes back as it was */
        var pg = play().game;
        S.game = (pg && pg.id === String(c.id)) ? pg : null;
      }
    }
    /* R793.1 (Jan, 2026-09-22): "if they just press explore ... it doesnt
       have an 'intro screen' it just jumps in and immediately shows them the
       first hero." With no hero on show yet, opening fetches the next one at
       once; the row shows a quiet loading line until it lands. The intro only
       remains as the retry screen after a failed load (it carries the note). */
    /* Tales: the table on today's turn comes back as it was (a telling cut off by a reload can roll again);
       with no table yet, a new one is dealt */
    if(mode() === 'tales' && !S.thanks){
      var tt = tales().turn;
      if(tt && tt.status === 'telling'){ tt.status = 'pick'; tt.rolls = Math.max(0, (tt.rolls | 0) - 1); saveTales(); }
      if(tt && tt.status === 'deal' && !TB.busy){ if(!taleTopUp(tt)) tales().turn = null; saveTales(); }
      if(!turn() && !TB.busy && live() && signedIn() && allHeroes().length){ talesDeal(); return; }
      render({ enter: true, fill: true });
      return;
    }
    if(!S.current && !S.thanks && !S.busy && live() && signedIn() && allHeroes().length){
      window.hohExploreEventNext();
    }
    render({ enter: true, fill: true });
  }
  window.hohExploreEventToggle = function(){
    if(!live() || !signedIn()) return;
    if(S.open){ S.open = false; render(); return; }
    openBody();
  };
  /* From anywhere (the Battlegrounds door, the Resources guide): back to the
     main Hall, open the event, bring it into view. */
  window.hohExploreEventOpen = function(){
    if(!live()) return;
    try {
      if(document.body.classList.contains('hoh-page-mode-yours') && typeof window.hohYhToggleClick === 'function') window.hohYhToggleClick();
    } catch(_){}
    setTimeout(function(){
      var r = root();
      if(!r) return;
      if(!S.open) openBody();
      try { r.scrollIntoView({ block: 'start', behavior: reduced() ? 'auto' : 'smooth' }); } catch(_){ try { r.scrollIntoView(); } catch(__){} }
    }, 120);
  };

  /* ---------- keeping the row honest ---------- */
  function syncVisibility(){
    var r = root(); if(!r) return;
    var d = D();
    var x = d && d.explore.cached();
    var off = S.off || !live() || !signedIn();
    if(!off && x && !x.done_today && (x.today | 0) < limit() && !S.current && !talesActive() && !pickHero()) off = true;
    r.classList.toggle('is-off', !!off);
    if(x && x.done_today && !S.open){ try { document.documentElement.classList.add('hoh-xev-done'); } catch(_){} }
    if(x && !x.done_today){ try { document.documentElement.classList.remove('hoh-xev-done'); } catch(_){} }
  }
  var syncing = false;
  function sync(){
    var d = D();
    if(!d || !live() || !signedIn()){ syncVisibility(); return; }
    if(syncing) return;
    syncing = true;
    /* the roster and the favourites must be in before "who is left" means anything */
    var waited = 0;
    (function waitRoster(){
      if(allHeroes().length || waited > 15000){
        Promise.all([d.explore.state(), d.favourites.load()]).then(function(res){
          syncing = false;
          var x = res[0];
          if(!x){ if(d.mode() === 'legacy'){ S.off = true; } syncVisibility(); return; }
          syncVisibility();
          paintBar();
          paintCombo();
        }, function(){ syncing = false; });
        return;
      }
      waited += 250;
      setTimeout(waitRoster, 250);
    })();
  }
  function resetForAccount(){
    S = { open: false, current: null, busy: false, game: null, finishReady: false, thanks: false, note: '', noteKind: '', entryGranted: false, off: false };
    SKIP = {}; P = null; PK = ''; _plan = null;
    TR = null; TRK = ''; TB.busy = false; TB.rolling = false; TB.landed = {}; TB.gen++;
    try { document.documentElement.classList.remove('hoh-xev-done'); } catch(_){}
    render();
    sync();
  }
  try {
    document.addEventListener('hoh:r793-mode', function(){ sync(); });
    document.addEventListener('hoh:signed-in', resetForAccount);
    document.addEventListener('hoh:signed-out', resetForAccount);
    document.addEventListener('hoh:explore-state', function(){ paintBar(); });
    /* A deferred script runs while readyState is already "interactive" and
       before the scripts after it (section-core among them), so the boot
       waits for DOMContentLoaded, with load as a backstop. sync() is safe
       to call twice. */
    if(document.readyState === 'complete'){ paintBar(); sync(); }
    else {
      document.addEventListener('DOMContentLoaded', function(){ paintBar(); sync(); });
      window.addEventListener('load', function(){ sync(); });
    }
    /* the day turns at midnight Pacific while a tab stays open */
    document.addEventListener('visibilitychange', function(){
      if(document.hidden) return;
      var d = D(); if(!d || !live()) return;
      var x = d.explore.cached();
      if(!x){ try { document.documentElement.classList.remove('hoh-xev-done'); } catch(_){} S.thanks = false; S.current = null; S.game = null; sync(); }
    });
  } catch(_){}
})();
